import React, { useState, useEffect, useRef, useCallback, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import html2canvas from 'html2canvas';
import { useModal } from '../context/ModalDialogContext';
import { extractExifFromBlob, reverseGeocodePlace } from '../utils/exifReader';
import { formatDateTimeValue, DATE_TIME_FORMAT_PRESETS } from '../utils/dateTimeFormatter';
import { QRCodeSVG } from '../utils/qrCode';
import AiDesignerChatPanel from './AiDesignerChatPanel';
import PrintExportModal from './PrintExportModal';
import {
  DEFAULT_CARD_PATTERNS,
  getStoredCustomPatterns,
  saveCustomPattern,
  deleteCustomPattern,
  generatePatternString
} from '../utils/cardPatterns';

const StableColorInput = ({ value, onChange, style, ...props }) => {
  const inputRef = useRef(null);
  const isFocusedRef = useRef(false);

  useEffect(() => {
    if (inputRef.current && !isFocusedRef.current && document.activeElement !== inputRef.current) {
      inputRef.current.value = value || '#ffffff';
    }
  }, [value]);

  return (
    <input
      ref={inputRef}
      type="color"
      defaultValue={value || '#ffffff'}
      onFocus={() => {
        isFocusedRef.current = true;
      }}
      onBlur={() => {
        isFocusedRef.current = false;
        if (inputRef.current && onChange) {
          onChange({ target: { value: inputRef.current.value } });
        }
      }}
      onChange={(e) => {
        if (onChange) {
          onChange(e);
        }
      }}
      style={style}
      {...props}
    />
  );
};

// Pre-defined themes for card styling
const THEMES = [
  { id: 'gold-dark', name: 'Gold & Dark Slate', primary: '#c9a054', secondary: '#1e293b', accent: '#3b82f6', text: '#f8fafc', bg: '#0f172a' },
  { id: 'silver-blue', name: 'Silver & Ocean Blue', primary: '#cbd5e1', secondary: '#0f172a', accent: '#06b6d4', text: '#f1f5f9', bg: '#0891b2' },
  { id: 'bronze-classic', name: 'Bronze & Cream', primary: '#b45309', secondary: '#fef3c7', accent: '#d97706', text: '#1e293b', bg: '#fef3c7' }
];

export const MAPPING_OPTIONS = [
  { value: '', label: 'None (Manual Input)' },
  {
    group: 'Organization Details',
    options: [
      { value: 'org.name', label: 'Organization → Name' },
      { value: 'org.website_address', label: 'Organization → Website (WWW)' },
      { value: 'org.enquiries_email', label: 'Organization → Enquiries Email' },
      { value: 'org.bookings_link', label: 'Organization → Bookings Link' },
      { value: 'org.social_media', label: 'Organization → Social Media' },
      { value: 'org.logo_url', label: 'Organization → Logo Image URL' },
      { value: 'org.billing_address', label: 'Organization → Billing Address' },
      { value: 'org.billing_city', label: 'Organization → City' },
      { value: 'org.billing_state', label: 'Organization → State' },
      { value: 'org.billing_zip', label: 'Organization → ZIP / Postal' },
      { value: 'org.billing_country', label: 'Organization → Country' }
    ]
  },
  {
    group: 'CoinVault / Physical Coin Details',
    options: [
      { value: 'coin_vault.coin_number', label: 'CoinVault → Coin Number (e.g. 1)' },
      { value: 'coin_vault.total_coins', label: 'CoinVault → Total Coins in Order (e.g. 10)' },
      { value: 'coin_vault.coin_fraction', label: 'CoinVault → Coin # of Total (e.g. #1 of 10)' },
      { value: 'coin_vault.public_code', label: 'CoinVault → Public Serial Code' },
      { value: 'coin_vault.rfid_url', label: 'CoinVault → NFC / RFID Verification URL' },
      { value: 'coin_vault.minted_at', label: 'CoinVault → Minted Date (YYYY-MM-DD)' },
      { value: 'coin_vault.order_id', label: 'CoinVault → Order ID' },
      { value: 'coin_vault.checksum_front', label: 'CoinVault → Front MD5 Checksum' },
      { value: 'coin_vault.checksum_back', label: 'CoinVault → Back MD5 Checksum' },
      { value: 'coin_vault.preview_front_url', label: 'CoinVault → Front Preview Image URL' },
      { value: 'coin_vault.preview_back_url', label: 'CoinVault → Back Preview Image URL' }
    ]
  },
  {
    group: 'Logged-in User Details',
    options: [
      { value: 'user.full_name', label: 'User → Full Name' },
      { value: 'user.first_name', label: 'User → First Name' },
      { value: 'user.last_name', label: 'User → Last Name' },
      { value: 'user.email', label: 'User → Email' },
      { value: 'user.phone_number', label: 'User → Phone Number' },
      { value: 'user.company_name', label: 'User → Company Name' },
      { value: 'user.role', label: 'User → Role' }
    ]
  },
  {
    group: 'Tenant / Brand Details',
    options: [
      { value: 'tenant.brand_name', label: 'Tenant → Brand Name' },
      { value: 'tenant.domain_name', label: 'Tenant → Domain / WWW' },
      { value: 'tenant.logo_url', label: 'Tenant → Logo Image URL' }
    ]
  },
  {
    group: 'System / Date & Time',
    options: [
      { value: 'system.current_year', label: 'System → Current Year (YYYY)' },
      { value: 'system.current_month_name', label: 'System → Current Month Name (e.g. September)' },
      { value: 'system.current_month_num', label: 'System → Current Month Number (01-12)' },
      { value: 'system.current_date', label: 'System → Current Date (YYYY-MM-DD)' },
      { value: 'system.current_day', label: 'System → Current Day (01-31)' },
      { value: 'system.current_day_name', label: 'System → Current Day Name (e.g. Wednesday)' },
      { value: 'system.current_time', label: 'System → Current Time (HH:MM)' },
      { value: 'system.current_datetime', label: 'System → Current Date & Time' }
    ]
  },
  {
    group: 'Photo / Image EXIF Metadata (Auto-Pre-filled / Optional)',
    options: [
      { value: 'photo_exif.place', label: 'Photo EXIF → Place / Location Name (Reverse Geocoded)' },
      { value: 'photo_exif.gps_coordinates', label: 'Photo EXIF → GPS Coordinates (Lat, Lng)' },
      { value: 'photo_exif.gps_latitude', label: 'Photo EXIF → GPS Latitude' },
      { value: 'photo_exif.gps_longitude', label: 'Photo EXIF → GPS Longitude' },
      { value: 'photo_exif.gps_dms', label: 'Photo EXIF → GPS DMS (Deg° Min\' Sec")' },
      { value: 'photo_exif.gps_altitude', label: 'Photo EXIF → GPS Altitude' },
      { value: 'photo_exif.date_taken', label: 'Photo EXIF → Date / Time Photo Taken' },
      { value: 'photo_exif.date', label: 'Photo EXIF → Date Photo Taken (YYYY-MM-DD)' },
      { value: 'photo_exif.time', label: 'Photo EXIF → Time Photo Taken (HH:MM)' }
    ]
  }
];

export const resolveMappedValue = (mappingKey, user, orgData, tenantConfig, photoExif = null, datasets = null, testDataOverrides = null, fieldMappings = null, coinData = null) => {
  if (!mappingKey) return null;
  if (mappingKey.startsWith('dataset_field.')) {
    const fieldLabel = mappingKey.replace('dataset_field.', '');
    if (testDataOverrides && testDataOverrides[fieldLabel] !== undefined && String(testDataOverrides[fieldLabel]).trim() !== '') {
      return String(testDataOverrides[fieldLabel]).trim();
    }
    if (fieldMappings && fieldMappings[fieldLabel]) {
      const nested = resolveMappedValue(fieldMappings[fieldLabel], user, orgData, tenantConfig, photoExif, datasets, testDataOverrides, null, coinData);
      if (nested !== null && nested !== undefined && String(nested).trim() !== '') {
        return String(nested).trim();
      }
    }
    if (datasets) {
      for (const ds of Object.values(datasets)) {
        if (ds?.variables?.[fieldLabel] !== undefined && String(ds.variables[fieldLabel]).trim() !== '') {
          return String(ds.variables[fieldLabel]).trim();
        }
      }
    }
    return '';
  }
  switch (mappingKey) {
    case 'coin_vault.coin_number':
    case 'coin.coin_number':
      return String(coinData?.coin_number ?? testDataOverrides?.['coin_vault.coin_number'] ?? '1');
    case 'coin_vault.total_coins':
    case 'coin.total_coins':
      return String(coinData?.total_coins ?? testDataOverrides?.['coin_vault.total_coins'] ?? '10');
    case 'coin_vault.coin_fraction':
    case 'coin.coin_fraction': {
      const num = coinData?.coin_number ?? testDataOverrides?.['coin_vault.coin_number'] ?? '1';
      const tot = coinData?.total_coins ?? testDataOverrides?.['coin_vault.total_coins'] ?? '10';
      return `#${num} of ${tot}`;
    }
    case 'coin_vault.public_code':
    case 'coin.public_code':
      return coinData?.public_code || testDataOverrides?.['coin_vault.public_code'] || 'f4ee83552bfb940a';
    case 'coin_vault.rfid_url':
    case 'coin.rfid_url':
      return coinData?.rfid_url || testDataOverrides?.['coin_vault.rfid_url'] || 'https://trophycoin.com/c/f4ee83552bfb940a';
    case 'coin_vault.minted_at':
    case 'coin.minted_at':
      return coinData?.minted_at ? String(coinData.minted_at).split('T')[0] : (testDataOverrides?.['coin_vault.minted_at'] || new Date().toISOString().split('T')[0]);
    case 'coin_vault.order_id':
    case 'coin.order_id':
      return String(coinData?.order_id ?? testDataOverrides?.['coin_vault.order_id'] ?? '104');
    case 'coin_vault.checksum_front':
    case 'coin.checksum_front':
      return coinData?.md5_front || coinData?.checksum_front || testDataOverrides?.['coin_vault.checksum_front'] || 'a1b2c3d4e5f60718293a4b5c6d7e8f90';
    case 'coin_vault.checksum_back':
    case 'coin.checksum_back':
      return coinData?.md5_back || coinData?.checksum_back || testDataOverrides?.['coin_vault.checksum_back'] || '09f8e7d6c5b4a39281706f5e4d3c2b1a';
    case 'coin_vault.preview_front_url':
    case 'coin.preview_front_url':
      return coinData?.preview_front_url || testDataOverrides?.['coin_vault.preview_front_url'] || '';
    case 'coin_vault.preview_back_url':
    case 'coin.preview_back_url':
      return coinData?.preview_back_url || testDataOverrides?.['coin_vault.preview_back_url'] || '';
    case 'photo_exif.place':
      return photoExif?.place || testDataOverrides?.['photo_exif.place'] || coinData?.card_data?.place || 'Sydney, New South Wales, Australia';
    case 'photo_exif.gps_coordinates':
      return photoExif?.gps_coordinates || '-33.868820, 151.209296';
    case 'photo_exif.gps_latitude':
      return photoExif?.gps_latitude || '-33.868820';
    case 'photo_exif.gps_longitude':
      return photoExif?.gps_longitude || '151.209296';
    case 'photo_exif.gps_dms':
      return photoExif?.gps_dms || '33°52\'7.8" S, 151°12\'33.5" E';
    case 'photo_exif.gps_altitude':
      return photoExif?.gps_altitude || '14.2 m';
    case 'photo_exif.date_taken':
      return photoExif?.date_taken || new Date().toISOString().split('T')[0];
    case 'photo_exif.date':
      return photoExif?.date || (photoExif?.date_taken ? photoExif.date_taken.split(' ')[0] : new Date().toISOString().split('T')[0]);
    case 'photo_exif.time':
      return photoExif?.time || (photoExif?.date_taken ? photoExif.date_taken.split(' ')[1] : '12:00');
    case 'system.current_year':
      return new Date().getFullYear().toString();
    case 'system.current_month_name':
      return new Intl.DateTimeFormat('en-US', { month: 'long' }).format(new Date());
    case 'system.current_month_num':
      return String(new Date().getMonth() + 1).padStart(2, '0');
    case 'system.current_date':
      return new Date().toISOString().split('T')[0];
    case 'system.current_day':
      return String(new Date().getDate()).padStart(2, '0');
    case 'system.current_day_name':
      return new Intl.DateTimeFormat('en-US', { weekday: 'long' }).format(new Date());
    case 'system.current_time':
      return new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    case 'system.current_datetime':
      return `${new Date().toISOString().split('T')[0]} ${new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
    case 'org.name':
      return orgData?.name || tenantConfig?.brand_name || user?.company_name || '';
    case 'org.website_address':
      return orgData?.website_address || tenantConfig?.domain_name || '';
    case 'org.enquiries_email':
      return orgData?.enquiries_email || user?.email || '';
    case 'org.bookings_link':
      return orgData?.bookings_link || '';
    case 'org.social_media':
      return orgData?.social_media || '';
    case 'org.logo_url':
      return orgData?.logo_url || tenantConfig?.logo_url || '';
    case 'org.billing_address':
      return orgData?.billing_address || user?.billing_address || '';
    case 'org.billing_city':
      return orgData?.billing_city || user?.billing_city || '';
    case 'org.billing_state':
      return orgData?.billing_state || user?.billing_state || '';
    case 'org.billing_zip':
      return orgData?.billing_zip || user?.billing_zip || '';
    case 'org.billing_country':
      return orgData?.billing_country || user?.billing_country || testDataOverrides?.['org.billing_country'] || testDataOverrides?.country || testDataOverrides?.trophy_country || '';
    case 'user.full_name':
      return [user?.first_name, user?.last_name].filter(Boolean).join(' ') || user?.email || '';
    case 'user.first_name':
      return user?.first_name || '';
    case 'user.last_name':
      return user?.last_name || '';
    case 'user.email':
      return user?.email || '';
    case 'user.phone_number':
      return user?.phone_number || '';
    case 'user.company_name':
      return user?.company_name || orgData?.name || '';
    case 'user.role':
      return user?.role || '';
    case 'tenant.brand_name':
      return tenantConfig?.brand_name || '';
    case 'tenant.domain_name':
      return tenantConfig?.domain_name || '';
    case 'tenant.logo_url':
      return tenantConfig?.logo_url || '';
    default:
      return null;
  }
};

export const openLinkSafely = (linkOrUrl, isEmail = false) => {
  if (!linkOrUrl) return;
  const url = typeof linkOrUrl === 'object' ? linkOrUrl.url : linkOrUrl;
  if (!url) return;

  const urlStr = String(url).trim();
  const isMail = isEmail || (typeof linkOrUrl === 'object' && linkOrUrl.type === 'email') || urlStr.startsWith('mailto:') || (urlStr.includes('@') && !urlStr.startsWith('http://') && !urlStr.startsWith('https://'));

  if (isMail) {
    const cleanEmail = urlStr.replace(/^mailto:/i, '').trim();
    const mailtoUrl = `mailto:${cleanEmail}`;
    
    // In Safari (iOS & macOS), direct programmatic navigation via window.location.href = 'mailto:...'
    // fails with "Safari cannot open the page because the address is invalid".
    // Creating an anchor element and dispatching a native click event
    // cleanly triggers the system Mail app / default email client without a document navigation error.
    const a = document.createElement('a');
    a.href = mailtoUrl;
    a.target = '_self';
    a.rel = 'noopener noreferrer';
    a.style.position = 'fixed';
    a.style.top = '-9999px';
    a.style.left = '-9999px';
    a.style.opacity = '0';
    document.body.appendChild(a);
    a.click();
    setTimeout(() => {
      if (document.body.contains(a)) {
        document.body.removeChild(a);
      }
    }, 300);
  } else {
    let cleanUrl = urlStr;
    if (!cleanUrl.startsWith('http://') && !cleanUrl.startsWith('https://')) {
      cleanUrl = `https://${cleanUrl}`;
    }
    const a = document.createElement('a');
    a.href = cleanUrl;
    a.target = '_blank';
    a.rel = 'noopener noreferrer';
    a.style.position = 'fixed';
    a.style.top = '-9999px';
    a.style.left = '-9999px';
    a.style.opacity = '0';
    document.body.appendChild(a);
    a.click();
    setTimeout(() => {
      if (document.body.contains(a)) {
        document.body.removeChild(a);
      }
    }, 300);
  }
};

export const resolveElementLink = (elem, linkMappings = {}, fieldMappings = {}, testDataOverrides = {}, datasets = {}, user = null, orgData = null, tenantConfig = null, photoExif = null) => {
  if (!elem) return null;
  const linkVar = elem.linkVariable;
  const mappingKey = (linkMappings && linkVar && linkMappings[linkVar]) || elem.linkMapping || '';
  let rawValue = '';

  // 1. If mapped to a specific mappingKey
  if (mappingKey) {
    const resolved = resolveMappedValue(mappingKey, user, orgData, tenantConfig, photoExif, datasets, testDataOverrides, fieldMappings);
    if (resolved !== null && resolved !== undefined && String(resolved).trim() !== '') {
      rawValue = String(resolved).trim();
    }
  }

  // 2. If not mapped or mapped value was blank, check if the variable itself is overridden or in datasets
  if (!rawValue && linkVar) {
    const cleanVar = linkVar.replace(/^\$/, '');
    if (testDataOverrides && testDataOverrides[cleanVar] !== undefined && String(testDataOverrides[cleanVar]).trim() !== '') {
      rawValue = String(testDataOverrides[cleanVar]).trim();
    } else if (testDataOverrides && testDataOverrides[linkVar] !== undefined && String(testDataOverrides[linkVar]).trim() !== '') {
      rawValue = String(testDataOverrides[linkVar]).trim();
    } else if (datasets) {
      for (const ds of Object.values(datasets)) {
        if (ds?.variables?.[cleanVar] !== undefined && String(ds.variables[cleanVar]).trim() !== '') {
          rawValue = String(ds.variables[cleanVar]).trim();
          break;
        } else if (ds?.variables?.[linkVar] !== undefined && String(ds.variables[linkVar]).trim() !== '') {
          rawValue = String(ds.variables[linkVar]).trim();
          break;
        }
      }
    }
  }

  // 3. Fallback to standard defaults for known link variables
  if (!rawValue && linkVar) {
    if (linkVar === '$booking_link') {
      rawValue = orgData?.bookings_link || '';
    } else if (linkVar === '$website_link_element') {
      rawValue = orgData?.website_address || tenantConfig?.domain_name || '';
    } else if (linkVar === '$emial_link' || linkVar === '$email_link') {
      rawValue = orgData?.enquiries_email || user?.email || '';
    } else if (linkVar === '$verify_link') {
      rawValue = tenantConfig?.domain_name ? `https://${tenantConfig.domain_name}/verify` : '';
    }
  }

  // 4. Fallback for text elements containing email addresses or web links directly
  if (!rawValue && elem.type === 'text') {
    let candidate = (elem.text || '').trim();
    if (candidate.startsWith('{{') && candidate.endsWith('}}')) {
      candidate = candidate.slice(2, -2).trim();
    }
    if (candidate === 'org.enquiries_email' || candidate === 'user.email') {
      const resolved = resolveMappedValue(candidate, user, orgData, tenantConfig, photoExif, datasets, testDataOverrides, fieldMappings);
      if (resolved) rawValue = String(resolved).trim();
    } else if (candidate === '$emial_link' || candidate === '$email_link') {
      rawValue = orgData?.enquiries_email || user?.email || '';
    } else if (candidate === '$booking_link') {
      rawValue = orgData?.bookings_link || '';
    } else if (candidate === '$website_link_element') {
      rawValue = orgData?.website_address || tenantConfig?.domain_name || '';
    } else if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(candidate)) {
      rawValue = candidate;
    } else if (/^https?:\/\/[^\s]+$/i.test(candidate) || /^www\.[^\s]+$/i.test(candidate)) {
      rawValue = candidate;
    }
  }

  if (!rawValue || rawValue.trim() === '') return null;
  rawValue = rawValue.trim();

  // 5. Format link as email or web URL
  const isEmail = linkVar === '$emial_link' || linkVar === '$email_link' || mappingKey === 'org.enquiries_email' || mappingKey === 'user.email' || (rawValue.includes('@') && !rawValue.startsWith('http://') && !rawValue.startsWith('https://'));

  if (isEmail) {
    const cleanEmail = rawValue.replace(/^mailto:/i, '').trim();
    return {
      type: 'email',
      url: `mailto:${cleanEmail}`,
      display: cleanEmail,
      linkVar: linkVar || '$email_link'
    };
  } else {
    let cleanUrl = rawValue;
    if (!cleanUrl.startsWith('http://') && !cleanUrl.startsWith('https://')) {
      cleanUrl = `https://${cleanUrl}`;
    }
    return {
      type: 'url',
      url: cleanUrl,
      display: rawValue,
      linkVar: linkVar || '$website_link_element'
    };
  }
};

/**
 * Determines if a specific card element acts as an interactive verification trigger.
 * Strictly checks that this specific element was explicitly assigned as the verify button.
 */
export const isElementVerifyTarget = (elem) => {
  if (!elem) return false;

  // 1. Direct verify link variable
  const linkVar = elem.linkVariable || elem.linkVar || '';
  if (linkVar === '$verify_link') {
    return true;
  }

  // 2. Direct verify action attribute
  if (elem.linkAction === 'verify' || elem.action === 'verify') {
    return true;
  }

  return false;
};

/**
 * Checks if a card template has any verify object defined across either front or back sides.
 */
export const hasCardVerifyObject = (template) => {
  if (!template) return false;
  const cfg = template.config || template;
  const frontElems = cfg?.sides?.front?.elements || [];
  const backElems = cfg?.sides?.back?.elements || [];
  const allElems = [...frontElems, ...backElems];

  return allElems.some(elem => isElementVerifyTarget(elem));
};

/**
 * Resolves the dynamic target content/URL for a QR Code element on a card template.
 */
export const resolveQrCodeValue = (
  elem,
  testDataOverrides = {},
  datasets = {},
  user = null,
  orgData = null,
  tenantConfig = null,
  fieldMappings = {}
) => {
  if (!elem) return 'https://fishermanstale.com';
  const qrSource = elem.qrSource || 'card_link';
  const domain = tenantConfig?.domain_name || (typeof window !== 'undefined' && window.location.host ? window.location.host : 'fishermanstale.com');
  const protocol = domain.includes('localhost') ? 'http' : 'https';
  const baseTenantUrl = `${protocol}://${domain}`;

  // 1. Coin / Card Public View Link
  if (qrSource === 'card_link') {
    const coinId = testDataOverrides?.coin_id || testDataOverrides?.id || testDataOverrides?.serial_number || 'SAMPLE-001';
    return `${baseTenantUrl}/c/${coinId}?src=qr`;
  }

  // 2. Authenticity Verification Link
  if (qrSource === 'verify') {
    const serial = testDataOverrides?.serial_number || testDataOverrides?.coin_id || '';
    return `${baseTenantUrl}/verify${serial ? `?coin=${encodeURIComponent(serial)}&src=qr` : '?src=qr'}`;
  }

  // 3. Lodge / Booking Link
  if (qrSource === 'booking') {
    if (orgData?.bookings_link) return orgData.bookings_link;
    if (testDataOverrides?.booking_link) return testDataOverrides.booking_link;
    return `${baseTenantUrl}/demobook`;
  }

  // 4. Website Link
  if (qrSource === 'website') {
    if (orgData?.website_address) {
      let web = orgData.website_address.trim();
      if (!web.startsWith('http://') && !web.startsWith('https://')) web = `https://${web}`;
      return web;
    }
    return baseTenantUrl;
  }

  // 5. Email Link
  if (qrSource === 'email') {
    const email = orgData?.enquiries_email || user?.email || `info@${domain}`;
    return `mailto:${email}`;
  }

  // 6. Dynamic Token / Template Sequence
  if (qrSource === 'dynamic') {
    let raw = elem.qrValue || elem.text || '';
    if (!raw) return `${baseTenantUrl}/c/${testDataOverrides?.coin_id || 'SAMPLE-001'}`;
    raw = raw.replace(/\$([a-zA-Z0-9_]+)/g, (match, varName) => {
      if (testDataOverrides && testDataOverrides[varName] !== undefined && testDataOverrides[varName] !== '') {
        return testDataOverrides[varName];
      }
      return match;
    });
    return raw;
  }

  // 7. Custom URL or Raw Text
  if (qrSource === 'custom' || elem.qrValue || elem.customUrl) {
    let val = (elem.qrValue || elem.customUrl || '').trim();
    if (!val) return baseTenantUrl;
    return val;
  }

  return baseTenantUrl;
};

const SHAPES_LIST = [
  {
    id: 'rect',
    label: 'Rectangle Tool',
    svg: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
      </svg>
    )
  },
  {
    id: 'circle',
    label: 'Ellipse Tool',
    svg: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="9" />
      </svg>
    )
  },
  {
    id: 'pill',
    label: 'Rounded Rectangle Tool',
    svg: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="6" width="18" height="12" rx="6" ry="6" />
      </svg>
    )
  },
  {
    id: 'triangle',
    label: 'Triangle Tool',
    svg: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polygon points="12 3 22 21 2 21" />
      </svg>
    )
  },
  {
    id: 'diamond',
    label: 'Diamond Tool',
    svg: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polygon points="12 2 22 12 12 22 2 12" />
      </svg>
    )
  },
  {
    id: 'polygon',
    label: 'Polygon Tool',
    svg: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polygon points="12 2 21 7.2 21 16.8 12 22 3 16.8 3 7.2" />
      </svg>
    )
  },
  {
    id: 'star',
    label: 'Star Tool',
    svg: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26" />
      </svg>
    )
  }
];

// Mock datasets for template binding simulation
const DATASETS = {};

const getLayerTypeIcon = (e) => {
  if (e.type === 'text') return 'A';
  if (e.type === 'image') return e.imageType === 'frame' ? '▧' : '▤';
  if (e.type === 'group') return '⧉';
  if (e.type === 'datagrid') return '⊞';
  if (e.type === 'accent') return (e.dividerType === 'vertical' || e.orientation === 'vertical') ? '│' : '―';
  if (e.type === 'certify') return '❂';
  if (e.type === 'shape') {
    if (e.shapeType === 'circle') return '●';
    if (e.shapeType === 'pill') return '⬭';
    if (e.shapeType === 'triangle') return '▲';
    if (e.shapeType === 'diamond') return '✦';
    if (e.shapeType === 'polygon') return '⬡';
    if (e.shapeType === 'star') return '★';
    return '■';
  }
  return '🗋';
};

export const getImageFilterStyle = (effectOrElem) => {
  const effect = typeof effectOrElem === 'object' && effectOrElem !== null ? effectOrElem.effect : effectOrElem;
  const hue = typeof effectOrElem === 'object' && effectOrElem !== null && effectOrElem.hue !== undefined
    ? (parseFloat(effectOrElem.hue) || 0)
    : 0;
  const saturation = typeof effectOrElem === 'object' && effectOrElem !== null && effectOrElem.saturation !== undefined && effectOrElem.saturation !== '' && !Number.isNaN(parseFloat(effectOrElem.saturation))
    ? parseFloat(effectOrElem.saturation)
    : 100;

  const filters = [];
  if (effect === 'mono_slate') {
    filters.push('grayscale(1)', 'contrast(1.15)', 'brightness(0.95)');
  } else if (effect === 'duotone_bronze') {
    filters.push('sepia(0.5)', 'hue-rotate(15deg)', 'contrast(1.15)', 'brightness(0.95)');
  }
  if (hue !== 0 && !Number.isNaN(hue)) {
    filters.push(`hue-rotate(${hue}deg)`);
  }
  if (saturation !== 100 && !Number.isNaN(saturation)) {
    filters.push(`saturate(${saturation}%)`);
  }
  return filters.length > 0 ? { filter: filters.join(' ') } : {};
};

const getImageOverlayStyle = (effect) => {
  let style = null;
  if (effect === 'foil_shimmer') {
    style = {
      background: 'linear-gradient(135deg, rgba(255,255,255,0) 20%, rgba(255,255,255,0.15) 40%, rgba(255,255,255,0.3) 50%, rgba(255,255,255,0.15) 60%, rgba(255,255,255,0) 80%)',
      backgroundSize: '250% 250%',
      backgroundPosition: '0% 0%',
      mixBlendMode: 'overlay',
      animation: 'shimmerFoilLoop 4s ease-in-out infinite'
    };
  } else if (effect === 'warm_leak') {
    style = {
      background: 'radial-gradient(circle at 0% 0%, rgba(255, 140, 50, 0.22) 0%, rgba(255, 200, 100, 0.05) 50%, transparent 100%)',
      mixBlendMode: 'screen'
    };
  } else if (effect === 'cold_frost') {
    style = {
      background: 'radial-gradient(circle at 100% 100%, rgba(100, 180, 255, 0.18) 0%, rgba(150, 220, 255, 0.03) 60%, transparent 100%)',
      mixBlendMode: 'screen'
    };
  } else if (effect === 'matte_vignette') {
    style = {
      background: 'radial-gradient(circle, transparent 50%, rgba(0, 0, 0, 0.4) 100%)',
      mixBlendMode: 'multiply'
    };
  } else if (effect === 'vintage_sepia') {
    style = {
      background: 'rgba(210, 150, 80, 0.1)',
      mixBlendMode: 'multiply'
    };
  } else if (effect === 'gold_dust') {
    style = {
      background: 'repeating-linear-gradient(45deg, rgba(212, 175, 55, 0.03) 0px, rgba(212, 175, 55, 0.03) 1px, transparent 1px, transparent 6px), repeating-linear-gradient(-45deg, rgba(212, 175, 55, 0.03) 0px, rgba(212, 175, 55, 0.03) 1px, transparent 1px, transparent 8px)',
      mixBlendMode: 'overlay'
    };
  } else if (effect === 'silver_satin') {
    style = {
      background: 'linear-gradient(135deg, rgba(255,255,255,0.03) 0%, rgba(200,200,210,0.12) 30%, rgba(255,255,255,0.2) 50%, rgba(200,200,210,0.12) 70%, rgba(255,255,255,0.03) 100%)',
      mixBlendMode: 'overlay'
    };
  } else if (effect === 'warm_velvet') {
    style = {
      background: 'linear-gradient(180deg, rgba(200, 50, 100, 0.06) 0%, rgba(255, 120, 50, 0.1) 100%)',
      mixBlendMode: 'overlay'
    };
  } else if (effect === 'emerald_glaze') {
    style = {
      background: 'linear-gradient(135deg, rgba(16, 185, 129, 0) 30%, rgba(16, 185, 129, 0.15) 50%, rgba(16, 185, 129, 0) 70%)',
      mixBlendMode: 'overlay'
    };
  } else if (effect === 'royal_velvet') {
    style = {
      background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.08) 0%, rgba(168, 85, 247, 0.08) 100%)',
      mixBlendMode: 'color-dodge'
    };
  } else if (effect === 'iridescent_rainbow') {
    style = {
      background: 'linear-gradient(135deg, rgba(255,100,100,0.12) 0%, rgba(255,200,100,0.12) 20%, rgba(100,255,100,0.12) 40%, rgba(100,200,255,0.12) 60%, rgba(200,100,255,0.12) 80%, rgba(255,100,100,0.12) 100%)',
      backgroundSize: '400% 400%',
      mixBlendMode: 'overlay',
      animation: 'iridescentRainbowLoop 7s ease infinite'
    };
  } else if (effect === 'silver_glint') {
    style = {
      background: 'linear-gradient(90deg, rgba(255,255,255,0) 30%, rgba(255,255,255,0.35) 50%, rgba(255,255,255,0) 70%)',
      backgroundSize: '200% 100%',
      mixBlendMode: 'overlay',
      animation: 'silverGlintLoop 6s ease-in-out infinite'
    };
  } else if (effect === 'ambient_pulse') {
    style = {
      background: 'radial-gradient(circle at 10% 10%, rgba(255, 120, 40, 0.25) 0%, rgba(255, 180, 80, 0.08) 50%, transparent 100%)',
      mixBlendMode: 'screen',
      animation: 'ambientWarmPulseLoop 8s ease-in-out infinite'
    };
  } else if (effect === 'gold_flare') {
    style = {
      background: 'linear-gradient(110deg, rgba(212,175,55,0) 30%, rgba(212,175,55,0.2) 45%, rgba(255,235,160,0.4) 50%, rgba(212,175,55,0.2) 55%, rgba(212,175,55,0) 70%)',
      backgroundSize: '200% 100%',
      mixBlendMode: 'overlay',
      animation: 'goldFlareLoop 7s ease-in-out infinite'
    };
  } else if (effect === 'glass_reflection') {
    style = {
      background: 'linear-gradient(135deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0) 45%, rgba(255,255,255,0.12) 50%, rgba(255,255,255,0) 55%, rgba(255,255,255,0) 100%)',
      backgroundSize: '200% 200%',
      mixBlendMode: 'overlay',
      animation: 'glassReflectionLoop 8s linear infinite'
    };
  } else if (effect === 'cosmic_aurora') {
    style = {
      background: 'radial-gradient(circle at 80% 20%, rgba(0, 255, 230, 0.15) 0%, rgba(200, 0, 255, 0.1) 40%, transparent 80%)',
      mixBlendMode: 'screen',
      animation: 'cosmicAuroraLoop 12s ease-in-out infinite',
      width: '100%',
      height: '100%'
    };
  }

  if (style) {
    return {
      ...style,
      transform: 'translateZ(2px)',
      zIndex: 10
    };
  }
  return null;
};

// Global aspect ratio cache so any loaded image retains its natural aspect across re-renders and components
export const globalImageAspectCache = new Map();

/**
 * Synchronously or cached probe for an image source to obtain its natural aspect ratio.
 * Essential for WebKit / Mobile Safari where inline data URIs and cached images complete synchronously.
 */
export function getIntrinsicAspectFromSrc(src) {
  if (!src) return null;
  if (globalImageAspectCache.has(src)) return globalImageAspectCache.get(src);
  if (typeof Image !== 'undefined') {
    try {
      const probe = new Image();
      probe.src = src;
      if (probe.complete && probe.naturalWidth > 0 && probe.naturalHeight > 0) {
        const calculated = probe.naturalWidth / probe.naturalHeight;
        globalImageAspectCache.set(src, calculated);
        return calculated;
      }
    } catch (e) {
      // In SSR or non-browser environments
    }
  }
  return null;
}

export function resolveElementImageSrc(elem, options = {}, apiBase = '') {
  if (!elem) return '';
  const opts = typeof options === 'object' && options !== null ? options : {};
  let src = '';
  if ((opts.preferOriginal || elem.optimizeImage === false) && elem.originalSrc) {
    src = elem.originalSrc;
  } else if (elem.optimizeImage !== false && elem.optimizedSrc) {
    src = elem.optimizedSrc;
  } else {
    src = elem.src;
  }
  if (!src) return '';

  const wantsOptimize = (elem.optimizeImage !== false && !options.preferOriginal);

  // Normalize uploads path to current API_BASE and avoid mixed-content or foreign dev hosts
  if (src.includes('/uploads/')) {
    const pathPart = src.substring(src.indexOf('/uploads/'));
    const cleanPath = pathPart.split('?')[0];
    const query = pathPart.includes('?') ? pathPart.substring(pathPart.indexOf('?')) : '';
    const base = apiBase ? apiBase.replace(/\/+$/, '') : '';
    let separator = query ? '&' : '?';
    let finalUrl = `${base}${cleanPath}${query}${separator}cors=true`;
    if (wantsOptimize && !cleanPath.toLowerCase().endsWith('.svg') && !cleanPath.includes('opt_') && !finalUrl.includes('optimize=')) {
      finalUrl += '&optimize=true';
    }
    return finalUrl;
  }

  if (!src.startsWith('data:')) {
    const separator = src.includes('?') ? '&' : '?';
    return `${src}${separator}cors=true`;
  }
  return src || '';
}

export const getResolvedImageSrc = resolveElementImageSrc;

/**
 * Computes exact concentric border radii for framed elements (e.g. image frames).
 * Ensures inner layers and pictures follow the true inner radius of the frame without clipping
 * into corners or leaving black gaps between the border and the picture.
 */
export function calculateFrameBorderRadii(elem) {
  if (!elem) {
    return { rOuter: 0, rGap: 0, rPrimaryOuter: 0, rInner: 0 };
  }
  const hasBorder = elem.hasBorder !== false;
  const rawRadius = hasBorder ? (parseFloat(elem.borderRadius) || 0) : 0;
  const hasSecondBorder = hasBorder && Boolean(elem.hasSecondBorder);
  const strokeWidth2 = hasSecondBorder ? (parseFloat(elem.strokeWidth2 !== undefined ? elem.strokeWidth2 : 2) || 0) : 0;
  const borderGap = hasSecondBorder ? (parseFloat(elem.borderGap || 0) || 0) : 0;
  const primaryStroke = (hasBorder && (elem.strokeWidth !== undefined ? parseFloat(elem.strokeWidth) : 1) > 0)
    ? (elem.strokeWidth !== undefined ? parseFloat(elem.strokeWidth) : 1)
    : 0;

  const rOuter = Math.max(0, rawRadius);
  const rGap = Math.max(0, rOuter - strokeWidth2);
  const rPrimaryOuter = Math.max(0, rOuter - strokeWidth2 - borderGap);
  const rInner = Math.max(0, rPrimaryOuter - primaryStroke);

  return { rOuter, rGap, rPrimaryOuter, rInner };
}

export function PictureFrameImage({ elem, resolvedSrc, filterStyle }) {
  const imgRef = useRef(null);

  const resolvedFilterStyle = (() => {
    const baseFilterObj = filterStyle || getImageFilterStyle(elem);
    const hue = elem?.hue !== undefined ? (parseFloat(elem.hue) || 0) : 0;
    const saturation = elem?.saturation !== undefined && elem?.saturation !== '' && !Number.isNaN(parseFloat(elem.saturation))
      ? parseFloat(elem.saturation)
      : 100;
    const extraFilters = [];
    const existingFilter = baseFilterObj?.filter || '';
    if (hue !== 0 && !existingFilter.includes(`hue-rotate(${hue}deg)`)) {
      extraFilters.push(`hue-rotate(${hue}deg)`);
    }
    if (saturation !== 100 && !existingFilter.includes(`saturate(${saturation}%)`)) {
      extraFilters.push(`saturate(${saturation}%)`);
    }
    const combinedFilter = [existingFilter, ...extraFilters].filter(Boolean).join(' ');
    return combinedFilter ? { ...baseFilterObj, filter: combinedFilter } : (baseFilterObj || {});
  })();

  // A dynamic image is one loaded from a variable ($image_source) or where resolvedSrc differs from elem.src.
  // Template snapshot values (elem.imgAspect, elem.naturalWidth, etc.) describe the template designer's
  // placeholder image and MUST NOT be used to layout a dynamic user-uploaded image!
  const isDynamic = Boolean(elem?.imageVariable) || (elem?.src && resolvedSrc && elem.src !== resolvedSrc);

  const [aspect, setAspect] = useState(() => {
    if (resolvedSrc) {
      const intrinsic = getIntrinsicAspectFromSrc(resolvedSrc);
      if (intrinsic) return intrinsic;
    }
    // Only use template elem.imgAspect if it's NOT a dynamic variable replacement
    if (!isDynamic) {
      if (elem?.imgAspect) return elem.imgAspect;
      if (elem?.naturalWidth && elem?.naturalHeight) return elem.naturalWidth / elem.naturalHeight;
    }
    return null;
  });

  const checkImgElement = useCallback(() => {
    const img = imgRef.current;
    if (img && img.complete && img.naturalWidth > 0 && img.naturalHeight > 0) {
      const calculated = img.naturalWidth / img.naturalHeight;
      if (resolvedSrc) {
        globalImageAspectCache.set(resolvedSrc, calculated);
      }
      setAspect((prev) => (!prev || Math.abs(prev - calculated) > 0.001 ? calculated : prev));
    }
  }, [resolvedSrc]);

  // Synchronously measure on DOM mount if image completed synchronously (Mobile Safari / WebKit data-URI behavior)
  useLayoutEffect(() => {
    checkImgElement();
  }, [checkImgElement, resolvedSrc]);

  // Background probe ensures aspect ratio is resolved even if the browser suppresses synthetic onLoad
  useEffect(() => {
    if (!resolvedSrc) return;
    if (globalImageAspectCache.has(resolvedSrc)) {
      setAspect(globalImageAspectCache.get(resolvedSrc));
      return;
    }

    let active = true;
    if (typeof Image !== 'undefined') {
      try {
        const probe = new Image();
        probe.onload = () => {
          if (!active) return;
          if (probe.naturalWidth > 0 && probe.naturalHeight > 0) {
            const calculated = probe.naturalWidth / probe.naturalHeight;
            globalImageAspectCache.set(resolvedSrc, calculated);
            setAspect((prev) => (!prev || Math.abs(prev - calculated) > 0.001 ? calculated : prev));
          }
        };
        probe.src = resolvedSrc;
        if (probe.complete && probe.naturalWidth > 0 && probe.naturalHeight > 0) {
          const calculated = probe.naturalWidth / probe.naturalHeight;
          globalImageAspectCache.set(resolvedSrc, calculated);
          setAspect((prev) => (!prev || Math.abs(prev - calculated) > 0.001 ? calculated : prev));
        }
      } catch (err) {}
    }

    return () => {
      active = false;
    };
  }, [resolvedSrc]);

  const handleImageLoad = (e) => {
    const nw = e?.target?.naturalWidth || e?.currentTarget?.naturalWidth;
    const nh = e?.target?.naturalHeight || e?.currentTarget?.naturalHeight;
    if (nw && nh) {
      const calculatedAspect = nw / nh;
      if (resolvedSrc) {
        globalImageAspectCache.set(resolvedSrc, calculatedAspect);
      }
      setAspect((prev) => (!prev || Math.abs(prev - calculatedAspect) > 0.001 ? calculatedAspect : prev));
    }
  };

  const handleImageError = (e) => {
    const fallback = elem?.originalSrc || elem?.src;
    if (fallback && e?.currentTarget && e.currentTarget.src !== fallback) {
      e.currentTarget.src = fallback;
    }
  };

  if (!elem?.cropApplied) {
    return (
      <img
        ref={imgRef}
        src={resolvedSrc}
        crossOrigin="anonymous"
        alt="Subject Frame"
        onLoad={handleImageLoad}
        onError={handleImageError}
        style={{
          width: '100%',
          height: '100%',
          objectFit: 'contain',
          position: 'relative',
          zIndex: 1,
          transform: `${elem?.flipH ? 'scaleX(-1)' : ''} ${elem?.flipV ? 'scaleY(-1)' : ''}`.trim() || 'none',
          ...resolvedFilterStyle
        }}
      />
    );
  }

  const frameW = elem?.width || 100;
  const frameH = elem?.height || 100;
  const frameAspect = frameW / frameH;

  // Resolve true image aspect ratio:
  // 1. Measured aspect from state
  // 2. Cached aspect for this exact image source
  // 3. Template snapshot aspect (ONLY for static non-variable images)
  // 4. Default to frameAspect (100% width, 100% height) to prevent squishing before image decodes
  const imgAspect = aspect || (resolvedSrc && globalImageAspectCache.get(resolvedSrc)) || (!isDynamic ? elem?.imgAspect : null) || frameAspect;

  let widthStyle = '100%';
  let heightStyle = '100%';

  if (imgAspect < frameAspect) {
    // Portrait image in wider frame: width covers frame (100%), height extends above/below
    widthStyle = '100%';
    heightStyle = `${(frameAspect / imgAspect) * 100}%`;
  } else {
    // Landscape image in taller frame: height covers frame (100%), width extends left/right
    widthStyle = `${(imgAspect / frameAspect) * 100}%`;
    heightStyle = '100%';
  }

  return (
    <img
      ref={imgRef}
      src={resolvedSrc}
      crossOrigin="anonymous"
      alt="Subject Frame"
      onLoad={handleImageLoad}
      onError={handleImageError}
      style={{
        position: 'absolute',
        left: `${50 + (parseFloat(elem?.imgPanX) || 0)}%`,
        top: `${50 + (parseFloat(elem?.imgPanY) || 0)}%`,
        width: widthStyle,
        height: heightStyle,
        maxWidth: 'none',
        maxHeight: 'none',
        objectFit: 'cover',
        zIndex: 1,
        transform: `translate(-50%, -50%) scale(${elem?.imgZoom || 1}) ${elem?.flipH ? 'scaleX(-1)' : ''} ${elem?.flipV ? 'scaleY(-1)' : ''}`.trim() || 'none',
        transformOrigin: 'center center',
        ...resolvedFilterStyle
      }}
    />
  );
}

export const canonicalConfigStringify = (obj) => {
  if (obj === null || typeof obj !== 'object') {
    return JSON.stringify(obj);
  }
  if (Array.isArray(obj)) {
    return '[' + obj.map(item => canonicalConfigStringify(item === undefined ? null : item)).join(',') + ']';
  }
  const keys = Object.keys(obj).filter(k => obj[k] !== undefined).sort();
  return '{' + keys.map(k => JSON.stringify(k) + ':' + canonicalConfigStringify(obj[k])).join(',') + '}';
};

/**
 * Pure vector SVG renderer for Certify elements (stamp, rectangle, ribbon, watermark).
 * Scales with mathematical vector precision across all print DPIs (300/600 DPI) and displays.
 */
export const renderCertifyElement = (elem) => {
  if (!elem) return null;
  const textVal = elem.certifyText !== undefined ? elem.certifyText : 'TROPHY\nCERTIFIED';
  const fillVal = elem.fill || '#ef4444';
  const type = elem.certifyType || 'stamp';
  const fontMultiplier = (elem.certifyFontSize !== undefined ? elem.certifyFontSize : 100) / 100;
  const isGradient = typeof fillVal === 'string' && (fillVal.includes('gradient') || elem.fillType === 'gradient');
  const gradId = `certify-grad-${elem.id || 'default'}`;

  let stop1 = '#ef4444';
  let stop2 = '#b91c1c';
  if (isGradient) {
    const colors = fillVal.match(/(#[0-9a-fA-F]{3,8}|rgba?\([^)]+\))/g);
    if (colors && colors.length > 0) {
      stop1 = colors[0];
      stop2 = colors[colors.length - 1] || colors[0];
    }
  }
  const paintColor = isGradient ? `url(#${gradId})` : fillVal;
  const certifyFontFamily = "'Arial Black', 'Impact', 'Montserrat', -apple-system, BlinkMacSystemFont, sans-serif";

  if (type === 'stamp') {
    const rawLines = textVal.split('\n').map(l => l.trim()).filter(Boolean);
    const lines = rawLines.length > 0 ? rawLines : ['CERTIFIED'];
    const maxChars = Math.max(...lines.map(l => l.length), 1);

    let baseSize = 22;
    if (lines.length === 1) baseSize = 28;
    else if (lines.length === 2) baseSize = 22;
    else if (lines.length === 3) baseSize = 16;
    else baseSize = 13;

    const maxFitSize = Math.floor(142 / (maxChars * 0.65));
    const targetSize = Math.min(baseSize, maxFitSize);
    const fontSize = Math.max(9, Math.round(targetSize * fontMultiplier));
    const lineHeight = fontSize * 1.18;
    const totalBlockHeight = (lines.length - 1) * lineHeight;
    const startY = 100 - (totalBlockHeight / 2);

    return (
      <svg
        viewBox="0 0 200 200"
        width="100%"
        height="100%"
        preserveAspectRatio="xMidYMid meet"
        style={{ width: '100%', height: '100%', display: 'block', overflow: 'visible', pointerEvents: 'none' }}
        xmlns="http://www.w3.org/2000/svg"
      >
        {isGradient && (
          <defs>
            <linearGradient id={gradId} x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor={stop1} />
              <stop offset="100%" stopColor={stop2} />
            </linearGradient>
          </defs>
        )}
        <circle cx="100" cy="100" r="94" fill="none" stroke={paintColor} strokeWidth="4.5" />
        <circle cx="100" cy="100" r="86" fill="none" stroke={paintColor} strokeWidth="1.8" />
        <text
          x="100"
          y="43"
          textAnchor="middle"
          dominantBaseline="central"
          fill={paintColor}
          fontSize="14"
          fontWeight="800"
          fontFamily={certifyFontFamily}
          letterSpacing="3"
        >
          OFFICIAL
        </text>
        <line x1="52" y1="53" x2="148" y2="53" stroke={paintColor} strokeWidth="1.5" />
        {lines.map((line, idx) => (
          <text
            key={idx}
            x="100"
            y={startY + idx * lineHeight}
            textAnchor="middle"
            dominantBaseline="central"
            fill={paintColor}
            fontSize={fontSize}
            fontWeight="900"
            fontFamily={certifyFontFamily}
          >
            {line}
          </text>
        ))}
        <line x1="52" y1="147" x2="148" y2="147" stroke={paintColor} strokeWidth="1.5" />
        <text
          x="100"
          y="163"
          textAnchor="middle"
          dominantBaseline="central"
          fill={paintColor}
          fontSize="13"
          letterSpacing="4"
        >
          ★★★
        </text>
      </svg>
    );
  }

  if (type === 'rectangle') {
    const rawLines = textVal.split('\n').map(l => l.trim()).filter(Boolean);
    const lines = rawLines.length > 0 ? rawLines : ['CERTIFIED'];
    const maxChars = Math.max(...lines.map(l => l.length), 1);

    let baseSize = 18;
    if (lines.length === 1) baseSize = 24;
    else if (lines.length === 2) baseSize = 18;
    else if (lines.length === 3) baseSize = 14;
    else baseSize = 11;

    const maxFitSize = Math.floor(165 / (maxChars * 0.65));
    const targetSize = Math.min(baseSize, maxFitSize);
    const fontSize = Math.max(8, Math.round(targetSize * fontMultiplier));
    const lineHeight = fontSize * 1.18;
    const totalBlockHeight = (lines.length - 1) * lineHeight;
    const startY = 62.5 - (totalBlockHeight / 2);

    return (
      <svg
        viewBox="0 0 200 120"
        width="100%"
        height="100%"
        preserveAspectRatio="none"
        style={{ width: '100%', height: '100%', display: 'block', overflow: 'visible', pointerEvents: 'none' }}
        xmlns="http://www.w3.org/2000/svg"
      >
        {isGradient && (
          <defs>
            <linearGradient id={gradId} x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor={stop1} />
              <stop offset="100%" stopColor={stop2} />
            </linearGradient>
          </defs>
        )}
        <rect x="4" y="4" width="192" height="112" rx="6" ry="6" fill="none" stroke={paintColor} strokeWidth="4" />
        <rect x="9" y="9" width="182" height="102" rx="3" ry="3" fill="none" stroke={paintColor} strokeWidth="1.5" />
        <text
          x="100"
          y="26"
          textAnchor="middle"
          dominantBaseline="central"
          fill={paintColor}
          fontSize="13"
          fontWeight="800"
          fontFamily={certifyFontFamily}
          letterSpacing="3"
        >
          OFFICIAL
        </text>
        <line x1="30" y1="35" x2="170" y2="35" stroke={paintColor} strokeWidth="1.2" />
        {lines.map((line, idx) => (
          <text
            key={idx}
            x="100"
            y={startY + idx * lineHeight}
            textAnchor="middle"
            dominantBaseline="central"
            fill={paintColor}
            fontSize={fontSize}
            fontWeight="900"
            fontFamily={certifyFontFamily}
          >
            {line}
          </text>
        ))}
        <line x1="30" y1="90" x2="170" y2="90" stroke={paintColor} strokeWidth="1.2" />
        <text
          x="100"
          y="102"
          textAnchor="middle"
          dominantBaseline="central"
          fill={paintColor}
          fontSize="11"
          letterSpacing="4"
        >
          ★★★
        </text>
      </svg>
    );
  }

  if (type === 'ribbon') {
    const fullText = textVal.split('\n').join(' ').toUpperCase().trim() || 'TROPHY CERTIFIED';
    const maxChars = Math.max(fullText.length, 1);
    const baseSize = 25;
    const maxFitSize = Math.floor(275 / (maxChars * 0.62));
    const targetSize = Math.min(baseSize, maxFitSize);
    const fontSize = Math.max(9, Math.round(targetSize * fontMultiplier));

    return (
      <svg
        viewBox="0 0 300 60"
        width="100%"
        height="100%"
        preserveAspectRatio="none"
        style={{
          width: '100%',
          height: '100%',
          display: 'block',
          overflow: 'visible',
          pointerEvents: 'none',
          filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.35))'
        }}
        xmlns="http://www.w3.org/2000/svg"
      >
        {isGradient && (
          <defs>
            <linearGradient id={gradId} x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor={stop1} />
              <stop offset="100%" stopColor={stop2} />
            </linearGradient>
          </defs>
        )}
        <rect x="0" y="0" width="300" height="60" fill={paintColor} />
        <line x1="0" y1="4" x2="300" y2="4" stroke="rgba(255,255,255,0.45)" strokeWidth="2" />
        <line x1="0" y1="56" x2="300" y2="56" stroke="rgba(255,255,255,0.45)" strokeWidth="2" />
        <text
          x="150"
          y="30"
          textAnchor="middle"
          dominantBaseline="central"
          fill="#ffffff"
          fontSize={fontSize}
          fontWeight="900"
          letterSpacing="2"
          fontFamily={certifyFontFamily}
        >
          {fullText}
        </text>
      </svg>
    );
  }

  if (type === 'watermark') {
    const rawLines = textVal.split('\n').map(l => l.trim()).filter(Boolean);
    const lines = rawLines.length > 0 ? rawLines : ['CERTIFIED'];
    const maxChars = Math.max(...lines.map(l => l.length), 1);

    let baseSize = 34;
    if (lines.length === 1) baseSize = 46;
    else if (lines.length === 2) baseSize = 34;
    else if (lines.length === 3) baseSize = 23;
    else baseSize = 16;

    const maxFitSize = Math.floor(280 / (maxChars * 0.65));
    const targetSize = Math.min(baseSize, maxFitSize);
    const fontSize = Math.max(10, Math.round(targetSize * fontMultiplier));
    const lineHeight = fontSize * 1.15;
    const totalBlockHeight = (lines.length - 1) * lineHeight;
    const startY = 50 - (totalBlockHeight / 2);

    return (
      <svg
        viewBox="0 0 300 100"
        width="100%"
        height="100%"
        preserveAspectRatio="xMidYMid meet"
        style={{ width: '100%', height: '100%', display: 'block', overflow: 'visible', pointerEvents: 'none' }}
        xmlns="http://www.w3.org/2000/svg"
      >
        {isGradient && (
          <defs>
            <linearGradient id={gradId} x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor={stop1} />
              <stop offset="100%" stopColor={stop2} />
            </linearGradient>
          </defs>
        )}
        {lines.map((line, idx) => (
          <text
            key={idx}
            x="150"
            y={startY + idx * lineHeight}
            textAnchor="middle"
            dominantBaseline="central"
            fill={paintColor}
            opacity="0.32"
            fontSize={fontSize}
            fontWeight="900"
            letterSpacing="3"
            fontFamily={certifyFontFamily}
          >
            {line.toUpperCase()}
          </text>
        ))}
      </svg>
    );
  }

  return null;
};

export default function TemplateDesigner({ 
  user, 
  tenantConfig, 
  initialDesignId = null, 
  onClose = null, 
  onSaveSuccess = null, 
  isTrophyCardMode = false,
  tenants = [],
  selectedTenant = ''
}) {
  const token = sessionStorage.getItem('token') || localStorage.getItem('token');
  const isDev = window.location.port === '3000';
  const API_BASE = isDev ? `http://${window.location.hostname}:8000` : '';
  const modal = useModal();

  // Workspace View State
  const [viewMode, setViewMode] = useState('manager'); // 'manager' | 'designer'
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saveStatus, setSaveStatus] = useState('');
  const [saveStepMessage, setSaveStepMessage] = useState('');
  const [statusError, setStatusError] = useState('');
  const [selectedGridTemplateId, setSelectedGridTemplateId] = useState(null);
  const [flippedTemplateIds, setFlippedTemplateIds] = useState([]);
  const [templateSearchQuery, setTemplateSearchQuery] = useState('');
  const [gridZoom, setGridZoom] = useState(() => {
    const saved = localStorage.getItem('templateGridZoom');
    return saved ? Number(saved) : 100;
  });
  const [showExitConfirmModal, setShowExitConfirmModal] = useState(false);
  const [datasetsPanelExpanded, setDatasetsPanelExpanded] = useState(true);
  const [dynamicLinksPanelExpanded, setDynamicLinksPanelExpanded] = useState(false);
  const [templateFilter, setTemplateFilter] = useState('all'); // 'all' | 'system'
  const [showShareModal, setShowShareModal] = useState(false);
  const [sharingTemplate, setSharingTemplate] = useState(null);
  const [shareAllTenantUsers, setShareAllTenantUsers] = useState(true);
  const [availableCompanies, setAvailableCompanies] = useState([]);
  const [sharedCompanies, setSharedCompanies] = useState([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState('');
  const [sharedUserEmails, setSharedUserEmails] = useState([]);
  const [newShareEmail, setNewShareEmail] = useState('');
  const [unissuedPrompt, setUnissuedPrompt] = useState(null); // { templateId, templateName, count, coinNumbers }
  const [syncingUnissued, setSyncingUnissued] = useState(false);
  const [syncUnissuedError, setSyncUnissuedError] = useState('');
  const [showTemplateUsageInfo, setShowTemplateUsageInfo] = useState(false);
  const [templateUsageStats, setTemplateUsageStats] = useState({});
  const [loadingUsageStats, setLoadingUsageStats] = useState(false);
  const originalConfigRef = useRef(null);

  const isAdmin = Boolean(user && ["GLOBAL_ADMIN", "GLOBAL_POWER", "GLOBAL_STANDARD", "TENANT_ADMIN"].includes(user.role));
  const isGlobalRole = Boolean(user && ["GLOBAL_ADMIN", "GLOBAL_POWER", "GLOBAL_STANDARD"].includes(user.role));
  const canExportImport = isGlobalRole || user?.role === "TENANT_ADMIN";

  // Export & Import states
  const [isExporting, setIsExporting] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [importData, setImportData] = useState(null);
  const [importFileName, setImportFileName] = useState('');
  const [importTargetTenantId, setImportTargetTenantId] = useState(selectedTenant ? selectedTenant.toString() : '');
  const [importCardName, setImportCardName] = useState('');
  const [importLoading, setImportLoading] = useState(false);
  const [importError, setImportError] = useState('');
  const [importSuccess, setImportSuccess] = useState('');
  const [tenantList, setTenantList] = useState(tenants || []);

  // Dataset verification and Import states
  const [importVerifyReport, setImportVerifyReport] = useState(null);
  const [importVerifying, setImportVerifying] = useState(false);
  const [importIsGlobalScope, setImportIsGlobalScope] = useState(false);
  const [importAutoCreateDatasets, setImportAutoCreateDatasets] = useState(true);

  // Push to Production states
  const [showPushModal, setShowPushModal] = useState(false);
  const [pushTemplateId, setPushTemplateId] = useState(null);
  const [pushTargetUrl, setPushTargetUrl] = useState(() => localStorage.getItem('trophycoin_prod_push_url') || 'https://trophycoin-app-cxyiczjsma-uc.a.run.app');
  const [pushRemoteToken, setPushRemoteToken] = useState(() => localStorage.getItem('trophycoin_prod_push_token') || '');
  const [pushRemoteTenants, setPushRemoteTenants] = useState([]);
  const [pushSelectedScope, setPushSelectedScope] = useState('global');
  const [pushLoadingTenants, setPushLoadingTenants] = useState(false);
  const [pushLoading, setPushLoading] = useState(false);
  const [pushVerifying, setPushVerifying] = useState(false);
  const [pushVerifyReport, setPushVerifyReport] = useState(null);
  const [pushError, setPushError] = useState('');
  const [pushSuccess, setPushSuccess] = useState('');
  const [pushAutoCreateDatasets, setPushAutoCreateDatasets] = useState(true);

  // High-Resolution Print Export states (Epson EcoTank L8160)
  const [showPrintExportModal, setShowPrintExportModal] = useState(false);
  const [isPrintExporting, setIsPrintExporting] = useState(false);
  const [printExportStep, setPrintExportStep] = useState('');
  const [printExportError, setPrintExportError] = useState('');
  const [printExportSuccess, setPrintExportSuccess] = useState(false);

  // ----------------------------------------------------
  // DESIGNER STATE VARIABLES
  // ----------------------------------------------------
  const [selectedTemplateId, setSelectedTemplateId] = useState(null);
  const [designStatus, setDesignStatus] = useState(null);
  const [designIsTemplate, setDesignIsTemplate] = useState(true);
  const [isSystemTemplate, setIsSystemTemplate] = useState(false);
  const [autoOptimizeImages, setAutoOptimizeImages] = useState(true);
  const [templateName, setTemplateName] = useState('New Template');
  const [activeSide, setActiveSide] = useState('front'); // 'front' | 'back'
  const [activeTool, setActiveTool] = useState('select'); // 'select' | 'shape' | 'image' | 'text' | 'datagrid' | 'accent'
  const [selectedElementId, setSelectedElementId] = useState(null);
  const [previewFlipped, setPreviewFlipped] = useState(false);
  const [show3DPreview, setShow3DPreview] = useState(false);
  const [labelStylingExpanded, setLabelStylingExpanded] = useState(false);
  const [valueStylingExpanded, setValueStylingExpanded] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [activeDatasetKey, setActiveDatasetKey] = useState('');
  const [datasets, setDatasets] = useState(DATASETS);
  const [linkedDatasets, setLinkedDatasets] = useState([]);
  const [enabledFields, setEnabledFields] = useState({});
  const [hiddenFields, setHiddenFields] = useState({});
  const [expandedDatasets, setExpandedDatasets] = useState({});
  const [editingTextElementId, setEditingTextElementId] = useState(null);
  const [renamingLayerId, setRenamingLayerId] = useState(null);
  const [renameValue, setRenameValue] = useState('');
  const [draggedLayerIndex, setDraggedLayerIndex] = useState(null);
  const [refreshingData, setRefreshingData] = useState(false);
  
  // Right side panel active tab
  const [activeRightTab, setActiveRightTab] = useState('layers'); // 'layers' | 'transform' | 'settings'
  const [showOpacitySlider, setShowOpacitySlider] = useState(false);
  const [showCardActionsDropdown, setShowCardActionsDropdown] = useState(false);
  const [showEditorActionsDropdown, setShowEditorActionsDropdown] = useState(false);
  const [showGridZoomSlider, setShowGridZoomSlider] = useState(false);
  const [editingGlobalColor, setEditingGlobalColor] = useState(null);
  const [localColorVal, setLocalColorVal] = useState('');
  const [isUploadingAsset, setIsUploadingAsset] = useState(false);
  const [assetUploadError, setAssetUploadError] = useState('');
  const [assetUploadSuccess, setAssetUploadSuccess] = useState('');
  const [isAssetDragOver, setIsAssetDragOver] = useState(false);
  const assetFileInputRef = useRef(null);
  
  // Undo/Redo Stacks
  const [undoStack, setUndoStack] = useState([]);
  const [redoStack, setRedoStack] = useState([]);

  // Universal Top Banner Slide-in Notification Observers
  useEffect(() => {
    if (saveStatus && saveStatus !== 'Saving draft...') {
      modal.notify(saveStatus, 'success');
    }
  }, [saveStatus, modal]);

  useEffect(() => {
    if (statusError) modal.notify(statusError, 'error');
  }, [statusError, modal]);

  useEffect(() => {
    if (importSuccess) modal.notify(importSuccess, 'success');
  }, [importSuccess, modal]);

  useEffect(() => {
    if (pushSuccess) modal.notify(pushSuccess, 'success');
  }, [pushSuccess, modal]);

  useEffect(() => {
    if (assetUploadSuccess) modal.notify(assetUploadSuccess, 'success');
  }, [assetUploadSuccess, modal]);

  useEffect(() => {
    if (assetUploadError) modal.notify(assetUploadError, 'error');
  }, [assetUploadError, modal]);

  useEffect(() => {
    if (syncUnissuedError) modal.notify(syncUnissuedError, 'error');
  }, [syncUnissuedError, modal]);

  // Canvas zoom
  const [zoomLevel, setZoomLevel] = useState(0.85); // Scale multiplier

  // Designer properties structure
  const [cardDimensions, setCardDimensions] = useState({ width: 400, height: 560 });
  const [globalStyles, setGlobalStyles] = useState({
    borderRadius: '18px',
    theme: 'gold-dark',
    borderColor: '#c9a054',
    borderWidth: 6,
    borderStyle: 'solid'
  });
  
  const [sides, setSides] = useState({
    front: {
      background: { type: 'gradient', value: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)' },
      elements: []
    },
    back: {
      background: { type: 'color', value: '#111111' },
      elements: []
    }
  });

  // Dynamic User / Organization Mapping & Dataset Ordering States
  const [fieldMappings, setFieldMappings] = useState({});
  const [lockedOptions, setLockedOptions] = useState({});
  const [requiredFields, setRequiredFields] = useState({});
  const [linkMappings, setLinkMappings] = useState({});
  const [dateFormats, setDateFormats] = useState({});
  const [formDatasetOrder, setFormDatasetOrder] = useState([]);
  const [collapsedFormDatasets, setCollapsedFormDatasets] = useState({});
  const [hidePrepopulatedDatasets, setHidePrepopulatedDatasets] = useState(false);
  const [orgData, setOrgData] = useState(null);

  // AI Design Copilot State
  const [showAiPanel, setShowAiPanel] = useState(() => {
    try {
      return localStorage.getItem('trophycoin_ai_panel_open_card') === 'true';
    } catch (e) {
      return false;
    }
  });
  const [isAiServerAvailable, setIsAiServerAvailable] = useState(false);

  useEffect(() => {
    try {
      localStorage.setItem('trophycoin_ai_panel_open_card', showAiPanel ? 'true' : 'false');
    } catch (e) {}
  }, [showAiPanel]);

  const hasGeminiKey = Boolean(
    (
      (selectedTenant && typeof selectedTenant === 'object' ? selectedTenant.gemini_api_key : null) ||
      (tenants && tenants.length > 0 && selectedTenant ? tenants.find(t => t.id.toString() === selectedTenant.toString())?.gemini_api_key : null) ||
      tenantConfig?.gemini_api_key ||
      user?.tenant?.gemini_api_key ||
      ''
    ).trim()
  ) || isAiServerAvailable;

  useEffect(() => {
    let isMounted = true;
    const checkAiStatus = async () => {
      try {
        const tId = (selectedTenant && typeof selectedTenant === 'object' ? selectedTenant.id : selectedTenant) || tenantConfig?.id || user?.tenant_id;
        const url = `${API_BASE}/api/v1/ai-designer/status${tId ? `?tenant_id=${tId}` : ''}`;
        const res = await fetch(url, {
          headers: {
            'Authorization': token ? `Bearer ${token}` : '',
            'X-Tenant-Domain': window.location.hostname
          }
        });
        if (res.ok && isMounted) {
          const data = await res.json();
          setIsAiServerAvailable(Boolean(data.available));
        }
      } catch (e) {}
    };
    if (viewMode === 'designer') {
      checkAiStatus();
    }
    return () => { isMounted = false; };
  }, [selectedTenant, tenantConfig, user, token, API_BASE, viewMode]);

  const handleApplyAiCardMutations = (mutations) => {
    if (!mutations) return;

    if (mutations.__revert_state) {
      saveHistoryState();
      const prev = mutations.__revert_state;
      if (prev.globalStyles) setGlobalStyles(prev.globalStyles);
      if (prev.sides) setSides(prev.sides);
      if (prev.cardDimensions) setCardDimensions(prev.cardDimensions);
      if (prev.fieldMappings) setFieldMappings(prev.fieldMappings);
      return;
    }

    saveHistoryState();

    if (mutations.globalStyles) {
      setGlobalStyles(prev => ({
        ...prev,
        ...mutations.globalStyles
      }));
    }

    if (mutations.cardDimensions) {
      setCardDimensions(prev => ({
        ...prev,
        ...mutations.cardDimensions
      }));
    }

    const targetSide = mutations.apply_side === 'back' ? 'back' : activeSide;

    setSides(prevSides => {
      let currentElements = [...(prevSides[targetSide]?.elements || [])];

      if (mutations.replace_elements && mutations.elements) {
        currentElements = mutations.elements;
      } else {
        if (mutations.updated_elements && Array.isArray(mutations.updated_elements)) {
          const updateMap = new Map(mutations.updated_elements.map(u => [u.id, u]));
          currentElements = currentElements.map(el => {
            if (updateMap.has(el.id)) {
              return { ...el, ...updateMap.get(el.id) };
            }
            return el;
          });
        }

        if (mutations.added_elements && Array.isArray(mutations.added_elements)) {
          const additions = mutations.added_elements.map((item, idx) => ({
            ...item,
            id: item.id || `elem_ai_${Date.now()}_${idx}`
          }));
          currentElements = [...currentElements, ...additions];
        }
      }

      return {
        ...prevSides,
        [targetSide]: {
          ...prevSides[targetSide],
          elements: currentElements
        }
      };
    });
  };

  useEffect(() => {
    if (token && viewMode === 'designer' && user?.organization_id) {
      fetch(`${API_BASE}/api/v1/organizations/me`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-Tenant-Domain': window.location.hostname
        }
      })
        .then(res => res.ok ? res.json() : null)
        .then(data => {
          if (data) setOrgData(data);
        })
        .catch(err => console.log('No organization found for current user', err));
    }
  }, [token, API_BASE, viewMode, user?.organization_id]);

  // Shape creation options
  const [selectedShapeSubtype, setSelectedShapeSubtype] = useState('rect'); // 'rect' | 'circle' | 'pill'
  const [showShapeMenu, setShowShapeMenu] = useState(false);
  const [showImageMenu, setShowImageMenu] = useState(false);
  const [selectedImageSubTool, setSelectedImageSubTool] = useState('frame'); // 'frame' | 'image'
  const [selectedDividerSubTool, setSelectedDividerSubTool] = useState('horizontal'); // 'horizontal' | 'vertical'
  const [showDividerMenu, setShowDividerMenu] = useState(false);
  const [showAssetSelectorModal, setShowAssetSelectorModal] = useState(false);
  const [pendingImageClickCoords, setPendingImageClickCoords] = useState(null);
  const [uploadedAssets, setUploadedAssets] = useState([]);
  const [isChangingImageSource, setIsChangingImageSource] = useState(false);
  const [assetSearchQuery, setAssetSearchQuery] = useState('');

  // Picture Frame Image Pan & Zoom states
  const [showImageAdjustModal, setShowImageAdjustModal] = useState(false);
  const [adjustingElement, setAdjustingElement] = useState(null);
  const [tempPanX, setTempPanX] = useState(0);
  const [tempPanY, setTempPanY] = useState(0);
  const [tempZoom, setTempZoom] = useState(1);
  const [tempFrameW, setTempFrameW] = useState(160);
  const [tempFrameH, setTempFrameH] = useState(120);
  const [lockCropAspect, setLockCropAspect] = useState(false);
  const [selectedCropPreset, setSelectedCropPreset] = useState('custom');
  const [imgNaturalSize, setImgNaturalSize] = useState({ w: 0, h: 0 });
  const isPanningRef = useRef(false);
  const panStartRef = useRef({ startX: 0, startY: 0, initialPanX: 0, initialPanY: 0 });
  const isCropResizingRef = useRef(false);
  const cropResizeStartRef = useRef(null);
  const [bordersExpanded, setBordersExpanded] = useState(true);
  const [fillExpanded, setFillExpanded] = useState(true);
  const [gridExpanded, setGridExpanded] = useState(true);
  const [characterExpanded, setCharacterExpanded] = useState(true);
  const [dynamicDataExpanded, setDynamicDataExpanded] = useState(true);
  const [newStaticText, setNewStaticText] = useState('');
  const [shadowExpanded, setShadowExpanded] = useState(true);
  const [glowExpanded, setGlowExpanded] = useState(true);
  const [showGradientModal, setShowGradientModal] = useState(false);
  const [certifySettingsExpanded, setCertifySettingsExpanded] = useState(true);
  const [qrSettingsExpanded, setQrSettingsExpanded] = useState(true);
  const [showSequenceModal, setShowSequenceModal] = useState(false);
  const [showGridDataModal, setShowGridDataModal] = useState(false);
  const [selectedDatasetKey, setSelectedDatasetKey] = useState('');
  const [selectedFieldLabel, setSelectedFieldLabel] = useState('');
  const [resampleExpanded, setResampleExpanded] = useState(false);
  const [resamplingLoading, setResamplingLoading] = useState(false);
  const [resampleQuality, setResampleQuality] = useState(88);
  const [resampleMaxDim, setResampleMaxDim] = useState(2048);
  const [resampleMsg, setResampleMsg] = useState(null);
  const [gradientState, setGradientState] = useState({
    type: 'linear',
    angle: 90,
    stops: [
      { color: '#ffffff', position: 0, opacity: 1 },
      { color: '#000000', position: 100, opacity: 1 }
    ]
  });
  const [selectedStopIndex, setSelectedStopIndex] = useState(0);
  const [editingGradientTarget, setEditingGradientTarget] = useState('primary'); // 'primary' | 'secondary'

  const [gradientWindowPos, setGradientWindowPos] = useState({ x: 200, y: 80 });
  const isDraggingRef = useRef(false);
  const dragStartRef = useRef({ x: 0, y: 0 });

  const [gradientPresets, setGradientPresets] = useState(() => {
    try {
      const saved = localStorage.getItem('gradientPresets');
      return saved ? JSON.parse(saved) : [
        { type: 'linear', angle: 90, stops: [{ color: '#ffffff', position: 0, opacity: 1 }, { color: '#000000', position: 100, opacity: 1 }] },
        { type: 'linear', angle: 90, stops: [{ color: '#ff4d4f', position: 0, opacity: 1 }, { color: '#f5222d', position: 100, opacity: 1 }] },
        { type: 'linear', angle: 90, stops: [{ color: '#1677ff', position: 0, opacity: 1 }, { color: '#0958d9', position: 100, opacity: 1 }] },
        { type: 'linear', angle: 90, stops: [{ color: '#52c41a', position: 0, opacity: 1 }, { color: '#389e0d', position: 100, opacity: 1 }] }
      ];
    } catch (e) {
      return [];
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem('gradientPresets', JSON.stringify(gradientPresets));
    } catch (e) {
      console.error(e);
    }
  }, [gradientPresets]);
  const handleGradientWindowDragStart = (e) => {
    if (e.button !== 0) return;
    isDraggingRef.current = true;
    dragStartRef.current = {
      x: e.clientX - gradientWindowPos.x,
      y: e.clientY - gradientWindowPos.y
    };

    const handleMouseMove = (moveEvent) => {
      if (!isDraggingRef.current) return;
      setGradientWindowPos({
        x: moveEvent.clientX - dragStartRef.current.x,
        y: moveEvent.clientY - dragStartRef.current.y
      });
    };

    const handleMouseUp = () => {
      isDraggingRef.current = false;
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
  };

  const [showPatternModal, setShowPatternModal] = useState(false);
  const [patternState, setPatternState] = useState(() => ({
    id: DEFAULT_CARD_PATTERNS[0].id,
    name: DEFAULT_CARD_PATTERNS[0].name,
    image: DEFAULT_CARD_PATTERNS[0].image,
    tileWidth: DEFAULT_CARD_PATTERNS[0].tileWidth,
    tileHeight: DEFAULT_CARD_PATTERNS[0].tileHeight,
    scale: 100,
    opacity: 100,
    repeat: 'repeat',
    tintColor: DEFAULT_CARD_PATTERNS[0].defaultTintColor || '#18181c',
    blendMode: 'normal'
  }));
  const [editingPatternTarget, setEditingPatternTarget] = useState('shapeFill'); // 'shapeFill' | 'cardBackground' | 'accentFill' | 'certifyFill' | 'datagridFill'
  const [patternWindowPos, setPatternWindowPos] = useState({ x: 240, y: 80 });
  const [customPatterns, setCustomPatterns] = useState(() => getStoredCustomPatterns());
  const [patternCategoryFilter, setPatternCategoryFilter] = useState('All');
  const isDraggingPatternRef = useRef(false);
  const dragPatternStartRef = useRef({ x: 0, y: 0 });
  const patternFileInputRef = useRef(null);

  const handlePatternWindowDragStart = (e) => {
    if (e.button !== 0) return;
    isDraggingPatternRef.current = true;
    dragPatternStartRef.current = {
      x: e.clientX - patternWindowPos.x,
      y: e.clientY - patternWindowPos.y
    };

    const handleMouseMove = (moveEvent) => {
      if (!isDraggingPatternRef.current) return;
      const newX = Math.max(10, Math.min(window.innerWidth - 480, moveEvent.clientX - dragPatternStartRef.current.x));
      const newY = Math.max(10, Math.min(window.innerHeight - 100, moveEvent.clientY - dragPatternStartRef.current.y));
      setPatternWindowPos({ x: newX, y: newY });
    };

    const handleMouseUp = () => {
      isDraggingPatternRef.current = false;
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
  };

  const handlePatternFileUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (event) => {
      const dataUri = event.target?.result;
      if (!dataUri) return;
      
      const newCustomPattern = {
        id: `custom_${Date.now()}`,
        name: file.name.replace(/\.[^/.]+$/, "") || 'Custom Tile',
        category: 'Custom',
        tileWidth: 32,
        tileHeight: 32,
        defaultScale: 100,
        defaultTintColor: 'transparent',
        image: dataUri,
        isCustom: true
      };

      if (typeof Image !== 'undefined') {
        const img = new Image();
        img.onload = () => {
          if (img.naturalWidth) newCustomPattern.tileWidth = img.naturalWidth;
          if (img.naturalHeight) newCustomPattern.tileHeight = img.naturalHeight;
          saveCustomPattern(newCustomPattern);
          setCustomPatterns(getStoredCustomPatterns());
          setPatternState(prev => ({
            ...prev,
            id: newCustomPattern.id,
            name: newCustomPattern.name,
            image: newCustomPattern.image,
            tileWidth: newCustomPattern.tileWidth,
            tileHeight: newCustomPattern.tileHeight
          }));
        };
        img.onerror = () => {
          saveCustomPattern(newCustomPattern);
          setCustomPatterns(getStoredCustomPatterns());
          setPatternState(prev => ({
            ...prev,
            id: newCustomPattern.id,
            name: newCustomPattern.name,
            image: newCustomPattern.image
          }));
        };
        img.src = dataUri;
      } else {
        saveCustomPattern(newCustomPattern);
        setCustomPatterns(getStoredCustomPatterns());
        setPatternState(prev => ({
          ...prev,
          id: newCustomPattern.id,
          name: newCustomPattern.name,
          image: newCustomPattern.image
        }));
      }
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  const handleApplyPattern = () => {
    const patternValue = generatePatternString(patternState);
    if (editingPatternTarget === 'cardBackground') {
      saveHistoryState();
      setSides(prev => ({
        ...prev,
        [activeSide]: {
          ...prev[activeSide],
          background: {
            type: 'pattern',
            value: patternValue,
            pattern: { ...patternState }
          }
        }
      }));
      setShowPatternModal(false);
      return;
    }

    if (!activeElement) return;
    saveHistoryState();
    updateActiveElement({
      fillType: 'pattern',
      fill: patternValue,
      pattern: { ...patternState }
    });
    setShowPatternModal(false);
  };

  const handleClearPattern = () => {
    if (editingPatternTarget === 'cardBackground') {
      saveHistoryState();
      setSides(prev => ({
        ...prev,
        [activeSide]: {
          ...prev[activeSide],
          background: {
            type: 'color',
            value: patternState.tintColor && patternState.tintColor !== 'transparent' ? patternState.tintColor : '#0f172a'
          }
        }
      }));
      setShowPatternModal(false);
      return;
    }

    if (!activeElement) return;
    saveHistoryState();
    updateActiveElement({
      fillType: 'solid',
      fill: patternState.tintColor && patternState.tintColor !== 'transparent' ? patternState.tintColor : '#e6e6e6',
      pattern: null
    });
    setShowPatternModal(false);
  };


  // Dragging / Resizing mechanics ref
  const dragInfo = useRef({
    isDragging: false,
    isResizing: false,
    resizeHandle: null,
    startX: 0,
    startY: 0,
    startElemX: 0,
    startElemY: 0,
    startElemW: 0,
    startElemH: 0
  });

  const barRef = useRef(null);

  // ----------------------------------------------------
  // FETCH / SAVE / MANAGEMENT ACTIONS
  // ----------------------------------------------------
  useEffect(() => {
    localStorage.setItem('templateGridZoom', gridZoom);
  }, [gridZoom]);

  useEffect(() => {
    if (editingGlobalColor) {
      setLocalColorVal(editingGlobalColor);
    } else {
      setLocalColorVal('');
    }
  }, [editingGlobalColor]);

  useEffect(() => {
    if (viewMode === 'manager') {
      fetchTemplates();
      fetchAvailableCompanies();
      if (showTemplateUsageInfo) {
        fetchTemplateUsageStats();
      }
    } else {
      fetchSharedDatasets();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewMode, selectedTenant]);

  useEffect(() => {
    const handleGlobalKeyDown = (e) => {
      if (viewMode !== 'designer' || !selectedElementId || activeTool !== 'select') return;

      // Do not nudge if typing inside inputs or textareas
      const tag = document.activeElement ? document.activeElement.tagName.toUpperCase() : '';
      if (tag === 'INPUT' || tag === 'TEXTAREA') return;

      const arrowKeys = ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'];
      if (!arrowKeys.includes(e.key)) return;

      e.preventDefault(); // Stop window scrolling

      const elem = sides[activeSide].elements.find(el => el.id === selectedElementId);
      if (!elem || elem.locked) return;

      const amount = e.shiftKey ? 10 : 1;
      let dx = 0;
      let dy = 0;

      if (e.key === 'ArrowUp') dy = -amount;
      if (e.key === 'ArrowDown') dy = amount;
      if (e.key === 'ArrowLeft') dx = -amount;
      if (e.key === 'ArrowRight') dx = amount;

      saveHistoryState();
      
      setSides({
        ...sides,
        [activeSide]: {
          ...sides[activeSide],
          elements: sides[activeSide].elements.map(el => {
            const isSelf = el.id === selectedElementId;
            const isChild = el.parentId === selectedElementId;
            if ((isSelf || isChild) && !el.locked) {
              return {
                ...el,
                x: el.x !== undefined ? el.x + dx : el.x,
                y: el.y !== undefined ? el.y + dy : el.y
              };
            }
            return el;
          })
        }
      });
    };

    window.addEventListener('keydown', handleGlobalKeyDown);
    return () => {
      window.removeEventListener('keydown', handleGlobalKeyDown);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewMode, selectedElementId, sides, activeSide, activeTool]);

  useEffect(() => {
    const handleDocumentClick = (e) => {
      if (showShapeMenu && !e.target.closest('#shape-tool-btn') && !e.target.closest('#shape-flyout-menu')) {
        setShowShapeMenu(false);
      }
      if (showImageMenu && !e.target.closest('#image-tool-btn') && !e.target.closest('#image-flyout-menu')) {
        setShowImageMenu(false);
      }
      if (showDividerMenu && !e.target.closest('#accent-tool-btn') && !e.target.closest('#accent-flyout-menu')) {
        setShowDividerMenu(false);
      }
    };
    document.addEventListener('click', handleDocumentClick);
    return () => {
      document.removeEventListener('click', handleDocumentClick);
    };
  }, [showShapeMenu, showImageMenu, showDividerMenu]);

  const fetchUploadedAssets = async () => {
    if (!token) return;
    try {
      let url = `${API_BASE}/api/upload?`;
      const params = new URLSearchParams();
      if (user?.id) {
        params.append('associated_user_id', user.id);
      }
      const activeTenantId = selectedTenant || user?.tenant_id;
      if (activeTenantId) {
        params.append('tenant_id', activeTenantId);
      }
      url += params.toString();

      const res = await fetch(url, {
        headers: {
          Authorization: `Bearer ${token}`,
          'X-Tenant-Domain': window.location.hostname
        }
      });
      if (res.ok) {
        const data = await res.json();
        setUploadedAssets(data || []);
      }
    } catch (err) {
      console.error('Failed to fetch assets in TemplateDesigner', err);
    }
  };

  const handleUploadAssetFiles = async (files) => {
    if (!files || files.length === 0) return;
    setIsUploadingAsset(true);
    setAssetUploadError('');
    setAssetUploadSuccess('');

    const activeTenantId = selectedTenant || user?.tenant_id;
    let successCount = 0;

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const isImg = file.type.startsWith('image/') || file.name.match(/\.(png|jpg|jpeg|svg|webp|gif)$/i);
      if (!isImg) {
        setAssetUploadError(`File "${file.name}" is not a supported image/SVG format.`);
        continue;
      }

      const formData = new FormData();
      formData.append('file', file);
      formData.append('display_name', file.name);

      let url = `${API_BASE}/api/upload?`;
      const params = new URLSearchParams();
      if (user?.id) params.append('associated_user_id', user.id.toString());
      if (activeTenantId) params.append('tenant_id', activeTenantId.toString());
      url += params.toString();

      try {
        const headers = {
          'X-Tenant-Domain': window.location.hostname
        };
        if (token) headers['Authorization'] = `Bearer ${token}`;

        const res = await fetch(url, {
          method: 'POST',
          headers,
          body: formData
        });

        if (!res.ok) {
          const errText = await res.text();
          throw new Error(errText || `Upload failed with status ${res.status}`);
        }

        const data = await res.json();
        if (data?.file) {
          const newAssetItem = {
            id: data.file.id,
            filename: data.file.filename,
            original_name: data.file.original_name || file.name,
            original_filename: data.file.original_name || file.name,
            location: data.file.location,
            file_url: data.file.location,
            mime_type: data.file.mime_type,
            file_size: data.file.file_size
          };
          setUploadedAssets(prev => [newAssetItem, ...prev.filter(a => a.id !== newAssetItem.id)]);
          successCount++;
        }
      } catch (err) {
        console.error('Failed to upload asset:', err);
        setAssetUploadError(`Failed to upload ${file.name}: ${err.message}`);
      }
    }

    setIsUploadingAsset(false);
    if (successCount > 0) {
      setAssetUploadSuccess(`Successfully uploaded ${successCount} asset${successCount > 1 ? 's' : ''}!`);
      setTimeout(() => setAssetUploadSuccess(''), 4000);
      fetchUploadedAssets();
    }
  };

  useEffect(() => {
    if (viewMode === 'designer' || showAssetSelectorModal) {
      fetchUploadedAssets();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, user, selectedTenant, viewMode, showAssetSelectorModal]);

  useEffect(() => {
    if (tenants && tenants.length > 0) {
      setTenantList(tenants);
      if (!importTargetTenantId) {
        setImportTargetTenantId(selectedTenant ? selectedTenant.toString() : tenants[0].id.toString());
      }
    } else if (isGlobalRole && token) {
      fetch(`${API_BASE}/api/v1/tenants`, {
        headers: { Authorization: `Bearer ${token}` }
      })
        .then(r => r.ok ? r.json() : [])
        .then(data => {
          if (Array.isArray(data) && data.length > 0) {
            setTenantList(data);
            if (!importTargetTenantId) {
              setImportTargetTenantId(selectedTenant ? selectedTenant.toString() : data[0].id.toString());
            }
          }
        })
        .catch(() => {});
    } else if (user?.tenant_id && tenantList.length === 0) {
      const fallback = [{
        id: user.tenant_id,
        brand_name: tenantConfig?.brand_name || `Tenant #${user.tenant_id}`,
        domain_name: tenantConfig?.domain_name || window.location.hostname
      }];
      setTenantList(fallback);
      if (!importTargetTenantId) {
        setImportTargetTenantId(user.tenant_id.toString());
      }
    }
  }, [tenants, selectedTenant, isGlobalRole, token, API_BASE, importTargetTenantId, user, tenantConfig]);

  const fetchTemplates = async () => {
    if (!token) return;
    setLoading(true);
    try {
      let url = `${API_BASE}/api/v1/designs?design_type=brag_page&is_template=true&summary=true`;
      if (selectedTenant) {
        url += `&tenant_id=${selectedTenant}`;
      }
      const res = await fetch(url, {
        headers: {
          Authorization: `Bearer ${token}`,
          'X-Tenant-Domain': window.location.hostname
        }
      });
      if (res.ok) {
        const data = await res.json();
        const filtered = data.filter(d => d.config && (d.config.is_template === true || d.config.is_template === 'true' || d.config.is_template === undefined));
        setTemplates(filtered);
      }
    } catch (err) {
      console.error("Failed to load templates list", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchTemplateUsageStats = async () => {
    if (!token) return;
    setLoadingUsageStats(true);
    try {
      let url = `${API_BASE}/api/v1/coin-vault/templates/usage-stats`;
      if (selectedTenant) {
        url += `?tenant_id=${selectedTenant}`;
      }
      const res = await fetch(url, {
        headers: {
          Authorization: `Bearer ${token}`,
          'X-Tenant-Domain': window.location.hostname
        }
      });
      if (res.ok) {
        const data = await res.json();
        setTemplateUsageStats(data.stats || {});
      }
    } catch (err) {
      console.error("Failed to load template usage statistics", err);
    } finally {
      setLoadingUsageStats(false);
    }
  };

  const handleToggleTemplateUsageInfo = () => {
    const nextState = !showTemplateUsageInfo;
    setShowTemplateUsageInfo(nextState);
    if (nextState) {
      fetchTemplateUsageStats();
    }
  };

  const handleExportCard = async (templateId) => {
    const idToExport = templateId || selectedGridTemplateId || selectedTemplateId;
    if (!idToExport) return;
    setIsExporting(true);
    setStatusError('');
    try {
      const res = await fetch(`${API_BASE}/api/v1/designs/${idToExport}/export`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'X-Tenant-Domain': window.location.hostname
        }
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Failed to export trophy card.');
      }
      const data = await res.json();
      const cardName = data.card?.name || 'trophy_card';
      const safeName = cardName.replace(/[^a-z0-9]/gi, '_').toLowerCase();
      const filename = `${safeName}_export.json`;
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error exporting card:', err);
      alert(err.message || 'Export failed.');
    } finally {
      setIsExporting(false);
    }
  };

  const handleOpenPrintModalForGrid = async (templateId) => {
    const id = templateId || selectedGridTemplateId || selectedTemplateId;
    if (id && id !== selectedTemplateId) {
      const tmpl = templates.find(t => t.id === id);
      if (tmpl) {
        await handleEditTemplate(tmpl);
      }
    }
    setPrintExportError('');
    setPrintExportSuccess(false);
    setShowPrintExportModal(true);
  };

  const handleExecutePrintExport = async (printOptions) => {
    setIsPrintExporting(true);
    setPrintExportError('');
    setPrintExportSuccess(false);
    setPrintExportStep('Preparing high-resolution card faces & original master assets...');

    const activeSideOriginal = activeSide;
    const prevSelectedId = selectedElementId;
    setSelectedElementId(null);

    // Helpers to clean canvas and HTML for print export so card edges are perfectly clean
    const prepareCanvasClean = (el) => {
      if (!el) return () => {};
      const prevShadow = el.style.boxShadow;
      const prevTransform = el.style.transform;
      const prevTransition = el.style.transition;
      el.style.boxShadow = 'none';
      el.style.transform = 'none';
      el.style.transition = 'none';
      return () => {
        el.style.boxShadow = prevShadow;
        el.style.transform = prevTransform;
        el.style.transition = prevTransition;
      };
    };

    const sanitizePrintHtml = (html) => {
      if (!html) return '';
      // Target only the root #catch-card-canvas container style to neutralize outer scale/shadow,
      // strictly preserving all child element transforms (rotations, scales) and effects.
      if (html.includes('id="catch-card-canvas"') || html.includes("id='catch-card-canvas'")) {
        return html.replace(
          /(<div[^>]*id=["']catch-card-canvas["'][^>]*style=["'])([^"']*)(["'])/i,
          (match, prefix, styleStr, suffix) => {
            let cleanStyle = styleStr
              .replace(/box-shadow:\s*[^;]+;?/gi, '')
              .replace(/transform:\s*scale\([^)]+\);?/gi, '')
              .replace(/transform:\s*matrix[^;]+;?/gi, '')
              .replace(/transition:\s*[^;]+;?/gi, '');
            cleanStyle = `box-shadow: none !important; transform: none !important; transition: none !important; margin: 0 !important; ${cleanStyle}`;
            return `${prefix}${cleanStyle}${suffix}`;
          }
        );
      }
      return html;
    };

    try {
      // Determine client-side raster capture scale (4.0x for 600 DPI photo mode, 3.0x for 300 DPI)
      const clientScale = (printOptions && printOptions.dpi >= 600) ? 4.0 : 3.0;

      // 1. Capture Front Card Face
      setPrintExportStep('Rendering front face with uncompressed master assets...');
      setActiveSide('front');
      await new Promise(resolve => setTimeout(resolve, 150));
      const frontCanvas = document.getElementById('catch-card-canvas');
      const restoreFront = prepareCanvasClean(frontCanvas);
      await new Promise(resolve => setTimeout(resolve, 50));
      const frontHtml = sanitizePrintHtml(frontCanvas ? frontCanvas.outerHTML : '');
      let frontImage = null;
      try {
        if (frontCanvas && typeof html2canvas === 'function') {
          const cv = await html2canvas(frontCanvas, {
            scale: clientScale,
            useCORS: true,
            backgroundColor: null,
            logging: false,
            onclone: (clonedDoc) => {
              const c = clonedDoc.getElementById('catch-card-canvas');
              if (c) {
                c.style.boxShadow = 'none';
                c.style.transform = 'none';
                c.style.transition = 'none';
                c.style.margin = '0';
              }
            }
          });
          frontImage = cv.toDataURL('image/png');
        }
      } catch (fSnapErr) {
        console.warn('html2canvas front capture warning:', fSnapErr);
      } finally {
        restoreFront();
      }

      // 2. Capture Back Card Face
      setPrintExportStep('Rendering back face & calculating fold alignment...');
      setActiveSide('back');
      await new Promise(resolve => setTimeout(resolve, 150));
      const backCanvas = document.getElementById('catch-card-canvas');
      const restoreBack = prepareCanvasClean(backCanvas);
      await new Promise(resolve => setTimeout(resolve, 50));
      const backHtml = sanitizePrintHtml(backCanvas ? backCanvas.outerHTML : '');
      let backImage = null;
      try {
        if (backCanvas && typeof html2canvas === 'function') {
          const cv = await html2canvas(backCanvas, {
            scale: clientScale,
            useCORS: true,
            backgroundColor: null,
            logging: false,
            onclone: (clonedDoc) => {
              const c = clonedDoc.getElementById('catch-card-canvas');
              if (c) {
                c.style.boxShadow = 'none';
                c.style.transform = 'none';
                c.style.transition = 'none';
                c.style.margin = '0';
              }
            }
          });
          backImage = cv.toDataURL('image/png');
        }
      } catch (bSnapErr) {
        console.warn('html2canvas back capture warning:', bSnapErr);
      } finally {
        restoreBack();
      }

      // Restore original side and selection
      setActiveSide(activeSideOriginal);
      if (prevSelectedId) {
        setSelectedElementId(prevSelectedId);
      }

      // 3. Request Backend High-DPI PDF Generation
      setPrintExportStep('Compiling Epson EcoTank L8160 PDF with crop marks & registration...');
      const payload = {
        ...printOptions,
        design_id: selectedTemplateId || selectedGridTemplateId || null,
        front_html: frontHtml,
        back_html: backHtml,
        front_image: frontImage,
        back_image: backImage,
        use_original_assets: true
      };

      const res = await fetch(`${API_BASE}/api/v1/designs/export-print`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
          'X-Tenant-Domain': window.location.hostname
        },
        body: JSON.stringify(payload)
      });

      if (!res.ok) {
        const errJson = await res.json().catch(() => ({}));
        throw new Error(errJson.detail || 'Failed to generate print PDF.');
      }

      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const safeName = (printOptions.card_name || templateName || 'Trophy_Card').replace(/[^a-zA-Z0-9_-]/g, '_');
      a.download = `${safeName}_Epson_L8160_Print.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      setPrintExportSuccess(true);
    } catch (err) {
      console.error('Print export error:', err);
      setPrintExportError(err.message || 'Failed to generate print PDF.');
    } finally {
      setIsPrintExporting(false);
      setActiveSide(activeSideOriginal);
      if (prevSelectedId) {
        setSelectedElementId(prevSelectedId);
      }
      const c = document.getElementById('catch-card-canvas');
      if (c) {
        c.style.boxShadow = '';
        c.style.transform = '';
        c.style.transition = '';
      }
    }
  };

  const runImportVerification = async (dataToVerify, targetTenant, isGlobal) => {
    if (!dataToVerify) {
      setImportVerifyReport(null);
      return;
    }
    setImportVerifying(true);
    try {
      const res = await fetch(`${API_BASE}/api/v1/designs/import/verify`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
          'X-Tenant-Domain': window.location.hostname
        },
        body: JSON.stringify({
          target_tenant_id: isGlobal ? null : (targetTenant ? parseInt(targetTenant, 10) : null),
          is_global_scope: Boolean(isGlobal),
          export_data: dataToVerify
        })
      });
      if (res.ok) {
        const report = await res.json();
        setImportVerifyReport(report);
      } else {
        setImportVerifyReport(null);
      }
    } catch (err) {
      console.error("Failed to verify import data:", err);
      setImportVerifyReport(null);
    } finally {
      setImportVerifying(false);
    }
  };

  const handleOpenImportModal = () => {
    setImportData(null);
    setImportFileName('');
    setImportCardName('');
    setImportError('');
    setImportSuccess('');
    setImportVerifyReport(null);
    setImportIsGlobalScope(false);
    setImportAutoCreateDatasets(true);
    if (selectedTenant) {
      setImportTargetTenantId(selectedTenant.toString());
    } else if (tenantList.length > 0) {
      setImportTargetTenantId(tenantList[0].id.toString());
    }
    setShowImportModal(true);
  };

  const handleFileChange = (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    setImportFileName(file.name);
    setImportError('');
    setImportSuccess('');
    setImportVerifyReport(null);

    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const parsed = JSON.parse(evt.target.result);
        if ((parsed.schema_version !== 'trophycoin_card_v1' && parsed.schema_version !== 'trophycoin_card_v2') || !parsed.card) {
          setImportError("Invalid export file. Expected schema_version 'trophycoin_card_v1' and card configuration.");
          setImportData(null);
          return;
        }
        setImportData(parsed);
        setImportCardName(parsed.card?.name ? `${parsed.card.name} (Copy)` : 'Imported Trophy Card');
        runImportVerification(parsed, importTargetTenantId, importIsGlobalScope);
      } catch (err) {
        setImportError(`Failed to parse JSON file: ${err.message}`);
        setImportData(null);
      }
    };
    reader.onerror = () => {
      setImportError('Failed to read file.');
      setImportData(null);
    };
    reader.readAsText(file);
  };

  const handleExecuteImport = async () => {
    if (!importData) {
      setImportError('Please select a valid card export JSON file.');
      return;
    }
    if (!importIsGlobalScope && !importTargetTenantId) {
      setImportError('Please select a target tenant.');
      return;
    }

    setImportLoading(true);
    setImportError('');
    setImportSuccess('');

    try {
      const payload = {
        target_tenant_id: importIsGlobalScope ? null : parseInt(importTargetTenantId, 10),
        is_global_scope: Boolean(importIsGlobalScope),
        auto_create_datasets: importAutoCreateDatasets,
        card_name: (importCardName || importData.card?.name || 'Imported Card').trim(),
        export_data: {
          ...importData,
          card: {
            ...importData.card,
            name: (importCardName || importData.card?.name || 'Imported Card').trim()
          }
        }
      };

      const res = await fetch(`${API_BASE}/api/v1/designs/import`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
          'X-Tenant-Domain': window.location.hostname
        },
        body: JSON.stringify(payload)
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Import failed.');
      }

      const result = await res.json();
      const dsCount = result.synced_datasets_count || 0;
      const dsText = dsCount > 0 ? ` and ${dsCount} datasets synchronized` : '';
      setImportSuccess(`Successfully imported "${result.design?.name || result.design_name || importCardName}" into card library with ${result.imported_assets_count} bundled assets${dsText}!`);
      
      await fetchTemplates();

      setTimeout(() => {
        setShowImportModal(false);
      }, 1800);
    } catch (err) {
      console.error('Error importing card:', err);
      setImportError(err.message || 'Import failed.');
    } finally {
      setImportLoading(false);
    }
  };

  const handleOpenPushModal = (templateId) => {
    const id = templateId || selectedGridTemplateId || selectedTemplateId;
    if (!id) return;
    setPushTemplateId(id);
    setPushError('');
    setPushSuccess('');
    setPushVerifyReport(null);
    setShowPushModal(true);
  };

  const handleFetchRemoteTenants = async () => {
    setPushLoadingTenants(true);
    setPushError('');
    try {
      const cleanUrl = (pushTargetUrl || '').trim().replace(/\/+$/, '');
      localStorage.setItem('trophycoin_prod_push_url', cleanUrl);
      if (pushRemoteToken) {
        localStorage.setItem('trophycoin_prod_push_token', pushRemoteToken.trim());
      }

      const res = await fetch(`${API_BASE}/api/v1/designs/push/remote-tenants`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
          'X-Tenant-Domain': window.location.hostname
        },
        body: JSON.stringify({
          target_server_url: cleanUrl,
          remote_auth_token: pushRemoteToken ? pushRemoteToken.trim() : null
        })
      });

      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.detail || 'Failed to fetch remote tenants');
      }

      const tenants = Array.isArray(data.tenants) ? data.tenants : [];
      setPushRemoteTenants(tenants);
      if (tenants.length > 0 && pushSelectedScope !== 'global') {
        setPushSelectedScope(tenants[0].id.toString());
      }
    } catch (err) {
      console.error("Failed to fetch remote tenants:", err);
      setPushError(err.message || 'Failed to connect to remote server');
    } finally {
      setPushLoadingTenants(false);
    }
  };

  const handleVerifyPush = async () => {
    if (!pushTemplateId) return;
    setPushVerifying(true);
    setPushError('');
    try {
      const cleanUrl = (pushTargetUrl || '').trim().replace(/\/+$/, '');
      const isGlobal = pushSelectedScope === 'global';
      const targetTenantId = !isGlobal ? parseInt(pushSelectedScope, 10) : null;

      const res = await fetch(`${API_BASE}/api/v1/designs/${pushTemplateId}/push/verify`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
          'X-Tenant-Domain': window.location.hostname
        },
        body: JSON.stringify({
          target_server_url: cleanUrl,
          remote_auth_token: pushRemoteToken ? pushRemoteToken.trim() : null,
          target_tenant_id: targetTenantId,
          is_global_scope: isGlobal
        })
      });

      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.detail || 'Remote verification failed');
      }
      setPushVerifyReport(data);
    } catch (err) {
      console.error("Verification error:", err);
      setPushError(err.message || 'Verification failed');
      setPushVerifyReport(null);
    } finally {
      setPushVerifying(false);
    }
  };

  const handleExecutePush = async () => {
    if (!pushTemplateId) return;
    setPushLoading(true);
    setPushError('');
    setPushSuccess('');
    try {
      const cleanUrl = (pushTargetUrl || '').trim().replace(/\/+$/, '');
      const isGlobal = pushSelectedScope === 'global';
      const targetTenantId = !isGlobal ? parseInt(pushSelectedScope, 10) : null;

      const res = await fetch(`${API_BASE}/api/v1/designs/${pushTemplateId}/push`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
          'X-Tenant-Domain': window.location.hostname
        },
        body: JSON.stringify({
          target_server_url: cleanUrl,
          remote_auth_token: pushRemoteToken ? pushRemoteToken.trim() : null,
          target_tenant_id: targetTenantId,
          is_global_scope: isGlobal,
          auto_create_datasets: pushAutoCreateDatasets
        })
      });

      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.detail || 'Push to remote server failed');
      }

      setPushSuccess(data.message || `Card successfully pushed to ${cleanUrl}!`);
      setTimeout(() => {
        setShowPushModal(false);
      }, 2200);
    } catch (err) {
      console.error("Push failed:", err);
      setPushError(err.message || 'Failed to push card');
    } finally {
      setPushLoading(false);
    }
  };

  const fetchAvailableCompanies = async () => {
    if (!token) return;
    try {
      let url = `${API_BASE}/api/v1/organizations`;
      if (selectedTenant && isGlobalRole) {
        url += `?tenant_id=${selectedTenant}`;
      }
      const res = await fetch(url, {
        headers: {
          Authorization: `Bearer ${token}`,
          'X-Tenant-Domain': window.location.hostname
        }
      });
      if (res.ok) {
        const data = await res.json();
        const orgs = Array.isArray(data) ? data : (data && Array.isArray(data.organizations) ? data.organizations : []);
        setAvailableCompanies(orgs);
      }
    } catch (err) {
      console.error("Failed to load companies for sharing", err);
    }
  };

  const handleOpenShareModal = (template) => {
    setSharingTemplate(template);
    const config = template.config || {};
    setShareAllTenantUsers(config.shared_all !== false);

    let initialCompanies = [];
    if (Array.isArray(config.shared_companies) && config.shared_companies.length > 0) {
      initialCompanies = config.shared_companies.map(c => typeof c === 'object' && c !== null ? c : { id: c, name: `Company #${c}` });
    } else if (Array.isArray(config.shared_company_ids) && config.shared_company_ids.length > 0) {
      initialCompanies = config.shared_company_ids.map(id => {
        const found = availableCompanies.find(c => c.id === id);
        return found ? { id: found.id, name: found.name } : { id, name: `Company #${id}` };
      });
    }
    setSharedCompanies(initialCompanies);
    setSelectedCompanyId('');
    setSharedUserEmails(config.shared_user_emails || []);
    setNewShareEmail('');
    fetchAvailableCompanies();
    setShowShareModal(true);
  };

  const handleAddCompany = (compIdToAdd) => {
    const compId = compIdToAdd !== undefined ? compIdToAdd : parseInt(selectedCompanyId);
    if (!compId) return;
    const compObj = availableCompanies.find(c => c.id === compId);
    const name = compObj ? compObj.name : `Company #${compId}`;
    if (!sharedCompanies.some(c => c.id === compId)) {
      setSharedCompanies([...sharedCompanies, { id: compId, name }]);
    }
    setSelectedCompanyId('');
  };

  const handleRemoveCompany = (compIdToRemove) => {
    setSharedCompanies(sharedCompanies.filter(c => c.id !== compIdToRemove));
  };

  const handleSaveShareSettings = async () => {
    if (!sharingTemplate) return;
    
    const updatedConfig = {
      ...(sharingTemplate.config || {}),
      shared_all: shareAllTenantUsers,
      shared_companies: sharedCompanies,
      shared_company_ids: sharedCompanies.map(c => c.id),
      shared_user_emails: sharedUserEmails
    };
    
    try {
      const res = await fetch(`${API_BASE}/api/v1/designs/${sharingTemplate.id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          config: updatedConfig
        })
      });
      
      if (res.ok) {
        fetchTemplates();
        setShowShareModal(false);
        alert("Template share settings updated successfully!");
      } else {
        const data = await res.json();
        alert(data.detail || "Failed to update share settings.");
      }
    } catch (err) {
      console.error(err);
      alert("Error occurred while saving share settings.");
    }
  };

  const handleToggleSystemTemplate = async () => {
    if (!selectedGridTemplateId) return;
    const selectedT = templates.find(t => t.id === selectedGridTemplateId);
    if (!selectedT) return;
    const currentIsSys = Boolean(selectedT.config?.is_system_template);
    const nextIsSys = !currentIsSys;
    
    try {
      const updatedConfig = {
        ...(selectedT.config || {}),
        is_system_template: nextIsSys
      };
      const res = await fetch(`${API_BASE}/api/v1/designs/${selectedT.id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          config: updatedConfig
        })
      });
      if (res.ok) {
        await fetchTemplates();
      } else {
        const data = await res.json();
        alert(data.detail || "Failed to update system template status.");
      }
    } catch (err) {
      console.error("Failed to toggle system template", err);
      alert("Error occurred while toggling system template status.");
    }
  };

  const fetchSharedDatasets = async () => {
    try {
      const res = await fetch(`${API_BASE}/api/v1/data-groups`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'X-Tenant-Domain': window.location.hostname
        }
      });
      if (res.ok) {
        const groups = await res.json();
        if (groups.length > 0) {
          const formatted = {};
          groups.forEach(g => {
            const variables = {};
            g.fields.forEach(f => {
              variables[f.label] = f.sample_data || (f.type === 'number' ? '42.5' : 'Sample Data');
            });
            formatted[g.id.toString()] = {
              name: g.name,
              variables,
              fields: g.fields || []
            };
          });
          setDatasets(formatted);
          const firstId = groups[0].id.toString();
          setActiveDatasetKey(prev => prev || firstId);
          return formatted;
        }
      }
    } catch (err) {
      console.error(err);
    }
    return null;
  };

  const handleRefreshData = async () => {
    setRefreshingData(true);
    try {
      const latestDatasets = await fetchSharedDatasets() || datasets;
      
      saveHistoryState();
      
      const updatedSides = {};
      
      Object.keys(sides).forEach(sideKey => {
        const side = sides[sideKey];
        const updatedElements = side.elements.map(elem => {
          if (elem.type === 'text' && elem.dynamicFields && elem.dynamicFields.length > 0) {
            const updatedDynamicFields = elem.dynamicFields.map(item => {
              if (item.type === 'dynamic') {
                const ds = latestDatasets[item.datasetKey];
                if (ds && ds.fields) {
                  const fieldObj = ds.fields.find(f => f.label === item.label);
                  if (fieldObj) {
                    return {
                      ...item,
                      sample_data: fieldObj.sample_data || ds.variables?.[item.label] || item.sample_data || '',
                      render: fieldObj.render || 'none',
                      custom_renderer: fieldObj.custom_renderer || null
                    };
                  }
                }
              }
              return item;
            });
            
            const newText = updatedDynamicFields.map(item => item.type === 'static' ? item.value : `$${item.label}`).join('');
            
            return {
              ...elem,
              dynamicFields: updatedDynamicFields,
              text: newText
            };
          }
          return elem;
        });
        
        updatedSides[sideKey] = {
          ...side,
          elements: updatedElements
        };
      });
      
      setSides(updatedSides);
    } catch (err) {
      console.error("Failed to refresh datasets and elements:", err);
    } finally {
      setTimeout(() => {
        setRefreshingData(false);
      }, 600);
    }
  };

  const saveHistoryState = (newSidesState) => {
    setUndoStack(prev => [...prev, JSON.stringify(sides)]);
    setRedoStack([]); // Clear redo
    if (newSidesState) {
      setSides(newSidesState);
    }
  };

  const handleUndo = () => {
    if (undoStack.length === 0) return;
    const previous = undoStack[undoStack.length - 1];
    setUndoStack(prev => prev.slice(0, -1));
    setRedoStack(prev => [...prev, JSON.stringify(sides)]);
    setSides(JSON.parse(previous));
  };

  const handleRedo = () => {
    if (redoStack.length === 0) return;
    const next = redoStack[redoStack.length - 1];
    setRedoStack(prev => prev.slice(0, -1));
    setUndoStack(prev => [...prev, JSON.stringify(sides)]);
    setSides(JSON.parse(next));
  };

  const handleToggleFlipH = () => {
    if (!activeElement || activeElement.type !== 'image') return;
    const updatedElements = sides[activeSide].elements.map(e => 
      e.id === selectedElementId ? { ...e, flipH: !e.flipH } : e
    );
    saveHistoryState({
      ...sides,
      [activeSide]: {
        ...sides[activeSide],
        elements: updatedElements
      }
    });
  };

  const handleToggleFlipV = () => {
    if (!activeElement || activeElement.type !== 'image') return;
    const updatedElements = sides[activeSide].elements.map(e => 
      e.id === selectedElementId ? { ...e, flipV: !e.flipV } : e
    );
    saveHistoryState({
      ...sides,
      [activeSide]: {
        ...sides[activeSide],
        elements: updatedElements
      }
    });
  };

  const handleCreateNewTemplate = async () => {
    const name = await modal.prompt("Enter template name:", "New Catch Card Template", {
      title: "Create New Template",
      confirmText: "Create Template"
    });
    if (!name) return;
    fetchSharedDatasets();
    setTemplateName(name);
    setSelectedTemplateId(null);
    setIsSystemTemplate(false);
    setAutoOptimizeImages(true);

    const dsKeys = Object.keys(datasets);
    const initialEnabled = {};
    dsKeys.forEach(k => {
      initialEnabled[k] = {};
      if (datasets[k]?.fields) {
        datasets[k].fields.forEach(f => {
          initialEnabled[k][f.label] = true;
        });
      }
    });

    const initialSides = {
      front: {
        background: { type: 'gradient', value: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)' },
        elements: []
      },
      back: {
        background: { type: 'color', value: '#0f172a' },
        elements: []
      }
    };
    const initialGlobalStyles = {
      borderRadius: '18px',
      theme: 'gold-dark',
      borderColor: '#c9a054',
      borderWidth: 6,
      borderStyle: 'solid'
    };
    const initialCardDimensions = { width: 400, height: 560 };

    setLinkedDatasets(dsKeys);
    setEnabledFields(initialEnabled);
    setHiddenFields({});
    setFieldMappings({});
    setLockedOptions({});
    setRequiredFields({});
    setLinkMappings({});
    setDateFormats({});
    setFormDatasetOrder([]);
    setHidePrepopulatedDatasets(false);
    setSides(initialSides);
    setGlobalStyles(initialGlobalStyles);
    setCardDimensions(initialCardDimensions);
    setDesignStatus(null);
    setDesignIsTemplate(true);
    setUndoStack([]);
    setRedoStack([]);

    const initialConfig = {
      name,
      is_system_template: false,
      autoOptimizeImages: true,
      sides: initialSides,
      globalStyles: initialGlobalStyles,
      cardDimensions: initialCardDimensions,
      linkedDatasets: dsKeys,
      enabledFields: initialEnabled,
      hidden_fields: {},
      field_mappings: {},
      locked_options: {},
      required_fields: {},
      link_mappings: {},
      date_formats: {},
      form_dataset_order: [],
      hide_prepopulated_datasets: false,
      hidePrepopulatedDatasets: false
    };
    originalConfigRef.current = canonicalConfigStringify(initialConfig);
    setSelectedElementId(null);
    setViewMode('designer');
  };

  const handleEditTemplate = async (design) => {
    fetchSharedDatasets();
    let fullDesign = design;
    if (!design.config?.sides && design.id) {
      setLoading(true);
      try {
        const res = await fetch(`${API_BASE}/api/v1/designs/${design.id}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'X-Tenant-Domain': window.location.hostname
          }
        });
        if (res.ok) {
          fullDesign = await res.json();
        }
      } catch (err) {
        console.error("Failed to load full template details", err);
      } finally {
        setLoading(false);
      }
    }

    setSelectedTemplateId(fullDesign.id);
    const designName = fullDesign.name || '';
    setTemplateName(designName);
    setDesignStatus(fullDesign.config?.status || null);
    setDesignIsTemplate(fullDesign.config?.is_template !== false);
    const isSys = isTrophyCardMode ? false : Boolean(fullDesign.config?.is_system_template);
    setIsSystemTemplate(isSys);
    setAutoOptimizeImages(fullDesign.config?.autoOptimizeImages !== false);
    
    // Load config states safely
    const normalizeGroupLayerOrder = (elements) => {
      if (!Array.isArray(elements) || elements.length === 0) return [];
      const visualElements = [...elements].reverse();
      const groupById = new Map();
      visualElements.forEach(el => {
        if (el && el.type === 'group') {
          groupById.set(el.id, el);
        }
      });
      if (groupById.size === 0) return elements;

      const visited = new Set();
      const normalizedVisual = [];

      const emitGroupWithChildren = (groupEl) => {
        if (!groupEl || visited.has(groupEl.id)) return;
        visited.add(groupEl.id);
        normalizedVisual.push(groupEl);
        visualElements.forEach(child => {
          if (child && child.parentId === groupEl.id && !visited.has(child.id)) {
            if (child.type === 'group') {
              emitGroupWithChildren(child);
            } else {
              visited.add(child.id);
              normalizedVisual.push(child);
            }
          }
        });
      };

      visualElements.forEach(el => {
        if (!el || visited.has(el.id)) return;
        if (el.parentId && groupById.has(el.parentId) && !visited.has(el.parentId)) {
          // A child appeared above its parent group in visualElements; emit the parent group first
          emitGroupWithChildren(groupById.get(el.parentId));
          return;
        }
        if (el.type === 'group') {
          emitGroupWithChildren(el);
        } else {
          visited.add(el.id);
          normalizedVisual.push(el);
        }
      });

      return normalizedVisual.reverse();
    };

    const cfg = fullDesign.config || {};
    const rawSides = cfg.sides || {
      front: {
        background: { type: 'gradient', value: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)' },
        elements: []
      },
      back: {
        background: { type: 'color', value: '#111111' },
        elements: []
      }
    };
    const loadedSides = {
      ...rawSides,
      front: {
        ...(rawSides.front || { background: { type: 'gradient', value: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)' } }),
        elements: normalizeGroupLayerOrder(rawSides.front?.elements || [])
      },
      back: {
        ...(rawSides.back || { background: { type: 'color', value: '#111111' } }),
        elements: normalizeGroupLayerOrder(rawSides.back?.elements || [])
      }
    };
    const loadedGlobalStyles = cfg.globalStyles || {
      borderRadius: '18px',
      theme: 'gold-dark',
      borderColor: '#c9a054',
      borderWidth: 6,
      borderStyle: 'solid'
    };
    const loadedCardDimensions = cfg.cardDimensions || { width: 400, height: 560 };
    const loadedLinkedDatasets = Array.isArray(cfg.linkedDatasets) ? cfg.linkedDatasets : [];
    const loadedEnabledFields = (cfg.enabledFields && typeof cfg.enabledFields === 'object') ? cfg.enabledFields : {};
    const loadedHiddenFields = (cfg.hidden_fields && typeof cfg.hidden_fields === 'object')
      ? cfg.hidden_fields
      : ((cfg.hiddenFields && typeof cfg.hiddenFields === 'object') ? cfg.hiddenFields : {});
    const loadedFieldMappings = (cfg.field_mappings && typeof cfg.field_mappings === 'object') ? cfg.field_mappings : {};
    const loadedLockedOptions = (cfg.locked_options && typeof cfg.locked_options === 'object') ? cfg.locked_options : {};
    const loadedRequiredFields = (cfg.required_fields && typeof cfg.required_fields === 'object') ? cfg.required_fields : {};
    const loadedLinkMappings = (cfg.link_mappings && typeof cfg.link_mappings === 'object') ? cfg.link_mappings : {};
    const loadedDateFormats = (cfg.date_formats && typeof cfg.date_formats === 'object')
      ? cfg.date_formats
      : ((cfg.field_date_formats && typeof cfg.field_date_formats === 'object') ? cfg.field_date_formats : {});
    const loadedFormDatasetOrder = Array.isArray(cfg.form_dataset_order) ? cfg.form_dataset_order : [];
    const loadedHidePrepopulated = Boolean(
      cfg.hide_prepopulated_datasets ??
      cfg.hidePrepopulatedDatasets ??
      cfg.skip_prepopulated_datasets ??
      false
    );

    setSides(loadedSides);
    setGlobalStyles(loadedGlobalStyles);
    setCardDimensions(loadedCardDimensions);
    setLinkedDatasets(loadedLinkedDatasets);
    setEnabledFields(loadedEnabledFields);
    setHiddenFields(loadedHiddenFields);
    setFieldMappings(loadedFieldMappings);
    setLockedOptions(loadedLockedOptions);
    setRequiredFields(loadedRequiredFields);
    setLinkMappings(loadedLinkMappings);
    setDateFormats(loadedDateFormats);
    setFormDatasetOrder(loadedFormDatasetOrder);
    setHidePrepopulatedDatasets(loadedHidePrepopulated);

    const loadedConfig = {
      name: designName,
      is_system_template: isSys,
      autoOptimizeImages: design.config?.autoOptimizeImages !== false,
      sides: loadedSides,
      globalStyles: loadedGlobalStyles,
      cardDimensions: loadedCardDimensions,
      linkedDatasets: loadedLinkedDatasets,
      enabledFields: loadedEnabledFields,
      hidden_fields: loadedHiddenFields,
      field_mappings: loadedFieldMappings,
      locked_options: loadedLockedOptions,
      required_fields: loadedRequiredFields,
      link_mappings: loadedLinkMappings,
      date_formats: loadedDateFormats,
      form_dataset_order: loadedFormDatasetOrder,
      hide_prepopulated_datasets: loadedHidePrepopulated,
      hidePrepopulatedDatasets: loadedHidePrepopulated
    };
    originalConfigRef.current = canonicalConfigStringify(loadedConfig);
    setSelectedElementId(null);
    setUndoStack([]);
    setRedoStack([]);
    setViewMode('designer');
  };

  useEffect(() => {
    if (initialDesignId) {
      const fetchInitialDesign = async () => {
        setLoading(true);
        try {
          const res = await fetch(`${API_BASE}/api/v1/designs/${initialDesignId}`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          const design = await res.json();
          if (res.ok && design) {
            handleEditTemplate(design);
          }
        } catch (err) {
          console.error("Failed to load initial design", err);
        } finally {
          setLoading(false);
        }
      };
      fetchInitialDesign();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialDesignId, token, API_BASE]);

  const handleDuplicateTemplate = async (design, e) => {
    e.stopPropagation();
    const name = await modal.prompt("Enter a name for the duplicated template:", `${design.name} (Copy)`, {
      title: "Duplicate Template",
      confirmText: "Duplicate"
    });
    if (!name) return;

    let sourceConfig = design.config;
    if (!sourceConfig?.sides && design.id) {
      try {
        const res = await fetch(`${API_BASE}/api/v1/designs/${design.id}`, {
          headers: {
            Authorization: `Bearer ${token}`,
            'X-Tenant-Domain': window.location.hostname
          }
        });
        if (res.ok) {
          const full = await res.json();
          sourceConfig = full.config;
        }
      } catch (err) {
        console.error("Failed to fetch full template for duplicate", err);
      }
    }

    const payload = {
      name,
      design_type: 'brag_page',
      config: {
        ...sourceConfig,
        is_template: true
      },
      associated_user_id: user?.id || 1,
      tenant_id: user?.tenant_id || null
    };

    try {
      const res = await fetch(`${API_BASE}/api/v1/designs`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
          'X-Tenant-Domain': window.location.hostname
        },
        body: JSON.stringify(payload)
      });
      if (res.ok) {
        setSaveStatus(`Cloned template successfully!`);
        fetchTemplates();
        setTimeout(() => setSaveStatus(''), 3000);
      }
    } catch (err) {
      console.error(err);
    }
  };

  const toggleFlipTemplate = (id) => {
    setFlippedTemplateIds(prev => 
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  const handleSetLinkVariable = (elemId, linkVar) => {
    const newSides = JSON.parse(JSON.stringify(sides));
    ['front', 'back'].forEach(sideKey => {
      if (newSides[sideKey]?.elements) {
        newSides[sideKey].elements = newSides[sideKey].elements.map(e => {
          if (e.id === elemId) {
            return { ...e, linkVariable: linkVar };
          }
          if (e.linkVariable === linkVar) {
            const next = { ...e };
            delete next.linkVariable;
            return next;
          }
          return e;
        });
      }
    });
    saveHistoryState(newSides);
  };

  const handleDeleteTemplate = async (id, e) => {
    if (e && typeof e.stopPropagation === 'function') {
      e.stopPropagation();
    }
    const confirmed = await modal.confirm("Are you sure you want to delete this template?", {
      title: "Delete Template",
      danger: true,
      confirmText: "Delete"
    });
    if (!confirmed) return;

    try {
      const res = await fetch(`${API_BASE}/api/v1/designs/${id}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
          'X-Tenant-Domain': window.location.hostname
        }
      });
      if (res.ok) {
        setSaveStatus("Template deleted.");
        fetchTemplates();
        setTimeout(() => setSaveStatus(''), 3000);
      }
    } catch (err) {
      console.error(err);
    }
  };

  const captureCardPreview = async () => {
    const canvasElement = document.getElementById('catch-card-canvas');
    if (!canvasElement) return null;

    // Temporarily deselect element to hide selection outline/handles
    const prevSelectedId = selectedElementId;
    setSelectedElementId(null);

    // Wait for the DOM / selection box to disappear
    await new Promise(resolve => setTimeout(resolve, 80));

    // Ensure all images in canvas have finished decoding/loading before snapshotting
    const imgElements = Array.from(canvasElement.querySelectorAll('img'));
    if (imgElements.length > 0) {
      await Promise.all(imgElements.map(img => {
        if (img.complete && img.naturalWidth > 0) return Promise.resolve();
        return new Promise(resolve => {
          const timer = setTimeout(resolve, 400);
          img.addEventListener('load', () => { clearTimeout(timer); resolve(); }, { once: true });
          img.addEventListener('error', () => { clearTimeout(timer); resolve(); }, { once: true });
        });
      }));
    }

    // Temporarily reset zoom transform & transition to capture at 100% scale
    const originalTransform = canvasElement.style.transform;
    const originalTransition = canvasElement.style.transition;
    canvasElement.style.transition = 'none';
    canvasElement.style.transform = 'none';

    // Try server-side screenshot rendering first (Option A)
    const useServerSide = localStorage.getItem('useServerSidePreview') !== 'false';
    let previewData = null;

    if (useServerSide) {
      try {
        const cardHtml = canvasElement.outerHTML;
        
        // Include minimal page wrapper with CSS font links and viewport resets
        const htmlDoc = `
          <!DOCTYPE html>
          <html>
          <head>
            <meta charset="utf-8">
            <link rel="preconnect" href="https://fonts.googleapis.com">
            <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
            <link href="https://fonts.googleapis.com/css2?family=Montserrat:wght@400;700&family=Open+Sans:wght@400;700&family=Roboto:wght@400;700&display=swap" rel="stylesheet">
            <style>
              *, ::before, ::after {
                box-sizing: border-box !important;
              }
              html, body {
                margin: 0;
                padding: 0;
                background: transparent;
                overflow: hidden;
                width: 100%;
                height: 100%;
              }
              body {
                display: flex;
                justify-content: center;
                align-items: center;
              }
            </style>
          </head>
          <body>
            ${cardHtml}
          </body>
          </html>
        `;

        const response = await fetch(`${API_BASE}/api/v1/designs/screenshot`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
            'X-Tenant-Domain': window.location.hostname
          },
          body: JSON.stringify({
            html: htmlDoc,
            width: cardDimensions.width || 350,
            height: cardDimensions.height || 490
          })
        });

        if (response.ok) {
          const result = await response.json();
          if (result && result.base64) {
            previewData = result.base64;
          }
        }
        if (!previewData) {
          console.warn("Server-side screenshot failed, falling back to html2canvas");
        }
      } catch (serverErr) {
        console.error("Failed to generate server-side preview, falling back to html2canvas:", serverErr);
      }
    }

    if (!previewData) {
      try {
        const canvas = await html2canvas(canvasElement, {
          backgroundColor: null, // transparent background for rounded corners
          useCORS: true,
          scale: 1.5, // 1.5x scale for decent quality but lightweight payload
          logging: false
        });
        if (canvas && typeof canvas.toDataURL === 'function') {
          try {
            previewData = canvas.toDataURL('image/webp', 0.82);
            if (!previewData || !previewData.startsWith('data:image/webp')) {
              previewData = canvas.toDataURL('image/png');
            }
          } catch (e) {
            previewData = canvas.toDataURL('image/png');
          }
        }
      } catch (err) {
        console.error("Failed to capture template preview:", err);
      }
    }

    // Restore original transform, transition, and selection
    canvasElement.style.transform = originalTransform;
    canvasElement.style.transition = originalTransition;
    if (prevSelectedId) {
      setSelectedElementId(prevSelectedId);
    }

    return previewData;
  };

  const handleSaveDesign = async () => {
    if (saveStatus === 'Saving draft...') return false;
    setSaveStatus('Saving draft...');
    setSaveStepMessage('Rendering front face preview...');

    // Save original active side and deselect elements to prevent capture outlines
    const activeSideOriginal = activeSide;
    const prevSelectedId = selectedElementId;
    setSelectedElementId(null);

    // Capture front side preview
    setActiveSide('front');
    await new Promise(resolve => setTimeout(resolve, 150));
    const frontPreview = await captureCardPreview();

    // Capture back side preview
    setSaveStepMessage('Rendering back face preview...');
    setActiveSide('back');
    await new Promise(resolve => setTimeout(resolve, 150));
    const backPreview = await captureCardPreview();

    // Restore original active side
    setActiveSide(activeSideOriginal);
    if (prevSelectedId) {
      setSelectedElementId(prevSelectedId);
    }

    if (isTrophyCardMode) {
      const dynamicFields = getDynamicFieldsInTemplate();
      const missing = [];
      dynamicFields.forEach(f => {
        const dsKey = f.datasetKey || '';
        const enabledForDS = enabledFields[dsKey] || enabledFields[String(dsKey)] || {};
        const hiddenForDS = hiddenFields[dsKey] || hiddenFields[String(dsKey)] || {};
        const isOmitted = enabledForDS[f.label] === false || (enabledForDS[f.label] === undefined && enabledFields[f.label] === false);
        const isHidden = !isOmitted && (hiddenForDS[f.label] === true || hiddenFields[f.label] === true || enabledForDS[f.label] === 'hidden' || enabledFields[f.label] === 'hidden');
        if (isOmitted || isHidden) return;

        const isReq = requiredFields[f.label] !== undefined ? !!requiredFields[f.label] : !!f.fieldDef?.required;
        const mappingKey = fieldMappings[f.label] || '';
        const isMapped = !!mappingKey;
        const isExif = isMapped && mappingKey.startsWith('photo_exif.');
        const isLocked = (isMapped && !isExif) || (lockedOptions[f.label] !== undefined && lockedOptions[f.label] !== '');
        
        if (isReq && !isLocked) {
          const ds = datasets[f.datasetKey];
          const val = ds?.variables?.[f.label];
          if (val === undefined || val === null || String(val).trim() === '') {
            missing.push(f.alias || f.label.replace(/_/g, ' '));
          }
        }
      });

      if (missing.length > 0) {
        setSaveStatus('');
        setSaveStepMessage('');
        setStatusError(`Please fill in all required fields: ${missing.join(', ')}`);
        setTimeout(() => setStatusError(''), 4000);
        return false;
      }
    }

    setSaveStepMessage(isTrophyCardMode ? 'Saving trophy card changes...' : 'Saving template changes...');

    const configPayload = {
      is_template: isTrophyCardMode ? false : designIsTemplate,
      is_system_template: isTrophyCardMode ? false : isSystemTemplate,
      autoOptimizeImages: autoOptimizeImages !== false,
      status: isTrophyCardMode ? (designStatus || 'proofing') : designStatus,
      cardDimensions,
      globalStyles,
      sides,
      linkedDatasets,
      enabledFields,
      hidden_fields: hiddenFields,
      field_mappings: fieldMappings,
      locked_options: lockedOptions,
      required_fields: requiredFields,
      link_mappings: linkMappings,
      date_formats: dateFormats,
      form_dataset_order: formDatasetOrder,
      hide_prepopulated_datasets: Boolean(hidePrepopulatedDatasets),
      hidePrepopulatedDatasets: Boolean(hidePrepopulatedDatasets),
      preview_image: frontPreview,
      preview_image_back: backPreview
    };

    const isEdit = selectedTemplateId !== null;
    const payload = {
      name: templateName,
      config: configPayload
    };
    if (!isEdit) {
      payload.design_type = 'brag_page';
      payload.associated_user_id = user?.id || 1;
      payload.tenant_id = user?.tenant_id || null;
    }

    const url = selectedTemplateId ? `${API_BASE}/api/v1/designs/${selectedTemplateId}` : `${API_BASE}/api/v1/designs`;
    const method = selectedTemplateId ? 'PATCH' : 'POST';

    try {
      const res = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
          'X-Tenant-Domain': window.location.hostname
        },
        body: JSON.stringify(payload)
      });
      if (res.ok) {
        const data = await res.json();
        if (!selectedTemplateId) {
          setSelectedTemplateId(data.id);
        }
        originalConfigRef.current = canonicalConfigStringify(getCurrentConfig());
        fetchTemplates();
        setSaveStepMessage('');
        setSaveStatus(`Template saved successfully!`);
        setTimeout(() => setSaveStatus(''), 3000);
        if (isTrophyCardMode && onSaveSuccess) {
          onSaveSuccess(data);
        }

        // Check if any unissued coins in CoinVault use this template
        const targetTemplateId = selectedTemplateId || data.id;
        if (targetTemplateId) {
          checkUnissuedCoinsCount(targetTemplateId, templateName);
        }

        return true;
      } else {
        throw new Error("Failed to save.");
      }
    } catch (err) {
      setSaveStepMessage('');
      setSaveStatus('');
      setStatusError(`Save error: ${err.message}`);
      setTimeout(() => setStatusError(''), 3000);
      return false;
    }
  };

  const getCurrentConfig = () => {
    return {
      name: templateName || '',
      is_system_template: isTrophyCardMode ? false : Boolean(isSystemTemplate),
      autoOptimizeImages: autoOptimizeImages !== false,
      sides: sides || {},
      globalStyles: globalStyles || {},
      cardDimensions: cardDimensions || { width: 400, height: 560 },
      linkedDatasets: linkedDatasets || [],
      enabledFields: enabledFields || {},
      hidden_fields: hiddenFields || {},
      field_mappings: fieldMappings || {},
      locked_options: lockedOptions || {},
      required_fields: requiredFields || {},
      link_mappings: linkMappings || {},
      date_formats: dateFormats || {},
      form_dataset_order: formDatasetOrder || [],
      hide_prepopulated_datasets: Boolean(hidePrepopulatedDatasets),
      hidePrepopulatedDatasets: Boolean(hidePrepopulatedDatasets)
    };
  };

  const handleExitDesigner = () => {
    const currentConfigStr = canonicalConfigStringify(getCurrentConfig());
    if (originalConfigRef.current && originalConfigRef.current !== currentConfigStr) {
      setShowExitConfirmModal(true);
    } else {
      if (isTrophyCardMode && onClose) {
        onClose();
      } else {
        setViewMode('manager');
      }
    }
  };

  const renderSavingModal = () => {
    if (saveStatus !== 'Saving draft...') return null;

    const modalNode = (
      <div
        data-testid="saving-template-modal"
        data-html2canvas-ignore="true"
        onClick={(e) => e.stopPropagation()}
        onPointerDown={(e) => e.stopPropagation()}
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(10, 10, 14, 0.72)',
          backdropFilter: 'blur(4px)',
          WebkitBackdropFilter: 'blur(4px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000005,
          cursor: 'wait',
          userSelect: 'none',
          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
        }}
      >
        <div
          style={{
            background: '#1b1b1d',
            border: '1px solid #3c3c42',
            borderRadius: '12px',
            width: '360px',
            maxWidth: '90vw',
            padding: '24px 22px',
            boxShadow: '0 20px 50px rgba(0, 0, 0, 0.75)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            textAlign: 'center',
            gap: '14px'
          }}
        >
          <div
            style={{
              width: '44px',
              height: '44px',
              borderRadius: '50%',
              backgroundColor: 'rgba(22, 119, 255, 0.14)',
              border: '1px solid rgba(22, 119, 255, 0.35)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#1677ff"
              strokeWidth="2.5"
              strokeLinecap="round"
              style={{ animation: 'spin 0.9s linear infinite' }}
            >
              <circle cx="12" cy="12" r="9" stroke="rgba(22, 119, 255, 0.25)" />
              <path d="M12 3a9 9 0 0 1 9 9" stroke="#38bdf8" />
            </svg>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <h3 style={{ margin: 0, fontSize: '15px', fontWeight: '700', color: '#ffffff' }}>
              {isTrophyCardMode ? 'Saving Trophy Card...' : 'Saving Template...'}
            </h3>
            <p style={{ margin: 0, fontSize: '12px', color: '#38bdf8', fontWeight: '600' }}>
              {saveStepMessage || 'Generating card previews and saving...'}
            </p>
            <p style={{ margin: '4px 0 0 0', fontSize: '11px', color: '#8e8e93', lineHeight: '1.45' }}>
              Please wait while the system finishes rendering both card faces and saving your work.
            </p>
          </div>
        </div>
      </div>
    );

    if (typeof document !== 'undefined' && document.body) {
      return createPortal(modalNode, document.body);
    }
    return modalNode;
  };

  const renderExitConfirmModal = () => {
    if (!showExitConfirmModal) return null;

    return (
      <div 
        data-testid="unsaved-changes-modal"
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.75)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 999999,
          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
        }}
      >
        <div style={{
          background: 'var(--card-bg, #1b1b1d)',
          border: '1px solid var(--border-color, #3c3c42)',
          borderRadius: '8px',
          width: '380px',
          padding: '20px',
          boxShadow: '0 8px 24px rgba(0,0,0,0.5)',
          display: 'flex',
          flexDirection: 'column',
          gap: '16px'
        }}>
          <div>
            <h3 style={{ margin: '0 0 8px 0', fontSize: '14px', fontWeight: 'bold', color: 'var(--text-main, #ffffff)' }}>Unsaved Changes</h3>
            <p style={{ margin: 0, fontSize: '12px', color: 'var(--text-mute, #8e8e93)', lineHeight: '1.5' }}>
              You have unsaved changes in this template. Would you like to save them before exiting?
            </p>
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', borderTop: '1px solid var(--border-color, #2d2d30)', paddingTop: '12px' }}>
            <button
              data-testid="exit-modal-cancel-btn"
              disabled={saveStatus === 'Saving draft...'}
              onClick={() => {
                setShowExitConfirmModal(false);
              }}
              style={{
                padding: '6px 12px',
                fontSize: '11px',
                backgroundColor: 'transparent',
                color: '#dfdfe3',
                border: '1px solid #3c3c42',
                borderRadius: '4px',
                cursor: saveStatus === 'Saving draft...' ? 'not-allowed' : 'pointer'
              }}
            >
              Cancel
            </button>
            <button
              data-testid="exit-modal-discard-btn"
              disabled={saveStatus === 'Saving draft...'}
              onClick={() => {
                setShowExitConfirmModal(false);
                if (isTrophyCardMode && onClose) {
                  onClose();
                } else {
                  setViewMode('manager');
                }
              }}
              style={{
                padding: '6px 12px',
                fontSize: '11px',
                backgroundColor: '#3c3c42',
                color: '#ff7b72',
                border: 'none',
                borderRadius: '4px',
                cursor: saveStatus === 'Saving draft...' ? 'not-allowed' : 'pointer',
                fontWeight: 'bold'
              }}
            >
              Exit Without Saving
            </button>
            <button
              data-testid="exit-modal-save-btn"
              disabled={saveStatus === 'Saving draft...'}
              onClick={async () => {
                const saved = await handleSaveDesign();
                if (saved) {
                  setShowExitConfirmModal(false);
                  if (isTrophyCardMode && onClose) {
                    onClose();
                  } else {
                    setViewMode('manager');
                  }
                }
              }}
              style={{
                padding: '6px 12px',
                fontSize: '11px',
                backgroundColor: '#ffffff',
                color: '#18181b',
                border: 'none',
                borderRadius: '4px',
                cursor: saveStatus === 'Saving draft...' ? 'not-allowed' : 'pointer',
                fontWeight: 'bold'
              }}
            >
              {saveStatus === 'Saving draft...' ? 'Saving...' : 'Save & Exit'}
            </button>
          </div>
        </div>
      </div>
    );
  };

  const checkUnissuedCoinsCount = async (targetTemplateId, name) => {
    try {
      const res = await fetch(`${API_BASE}/api/v1/coin-vault/template/${targetTemplateId}/unissued-count`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'X-Tenant-Domain': window.location.hostname
        }
      });
      if (res.ok) {
        const data = await res.json();
        if (data.unissued_count > 0) {
          setUnissuedPrompt({
            templateId: targetTemplateId,
            templateName: name || templateName || 'Template',
            count: data.unissued_count,
            coinNumbers: data.coin_numbers || []
          });
        }
      }
    } catch (err) {
      console.warn('Could not check unissued coins count:', err);
    }
  };

  const handleSyncUnissuedCoins = async (templateId) => {
    if (!templateId) return;
    setSyncingUnissued(true);
    setSyncUnissuedError('');
    try {
      const res = await fetch(`${API_BASE}/api/v1/coin-vault/template/${templateId}/sync-unissued`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
          'X-Tenant-Domain': window.location.hostname
        }
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.detail || 'Failed to update unissued coins');
      }
      setUnissuedPrompt(null);
      setSyncUnissuedError('');
      setSaveStatus(`✓ Updated ${data.updated_count || 'all'} unissued coin(s) in CoinVault!`);
      setTimeout(() => setSaveStatus(''), 4000);
    } catch (err) {
      setSyncUnissuedError(err.message || 'Failed to update unissued coins');
      setStatusError(`Sync error: ${err.message}`);
      setTimeout(() => setStatusError(''), 4000);
    } finally {
      setSyncingUnissued(false);
    }
  };

  const renderUnissuedSyncModal = () => {
    if (!unissuedPrompt) return null;

    const count = unissuedPrompt.count;
    const cardText = count === 1 ? 'unissued card/coin' : 'unissued cards/coins';

    const handleDismiss = () => {
      setUnissuedPrompt(null);
      setSyncUnissuedError('');
    };

    return createPortal(
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.75)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999999,
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
      }}>
        <div style={{
          background: 'var(--card-bg, #1b1b1d)',
          border: '1px solid var(--border-color, #3c3c42)',
          borderRadius: '10px',
          width: '440px',
          maxWidth: '92vw',
          padding: '22px',
          boxShadow: '0 12px 32px rgba(0,0,0,0.6)',
          display: 'flex',
          flexDirection: 'column',
          gap: '16px',
          boxSizing: 'border-box'
        }}>
          {/* Header */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ fontSize: '18px' }}>🪙</span>
              <h3 style={{ margin: 0, fontSize: '15px', fontWeight: 'bold', color: 'var(--text-main, #ffffff)' }}>
                Update CoinVault Cards?
              </h3>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{
                fontSize: '11px',
                fontWeight: '700',
                padding: '2px 8px',
                borderRadius: '12px',
                backgroundColor: 'rgba(56, 189, 248, 0.15)',
                color: '#38bdf8',
                border: '1px solid rgba(56, 189, 248, 0.35)'
              }}>
                {count} {count === 1 ? 'Unissued Card' : 'Unissued Cards'}
              </span>
              <button
                type="button"
                onClick={handleDismiss}
                style={{
                  background: 'none',
                  border: 'none',
                  color: 'var(--text-mute, #94a3b8)',
                  cursor: 'pointer',
                  fontSize: '18px',
                  lineHeight: '1',
                  padding: '2px 4px',
                  borderRadius: '4px'
                }}
                title="Dismiss"
              >
                ✕
              </button>
            </div>
          </div>

          {/* Error Banner if any */}
          {syncUnissuedError && (
            <div style={{
              padding: '10px 14px',
              borderRadius: '6px',
              backgroundColor: 'rgba(239, 68, 68, 0.15)',
              border: '1px solid rgba(239, 68, 68, 0.4)',
              color: '#f87171',
              fontSize: '12px',
              lineHeight: '1.4'
            }}>
              ⚠️ <strong>Error:</strong> {syncUnissuedError}
            </div>
          )}

          {/* Description */}
          <p style={{ margin: 0, fontSize: '13px', color: 'var(--text-main, #cbd5e1)', lineHeight: '1.5' }}>
            This template is currently used by <strong style={{ color: '#38bdf8' }}>{count} {cardText}</strong> in the CoinVault that {count === 1 ? 'has' : 'have'} not been issued yet.
          </p>
          <p style={{ margin: 0, fontSize: '12px', color: 'var(--text-mute, #94a3b8)', lineHeight: '1.4' }}>
            Would you like to apply this new template design and form fields to the unissued {count === 1 ? 'card' : 'cards'} in the Vault?
          </p>

          {/* Actions */}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', borderTop: '1px solid var(--border-color, #2d2d30)', paddingTop: '14px', marginTop: '4px' }}>
            <button
              type="button"
              disabled={syncingUnissued}
              onClick={handleDismiss}
              style={{
                padding: '8px 14px',
                fontSize: '12px',
                fontWeight: '600',
                backgroundColor: 'transparent',
                color: 'var(--text-mute, #94a3b8)',
                border: '1px solid var(--border-color, #3c3c42)',
                borderRadius: '6px',
                cursor: syncingUnissued ? 'not-allowed' : 'pointer'
              }}
            >
              No, Keep Existing
            </button>
            <button
              type="button"
              disabled={syncingUnissued}
              onClick={() => handleSyncUnissuedCoins(unissuedPrompt.templateId)}
              style={{
                padding: '8px 16px',
                fontSize: '12px',
                fontWeight: '700',
                background: 'var(--primary-gradient, var(--primary-color, #0284c7))',
                color: '#ffffff',
                border: 'none',
                borderRadius: '6px',
                cursor: syncingUnissued ? 'wait' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                boxShadow: '0 2px 8px rgba(2, 132, 199, 0.4)'
              }}
            >
              {syncingUnissued ? (
                <span>Updating Vault...</span>
              ) : (
                <span>Yes, Update {count === 1 ? 'Card' : `${count} Cards`}</span>
              )}
            </button>
          </div>
        </div>
      </div>,
      document.body
    );
  };

  const renderShareModal = () => {
    if (!showShareModal || !sharingTemplate) return null;
    
    return createPortal(
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.75)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 999999,
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
      }}>
        <div style={{
          background: 'var(--card-bg, #1b1b1d)',
          border: '1px solid var(--border-color, #3c3c42)',
          borderRadius: '8px',
          width: '460px',
          padding: '20px',
          boxShadow: '0 8px 24px rgba(0,0,0,0.5)',
          display: 'flex',
          flexDirection: 'column',
          gap: '16px'
        }}>
          {/* Header */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid var(--border-color, #2d2d30)', paddingBottom: '10px' }}>
            <span style={{ fontSize: '13px', fontWeight: 'bold', color: 'var(--text-main, #dfdfe3)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              Share Template: {sharingTemplate.name}
            </span>
            <button 
              onClick={() => setShowShareModal(false)}
              style={{ backgroundColor: 'transparent', border: 'none', color: 'var(--text-mute, #8e8e93)', cursor: 'pointer', fontSize: '14px' }}
            >
              ✕
            </button>
          </div>

          {/* Modal Body */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '14px', color: 'var(--text-main, #dfdfe3)' }}>
            {/* Option 1: Share with all tenant users */}
            <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', fontSize: '13px' }}>
              <input
                type="checkbox"
                checked={shareAllTenantUsers}
                onChange={(e) => setShareAllTenantUsers(e.target.checked)}
                style={{ width: '16px', height: '16px', accentColor: '#1677ff' }}
              />
              <span>Share with all users in this tenant</span>
            </label>

            {/* Option 2: Share with specific companies / users */}
            {!shareAllTenantUsers && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', borderTop: '1px solid var(--border-color, #2d2d30)', paddingTop: '12px' }}>
                {/* Share with specific companies */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  <span style={{ fontSize: '12px', fontWeight: '600', color: 'var(--text-mute, #8e8e93)' }}>
                    Share with specific companies:
                  </span>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <select
                      data-testid="share-company-dropdown"
                      value={selectedCompanyId}
                      onChange={(e) => setSelectedCompanyId(e.target.value)}
                      style={{
                        flex: 1,
                        backgroundColor: 'var(--input-bg, #0c0c0e)',
                        border: '1px solid var(--border-color, #3c3c42)',
                        borderRadius: '4px',
                        padding: '7px 10px',
                        color: 'var(--text-main, #dfdfe3)',
                        fontSize: '12px',
                        outline: 'none',
                        cursor: 'pointer'
                      }}
                    >
                      <option value="">-- Select a Company to Share With --</option>
                      {availableCompanies
                        .filter(c => !sharedCompanies.some(sc => sc.id === c.id))
                        .map(c => (
                          <option key={c.id} value={c.id}>
                            {c.name}
                          </option>
                        ))}
                    </select>
                    <button
                      type="button"
                      data-testid="add-share-company-btn"
                      disabled={!selectedCompanyId}
                      onClick={() => handleAddCompany()}
                      style={{
                        background: selectedCompanyId ? 'var(--primary-gradient, var(--primary-color, #1677ff))' : 'var(--disabled-bg, #27272a)',
                        color: selectedCompanyId ? 'white' : 'var(--text-mute, #71717a)',
                        border: 'none',
                        borderRadius: '4px',
                        padding: '6px 14px',
                        fontSize: '12px',
                        fontWeight: '600',
                        cursor: selectedCompanyId ? 'pointer' : 'not-allowed',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      Add Company
                    </button>
                  </div>

                  {/* List of shared companies */}
                  <div style={{
                    minHeight: '40px',
                    maxHeight: '120px',
                    overflowY: 'auto',
                    backgroundColor: 'var(--input-bg, #0c0c0e)',
                    border: '1px solid var(--border-color, #2d2d30)',
                    borderRadius: '4px',
                    padding: '8px',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '6px'
                  }}>
                    {sharedCompanies.length === 0 ? (
                      <span style={{ fontSize: '11px', color: 'var(--text-mute, #8e8e93)', fontStyle: 'italic', textAlign: 'center', padding: '6px 0' }}>
                        No companies selected yet. Select a company above and click Add Company.
                      </span>
                    ) : (
                      sharedCompanies.map(comp => (
                        <div
                          key={comp.id}
                          data-testid={`shared-company-pill-${comp.id}`}
                          style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            backgroundColor: 'rgba(37, 99, 235, 0.12)',
                            border: '1px solid rgba(59, 130, 246, 0.3)',
                            padding: '5px 10px',
                            borderRadius: '4px'
                          }}
                        >
                          <span style={{ fontSize: '12px', fontWeight: '600', color: '#93c5fd', display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <span>🏢</span>
                            <span>{comp.name}</span>
                          </span>
                          <button
                            type="button"
                            data-testid={`remove-company-${comp.id}`}
                            onClick={() => handleRemoveCompany(comp.id)}
                            style={{
                              backgroundColor: 'transparent',
                              border: 'none',
                              color: '#ff4d4f',
                              cursor: 'pointer',
                              fontSize: '11px',
                              fontWeight: '700'
                            }}
                          >
                            Remove
                          </button>
                        </div>
                      ))
                    )}
                  </div>
                </div>

                {/* Optional individual users */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', borderTop: '1px solid var(--border-color, #222226)', paddingTop: '10px' }}>
                  <span style={{ fontSize: '12px', fontWeight: '600', color: 'var(--text-mute, #8e8e93)' }}>Share with specific users (optional):</span>
                  
                  {/* Email Input row */}
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <input
                      type="email"
                      placeholder="user@example.com"
                      value={newShareEmail}
                      onChange={(e) => setNewShareEmail(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          if (newShareEmail.trim() && !sharedUserEmails.includes(newShareEmail.trim())) {
                            setSharedUserEmails([...sharedUserEmails, newShareEmail.trim()]);
                            setNewShareEmail('');
                          }
                        }
                      }}
                      style={{
                        flex: 1,
                        backgroundColor: 'var(--input-bg, #0c0c0e)',
                        border: '1px solid var(--border-color, #3c3c42)',
                        borderRadius: '4px',
                        padding: '6px 10px',
                        color: 'var(--text-main, #dfdfe3)',
                        fontSize: '12px'
                      }}
                    />
                    <button
                      type="button"
                      onClick={() => {
                        if (newShareEmail.trim() && !sharedUserEmails.includes(newShareEmail.trim())) {
                          setSharedUserEmails([...sharedUserEmails, newShareEmail.trim()]);
                          setNewShareEmail('');
                        }
                      }}
                      style={{
                        background: 'var(--primary-gradient, var(--primary-color, #1677ff))',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        padding: '6px 12px',
                        fontSize: '12px',
                        fontWeight: '600',
                        cursor: 'pointer'
                      }}
                    >
                      Add
                    </button>
                  </div>

                  {/* List of shared emails */}
                  <div style={{
                    maxHeight: '100px',
                    overflowY: 'auto',
                    backgroundColor: 'var(--input-bg, #0c0c0e)',
                    border: '1px solid var(--border-color, #2d2d30)',
                    borderRadius: '4px',
                    padding: '6px 8px',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '4px'
                  }}>
                    {sharedUserEmails.length === 0 ? (
                      <span style={{ fontSize: '11px', color: 'var(--text-mute, #8e8e93)', fontStyle: 'italic', textAlign: 'center', padding: '4px 0' }}>
                        No specific users added.
                      </span>
                    ) : (
                      sharedUserEmails.map((email, idx) => (
                        <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: 'var(--panel-bg, #18181a)', padding: '3px 8px', borderRadius: '3px' }}>
                          <span style={{ fontSize: '11px', color: 'var(--text-main, #dfdfe3)' }}>{email}</span>
                          <button
                            type="button"
                            onClick={() => setSharedUserEmails(sharedUserEmails.filter(e => e !== email))}
                            style={{
                              backgroundColor: 'transparent',
                              border: 'none',
                              color: '#ff4d4f',
                              cursor: 'pointer',
                              fontSize: '10px'
                            }}
                          >
                            Remove
                          </button>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Footer Buttons */}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', borderTop: '1px solid var(--border-color, #2d2d30)', paddingTop: '12px', marginTop: '4px' }}>
            <button
              type="button"
              onClick={() => setShowShareModal(false)}
              style={{
                background: 'var(--panel-bg, #2d2d30)',
                color: 'var(--text-main, #dfdfe3)',
                border: '1px solid var(--border-color, #3c3c42)',
                borderRadius: '4px',
                padding: '6px 12px',
                fontSize: '12px',
                cursor: 'pointer'
              }}
            >
              Cancel
            </button>
            <button
              type="button"
              data-testid="save-share-settings-btn"
              onClick={handleSaveShareSettings}
              style={{
                background: 'var(--primary-gradient, var(--primary-color, #1677ff))',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                padding: '6px 16px',
                fontSize: '12px',
                fontWeight: '600',
                cursor: 'pointer'
              }}
            >
              Save
            </button>
          </div>
        </div>
      </div>,
      document.body
    );
  };

  const renderImportModal = () => {
    if (!showImportModal) return null;

    const sourceTenantName = importData?.source_tenant?.brand_name || 'N/A (Generic / Global)';
    const frontElemsCount = (importData?.card?.config?.sides?.front?.elements || []).length;
    const backElemsCount = (importData?.card?.config?.sides?.back?.elements || []).length;
    const cardWidth = importData?.card?.config?.card_width_mm || 63.5;
    const cardHeight = importData?.card?.config?.card_height_mm || 88.9;
    const bundledAssets = importData?.assets || [];

    return createPortal(
      <div 
        data-testid="import-card-modal-backdrop"
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.75)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 999999,
          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
        }}
      >
        <div style={{
          background: 'var(--card-bg, #1b1b1d)',
          border: '1px solid var(--border-color, #3c3c42)',
          borderRadius: '10px',
          width: '560px',
          maxHeight: '90vh',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 12px 36px rgba(0,0,0,0.6)',
          overflow: 'hidden'
        }}>
          {/* Header */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            borderBottom: '1px solid var(--border-color, #2d2d30)',
            padding: '16px 20px',
            background: 'var(--panel-bg, #141416)'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ fontSize: '18px' }}>📥</span>
              <span style={{ fontSize: '14px', fontWeight: 'bold', color: 'var(--text-main, #dfdfe3)', letterSpacing: '0.3px' }}>
                Import Trophy Card & Assets
              </span>
            </div>
            <button 
              onClick={() => setShowImportModal(false)}
              style={{ backgroundColor: 'transparent', border: 'none', color: 'var(--text-mute, #8e8e93)', cursor: 'pointer', fontSize: '16px', padding: '4px' }}
            >
              ✕
            </button>
          </div>

          {/* Modal Content */}
          <div style={{
            padding: '20px',
            overflowY: 'auto',
            display: 'flex',
            flexDirection: 'column',
            gap: '16px',
            color: '#dfdfe3'
          }}>
            {/* Target Scope & Tenant Selector */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              <label style={{ fontSize: '11px', fontWeight: '700', color: '#a2a2a8', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                Target Scope & Card Library
              </label>
              {isGlobalRole ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', cursor: 'pointer', color: '#dfdfe3' }}>
                      <input 
                        type="radio" 
                        name="importScopeOption" 
                        checked={!importIsGlobalScope} 
                        onChange={() => {
                          setImportIsGlobalScope(false);
                          runImportVerification(importData, importTargetTenantId, false);
                        }}
                      />
                      <span>Specific Tenant</span>
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', cursor: 'pointer', color: '#dfdfe3' }}>
                      <input 
                        type="radio" 
                        name="importScopeOption" 
                        checked={importIsGlobalScope} 
                        onChange={() => {
                          setImportIsGlobalScope(true);
                          runImportVerification(importData, null, true);
                        }}
                      />
                      <span>🌐 Global System Template (all tenants)</span>
                    </label>
                  </div>
                  {!importIsGlobalScope && (
                    <select
                      data-testid="import-target-tenant-select"
                      value={importTargetTenantId}
                      onChange={(e) => {
                        setImportTargetTenantId(e.target.value);
                        runImportVerification(importData, e.target.value, false);
                      }}
                      style={{
                        backgroundColor: '#27272a',
                        color: '#ffffff',
                        border: '1px solid #3f3f46',
                        borderRadius: '6px',
                        padding: '8px 12px',
                        fontSize: '13px',
                        outline: 'none',
                        cursor: 'pointer'
                      }}
                    >
                      {tenantList.length === 0 ? (
                        <option value="">No tenants available</option>
                      ) : (
                        tenantList.map(t => (
                          <option key={t.id} value={t.id.toString()}>
                            {t.brand_name || `Tenant #${t.id}`} (ID: {t.id})
                          </option>
                        ))
                      )}
                    </select>
                  )}
                </div>
              ) : (
                <div style={{
                  backgroundColor: '#27272a',
                  color: '#ffffff',
                  border: '1px solid #3f3f46',
                  borderRadius: '6px',
                  padding: '8px 12px',
                  fontSize: '13px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  <span>🏢</span>
                  <span style={{ fontWeight: '600' }}>
                    {tenantConfig?.brand_name || tenantList[0]?.brand_name || `Tenant #${user?.tenant_id}`}
                  </span>
                  <span style={{ fontSize: '11px', color: '#8e8e93', marginLeft: 'auto' }}>(Your Tenant Library)</span>
                </div>
              )}
              <span style={{ fontSize: '11px', color: '#71717a' }}>
                The trophy card template and all bundled assets will be registered in this scope.
              </span>
            </div>

            {/* File Upload Box */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              <label style={{ fontSize: '11px', fontWeight: '700', color: '#a2a2a8', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                Card Export JSON File
              </label>
              <label style={{
                border: '2px dashed ' + (importFileName ? '#3b82f6' : '#3f3f46'),
                borderRadius: '8px',
                padding: '16px',
                textAlign: 'center',
                cursor: 'pointer',
                backgroundColor: importFileName ? 'rgba(59, 130, 246, 0.08)' : 'rgba(255,255,255,0.02)',
                transition: 'all 0.2s',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '6px'
              }}>
                <input 
                  type="file" 
                  accept=".json,application/json" 
                  data-testid="import-file-input"
                  onChange={handleFileChange} 
                  style={{ display: 'none' }} 
                />
                <span style={{ fontSize: '24px' }}>📄</span>
                <span style={{ fontSize: '12px', fontWeight: '600', color: importFileName ? '#93c5fd' : '#dfdfe3' }}>
                  {importFileName ? importFileName : 'Click to select Trophy Card JSON file'}
                </span>
                <span style={{ fontSize: '11px', color: '#71717a' }}>
                  Accepts self-contained .json exports with schema_version: trophycoin_card_v1 or trophycoin_card_v2
                </span>
              </label>
            </div>

            {/* Card Preview Details */}
            {importData && (
              <div style={{
                backgroundColor: '#27272a',
                border: '1px solid #3f3f46',
                borderRadius: '8px',
                padding: '14px',
                display: 'flex',
                flexDirection: 'column',
                gap: '12px'
              }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  <label style={{ fontSize: '11px', fontWeight: '700', color: '#a2a2a8', textTransform: 'uppercase' }}>
                    Card Name for Target Library
                  </label>
                  <input
                    type="text"
                    data-testid="import-card-name-input"
                    value={importCardName}
                    onChange={(e) => setImportCardName(e.target.value)}
                    style={{
                      backgroundColor: '#18181b',
                      border: '1px solid #52525b',
                      borderRadius: '4px',
                      padding: '6px 10px',
                      color: '#ffffff',
                      fontSize: '12px',
                      outline: 'none'
                    }}
                  />
                </div>

                {/* Badges / Specs */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px' }}>
                  <div style={{ backgroundColor: '#18181b', padding: '8px', borderRadius: '4px', border: '1px solid #3f3f46' }}>
                    <div style={{ fontSize: '10px', color: '#71717a' }}>SOURCE TENANT</div>
                    <div style={{ fontSize: '12px', fontWeight: '600', color: '#e4e4e7', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {sourceTenantName}
                    </div>
                  </div>
                  <div style={{ backgroundColor: '#18181b', padding: '8px', borderRadius: '4px', border: '1px solid #3f3f46' }}>
                    <div style={{ fontSize: '10px', color: '#71717a' }}>GEOMETRY</div>
                    <div style={{ fontSize: '12px', fontWeight: '600', color: '#e4e4e7' }}>
                      {frontElemsCount} front / {backElemsCount} back
                    </div>
                  </div>
                  <div style={{ backgroundColor: '#18181b', padding: '8px', borderRadius: '4px', border: '1px solid #3f3f46' }}>
                    <div style={{ fontSize: '10px', color: '#71717a' }}>DIMENSIONS</div>
                    <div style={{ fontSize: '12px', fontWeight: '600', color: '#e4e4e7' }}>
                      {cardWidth} × {cardHeight} mm
                    </div>
                  </div>
                </div>

                {/* Assets preview list */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontSize: '11px', fontWeight: '700', color: '#a2a2a8', textTransform: 'uppercase' }}>
                      Bundled Assets ({bundledAssets.length})
                    </span>
                    <span style={{ fontSize: '10px', color: '#71717a' }}>
                      Will be registered in target asset library
                    </span>
                  </div>

                  {bundledAssets.length === 0 ? (
                    <div style={{ fontSize: '11px', color: '#71717a', fontStyle: 'italic', padding: '6px 0' }}>
                      No embedded raster assets (card uses vector elements and standard fonts).
                    </div>
                  ) : (
                    <div style={{
                      display: 'flex',
                      gap: '8px',
                      overflowX: 'auto',
                      padding: '4px 0',
                      maxHeight: '110px'
                    }}>
                      {bundledAssets.map((ast, idx) => (
                        <div 
                          key={idx}
                          style={{
                            minWidth: '80px',
                            maxWidth: '80px',
                            backgroundColor: '#18181b',
                            border: '1px solid #3f3f46',
                            borderRadius: '6px',
                            padding: '6px',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: '4px',
                            flexShrink: 0
                          }}
                        >
                          <img 
                            src={ast.data_base64 || ast.thumbnail_base64} 
                            alt={ast.filename || 'asset'} 
                            style={{
                              width: '40px',
                              height: '40px',
                              objectFit: 'contain',
                              borderRadius: '4px',
                              backgroundColor: '#000000'
                            }}
                          />
                          <span style={{
                            fontSize: '9px',
                            color: '#e4e4e7',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            width: '100%',
                            textAlign: 'center'
                          }}>
                            {ast.display_name || ast.filename || 'asset'}
                          </span>
                          <span style={{ fontSize: '8px', color: '#71717a' }}>
                            {Math.round((ast.file_size || 0) / 1024)} KB
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Dataset & Field Verification */}
                <div 
                  data-testid="import-dataset-verification"
                  style={{
                    backgroundColor: '#18181b',
                    border: '1px solid #3f3f46',
                    borderRadius: '6px',
                    padding: '12px',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '10px'
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontSize: '11px', fontWeight: '700', color: '#a2a2a8', textTransform: 'uppercase' }}>
                      Dataset & Field Verification {importVerifyReport?.datasets ? `(${importVerifyReport.datasets.length})` : ''}
                    </span>
                    {importVerifying && (
                      <span style={{ fontSize: '10px', color: '#93c5fd' }}>
                        Verifying against target... ⏳
                      </span>
                    )}
                  </div>

                  {importVerifyReport?.datasets && importVerifyReport.datasets.length > 0 ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      {importVerifyReport.datasets.map((ds, idx) => (
                        <div 
                          key={idx}
                          data-testid={`dataset-verif-${idx}`}
                          style={{
                            backgroundColor: '#27272a',
                            border: '1px solid ' + (ds.status === 'matched_existing' ? 'rgba(34, 197, 94, 0.4)' : 'rgba(59, 130, 246, 0.4)'),
                            borderRadius: '6px',
                            padding: '8px 10px',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '6px'
                          }}
                        >
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <span style={{ fontSize: '12px', fontWeight: '600', color: '#ffffff' }}>
                              📊 {ds.name}
                            </span>
                            <span style={{
                              fontSize: '10px',
                              fontWeight: '700',
                              padding: '2px 8px',
                              borderRadius: '10px',
                              backgroundColor: ds.status === 'matched_existing' ? 'rgba(34, 197, 94, 0.2)' : 'rgba(59, 130, 246, 0.2)',
                              color: ds.status === 'matched_existing' ? '#86efac' : '#93c5fd',
                              border: '1px solid ' + (ds.status === 'matched_existing' ? '#22c55e' : '#3b82f6')
                            }}>
                              {ds.status === 'matched_existing' 
                                ? `Matched Existing (${ds.matched_fields_count}/${ds.fields_count} fields)` 
                                : `Auto-Creates in Target (${ds.fields_count} fields)`}
                            </span>
                          </div>

                          {/* Field pills */}
                          {ds.fields && ds.fields.length > 0 && (
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginTop: '2px' }}>
                              {ds.fields.map((f, fIdx) => (
                                <span 
                                  key={fIdx}
                                  style={{
                                    fontSize: '10px',
                                    padding: '2px 6px',
                                    borderRadius: '4px',
                                    backgroundColor: f.status === 'matched' ? 'rgba(34, 197, 94, 0.15)' : 'rgba(234, 179, 8, 0.15)',
                                    color: f.status === 'matched' ? '#86efac' : '#fde047',
                                    border: '1px solid ' + (f.status === 'matched' ? 'rgba(34, 197, 94, 0.3)' : 'rgba(234, 179, 8, 0.3)')
                                  }}
                                >
                                  {f.status === 'matched' ? '✓ ' : '+ '}
                                  {f.label} ({f.type || 'text'})
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div style={{ fontSize: '11px', color: '#71717a', fontStyle: 'italic' }}>
                      {importData?.datasets?.length === 0 
                        ? 'Card uses static text and graphic assets without external datasets.' 
                        : 'Checking referenced dataset bindings...'}
                    </div>
                  )}

                  {/* Warnings if any */}
                  {importVerifyReport?.warnings && importVerifyReport.warnings.length > 0 && (
                    <div style={{
                      backgroundColor: 'rgba(234, 179, 8, 0.1)',
                      border: '1px solid rgba(234, 179, 8, 0.3)',
                      borderRadius: '4px',
                      padding: '8px 10px',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '4px'
                    }}>
                      {importVerifyReport.warnings.map((w, wIdx) => (
                        <span key={wIdx} style={{ fontSize: '11px', color: '#fef08a' }}>
                          ⚠️ {w}
                        </span>
                      ))}
                    </div>
                  )}

                  {/* Auto-create checkbox */}
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '11px', color: '#a1a1aa', cursor: 'pointer', marginTop: '2px' }}>
                    <input 
                      type="checkbox"
                      checked={importAutoCreateDatasets}
                      onChange={(e) => setImportAutoCreateDatasets(e.target.checked)}
                    />
                    <span>Auto-create missing datasets and missing fields in target environment upon import</span>
                  </label>
                </div>
              </div>
            )}

            {/* Error and Success banners */}
            {importError && (
              <div 
                data-testid="import-error-banner"
                style={{
                  backgroundColor: 'rgba(239, 68, 68, 0.15)',
                  border: '1px solid #ef4444',
                  borderRadius: '6px',
                  padding: '10px 14px',
                  color: '#fca5a5',
                  fontSize: '12px'
                }}
              >
                ⚠️ {importError}
              </div>
            )}

            {importSuccess && (
              <div 
                data-testid="import-success-banner"
                style={{
                  backgroundColor: 'rgba(34, 197, 94, 0.15)',
                  border: '1px solid #22c55e',
                  borderRadius: '6px',
                  padding: '10px 14px',
                  color: '#86efac',
                  fontSize: '12px'
                }}
              >
                ✅ {importSuccess}
              </div>
            )}
          </div>

          {/* Footer */}
          <div style={{
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
            gap: '10px',
            borderTop: '1px solid var(--border-color, #2d2d30)',
            padding: '14px 20px',
            background: 'var(--panel-bg, #141416)'
          }}>
            <button
              type="button"
              onClick={() => setShowImportModal(false)}
              disabled={importLoading}
              style={{
                padding: '8px 16px',
                fontSize: '12px',
                backgroundColor: 'transparent',
                color: 'var(--text-mute, #a2a2a8)',
                border: '1px solid var(--border-color, #3c3c42)',
                borderRadius: '6px',
                cursor: 'pointer',
                fontWeight: '600'
              }}
            >
              Cancel
            </button>

            <button
              type="button"
              data-testid="execute-import-btn"
              onClick={handleExecuteImport}
              disabled={!importData || (!importIsGlobalScope && !importTargetTenantId) || importLoading}
              style={{
                padding: '8px 18px',
                fontSize: '12px',
                background: (!importData || (!importIsGlobalScope && !importTargetTenantId) || importLoading) ? 'var(--disabled-bg, #27272a)' : 'var(--primary-gradient, var(--primary-color, #1677ff))',
                color: (!importData || (!importIsGlobalScope && !importTargetTenantId) || importLoading) ? 'var(--disabled-text, #71717a)' : '#ffffff',
                border: 'none',
                borderRadius: '6px',
                cursor: (!importData || (!importIsGlobalScope && !importTargetTenantId) || importLoading) ? 'not-allowed' : 'pointer',
                fontWeight: '700',
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
              }}
            >
              {importLoading ? (
                <>
                  <span>⏳</span>
                  <span>Importing...</span>
                </>
              ) : (
                <>
                  <span>📥</span>
                  <span>{importIsGlobalScope ? 'Import to Global Library' : 'Import to Tenant Library'}</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>,
      document.body
    );
  };

  const renderPushModal = () => {
    if (!showPushModal) return null;

    return createPortal(
      <div 
        data-testid="push-card-modal-backdrop"
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.75)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 999999,
          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
        }}
      >
        <div style={{
          background: 'var(--card-bg, #1b1b1d)',
          border: '1px solid var(--border-color, #3c3c42)',
          borderRadius: '10px',
          width: '580px',
          maxHeight: '90vh',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 12px 36px rgba(0,0,0,0.6)',
          overflow: 'hidden'
        }}>
          {/* Header */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            borderBottom: '1px solid var(--border-color, #2d2d30)',
            padding: '16px 20px',
            background: 'var(--panel-bg, #141416)'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ fontSize: '18px' }}>🚀</span>
              <span style={{ fontSize: '14px', fontWeight: 'bold', color: 'var(--text-main, #dfdfe3)', letterSpacing: '0.3px' }}>
                Push Trophy Card to Production / Remote Server
              </span>
            </div>
            <button 
              onClick={() => setShowPushModal(false)}
              style={{ backgroundColor: 'transparent', border: 'none', color: 'var(--text-mute, #8e8e93)', cursor: 'pointer', fontSize: '16px', padding: '4px' }}
            >
              ✕
            </button>
          </div>

          {/* Modal Content */}
          <div style={{
            padding: '20px',
            overflowY: 'auto',
            display: 'flex',
            flexDirection: 'column',
            gap: '16px',
            color: '#dfdfe3'
          }}>
            {/* Target Remote URL */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <label style={{ fontSize: '11px', fontWeight: '700', color: '#a2a2a8', textTransform: 'uppercase' }}>
                Remote Destination Server URL
              </label>
              <input
                type="text"
                data-testid="push-target-url-input"
                value={pushTargetUrl}
                onChange={(e) => setPushTargetUrl(e.target.value)}
                placeholder="https://trophycoin-app-cxyiczjsma-uc.a.run.app"
                style={{
                  backgroundColor: '#27272a',
                  border: '1px solid #3f3f46',
                  borderRadius: '6px',
                  padding: '8px 12px',
                  color: '#ffffff',
                  fontSize: '13px',
                  outline: 'none'
                }}
              />
            </div>

            {/* Remote Auth Token */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <label style={{ fontSize: '11px', fontWeight: '700', color: '#a2a2a8', textTransform: 'uppercase' }}>
                  Remote Admin JWT Bearer Token
                </label>
                <span style={{ fontSize: '10px', color: '#71717a' }}>Saved locally</span>
              </div>
              <input
                type="password"
                data-testid="push-remote-token-input"
                value={pushRemoteToken}
                onChange={(e) => setPushRemoteToken(e.target.value)}
                placeholder="Paste remote JWT access token..."
                style={{
                  backgroundColor: '#27272a',
                  border: '1px solid #3f3f46',
                  borderRadius: '6px',
                  padding: '8px 12px',
                  color: '#ffffff',
                  fontSize: '13px',
                  outline: 'none'
                }}
              />
            </div>

            {/* Scope selection & Connect */}
            <div style={{
              backgroundColor: '#27272a',
              border: '1px solid #3f3f46',
              borderRadius: '8px',
              padding: '14px',
              display: 'flex',
              flexDirection: 'column',
              gap: '12px'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: '11px', fontWeight: '700', color: '#a2a2a8', textTransform: 'uppercase' }}>
                  Target Scope on Destination
                </span>
                <button
                  type="button"
                  data-testid="push-fetch-tenants-btn"
                  onClick={handleFetchRemoteTenants}
                  disabled={pushLoadingTenants}
                  style={{
                    backgroundColor: '#3f3f46',
                    color: '#ffffff',
                    border: 'none',
                    borderRadius: '4px',
                    padding: '4px 10px',
                    fontSize: '11px',
                    fontWeight: '600',
                    cursor: pushLoadingTenants ? 'not-allowed' : 'pointer'
                  }}
                >
                  {pushLoadingTenants ? 'Connecting... ⏳' : 'Connect & Fetch Tenants 🔄'}
                </button>
              </div>

              {/* Scope Radios */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px', cursor: 'pointer' }}>
                  <input
                    type="radio"
                    name="pushScopeOption"
                    value="global"
                    checked={pushSelectedScope === 'global'}
                    onChange={() => {
                      setPushSelectedScope('global');
                      setPushVerifyReport(null);
                    }}
                  />
                  <span>🌐 <strong>Global System Template</strong> (accessible across all tenants)</span>
                </label>

                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px', cursor: 'pointer' }}>
                  <input
                    type="radio"
                    name="pushScopeOption"
                    value="tenant"
                    checked={pushSelectedScope !== 'global'}
                    onChange={() => {
                      if (pushRemoteTenants.length > 0) {
                        setPushSelectedScope(pushRemoteTenants[0].id.toString());
                      } else {
                        setPushSelectedScope('1');
                      }
                      setPushVerifyReport(null);
                    }}
                  />
                  <span>🏢 <strong>Specific Remote Tenant</strong></span>
                </label>

                {pushSelectedScope !== 'global' && (
                  <div style={{ marginLeft: '24px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    {pushRemoteTenants.length > 0 ? (
                      <select
                        data-testid="push-target-tenant-select"
                        value={pushSelectedScope}
                        onChange={(e) => {
                          setPushSelectedScope(e.target.value);
                          setPushVerifyReport(null);
                        }}
                        style={{
                          backgroundColor: '#18181b',
                          color: '#ffffff',
                          border: '1px solid #52525b',
                          borderRadius: '4px',
                          padding: '6px 10px',
                          fontSize: '12px',
                          outline: 'none'
                        }}
                      >
                        {pushRemoteTenants.map(t => (
                          <option key={t.id} value={t.id.toString()}>
                            {t.brand_name || t.domain_name} (ID: {t.id})
                          </option>
                        ))}
                      </select>
                    ) : (
                      <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                        <input
                          type="number"
                          value={pushSelectedScope === 'global' ? '' : pushSelectedScope}
                          onChange={(e) => setPushSelectedScope(e.target.value)}
                          placeholder="Tenant ID (e.g. 1)"
                          style={{
                            backgroundColor: '#18181b',
                            border: '1px solid #52525b',
                            borderRadius: '4px',
                            padding: '6px 10px',
                            color: '#ffffff',
                            fontSize: '12px',
                            width: '120px'
                          }}
                        />
                        <span style={{ fontSize: '11px', color: '#71717a' }}>Click "Connect & Fetch Tenants" to load tenant list</span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Preflight Verification Action & Report */}
            <div style={{
              backgroundColor: '#18181b',
              border: '1px solid #3f3f46',
              borderRadius: '8px',
              padding: '14px',
              display: 'flex',
              flexDirection: 'column',
              gap: '10px'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: '11px', fontWeight: '700', color: '#a2a2a8', textTransform: 'uppercase' }}>
                  Preflight Remote Verification
                </span>
                <button
                  type="button"
                  data-testid="push-verify-btn"
                  onClick={handleVerifyPush}
                  disabled={pushVerifying}
                  style={{
                    backgroundColor: '#27272a',
                    color: '#93c5fd',
                    border: '1px solid #3b82f6',
                    borderRadius: '4px',
                    padding: '4px 10px',
                    fontSize: '11px',
                    fontWeight: '600',
                    cursor: pushVerifying ? 'not-allowed' : 'pointer'
                  }}
                >
                  {pushVerifying ? 'Verifying... ⏳' : '🔍 Verify on Remote Server'}
                </button>
              </div>

              {pushVerifyReport ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '6px' }}>
                    <div style={{ backgroundColor: '#27272a', padding: '6px 8px', borderRadius: '4px', border: '1px solid #3f3f46' }}>
                      <div style={{ fontSize: '9px', color: '#71717a' }}>TARGET SCOPE</div>
                      <div style={{ fontSize: '11px', fontWeight: '600', color: '#e4e4e7', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {pushVerifyReport.target_scope}
                      </div>
                    </div>
                    <div style={{ backgroundColor: '#27272a', padding: '6px 8px', borderRadius: '4px', border: '1px solid #3f3f46' }}>
                      <div style={{ fontSize: '9px', color: '#71717a' }}>ASSETS</div>
                      <div style={{ fontSize: '11px', fontWeight: '600', color: '#e4e4e7' }}>
                        {pushVerifyReport.assets_count} bundled
                      </div>
                    </div>
                    <div style={{ backgroundColor: '#27272a', padding: '6px 8px', borderRadius: '4px', border: '1px solid #3f3f46' }}>
                      <div style={{ fontSize: '9px', color: '#71717a' }}>DATASETS</div>
                      <div style={{ fontSize: '11px', fontWeight: '600', color: '#e4e4e7' }}>
                        {pushVerifyReport.datasets?.length || 0} referenced
                      </div>
                    </div>
                  </div>

                  {pushVerifyReport.datasets && pushVerifyReport.datasets.length > 0 && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                      {pushVerifyReport.datasets.map((ds, idx) => (
                        <div key={idx} style={{
                          fontSize: '11px',
                          display: 'flex',
                          justifyContent: 'space-between',
                          padding: '4px 8px',
                          borderRadius: '4px',
                          backgroundColor: '#27272a',
                          border: '1px solid ' + (ds.status === 'matched_existing' ? 'rgba(34, 197, 94, 0.3)' : 'rgba(59, 130, 246, 0.3)')
                        }}>
                          <span style={{ fontWeight: '600' }}>📊 {ds.name}</span>
                          <span style={{ color: ds.status === 'matched_existing' ? '#86efac' : '#93c5fd' }}>
                            {ds.status === 'matched_existing' ? `Matched (${ds.matched_fields_count}/${ds.fields_count} fields)` : `Auto-creates (${ds.fields_count} fields)`}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}

                  {pushVerifyReport.warnings && pushVerifyReport.warnings.length > 0 && (
                    <div style={{
                      backgroundColor: 'rgba(234, 179, 8, 0.1)',
                      border: '1px solid rgba(234, 179, 8, 0.3)',
                      borderRadius: '4px',
                      padding: '6px 8px',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '2px'
                    }}>
                      {pushVerifyReport.warnings.map((w, wIdx) => (
                        <span key={wIdx} style={{ fontSize: '10px', color: '#fef08a' }}>⚠️ {w}</span>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <div style={{ fontSize: '11px', color: '#71717a', fontStyle: 'italic' }}>
                  Click "Verify on Remote Server" to test dataset links and asset bundle compatibility prior to pushing.
                </div>
              )}

              {/* Auto-create checkbox */}
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '11px', color: '#a1a1aa', cursor: 'pointer' }}>
                <input 
                  type="checkbox"
                  checked={pushAutoCreateDatasets}
                  onChange={(e) => setPushAutoCreateDatasets(e.target.checked)}
                />
                <span>Auto-create missing datasets and fields on remote server upon push</span>
              </label>
            </div>

            {/* Error & Success banners */}
            {pushError && (
              <div 
                data-testid="push-error-banner"
                style={{
                  backgroundColor: 'rgba(239, 68, 68, 0.15)',
                  border: '1px solid #ef4444',
                  borderRadius: '6px',
                  padding: '10px 14px',
                  color: '#fca5a5',
                  fontSize: '12px'
                }}
              >
                ⚠️ {pushError}
              </div>
            )}

            {pushSuccess && (
              <div 
                data-testid="push-success-banner"
                style={{
                  backgroundColor: 'rgba(34, 197, 94, 0.15)',
                  border: '1px solid #22c55e',
                  borderRadius: '6px',
                  padding: '10px 14px',
                  color: '#86efac',
                  fontSize: '12px'
                }}
              >
                ✅ {pushSuccess}
              </div>
            )}
          </div>

          {/* Footer */}
          <div style={{
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
            gap: '10px',
            borderTop: '1px solid var(--border-color, #2d2d30)',
            padding: '14px 20px',
            background: 'var(--panel-bg, #141416)'
          }}>
            <button
              type="button"
              onClick={() => setShowPushModal(false)}
              disabled={pushLoading}
              style={{
                padding: '8px 16px',
                fontSize: '12px',
                backgroundColor: 'transparent',
                color: 'var(--text-mute, #a2a2a8)',
                border: '1px solid var(--border-color, #3c3c42)',
                borderRadius: '6px',
                cursor: 'pointer',
                fontWeight: '600'
              }}
            >
              Cancel
            </button>

            <button
              type="button"
              data-testid="execute-push-btn"
              onClick={handleExecutePush}
              disabled={pushLoading || !pushTargetUrl}
              style={{
                padding: '8px 18px',
                fontSize: '12px',
                background: (pushLoading || !pushTargetUrl) ? 'var(--disabled-bg, #27272a)' : 'var(--primary-gradient, var(--primary-color, #3b82f6))',
                color: (pushLoading || !pushTargetUrl) ? 'var(--disabled-text, #71717a)' : '#ffffff',
                border: 'none',
                borderRadius: '6px',
                cursor: (pushLoading || !pushTargetUrl) ? 'not-allowed' : 'pointer',
                fontWeight: '700',
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
              }}
            >
              {pushLoading ? (
                <>
                  <span>⏳</span>
                  <span>Pushing to Remote...</span>
                </>
              ) : (
                <>
                  <span>🚀</span>
                  <span>Push Card to Production</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>,
      document.body
    );
  };

  const renderPrintExportModal = () => {
    if (!showPrintExportModal) return null;

    const modalContent = (
      <PrintExportModal
        isOpen={showPrintExportModal}
        onClose={() => {
          if (!isPrintExporting) {
            setShowPrintExportModal(false);
          }
        }}
        cardName={templateName || 'Trophy Card'}
        cardDimensions={cardDimensions}
        onGenerate={handleExecutePrintExport}
        isProcessing={isPrintExporting}
        processingStep={printExportStep}
        error={printExportError}
        success={printExportSuccess}
        onReset={() => {
          setPrintExportError('');
          setPrintExportSuccess(false);
        }}
      />
    );

    return typeof document !== 'undefined' && document.body
      ? createPortal(modalContent, document.body)
      : modalContent;
  };

  // ----------------------------------------------------
  // VECTOR CREATION & SELECTION UTILS
  // ----------------------------------------------------
  const createNewElement = (toolType, x, y) => {
    const newId = `el_${Date.now()}`;
    let newElement = null;

    if (toolType === 'shape') {
      const displayNames = {
        rect: 'Rectangle',
        circle: 'Ellipse',
        pill: 'Rounded Rectangle',
        triangle: 'Triangle',
        diamond: 'Diamond',
        polygon: 'Polygon',
        star: 'Star'
      };
      const dispName = displayNames[selectedShapeSubtype] || 'Shape';
      newElement = {
        id: newId,
        type: 'shape',
        shapeType: selectedShapeSubtype,
        name: `${dispName} Shape`,
        x: x - 50,
        y: y - 25,
        width: 100,
        height: 50,
        fill: '#ffffff',
        strokeColor: '#cbd5e1',
        strokeWidth: 2,
        borderRadius: selectedShapeSubtype === 'circle' ? 999 : selectedShapeSubtype === 'pill' ? 999 : 8,
        visible: true,
        locked: false
      };
    } else if (toolType === 'image') {
      if (selectedImageSubTool === 'image') {
        setIsChangingImageSource(false);
        setPendingImageClickCoords({ x, y });
        setShowAssetSelectorModal(true);
        return; // Intercept: don't place placeholder image yet
      }
      newElement = {
        id: newId,
        type: 'image',
        imageType: 'frame',
        imageVariable: '$image_source',
        name: 'Trophy Frame',
        x: x - 80,
        y: y - 60,
        width: 160,
        height: 120,
        borderRadius: 8,
        strokeWidth: 1,
        strokeColor: '#ffffff',
        visible: true,
        locked: false
      };
    } else if (toolType === 'text') {
      newElement = {
        id: newId,
        type: 'text',
        name: 'Header Label',
        x: x - 100,
        y: y - 15,
        width: 200,
        height: 30,
        text: 'Series 2026',
        fontSize: 14,
        fontFamily: 'sans-serif',
        fontWeight: 'normal',
        color: '#ffffff',
        alignment: 'center',
        rotation: 0,
        visible: true,
        locked: false
      };
    } else if (toolType === 'datagrid') {
      newElement = {
        id: newId,
        type: 'datagrid',
        name: 'Stats Grid',
        x: x - 150,
        y: y - 75,
        width: 300,
        height: 150,
        rows: [
          ['Weight', '$weight'],
          ['Species', '$species']
        ],
        strokeWidth: 1,
        strokeColor: '#334155',
        visible: true,
        locked: false
      };
    } else if (toolType === 'accent') {
      const isVert = selectedDividerSubTool === 'vertical';
      newElement = {
        id: newId,
        type: 'accent',
        dividerType: selectedDividerSubTool,
        name: isVert ? 'Vertical Divider' : 'Horizontal Divider',
        x: isVert ? x - 1 : x - 100,
        y: isVert ? y - 100 : y - 1,
        width: isVert ? 2 : 200,
        height: isVert ? 200 : 2,
        strokeColor: '#cbd5e1',
        strokeWidth: 2,
        fill: '#cbd5e1',
        fillType: 'solid',
        visible: true,
        locked: false
      };
    } else if (toolType === 'certify') {
      newElement = {
        id: newId,
        type: 'certify',
        name: 'Certification Seal',
        x: x - 50,
        y: y - 50,
        width: 100,
        height: 100,
        fill: '#ef4444',
        fillType: 'solid',
        certifyType: 'stamp',
        certifyText: 'TROPHY\nCERTIFIED',
        rotation: 0,
        visible: true,
        locked: false
      };
    } else if (toolType === 'qrcode') {
      newElement = {
        id: newId,
        type: 'qrcode',
        name: 'QR Code',
        x: x - 45,
        y: y - 45,
        width: 90,
        height: 90,
        qrSource: 'card_link', // 'card_link' | 'verify' | 'booking' | 'website' | 'email' | 'custom' | 'dynamic'
        qrValue: '',
        fgColor: '#000000',
        bgColor: '#ffffff',
        margin: 2,
        borderRadius: 6,
        hasBorder: false,
        strokeColor: '#cbd5e1',
        strokeWidth: 1,
        rotation: 0,
        openOnClick: true,
        visible: true,
        locked: false
      };
    }

    if (newElement) {
      saveHistoryState();
      setSides(prevSides => {
        const updatedElements = [...prevSides[activeSide].elements, newElement];
        return {
          ...prevSides,
          [activeSide]: {
            ...prevSides[activeSide],
            elements: updatedElements
          }
        };
      });
      setSelectedElementId(newId);
      setActiveRightTab('transform'); // Auto-select Properties tab
      setActiveTool('select'); // Back to pointer
    }
  };

  const handleCanvasClick = (e) => {
    if (activeTool === 'select') {
      if (e.target === e.currentTarget) {
        setSelectedElementId(null);
      }
      return;
    }
    if (e.target !== e.currentTarget) return; // Only trigger if clicking canvas sheet itself
    
    // Calculate click coordinates relative to the 400x560 canvas
    const rect = e.currentTarget.getBoundingClientRect();
    const clickX = Math.round((e.clientX - rect.left) / zoomLevel);
    const clickY = Math.round((e.clientY - rect.top) / zoomLevel);

    createNewElement(activeTool, clickX, clickY);
  };

  const handlePointerDown = (e, elem, action = 'drag', handle = null) => {
    e.stopPropagation();
    if (elem.locked) return;
    
    setSelectedElementId(elem.id);

    const targetEl = (action === 'rotate' ? e.currentTarget?.parentElement : e.currentTarget) || e.currentTarget;
    const elemRect = targetEl?.getBoundingClientRect ? targetEl.getBoundingClientRect() : { left: e.clientX, top: e.clientY, width: elem.width, height: elem.height };
    const elemCenterX = elemRect.left + elemRect.width / 2;
    const elemCenterY = elemRect.top + elemRect.height / 2;
    
    dragInfo.current = {
      isDragging: action === 'drag',
      isResizing: action === 'resize',
      isRotating: action === 'rotate',
      resizeHandle: handle,
      startX: e.clientX,
      startY: e.clientY,
      startElemX: elem.x,
      startElemY: elem.y,
      startElemW: elem.width,
      startElemH: elem.height,
      startRotation: elem.rotation || 0,
      centerX: elemCenterX,
      centerY: elemCenterY
    };

    document.addEventListener('pointermove', handlePointerMove);
    document.addEventListener('pointerup', handlePointerUp);
  };

  const handlePointerMove = (e) => {
    const info = dragInfo.current;
    if (!info.isDragging && !info.isResizing && !info.isRotating) return;

    const dx = Math.round((e.clientX - info.startX) / zoomLevel);
    const dy = Math.round((e.clientY - info.startY) / zoomLevel);

    const updatedElements = sides[activeSide].elements.map(elem => {
      if (elem.id === selectedElementId) {
        if (info.isRotating) {
          const angleRad = Math.atan2(e.clientY - info.centerY, e.clientX - info.centerX);
          let angleDeg = Math.round((angleRad * (180 / Math.PI)) + 90);
          if (angleDeg < 0) angleDeg += 360;
          if (angleDeg >= 360) angleDeg -= 360;
          if (e.shiftKey) {
            angleDeg = Math.round(angleDeg / 15) * 15;
          }
          return { ...elem, rotation: angleDeg };
        } else if (info.isDragging) {
          return {
            ...elem,
            x: info.startElemX + dx,
            y: info.startElemY + dy
          };
        } else if (info.isResizing) {
          let w = info.startElemW;
          let h = info.startElemH;
          let x = info.startElemX;
          let y = info.startElemY;

          const isConstrained = Boolean(elem.lockAspect || e.shiftKey || elem.type === 'certify' || elem.type === 'qrcode');

          if (isConstrained) {
            const ratio = elem.type === 'qrcode'
              ? 1
              : (elem.aspectRatio || ((info.startElemW && info.startElemH) ? (info.startElemW / info.startElemH) : 1));
            const handle = info.resizeHandle;

            if (handle === 'e' || handle === 'w') {
              w = handle === 'e' 
                ? Math.max(10, info.startElemW + dx)
                : Math.max(10, info.startElemW - dx);
              h = Math.max(10, Math.round(w / ratio));
              if (handle === 'w') {
                x = info.startElemX + (info.startElemW - w);
              }
              y = info.startElemY - Math.round((h - info.startElemH) / 2);
            } else if (handle === 's' || handle === 'n') {
              h = handle === 's'
                ? Math.max(10, info.startElemH + dy)
                : Math.max(10, info.startElemH - dy);
              w = Math.max(10, Math.round(h * ratio));
              if (handle === 'n') {
                y = info.startElemY + (info.startElemH - h);
              }
              x = info.startElemX - Math.round((w - info.startElemW) / 2);
            } else {
              // Corner handles: 'se', 'sw', 'ne', 'nw'
              const scaleX = handle.includes('w')
                ? (info.startElemW - dx) / info.startElemW
                : (info.startElemW + dx) / info.startElemW;
              const scaleY = handle.includes('n')
                ? (info.startElemH - dy) / info.startElemH
                : (info.startElemH + dy) / info.startElemH;

              const scale = Math.max(0.05, Math.abs(dx) >= Math.abs(dy * ratio) ? scaleX : scaleY);

              w = Math.max(elem.type === 'qrcode' ? 20 : 10, Math.round(info.startElemW * scale));
              h = elem.type === 'qrcode' ? w : Math.max(10, Math.round(w / ratio));

              if (handle.includes('w')) {
                x = info.startElemX + (info.startElemW - w);
              }
              if (handle.includes('n')) {
                y = info.startElemY + (info.startElemH - h);
              }
            }
          } else {
            if (info.resizeHandle.includes('e')) w = Math.max(10, info.startElemW + dx);
            if (info.resizeHandle.includes('s')) h = Math.max(10, info.startElemH + dy);
            if (info.resizeHandle.includes('w')) {
              const nextW = Math.max(10, info.startElemW - dx);
              x = info.startElemX + (info.startElemW - nextW);
              w = nextW;
            }
            if (info.resizeHandle.includes('n')) {
              const nextH = Math.max(10, info.startElemH - dy);
              y = info.startElemY + (info.startElemH - nextH);
              h = nextH;
            }
          }

          return { ...elem, x, y, width: w, height: h };
        }
      }
      return elem;
    });

    setSides({
      ...sides,
      [activeSide]: {
        ...sides[activeSide],
        elements: updatedElements
      }
    });
  };

  const handlePointerUp = () => {
    if (dragInfo.current.isDragging || dragInfo.current.isResizing) {
      saveHistoryState();
    }
    dragInfo.current.isDragging = false;
    dragInfo.current.isResizing = false;
    dragInfo.current.resizeHandle = null;
    document.removeEventListener('pointermove', handlePointerMove);
    document.removeEventListener('pointerup', handlePointerUp);
  };

  const openImageAdjustModal = (elem) => {
    const target = elem || activeElement;
    if (!target) return;
    if (target.id) setSelectedElementId(target.id);
    setAdjustingElement(target);
    setTempPanX(target.imgPanX || 0);
    setTempPanY(target.imgPanY || 0);
    setTempZoom(target.imgZoom || 1);
    setTempFrameW(target.width || 160);
    setTempFrameH(target.height || 120);
    setLockCropAspect(false);
    setSelectedCropPreset('custom');
    
    // Preload image natural dimensions so uncropped aspect ratio is known immediately
    const src = getResolvedImageSrc(target);
    if (src) {
      const img = new Image();
      img.onload = () => {
        setImgNaturalSize({ w: img.naturalWidth, h: img.naturalHeight });
      };
      img.src = src;
    } else {
      setImgNaturalSize({ w: 0, h: 0 });
    }
    setShowImageAdjustModal(true);
  };

  const handleResampleImage = async (element) => {
    if (!element) return;
    const targetSrc = element.originalSrc || element.src;
    if (!targetSrc) return;
    setResamplingLoading(true);
    setResampleMsg(null);
    try {
      const cleanPath = targetSrc.split('?')[0];
      const authToken = token || localStorage.getItem('token') || sessionStorage.getItem('token') || (user && user.token) || '';
      const res = await fetch(`${API_BASE}/api/upload/resample`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(authToken ? { 'Authorization': `Bearer ${authToken}` } : {}),
          'X-Tenant-Domain': window.location.hostname
        },
        body: JSON.stringify({
          url: cleanPath,
          quality: resampleQuality,
          max_dimension: resampleMaxDim === 0 ? null : resampleMaxDim
        })
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.detail || 'Resampling failed');
      }
      saveHistoryState();
      updateActiveElement({
        optimizedSrc: data.optimized_url,
        originalSrc: data.original_url || element.originalSrc || targetSrc,
        optimizeImage: true,
        optimizedSize: data.optimized_size,
        originalSize: data.original_size,
        savingsPercent: data.savings_percent
      });
      setResampleMsg(`Resampled from master! (${Math.round(data.original_size / 1024)} KB → ${Math.round(data.optimized_size / 1024)} KB, -${data.savings_percent}%)`);
    } catch (err) {
      setResampleMsg(`Notice: ${err.message}`);
    } finally {
      setResamplingLoading(false);
    }
  };

  const handleSelectImageAsset = (assetUrl) => {
    let cleanSrc = assetUrl;
    if (typeof cleanSrc === 'string' && cleanSrc.includes('/uploads/')) {
      const pathPart = cleanSrc.substring(cleanSrc.indexOf('/uploads/'));
      cleanSrc = pathPart.split('?')[0];
    }
    if (isChangingImageSource) {
      if (activeElement) {
        // Newly loaded picture source starts uncropped, opening adjuster so user can pan/zoom or keep full photo
        const updatedTarget = {
          ...activeElement,
          src: cleanSrc,
          originalSrc: cleanSrc,
          optimizedSrc: null,
          optimizeImage: true,
          cropApplied: false,
          imgPanX: 0,
          imgPanY: 0,
          imgZoom: 1
        };
        updateActiveElement({
          src: cleanSrc,
          originalSrc: cleanSrc,
          optimizedSrc: null,
          optimizeImage: true,
          cropApplied: false,
          imgPanX: 0,
          imgPanY: 0,
          imgZoom: 1
        });
        if (activeElement.type === 'image') {
          openImageAdjustModal(updatedTarget);
        }
      }
      setShowAssetSelectorModal(false);
      setIsChangingImageSource(false);
      return;
    }
    // If an image element is currently active and selected, update its source directly
    if (activeElement && activeElement.type === 'image' && !pendingImageClickCoords) {
      const updatedTarget = {
        ...activeElement,
        src: cleanSrc,
        originalSrc: cleanSrc,
        optimizedSrc: null,
        optimizeImage: true,
        cropApplied: false,
        imgPanX: 0,
        imgPanY: 0,
        imgZoom: 1
      };
      updateActiveElement({
        src: cleanSrc,
        originalSrc: cleanSrc,
        optimizedSrc: null,
        optimizeImage: true,
        cropApplied: false,
        imgPanX: 0,
        imgPanY: 0,
        imgZoom: 1
      });
      openImageAdjustModal(updatedTarget);
      setShowAssetSelectorModal(false);
      return;
    }

    const coords = pendingImageClickCoords || {
      x: Math.round(cardDimensions.width / 2),
      y: Math.round(cardDimensions.height / 2)
    };
    const newId = `elem-${Date.now()}`;
    const newElement = {
      id: newId,
      type: 'image',
      imageType: 'image',
      name: 'Image Layer',
      x: coords.x - 80,
      y: coords.y - 60,
      width: 160,
      height: 120,
      src: cleanSrc,
      originalSrc: cleanSrc,
      optimizedSrc: null,
      optimizeImage: true,
      cropApplied: false,
      imgPanX: 0,
      imgPanY: 0,
      imgZoom: 1,
      borderRadius: 0,
      strokeWidth: 0,
      strokeColor: '#ffffff',
      hasBorder: false,
      fill: 'transparent',
      visible: true,
      locked: false
    };

    saveHistoryState();
    setSides({
      ...sides,
      [activeSide]: {
        ...sides[activeSide],
        elements: [...sides[activeSide].elements, newElement]
      }
    });
    setSelectedElementId(newId);
    setPendingImageClickCoords(null);
    setShowAssetSelectorModal(false);
    openImageAdjustModal(newElement);
  };

  const handleAddGroupFolder = () => {
    const newId = `group-${Date.now()}`;
    const newGroup = {
      id: newId,
      type: 'group',
      name: 'New Group',
      collapsed: false,
      visible: true,
      locked: false
    };
    saveHistoryState();
    setSides({
      ...sides,
      [activeSide]: {
        ...sides[activeSide],
        elements: [...sides[activeSide].elements, newGroup]
      }
    });
    setSelectedElementId(newId);
  };

  const handleDuplicateElement = () => {
    if (!selectedElementId) return;
    const activeElem = sides[activeSide].elements.find(el => el.id === selectedElementId);
    if (!activeElem) return;

    saveHistoryState();
    const newElements = [...sides[activeSide].elements];
    
    if (activeElem.type === 'group') {
      const newGroupId = `group-${Date.now()}`;
      const clonedGroup = {
        ...activeElem,
        id: newGroupId,
        name: activeElem.name ? `${activeElem.name} (Copy)` : 'Group (Copy)',
      };
      
      const children = sides[activeSide].elements.filter(el => el.parentId === activeElem.id);
      children.forEach((child, idx) => {
        const newChildId = `el-${Date.now()}-${idx}-${Math.random().toString(36).substr(2, 4)}`;
        const clonedChild = {
          ...child,
          id: newChildId,
          parentId: newGroupId,
          x: child.x !== undefined ? child.x + 15 : undefined,
          y: child.y !== undefined ? child.y + 15 : undefined
        };
        newElements.push(clonedChild);
      });

      // Push the group folder AFTER its children in elements so that in the reversed Layers list
      // the folder header appears directly ABOVE its children.
      newElements.push(clonedGroup);
      
      setSides({
        ...sides,
        [activeSide]: {
          ...sides[activeSide],
          elements: newElements
        }
      });
      setSelectedElementId(newGroupId);
    } else {
      const newId = `el-${Date.now()}-${Math.random().toString(36).substr(2, 4)}`;
      const clonedElement = {
        ...activeElem,
        id: newId,
        name: activeElem.name ? `${activeElem.name} (Copy)` : undefined,
        x: activeElem.x !== undefined ? activeElem.x + 15 : undefined,
        y: activeElem.y !== undefined ? activeElem.y + 15 : undefined
      };
      
      if (activeElem.parentId) {
        const activeIdx = newElements.findIndex(el => el.id === activeElem.id);
        if (activeIdx !== -1) {
          newElements.splice(activeIdx + 1, 0, clonedElement);
        } else {
          newElements.push(clonedElement);
        }
      } else {
        newElements.push(clonedElement);
      }
      
      setSides({
        ...sides,
        [activeSide]: {
          ...sides[activeSide],
          elements: newElements
        }
      });
      setSelectedElementId(newId);
    }
  };

  const handleDuplicateElementToOpposite = () => {
    if (!selectedElementId) return;
    const activeElem = sides[activeSide].elements.find(el => el.id === selectedElementId);
    if (!activeElem) return;

    const targetSide = activeSide === 'front' ? 'back' : 'front';
    saveHistoryState();
    
    const newElements = [...sides[targetSide].elements];
    let newSelectedId = null;

    if (activeElem.type === 'group') {
      const newGroupId = `group-${Date.now()}`;
      const clonedGroup = {
        ...activeElem,
        id: newGroupId,
        name: activeElem.name ? `${activeElem.name} (Copy)` : 'Group (Copy)',
        parentId: undefined
      };
      
      newSelectedId = newGroupId;
      
      const children = sides[activeSide].elements.filter(el => el.parentId === activeElem.id);
      children.forEach((child, idx) => {
        const newChildId = `el-${Date.now()}-${idx}-${Math.random().toString(36).substr(2, 4)}`;
        const clonedChild = {
          ...child,
          id: newChildId,
          parentId: newGroupId,
          x: child.x,
          y: child.y
        };
        newElements.push(clonedChild);
      });

      // Push the group folder AFTER its children in elements so that in the reversed Layers list
      // the folder header appears directly ABOVE its children.
      newElements.push(clonedGroup);
    } else {
      const newId = `el-${Date.now()}-${Math.random().toString(36).substr(2, 4)}`;
      const clonedElement = {
        ...activeElem,
        id: newId,
        name: activeElem.name ? `${activeElem.name} (Copy)` : undefined,
        parentId: undefined,
        x: activeElem.x,
        y: activeElem.y
      };
      
      newElements.push(clonedElement);
      newSelectedId = newId;
    }

    setSides(prevSides => ({
      ...prevSides,
      [targetSide]: {
        ...prevSides[targetSide],
        elements: newElements
      }
    }));
    
    setActiveSide(targetSide);
    setSelectedElementId(newSelectedId);
    if (activeElem.type !== 'group') {
      setActiveRightTab('transform');
    }
  };

  const toggleGroupCollapse = (groupId) => {
    setSides({
      ...sides,
      [activeSide]: {
        ...sides[activeSide],
        elements: sides[activeSide].elements.map(el => 
          el.id === groupId ? { ...el, collapsed: !el.collapsed } : el
        )
      }
    });
  };

  const getElementDepth = (elem) => {
    let depth = 0;
    let current = elem;
    while (current && current.parentId) {
      depth++;
      const targetId = current.parentId;
      const parent = sides[activeSide].elements.find(el => el.id === targetId);
      current = parent;
      if (depth > 10) break;
    }
    return depth;
  };

  const toggleElementLock = (elementId, currentLockedState) => {
    saveHistoryState();
    const nextLockedState = !currentLockedState;
    setSides(prevSides => {
      const currentSideElements = prevSides[activeSide].elements;
      const elem = currentSideElements.find(el => el.id === elementId);
      return {
        ...prevSides,
        [activeSide]: {
          ...prevSides[activeSide],
          elements: currentSideElements.map(el => {
            const isSelf = el.id === elementId;
            const isChild = elem && elem.type === 'group' && el.parentId === elementId;
            if (isSelf || isChild) {
              return { ...el, locked: nextLockedState };
            }
            return el;
          })
        }
      };
    });
  };

  const toggleElementVisibility = (elementId, isVisible) => {
    saveHistoryState();
    setSides(prevSides => {
      const currentSideElements = prevSides[activeSide].elements;
      return {
        ...prevSides,
        [activeSide]: {
          ...prevSides[activeSide],
          elements: currentSideElements.map(el =>
            el.id === elementId ? { ...el, visible: isVisible } : el
          )
        }
      };
    });
  };

  const handleDeleteElement = (id) => {
    saveHistoryState();
    
    // Find all children belonging to this group if deleted item is a group
    const toDeleteIds = [id];
    const elem = sides[activeSide].elements.find(e => e.id === id);
    if (elem && elem.type === 'group') {
      sides[activeSide].elements.forEach(el => {
        if (el.parentId === id) {
          toDeleteIds.push(el.id);
        }
      });
    }

    setSides({
      ...sides,
      [activeSide]: {
        ...sides[activeSide],
        elements: sides[activeSide].elements.filter(e => !toDeleteIds.includes(e.id))
      }
    });
    setSelectedElementId(null);
  };

  const handleDragStart = (e, index) => {
    setDraggedLayerIndex(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
  };

  const handleDrop = (e, targetIndex) => {
    e.preventDefault();
    e.stopPropagation();
    if (draggedLayerIndex === null || draggedLayerIndex === targetIndex) return;

    saveHistoryState();

    const visualElements = [...sides[activeSide].elements].reverse();
    const draggedItem = visualElements[draggedLayerIndex];
    const targetItem = visualElements[targetIndex];

    if (!draggedItem || !targetItem) {
      setDraggedLayerIndex(null);
      return;
    }

    let newElements;

    if (draggedItem.type === 'group') {
      // Dragging a folder group moves the group and all its children together
      // Deep copy children and group to ensure new references
      const children = visualElements.filter(el => el.parentId === draggedItem.id).map(el => ({ ...el }));
      const groupClone = { ...draggedItem };
      const itemsToMove = [groupClone, ...children];

      // Remove the group and its children from the list, deep copying remaining
      const remaining = visualElements
        .filter(el => el.id !== draggedItem.id && el.parentId !== draggedItem.id)
        .map(el => ({ ...el }));

      // Find the target in the remaining elements
      const targetInRemaining = remaining.find(el => el.id === targetItem.id);
      if (targetInRemaining) {
        const targetIdx = remaining.indexOf(targetInRemaining);
        const realTarget = targetInRemaining.parentId
          ? remaining.find(el => el.id === targetInRemaining.parentId)
          : targetInRemaining;

        if (realTarget && realTarget.type === 'group') {
          // Insert after the target group and all its children
          let insertIdx = remaining.indexOf(realTarget) + 1;
          while (insertIdx < remaining.length && remaining[insertIdx].parentId === realTarget.id) {
            insertIdx++;
          }
          remaining.splice(insertIdx, 0, ...itemsToMove);
        } else {
          // Insert at the target item's position
          remaining.splice(targetIdx, 0, ...itemsToMove);
        }
      } else {
        // Fallback: append to end
        remaining.push(...itemsToMove);
      }
      newElements = remaining.reverse();
    } else {
      // Dragging a normal layer - clone the list and elements to keep updates pure
      const clonedVisualElements = visualElements.map(el => ({ ...el }));
      const [draggedElement] = clonedVisualElements.splice(draggedLayerIndex, 1);
      const item = { ...draggedElement };

      // Re-find targetItem in the cloned list to get its new index
      const clonedTargetItem = clonedVisualElements.find(el => el.id === targetItem.id);
      if (clonedTargetItem) {
        const newTargetIndex = clonedVisualElements.indexOf(clonedTargetItem);

        if (clonedTargetItem.type === 'group') {
          // Place it inside the group as the first element (physically index + 1 in visualElements)
          item.parentId = clonedTargetItem.id;
          clonedVisualElements.splice(newTargetIndex + 1, 0, item);
        } else if (clonedTargetItem.parentId) {
          // Place it at the target item's position and inherit the group
          item.parentId = clonedTargetItem.parentId;
          clonedVisualElements.splice(newTargetIndex, 0, item);
        } else {
          // Place it at the target item's position and remove from group
          delete item.parentId;
          clonedVisualElements.splice(newTargetIndex, 0, item);
        }
      } else {
        // Fallback: just push back
        clonedVisualElements.push(item);
      }
      newElements = clonedVisualElements.reverse();
    }

    setSides({
      ...sides,
      [activeSide]: {
        ...sides[activeSide],
        elements: newElements
      }
    });

    setDraggedLayerIndex(null);
  };

  const handleContainerDrop = (e) => {
    e.preventDefault();
    if (draggedLayerIndex === null) return;

    saveHistoryState();

    const visualElements = [...sides[activeSide].elements].reverse();
    const draggedItem = visualElements[draggedLayerIndex];

    if (!draggedItem) {
      setDraggedLayerIndex(null);
      return;
    }

    let newElements;

    if (draggedItem.type === 'group') {
      // Dragging a folder group moves the group and all its children together to the bottom
      const children = visualElements.filter(el => el.parentId === draggedItem.id).map(el => ({ ...el }));
      const groupClone = { ...draggedItem };
      const itemsToMove = [groupClone, ...children];

      const remaining = visualElements
        .filter(el => el.id !== draggedItem.id && el.parentId !== draggedItem.id)
        .map(el => ({ ...el }));

      remaining.push(...itemsToMove);
      newElements = remaining.reverse();
    } else {
      // Dragging a normal layer moves it to the bottom and removes it from its group
      const clonedVisualElements = visualElements.map(el => ({ ...el }));
      const [draggedElement] = clonedVisualElements.splice(draggedLayerIndex, 1);
      const item = { ...draggedElement };
      delete item.parentId;
      clonedVisualElements.push(item);
      newElements = clonedVisualElements.reverse();
    }

    setSides({
      ...sides,
      [activeSide]: {
        ...sides[activeSide],
        elements: newElements
      }
    });

    setDraggedLayerIndex(null);
  };

  const handleDragEnd = () => {
    setDraggedLayerIndex(null);
  };

  const applyRenderer = (val, rendererId, fieldObj = null) => {
    if (!rendererId || rendererId === 'none') return val;
    const strVal = String(val).trim();

    if (rendererId === 'uppercase') {
      return strVal.toUpperCase();
    }
    if (rendererId === 'lowercase') {
      return strVal.toLowerCase();
    }
    if (rendererId === 'titlecase') {
      return strVal.toLowerCase().replace(/\b\w/g, c => c.toUpperCase());
    }
    if (rendererId === 'metric_to_imperial') {
      const numMatch = strVal.match(/^([\d.]+)\s*(kg|cm|m|g|ml)?$/i);
      if (numMatch) {
        const num = parseFloat(numMatch[1]);
        const unit = numMatch[2] ? numMatch[2].toLowerCase() : '';
        if (unit === 'kg') {
          return `${(num * 2.20462).toFixed(1)} lbs`;
        }
        if (unit === 'g') {
          return `${(num * 0.035274).toFixed(1)} oz`;
        }
        if (unit === 'cm') {
          return `${(num * 0.393701).toFixed(1)} in`;
        }
        if (unit === 'm') {
          return `${(num * 3.28084).toFixed(1)} ft`;
        }
      }
      return strVal;
    }
    if (rendererId === 'pad_5') {
      const cleanNum = strVal.replace(/\D/g, '');
      if (!cleanNum) return strVal;
      return cleanNum.padStart(5, '0');
    }
    if (rendererId === 'prepend_hash_pad_5') {
      const cleanNum = strVal.replace(/\D/g, '');
      if (!cleanNum) return strVal;
      return `#${cleanNum.padStart(5, '0')}`;
    }

    try {
      const saved = localStorage.getItem('customRegexRenderers');
      let customRules = saved ? JSON.parse(saved) : [];
      let rule = customRules.find(r => String(r.id) === String(rendererId));
      
      // Fallback: If not in local storage, retrieve from database payload on fieldObj
      if (!rule && fieldObj && fieldObj.custom_renderer && String(fieldObj.custom_renderer.id) === String(rendererId)) {
        rule = fieldObj.custom_renderer;
        try {
          const updatedRules = [...customRules, rule];
          localStorage.setItem('customRegexRenderers', JSON.stringify(updatedRules));
        } catch (storageErr) {
          console.warn("[applyRenderer] Failed to auto-cache custom rule to localStorage:", storageErr);
        }
      }

      if (rule && rule.regex) {
        let pattern = rule.regex;
        let flags = 'g';
        if (pattern.startsWith('/')) {
          const lastSlashIdx = pattern.lastIndexOf('/');
          if (lastSlashIdx > 0) {
            flags = pattern.slice(lastSlashIdx + 1) || 'g';
            pattern = pattern.slice(1, lastSlashIdx);
          }
        }
        const re = new RegExp(pattern, flags);
        const res = strVal.replace(re, rule.replace);
        console.log(`[applyRenderer] SUCCESS rendererId: "${rendererId}", pattern: "${pattern}", flags: "${flags}", replace: "${rule.replace}", val: "${strVal}", result: "${res}"`);
        return res;
      } else {
        console.log(`[applyRenderer] Rule not found for ID: "${rendererId}". Available rules:`, customRules);
      }
    } catch (e) {
      console.error("Failed to parse custom regex rules or apply custom regex:", e);
    }

    return strVal;
  };

  const parseColorToHexAndOpacity = (colorStr) => {
    let color = '#ffffff';
    let opacity = 1;

    if (!colorStr) return { color, opacity };

    const str = colorStr.trim().toLowerCase();

    if (str.startsWith('#')) {
      color = str;
      return { color, opacity };
    }

    if (str.startsWith('rgba') || str.startsWith('rgb')) {
      const match = str.match(/rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*(?:,\s*([\d.]+)\s*)?\)/);
      if (match) {
        const r = parseInt(match[1]);
        const g = parseInt(match[2]);
        const b = parseInt(match[3]);
        const a = match[4] !== undefined ? parseFloat(match[4]) : 1;

        const toHex = (val) => {
          const hex = val.toString(16);
          return hex.length === 1 ? '0' + hex : hex;
        };
        color = '#' + toHex(r) + toHex(g) + toHex(b);
        opacity = a;
      }
    } else {
      color = str;
    }

    return { color, opacity };
  };

  const parseCSSGradient = (str) => {
    const defaultGrad = {
      type: 'linear',
      angle: 90,
      stops: [
        { color: '#ffffff', position: 0, opacity: 1 },
        { color: '#000000', position: 100, opacity: 1 }
      ]
    };
    if (!str || typeof str !== 'string') return defaultGrad;

    try {
      const type = str.includes('radial-gradient') ? 'radial' : 'linear';
      let angle = 90;

      if (type === 'linear') {
        const angleMatch = str.match(/(\d+)deg/);
        if (angleMatch) {
          angle = parseInt(angleMatch[1]);
        }
      }

      const stopsMatch = str.match(/gradient\((.*)\)/);
      if (stopsMatch) {
        const inner = stopsMatch[1];
        const parts = [];
        let current = '';
        let parenCount = 0;
        for (let i = 0; i < inner.length; i++) {
          const char = inner[i];
          if (char === '(') parenCount++;
          else if (char === ')') parenCount--;
          
          if (char === ',' && parenCount === 0) {
            parts.push(current.trim());
            current = '';
          } else {
            current += char;
          }
        }
        if (current.trim()) {
          parts.push(current.trim());
        }

        const stopParts = parts.filter(p => !p.includes('deg') && p !== 'circle');
        const stops = stopParts.map(p => {
          const lastSpace = p.lastIndexOf(' ');
          if (lastSpace !== -1) {
            const rawColor = p.slice(0, lastSpace).trim();
            const posStr = p.slice(lastSpace).trim();
            const position = parseInt(posStr.replace('%', ''));
            const { color, opacity } = parseColorToHexAndOpacity(rawColor);
            return { color, position: isNaN(position) ? 0 : position, opacity };
          }
          const { color, opacity } = parseColorToHexAndOpacity(p);
          return { color, position: 0, opacity };
        });

        if (stops.length >= 2) {
          return { type, angle, stops };
        }
      }
    } catch (e) {
      console.error("Failed to parse CSS gradient:", e);
    }

    return defaultGrad;
  };

  const getGlobalColors = () => {
    const colors = [];

    // 1. Backgrounds
    if (sides.front?.background?.value) {
      colors.push(sides.front.background.value);
    }
    if (sides.back?.background?.value) {
      colors.push(sides.back.background.value);
    }

    // 2. Global Border
    if (globalStyles?.borderColor) {
      colors.push(globalStyles.borderColor);
    }

    // 3. Layer elements colors
    const checkElement = (elem) => {
      if (!elem) return;
      if (elem.type === 'text') {
        if (elem.color) colors.push(elem.color);
        if (elem.textStrokeColor) colors.push(elem.textStrokeColor);
        if (elem.shadowColor) colors.push(elem.shadowColor);
      } else if (elem.type === 'shape' || elem.type === 'datagrid' || elem.type === 'certify' || elem.type === 'accent') {
        if (elem.fill) colors.push(elem.fill);
        if (elem.strokeColor) colors.push(elem.strokeColor);
        if (elem.stroke) colors.push(elem.stroke);
        if (elem.borderColor) colors.push(elem.borderColor);
        if (elem.shadowColor) colors.push(elem.shadowColor);
      } else if (elem.type === 'image') {
        if (elem.strokeColor) colors.push(elem.strokeColor);
        if (elem.borderColor) colors.push(elem.borderColor);
      }
    };

    sides.front?.elements?.forEach(checkElement);
    sides.back?.elements?.forEach(checkElement);

    const unique = [];
    colors.forEach(c => {
      if (c && typeof c === 'string' && c.trim() && !unique.includes(c)) {
        unique.push(c);
      }
    });

    return unique;
  };

  const updateGlobalColor = (oldColor, newColor) => {
    if (!oldColor || !newColor || oldColor === newColor) return;

    saveHistoryState(); // Save to undo stack

    // 1. Update backgrounds
    const updatedFrontBackground = { ...sides.front.background };
    if (updatedFrontBackground.value === oldColor) {
      updatedFrontBackground.value = newColor;
    }

    const updatedBackBackground = { ...sides.back.background };
    if (updatedBackBackground.value === oldColor) {
      updatedBackBackground.value = newColor;
    }

    // 2. Update elements
    const updateElementsColors = (elements) => {
      return (elements || []).map(elem => {
        const copy = { ...elem };
        if (copy.type === 'text') {
          if (copy.color === oldColor) copy.color = newColor;
          if (copy.textStrokeColor === oldColor) copy.textStrokeColor = newColor;
          if (copy.shadowColor === oldColor) copy.shadowColor = newColor;
        } else if (copy.type === 'shape' || copy.type === 'datagrid' || copy.type === 'certify' || copy.type === 'accent') {
          if (copy.fill === oldColor) copy.fill = newColor;
          if (copy.strokeColor === oldColor) copy.strokeColor = newColor;
          if (copy.stroke === oldColor) copy.stroke = newColor;
          if (copy.borderColor === oldColor) copy.borderColor = newColor;
          if (copy.shadowColor === oldColor) copy.shadowColor = newColor;
        } else if (copy.type === 'image') {
          if (copy.strokeColor === oldColor) copy.strokeColor = newColor;
          if (copy.borderColor === oldColor) copy.borderColor = newColor;
        }
        return copy;
      });
    };

    setSides({
      front: {
        ...sides.front,
        background: updatedFrontBackground,
        elements: updateElementsColors(sides.front.elements || [])
      },
      back: {
        ...sides.back,
        background: updatedBackBackground,
        elements: updateElementsColors(sides.back.elements || [])
      }
    });

    // 3. Update globalStyles border
    if (globalStyles.borderColor === oldColor) {
      setGlobalStyles({
        ...globalStyles,
        borderColor: newColor
      });
    }

    // Update selection states so editor keeps tracking
    if (editingGlobalColor === oldColor) {
      setEditingGlobalColor(newColor);
    }
  };

  const handleSwatchDoubleClick = (col) => {
    if (col.includes('gradient')) {
      const parsed = parseCSSGradient(col);
      setGradientState(parsed);
      setEditingGlobalColor(col);
      setEditingGradientTarget('globalSwatch');
      setSelectedStopIndex(0);
      setShowGradientModal(true);
    } else {
      setEditingGlobalColor(col);
      setTimeout(() => {
        const picker = document.getElementById('global-swatch-color-picker');
        if (picker) {
          picker.click();
        }
      }, 50);
    }
  };

  const getMappedOrLockedValue = (label) => {
    if (!label) return undefined;
    if (lockedOptions && lockedOptions[label] !== undefined && lockedOptions[label] !== null && lockedOptions[label] !== '') {
      return lockedOptions[label];
    }
    if (fieldMappings && fieldMappings[label]) {
      const resolved = resolveMappedValue(fieldMappings[label], user, orgData, tenantConfig);
      if (resolved !== null && resolved !== undefined && resolved !== '') {
        return resolved;
      }
    }
    return undefined;
  };

  // Variable translation helper (resolves variables across all linked datasets)
  const resolveTemplateVariables = (str) => {
    if (!str) return '';
    let resolved = str;
    
    // 1. Resolve from lockedOptions
    if (lockedOptions) {
      Object.keys(lockedOptions).forEach(key => {
        const val = lockedOptions[key] !== undefined ? lockedOptions[key] : '';
        resolved = resolved.replace(new RegExp(`\\$${key}\\b`, 'gi'), val);
      });
    }

    // 2. Resolve from fieldMappings
    if (fieldMappings) {
      Object.keys(fieldMappings).forEach(key => {
        if (lockedOptions && lockedOptions[key] !== undefined && lockedOptions[key] !== '') return;
        const val = resolveMappedValue(fieldMappings[key], user, orgData, tenantConfig);
        if (val !== null && val !== undefined && val !== '') {
          resolved = resolved.replace(new RegExp(`\\$${key}\\b`, 'gi'), val);
        }
      });
    }

    // 3. Resolve variables from all linked datasets
    if (linkedDatasets && Array.isArray(linkedDatasets)) {
      linkedDatasets.forEach(dsKey => {
        const ds = datasets[dsKey];
        if (ds && ds.variables) {
          Object.keys(ds.variables).forEach(key => {
            const isMapped = fieldMappings && fieldMappings[key];
            const mappedVal = isMapped ? resolveMappedValue(fieldMappings[key], user, orgData, tenantConfig) : null;
            if (
              (lockedOptions && lockedOptions[key] !== undefined && lockedOptions[key] !== '') ||
              (isMapped && mappedVal !== null && mappedVal !== undefined && mappedVal !== '')
            ) return;
            let val = ds.variables[key] !== undefined && ds.variables[key] !== '' ? ds.variables[key] : '';
            
            // Fallback to schema field sample_data if variable is empty
            const fieldObj = ds.fields?.find(f => f.label === key);
            if (!val && fieldObj?.sample_data) {
              val = fieldObj.sample_data;
            }

            // Apply formatting if date format or renderer is specified on the schema field
            const dateFmt = (dateFormats && (dateFormats[key] || (fieldObj?.alias && dateFormats[fieldObj.alias]))) || fieldObj?.date_format;
            if (dateFmt && val) {
              val = formatDateTimeValue(val, dateFmt);
            }
            if (fieldObj && fieldObj.render) {
              val = applyRenderer(val, fieldObj.render, fieldObj);
            }
            
            resolved = resolved.replace(new RegExp(`\\$${key}\\b`, 'gi'), val);
          });
        }
        if (ds && ds.fields) {
          ds.fields.forEach(fieldObj => {
            const key = fieldObj.label;
            const isMapped = fieldMappings && fieldMappings[key];
            const mappedVal = isMapped ? resolveMappedValue(fieldMappings[key], user, orgData, tenantConfig) : null;
            if (
              (lockedOptions && lockedOptions[key] !== undefined && lockedOptions[key] !== '') ||
              (isMapped && mappedVal !== null && mappedVal !== undefined && mappedVal !== '') ||
              (ds.variables && ds.variables[key] !== undefined && ds.variables[key] !== '')
            ) return;
            let val = fieldObj.sample_data || '';
            const dateFmt = (dateFormats && (dateFormats[key] || (fieldObj?.alias && dateFormats[fieldObj.alias]))) || fieldObj?.date_format;
            if (dateFmt && val) {
              val = formatDateTimeValue(val, dateFmt);
            }
            if (fieldObj && fieldObj.render) {
              val = applyRenderer(val, fieldObj.render, fieldObj);
            }
            resolved = resolved.replace(new RegExp(`\\$${key}\\b`, 'gi'), val);
          });
        }
      });
    }
    
    // 4. Fallback to active dataset variables if any remain
    const activeDS = datasets[activeDatasetKey] || (Object.keys(datasets).length > 0 ? datasets[Object.keys(datasets)[0]] : null);
    if (activeDS && activeDS.variables) {
      Object.keys(activeDS.variables).forEach(key => {
        const isMapped = fieldMappings && fieldMappings[key];
        const mappedVal = isMapped ? resolveMappedValue(fieldMappings[key], user, orgData, tenantConfig) : null;
        if (
          (lockedOptions && lockedOptions[key] !== undefined && lockedOptions[key] !== '') ||
          (isMapped && mappedVal !== null && mappedVal !== undefined && mappedVal !== '')
        ) return;
        let val = activeDS.variables[key] !== undefined && activeDS.variables[key] !== '' ? activeDS.variables[key] : '';
        
        // Fallback to schema field sample_data if variable is empty
        const fieldObj = activeDS.fields?.find(f => f.label === key);
        if (!val && fieldObj?.sample_data) {
          val = fieldObj.sample_data;
        }

        // Apply formatting if date format or renderer is specified on the schema field
        const dateFmt = (dateFormats && (dateFormats[key] || (fieldObj?.alias && dateFormats[fieldObj.alias]))) || fieldObj?.date_format;
        if (dateFmt && val) {
          val = formatDateTimeValue(val, dateFmt);
        }
        if (fieldObj && fieldObj.render) {
          val = applyRenderer(val, fieldObj.render, fieldObj);
        }
        
        resolved = resolved.replace(new RegExp(`\\$${key}\\b`, 'gi'), val);
      });
    }

    return resolved;
  };

  const getResolvedImageSrc = (elem, options = {}) => {
    if (!elem) return '';
    let src = '';
    if (elem.imageType === 'frame') {
      const varName = elem.imageVariable || '$image_source';
      const rawLabel = varName.replace(/^\$/, '');
      const mappedVal = getMappedOrLockedValue(rawLabel);
      if (mappedVal) {
        src = mappedVal;
      } else {
        const resolved = resolveTemplateVariables(varName);
        if (resolved && resolved !== varName) {
          src = resolved;
        }
      }
    }
    if (!src) {
      if ((options.preferOriginal || elem.optimizeImage === false) && elem.originalSrc) {
        src = elem.originalSrc;
      } else if (elem.optimizeImage !== false && elem.optimizedSrc) {
        src = elem.optimizedSrc;
      } else {
        src = elem.src;
      }
    }
    if (!src) return '';

    const wantsOptimize = (elem.optimizeImage !== false && !options.preferOriginal);

    // Normalize uploads path to current API_BASE and avoid mixed-content or foreign dev hosts
    if (src.includes('/uploads/')) {
      const pathPart = src.substring(src.indexOf('/uploads/'));
      const cleanPath = pathPart.split('?')[0];
      const query = pathPart.includes('?') ? pathPart.substring(pathPart.indexOf('?')) : '';
      const base = API_BASE ? API_BASE.replace(/\/+$/, '') : '';
      let separator = query ? '&' : '?';
      let finalUrl = `${base}${cleanPath}${query}${separator}cors=true`;
      if (wantsOptimize && !cleanPath.toLowerCase().endsWith('.svg') && !cleanPath.includes('opt_') && !finalUrl.includes('optimize=')) {
        finalUrl += '&optimize=true';
      }
      return finalUrl;
    }

    if (!src.startsWith('data:')) {
      const separator = src.includes('?') ? '&' : '?';
      return `${src}${separator}cors=true`;
    }
    return src || '';
  };

  const getDynamicFieldsInTemplate = () => {
    const list = [];
    const seen = new Set();

    const add = (datasetKey, label, type, sampleVal) => {
      if (seen.has(label)) return;
      seen.add(label);

      let fieldDef = null;
      let foundDatasetKey = datasetKey;
      
      if (datasetKey && datasets[datasetKey]) {
        fieldDef = datasets[datasetKey].fields?.find(f => f.label === label);
      } else if (datasets) {
        for (const dsId of Object.keys(datasets)) {
          const f = datasets[dsId].fields?.find(f => f.label === label);
          if (f) {
            fieldDef = f;
            foundDatasetKey = dsId;
            break;
          }
        }
      }

      list.push({
        datasetKey: foundDatasetKey || '',
        label,
        type: (fieldDef?.type === 'options' || fieldDef?.option_source === 'species_library' || (fieldDef?.options && fieldDef.options.length > 0)) ? 'options' : (fieldDef?.type || type || 'text'),
        sampleValue: sampleVal || fieldDef?.sample_data || '',
        options: fieldDef?.options || [],
        option_source: fieldDef?.option_source || 'static',
        species_field: fieldDef?.species_field || 'common_name',
        species_category: fieldDef?.species_category || 'All',
        fieldDef
      });
    };

    const traverse = (elements) => {
      (elements || []).forEach(el => {
        if (el.type === 'text') {
          if (el.dynamicFields && el.dynamicFields.length > 0) {
            el.dynamicFields.forEach(df => {
              if (df.type === 'dynamic' && df.label) {
                add(df.datasetKey, df.label, 'text', df.sample_data);
              }
            });
          }
          if (el.text) {
            const matches = el.text.match(/\$[a-zA-Z0-9_]+\b/g);
            if (matches) {
              matches.forEach(m => {
                add('', m.substring(1), 'text', '');
              });
            }
          }
        }
        if (el.type === 'image' && el.imageType === 'frame') {
          const varName = el.imageVariable || '$image_source';
          if (varName.startsWith('$')) {
            add('', varName.substring(1), 'image', el.src);
          }
        }
        if (el.type === 'datagrid' && el.rows) {
          el.rows.forEach(row => {
            if (row && row[1]) {
              const matches = row[1].match(/\$[a-zA-Z0-9_]+\b/g);
              if (matches) {
                matches.forEach(m => {
                  add('', m.substring(1), 'text', '');
                });
              }
            }
          });
        }
      });
    };

    traverse(sides.front?.elements);
    traverse(sides.back?.elements);

    return list;
  };

  const updateDatasetVariable = (datasetKey, label, newValue) => {
    let dsKey = datasetKey;
    if (!dsKey) {
      for (const key of Object.keys(datasets)) {
        if (datasets[key]?.variables && datasets[key].variables[label] !== undefined) {
          dsKey = key;
          break;
        }
      }
    }
    if (!dsKey) {
      dsKey = activeDatasetKey || Object.keys(datasets)[0] || 'default';
    }

    setDatasets(prev => {
      const copy = { ...prev };
      if (!copy[dsKey]) {
        copy[dsKey] = { name: dsKey, fields: [], variables: {} };
      }
      copy[dsKey] = {
        ...copy[dsKey],
        variables: {
          ...copy[dsKey].variables,
          [label]: newValue
        }
      };
      return copy;
    });
  };

  const applyMappedValues = (mappings = fieldMappings, locked = lockedOptions) => {
    const dynamicFields = getDynamicFieldsInTemplate();
    dynamicFields.forEach(field => {
      if (locked && locked[field.label] !== undefined && locked[field.label] !== null && locked[field.label] !== '') {
        updateDatasetVariable(field.datasetKey, field.label, locked[field.label]);
      } else if (mappings && mappings[field.label]) {
        const mappingKey = mappings[field.label];
        const resolved = resolveMappedValue(mappingKey, user, orgData, tenantConfig);
        if (resolved !== null && resolved !== undefined && resolved !== '') {
          updateDatasetVariable(field.datasetKey, field.label, resolved);
        }
      }
    });
  };

  useEffect(() => {
    if (orgData || user || tenantConfig || Object.keys(lockedOptions).length > 0) {
      applyMappedValues(fieldMappings, lockedOptions);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgData, user, tenantConfig, fieldMappings, lockedOptions]);

  const getActiveFields = () => {
    const activeList = [];
    if (!linkedDatasets || !Array.isArray(linkedDatasets)) return activeList;
    linkedDatasets.forEach(dsKey => {
      if (!dsKey) return;
      const ds = datasets[dsKey];
      if (!ds) return;
      const enabledForDS = enabledFields[dsKey] || {};
      
      const fieldsList = ds.fields && ds.fields.length > 0
        ? ds.fields.filter(f => f && f.label).map(f => ({
            label: f.label,
            alias: f.alias || f.label,
            sample_data: f.sample_data || ds.variables?.[f.label] || '',
            render: f.render || 'none'
          }))
        : Object.keys(ds.variables || {}).map(k => ({
            label: k,
            alias: k.charAt(0).toUpperCase() + k.slice(1).replace(/_/g, ' '),
            sample_data: ds.variables[k] || '',
            render: 'none'
          }));
      
      const hasDsConfig = Object.keys(enabledForDS).length > 0;
      fieldsList.forEach(f => {
        const isEnabled = hasDsConfig ? (enabledForDS[f.label] === true) : true;
        if (f && f.label && isEnabled) {
          activeList.push({
            datasetKey: dsKey,
            label: f.label,
            alias: f.alias || f.label,
            sample_data: f.sample_data || '',
            render: f.render || 'none'
          });
        }
      });
    });
    return activeList;
  };

  const applyTextTransform = (text, modifier) => {
    if (!text) return '';
    if (modifier === 'uppercase') {
      return text.toUpperCase();
    } else if (modifier === 'lowercase') {
      return text.toLowerCase();
    } else if (modifier === 'capitalize') {
      return text.replace(/\S+/g, (word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase());
    }
    return text;
  };

  const resolveElementText = (elem) => {
    if (!elem) return '';
    let text = '';
    if (elem.type === 'text' && elem.dynamicFields && elem.dynamicFields.length > 0) {
      text = elem.dynamicFields.map((item, i) => {
        if (item.type === 'static') {
          return item.value;
        } else {
          const ds = datasets[item.datasetKey];
          const mappedVal = getMappedOrLockedValue(item.label);
          let val = '';
          if (mappedVal !== undefined && mappedVal !== null && mappedVal !== '') {
            val = mappedVal;
          } else {
            if (ds && ds.variables && ds.variables[item.label] !== undefined && ds.variables[item.label] !== '') {
              val = ds.variables[item.label];
            } else {
              val = item.sample_data || '';
            }
          }
          
          let rendererId = item.render || 'none';
          let matchedFieldObj = null;
          if (ds && ds.fields) {
            const fieldObj = ds.fields.find(f => f.label === item.label);
            console.log(`[resolveElementText] matching fieldObj for "${item.label}":`, fieldObj);
            if (fieldObj) {
              matchedFieldObj = fieldObj;
              if (fieldObj.render) {
                rendererId = fieldObj.render;
              }
            }
          }
          if (!matchedFieldObj && item.custom_renderer) {
            matchedFieldObj = { custom_renderer: item.custom_renderer };
          }
          if (!val) {
            val = item.sample_data || matchedFieldObj?.sample_data || '';
          }
          
          const dateFmt = (dateFormats && (dateFormats[item.label] || (matchedFieldObj?.alias && dateFormats[matchedFieldObj.alias]))) || matchedFieldObj?.date_format || null;
          if (dateFmt && val) {
            val = formatDateTimeValue(val, dateFmt);
          }

          if (rendererId && rendererId !== 'none') {
            console.log(`[resolveElementText] Applying renderer "${rendererId}" on value "${val}"`);
            val = applyRenderer(val, rendererId, matchedFieldObj);
            console.log(`[resolveElementText] Formatted result: "${val}"`);
          }
          return val;
        }
      }).join('');
    } else {
      text = resolveTemplateVariables(elem.text);
    }

    return applyTextTransform(text, elem.textTransformModifier);
  };

  const renderGridDataModal = () => {
    if (!showGridDataModal || !activeElement || activeElement.type !== 'datagrid') return null;

    const dsObj = datasets[selectedDatasetKey];
    const fields = dsObj ? (
      dsObj.fields && dsObj.fields.length > 0
        ? dsObj.fields.filter(f => f && f.label).map(f => ({
            label: f.label,
            alias: f.alias || f.label
          }))
        : Object.keys(dsObj.variables || {}).map(k => ({
            label: k,
            alias: k.charAt(0).toUpperCase() + k.slice(1).replace(/_/g, ' ')
          }))
    ) : [];

    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.75)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 99999,
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
      }}>
        <div style={{
          background: 'var(--card-bg, #1b1b1d)',
          border: '1px solid var(--border-color, #3c3c42)',
          borderRadius: '8px',
          width: '520px',
          maxHeight: '85vh',
          padding: '16px',
          boxShadow: '0 8px 24px rgba(0,0,0,0.5)',
          display: 'flex',
          flexDirection: 'column',
          gap: '12px'
        }}>
          {/* Header */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid var(--border-color, #2d2d30)', paddingBottom: '8px' }}>
            <span style={{ fontSize: '11px', fontWeight: 'bold', color: 'var(--text-main, #dfdfe3)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              Edit Grid Data Rows
            </span>
            <button 
              onClick={() => setShowGridDataModal(false)}
              style={{ backgroundColor: 'transparent', border: 'none', color: 'var(--text-mute, #8e8e93)', cursor: 'pointer', fontSize: '14px' }}
            >
              ✕
            </button>
          </div>

          {/* Linked Datasets Import Selector Section */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', background: 'var(--panel-bg, #141416)', padding: '10px', borderRadius: '4px', border: '1px solid var(--border-color, #2d2d30)' }}>
            <span style={{ fontSize: '10px', color: 'var(--text-mute, #8e8e93)', fontWeight: 'bold' }}>Import Fields from Linked Dataset</span>
            <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
              <select
                value={selectedDatasetKey}
                onChange={(e) => {
                  setSelectedDatasetKey(e.target.value);
                  setSelectedFieldLabel('');
                }}
                style={{ flex: 1, backgroundColor: '#1b1b1d', border: '1px solid #3c3c42', borderRadius: '4px', padding: '6px', color: '#fff', fontSize: '11px', outline: 'none' }}
              >
                <option value="">-- Select Linked Dataset --</option>
                {linkedDatasets.map(key => {
                  const lds = datasets[key];
                  return lds ? <option key={key} value={key}>{lds.name}</option> : null;
                })}
              </select>

              {selectedDatasetKey && (
                <button
                  onClick={() => {
                    const ds = datasets[selectedDatasetKey];
                    if (!ds) return;
                    const dsFields = ds.fields && ds.fields.length > 0
                      ? ds.fields.filter(f => f && f.label).map(f => [f.alias || f.label, `$${f.label}`])
                      : Object.keys(ds.variables || {}).map(k => [
                          k.charAt(0).toUpperCase() + k.slice(1).replace(/_/g, ' '),
                          `$${k}`
                        ]);
                    
                    // Append all fields to rows
                    const nextRows = [...(activeElement.rows || []), ...dsFields];
                    updateActiveElement({ rows: nextRows });
                  }}
                  style={{
                    backgroundColor: '#1677ff',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '4px',
                    padding: '6px 12px',
                    fontSize: '11px',
                    fontWeight: 'bold',
                    cursor: 'pointer'
                  }}
                >
                  ADD ALL
                </button>
              )}
            </div>

            {selectedDatasetKey && fields.length > 0 && (
              <div style={{ display: 'flex', gap: '6px', alignItems: 'center', marginTop: '6px' }}>
                <select
                  value={selectedFieldLabel}
                  onChange={(e) => setSelectedFieldLabel(e.target.value)}
                  style={{ flex: 1, backgroundColor: '#1b1b1d', border: '1px solid #3c3c42', borderRadius: '4px', padding: '6px', color: '#fff', fontSize: '11px', outline: 'none' }}
                >
                  <option value="">-- Select Field to Add --</option>
                  {fields.map(f => (
                    <option key={f.label} value={f.label}>{f.alias} (${f.label})</option>
                  ))}
                </select>
                
                {selectedFieldLabel && (
                  <button
                    onClick={() => {
                      const matchedField = fields.find(f => f.label === selectedFieldLabel);
                      if (matchedField) {
                        const nextRows = [...(activeElement.rows || []), [matchedField.alias, `$${matchedField.label}`]];
                        updateActiveElement({ rows: nextRows });
                      }
                    }}
                    style={{
                      backgroundColor: '#1677ff',
                      color: '#fff',
                      border: 'none',
                      borderRadius: '4px',
                      padding: '6px 12px',
                      fontSize: '11px',
                      fontWeight: 'bold',
                      cursor: 'pointer'
                    }}
                  >
                    Add Field
                  </button>
                )}
              </div>
            )}
          </div>

          {/* Add New Static Row Button (Moved to above the list) */}
          <button
            onClick={() => {
              const nextRows = [...(activeElement.rows || []), ['New Label', 'New Value']];
              updateActiveElement({ rows: nextRows });
            }}
            style={{
              backgroundColor: '#1b1b1d',
              border: '1px solid #3c3c42',
              borderRadius: '4px',
              color: '#1677ff',
              fontWeight: 'bold',
              fontSize: '11px',
              padding: '6px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '4px',
              marginTop: '4px'
            }}
          >
            ➕ Add New Static Row
          </button>

          {/* Rows List Editor Container */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', overflowY: 'auto', flex: 1, paddingRight: '4px', maxHeight: '400px' }}>
            <span style={{ fontSize: '10px', color: '#8e8e93', fontWeight: 'bold', textTransform: 'uppercase' }}>Rows Configuration</span>
            
            {activeElement.rows && activeElement.rows.map((row, rIdx) => (
              <div key={rIdx} style={{ display: 'flex', alignItems: 'center', gap: '8px', backgroundColor: '#232326', padding: '6px 8px', borderRadius: '4px', border: '1px solid #2d2d30' }}>
                
                {/* Row Label */}
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '2px' }}>
                  <span style={{ fontSize: '8px', color: '#8e8e93' }}>Left Label</span>
                  <input
                    type="text"
                    value={row[0]}
                    onChange={(e) => {
                      const nextRows = [...activeElement.rows];
                      nextRows[rIdx] = [e.target.value, row[1]];
                      updateActiveElement({ rows: nextRows });
                    }}
                    style={{ width: '100%', backgroundColor: '#1b1b1d', border: '1px solid #3c3c42', borderRadius: '4px', padding: '4px 6px', color: '#fff', fontSize: '11px', outline: 'none', boxSizing: 'border-box' }}
                  />
                </div>

                {/* Row Value / Variable */}
                <div style={{ flex: 1.5, display: 'flex', flexDirection: 'column', gap: '2px' }}>
                  <span style={{ fontSize: '8px', color: '#8e8e93' }}>Right Value / Variable ($)</span>
                  <input
                    type="text"
                    value={row[1]}
                    onChange={(e) => {
                      const nextRows = [...activeElement.rows];
                      nextRows[rIdx] = [row[0], e.target.value];
                      updateActiveElement({ rows: nextRows });
                    }}
                    style={{ width: '100%', backgroundColor: '#1b1b1d', border: '1px solid #3c3c42', borderRadius: '4px', padding: '4px 6px', color: '#fff', fontSize: '11px', outline: 'none', boxSizing: 'border-box' }}
                  />
                </div>

                {/* Action buttons (Move Up, Move Down, Delete) */}
                <div style={{ display: 'flex', gap: '3px', marginTop: '12px' }}>
                  <button
                    onClick={() => {
                      if (rIdx === 0) return;
                      const nextRows = [...activeElement.rows];
                      const temp = nextRows[rIdx];
                      nextRows[rIdx] = nextRows[rIdx - 1];
                      nextRows[rIdx - 1] = temp;
                      updateActiveElement({ rows: nextRows });
                    }}
                    disabled={rIdx === 0}
                    style={{
                      backgroundColor: '#1b1b1d',
                      border: '1px solid #3c3c42',
                      borderRadius: '3px',
                      color: rIdx === 0 ? '#55555c' : '#dfdfe3',
                      width: '20px',
                      height: '20px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: rIdx === 0 ? 'default' : 'pointer'
                    }}
                    title="Move Row Up"
                  >
                    ▲
                  </button>

                  <button
                    onClick={() => {
                      if (rIdx === activeElement.rows.length - 1) return;
                      const nextRows = [...activeElement.rows];
                      const temp = nextRows[rIdx];
                      nextRows[rIdx] = nextRows[rIdx + 1];
                      nextRows[rIdx + 1] = temp;
                      updateActiveElement({ rows: nextRows });
                    }}
                    disabled={rIdx === activeElement.rows.length - 1}
                    style={{
                      backgroundColor: '#1b1b1d',
                      border: '1px solid #3c3c42',
                      borderRadius: '3px',
                      color: rIdx === activeElement.rows.length - 1 ? '#55555c' : '#dfdfe3',
                      width: '20px',
                      height: '20px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: rIdx === activeElement.rows.length - 1 ? 'default' : 'pointer'
                    }}
                    title="Move Row Down"
                  >
                    ▼
                  </button>

                  <button
                    onClick={() => {
                      const nextRows = activeElement.rows.filter((_, i) => i !== rIdx);
                      updateActiveElement({ rows: nextRows });
                    }}
                    style={{
                      backgroundColor: '#1b1b1d',
                      border: '1px solid #3c3c42',
                      borderRadius: '3px',
                      color: '#ff7b72',
                      width: '20px',
                      height: '20px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer'
                    }}
                    title="Delete Row"
                  >
                    🗑️
                  </button>
                </div>
              </div>
            ))}

            {(!activeElement.rows || activeElement.rows.length === 0) && (
              <div style={{ textAlign: 'center', padding: '20px 0', color: '#5e5e64', fontSize: '11px', border: '1px dashed #3c3c42', borderRadius: '4px' }}>
                No rows configured. Click the button above to add one.
              </div>
            )}
          </div>

          {/* Footer */}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', borderTop: '1px solid var(--border-color, #2d2d30)', paddingTop: '10px', marginTop: '12px' }}>
            <button
              onClick={() => setShowGridDataModal(false)}
              style={{
                padding: '6px 16px',
                fontSize: '11px',
                background: 'var(--primary-gradient, var(--primary-color, #1677ff))',
                color: '#ffffff',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontWeight: 'bold'
              }}
            >
              OK / Done
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderAssetSelectorModal = () => {
    if (!showAssetSelectorModal) return null;

    const filtered = uploadedAssets.filter(asset => {
      if (!asset) return false;
      const filename = asset.original_name || asset.original_filename || asset.filename || '';
      return filename.toLowerCase().includes(assetSearchQuery.toLowerCase());
    });

    return (
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Asset Library & Upload"
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.75)',
          backdropFilter: 'blur(3px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 99999,
          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
        }}
        onClick={(e) => {
          if (e.target === e.currentTarget) {
            setShowAssetSelectorModal(false);
            setPendingImageClickCoords(null);
            setIsChangingImageSource(false);
            setAssetUploadError('');
            setAssetUploadSuccess('');
          }
        }}
      >
        <div
          onDragOver={(e) => {
            e.preventDefault();
            setIsAssetDragOver(true);
          }}
          onDragLeave={(e) => {
            if (e.currentTarget === e.target) {
              setIsAssetDragOver(false);
            }
          }}
          onDrop={(e) => {
            e.preventDefault();
            setIsAssetDragOver(false);
            if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
              handleUploadAssetFiles(e.dataTransfer.files);
            }
          }}
          style={{
            background: 'var(--card-bg, #1b1b1d)',
            border: isAssetDragOver ? '2px dashed var(--primary-color, #3b82f6)' : '1px solid var(--border-color, #3c3c42)',
            borderRadius: '8px',
            width: '740px',
            maxWidth: '92vw',
            maxHeight: '85vh',
            display: 'flex',
            flexDirection: 'column',
            boxShadow: '0 12px 36px rgba(0,0,0,0.65)',
            overflow: 'hidden',
            transition: 'border 0.15s ease'
          }}
        >
          {/* Header */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid var(--border-color, #2d2d30)', padding: '12px 18px', background: 'var(--panel-bg, #212124)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ fontSize: '15px' }}>🖼️</span>
              <span style={{ fontSize: '12px', fontWeight: 'bold', color: 'var(--text-main, #ffffff)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                Asset Library & Upload
              </span>
              <span style={{ fontSize: '11px', color: 'var(--text-mute, #8e8e93)', marginLeft: '6px' }}>
                ({filtered.length} {filtered.length === 1 ? 'asset' : 'assets'})
              </span>
            </div>
            <button
              onClick={() => {
                setShowAssetSelectorModal(false);
                setPendingImageClickCoords(null);
                setIsChangingImageSource(false);
                setAssetUploadError('');
                setAssetUploadSuccess('');
              }}
              style={{ backgroundColor: 'transparent', border: 'none', color: '#8e8e93', cursor: 'pointer', fontSize: '16px', padding: '4px' }}
              title="Close Modal"
            >
              ✕
            </button>
          </div>

          {/* Action & Upload Bar */}
          <div style={{ padding: '10px 18px', borderBottom: '1px solid #2d2d30', display: 'flex', gap: '10px', alignItems: 'center', backgroundColor: '#18181a' }}>
            <div style={{ flex: 1, position: 'relative' }}>
              <input
                type="text"
                placeholder="Search assets by filename..."
                value={assetSearchQuery}
                onChange={(e) => setAssetSearchQuery(e.target.value)}
                style={{
                  width: '100%',
                  backgroundColor: '#141416',
                  border: '1px solid #3c3c42',
                  borderRadius: '5px',
                  padding: '7px 28px 7px 10px',
                  color: '#fff',
                  fontSize: '12px',
                  outline: 'none',
                  boxSizing: 'border-box'
                }}
              />
              {assetSearchQuery && (
                <button
                  onClick={() => setAssetSearchQuery('')}
                  style={{
                    position: 'absolute',
                    right: '8px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    backgroundColor: 'transparent',
                    border: 'none',
                    color: '#8e8e93',
                    cursor: 'pointer',
                    fontSize: '11px',
                    fontWeight: 'bold'
                  }}
                >
                  ✕
                </button>
              )}
            </div>

            {/* Hidden File Input & Upload Trigger */}
            <input
              ref={assetFileInputRef}
              type="file"
              accept="image/*,.svg"
              multiple
              style={{ display: 'none' }}
              onChange={(e) => {
                if (e.target.files && e.target.files.length > 0) {
                  handleUploadAssetFiles(e.target.files);
                  e.target.value = '';
                }
              }}
            />
            <button
              type="button"
              disabled={isUploadingAsset}
              onClick={() => assetFileInputRef.current && assetFileInputRef.current.click()}
              data-testid="upload-new-asset-btn"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                backgroundColor: isUploadingAsset ? '#3c3c42' : '#1677ff',
                color: '#ffffff',
                border: 'none',
                borderRadius: '5px',
                padding: '7px 14px',
                fontSize: '12px',
                fontWeight: '600',
                cursor: isUploadingAsset ? 'not-allowed' : 'pointer',
                opacity: isUploadingAsset ? 0.7 : 1,
                whiteSpace: 'nowrap',
                transition: 'all 0.15s'
              }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <polyline points="17 8 12 3 7 8" />
                <line x1="12" y1="3" x2="12" y2="15" />
              </svg>
              <span>{isUploadingAsset ? 'Uploading...' : 'Upload Asset'}</span>
            </button>
          </div>

          {/* Feedback & Error Alerts */}
          {isUploadingAsset && (
            <div style={{ padding: '8px 18px', backgroundColor: 'rgba(59, 130, 246, 0.15)', borderBottom: '1px solid rgba(59, 130, 246, 0.3)', color: '#93c5fd', fontSize: '11px', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <svg className="animate-spin" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" style={{ animation: 'spin 1s linear infinite' }}>
                <circle cx="12" cy="12" r="10" stroke="rgba(255,255,255,0.2)" />
                <path d="M12 2a10 10 0 0 1 10 10" stroke="#93c5fd" />
              </svg>
              <span>Uploading image asset(s)...</span>
            </div>
          )}
          {assetUploadError && (
            <div style={{ padding: '8px 18px', backgroundColor: 'rgba(239, 68, 68, 0.15)', borderBottom: '1px solid rgba(239, 68, 68, 0.3)', color: '#f87171', fontSize: '11px', fontWeight: '500' }}>
              ⚠️ {assetUploadError}
            </div>
          )}
          {assetUploadSuccess && (
            <div style={{ padding: '8px 18px', backgroundColor: 'rgba(34, 197, 94, 0.15)', borderBottom: '1px solid rgba(34, 197, 94, 0.3)', color: '#4ade80', fontSize: '11px', fontWeight: '500' }}>
              ✓ {assetUploadSuccess}
            </div>
          )}

          {/* Assets Grid */}
          <div style={{ flex: 1, overflowY: 'auto', padding: '16px', display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '12px', maxHeight: '420px', minHeight: '160px', backgroundColor: '#141416' }}>
            {filtered.length === 0 ? (
              <div style={{ gridColumn: 'span 5', textAlign: 'center', color: '#8e8e93', fontSize: '12px', padding: '40px 20px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
                <span style={{ fontSize: '28px' }}>📁</span>
                <span style={{ color: '#dfdfe3', fontWeight: '600' }}>
                  {uploadedAssets.length === 0 ? 'No uploaded assets found' : 'No matching assets found'}
                </span>
                <span style={{ fontSize: '11px', color: '#6b7280' }}>
                  {uploadedAssets.length === 0
                    ? 'Click "+ Upload Asset" above or drag and drop PNG, JPG, or SVG files here to add them to your card.'
                    : 'Try clearing your search query above.'}
                </span>
              </div>
            ) : (
              filtered.map((asset) => {
                const rawPath = asset.file_url || asset.location || '';
                let cleanRelativePath = rawPath;
                if (rawPath.includes('/uploads/')) {
                  cleanRelativePath = rawPath.substring(rawPath.indexOf('/uploads/')).split('?')[0];
                }
                const base = API_BASE ? API_BASE.replace(/\/+$/, '') : '';
                let assetUrl = cleanRelativePath.startsWith('/')
                  ? `${base}${cleanRelativePath}`
                  : (cleanRelativePath.startsWith('http') ? cleanRelativePath : `${base}/${cleanRelativePath}`);
                if (assetUrl && !assetUrl.startsWith('data:')) {
                  const separator = assetUrl.includes('?') ? '&' : '?';
                  assetUrl = `${assetUrl}${separator}cors=true`;
                }
                const filename = asset.original_name || asset.original_filename || asset.filename || 'Asset';
                const mimeType = asset.mime_type || asset.file_type || '';
                const isSvg = mimeType.toLowerCase().includes('svg') || filename.toLowerCase().endsWith('.svg');
                const ext = isSvg ? 'SVG' : (filename.split('.').pop() || 'IMG').toUpperCase();

                return (
                  <div
                    key={asset.id || filename}
                    onClick={() => handleSelectImageAsset(cleanRelativePath || assetUrl)}
                    title={`Click to use "${filename}" on card`}
                    style={{
                      backgroundColor: '#262629',
                      position: 'relative',
                      zIndex: 1,
                      border: '1px solid #3c3c42',
                      borderRadius: '6px',
                      overflow: 'hidden',
                      cursor: 'pointer',
                      transition: 'all 0.15s ease',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      padding: '8px',
                      height: '120px',
                      boxSizing: 'border-box'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.borderColor = '#1677ff';
                      e.currentTarget.style.backgroundColor = '#2c2c30';
                      e.currentTarget.style.transform = 'translateY(-1px)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.borderColor = '#3c3c42';
                      e.currentTarget.style.backgroundColor = '#262629';
                      e.currentTarget.style.transform = 'none';
                    }}
                  >
                    <div style={{ width: '100%', height: '70px', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#18181a', borderRadius: '4px', overflow: 'hidden', position: 'relative' }}>
                      {isSvg ? (
                        <img src={assetUrl} crossOrigin="anonymous" alt={filename} style={{ width: '80%', height: '80%', objectFit: 'contain' }} />
                      ) : (
                        <img src={assetUrl} crossOrigin="anonymous" alt={filename} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      )}
                      <span style={{
                        position: 'absolute',
                        top: '3px',
                        right: '3px',
                        backgroundColor: 'rgba(0,0,0,0.6)',
                        color: '#a2a2a8',
                        fontSize: '9px',
                        padding: '1px 4px',
                        borderRadius: '3px',
                        fontWeight: '600'
                      }}>
                        {ext}
                      </span>
                    </div>
                    <span style={{ fontSize: '10px', color: '#dfdfe3', marginTop: '6px', width: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', textAlign: 'center' }}>
                      {filename}
                    </span>
                  </div>
                );
              })
            )}
          </div>

          {/* Footer */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderTop: '1px solid var(--border-color, #2d2d30)', padding: '10px 18px', background: 'var(--panel-bg, #1b1b1d)' }}>
            <span style={{ fontSize: '11px', color: 'var(--text-mute, #8e8e93)' }}>
              💡 Click an asset to place it on your card canvas. Drag & drop image files to upload anytime.
            </span>
            <button
              onClick={() => {
                setShowAssetSelectorModal(false);
                setPendingImageClickCoords(null);
                setIsChangingImageSource(false);
                setAssetUploadError('');
                setAssetUploadSuccess('');
              }}
              style={{
                padding: '6px 16px',
                fontSize: '11px',
                background: 'var(--card-bg, #3c3c42)',
                color: 'var(--text-main, #dfdfe3)',
                border: '1px solid var(--border-color, #3c3c42)',
                borderRadius: '4px',
                cursor: 'pointer',
                fontWeight: 'bold',
                transition: 'background 0.15s'
              }}
            >
              Close
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderImageAdjustModal = () => {
    if (!showImageAdjustModal || !adjustingElement) return null;

    const elem = adjustingElement;
    const resolvedSrc = getResolvedImageSrc(elem);

    // Frame aspect ratio & sizing for crop aperture
    const frameW = tempFrameW || elem.width || 160;
    const frameH = tempFrameH || elem.height || 120;
    const frameAspect = frameW / frameH;

    // Workspace dimensions inside modal
    const workspaceW = 520;
    const workspaceH = 320;

    // Calculate crop aperture box size inside workspace (leaving space so uncropped photo edges are visible)
    const maxCropW = 360;
    const maxCropH = 220;
    let cropW = maxCropW;
    let cropH = cropW / frameAspect;
    if (cropH > maxCropH) {
      cropH = maxCropH;
      cropW = cropH * frameAspect;
    }
    cropW = Math.max(60, Math.round(cropW));
    cropH = Math.max(50, Math.round(cropH));

    const cropLeft = Math.round((workspaceW - cropW) / 2);
    const cropTop = Math.round((workspaceH - cropH) / 2);
    const cropCenterX = cropLeft + cropW / 2;
    const cropCenterY = cropTop + cropH / 2;

    // Image natural sizing and base dimension calculation
    const naturalW = imgNaturalSize.w || 0;
    const naturalH = imgNaturalSize.h || 0;
    const imgAspect = (naturalW > 0 && naturalH > 0) ? (naturalW / naturalH) : frameAspect;

    // Base dimensions to cover the crop aperture at zoom = 1.0 without distortion
    let baseW = cropW;
    let baseH = cropH;
    if (imgAspect < frameAspect) {
      // Portrait image in wider frame: width covers frame, height extends above/below
      baseW = cropW;
      baseH = cropW / imgAspect;
    } else {
      // Landscape image in taller frame: height covers frame, width extends left/right
      baseH = cropH;
      baseW = cropH * imgAspect;
    }
    baseW = Math.round(baseW);
    baseH = Math.round(baseH);

    const currentRatioVal = tempFrameH > 0 ? (tempFrameW / tempFrameH) : 1;
    const formatRatio = (r) => {
      if (Math.abs(r - 1) < 0.02) return '1:1';
      if (Math.abs(r - 4/3) < 0.02) return '4:3';
      if (Math.abs(r - 3/4) < 0.02) return '3:4';
      if (Math.abs(r - 16/9) < 0.03) return '16:9';
      if (Math.abs(r - 9/16) < 0.03) return '9:16';
      if (Math.abs(r - 3/2) < 0.02) return '3:2';
      if (Math.abs(r - 2/3) < 0.02) return '2:3';
      return `${Math.round(r * 100) / 100}:1`;
    };

    const handleFitCropToImage = () => {
      let imgW = imgNaturalSize.w || 0;
      let imgH = imgNaturalSize.h || 0;
      if (imgW === 0 || imgH === 0) {
        if (elem.naturalWidth && elem.naturalHeight) {
          imgW = elem.naturalWidth;
          imgH = elem.naturalHeight;
        } else if (resolvedSrc && globalImageAspectCache.has(resolvedSrc)) {
          const cached = globalImageAspectCache.get(resolvedSrc);
          if (cached > 0) {
            imgW = 1000;
            imgH = Math.round(1000 / cached);
          }
        }
      }

      if (imgW > 0 && imgH > 0) {
        const targetAspect = imgW / imgH;
        const currentMax = Math.max(tempFrameW, tempFrameH, 120);
        let newW, newH;
        if (targetAspect >= 1) {
          newW = Math.round(currentMax);
          newH = Math.max(30, Math.round(currentMax / targetAspect));
        } else {
          newH = Math.round(currentMax);
          newW = Math.max(30, Math.round(currentMax * targetAspect));
        }
        setTempFrameW(newW);
        setTempFrameH(newH);
        setTempPanX(0);
        setTempPanY(0);
        setTempZoom(1);
        setSelectedCropPreset('image');
      } else {
        setTempPanX(0);
        setTempPanY(0);
        setTempZoom(1);
      }
    };

    const applyAspectRatioPreset = (presetKey, ratio) => {
      if (presetKey === 'image') {
        handleFitCropToImage();
        return;
      }
      const currentMax = Math.max(tempFrameW, tempFrameH, 120);
      let newW, newH;
      if (ratio >= 1) {
        newW = Math.round(currentMax);
        newH = Math.max(30, Math.round(currentMax / ratio));
      } else {
        newH = Math.round(currentMax);
        newW = Math.max(30, Math.round(currentMax * ratio));
      }
      setTempFrameW(newW);
      setTempFrameH(newH);
      setSelectedCropPreset(presetKey);
    };

    const handleSwapCropOrientation = () => {
      const nextW = tempFrameH;
      const nextH = tempFrameW;
      setTempFrameW(nextW);
      setTempFrameH(nextH);
      setSelectedCropPreset('custom');
    };

    const handleWidthChange = (val) => {
      const w = Math.max(30, Math.min(1200, parseInt(val) || 30));
      if (lockCropAspect && tempFrameW > 0) {
        const ratio = tempFrameW / tempFrameH;
        setTempFrameH(Math.max(30, Math.round(w / ratio)));
      }
      setTempFrameW(w);
      setSelectedCropPreset('custom');
    };

    const handleHeightChange = (val) => {
      const h = Math.max(30, Math.min(1200, parseInt(val) || 30));
      if (lockCropAspect && tempFrameH > 0) {
        const ratio = tempFrameW / tempFrameH;
        setTempFrameW(Math.max(30, Math.round(h * ratio)));
      }
      setTempFrameH(h);
      setSelectedCropPreset('custom');
    };

    const handleCropResizePointerDown = (handle, e) => {
      e.preventDefault();
      e.stopPropagation();
      isCropResizingRef.current = true;
      cropResizeStartRef.current = {
        handle,
        startX: e.clientX,
        startY: e.clientY,
        initialW: tempFrameW,
        initialH: tempFrameH,
        initialAspect: tempFrameW / tempFrameH,
        scale: (cropW > 0 && tempFrameW > 0) ? (cropW / tempFrameW) : 1
      };

      const handlePointerMove = (moveEvt) => {
        if (!isCropResizingRef.current || !cropResizeStartRef.current) return;
        const { handle: curHandle, startX, startY, initialW, initialH, initialAspect, scale } = cropResizeStartRef.current;
        const dxScreen = moveEvt.clientX - startX;
        const dyScreen = moveEvt.clientY - startY;
        const effScale = scale > 0 ? scale : 1;
        const dx = dxScreen / effScale;
        const dy = dyScreen / effScale;

        let newW = initialW;
        let newH = initialH;

        if (curHandle.includes('e')) newW = Math.max(30, Math.round(initialW + dx));
        if (curHandle.includes('w')) newW = Math.max(30, Math.round(initialW - dx));
        if (curHandle.includes('s')) newH = Math.max(30, Math.round(initialH + dy));
        if (curHandle.includes('n')) newH = Math.max(30, Math.round(initialH - dy));

        if (lockCropAspect) {
          if (curHandle === 'e' || curHandle === 'w') {
            newH = Math.max(30, Math.round(newW / initialAspect));
          } else if (curHandle === 'n' || curHandle === 's') {
            newW = Math.max(30, Math.round(newH * initialAspect));
          } else {
            const ratioW = newW / initialW;
            const ratioH = newH / initialH;
            const chosen = Math.abs(ratioW - 1) > Math.abs(ratioH - 1) ? ratioW : ratioH;
            newW = Math.max(30, Math.round(initialW * chosen));
            newH = Math.max(30, Math.round(initialH * chosen));
          }
        }

        setTempFrameW(newW);
        setTempFrameH(newH);
        setSelectedCropPreset('custom');
      };

      const handlePointerUp = () => {
        isCropResizingRef.current = false;
        cropResizeStartRef.current = null;
        window.removeEventListener('pointermove', handlePointerMove);
        window.removeEventListener('pointerup', handlePointerUp);
      };

      window.addEventListener('pointermove', handlePointerMove);
      window.addEventListener('pointerup', handlePointerUp);
    };

    const handleApply = () => {
      saveHistoryState();
      const updatedPanX = Math.round(tempPanX * 10) / 10;
      const updatedPanY = Math.round(tempPanY * 10) / 10;
      const updatedZoom = Math.round(tempZoom * 100) / 100;
      const naturalW = imgNaturalSize.w || 0;
      const naturalH = imgNaturalSize.h || 0;
      const calculatedAspect = (naturalW > 0 && naturalH > 0) ? (naturalW / naturalH) : (tempFrameW / tempFrameH);

      if (resolvedSrc && naturalW > 0 && naturalH > 0) {
        globalImageAspectCache.set(resolvedSrc, naturalW / naturalH);
      }

      const oldW = elem.width || 160;
      const oldH = elem.height || 120;
      const finalW = Math.max(20, Math.round(tempFrameW));
      const finalH = Math.max(20, Math.round(tempFrameH));
      const oldCenterX = (elem.x || 0) + oldW / 2;
      const oldCenterY = (elem.y || 0) + oldH / 2;
      const newX = Math.round(oldCenterX - finalW / 2);
      const newY = Math.round(oldCenterY - finalH / 2);

      setSides(prev => ({
        ...prev,
        [activeSide]: {
          ...prev[activeSide],
          elements: prev[activeSide].elements.map(el =>
            el.id === elem.id
              ? { 
                  ...el, 
                  x: newX,
                  y: newY,
                  width: finalW,
                  height: finalH,
                  cropApplied: true, 
                  imgPanX: updatedPanX, 
                  imgPanY: updatedPanY, 
                  imgZoom: updatedZoom,
                  imgAspect: calculatedAspect,
                  naturalWidth: naturalW,
                  naturalHeight: naturalH
                }
              : el
          )
        }
      }));
      setShowImageAdjustModal(false);
      setAdjustingElement(null);
    };

    const handleRemoveCrop = () => {
      saveHistoryState();
      setSides(prev => ({
        ...prev,
        [activeSide]: {
          ...prev[activeSide],
          elements: prev[activeSide].elements.map(el =>
            el.id === elem.id
              ? { ...el, cropApplied: false, imgPanX: 0, imgPanY: 0, imgZoom: 1 }
              : el
          )
        }
      }));
      setShowImageAdjustModal(false);
      setAdjustingElement(null);
    };

    const handleClose = () => {
      setShowImageAdjustModal(false);
      setAdjustingElement(null);
    };

    const handleWheel = (e) => {
      e.preventDefault();
      e.stopPropagation();
      const delta = e.deltaY < 0 ? 0.05 : -0.05;
      setTempZoom(z => Math.max(0.4, Math.min(4, Math.round((z + delta) * 100) / 100)));
    };

    const handleWorkspacePointerDown = (e) => {
      e.preventDefault();
      e.stopPropagation();
      isPanningRef.current = true;
      panStartRef.current = {
        startX: e.clientX,
        startY: e.clientY,
        initialPanX: tempPanX,
        initialPanY: tempPanY
      };

      const handlePointerMove = (moveEvt) => {
        if (!isPanningRef.current) return;
        const dx = moveEvt.clientX - panStartRef.current.startX;
        const dy = moveEvt.clientY - panStartRef.current.startY;
        const deltaPctX = (dx / cropW) * 100;
        const deltaPctY = (dy / cropH) * 100;
        setTempPanX(Math.round((panStartRef.current.initialPanX + deltaPctX) * 10) / 10);
        setTempPanY(Math.round((panStartRef.current.initialPanY + deltaPctY) * 10) / 10);
      };

      const handlePointerUp = () => {
        isPanningRef.current = false;
        window.removeEventListener('pointermove', handlePointerMove);
        window.removeEventListener('pointerup', handlePointerUp);
      };

      window.addEventListener('pointermove', handlePointerMove);
      window.addEventListener('pointerup', handlePointerUp);
    };

    const radiusScale = cropW / frameW;
    const previewRadius = typeof elem.borderRadius === 'number' ? Math.round(elem.borderRadius * radiusScale) : elem.borderRadius || 0;

    return (
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.75)',
          backdropFilter: 'blur(4px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 99999,
          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
        }}
        onClick={handleClose}
      >
        <div
          onClick={(e) => e.stopPropagation()}
          style={{
            width: '580px',
            maxWidth: '95vw',
            background: 'var(--card-bg, #1b1b1d)',
            borderRadius: '10px',
            border: '1px solid var(--border-color, #3c3c42)',
            boxShadow: '0 16px 48px rgba(0, 0, 0, 0.7)',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden'
          }}
        >
          {/* Header */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid var(--border-color, #2d2d30)', padding: '12px 18px', background: 'var(--panel-bg, #18181a)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ fontSize: '12px', fontWeight: 'bold', color: 'var(--text-main, #dfdfe3)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                Pan & Zoom Image Crop
              </span>
              <span style={{ fontSize: '10px', color: '#8e8e93', backgroundColor: '#2b2b2f', padding: '2px 6px', borderRadius: '4px' }}>
                Frame: {Math.round(frameW)} × {Math.round(frameH)} px ({formatRatio(currentRatioVal)})
              </span>
              <button
                type="button"
                data-testid="header-fit-crop-to-image-btn"
                onClick={handleFitCropToImage}
                style={{
                  backgroundColor: '#1e293b',
                  color: '#38bdf8',
                  border: '1px solid #0284c7',
                  borderRadius: '4px',
                  padding: '2px 8px',
                  fontSize: '10px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px'
                }}
                title="Fit crop frame to match photo's natural proportions"
              >
                🎯 Fit to Image
              </button>
            </div>
            <button
              type="button"
              onClick={handleClose}
              style={{ backgroundColor: 'transparent', border: 'none', color: '#8e8e93', cursor: 'pointer', fontSize: '15px' }}
            >
              ✕
            </button>
          </div>

          {/* Direct Manipulation Viewport */}
          <div style={{
            padding: '14px 20px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: '#111113',
            borderBottom: '1px solid #2d2d30',
            userSelect: 'none'
          }}>
            {/* The Pan/Zoom Workspace where full uncropped photo is visible */}
            <div
              onPointerDown={handleWorkspacePointerDown}
              onWheel={handleWheel}
              style={{
                width: `${workspaceW}px`,
                height: `${workspaceH}px`,
                borderRadius: '8px',
                position: 'relative',
                overflow: 'hidden',
                cursor: isPanningRef.current ? 'grabbing' : 'grab',
                backgroundColor: '#0a0a0c',
                backgroundImage: 'radial-gradient(#26262b 1px, transparent 1px)',
                backgroundSize: '16px 16px',
                touchAction: 'none'
              }}
              title="Click and drag to position image • Scroll wheel to zoom"
            >
              {resolvedSrc ? (
                <img
                  src={resolvedSrc}
                  crossOrigin="anonymous"
                  alt="Full Uncropped Subject"
                  draggable={false}
                  onLoad={(e) => {
                    if (e.target.naturalWidth && e.target.naturalHeight) {
                      setImgNaturalSize({ w: e.target.naturalWidth, h: e.target.naturalHeight });
                    }
                  }}
                  style={{
                    position: 'absolute',
                    left: `${cropCenterX + (tempPanX / 100) * cropW}px`,
                    top: `${cropCenterY + (tempPanY / 100) * cropH}px`,
                    width: `${baseW}px`,
                    height: `${baseH}px`,
                    transform: `translate(-50%, -50%) scale(${tempZoom}) ${elem.flipH ? 'scaleX(-1)' : ''} ${elem.flipV ? 'scaleY(-1)' : ''}`.trim() || 'none',
                    transformOrigin: 'center center',
                    userSelect: 'none',
                    pointerEvents: 'none',
                    ...getImageFilterStyle(elem)
                  }}
                />
              ) : (
                <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#71717a', fontSize: '11px' }}>
                  No Image Loaded
                </div>
              )}

              {/* Crop Aperture Frame: Inside is clear, outside is dimmed via box-shadow */}
              <div
                data-testid="crop-aperture-frame"
                style={{
                  position: 'absolute',
                  left: `${cropLeft}px`,
                  top: `${cropTop}px`,
                  width: `${cropW}px`,
                  height: `${cropH}px`,
                  borderRadius: `${previewRadius}px`,
                  border: (elem.hasBorder !== false && elem.borderFillType !== 'gradient')
                    ? `${Math.max(1.5, Math.round((elem.strokeWidth || 1) * radiusScale))}px ${elem.strokeStyle || 'solid'} ${elem.strokeColor || '#ffffff'}`
                    : '2px solid #1677ff',
                  boxShadow: '0 0 0 9999px rgba(0, 0, 0, 0.65)',
                  pointerEvents: 'none',
                  zIndex: 10
                }}
              >
                {/* Rule of Thirds Guides */}
                <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', opacity: 0.35 }}>
                  <div style={{ position: 'absolute', top: '33.33%', left: 0, right: 0, height: '1px', borderTop: '1px dashed #ffffff' }} />
                  <div style={{ position: 'absolute', top: '66.66%', left: 0, right: 0, height: '1px', borderTop: '1px dashed #ffffff' }} />
                  <div style={{ position: 'absolute', left: '33.33%', top: 0, bottom: 0, width: '1px', borderLeft: '1px dashed #ffffff' }} />
                  <div style={{ position: 'absolute', left: '66.66%', top: 0, bottom: 0, width: '1px', borderLeft: '1px dashed #ffffff' }} />
                </div>

                {/* Center Target Crosshair */}
                <div style={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  width: '12px',
                  height: '12px',
                  pointerEvents: 'none',
                  opacity: 0.45
                }}>
                  <div style={{ position: 'absolute', top: '5px', left: 0, right: 0, height: '2px', backgroundColor: '#ffffff' }} />
                  <div style={{ position: 'absolute', left: '5px', top: 0, bottom: 0, width: '2px', backgroundColor: '#ffffff' }} />
                </div>

                {/* Resize Handles on the Crop Aperture */}
                <div
                  onPointerDown={(e) => handleCropResizePointerDown('nw', e)}
                  title="Drag to resize crop"
                  data-testid="crop-handle-nw"
                  style={{
                    position: 'absolute',
                    top: -5,
                    left: -5,
                    width: 10,
                    height: 10,
                    backgroundColor: '#ffffff',
                    border: '2px solid #1677ff',
                    borderRadius: '2px',
                    cursor: 'nwse-resize',
                    pointerEvents: 'auto',
                    zIndex: 30
                  }}
                />
                <div
                  onPointerDown={(e) => handleCropResizePointerDown('ne', e)}
                  title="Drag to resize crop"
                  data-testid="crop-handle-ne"
                  style={{
                    position: 'absolute',
                    top: -5,
                    right: -5,
                    width: 10,
                    height: 10,
                    backgroundColor: '#ffffff',
                    border: '2px solid #1677ff',
                    borderRadius: '2px',
                    cursor: 'nesw-resize',
                    pointerEvents: 'auto',
                    zIndex: 30
                  }}
                />
                <div
                  onPointerDown={(e) => handleCropResizePointerDown('sw', e)}
                  title="Drag to resize crop"
                  data-testid="crop-handle-sw"
                  style={{
                    position: 'absolute',
                    bottom: -5,
                    left: -5,
                    width: 10,
                    height: 10,
                    backgroundColor: '#ffffff',
                    border: '2px solid #1677ff',
                    borderRadius: '2px',
                    cursor: 'nesw-resize',
                    pointerEvents: 'auto',
                    zIndex: 30
                  }}
                />
                <div
                  onPointerDown={(e) => handleCropResizePointerDown('se', e)}
                  title="Drag to resize crop"
                  data-testid="crop-handle-se"
                  style={{
                    position: 'absolute',
                    bottom: -5,
                    right: -5,
                    width: 10,
                    height: 10,
                    backgroundColor: '#ffffff',
                    border: '2px solid #1677ff',
                    borderRadius: '2px',
                    cursor: 'nwse-resize',
                    pointerEvents: 'auto',
                    zIndex: 30
                  }}
                />
                <div
                  onPointerDown={(e) => handleCropResizePointerDown('n', e)}
                  title="Drag to adjust crop height"
                  data-testid="crop-handle-n"
                  style={{
                    position: 'absolute',
                    top: -4,
                    left: 'calc(50% - 14px)',
                    width: 28,
                    height: 8,
                    backgroundColor: '#ffffff',
                    border: '1.5px solid #1677ff',
                    borderRadius: '4px',
                    cursor: 'ns-resize',
                    pointerEvents: 'auto',
                    zIndex: 30
                  }}
                />
                <div
                  onPointerDown={(e) => handleCropResizePointerDown('s', e)}
                  title="Drag to adjust crop height"
                  data-testid="crop-handle-s"
                  style={{
                    position: 'absolute',
                    bottom: -4,
                    left: 'calc(50% - 14px)',
                    width: 28,
                    height: 8,
                    backgroundColor: '#ffffff',
                    border: '1.5px solid #1677ff',
                    borderRadius: '4px',
                    cursor: 'ns-resize',
                    pointerEvents: 'auto',
                    zIndex: 30
                  }}
                />
                <div
                  onPointerDown={(e) => handleCropResizePointerDown('w', e)}
                  title="Drag to adjust crop width"
                  data-testid="crop-handle-w"
                  style={{
                    position: 'absolute',
                    left: -4,
                    top: 'calc(50% - 14px)',
                    width: 8,
                    height: 28,
                    backgroundColor: '#ffffff',
                    border: '1.5px solid #1677ff',
                    borderRadius: '4px',
                    cursor: 'ew-resize',
                    pointerEvents: 'auto',
                    zIndex: 30
                  }}
                />
                <div
                  onPointerDown={(e) => handleCropResizePointerDown('e', e)}
                  title="Drag to adjust crop width"
                  data-testid="crop-handle-e"
                  style={{
                    position: 'absolute',
                    right: -4,
                    top: 'calc(50% - 14px)',
                    width: 8,
                    height: 28,
                    backgroundColor: '#ffffff',
                    border: '1.5px solid #1677ff',
                    borderRadius: '4px',
                    cursor: 'ew-resize',
                    pointerEvents: 'auto',
                    zIndex: 30
                  }}
                />
              </div>
            </div>

            {/* Hint */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: `${workspaceW}px`, marginTop: '8px', color: '#8e8e93', fontSize: '10px' }}>
              <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <span>🖐️ Drag photo to pan</span>
                <span>•</span>
                <span>🔄 Scroll wheel to zoom</span>
                <span>•</span>
                <span>↔️ Drag blue handles to resize crop</span>
              </span>
              <span style={{ color: '#71717a' }}>
                Dimmed area shows uncropped photo
              </span>
            </div>
          </div>

          {/* Controls Bar */}
          <div style={{ padding: '12px 20px', display: 'flex', flexDirection: 'column', gap: '10px', backgroundColor: '#1b1b1d', maxHeight: '340px', overflowY: 'auto' }}>
            {/* Row 1: Crop Frame Adjustments & Aspect Ratio Presets */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', paddingBottom: '8px', borderBottom: '1px solid #2d2d30' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: '11px', fontWeight: 'bold', color: '#dfdfe3', display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <span>Crop Aperture & Aspect Ratio</span>
                  <span style={{ fontSize: '9px', fontWeight: 'normal', color: '#38bdf8', backgroundColor: 'rgba(56, 189, 248, 0.1)', border: '1px solid rgba(56, 189, 248, 0.25)', padding: '1px 5px', borderRadius: '3px' }}>
                    {formatRatio(currentRatioVal)}
                  </span>
                </span>
                {/* Dimensions Steppers & Lock */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
                    <span style={{ fontSize: '10px', color: '#8e8e93' }}>W:</span>
                    <input
                      type="number"
                      data-testid="crop-width-input"
                      value={Math.round(tempFrameW)}
                      onChange={(e) => handleWidthChange(e.target.value)}
                      style={{
                        width: '46px',
                        backgroundColor: '#26262b',
                        border: '1px solid #3c3c42',
                        borderRadius: '3px',
                        color: '#dfdfe3',
                        fontSize: '10px',
                        padding: '2px 4px',
                        textAlign: 'center'
                      }}
                      min="30"
                      max="1200"
                    />
                    <span style={{ fontSize: '9px', color: '#71717a' }}>px</span>
                  </div>
                  <span style={{ color: '#71717a', fontSize: '10px' }}>×</span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
                    <span style={{ fontSize: '10px', color: '#8e8e93' }}>H:</span>
                    <input
                      type="number"
                      data-testid="crop-height-input"
                      value={Math.round(tempFrameH)}
                      onChange={(e) => handleHeightChange(e.target.value)}
                      style={{
                        width: '46px',
                        backgroundColor: '#26262b',
                        border: '1px solid #3c3c42',
                        borderRadius: '3px',
                        color: '#dfdfe3',
                        fontSize: '10px',
                        padding: '2px 4px',
                        textAlign: 'center'
                      }}
                      min="30"
                      max="1200"
                    />
                    <span style={{ fontSize: '9px', color: '#71717a' }}>px</span>
                  </div>
                  <button
                    type="button"
                    data-testid="crop-lock-ratio-btn"
                    onClick={() => setLockCropAspect(!lockCropAspect)}
                    style={{
                      backgroundColor: lockCropAspect ? '#1677ff' : '#2b2b2f',
                      color: lockCropAspect ? '#fff' : '#8e8e93',
                      border: '1px solid #3c3c42',
                      borderRadius: '3px',
                      padding: '2px 6px',
                      fontSize: '9px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '3px'
                    }}
                    title={lockCropAspect ? 'Aspect ratio locked' : 'Aspect ratio unlocked'}
                  >
                    {lockCropAspect ? '🔒 Locked' : '🔓 Unlocked'}
                  </button>
                </div>
              </div>

              {/* Presets Button Row */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '4px', flexWrap: 'wrap' }}>
                <button
                  type="button"
                  data-testid="fit-crop-to-image-btn"
                  onClick={handleFitCropToImage}
                  style={{
                    backgroundColor: selectedCropPreset === 'image' ? '#0284c7' : '#1e293b',
                    color: '#38bdf8',
                    border: '1px solid #0284c7',
                    borderRadius: '4px',
                    padding: '3px 8px',
                    fontSize: '10px',
                    fontWeight: 'bold',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px'
                  }}
                  title="Adjust crop frame to match photo's exact natural aspect ratio"
                >
                  🎯 Fit Crop to Image
                </button>
                {[
                  { label: '1:1', ratio: 1, key: '1:1' },
                  { label: '4:3', ratio: 4/3, key: '4:3' },
                  { label: '3:2', ratio: 3/2, key: '3:2' },
                  { label: '16:9', ratio: 16/9, key: '16:9' },
                  { label: '9:16', ratio: 9/16, key: '9:16' }
                ].map(p => (
                  <button
                    key={p.key}
                    type="button"
                    data-testid={`preset-${p.key}`}
                    onClick={() => applyAspectRatioPreset(p.key, p.ratio)}
                    style={{
                      backgroundColor: selectedCropPreset === p.key ? '#1677ff' : '#2b2b2f',
                      color: selectedCropPreset === p.key ? '#ffffff' : '#dfdfe3',
                      border: '1px solid #3c3c42',
                      borderRadius: '3px',
                      padding: '3px 7px',
                      fontSize: '9px',
                      cursor: 'pointer'
                    }}
                  >
                    {p.label}
                  </button>
                ))}
                <button
                  type="button"
                  data-testid="swap-crop-orientation-btn"
                  onClick={handleSwapCropOrientation}
                  style={{
                    backgroundColor: '#2b2b2f',
                    color: '#dfdfe3',
                    border: '1px solid #3c3c42',
                    borderRadius: '3px',
                    padding: '3px 7px',
                    fontSize: '9px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '3px'
                  }}
                  title="Swap Width and Height (Landscape ↔ Portrait)"
                >
                  🔄 Rotate
                </button>
              </div>
            </div>

            {/* Row 2: Zoom Row */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: '11px', fontWeight: 'bold', color: '#dfdfe3' }}>Zoom Level</span>
                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  {['0.75', '1', '1.25', '1.5', '2'].map(lvl => (
                    <button
                      key={lvl}
                      type="button"
                      onClick={() => setTempZoom(parseFloat(lvl))}
                      style={{
                        backgroundColor: tempZoom === parseFloat(lvl) ? '#1677ff' : '#2b2b2f',
                        color: tempZoom === parseFloat(lvl) ? '#fff' : '#dfdfe3',
                        border: '1px solid #3c3c42',
                        borderRadius: '3px',
                        padding: '1px 6px',
                        fontSize: '9px',
                        cursor: 'pointer'
                      }}
                    >
                      {Math.round(parseFloat(lvl) * 100)}%
                    </button>
                  ))}
                  <span style={{ fontSize: '11px', color: '#1677ff', fontFamily: 'monospace', minWidth: '42px', textAlign: 'right', fontWeight: 'bold' }}>
                    {Math.round(tempZoom * 100)}%
                  </span>
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <button
                  type="button"
                  onClick={() => setTempZoom(z => Math.max(0.4, Math.round((z - 0.1) * 100) / 100))}
                  style={{ backgroundColor: '#2b2b2f', color: '#dfdfe3', border: '1px solid #3c3c42', borderRadius: '4px', width: '22px', height: '22px', cursor: 'pointer', fontSize: '13px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                >
                  -
                </button>
                <input
                  type="range"
                  min="0.4"
                  max="4"
                  step="0.05"
                  value={tempZoom}
                  onChange={(e) => setTempZoom(parseFloat(e.target.value))}
                  style={{ flex: 1, accentColor: '#1677ff', cursor: 'pointer' }}
                />
                <button
                  type="button"
                  onClick={() => setTempZoom(z => Math.min(4, Math.round((z + 0.1) * 100) / 100))}
                  style={{ backgroundColor: '#2b2b2f', color: '#dfdfe3', border: '1px solid #3c3c42', borderRadius: '4px', width: '22px', height: '22px', cursor: 'pointer', fontSize: '13px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                >
                  +
                </button>
              </div>
            </div>

            {/* Row 3: Pan Sliders Row */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '10px', color: '#8e8e93' }}>
                  <span>Horizontal Pan (X)</span>
                  <span style={{ color: '#dfdfe3', fontFamily: 'monospace' }}>{Math.round(tempPanX)}%</span>
                </div>
                <input
                  type="range"
                  min="-100"
                  max="100"
                  step="1"
                  value={tempPanX}
                  onChange={(e) => setTempPanX(parseFloat(e.target.value))}
                  style={{ width: '100%', accentColor: '#1677ff', cursor: 'pointer' }}
                />
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '10px', color: '#8e8e93' }}>
                  <span>Vertical Pan (Y)</span>
                  <span style={{ color: '#dfdfe3', fontFamily: 'monospace' }}>{Math.round(tempPanY)}%</span>
                </div>
                <input
                  type="range"
                  min="-100"
                  max="100"
                  step="1"
                  value={tempPanY}
                  onChange={(e) => setTempPanY(parseFloat(e.target.value))}
                  style={{ width: '100%', accentColor: '#1677ff', cursor: 'pointer' }}
                />
              </div>
            </div>

            {/* Row 4: Quick Presets & Full Photo Option */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingTop: '2px' }}>
              <div style={{ display: 'flex', gap: '6px' }}>
                <button
                  type="button"
                  onClick={() => { setTempPanX(0); setTempPanY(0); }}
                  style={{ backgroundColor: '#2b2b2f', color: '#dfdfe3', border: '1px solid #3c3c42', borderRadius: '4px', padding: '3px 8px', fontSize: '10px', cursor: 'pointer' }}
                >
                  Center Pan
                </button>
                <button
                  type="button"
                  onClick={() => { setTempPanX(0); setTempPanY(0); setTempZoom(1); }}
                  style={{ backgroundColor: '#2b2b2f', color: '#dfdfe3', border: '1px solid #3c3c42', borderRadius: '4px', padding: '3px 8px', fontSize: '10px', cursor: 'pointer' }}
                >
                  Reset Pan & Zoom
                </button>
                <button
                  type="button"
                  data-testid="quick-fit-crop-to-image-btn"
                  onClick={handleFitCropToImage}
                  style={{ backgroundColor: '#1e293b', color: '#38bdf8', border: '1px solid #0284c7', borderRadius: '4px', padding: '3px 8px', fontSize: '10px', fontWeight: '600', cursor: 'pointer' }}
                >
                  🎯 Fit to Image
                </button>
              </div>
              <button
                type="button"
                onClick={handleRemoveCrop}
                style={{
                  backgroundColor: '#2b2b2f',
                  color: '#faad14',
                  border: '1px solid #483d1c',
                  borderRadius: '4px',
                  padding: '3px 8px',
                  fontSize: '10px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px'
                }}
                title="Remove crop entirely and fit full photo into frame without clipping"
              >
                🖼️ Fit Full Photo (No Crop)
              </button>
            </div>
          </div>

          {/* Footer */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderTop: '1px solid var(--border-color, #2d2d30)', padding: '12px 20px', background: 'var(--panel-bg, #18181a)' }}>
            <span style={{ fontSize: '10px', color: 'var(--text-mute, #71717a)' }}>
              Crop box: {Math.round(tempFrameW)} × {Math.round(tempFrameH)} px
            </span>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button
                type="button"
                onClick={handleClose}
                style={{
                  padding: '6px 14px',
                  fontSize: '11px',
                  background: 'var(--card-bg, #2b2b2f)',
                  color: 'var(--text-main, #dfdfe3)',
                  border: '1px solid var(--border-color, #3c3c42)',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontWeight: 'bold'
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                data-testid="apply-crop-btn"
                onClick={handleApply}
                style={{
                  padding: '6px 18px',
                  fontSize: '11px',
                  background: 'var(--primary-gradient, var(--primary-color, #1677ff))',
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontWeight: 'bold',
                  boxShadow: '0 2px 8px rgba(22, 119, 255, 0.3)'
                }}
              >
                Apply Crop to Frame
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const getElementEffectsStyle = (elem) => {
    if (!elem) return {};
    
    // 1. Gather Shadow parameters
    let shadowStr = '';
    let shadowFilterStr = '';
    let textShadowStr = '';
    if (elem.shadowEnabled) {
      const hex = elem.shadowColor || '#000000';
      const opacity = elem.shadowOpacity !== undefined ? elem.shadowOpacity : 0.5;
      let r = 0, g = 0, b = 0;
      if (hex.startsWith('#')) {
        r = parseInt(hex.slice(1, 3), 16) || 0;
        g = parseInt(hex.slice(3, 5), 16) || 0;
        b = parseInt(hex.slice(5, 7), 16) || 0;
      }
      const colorStr = `rgba(${r}, ${g}, ${b}, ${opacity})`;
      const offsetX = elem.shadowOffsetX || 0;
      const offsetY = elem.shadowOffsetY || 0;
      const blur = elem.shadowBlur !== undefined ? elem.shadowBlur : 5;
      const spread = elem.shadowSpread || 0;
      
      shadowStr = `${offsetX}px ${offsetY}px ${blur}px ${spread}px ${colorStr}`;
      shadowFilterStr = `drop-shadow(${offsetX}px ${offsetY}px ${blur}px ${colorStr})`;
      textShadowStr = `${offsetX}px ${offsetY}px ${blur}px ${colorStr}`;
    }
    
    // 2. Gather Glow parameters
    let glowStr = '';
    let glowFilterStr = '';
    let textGlowStr = '';
    if (elem.glowEnabled) {
      const hex = elem.glowColor || '#00ff00';
      const opacity = elem.glowOpacity !== undefined ? elem.glowOpacity : 0.6;
      let r = 0, g = 0, b = 0;
      if (hex.startsWith('#')) {
        r = parseInt(hex.slice(1, 3), 16) || 0;
        g = parseInt(hex.slice(3, 5), 16) || 0;
        b = parseInt(hex.slice(5, 7), 16) || 0;
      }
      const colorStr = `rgba(${r}, ${g}, ${b}, ${opacity})`;
      const blur = elem.glowBlur !== undefined ? elem.glowBlur : 10;
      const spread = elem.glowSpread || 0;
      const isInner = elem.glowType === 'inner';
      const insetPrefix = isInner ? 'inset ' : '';
      
      glowStr = `${insetPrefix}0px 0px ${blur}px ${spread}px ${colorStr}`;
      glowFilterStr = `drop-shadow(0px 0px ${blur}px ${colorStr})`;
      textGlowStr = `0px 0px ${blur}px ${colorStr}`;
    }
    
    // Compute border radius for containment
    let borderRadius = undefined;
    if (elem.shadowEnabled || elem.glowEnabled) {
      if (elem.type === 'shape') {
        if (elem.shapeType === 'circle') borderRadius = '50%';
        else if (elem.shapeType === 'pill') borderRadius = '999px';
        else if (elem.shapeType === 'rect') borderRadius = `${elem.hasBorder !== false ? (elem.borderRadius !== undefined ? elem.borderRadius : 0) : 0}px`;
      } else if (elem.type === 'image') {
        borderRadius = `${elem.hasBorder !== false ? (elem.borderRadius !== undefined ? elem.borderRadius : 0) : 0}px`;
      }
    }
    
    const style = {};
    if (borderRadius) {
      style.borderRadius = borderRadius;
    }
    
    if (elem.type === 'text') {
      const textShadows = [textShadowStr, textGlowStr].filter(Boolean).join(', ');
      if (textShadows) {
        style.textShadow = textShadows;
      }
    } else if (elem.type === 'shape' && ['triangle', 'polygon', 'diamond', 'star'].includes(elem.shapeType)) {
      const filters = [shadowFilterStr, glowFilterStr].filter(Boolean).join(' ');
      if (filters) {
        style.filter = filters;
      }
    } else {
      const boxShadows = [shadowStr, glowStr].filter(Boolean).join(', ');
      if (boxShadows) {
        style.boxShadow = boxShadows;
      }
    }
    
    return style;
  };

  const renderColorInputRow = (val, onChange, placeholder = '#ffffff') => {
    const hasEyeDropper = typeof window !== 'undefined' && 'EyeDropper' in window;
    return (
      <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
        <input
          type="text"
          value={val || ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          style={{ flex: 1, backgroundColor: '#1b1b1d', border: '1px solid #3c3c42', borderRadius: '4px', padding: '4px 6px', color: '#fff', fontSize: '11px', outline: 'none' }}
        />
        <StableColorInput
          value={val && val.startsWith('#') ? val : '#ffffff'}
          onChange={(e) => onChange(e.target.value)}
          style={{ width: '28px', height: '24px', border: 'none', padding: 0, cursor: 'pointer', backgroundColor: 'transparent' }}
        />
        {hasEyeDropper ? (
          <button
            type="button"
            onClick={async () => {
              try {
                const eyeDropper = new window.EyeDropper();
                const result = await eyeDropper.open();
                onChange(result.sRGBHex);
              } catch (e) {
                console.log("EyeDropper closed or failed:", e);
              }
            }}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '24px',
              height: '24px',
              backgroundColor: '#1b1b1d',
              border: '1px solid #3c3c42',
              borderRadius: '4px',
              color: '#dfdfe3',
              cursor: 'pointer',
              padding: 0
            }}
            onMouseOver={(e) => { e.currentTarget.style.backgroundColor = '#2d2d31'; }}
            onMouseOut={(e) => { e.currentTarget.style.backgroundColor = '#1b1b1d'; }}
            title="Sample color from screen"
          >
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 3L11 13" />
              <path d="M19 5l-2.5-2.5a1.5 1.5 0 0 0-2.12 0l-3 3a1.5 1.5 0 0 0 0 2.12L13.88 10" />
              <path d="M11 11H8.5a1.5 1.5 0 0 0-1.5 1.5v1.79c0 .4-.16.78-.44 1.06L3.5 18.5a1 1 0 0 0 0 1.41l.59.59a1 1 0 0 0 1.41 0l3.15-3.06c.28-.28.66-.44 1.06-.44H11.5A1.5 1.5 0 0 0 13 15.5V13" />
            </svg>
          </button>
        ) : (
          <button
            type="button"
            disabled
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '24px',
              height: '24px',
              backgroundColor: '#1b1b1d',
              border: '1px solid #232325',
              borderRadius: '4px',
              color: '#555558',
              cursor: 'not-allowed',
              padding: 0
            }}
            title="Eyedropper requires a secure context (HTTPS or localhost). If using a custom domain over HTTP, enable Chrome flag #unsafely-treat-insecure-origin-as-secure"
          >
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" opacity="0.3">
              <path d="M21 3L11 13" />
              <path d="M19 5l-2.5-2.5a1.5 1.5 0 0 0-2.12 0l-3 3a1.5 1.5 0 0 0 0 2.12L13.88 10" />
              <path d="M11 11H8.5a1.5 1.5 0 0 0-1.5 1.5v1.79c0 .4-.16.78-.44 1.06L3.5 18.5a1 1 0 0 0 0 1.41l.59.59a1 1 0 0 0 1.41 0l3.15-3.06c.28-.28.66-.44 1.06-.44H11.5A1.5 1.5 0 0 0 13 15.5V13" />
            </svg>
          </button>
        )}
      </div>
    );
  };


  // Preset theme application
  // eslint-disable-next-line no-unused-vars
  const applyTheme = (themeId) => {
    const selectedTheme = THEMES.find(t => t.id === themeId);
    if (!selectedTheme) return;
    saveHistoryState();
    setGlobalStyles(prev => ({
      ...prev,
      theme: themeId,
      borderColor: selectedTheme.primary
    }));
    setSides(prev => ({
      ...prev,
      front: {
        ...prev.front,
        background: { type: 'gradient', value: `linear-gradient(135deg, ${selectedTheme.secondary} 0%, ${selectedTheme.bg} 100%)` }
      },
      back: {
        ...prev.back,
        background: { type: 'color', value: selectedTheme.bg }
      }
    }));
  };

  // Find currently active element configuration
  const activeElement = sides[activeSide].elements.find(e => e.id === selectedElementId);

  const updateActiveElement = (properties) => {
    const isLockToggle = properties.locked !== undefined;
    const isTextUpdate = properties.hasOwnProperty('text');
    setSides(prevSides => {
      const currentSideElements = prevSides[activeSide].elements;
      const activeElem = currentSideElements.find(e => e.id === selectedElementId);
      
      let updatedProps = { ...properties };
      if (isTextUpdate && activeElem && activeElem.type === 'text') {
        const newText = properties.text || '';
        const maxLen = 30;
        updatedProps.name = newText.length > maxLen 
          ? newText.substring(0, maxLen).trimEnd() + '...' 
          : newText || 'Text Layer';
      }

      return {
        ...prevSides,
        [activeSide]: {
          ...prevSides[activeSide],
          elements: currentSideElements.map(e => {
            const isSelf = e.id === selectedElementId;
            const isChild = isLockToggle && activeElem && activeElem.type === 'group' && e.parentId === selectedElementId;
            if (isSelf || isChild) {
              return { ...e, ...updatedProps };
            }
            return e;
          })
        }
      };
    });
  };

  const generateGradientString = (state) => {
    if (!state || !state.stops || !Array.isArray(state.stops)) {
      return 'linear-gradient(90deg, #ffffff 0%, #000000 100%)';
    }
    const sortedStops = [...state.stops].sort((a, b) => a.position - b.position);
    const stopsString = sortedStops.map(stop => {
      const hex = stop.color || '#ffffff';
      const shorthandRegex = /^#?([a-f\d])([a-f\d])([a-f\d])$/i;
      const fullHex = hex.replace(shorthandRegex, (m, r, g, b) => r + r + g + g + b + b);
      const match = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(fullHex);
      const rgb = match ? {
        r: parseInt(match[1], 16),
        g: parseInt(match[2], 16),
        b: parseInt(match[3], 16)
      } : { r: 255, g: 255, b: 255 };
      const opacity = stop.opacity !== undefined ? stop.opacity : 1;
      return `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${opacity}) ${stop.position}%`;
    }).join(', ');

    if (state.type === 'radial') {
      return `radial-gradient(circle, ${stopsString})`;
    } else {
      return `linear-gradient(${state.angle || 90}deg, ${stopsString})`;
    }
  };

  const handleApplyGradient = () => {
    const gradientValue = generateGradientString(gradientState);
    if (editingGradientTarget === 'cardBackground') {
      saveHistoryState();
      setSides({
        ...sides,
        [activeSide]: {
          ...sides[activeSide],
          background: {
            type: 'gradient',
            value: gradientValue,
            gradient: gradientState
          }
        }
      });
      setShowGradientModal(false);
      return;
    }

    if (editingGradientTarget === 'globalSwatch') {
      updateGlobalColor(editingGlobalColor, gradientValue);
      setShowGradientModal(false);
      return;
    }

    if (!activeElement) return;
    if (editingGradientTarget === 'secondary') {
      updateActiveElement({
        strokeColor2: gradientValue,
        borderGradient2: gradientState
      });
    } else if (editingGradientTarget === 'shapeFill') {
      updateActiveElement({
        fill: gradientValue,
        fillGradient: gradientState
      });
    } else if (editingGradientTarget === 'textFill') {
      updateActiveElement({
        color: gradientValue,
        colorGradient: gradientState
      });
    } else if (editingGradientTarget === 'textStroke') {
      updateActiveElement({
        textStrokeColor: gradientValue,
        textStrokeGradient: gradientState
      });
    } else if (editingGradientTarget === 'headerFill') {
      updateActiveElement({
        headerFill: gradientValue,
        headerFillGradient: gradientState
      });
    } else if (editingGradientTarget === 'datagridLabel') {
      updateActiveElement({
        labelColor: gradientValue,
        labelGradient: gradientState
      });
    } else if (editingGradientTarget === 'datagridValue') {
      updateActiveElement({
        valueColor: gradientValue,
        valueGradient: gradientState
      });
    } else {
      updateActiveElement({
        strokeColor: gradientValue,
        borderGradient: gradientState
      });
    }
    setShowGradientModal(false);
  };

  const handleInsertStop = () => {
    const activeStop = gradientState.stops[selectedStopIndex] || gradientState.stops[0];
    if (!activeStop) return;
    const currentPos = activeStop.position;
    const positions = gradientState.stops.map(s => s.position).sort((a, b) => a - b);
    const nextPos = positions.find(p => p > currentPos) || 100;
    const newPos = Math.round((currentPos + nextPos) / 2);
    
    const newStop = {
      color: activeStop.color || '#ffffff',
      position: newPos,
      opacity: activeStop.opacity !== undefined ? activeStop.opacity : 1
    };
    
    setGradientState(prev => {
      const newStops = [...prev.stops, newStop];
      newStops.sort((a, b) => a.position - b.position);
      const idx = newStops.indexOf(newStop);
      setSelectedStopIndex(idx >= 0 ? idx : newStops.length - 1);
      return { ...prev, stops: newStops };
    });
  };

  const handleDeleteStop = () => {
    if (gradientState.stops.length <= 2) return;
    setGradientState(prev => {
      const newStops = prev.stops.filter((_, idx) => idx !== selectedStopIndex);
      setSelectedStopIndex(0);
      return { ...prev, stops: newStops };
    });
  };

  const handleReverseGradient = () => {
    setGradientState(prev => {
      const reversedStops = prev.stops.map(s => ({
        ...s,
        position: 100 - s.position
      }));
      reversedStops.sort((a, b) => a.position - b.position);
      return { ...prev, stops: reversedStops };
    });
    setSelectedStopIndex(0);
  };

  const handleCopyStop = () => {
    const activeStop = gradientState.stops[selectedStopIndex] || gradientState.stops[0];
    if (!activeStop) return;
    const newPos = Math.min(100, activeStop.position + 5);
    const newStop = {
      color: activeStop.color,
      position: newPos,
      opacity: activeStop.opacity
    };
    setGradientState(prev => {
      const newStops = [...prev.stops, newStop];
      newStops.sort((a, b) => a.position - b.position);
      const idx = newStops.indexOf(newStop);
      setSelectedStopIndex(idx >= 0 ? idx : newStops.length - 1);
      return { ...prev, stops: newStops };
    });
  };

  const activeStop = gradientState && gradientState.stops && Array.isArray(gradientState.stops)
    ? (gradientState.stops[selectedStopIndex] || gradientState.stops[0])
    : null;

  // ----------------------------------------------------
  // CONDITIONAL RENDER: FULL-SCREEN DESIGNER (React Portal)
  // ----------------------------------------------------
  if (viewMode === 'designer') {
    return (
      <>
        {createPortal(
          <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        zIndex: 999999, // Render on top of everything
        backgroundColor: '#1a1a1c',
        color: '#dfdfe3',
        display: 'flex',
        flexDirection: 'column',
        fontFamily: 'system-ui, -apple-system, sans-serif',
        overflow: 'hidden'
      }}>
        <style>{`
          @keyframes shimmerFoilLoop {
            0% { background-position: 0% 0%; }
            50% { background-position: 100% 100%; }
            100% { background-position: 0% 0%; }
          }
          @keyframes iridescentRainbowLoop {
            0% { background-position: 0% 50%; }
            50% { background-position: 100% 50%; }
            100% { background-position: 0% 50%; }
          }
          @keyframes silverGlintLoop {
            0% { background-position: -200% 0%; }
            30% { background-position: 200% 0%; }
            100% { background-position: 200% 0%; }
          }
          @keyframes ambientWarmPulseLoop {
            0% { opacity: 0.3; }
            50% { opacity: 0.75; }
            100% { opacity: 0.3; }
          }
          @keyframes goldFlareLoop {
            0% { background-position: -200% 0%; }
            40% { background-position: 200% 0%; }
            100% { background-position: 200% 0%; }
          }
          @keyframes glassReflectionLoop {
            0% { background-position: 0% 0%; }
            100% { background-position: 200% 200%; }
          }
          @keyframes cosmicAuroraLoop {
            0% { transform: scale(1) translate(0, 0); opacity: 0.4; }
            50% { transform: scale(1.15) translate(-4%, -4%); opacity: 0.8; }
            100% { transform: scale(1) translate(0, 0); opacity: 0.4; }
          }
        `}</style>
        {/* Top Context Toolbar */}
        <div style={{
          height: '42px',
          backgroundColor: '#2b2b2f',
          borderBottom: '1px solid #3c3c42',
          display: 'flex',
          alignItems: 'center',
          userSelect: 'none',
          position: 'relative',
          width: '100%'
        }}>
          {/* Left & Center Canvas Region (spanning width: calc(100% - 460px)) */}
          <div style={{
            flex: 1,
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            paddingLeft: '12px',
            paddingRight: 0,
            minWidth: 0
          }}>
            {/* Left Context: Exit, Save Draft, Template Name, Undo/Redo, Flip H/V */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', minWidth: 0, flexShrink: 1 }}>
              <button
                onClick={handleExitDesigner}
                title="Exit Designer and Return"
                data-testid="exit-designer-btn"
                style={{
                  height: '28px',
                  boxSizing: 'border-box',
                  background: 'none',
                  border: '1px solid #55555c',
                  color: '#ffffff',
                  borderRadius: '4px',
                  padding: '0 10px',
                  fontSize: '11px',
                  cursor: 'pointer',
                  fontWeight: '600',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '4px',
                  backgroundColor: '#38383d',
                  flexShrink: 0
                }}
              >
                ◀ Exit
              </button>

              <button
                onClick={handleSaveDesign}
                disabled={saveStatus === 'Saving draft...'}
                data-testid="save-draft-btn"
                style={{
                  height: '28px',
                  boxSizing: 'border-box',
                  backgroundColor: saveStatus === 'Saving draft...' ? '#3c3c42' : '#1677ff',
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: '4px',
                  padding: '0 14px',
                  fontSize: '11px',
                  fontWeight: '700',
                  cursor: saveStatus === 'Saving draft...' ? 'not-allowed' : 'pointer',
                  opacity: saveStatus === 'Saving draft...' ? 0.7 : 1,
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0
                }}
              >
                {saveStatus === 'Saving draft...' ? 'Saving...' : 'Save Draft'}
              </button>

              {saveStatus && (
                <span style={{ 
                  color: saveStatus === 'Saving draft...' ? '#8e8e93' : '#63c37c', 
                  fontSize: '11px', 
                  fontWeight: 'bold', 
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '4px',
                  flexShrink: 0
                }}>
                  {saveStatus === 'Saving draft...' ? (
                    <>
                      <svg className="animate-spin" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" style={{ animation: 'spin 1s linear infinite' }}>
                        <circle cx="12" cy="12" r="10" stroke="rgba(255,255,255,0.2)" />
                        <path d="M12 2a10 10 0 0 1 10 10" stroke="#8e8e93" />
                      </svg>
                      Saving...
                    </>
                  ) : (
                    '✓ Saved'
                  )}
                </span>
              )}
              {statusError && <span style={{ color: '#ff7b72', fontSize: '11px', fontWeight: 'bold', flexShrink: 0 }}>⚠️ Error</span>}

              <div style={{ width: '1px', height: '20px', backgroundColor: '#3c3c42', margin: '0 2px', flexShrink: 0 }} />

              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', minWidth: 0, flexShrink: 1 }}>
                <span style={{ fontSize: '11px', color: '#8e8e93', fontWeight: 'bold', whiteSpace: 'nowrap' }}>
                  {isTrophyCardMode ? "Trophy Card Name:" : "Template:"}
                </span>
                <input
                  type="text"
                  data-testid="template-name-input"
                  value={templateName}
                  onChange={(e) => setTemplateName(e.target.value)}
                  style={{
                    height: '28px',
                    boxSizing: 'border-box',
                    backgroundColor: '#1b1b1d',
                    border: '1px solid #3c3c42',
                    color: '#ffffff',
                    padding: '0 8px',
                    borderRadius: '4px',
                    fontSize: '12px',
                    fontWeight: 'bold',
                    width: '180px',
                    maxWidth: '180px',
                    minWidth: '100px',
                    marginRight: '4px'
                  }}
                />
                {isSystemTemplate && (
                  <span title="Official System Card Template" style={{ fontSize: '13px', marginRight: '4px', cursor: 'default', flexShrink: 0 }}>
                    ⭐
                  </span>
                )}
                <button
                  onClick={handleUndo}
                  disabled={undoStack.length === 0}
                  title="Undo"
                  style={{
                    color: undoStack.length === 0 ? '#4b5563' : '#a2a2a8',
                    border: '1px solid ' + (undoStack.length === 0 ? '#27272a' : '#3c3c42'),
                    borderRadius: '4px',
                    width: '28px',
                    height: '28px',
                    boxSizing: 'border-box',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: undoStack.length === 0 ? 'not-allowed' : 'pointer',
                    transition: 'all 0.2s',
                    backgroundColor: undoStack.length === 0 ? 'transparent' : 'rgba(255,255,255,0.03)',
                    flexShrink: 0
                  }}
                >
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ display: 'block' }}>
                    <path d="M3 7v6h6"></path>
                    <path d="M21 17a9 9 0 0 0-9-9 9 9 0 0 0-6 2.3L3 13"></path>
                  </svg>
                </button>
                <button
                  onClick={handleRedo}
                  disabled={redoStack.length === 0}
                  title="Redo"
                  style={{
                    color: redoStack.length === 0 ? '#4b5563' : '#a2a2a8',
                    border: '1px solid ' + (redoStack.length === 0 ? '#27272a' : '#3c3c42'),
                    borderRadius: '4px',
                    width: '28px',
                    height: '28px',
                    boxSizing: 'border-box',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: redoStack.length === 0 ? 'not-allowed' : 'pointer',
                    transition: 'all 0.2s',
                    backgroundColor: undoStack.length === 0 ? 'transparent' : 'rgba(255,255,255,0.03)',
                    flexShrink: 0
                  }}
                >
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ display: 'block' }}>
                    <path d="M21 7v6h-6"></path>
                    <path d="M3 17a9 9 0 0 1 9-9 9 9 0 0 1 6 2.3l3 2.7"></path>
                  </svg>
                </button>
              </div>

              {/* Dynamic Context Settings */}
              {activeTool === 'select' && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', borderLeft: '1px solid #3c3c42', paddingLeft: '10px', flexShrink: 0 }}>
                  <button
                    onClick={handleToggleFlipH}
                    disabled={!activeElement || activeElement.type !== 'image'}
                    title="Flip Horizontally"
                    style={{
                      color: (!activeElement || activeElement.type !== 'image') ? '#4b5563' : '#a2a2a8',
                      border: '1px solid ' + ((!activeElement || activeElement.type !== 'image') ? '#27272a' : '#3c3c42'),
                      borderRadius: '4px',
                      width: '28px',
                      height: '28px',
                      boxSizing: 'border-box',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: (!activeElement || activeElement.type !== 'image') ? 'not-allowed' : 'pointer',
                      transition: 'all 0.2s',
                      backgroundColor: (!activeElement || activeElement.type !== 'image') ? 'transparent' : 'rgba(255,255,255,0.03)'
                    }}
                  >
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ display: 'block' }}>
                      <line x1="12" y1="2" x2="12" y2="22" strokeDasharray="3 3" />
                      <polygon points="12 18 12 6 4 12" />
                      <polygon points="12 18 12 6 20 12" />
                    </svg>
                  </button>
                  <button
                    onClick={handleToggleFlipV}
                    disabled={!activeElement || activeElement.type !== 'image'}
                    title="Flip Vertically"
                    style={{
                      color: (!activeElement || activeElement.type !== 'image') ? '#4b5563' : '#a2a2a8',
                      border: '1px solid ' + ((!activeElement || activeElement.type !== 'image') ? '#27272a' : '#3c3c42'),
                      borderRadius: '4px',
                      width: '28px',
                      height: '28px',
                      boxSizing: 'border-box',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: (!activeElement || activeElement.type !== 'image') ? 'not-allowed' : 'pointer',
                      transition: 'all 0.2s',
                      backgroundColor: (!activeElement || activeElement.type !== 'image') ? 'transparent' : 'rgba(255,255,255,0.03)'
                    }}
                  >
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ display: 'block' }}>
                      <line x1="2" y1="12" x2="22" y2="12" strokeDasharray="3 3" />
                      <polygon points="18 12 6 12 12 4" />
                      <polygon points="18 12 6 12 12 20" />
                    </svg>
                  </button>
                </div>
              )}
            </div>

            {/* Layout Tabs (Ending precisely at the 460px panel boundary) */}
            <div style={{ display: 'flex', height: '100%', alignItems: 'flex-end', flexShrink: 0 }}>
              <div style={{ display: 'flex', gap: '2px' }}>
                <button
                  onClick={() => { setActiveSide('front'); setPreviewFlipped(false); }}
                  style={{
                    backgroundColor: activeSide === 'front' ? '#1a1a1c' : '#333338',
                    color: activeSide === 'front' ? '#ffffff' : '#a2a2a8',
                    border: '1px solid #3c3c42',
                    borderBottom: activeSide === 'front' ? '1px solid #1a1a1c' : '1px solid #3c3c42',
                    borderRadius: '4px 4px 0 0',
                    padding: '4px 14px',
                    fontSize: '11px',
                    fontWeight: 'bold',
                    cursor: 'pointer',
                    height: '28px',
                    boxSizing: 'border-box',
                    zIndex: activeSide === 'front' ? 2 : 1
                  }}
                >
                  Front Side Layout
                </button>
                <button
                  onClick={() => { setActiveSide('back'); setPreviewFlipped(true); }}
                  style={{
                    backgroundColor: activeSide === 'back' ? '#1a1a1c' : '#333338',
                    color: activeSide === 'back' ? '#ffffff' : '#a2a2a8',
                    border: '1px solid #3c3c42',
                    borderBottom: activeSide === 'back' ? '1px solid #1a1a1c' : '1px solid #3c3c42',
                    borderRadius: '4px 4px 0 0',
                    padding: '4px 14px',
                    fontSize: '11px',
                    fontWeight: 'bold',
                    cursor: 'pointer',
                    height: '28px',
                    boxSizing: 'border-box',
                    zIndex: activeSide === 'back' ? 2 : 1
                  }}
                >
                  Back Side Layout
                </button>
              </div>
            </div>
          </div>

          {/* Right Section: Header across the 460px inspector panel */}
          <div style={{
            width: '460px',
            flexShrink: 0,
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: '8px',
            paddingRight: '12px'
          }}>
            {/* Flip Preview Button to the left of Assets */}
            <button
              type="button"
              onClick={() => setShow3DPreview(true)}
              data-testid="topbar-flip-preview-btn"
              title="3D Flip Preview"
              style={{
                height: '28px',
                boxSizing: 'border-box',
                backgroundColor: '#38383d',
                color: '#ffffff',
                border: '1px solid #55555c',
                borderRadius: '4px',
                padding: '0 10px',
                fontSize: '11px',
                cursor: 'pointer',
                fontWeight: '600',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '5px',
                flexShrink: 0,
                transition: 'all 0.15s'
              }}
              onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#45454b'; }}
              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#38383d'; }}
            >
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ display: 'block' }}>
                <polyline points="23 4 23 10 17 10"></polyline>
                <polyline points="1 20 1 14 7 14"></polyline>
                <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"></path>
              </svg>
              <span>Flip Preview</span>
            </button>

            {/* Assets Button next to Actions */}
            <button
              type="button"
              onClick={() => {
                setIsChangingImageSource(false);
                setPendingImageClickCoords(null);
                setShowAssetSelectorModal(true);
              }}
              data-testid="topbar-assets-btn"
              title="Open Asset Library & Upload New Assets"
              style={{
                height: '28px',
                boxSizing: 'border-box',
                backgroundColor: '#38383d',
                color: '#ffffff',
                border: '1px solid #55555c',
                borderRadius: '4px',
                padding: '0 10px',
                fontSize: '11px',
                fontWeight: '600',
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '5px',
                flexShrink: 0,
                transition: 'all 0.15s'
              }}
              onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#45454b'; }}
              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#38383d'; }}
            >
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                <circle cx="8.5" cy="8.5" r="1.5" />
                <polyline points="21 15 16 10 5 21" />
              </svg>
              <span>Assets</span>
            </button>

            {canExportImport && (
              <div style={{ position: 'relative' }}>
                <button
                  type="button"
                  onClick={() => setShowEditorActionsDropdown(prev => !prev)}
                  data-testid="editor-actions-dropdown-btn"
                  title="Card Template Actions (Export, Import, Push to Prod)"
                  style={{
                    height: '28px',
                    boxSizing: 'border-box',
                    backgroundColor: '#38383d',
                    color: '#ffffff',
                    border: '1px solid #55555c',
                    borderRadius: '4px',
                    padding: '0 10px',
                    fontSize: '11px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '5px',
                    flexShrink: 0,
                    transition: 'all 0.15s'
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#45454b'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#38383d'; }}
                >
                  <span style={{ fontSize: '12px' }}>⚡</span>
                  <span>Actions</span>
                  <span style={{ fontSize: '9px', color: '#a2a2a8' }}>▾</span>
                </button>

                {showEditorActionsDropdown && (
                  <>
                    <div
                      onClick={() => setShowEditorActionsDropdown(false)}
                      style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 999999 }}
                    />
                    <div
                      data-testid="editor-actions-dropdown-menu"
                      style={{
                        position: 'absolute',
                        top: '100%',
                        right: 0,
                        marginTop: '4px',
                        backgroundColor: '#1b1b1d',
                        border: '1px solid #3c3c42',
                        borderRadius: '6px',
                        padding: '4px',
                        zIndex: 1000000,
                        display: 'flex',
                        flexDirection: 'column',
                        minWidth: '175px',
                        boxShadow: '0 8px 24px rgba(0,0,0,0.6)',
                        gap: '2px'
                      }}
                    >
                      <button
                        onClick={(e) => {
                          if (e && e.stopPropagation) e.stopPropagation();
                          setShowEditorActionsDropdown(false);
                          setPrintExportError('');
                          setPrintExportSuccess(false);
                          setShowPrintExportModal(true);
                        }}
                        title="Export Highest Quality Print PDF for Epson EcoTank L8160 (Book Mode & Normal Mode)"
                        data-testid="editor-export-print-btn"
                        style={{
                          width: '100%',
                          backgroundColor: 'transparent',
                          color: '#38bdf8',
                          border: 'none',
                          borderRadius: '4px',
                          padding: '7px 10px',
                          fontSize: '12px',
                          fontWeight: '600',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          textAlign: 'left',
                          transition: 'background-color 0.15s'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = '#2d2d30';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = 'transparent';
                        }}
                      >
                        <span style={{ fontSize: '14px' }}>🖨️</span>
                        <span>Export Print (PDF)</span>
                      </button>

                      <button
                        disabled={!selectedTemplateId || isExporting}
                        onClick={() => {
                          setShowEditorActionsDropdown(false);
                          handleExportCard(selectedTemplateId);
                        }}
                        title="Export Card Package with Assets"
                        data-testid="editor-export-btn"
                        style={{
                          width: '100%',
                          backgroundColor: 'transparent',
                          color: (!selectedTemplateId || isExporting) ? '#6b7280' : '#dfdfe3',
                          border: 'none',
                          borderRadius: '4px',
                          padding: '7px 10px',
                          fontSize: '12px',
                          fontWeight: '500',
                          cursor: (!selectedTemplateId || isExporting) ? 'not-allowed' : 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          textAlign: 'left',
                          transition: 'background-color 0.15s'
                        }}
                        onMouseEnter={(e) => {
                          if (selectedTemplateId && !isExporting) e.currentTarget.style.backgroundColor = '#2d2d30';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = 'transparent';
                        }}
                      >
                        <span style={{ fontSize: '13px' }}>📤</span>
                        <span>{isExporting ? 'Exporting...' : 'Export Card'}</span>
                      </button>

                      <button
                        onClick={() => {
                          setShowEditorActionsDropdown(false);
                          handleOpenImportModal();
                        }}
                        title="Import Trophy Card JSON & Assets into a Tenant Card Library"
                        data-testid="editor-import-btn"
                        style={{
                          width: '100%',
                          backgroundColor: 'transparent',
                          color: '#dfdfe3',
                          border: 'none',
                          borderRadius: '4px',
                          padding: '7px 10px',
                          fontSize: '12px',
                          fontWeight: '500',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          textAlign: 'left',
                          transition: 'background-color 0.15s'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = '#2d2d30';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = 'transparent';
                        }}
                      >
                        <span style={{ fontSize: '13px' }}>📥</span>
                        <span>Import Card</span>
                      </button>

                      <button
                        disabled={!selectedTemplateId}
                        onClick={() => {
                          setShowEditorActionsDropdown(false);
                          handleOpenPushModal(selectedTemplateId);
                        }}
                        title="Push Card to Production / Remote Server"
                        data-testid="editor-push-card-btn"
                        style={{
                          width: '100%',
                          backgroundColor: 'transparent',
                          color: selectedTemplateId ? '#60a5fa' : '#6b7280',
                          border: 'none',
                          borderRadius: '4px',
                          padding: '7px 10px',
                          fontSize: '12px',
                          fontWeight: '500',
                          cursor: selectedTemplateId ? 'pointer' : 'not-allowed',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          textAlign: 'left',
                          transition: 'background-color 0.15s'
                        }}
                        onMouseEnter={(e) => {
                          if (selectedTemplateId) e.currentTarget.style.backgroundColor = '#2d2d30';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = 'transparent';
                        }}
                      >
                        <span style={{ fontSize: '13px' }}>🚀</span>
                        <span>Push to Prod</span>
                      </button>

                      {!isTrophyCardMode && isAdmin && (
                        <>
                          <div style={{ height: '1px', backgroundColor: '#2d2d30', margin: '3px 0' }} />
                          <button
                            type="button"
                            onClick={() => {
                              setShowEditorActionsDropdown(false);
                              setIsSystemTemplate(prev => !prev);
                            }}
                            title={isSystemTemplate ? "Designated as Official System Card Template. Click to untag." : "Click to designate as System Card Template for Purchased Package Automation."}
                            data-testid="editor-toggle-system-btn"
                            style={{
                              width: '100%',
                              backgroundColor: 'transparent',
                              color: isSystemTemplate ? '#facc15' : '#dfdfe3',
                              border: 'none',
                              borderRadius: '4px',
                              padding: '7px 10px',
                              fontSize: '12px',
                              fontWeight: '500',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '8px',
                              textAlign: 'left',
                              transition: 'background-color 0.15s'
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.backgroundColor = '#2d2d30';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.backgroundColor = 'transparent';
                            }}
                          >
                            <span style={{ fontSize: '13px' }}>⭐</span>
                            <span>{isSystemTemplate ? 'Remove System Tag' : 'Tag as System'}</span>
                          </button>
                        </>
                      )}
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Workspace body */}
        <div style={{
          flex: 1,
          display: 'grid',
          gridTemplateColumns: showAiPanel && hasGeminiKey ? '48px 350px 1fr 460px' : '48px 1fr 460px',
          backgroundColor: '#1a1a1c',
          overflow: 'hidden'
        }}>
          {/* LEFT TOOLBAR */}
          <div style={{
            backgroundColor: '#2b2b2f',
            borderRight: '1px solid #3c3c42',
            display: 'flex',
            flexDirection: 'column',
            padding: '8px 0',
            gap: '6px',
            alignItems: 'center',
            userSelect: 'none',
            position: 'relative'
          }}>
            {[
              {
                id: 'select',
                label: 'Pointer',
                svg: (
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polygon points="5 3 19 12 12 14 9 21 5 3" />
                  </svg>
                )
              },
              {
                id: 'shape',
                label: 'Shape Tool',
                svg: (SHAPES_LIST.find(sh => sh.id === selectedShapeSubtype) || SHAPES_LIST[0]).svg
              },
              {
                id: 'image',
                label: selectedImageSubTool === 'frame' ? 'Picture Frame' : 'Image Element',
                svg: selectedImageSubTool === 'frame' ? (
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                    <circle cx="9" cy="9" r="2" />
                    <path d="M21 15l-3.086-3.086a2 2 0 00-2.828 0L6 21" />
                  </svg>
                ) : (
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="2" y="3" width="20" height="14" rx="2" ry="2" />
                    <line x1="2" y1="20" x2="22" y2="20" />
                    <polyline points="12 13 17 8 22 13" />
                    <polyline points="2 13 7 8 13 13" />
                  </svg>
                )
              },
              {
                id: 'text',
                label: 'Artistic Text',
                svg: (
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="4 7 4 4 20 4 20 7" />
                    <line x1="9" y1="20" x2="15" y2="20" />
                    <line x1="12" y1="4" x2="12" y2="20" />
                  </svg>
                )
              },
              {
                id: 'datagrid',
                label: 'Table Frame',
                svg: (
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M3 3h18v18H3zM21 9H3M21 15H3M12 3v18" />
                  </svg>
                )
              },
              {
                id: 'accent',
                label: selectedDividerSubTool === 'vertical' ? 'Vertical Divider' : 'Horizontal Divider',
                svg: selectedDividerSubTool === 'vertical' ? (
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ transform: 'rotate(90deg)', display: 'block' }}>
                    <line x1="3" y1="12" x2="21" y2="12" />
                    <circle cx="12" cy="12" r="4.5" />
                  </svg>
                ) : (
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ display: 'block' }}>
                    <line x1="3" y1="12" x2="21" y2="12" />
                    <circle cx="12" cy="12" r="4.5" />
                  </svg>
                )
              },
              {
                id: 'certify',
                label: 'Certify Stamp',
                svg: (
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="8" r="7" />
                    <polyline points="8.21 13.89 7 23 12 20 17 23 15.79 13.88" />
                  </svg>
                )
              },
              {
                id: 'qrcode',
                label: 'QR Code Tool',
                svg: (
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="3" width="7" height="7" rx="1" />
                    <rect x="14" y="3" width="7" height="7" rx="1" />
                    <rect x="3" y="14" width="7" height="7" rx="1" />
                    <rect x="14" y="14" width="3" height="3" />
                    <path d="M21 14v3h-3" />
                    <path d="M18 21h3v-3" />
                  </svg>
                )
              }
            ].map(t => {
              const isActive = activeTool === t.id;
              return (
                <button
                   key={t.id}
                   id={t.id === 'shape' ? 'shape-tool-btn' : t.id === 'image' ? 'image-tool-btn' : t.id === 'accent' ? 'accent-tool-btn' : t.id === 'qrcode' ? 'qrcode-tool-btn' : undefined}
                   onClick={() => {
                     if (t.id === 'shape') {
                       setShowShapeMenu(prev => !prev);
                       setShowImageMenu(false);
                       setShowDividerMenu(false);
                     } else if (t.id === 'image') {
                       setShowImageMenu(prev => !prev);
                       setShowShapeMenu(false);
                       setShowDividerMenu(false);
                     } else if (t.id === 'accent') {
                       setShowDividerMenu(prev => !prev);
                       setShowShapeMenu(false);
                       setShowImageMenu(false);
                     } else {
                       setShowShapeMenu(false);
                       setShowImageMenu(false);
                       setShowDividerMenu(false);
                     }
                     setActiveTool(t.id);
                   }}
                   onDoubleClick={(evt) => {
                     evt.stopPropagation();
                     if (['shape', 'image', 'text', 'datagrid', 'accent', 'certify', 'qrcode'].includes(t.id)) {
                       const centerX = Math.round(cardDimensions.width / 2);
                       const centerY = Math.round(cardDimensions.height / 2);
                       setShowShapeMenu(false);
                       setShowImageMenu(false);
                       setShowDividerMenu(false);
                       createNewElement(t.id, centerX, centerY);
                     }
                   }}
                  title={t.label}
                  style={{
                    position: 'relative',
                    width: '32px',
                    height: '32px',
                    borderRadius: '4px',
                    border: 'none',
                    backgroundColor: isActive ? '#dfdfe3' : 'transparent',
                    color: isActive ? '#18181b' : '#a2a2a8',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'all 0.1s'
                  }}
                >
                  {t.svg}
                  {(t.id === 'shape' || t.id === 'image' || t.id === 'accent') && (
                    <div style={{
                      position: 'absolute',
                      bottom: '2px',
                      right: '2px',
                      width: 0,
                      height: 0,
                      borderStyle: 'solid',
                      borderWidth: '0 0 5px 5px',
                      borderColor: `transparent transparent ${isActive ? '#18181b' : '#a2a2a8'} transparent`
                    }} />
                  )}
                </button>
              );
            })}

            <div style={{ width: '28px', height: '1px', backgroundColor: '#3c3c42', margin: '4px 0' }} />
            <button
              id="assets-tool-btn"
              data-testid="toolbar-assets-btn"
              onClick={() => {
                setShowShapeMenu(false);
                setShowImageMenu(false);
                setShowDividerMenu(false);
                setIsChangingImageSource(false);
                setPendingImageClickCoords(null);
                setShowAssetSelectorModal(true);
              }}
              title="Asset Library & Upload (PNG, JPG, SVG)"
              style={{
                position: 'relative',
                width: '32px',
                height: '32px',
                borderRadius: '4px',
                border: 'none',
                backgroundColor: showAssetSelectorModal ? '#dfdfe3' : 'transparent',
                color: showAssetSelectorModal ? '#18181b' : '#a2a2a8',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.1s'
              }}
              onMouseEnter={(e) => {
                if (!showAssetSelectorModal) {
                  e.currentTarget.style.color = '#dfdfe3';
                  e.currentTarget.style.backgroundColor = '#38383e';
                }
              }}
              onMouseLeave={(e) => {
                if (!showAssetSelectorModal) {
                  e.currentTarget.style.color = '#a2a2a8';
                  e.currentTarget.style.backgroundColor = 'transparent';
                }
              }}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                <circle cx="8.5" cy="8.5" r="1.5" />
                <polyline points="21 15 16 10 5 21" />
              </svg>
            </button>
            {hasGeminiKey && (
              <>
                <div style={{ width: '28px', height: '1px', backgroundColor: '#3c3c42', margin: '4px 0' }} />
                <button
                  id="ai-tool-btn"
                  onClick={() => setShowAiPanel(prev => !prev)}
                  title={showAiPanel ? "Close AI Design Copilot" : "Open AI Design Copilot"}
                  style={{
                    position: 'relative',
                    width: '32px',
                    height: '32px',
                    borderRadius: '6px',
                    border: showAiPanel ? '1px solid #bdb2ff' : 'none',
                    backgroundColor: showAiPanel ? 'rgba(189, 178, 255, 0.22)' : 'transparent',
                    color: showAiPanel ? '#bdb2ff' : '#a2a2a8',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'all 0.15s',
                    boxShadow: showAiPanel ? '0 0 10px rgba(189, 178, 255, 0.35)' : 'none'
                  }}
                  onMouseEnter={e => {
                    if (!showAiPanel) {
                      e.currentTarget.style.color = '#dfdfe3';
                      e.currentTarget.style.backgroundColor = '#38383e';
                    }
                  }}
                  onMouseLeave={e => {
                    if (!showAiPanel) {
                      e.currentTarget.style.color = '#a2a2a8';
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }
                  }}
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
                  </svg>
                </button>
              </>
            )}

            {/* Nested Layout Sub-panel (visible when Select tool is active and an element is selected) */}
            {activeTool === 'select' && activeElement && (
              <div
                id="layout-flyout-menu"
                style={{
                  position: 'absolute',
                  left: '52px',
                  top: '8px',
                  backgroundColor: '#2b2b2f',
                  border: '1px solid #3c3c42',
                  borderRadius: '6px',
                  padding: '8px 8px',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '8px',
                  boxShadow: '0 8px 24px rgba(0,0,0,0.3)',
                  zIndex: 100,
                  width: '158px',
                  fontFamily: 'system-ui, -apple-system, sans-serif'
                }}
              >
                {/* Section 1: Align to Canvas */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  <span style={{ fontSize: '9px', fontWeight: 'bold', color: '#8e8e93', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    Align To Canvas
                  </span>
                  
                  {/* Grid of Align Buttons (32x32px matching sidebar tools) */}
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 32px)', gap: '6px', justifyContent: 'center' }}>
                    <button
                      onClick={() => {
                        saveHistoryState();
                        updateActiveElement({ x: 0 });
                      }}
                      title="Align Left"
                      style={{
                        backgroundColor: '#1b1b1d',
                        border: '1px solid #3c3c42',
                        borderRadius: '4px',
                        color: '#dfdfe3',
                        width: '32px',
                        height: '32px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer'
                      }}
                    >
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="3" y1="3" x2="3" y2="21" />
                        <rect x="9" y="5" width="12" height="6" rx="1" />
                        <rect x="9" y="13" width="8" height="6" rx="1" />
                      </svg>
                    </button>

                    <button
                      onClick={() => {
                        saveHistoryState();
                        const bw = Number(globalStyles.borderWidth) || 0;
                        updateActiveElement({ x: Math.round((cardDimensions.width - 2 * bw - activeElement.width) / 2) });
                      }}
                      title="Align Horiz. Center"
                      style={{
                        backgroundColor: '#1b1b1d',
                        border: '1px solid #3c3c42',
                        borderRadius: '4px',
                        color: '#dfdfe3',
                        width: '32px',
                        height: '32px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer'
                      }}
                    >
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="12" y1="3" x2="12" y2="21" />
                        <rect x="6" y="5" width="12" height="6" rx="1" />
                        <rect x="8" y="13" width="8" height="6" rx="1" />
                      </svg>
                    </button>

                    <button
                      onClick={() => {
                        saveHistoryState();
                        const bw = Number(globalStyles.borderWidth) || 0;
                        updateActiveElement({ x: Math.round(cardDimensions.width - 2 * bw - activeElement.width) });
                      }}
                      title="Align Right"
                      style={{
                        backgroundColor: '#1b1b1d',
                        border: '1px solid #3c3c42',
                        borderRadius: '4px',
                        color: '#dfdfe3',
                        width: '32px',
                        height: '32px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer'
                      }}
                    >
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="21" y1="3" x2="21" y2="21" />
                        <rect x="3" y="5" width="12" height="6" rx="1" />
                        <rect x="7" y="13" width="8" height="6" rx="1" />
                      </svg>
                    </button>

                    <button
                      onClick={() => {
                        saveHistoryState();
                        updateActiveElement({ y: 0 });
                      }}
                      title="Align Top"
                      style={{
                        backgroundColor: '#1b1b1d',
                        border: '1px solid #3c3c42',
                        borderRadius: '4px',
                        color: '#dfdfe3',
                        width: '32px',
                        height: '32px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer'
                      }}
                    >
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="3" y1="3" x2="21" y2="3" />
                        <rect x="5" y="9" width="6" height="12" rx="1" />
                        <rect x="13" y="9" width="6" height="8" rx="1" />
                      </svg>
                    </button>

                    <button
                      onClick={() => {
                        saveHistoryState();
                        const bw = Number(globalStyles.borderWidth) || 0;
                        updateActiveElement({ y: Math.round((cardDimensions.height - 2 * bw - activeElement.height) / 2) });
                      }}
                      title="Align Vert. Center"
                      style={{
                        backgroundColor: '#1b1b1d',
                        border: '1px solid #3c3c42',
                        borderRadius: '4px',
                        color: '#dfdfe3',
                        width: '32px',
                        height: '32px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer'
                      }}
                    >
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="3" y1="12" x2="21" y2="12" />
                        <rect x="5" y="6" width="6" height="12" rx="1" />
                        <rect x="13" y="8" width="6" height="8" rx="1" />
                      </svg>
                    </button>

                    <button
                      onClick={() => {
                        saveHistoryState();
                        const bw = Number(globalStyles.borderWidth) || 0;
                        updateActiveElement({ y: Math.round(cardDimensions.height - 2 * bw - activeElement.height) });
                      }}
                      title="Align Bottom"
                      style={{
                        backgroundColor: '#1b1b1d',
                        border: '1px solid #3c3c42',
                        borderRadius: '4px',
                        color: '#dfdfe3',
                        width: '32px',
                        height: '32px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer'
                      }}
                    >
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="3" y1="21" x2="21" y2="21" />
                        <rect x="5" y="3" width="6" height="12" rx="1" />
                        <rect x="13" y="7" width="6" height="8" rx="1" />
                      </svg>
                    </button>
                  </div>
                </div>

                {/* Section 2: Geometry Stretch Tools */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', borderTop: '1px solid #3c3c42', paddingTop: '6px' }}>
                  <span style={{ fontSize: '9px', fontWeight: 'bold', color: '#8e8e93', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    Geometry
                  </span>
                  <div style={{ display: 'flex', gap: '6px' }}>
                    <button
                      onClick={() => {
                        saveHistoryState();
                        const bw = Number(globalStyles.borderWidth) || 0;
                        const targetW = Math.max(10, cardDimensions.width - 2 * bw);
                        if (activeElement.lockAspect) {
                          const ratio = activeElement.aspectRatio || (activeElement.width / Math.max(1, activeElement.height));
                          const targetH = Math.max(10, Math.round(targetW / ratio));
                          updateActiveElement({ 
                            x: 0, 
                            width: targetW,
                            height: targetH
                          });
                        } else {
                          updateActiveElement({ 
                            x: 0, 
                            width: targetW 
                          });
                        }
                      }}
                      title="Fit Element to Canvas Width"
                      style={{
                        flex: 1,
                        backgroundColor: '#1b1b1d',
                        border: '1px solid #3c3c42',
                        borderRadius: '3px',
                        color: '#dfdfe3',
                        height: '24px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer',
                        fontSize: '9px',
                        fontWeight: 'bold',
                        gap: '3px'
                      }}
                    >
                      <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="5 9 2 12 5 15" />
                        <polyline points="19 9 22 12 19 15" />
                        <line x1="2" y1="12" x2="22" y2="12" />
                      </svg>
                      Width
                    </button>
                    
                    <button
                      onClick={() => {
                        saveHistoryState();
                        const bw = Number(globalStyles.borderWidth) || 0;
                        const targetH = Math.max(10, cardDimensions.height - 2 * bw);
                        if (activeElement.lockAspect) {
                          const ratio = activeElement.aspectRatio || (activeElement.width / Math.max(1, activeElement.height));
                          const targetW = Math.max(10, Math.round(targetH * ratio));
                          updateActiveElement({ 
                            y: 0, 
                            width: targetW,
                            height: targetH
                          });
                        } else {
                          updateActiveElement({ 
                            y: 0, 
                            height: targetH 
                          });
                        }
                      }}
                      title="Fit Element to Canvas Height"
                      style={{
                        flex: 1,
                        backgroundColor: '#1b1b1d',
                        border: '1px solid #3c3c42',
                        borderRadius: '3px',
                        color: '#dfdfe3',
                        height: '24px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer',
                        fontSize: '9px',
                        fontWeight: 'bold',
                        gap: '3px'
                      }}
                    >
                      <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="9 5 12 2 15 5" />
                        <polyline points="9 19 12 22 15 19" />
                        <line x1="12" y1="2" x2="12" y2="22" />
                      </svg>
                      Height
                    </button>
                  </div>
                </div>

                {/* Section 3: Transform Coordinate Inputs */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', borderTop: '1px solid #3c3c42', paddingTop: '6px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontSize: '9px', fontWeight: 'bold', color: '#8e8e93', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                      Transform
                    </span>
                    {activeElement.lockAspect && (
                      <span style={{ fontSize: '8px', color: '#60a5fa', fontWeight: '600', backgroundColor: 'rgba(96, 165, 250, 0.1)', padding: '1px 4px', borderRadius: '2px', border: '1px solid rgba(96, 165, 250, 0.25)' }}>
                        🔒 {Math.round((activeElement.aspectRatio || (activeElement.width / Math.max(1, activeElement.height))) * 100) / 100}:1
                      </span>
                    )}
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '6px 6px' }}>
                    {/* X Coordinate */}
                    <div style={{ display: 'flex', gap: '3px', alignItems: 'center' }}>
                      <span style={{ fontSize: '9px', color: '#8e8e93', width: '8px' }}>X</span>
                      <input
                        type="number"
                        data-testid="transform-x-input"
                        value={activeElement.x}
                        onChange={(e) => updateActiveElement({ x: parseInt(e.target.value) || 0 })}
                        style={{ width: '100%', backgroundColor: '#1b1b1d', border: '1px solid #3c3c42', borderRadius: '4px', padding: '2px 3px', color: '#fff', fontSize: '11px', outline: 'none', textAlign: 'center', boxSizing: 'border-box' }}
                      />
                    </div>
                    {/* Y Coordinate */}
                    <div style={{ display: 'flex', gap: '3px', alignItems: 'center' }}>
                      <span style={{ fontSize: '9px', color: '#8e8e93', width: '8px' }}>Y</span>
                      <input
                        type="number"
                        data-testid="transform-y-input"
                        value={activeElement.y}
                        onChange={(e) => updateActiveElement({ y: parseInt(e.target.value) || 0 })}
                        style={{ width: '100%', backgroundColor: '#1b1b1d', border: '1px solid #3c3c42', borderRadius: '4px', padding: '2px 3px', color: '#fff', fontSize: '11px', outline: 'none', textAlign: 'center', boxSizing: 'border-box' }}
                      />
                    </div>

                    {/* Width, Lock Aspect Graphic, Height */}
                    <div style={{ display: 'flex', gap: '3px', alignItems: 'center', gridColumn: 'span 2' }}>
                      {/* Width Coordinate */}
                      <div style={{ display: 'flex', gap: '3px', alignItems: 'center', flex: 1, minWidth: 0 }}>
                        <span style={{ fontSize: '9px', color: '#8e8e93', width: '8px' }}>W</span>
                        <input
                          type="number"
                          data-testid="transform-width-input"
                          value={activeElement.width}
                          onChange={(e) => {
                            const val = parseInt(e.target.value) || 0;
                            if (activeElement.lockAspect) {
                              const ratio = activeElement.aspectRatio || (activeElement.width / Math.max(1, activeElement.height));
                              const newH = Math.max(1, Math.round(val / ratio));
                              updateActiveElement({ width: val, height: newH });
                            } else {
                              updateActiveElement({ width: val });
                            }
                          }}
                          style={{ width: '100%', backgroundColor: '#1b1b1d', border: '1px solid #3c3c42', borderRadius: '4px', padding: '2px 3px', color: '#fff', fontSize: '11px', outline: 'none', textAlign: 'center', boxSizing: 'border-box' }}
                        />
                      </div>

                      {/* Lock Aspect Graphic Button between W & H */}
                      <button
                        type="button"
                        data-testid="transform-lock-aspect-btn"
                        onClick={() => {
                          saveHistoryState();
                          const currentLocked = Boolean(activeElement.lockAspect);
                          const nextLocked = !currentLocked;
                          const ratio = (activeElement.width && activeElement.height)
                            ? (activeElement.width / activeElement.height)
                            : (activeElement.aspectRatio || 1);
                          updateActiveElement({
                            lockAspect: nextLocked,
                            aspectRatio: ratio
                          });
                        }}
                        title={activeElement.lockAspect 
                          ? `Aspect ratio locked to ${Math.round((activeElement.aspectRatio || (activeElement.width / Math.max(1, activeElement.height))) * 100) / 100}:1 (Click to unlock)` 
                          : "Aspect ratio unlocked (Click to lock to current aspect ratio)"
                        }
                        style={{
                          width: '18px',
                          height: '20px',
                          borderRadius: '3px',
                          border: activeElement.lockAspect ? '1px solid #1677ff' : '1px solid #3c3c42',
                          backgroundColor: activeElement.lockAspect ? '#1677ff' : '#232326',
                          color: activeElement.lockAspect ? '#ffffff' : '#8e8e93',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          padding: 0,
                          flexShrink: 0,
                          transition: 'all 0.15s'
                        }}
                        onMouseEnter={(e) => {
                          if (!activeElement.lockAspect) {
                            e.currentTarget.style.backgroundColor = '#38383e';
                            e.currentTarget.style.color = '#dfdfe3';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (!activeElement.lockAspect) {
                            e.currentTarget.style.backgroundColor = '#232326';
                            e.currentTarget.style.color = '#8e8e93';
                          }
                        }}
                      >
                        {activeElement.lockAspect ? (
                          <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ display: 'block' }}>
                            <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                            <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                          </svg>
                        ) : (
                          <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ display: 'block' }}>
                            <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                            <path d="M7 11V7a5 5 0 0 1 9.9-1" />
                          </svg>
                        )}
                      </button>

                      {/* Height Coordinate */}
                      <div style={{ display: 'flex', gap: '3px', alignItems: 'center', flex: 1, minWidth: 0 }}>
                        <span style={{ fontSize: '9px', color: '#8e8e93', width: '8px' }}>H</span>
                        <input
                          type="number"
                          data-testid="transform-height-input"
                          value={activeElement.height}
                          onChange={(e) => {
                            const val = parseInt(e.target.value) || 0;
                            if (activeElement.lockAspect) {
                              const ratio = activeElement.aspectRatio || (activeElement.width / Math.max(1, activeElement.height));
                              const newW = Math.max(1, Math.round(val * ratio));
                              updateActiveElement({ height: val, width: newW });
                            } else {
                              updateActiveElement({ height: val });
                            }
                          }}
                          style={{ width: '100%', backgroundColor: '#1b1b1d', border: '1px solid #3c3c42', borderRadius: '4px', padding: '2px 3px', color: '#fff', fontSize: '11px', outline: 'none', textAlign: 'center', boxSizing: 'border-box' }}
                        />
                      </div>
                    </div>

                    {/* Rotation Coordinate */}
                    <div style={{ display: 'flex', gap: '4px', alignItems: 'center', gridColumn: 'span 2' }}>
                      <span style={{ fontSize: '9px', color: '#8e8e93', width: '8px' }}>R</span>
                      <input
                        type="number"
                        min="-360"
                        max="360"
                        data-testid="transform-rotation-input"
                        value={activeElement.rotation || 0}
                        onChange={(e) => updateActiveElement({ rotation: parseInt(e.target.value) || 0 })}
                        style={{ flex: 1, backgroundColor: '#1b1b1d', border: '1px solid #3c3c42', borderRadius: '4px', padding: '2px 4px', color: '#fff', fontSize: '11px', outline: 'none', textAlign: 'center', boxSizing: 'border-box' }}
                        title="Rotation angle in degrees (0° = Horizontal)"
                      />
                      <span style={{ fontSize: '10px', color: '#8e8e93' }}>deg</span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Nested Shapes Flyout Menu */}
            {showShapeMenu && (
              <div
                id="shape-flyout-menu"
                style={{
                  position: 'absolute',
                  left: '52px',
                  top: '46px',
                  backgroundColor: '#2b2b2f',
                  border: '1px solid #3c3c42',
                  borderRadius: '6px',
                  padding: '4px 0',
                  display: 'flex',
                  flexDirection: 'column',
                  boxShadow: '0 8px 24px rgba(0,0,0,0.3)',
                  zIndex: 100,
                  width: '180px'
                }}
              >
                {SHAPES_LIST.map(sh => (
                  <button
                    key={sh.id}
                    onClick={() => {
                      setSelectedShapeSubtype(sh.id);
                      setActiveTool('shape');
                      setShowShapeMenu(false);
                    }}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px',
                      padding: '8px 12px',
                      border: 'none',
                      backgroundColor: selectedShapeSubtype === sh.id ? '#dfdfe3' : 'transparent',
                      color: selectedShapeSubtype === sh.id ? '#18181b' : '#dfdfe3',
                      fontSize: '11px',
                      textAlign: 'left',
                      cursor: 'pointer',
                      width: '100%',
                      transition: 'background-color 0.1s'
                    }}
                  >
                    <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '16px', height: '16px' }}>
                      {sh.svg}
                    </span>
                    <span>{sh.label}</span>
                  </button>
                ))}
              </div>
            )}

            {/* Nested Image Tool Flyout Menu */}
            {showImageMenu && (
              <div
                id="image-flyout-menu"
                style={{
                  position: 'absolute',
                  left: '52px',
                  top: '80px',
                  backgroundColor: '#2b2b2f',
                  border: '1px solid #3c3c42',
                  borderRadius: '6px',
                  padding: '4px 0',
                  display: 'flex',
                  flexDirection: 'column',
                  boxShadow: '0 8px 24px rgba(0,0,0,0.3)',
                  zIndex: 100,
                  width: '180px'
                }}
              >
                <button
                  onClick={() => {
                    setSelectedImageSubTool('frame');
                    setActiveTool('image');
                    setShowImageMenu(false);
                  }}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    padding: '8px 12px',
                    border: 'none',
                    backgroundColor: selectedImageSubTool === 'frame' ? '#dfdfe3' : 'transparent',
                    color: selectedImageSubTool === 'frame' ? '#18181b' : '#dfdfe3',
                    fontSize: '11px',
                    textAlign: 'left',
                    cursor: 'pointer',
                    width: '100%',
                    transition: 'background-color 0.1s'
                  }}
                >
                  <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '16px', height: '16px' }}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                      <circle cx="9" cy="9" r="2" />
                      <path d="M21 15l-3.086-3.086a2 2 0 00-2.828 0L6 21" />
                    </svg>
                  </span>
                  <span>Picture Frame</span>
                </button>
 
                <button
                  onClick={() => {
                    setSelectedImageSubTool('image');
                    setActiveTool('image');
                    setShowImageMenu(false);
                  }}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    padding: '8px 12px',
                    border: 'none',
                    backgroundColor: selectedImageSubTool === 'image' ? '#dfdfe3' : 'transparent',
                    color: selectedImageSubTool === 'image' ? '#18181b' : '#dfdfe3',
                    fontSize: '11px',
                    textAlign: 'left',
                    cursor: 'pointer',
                    width: '100%',
                    transition: 'background-color 0.1s'
                  }}
                >
                  <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '16px', height: '16px' }}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="2" y="3" width="20" height="14" rx="2" ry="2" />
                      <line x1="2" y1="20" x2="22" y2="20" />
                      <polyline points="12 13 17 8 22 13" />
                      <polyline points="2 13 7 8 13 13" />
                    </svg>
                  </span>
                  <span>Image Element</span>
                </button>
              </div>
            )}

            {/* Nested Divider Ornament Flyout Menu */}
            {showDividerMenu && (
              <div
                id="accent-flyout-menu"
                style={{
                  position: 'absolute',
                  left: '52px',
                  top: '198px',
                  backgroundColor: '#2b2b2f',
                  border: '1px solid #3c3c42',
                  borderRadius: '6px',
                  padding: '4px 0',
                  display: 'flex',
                  flexDirection: 'column',
                  boxShadow: '0 8px 24px rgba(0,0,0,0.3)',
                  zIndex: 100,
                  width: '180px'
                }}
              >
                <button
                  onClick={() => {
                    setSelectedDividerSubTool('horizontal');
                    setActiveTool('accent');
                    setShowDividerMenu(false);
                  }}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    padding: '8px 12px',
                    border: 'none',
                    backgroundColor: selectedDividerSubTool === 'horizontal' ? '#dfdfe3' : 'transparent',
                    color: selectedDividerSubTool === 'horizontal' ? '#18181b' : '#dfdfe3',
                    fontSize: '11px',
                    textAlign: 'left',
                    cursor: 'pointer',
                    width: '100%',
                    transition: 'background-color 0.1s'
                  }}
                >
                  <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '16px', height: '16px' }}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="3" y1="12" x2="21" y2="12" />
                      <circle cx="12" cy="12" r="4.5" />
                    </svg>
                  </span>
                  <span>Horizontal Divider</span>
                </button>

                <button
                  onClick={() => {
                    setSelectedDividerSubTool('vertical');
                    setActiveTool('accent');
                    setShowDividerMenu(false);
                  }}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    padding: '8px 12px',
                    border: 'none',
                    backgroundColor: selectedDividerSubTool === 'vertical' ? '#dfdfe3' : 'transparent',
                    color: selectedDividerSubTool === 'vertical' ? '#18181b' : '#dfdfe3',
                    fontSize: '11px',
                    textAlign: 'left',
                    cursor: 'pointer',
                    width: '100%',
                    transition: 'background-color 0.1s'
                  }}
                >
                  <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '16px', height: '16px' }}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ transform: 'rotate(90deg)' }}>
                      <line x1="3" y1="12" x2="21" y2="12" />
                      <circle cx="12" cy="12" r="4.5" />
                    </svg>
                  </span>
                  <span>Vertical Divider</span>
                </button>
              </div>
            )}
          </div>

          {/* AI DESIGN COPILOT CHAT PANEL (docked directly to the right of the toolbox) */}
          {showAiPanel && hasGeminiKey && (
            <div style={{
              height: '100%',
              maxHeight: '100%',
              minHeight: 0,
              minWidth: 0,
              overflow: 'hidden',
              display: 'flex'
            }}>
              <AiDesignerChatPanel
                mode="card"
                currentState={{
                  cardDimensions,
                  globalStyles,
                  sides,
                  fieldMappings,
                  activeSide,
                  selectedElement: activeElement
                }}
                availableAssets={uploadedAssets}
                onApplyMutations={handleApplyAiCardMutations}
                onClose={() => setShowAiPanel(false)}
                tenantConfig={tenantConfig}
                user={user}
                token={token}
              />
            </div>
          )}

          {/* CENTER STAGE CANVAS VIEWPORT */}
          <div 
            onClick={(e) => {
              if (e.target === e.currentTarget) {
                setSelectedElementId(null);
              }
            }}
            style={{
              flex: 1,
              backgroundColor: '#141416',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              position: 'relative',
              overflow: 'auto',
              padding: '30px'
            }}
          >
            {/* White Canvas sheet */}
            <div
              id="catch-card-canvas"
              onClick={handleCanvasClick}
              style={{
                width: `${cardDimensions.width}px`,
                height: `${cardDimensions.height}px`,
                borderRadius: globalStyles.borderRadius,
                border: `${globalStyles.borderWidth}px ${globalStyles.borderStyle} ${globalStyles.borderColor}`,
                backgroundColor: sides[activeSide]?.background?.type === 'pattern'
                  ? (sides[activeSide].background.pattern?.tintColor || '#ffffff')
                  : '#ffffff',
                background: sides[activeSide]?.background?.type === 'pattern'
                  ? (sides[activeSide].background.value || '#ffffff')
                  : (sides[activeSide]?.background?.value || '#ffffff'),
                position: 'relative',
                overflow: 'hidden',
                boxShadow: isPrintExporting ? 'none' : '0 20px 50px rgba(0, 0, 0, 0.4)',
                transform: isPrintExporting ? 'none' : `scale(${zoomLevel})`,
                transition: isPrintExporting ? 'none' : 'transform 0.15s ease',
                transformOrigin: 'center',
                boxSizing: 'border-box'
              }}
            >
              {sides[activeSide]?.background?.type === 'pattern' && sides[activeSide]?.background?.pattern && (
                <div style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  borderRadius: 'inherit',
                  pointerEvents: 'none',
                  opacity: sides[activeSide].background.pattern.opacity !== undefined ? (sides[activeSide].background.pattern.opacity > 1 ? sides[activeSide].background.pattern.opacity / 100 : sides[activeSide].background.pattern.opacity) : 1,
                  backgroundImage: `url("${sides[activeSide].background.pattern.image}")`,
                  backgroundRepeat: sides[activeSide].background.pattern.repeat || 'repeat',
                  backgroundSize: `${Math.max(2, Math.round((sides[activeSide].background.pattern.tileWidth || 32) * ((sides[activeSide].background.pattern.scale !== undefined ? sides[activeSide].background.pattern.scale : 100) / 100)))}px ${Math.max(2, Math.round((sides[activeSide].background.pattern.tileHeight || 32) * ((sides[activeSide].background.pattern.scale !== undefined ? sides[activeSide].background.pattern.scale : 100) / 100)))}px`,
                  backgroundPosition: '0 0',
                  mixBlendMode: sides[activeSide].background.pattern.blendMode || 'normal',
                  zIndex: 0
                }} />
              )}
              {sides[activeSide].elements
                .filter(e => {
                  if (e.visible === false) return false;
                  if (e.parentId) {
                    const parent = sides[activeSide].elements.find(el => el.id === e.parentId);
                    if (parent && parent.visible === false) return false;
                  }
                  return true;
                })
                .map(elem => {
                  if (elem.type === 'group') return null;
                  const isSelected = selectedElementId === elem.id;
                  return (
                    <div
                      key={elem.id}
                      onPointerDown={(e) => handlePointerDown(e, elem, 'drag')}
                      onDoubleClick={(e) => {
                        e.stopPropagation();
                        if (elem.type === 'image' && getResolvedImageSrc(elem)) {
                          openImageAdjustModal(elem);
                        }
                      }}
                      style={{
                        position: 'absolute',
                        left: `${elem.x}px`,
                        top: `${elem.y}px`,
                        width: `${elem.width}px`,
                        height: `${elem.height}px`,
                        boxSizing: 'border-box',
                        cursor: elem.locked ? 'default' : 'move',
                        border: 'none',
                        zIndex: sides[activeSide].elements.indexOf(elem),
                        opacity: (() => {
                          let opacity = elem.opacity !== undefined ? elem.opacity / 100 : 1;
                          if (elem.parentId) {
                            const parent = sides[activeSide].elements.find(el => el.id === elem.parentId);
                            if (parent && parent.opacity !== undefined) {
                              opacity *= (parent.opacity / 100);
                            }
                          }
                          return opacity;
                        })(),
                        mixBlendMode: elem.blendMode || 'normal',
                        transform: elem.rotation ? `rotate(${elem.rotation}deg)` : 'none',
                        transformOrigin: 'center center',
                        ...getElementEffectsStyle(elem)
                      }}
                    >
                       {elem.type === 'image' && (() => {
                        const { rOuter, rGap, rPrimaryOuter, rInner } = calculateFrameBorderRadii(elem);
                        return (
                          <div style={{
                            width: '100%',
                            height: '100%',
                            boxSizing: 'border-box',
                            borderRadius: `${rOuter}px`,
                            overflow: 'hidden',
                            
                            // 1. Secondary Border styling (solid, dashed, dotted)
                            border: (elem.hasBorder !== false && elem.hasSecondBorder && elem.borderFillType2 !== 'gradient')
                              ? `${elem.strokeWidth2 || 2}px ${elem.strokeStyle2 || 'solid'} ${elem.strokeColor2 || '#000000'}`
                              : 'none',
                            
                            // 2. Gap/Border padding
                            padding: (elem.hasBorder !== false && elem.hasSecondBorder)
                              ? (elem.borderFillType2 === 'gradient' ? `${elem.strokeWidth2 || 2}px` : `${elem.borderGap || 0}px`)
                              : 0,
                            background: (elem.hasBorder !== false && elem.hasSecondBorder && elem.borderFillType2 === 'gradient')
                              ? elem.strokeColor2 || 'linear-gradient(to right, red, blue)'
                              : 'transparent'
                          }}>
                            {/* Intermediate gap wrapper to support secondary gradient border gap */}
                            <div style={{
                              width: '100%',
                              height: '100%',
                              boxSizing: 'border-box',
                              borderRadius: `${rGap}px`,
                              padding: (elem.hasBorder !== false && elem.hasSecondBorder && elem.borderFillType2 === 'gradient')
                                ? `${elem.borderGap || 0}px`
                                : 0,
                              background: (elem.hasBorder !== false && elem.hasSecondBorder && elem.borderFillType2 === 'gradient')
                                ? (sides[activeSide].background.value || '#ffffff')
                                : 'transparent',
                              display: (elem.hasBorder !== false && elem.hasSecondBorder && elem.borderFillType2 === 'gradient')
                                ? 'flex'
                                : 'block',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}>
                              {/* Inner element wrapper containing primary border */}
                              <div style={{
                                width: '100%',
                                height: '100%',
                                boxSizing: 'border-box',
                                borderRadius: `${rPrimaryOuter}px`,
                                overflow: 'hidden',
                                flexShrink: 0,
                                
                                // 3. Primary Border styling
                                border: (elem.hasBorder !== false && (elem.strokeWidth !== undefined ? elem.strokeWidth : 1) > 0 && elem.borderFillType !== 'gradient')
                                  ? `${elem.strokeWidth !== undefined ? elem.strokeWidth : 1}px ${elem.strokeStyle || 'solid'} ${elem.strokeColor || '#ffffff'}`
                                  : 'none',
                                
                                // 4. Primary Border Gradient padding
                                padding: (elem.hasBorder !== false && (elem.strokeWidth !== undefined ? elem.strokeWidth : 1) > 0 && elem.borderFillType === 'gradient')
                                  ? `${elem.strokeWidth !== undefined ? elem.strokeWidth : 1}px`
                                  : 0,
                                background: (elem.hasBorder !== false && (elem.strokeWidth !== undefined ? elem.strokeWidth : 1) > 0 && elem.borderFillType === 'gradient')
                                  ? elem.strokeColor || 'linear-gradient(to right, red, blue)'
                                  : (elem.fill || 'transparent'),
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                              }}>
                                {/* Inner content box for image clipping */}
                                <div style={{ position: 'relative', width: '100%', height: '100%', borderRadius: `${rInner}px`, overflow: 'hidden' }}>
                                  {getResolvedImageSrc(elem) ? (
                                    elem.hasBorder !== false && elem.borderFillType === 'gradient' ? (
                                      <div style={{ width: '100%', height: '100%', borderRadius: `${rInner}px`, overflow: 'hidden', position: 'relative', zIndex: 1 }}>
                                        <PictureFrameImage
                                          elem={elem}
                                          resolvedSrc={getResolvedImageSrc(elem)}
                                          filterStyle={getImageFilterStyle(elem.effect)}
                                        />
                                      </div>
                                    ) : (
                                      <PictureFrameImage
                                        elem={elem}
                                        resolvedSrc={getResolvedImageSrc(elem)}
                                        filterStyle={getImageFilterStyle(elem.effect)}
                                      />
                                    )
                                  ) : (
                                    <div style={{
                                      width: '100%',
                                      height: '100%',
                                      backgroundColor: '#262629', display: 'flex',
                                      flexDirection: 'column',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      color: 'var(--text-mute)',
                                      gap: '6px',
                                      position: 'relative',
                                      zIndex: 1
                                    }}>
                                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ opacity: 0.7 }}>
                                        <circle cx="12" cy="12" r="10" />
                                        <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
                                        <line x1="12" y1="17" x2="12.01" y2="17" />
                                      </svg>
                                      <span style={{ fontSize: '9px', opacity: 0.6 }}>No Source</span>
                                    </div>
                                  )}
                                  {/* Overlay element for gradient/sheen overlays */}
                                  {getImageOverlayStyle(elem.effect) && (
                                    <div style={{
                                      position: 'absolute',
                                      top: 0, left: 0, right: 0, bottom: 0,
                                      pointerEvents: 'none',
                                      borderRadius: `${rInner}px`,
                                      ...getImageOverlayStyle(elem.effect), zIndex: 5
                                    }} />
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })()}

                      {elem.type === 'shape' && (
                        elem.shapeType === 'triangle' ? (
                          <svg width="100%" height="100%" viewBox="0 0 100 100" preserveAspectRatio="none">
                            {elem.fillType === 'pattern' && elem.pattern && (
                              <defs>
                                <pattern
                                  id={`pat-${elem.id}`}
                                  patternUnits="userSpaceOnUse"
                                  width={Math.max(2, Math.round((elem.pattern.tileWidth || 32) * ((elem.pattern.scale !== undefined ? elem.pattern.scale : 100) / 100)))}
                                  height={Math.max(2, Math.round((elem.pattern.tileHeight || 32) * ((elem.pattern.scale !== undefined ? elem.pattern.scale : 100) / 100)))}
                                >
                                  {elem.pattern.tintColor && elem.pattern.tintColor !== 'transparent' && (
                                    <rect width="100%" height="100%" fill={elem.pattern.tintColor} />
                                  )}
                                  <image
                                    href={elem.pattern.image}
                                    width={Math.max(2, Math.round((elem.pattern.tileWidth || 32) * ((elem.pattern.scale !== undefined ? elem.pattern.scale : 100) / 100)))}
                                    height={Math.max(2, Math.round((elem.pattern.tileHeight || 32) * ((elem.pattern.scale !== undefined ? elem.pattern.scale : 100) / 100)))}
                                    opacity={elem.pattern.opacity !== undefined ? (elem.pattern.opacity > 1 ? elem.pattern.opacity / 100 : elem.pattern.opacity) : 1}
                                  />
                                </pattern>
                              </defs>
                            )}
                            <polygon points="50,0 0,100 100,100" fill={elem.fillType === 'pattern' && elem.pattern ? `url(#pat-${elem.id})` : (elem.fill || '#e6e6e6')} />
                          </svg>
                        ) : elem.shapeType === 'diamond' ? (
                          <svg width="100%" height="100%" viewBox="0 0 100 100" preserveAspectRatio="none">
                            {elem.fillType === 'pattern' && elem.pattern && (
                              <defs>
                                <pattern
                                  id={`pat-${elem.id}`}
                                  patternUnits="userSpaceOnUse"
                                  width={Math.max(2, Math.round((elem.pattern.tileWidth || 32) * ((elem.pattern.scale !== undefined ? elem.pattern.scale : 100) / 100)))}
                                  height={Math.max(2, Math.round((elem.pattern.tileHeight || 32) * ((elem.pattern.scale !== undefined ? elem.pattern.scale : 100) / 100)))}
                                >
                                  {elem.pattern.tintColor && elem.pattern.tintColor !== 'transparent' && (
                                    <rect width="100%" height="100%" fill={elem.pattern.tintColor} />
                                  )}
                                  <image
                                    href={elem.pattern.image}
                                    width={Math.max(2, Math.round((elem.pattern.tileWidth || 32) * ((elem.pattern.scale !== undefined ? elem.pattern.scale : 100) / 100)))}
                                    height={Math.max(2, Math.round((elem.pattern.tileHeight || 32) * ((elem.pattern.scale !== undefined ? elem.pattern.scale : 100) / 100)))}
                                    opacity={elem.pattern.opacity !== undefined ? (elem.pattern.opacity > 1 ? elem.pattern.opacity / 100 : elem.pattern.opacity) : 1}
                                  />
                                </pattern>
                              </defs>
                            )}
                            <polygon points="50,0 100,50 50,100 0,50" fill={elem.fillType === 'pattern' && elem.pattern ? `url(#pat-${elem.id})` : (elem.fill || '#e6e6e6')} />
                          </svg>
                        ) : elem.shapeType === 'polygon' ? (
                          <svg width="100%" height="100%" viewBox="0 0 100 100" preserveAspectRatio="none">
                            {elem.fillType === 'pattern' && elem.pattern && (
                              <defs>
                                <pattern
                                  id={`pat-${elem.id}`}
                                  patternUnits="userSpaceOnUse"
                                  width={Math.max(2, Math.round((elem.pattern.tileWidth || 32) * ((elem.pattern.scale !== undefined ? elem.pattern.scale : 100) / 100)))}
                                  height={Math.max(2, Math.round((elem.pattern.tileHeight || 32) * ((elem.pattern.scale !== undefined ? elem.pattern.scale : 100) / 100)))}
                                >
                                  {elem.pattern.tintColor && elem.pattern.tintColor !== 'transparent' && (
                                    <rect width="100%" height="100%" fill={elem.pattern.tintColor} />
                                  )}
                                  <image
                                    href={elem.pattern.image}
                                    width={Math.max(2, Math.round((elem.pattern.tileWidth || 32) * ((elem.pattern.scale !== undefined ? elem.pattern.scale : 100) / 100)))}
                                    height={Math.max(2, Math.round((elem.pattern.tileHeight || 32) * ((elem.pattern.scale !== undefined ? elem.pattern.scale : 100) / 100)))}
                                    opacity={elem.pattern.opacity !== undefined ? (elem.pattern.opacity > 1 ? elem.pattern.opacity / 100 : elem.pattern.opacity) : 1}
                                  />
                                </pattern>
                              </defs>
                            )}
                            <polygon points="25,0 75,0 100,50 75,100 25,100 0,50" fill={elem.fillType === 'pattern' && elem.pattern ? `url(#pat-${elem.id})` : (elem.fill || '#e6e6e6')} />
                          </svg>
                        ) : elem.shapeType === 'star' ? (
                          <svg width="100%" height="100%" viewBox="0 0 100 100" preserveAspectRatio="none">
                            {elem.fillType === 'pattern' && elem.pattern && (
                              <defs>
                                <pattern
                                  id={`pat-${elem.id}`}
                                  patternUnits="userSpaceOnUse"
                                  width={Math.max(2, Math.round((elem.pattern.tileWidth || 32) * ((elem.pattern.scale !== undefined ? elem.pattern.scale : 100) / 100)))}
                                  height={Math.max(2, Math.round((elem.pattern.tileHeight || 32) * ((elem.pattern.scale !== undefined ? elem.pattern.scale : 100) / 100)))}
                                >
                                  {elem.pattern.tintColor && elem.pattern.tintColor !== 'transparent' && (
                                    <rect width="100%" height="100%" fill={elem.pattern.tintColor} />
                                  )}
                                  <image
                                    href={elem.pattern.image}
                                    width={Math.max(2, Math.round((elem.pattern.tileWidth || 32) * ((elem.pattern.scale !== undefined ? elem.pattern.scale : 100) / 100)))}
                                    height={Math.max(2, Math.round((elem.pattern.tileHeight || 32) * ((elem.pattern.scale !== undefined ? elem.pattern.scale : 100) / 100)))}
                                    opacity={elem.pattern.opacity !== undefined ? (elem.pattern.opacity > 1 ? elem.pattern.opacity / 100 : elem.pattern.opacity) : 1}
                                  />
                                </pattern>
                              </defs>
                            )}
                            <polygon points="50,0 61,35 98,35 68,57 79,91 50,70 21,91 32,57 2,35 39,35" fill={elem.fillType === 'pattern' && elem.pattern ? `url(#pat-${elem.id})` : (elem.fill || '#e6e6e6')} />
                          </svg>
                        ) : (
                          // Rectangle, Circle, Pill with border styling
                          <div style={{
                            width: '100%',
                            height: '100%',
                            boxSizing: 'border-box',
                            borderRadius: elem.shapeType === 'circle' ? '50%' : elem.shapeType === 'pill' ? '999px' : `${elem.hasBorder !== false ? (elem.borderRadius !== undefined ? elem.borderRadius : 0) : 0}px`,
                            overflow: 'hidden',
                            
                            // 1. Secondary Border styling (Dotted, dashed, solid/line)
                            border: (elem.hasBorder !== false && elem.hasSecondBorder && elem.borderFillType2 !== 'gradient')
                              ? `${elem.strokeWidth2 || 2}px ${elem.strokeStyle2 || 'solid'} ${elem.strokeColor2 || '#000000'}`
                              : 'none',
                            
                            // 2. Gap/Border padding
                            padding: (elem.hasBorder !== false && elem.hasSecondBorder)
                              ? (elem.borderFillType2 === 'gradient' ? `${elem.strokeWidth2 || 2}px` : `${elem.borderGap || 0}px`)
                              : 0,
                            background: (elem.hasBorder !== false && elem.hasSecondBorder && elem.borderFillType2 === 'gradient')
                              ? elem.strokeColor2 || 'linear-gradient(to right, red, blue)'
                              : 'transparent'
                          }}>
                            {/* Intermediate gap wrapper to support secondary gradient border gap */}
                            <div style={{
                              width: '100%',
                              height: '100%',
                              boxSizing: 'border-box',
                              borderRadius: 'inherit',
                              padding: (elem.hasBorder !== false && elem.hasSecondBorder && elem.borderFillType2 === 'gradient')
                                ? `${elem.borderGap || 0}px`
                                : 0,
                              background: (elem.hasBorder !== false && elem.hasSecondBorder && elem.borderFillType2 === 'gradient')
                                ? (sides[activeSide].background.value || '#ffffff')
                                : 'transparent',
                              display: (elem.hasBorder !== false && elem.hasSecondBorder && elem.borderFillType2 === 'gradient')
                                ? 'flex'
                                : 'block',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}>
                              {/* Inner element wrapper that contains the primary border and fill background */}
                              <div style={{
                                width: '100%',
                                height: '100%',
                                boxSizing: 'border-box',
                                borderRadius: 'inherit', // inherits parent border radius
                                flexShrink: 0,
                                position: 'relative',
                                overflow: 'hidden',
                                
                                // 3. Primary Border styling (only if not gradient border fill)
                                border: (elem.hasBorder !== false && elem.borderFillType !== 'gradient') 
                                  ? `${elem.strokeWidth || 0}px ${elem.strokeStyle || 'solid'} ${elem.strokeColor || '#cbd5e1'}` 
                                  : 'none',
                                
                                // 4. Primary Border Gradient padding
                                padding: (elem.hasBorder !== false && elem.borderFillType === 'gradient')
                                  ? `${elem.strokeWidth || 0}px`
                                  : 0,
                                background: (elem.hasBorder !== false && elem.borderFillType === 'gradient')
                                  ? elem.strokeColor || 'linear-gradient(to right, red, blue)'
                                  : (elem.fillType === 'pattern' ? (elem.pattern?.tintColor || '#e6e6e6') : (elem.fill || '#e6e6e6'))
                              }}>
                                {/* Content box inside primary gradient border */}
                                {elem.hasBorder !== false && elem.borderFillType === 'gradient' && (
                                  <div style={{
                                    width: '100%',
                                    height: '100%',
                                    position: 'relative',
                                    overflow: 'hidden',
                                    background: elem.fillType === 'pattern' ? (elem.pattern?.tintColor || '#e6e6e6') : (elem.fill || '#e6e6e6'),
                                    borderRadius: 'inherit'
                                  }}>
                                    {elem.fillType === 'pattern' && elem.pattern && (
                                      <div style={{
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                        width: '100%',
                                        height: '100%',
                                        borderRadius: 'inherit',
                                        pointerEvents: 'none',
                                        opacity: elem.pattern.opacity !== undefined ? (elem.pattern.opacity > 1 ? elem.pattern.opacity / 100 : elem.pattern.opacity) : 1,
                                        backgroundImage: `url("${elem.pattern.image}")`,
                                        backgroundRepeat: elem.pattern.repeat || 'repeat',
                                        backgroundSize: `${Math.max(2, Math.round((elem.pattern.tileWidth || 32) * ((elem.pattern.scale !== undefined ? elem.pattern.scale : 100) / 100)))}px ${Math.max(2, Math.round((elem.pattern.tileHeight || 32) * ((elem.pattern.scale !== undefined ? elem.pattern.scale : 100) / 100)))}px`,
                                        backgroundPosition: '0 0',
                                        mixBlendMode: elem.pattern.blendMode || 'normal'
                                      }} />
                                    )}
                                  </div>
                                )}
                                {!(elem.hasBorder !== false && elem.borderFillType === 'gradient') && elem.fillType === 'pattern' && elem.pattern && (
                                  <div style={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    width: '100%',
                                    height: '100%',
                                    borderRadius: 'inherit',
                                    pointerEvents: 'none',
                                    opacity: elem.pattern.opacity !== undefined ? (elem.pattern.opacity > 1 ? elem.pattern.opacity / 100 : elem.pattern.opacity) : 1,
                                    backgroundImage: `url("${elem.pattern.image}")`,
                                    backgroundRepeat: elem.pattern.repeat || 'repeat',
                                    backgroundSize: `${Math.max(2, Math.round((elem.pattern.tileWidth || 32) * ((elem.pattern.scale !== undefined ? elem.pattern.scale : 100) / 100)))}px ${Math.max(2, Math.round((elem.pattern.tileHeight || 32) * ((elem.pattern.scale !== undefined ? elem.pattern.scale : 100) / 100)))}px`,
                                    backgroundPosition: '0 0',
                                    mixBlendMode: elem.pattern.blendMode || 'normal'
                                  }} />
                                )}
                              </div>
                            </div>
                          </div>
                        )
                      )}

                      {elem.type === 'text' && (
                        editingTextElementId === elem.id ? (
                          <input
                            type="text"
                            value={elem.text}
                            onChange={(e) => updateActiveElement({ text: e.target.value })}
                            onBlur={() => setEditingTextElementId(null)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                setEditingTextElementId(null);
                              }
                            }}
                            autoFocus
                            onFocus={(e) => e.target.select()}
                            style={{
                              width: '100%',
                              height: '100%',
                              backgroundColor: 'transparent',
                              border: 'none',
                              outline: 'none',
                              color: elem.color,
                              fontSize: `${elem.fontSize}px`,
                              fontFamily: elem.fontFamily,
                              fontWeight: elem.fontWeight,
                              fontStyle: elem.fontStyle || 'normal',
                              textDecoration: elem.textDecoration || 'none',
                              letterSpacing: `${elem.letterSpacing || 0}px`,
                              textTransform: elem.textTransform || 'none',
                              textAlign: elem.alignment || 'left',
                              lineHeight: elem.lineHeight || 1.1,
                              resize: 'none',
                              transform: `translateY(${elem.baselineShift || 0}px) scaleX(${elem.scaleX !== undefined ? elem.scaleX / 100 : 1}) scaleY(${elem.scaleY !== undefined ? elem.scaleY / 100 : 1}) skewX(${elem.shear || 0}deg)`,
                              transformOrigin: elem.alignment === 'center' ? 'center center' : elem.alignment === 'right' ? 'right center' : 'left center',
                              WebkitTextStrokeWidth: `${elem.textStrokeWidth || 0}px`,
                              WebkitTextStrokeColor: elem.textStrokeColorType === 'gradient' ? (elem.textStrokeGradient?.stops?.[0]?.color || '#ffffff') : (elem.textStrokeColor || '#ffffff')
                            }}
                          />
                        ) : (
                          <div
                            onDoubleClick={() => {
                              if (!elem.locked) {
                                setEditingTextElementId(elem.id);
                              }
                            }}
                            style={{
                              width: '100%',
                              height: '100%',
                              fontSize: `${elem.fontSize}px`,
                              fontFamily: elem.fontFamily,
                              fontWeight: elem.fontWeight,
                              fontStyle: elem.fontStyle || 'normal',
                              textDecoration: elem.textDecoration || 'none',
                              letterSpacing: `${elem.letterSpacing || 0}px`,
                              textTransform: elem.textTransform || 'none',
                              textAlign: elem.alignment || 'left',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: elem.alignment === 'center' ? 'center' : elem.alignment === 'right' ? 'flex-end' : 'flex-start',
                              lineHeight: elem.lineHeight || 1.1,
                              background: elem.colorType === 'gradient' ? (elem.color || 'linear-gradient(90deg, #ffffff 0%, #000000 100%)') : 'transparent',
                              WebkitBackgroundClip: elem.colorType === 'gradient' ? 'text' : 'unset',
                              WebkitTextFillColor: elem.colorType === 'gradient' ? 'transparent' : 'unset',
                              color: elem.colorType === 'gradient' ? 'transparent' : (elem.color || '#ffffff'),
                              transform: `translateY(${elem.baselineShift || 0}px) scaleX(${elem.scaleX !== undefined ? elem.scaleX / 100 : 1}) scaleY(${elem.scaleY !== undefined ? elem.scaleY / 100 : 1}) skewX(${elem.shear || 0}deg)`,
                              transformOrigin: elem.alignment === 'center' ? 'center center' : elem.alignment === 'right' ? 'right center' : 'left center',
                              WebkitTextStrokeWidth: `${elem.textStrokeWidth || 0}px`,
                              WebkitTextStrokeColor: elem.textStrokeColorType === 'gradient' ? (elem.textStrokeGradient?.stops?.[0]?.color || '#ffffff') : (elem.textStrokeColor || '#ffffff')
                            }}
                          >
                            {resolveElementText(elem)}
                          </div>
                        )
                      )}

                      {elem.type === 'datagrid' && (
                        <div style={{
                          width: '100%',
                          height: '100%',
                          boxSizing: 'border-box',
                          borderRadius: elem.hasBorder !== false ? `${elem.borderRadius || 0}px` : 0,
                          overflow: 'hidden',
                          
                          // 1. Secondary Border styling
                          border: (elem.hasBorder !== false && elem.hasSecondBorder && elem.borderFillType2 !== 'gradient')
                            ? `${elem.strokeWidth2 || 2}px ${elem.strokeStyle2 || 'solid'} ${elem.strokeColor2 || '#000000'}`
                            : 'none',
                          
                          // 2. Gap/Border padding
                          padding: (elem.hasBorder !== false && elem.hasSecondBorder)
                            ? (elem.borderFillType2 === 'gradient' ? `${elem.strokeWidth2 || 2}px` : `${elem.borderGap || 0}px`)
                            : 0,
                          background: (elem.hasBorder !== false && elem.hasSecondBorder && elem.borderFillType2 === 'gradient')
                            ? elem.strokeColor2 || 'linear-gradient(to right, red, blue)'
                            : 'transparent'
                        }}>
                          {/* Intermediate gap wrapper */}
                          <div style={{
                            width: '100%',
                            height: '100%',
                            boxSizing: 'border-box',
                            borderRadius: 'inherit',
                            padding: (elem.hasBorder !== false && elem.hasSecondBorder && elem.borderFillType2 === 'gradient')
                              ? `${elem.borderGap || 0}px`
                              : 0,
                            background: (elem.hasBorder !== false && elem.hasSecondBorder && elem.borderFillType2 === 'gradient')
                              ? (sides[activeSide].background.value || '#ffffff')
                              : 'transparent',
                            display: (elem.hasBorder !== false && elem.hasSecondBorder && elem.borderFillType2 === 'gradient')
                              ? 'flex'
                              : 'block',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}>
                            {/* Inner element wrapper containing primary border */}
                            <div style={{
                              width: '100%',
                              height: '100%',
                              boxSizing: 'border-box',
                              borderRadius: 'inherit',
                              overflow: 'hidden',
                              flexShrink: 0,
                              
                              // 3. Primary Border styling
                              border: (elem.hasBorder !== false && elem.borderFillType !== 'gradient')
                                ? `${elem.strokeWidth !== undefined ? elem.strokeWidth : 1}px ${elem.strokeStyle || 'solid'} ${elem.strokeColor || '#ffffff'}`
                                : 'none',
                              
                              // 4. Primary Border Gradient padding
                              padding: (elem.hasBorder !== false && elem.borderFillType === 'gradient')
                                ? `${elem.strokeWidth !== undefined ? elem.strokeWidth : 1}px`
                                : 0,
                              background: (elem.hasBorder !== false && elem.borderFillType === 'gradient')
                                ? elem.strokeColor || 'linear-gradient(to right, red, blue)'
                                : 'transparent',
                              display: 'flex',
                              flexDirection: 'column'
                            }}>
                              {/* Actual Table / Datagrid content */}
                              <div style={{
                                width: '100%',
                                height: '100%',
                                display: 'flex',
                                flexDirection: 'column',
                                boxSizing: 'border-box',
                                fontFamily: elem.fontFamily || 'Arial',
                                fontSize: `${elem.fontSize || 11}px`,
                                letterSpacing: `${elem.letterSpacing || 0}px`,
                                lineHeight: elem.lineHeight || 1.1,
                                background: elem.fillType === 'gradient'
                                  ? (elem.fill || 'linear-gradient(90deg, rgba(255,255,255,0.15) 0%, rgba(0,0,0,0.2) 100%)')
                                  : (elem.fillType === 'pattern' ? (elem.pattern?.tintColor || 'rgba(0,0,0,0.06)') : (elem.fill || 'rgba(0,0,0,0.06)')),
                                overflow: 'hidden',
                                borderRadius: 'inherit',
                                position: 'relative'
                              }}>
                                {elem.fillType === 'pattern' && elem.pattern && (
                                  <div style={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    width: '100%',
                                    height: '100%',
                                    borderRadius: 'inherit',
                                    pointerEvents: 'none',
                                    opacity: elem.pattern.opacity !== undefined ? (elem.pattern.opacity > 1 ? elem.pattern.opacity / 100 : elem.pattern.opacity) : 1,
                                    backgroundImage: `url("${elem.pattern.image}")`,
                                    backgroundRepeat: elem.pattern.repeat || 'repeat',
                                    backgroundSize: `${Math.max(2, Math.round((elem.pattern.tileWidth || 32) * ((elem.pattern.scale !== undefined ? elem.pattern.scale : 100) / 100)))}px ${Math.max(2, Math.round((elem.pattern.tileHeight || 32) * ((elem.pattern.scale !== undefined ? elem.pattern.scale : 100) / 100)))}px`,
                                    backgroundPosition: '0 0',
                                    mixBlendMode: elem.pattern.blendMode || 'normal',
                                    zIndex: 0
                                  }} />
                                )}
                                {/* Header Row */}
                                {elem.headerEnabled && (
                                  <div style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    background: elem.headerFillType === 'gradient' ? (elem.headerFill || 'linear-gradient(90deg, rgba(255,255,255,0.15) 0%, rgba(0,0,0,0.2) 100%)') : (elem.headerFill || 'rgba(255,255,255,0.1)'),
                                    color: elem.headerColor || '#ffffff',
                                    fontWeight: 'bold',
                                    paddingTop: `${elem.rowPadding !== undefined ? elem.rowPadding : 4}px`,
                                    paddingBottom: `${elem.rowPadding !== undefined ? elem.rowPadding : 4}px`,
                                    paddingLeft: `${elem.colPadding !== undefined ? elem.colPadding : 6}px`,
                                    paddingRight: `${elem.colPadding !== undefined ? elem.colPadding : 6}px`,
                                    borderBottom: (elem.gridLinesEnabled !== false)
                                      ? `${elem.gridLineThickness !== undefined ? elem.gridLineThickness : 1}px ${elem.gridLineStyle || 'solid'} ${elem.gridLineColor || 'rgba(255,255,255,0.08)'}`
                                      : 'none'
                                  }}>
                                    <span style={{
                                      width: `${elem.colWidthRatio || 50}%`,
                                      textAlign: elem.labelAlign || 'left',
                                      boxSizing: 'border-box'
                                    }}>
                                      {elem.headerLabelText || 'Attribute'}
                                    </span>
                                    <span style={{
                                      width: `${100 - (elem.colWidthRatio || 50)}%`,
                                      textAlign: elem.valueAlign || 'right',
                                      boxSizing: 'border-box'
                                    }}>
                                      {elem.headerValueText || 'Value'}
                                    </span>
                                  </div>
                                )}

                                {/* Data Rows */}
                                {elem.rows.map((row, idx) => (
                                  <div key={idx} style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    backgroundColor: (elem.zebraEnabled && idx % 2 === 1) ? (elem.zebraColor || 'rgba(255,255,255,0.03)') : 'transparent',
                                    paddingTop: `${elem.rowPadding !== undefined ? elem.rowPadding : 4}px`,
                                    paddingBottom: `${elem.rowPadding !== undefined ? elem.rowPadding : 4}px`,
                                    paddingLeft: `${elem.colPadding !== undefined ? elem.colPadding : 6}px`,
                                    paddingRight: `${elem.colPadding !== undefined ? elem.colPadding : 6}px`,
                                    borderBottom: (elem.gridLinesEnabled !== false && idx < elem.rows.length - 1)
                                      ? `${elem.gridLineThickness !== undefined ? elem.gridLineThickness : 1}px ${elem.gridLineStyle || 'solid'} ${elem.gridLineColor || 'rgba(255,255,255,0.08)'}`
                                      : 'none',
                                    boxSizing: 'border-box'
                                  }}>
                                    <span style={{ 
                                      width: `${elem.colWidthRatio || 50}%`,
                                      textAlign: elem.labelAlign || 'left',
                                      fontSize: `${elem.labelFontSize || elem.fontSize || 11}px`,
                                      fontFamily: elem.labelFontFamily || elem.fontFamily || 'Arial',
                                      fontWeight: elem.labelFontWeight || elem.fontWeight || '400',
                                      fontStyle: elem.labelFontStyle || elem.fontStyle || 'normal',
                                      textDecoration: elem.labelDecoration || elem.textDecoration || 'none',
                                      letterSpacing: `${elem.labelLetterSpacing !== undefined ? elem.labelLetterSpacing : (elem.letterSpacing || 0)}px`,
                                      background: elem.labelColorType === 'gradient' ? (elem.labelColor || 'linear-gradient(90deg, #ffffff 0%, #000000 100%)') : 'transparent',
                                      WebkitBackgroundClip: elem.labelColorType === 'gradient' ? 'text' : 'unset',
                                      WebkitTextFillColor: elem.labelColorType === 'gradient' ? 'transparent' : 'unset',
                                      color: elem.labelColorType === 'gradient' ? 'transparent' : (elem.labelColor || elem.color || '#ffffff'),
                                      display: elem.labelColorType === 'gradient' ? 'inline-block' : 'block',
                                      boxSizing: 'border-box',
                                      paddingRight: '4px',
                                      whiteSpace: 'nowrap',
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis'
                                    }}>{applyTextTransform(row[0], elem.labelTransformModifier)}</span>
                                    <span style={{ 
                                      width: `${100 - (elem.colWidthRatio || 50)}%`,
                                      textAlign: elem.valueAlign || 'right',
                                      fontSize: `${elem.valueFontSize || elem.fontSize || 11}px`,
                                      fontFamily: elem.valueFontFamily || elem.fontFamily || 'Arial',
                                      fontWeight: elem.valueFontWeight || (elem.fontWeight ? (parseInt(elem.fontWeight) >= 700 ? elem.fontWeight : '700') : '700'),
                                      fontStyle: elem.valueFontStyle || elem.fontStyle || 'normal',
                                      textDecoration: elem.valueDecoration || elem.textDecoration || 'none',
                                      letterSpacing: `${elem.valueLetterSpacing !== undefined ? elem.valueLetterSpacing : (elem.letterSpacing || 0)}px`,
                                      background: elem.valueColorType === 'gradient' ? (elem.valueColor || 'linear-gradient(90deg, #ffffff 0%, #000000 100%)') : 'transparent',
                                      WebkitBackgroundClip: elem.valueColorType === 'gradient' ? 'text' : 'unset',
                                      WebkitTextFillColor: elem.valueColorType === 'gradient' ? 'transparent' : 'unset',
                                      color: elem.valueColorType === 'gradient' ? 'transparent' : (elem.valueColor || elem.color || '#ffffff'),
                                      display: elem.valueColorType === 'gradient' ? 'inline-block' : 'block',
                                      boxSizing: 'border-box',
                                      paddingLeft: '4px',
                                      whiteSpace: 'nowrap',
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis'
                                    }}>{applyTextTransform(resolveTemplateVariables(row[1]), elem.valueTransformModifier)}</span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          </div>
                        </div>
                      )}

                      {elem.type === 'accent' && (
                        <div style={{
                          width: (elem.dividerType === 'vertical' || elem.orientation === 'vertical') ? `${elem.strokeWidth || 2}px` : '100%',
                          height: (elem.dividerType === 'vertical' || elem.orientation === 'vertical') ? '100%' : `${elem.strokeWidth || 2}px`,
                          background: elem.fill || elem.strokeColor || '#cbd5e1',
                          margin: (elem.dividerType === 'vertical' || elem.orientation === 'vertical') ? '0 auto' : '0'
                        }} />
                      )}

                      {elem.type === 'certify' && renderCertifyElement(elem)}

                      {elem.type === 'qrcode' && (
                        <div style={{
                          width: '100%',
                          height: '100%',
                          boxSizing: 'border-box',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          borderRadius: `${elem.borderRadius !== undefined ? elem.borderRadius : 6}px`,
                          overflow: 'hidden',
                          backgroundColor: elem.bgColor === 'transparent' ? 'transparent' : (elem.bgColor || '#ffffff'),
                          border: (elem.hasBorder && elem.strokeWidth) ? `${elem.strokeWidth}px ${elem.strokeStyle || 'solid'} ${elem.strokeColor || '#cbd5e1'}` : 'none',
                          padding: `${elem.padding || 0}px`
                        }}>
                          <QRCodeSVG
                            value={resolveQrCodeValue(elem, {}, datasets, user, orgData, tenantConfig)}
                            size={Math.min(elem.width, elem.height)}
                            fgColor={elem.fgColor || '#000000'}
                            bgColor={elem.bgColor === 'transparent' ? 'transparent' : (elem.bgColor || '#ffffff')}
                            margin={elem.margin !== undefined ? elem.margin : 2}
                            style={{ width: '100%', height: '100%', borderRadius: 'inherit' }}
                          />
                        </div>
                      )}

                      {isSelected && !elem.locked && (
                        <>
                          {/* Selection Bounding Box */}
                          <div style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            border: '1.5px solid #1677ff',
                            pointerEvents: 'none'
                          }} />
                          {/* Rotation Handle */}
                          <div style={{
                            position: 'absolute',
                            left: '50%',
                            top: '-20px',
                            width: '1px',
                            height: '20px',
                            backgroundColor: '#1677ff',
                            pointerEvents: 'none'
                          }} />
                          <div
                            onPointerDown={(e) => handlePointerDown(e, elem, 'rotate')}
                            onDoubleClick={(e) => {
                              e.stopPropagation();
                              saveHistoryState();
                              updateActiveElement({ rotation: 0 });
                            }}
                            title="Drag to rotate (Hold Shift to snap 15°, double-click to reset to 0°)"
                            style={{
                              position: 'absolute',
                              left: 'calc(50% - 5px)',
                              top: '-25px',
                              width: '10px',
                              height: '10px',
                              borderRadius: '50%',
                              backgroundColor: '#ffffff',
                              border: '1.5px solid #1677ff',
                              cursor: 'grab',
                              zIndex: 25
                            }}
                          />
                          {['nw', 'n', 'ne', 'e', 'se', 's', 'sw', 'w'].map(h => {
                            let leftStyle = 'auto';
                            let topStyle = 'auto';

                            if (h.includes('w')) leftStyle = '-4px';
                            if (h.includes('e')) leftStyle = 'calc(100% - 4px)';
                            if (h === 'n' || h === 's') leftStyle = 'calc(50% - 4px)';

                            if (h.includes('n')) topStyle = '-4px';
                            if (h.includes('s')) topStyle = 'calc(100% - 4px)';
                            if (h === 'e' || h === 'w') topStyle = 'calc(50% - 4px)';

                            return (
                              <div
                                key={h}
                                onPointerDown={(e) => handlePointerDown(e, elem, 'resize', h)}
                                style={{
                                  position: 'absolute',
                                  width: '7px',
                                  height: '7px',
                                  backgroundColor: '#ffffff',
                                  border: '1.5px solid #1677ff',
                                  zIndex: 10,
                                  cursor: `${h === 'n' || h === 's' ? 'ns' : h === 'e' || h === 'w' ? 'ew' : h === 'nw' || h === 'se' ? 'nwse' : 'nesw'}-resize`,
                                  left: leftStyle,
                                  top: topStyle
                                }}
                              />
                            );
                          })}
                          {elem.type === 'image' && getResolvedImageSrc(elem) && (
                            <button
                              type="button"
                              onPointerDown={(e) => e.stopPropagation()}
                              onClick={(e) => {
                                e.stopPropagation();
                                openImageAdjustModal(elem);
                              }}
                              style={{
                                position: 'absolute',
                                bottom: '-30px',
                                left: '50%',
                                transform: 'translateX(-50%)',
                                backgroundColor: '#1677ff',
                                color: '#ffffff',
                                border: 'none',
                                borderRadius: '14px',
                                padding: '3px 10px',
                                fontSize: '10px',
                                fontWeight: '600',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '5px',
                                boxShadow: '0 2px 8px rgba(0,0,0,0.5)',
                                whiteSpace: 'nowrap',
                                zIndex: 20
                              }}
                              title="Pan and Zoom image within this frame"
                            >
                              <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                <polyline points="15 3 21 3 21 9"></polyline>
                                <polyline points="9 21 3 21 3 15"></polyline>
                                <line x1="21" y1="3" x2="14" y2="10"></line>
                                <line x1="3" y1="21" x2="10" y2="14"></line>
                              </svg>
                              Pan & Zoom Image
                            </button>
                          )}
                        </>
                      )}
                    </div>
                  );
                })}
            </div>
          </div>

          {/* RIGHT SIDEBAR PANEL */}
          <div style={{
            backgroundColor: '#2b2b2f',
            borderLeft: '1px solid #3c3c42',
            display: 'flex',
            flexDirection: 'column',
            userSelect: 'none',
            overflow: 'hidden',
            width: '460px'
          }}>
            {/* Right sidebar tab selector */}
            <div style={{
              display: 'flex',
              height: '36px',
              backgroundColor: '#232326',
              borderBottom: '1px solid #3c3c42',
              padding: '0 4px',
              gap: '2px'
            }}>
              {[
                { id: 'layers', label: 'Layers' },
                { id: 'transform', label: 'Properties' },
                { id: 'settings', label: 'Card' },
                { id: 'data', label: 'Data' },
                { id: 'colour', label: 'Colour' },
                { id: 'form', label: 'Form' }
              ].map(t => (
                <button
                  key={t.id}
                  onClick={() => setActiveRightTab(t.id)}
                  style={{
                    flex: 1,
                    height: '100%',
                    backgroundColor: activeRightTab === t.id ? '#2b2b2f' : 'transparent',
                    color: activeRightTab === t.id ? '#ffffff' : '#8e8e93',
                    border: 'none',
                    borderBottom: activeRightTab === t.id ? '2px solid #ffffff' : 'none',
                    fontSize: '11px',
                    fontWeight: 'bold',
                    cursor: 'pointer',
                    padding: '0'
                  }}
                >
                  {t.label}
                </button>
              ))}
            </div>

            {/* Right Sidebar Tab Content Panel */}
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
              {activeRightTab === 'layers' && (
                <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
                  {/* Controls Row */}
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    fontSize: '11px',
                    color: '#a2a2a8',
                    padding: '8px 12px',
                    backgroundColor: '#2b2b2f',
                    borderBottom: '1px solid #232326',
                    gap: '6px'
                  }}>
                    {/* Opacity */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px', position: 'relative' }}>
                      <span>Opacity:</span>
                      <button
                        onClick={() => setShowOpacitySlider(!showOpacitySlider)}
                        disabled={!selectedElementId}
                        style={{
                          backgroundColor: '#1b1b1d',
                          border: '1px solid #3c3c42',
                          borderRadius: '3px',
                          color: '#fff',
                          fontSize: '10px',
                          padding: '1px 4px',
                          cursor: selectedElementId ? 'pointer' : 'default',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '2px'
                        }}
                      >
                        {activeElement ? (activeElement.opacity !== undefined ? activeElement.opacity : 100) : 100}% ▾
                      </button>

                      {showOpacitySlider && selectedElementId && (
                        <>
                          <div
                            onClick={() => setShowOpacitySlider(false)}
                            style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 99 }}
                          />
                          <div style={{
                            position: 'absolute',
                            top: '20px',
                            left: '32px',
                            backgroundColor: '#1b1b1d',
                            border: '1px solid #3c3c42',
                            borderRadius: '4px',
                            padding: '8px 8px',
                            zIndex: 100,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            boxShadow: '0 4px 12px rgba(0,0,0,0.5)',
                            gap: '6px'
                          }}>
                            <input
                              type="range"
                              min="0"
                              max="100"
                              value={activeElement ? (activeElement.opacity !== undefined ? activeElement.opacity : 100) : 100}
                              onChange={(evt) => {
                                if (selectedElementId && activeElement) {
                                  updateActiveElement({ opacity: parseInt(evt.target.value) });
                                }
                              }}
                              style={{
                                height: '80px',
                                width: '20px',
                                WebkitAppearance: 'slider-vertical',
                                cursor: 'pointer',
                                outline: 'none'
                              }}
                            />
                            <input
                              type="number"
                              min="0"
                              max="100"
                              value={activeElement ? (activeElement.opacity !== undefined ? activeElement.opacity : 100) : 100}
                              onChange={(evt) => {
                                let val = parseInt(evt.target.value);
                                if (isNaN(val)) val = 0;
                                if (val < 0) val = 0;
                                if (val > 100) val = 100;
                                if (selectedElementId && activeElement) {
                                  updateActiveElement({ opacity: val });
                                }
                              }}
                              style={{
                                width: '32px',
                                backgroundColor: '#222225',
                                border: '1px solid #2d2d30',
                                borderRadius: '3px',
                                color: '#fff',
                                fontSize: '9px',
                                padding: '1px 2px',
                                textAlign: 'center',
                                outline: 'none'
                              }}
                            />
                          </div>
                        </>
                      )}
                    </div>

                    {/* Blend Mode */}
                    <select
                      value={activeElement ? (activeElement.blendMode || 'normal') : 'normal'}
                      onChange={(e) => {
                        if (selectedElementId && activeElement) {
                          updateActiveElement({ blendMode: e.target.value });
                        }
                      }}
                      disabled={!selectedElementId}
                      style={{
                        backgroundColor: '#1b1b1d',
                        border: '1px solid #3c3c42',
                        borderRadius: '3px',
                        color: '#fff',
                        fontSize: '10px',
                        padding: '1px 4px',
                        flexGrow: 1
                      }}
                    >
                      <option value="normal">Normal</option>
                      <option value="multiply">Multiply</option>
                      <option value="screen">Screen</option>
                      <option value="overlay">Overlay</option>
                      <option value="darken">Darken</option>
                      <option value="lighten">Lighten</option>
                      <option value="color-dodge">Color Dodge</option>
                      <option value="color-burn">Color Burn</option>
                      <option value="difference">Difference</option>
                      <option value="exclusion">Exclusion</option>
                      <option value="hue">Hue</option>
                      <option value="saturation">Saturation</option>
                      <option value="color">Color</option>
                      <option value="luminosity">Luminosity</option>
                    </select>

                    {/* Settings & Lock */}
                    <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                      <span style={{ fontSize: '12px', cursor: 'pointer', opacity: 0.7 }} title="Settings">⚙</span>
                      <span
                        onClick={() => {
                          if (selectedElementId && activeElement) {
                            updateActiveElement({ locked: !activeElement.locked });
                          }
                        }}
                        style={{ fontSize: '11px', cursor: 'pointer', opacity: selectedElementId && activeElement?.locked ? 1 : 0.4 }}
                        title="Lock Layer"
                      >
                        🔒
                      </span>
                    </div>
                  </div>

                  {/* Scrollable Layers List Container */}
                  <div
                    onDragOver={(evt) => evt.preventDefault()}
                    onDrop={(evt) => {
                      if (evt.target === evt.currentTarget) {
                        handleContainerDrop(evt);
                      }
                    }}
                    style={{ flex: 1, overflowY: 'auto', backgroundColor: '#232326' }}
                  >
                    {[...sides[activeSide].elements].reverse().map((e, visualIndex) => {
                      const isSelected = selectedElementId === e.id;
                      if (e.parentId) {
                        const parent = sides[activeSide].elements.find(el => el.id === e.parentId);
                        if (parent && parent.collapsed) return null;
                      }
                      return (
                        <div
                          key={e.id}
                          data-testid={`layer-item-${e.id}`}
                          onClick={() => setSelectedElementId(e.id)}
                          draggable={true}
                          onDragStart={(evt) => handleDragStart(evt, visualIndex)}
                          onDragOver={(evt) => handleDragOver(evt, visualIndex)}
                          onDrop={(evt) => handleDrop(evt, visualIndex)}
                          onDragEnd={handleDragEnd}
                          style={{
                            display: 'flex',
                            height: '36px',
                            alignItems: 'center',
                            borderBottom: '1px solid #1e1e21',
                            cursor: 'grab',
                            backgroundColor: isSelected ? '#44444a' : draggedLayerIndex === visualIndex ? '#3a3a40' : '#2b2b2f',
                            color: isSelected ? '#ffffff' : '#dfdfe3',
                            fontSize: '11px',
                            transition: 'background-color 0.1s',
                            paddingLeft: `${getElementDepth(e) * 16}px`
                          }}
                        >
                          {/* Column 1: Tool Icon */}
                          <div style={{
                            width: '32px',
                            height: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            borderRight: '1px solid #1e1e21',
                            backgroundColor: isSelected ? 'rgba(255,255,255,0.08)' : 'transparent',
                            flexShrink: 0
                          }}>
                            {e.type === 'text' && <span style={{ fontWeight: '800', fontFamily: 'serif' }}>A</span>}
                            {e.type === 'group' && (
                              <span
                                onClick={(evt) => {
                                  evt.stopPropagation();
                                  toggleGroupCollapse(e.id);
                                }}
                                style={{ cursor: 'pointer', fontSize: '9px', display: 'flex', alignItems: 'center', gap: '2px' }}
                              >
                                {e.collapsed ? '▶' : '▼'}
                              </span>
                            )}
                            {e.type === 'image' && (
                              e.imageType === 'frame' ? (
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                  <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                                  <circle cx="9" cy="9" r="2" />
                                  <path d="M21 15l-3.086-3.086a2 2 0 00-2.828 0L6 21" />
                                </svg>
                              ) : (
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                  <rect x="2" y="3" width="20" height="14" rx="2" ry="2" />
                                  <line x1="2" y1="20" x2="22" y2="20" />
                                  <polyline points="12 13 17 8 22 13" />
                                  <polyline points="2 13 7 8 13 13" />
                                </svg>
                              )
                            )}
                            {e.type === 'shape' && (
                              e.shapeType === 'circle' ? <span>●</span> : e.shapeType === 'pill' ? <span>⬭</span> : e.shapeType === 'triangle' ? <span>▲</span> : e.shapeType === 'diamond' ? <span>✦</span> : e.shapeType === 'polygon' ? <span>⬡</span> : e.shapeType === 'star' ? <span>★</span> : <span>■</span>
                            )}
                            {e.type === 'datagrid' && (
                              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <rect x="3" y="3" width="18" height="18" rx="2" />
                                <line x1="9" y1="3" x2="9" y2="21" />
                                <line x1="15" y1="3" x2="15" y2="21" />
                                <line x1="3" y1="9" x2="21" y2="9" />
                                <line x1="3" y1="15" x2="21" y2="15" />
                              </svg>
                            )}
                            {e.type === 'accent' && (
                              (e.dividerType === 'vertical' || e.orientation === 'vertical') ? (
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ transform: 'rotate(90deg)', display: 'block' }}>
                                  <line x1="3" y1="12" x2="21" y2="12" />
                                  <circle cx="12" cy="12" r="4.5" />
                                </svg>
                              ) : (
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ display: 'block' }}>
                                  <line x1="3" y1="12" x2="21" y2="12" />
                                  <circle cx="12" cy="12" r="4.5" />
                                </svg>
                              )
                            )}
                            {e.type === 'certify' && (
                              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ display: 'block' }}>
                                <circle cx="12" cy="8" r="7" />
                                <polyline points="8.21 13.89 7 23 12 20 17 23 15.79 13.88" />
                              </svg>
                            )}
                            {e.type === 'qrcode' && (
                              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ display: 'block' }}>
                                <rect x="3" y="3" width="7" height="7" rx="1" />
                                <rect x="14" y="3" width="7" height="7" rx="1" />
                                <rect x="3" y="14" width="7" height="7" rx="1" />
                                <rect x="14" y="14" width="3" height="3" />
                                <path d="M21 14v3h-3" />
                                <path d="M18 21h3v-3" />
                              </svg>
                            )}
                          </div>

                          {/* Column 2: Miniature Thumbnail */}
                          <div style={{
                            width: '36px',
                            height: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            borderRight: '1px solid #1e1e21',
                            flexShrink: 0
                          }}>
                             {e.type === 'shape' && (
                               e.shapeType === 'triangle' ? (
                                 <div style={{
                                   width: '16px',
                                   height: '16px',
                                   backgroundColor: '#8e8e93',
                                   clipPath: 'polygon(50% 0%, 0% 100%, 100% 100%)'
                                 }} />
                               ) : e.shapeType === 'diamond' ? (
                                 <div style={{
                                   width: '16px',
                                   height: '16px',
                                   backgroundColor: '#8e8e93',
                                   clipPath: 'polygon(50% 0%, 100% 50%, 50% 100%, 0% 50%)'
                                 }} />
                               ) : e.shapeType === 'polygon' ? (
                                 <div style={{
                                   width: '16px',
                                   height: '16px',
                                   backgroundColor: '#8e8e93',
                                   clipPath: 'polygon(25% 0%, 75% 0%, 100% 50%, 75% 100%, 25% 100%, 0% 50%)'
                                 }} />
                               ) : e.shapeType === 'star' ? (
                                 <div style={{
                                   width: '16px',
                                   height: '16px',
                                   backgroundColor: '#8e8e93',
                                   clipPath: 'polygon(50% 0%, 61% 35%, 98% 35%, 68% 57%, 79% 91%, 50% 70%, 21% 91%, 32% 57%, 2% 35%, 39% 35%)'
                                 }} />
                               ) : (
                                 <div style={{
                                   width: '16px',
                                   height: '16px',
                                   backgroundColor: '#8e8e93',
                                   border: '1px solid rgba(255,255,255,0.3)',
                                   borderRadius: e.shapeType === 'circle' ? '50%' : e.shapeType === 'pill' ? '6px' : '2px'
                                 }} />
                               )
                             )}
                            {e.type === 'text' && (
                              <div style={{
                                width: '16px',
                                height: '16px',
                                backgroundColor: 'rgba(255,255,255,0.1)',
                                border: '1px solid rgba(255,255,255,0.2)',
                                borderRadius: '2px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: '8px',
                                fontWeight: 'bold'
                              }}>
                                T
                              </div>
                            )}
                            {e.type === 'image' && (
                              <div style={{
                                width: '16px',
                                height: '16px',
                                backgroundColor: '#444',
                                border: '1px solid rgba(255,255,255,0.2)',
                                borderRadius: '2px',
                                overflow: 'hidden'
                              }}>
                                <img
                                  src={getResolvedImageSrc(e)}
                                  alt=""
                                  style={{ width: '100%', height: '100%', objectFit: 'cover', filter: 'grayscale(1)' }}
                                />
                              </div>
                            )}
                            {e.type === 'datagrid' && (
                              <div style={{
                                width: '16px',
                                height: '16px',
                                backgroundColor: 'rgba(255,255,255,0.1)',
                                border: '1px solid rgba(255,255,255,0.2)',
                                borderRadius: '2px',
                                display: 'flex',
                                flexDirection: 'column',
                                padding: '2px',
                                gap: '2px'
                              }}>
                                <div style={{ height: '2px', backgroundColor: 'rgba(255,255,255,0.3)' }} />
                                <div style={{ height: '2px', backgroundColor: 'rgba(255,255,255,0.3)' }} />
                                <div style={{ height: '2px', backgroundColor: 'rgba(255,255,255,0.3)' }} />
                              </div>
                            )}
                            {e.type === 'accent' && (
                              <div style={{
                                width: (e.dividerType === 'vertical' || e.orientation === 'vertical') ? '2px' : '16px',
                                height: (e.dividerType === 'vertical' || e.orientation === 'vertical') ? '16px' : '2px',
                                backgroundColor: e.strokeColor || '#fff',
                                margin: (e.dividerType === 'vertical' || e.orientation === 'vertical') ? '0 auto' : '0'
                              }} />
                            )}
                            {e.type === 'certify' && (
                              <div style={{
                                width: '16px',
                                height: '16px',
                                backgroundColor: 'rgba(255,255,255,0.1)',
                                border: '1px solid rgba(255,255,255,0.2)',
                                borderRadius: '2px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: '#fff'
                              }}>
                                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" style={{ display: 'block' }}>
                                  <circle cx="12" cy="8" r="7" />
                                  <polyline points="8.21 13.89 7 23 12 20 17 23 15.79 13.88" />
                                </svg>
                              </div>
                            )}
                            {e.type === 'qrcode' && (
                              <div style={{
                                width: '16px',
                                height: '16px',
                                backgroundColor: '#ffffff',
                                border: '1px solid rgba(255,255,255,0.2)',
                                borderRadius: '2px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: '#000000'
                              }}>
                                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ display: 'block' }}>
                                  <rect x="3" y="3" width="7" height="7" />
                                  <rect x="14" y="3" width="7" height="7" />
                                  <rect x="3" y="14" width="7" height="7" />
                                  <rect x="14" y="14" width="3" height="3" />
                                </svg>
                              </div>
                            )}
                            {e.type === 'group' && (
                              <div style={{
                                width: '16px',
                                height: '16px',
                                backgroundColor: 'rgba(255,255,255,0.1)',
                                border: '1px solid rgba(255,255,255,0.2)',
                                borderRadius: '2px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: '#fff'
                              }}>
                                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ display: 'block' }}>
                                  <path d="M22 19a2 2 0 01-2 2H4a2 2 0 01-2-2V5a2 2 0 012-2h5l2 3h9a2 2 0 012 2z" />
                                </svg>
                              </div>
                            )}
                          </div>

                          {/* Column 3: Name / Desc */}
                          <div 
                            onDoubleClick={(evt) => {
                              evt.stopPropagation();
                              setRenamingLayerId(e.id);
                              setRenameValue(e.name || '');
                            }}
                            style={{
                              flex: 1,
                              paddingLeft: '8px',
                              display: 'flex',
                              flexDirection: 'row',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              overflow: 'hidden'
                            }}
                          >
                            {renamingLayerId === e.id ? (
                              <input
                                type="text"
                                value={renameValue}
                                onChange={(evt) => setRenameValue(evt.target.value)}
                                onKeyDown={(evt) => {
                                  if (evt.key === 'Enter') {
                                    evt.stopPropagation();
                                    saveHistoryState();
                                    setSides({
                                      ...sides,
                                      [activeSide]: {
                                        ...sides[activeSide],
                                        elements: sides[activeSide].elements.map(el => 
                                          el.id === e.id ? { ...el, name: renameValue } : el
                                        )
                                      }
                                    });
                                    setRenamingLayerId(null);
                                  } else if (evt.key === 'Escape') {
                                    evt.stopPropagation();
                                    setRenamingLayerId(null);
                                  }
                                }}
                                onBlur={() => {
                                  saveHistoryState();
                                  setSides({
                                    ...sides,
                                    [activeSide]: {
                                      ...sides[activeSide],
                                      elements: sides[activeSide].elements.map(el => 
                                        el.id === e.id ? { ...el, name: renameValue } : el
                                      )
                                    }
                                  });
                                  setRenamingLayerId(null);
                                }}
                                autoFocus
                                style={{
                                  backgroundColor: '#141416',
                                  border: '1px solid #1677ff',
                                  borderRadius: '3px',
                                  padding: '2px 4px',
                                  color: '#fff',
                                  fontSize: '11px',
                                  outline: 'none',
                                  width: '100%',
                                  boxSizing: 'border-box'
                                }}
                              />
                            ) : (
                              <>
                                <span style={{
                                  fontWeight: isSelected ? '700' : 'normal',
                                  overflow: 'hidden',
                                  whiteSpace: 'nowrap',
                                  textOverflow: 'ellipsis',
                                  flex: 1
                                }}>
                                  {e.name}
                                </span>
                              </>
                            )}
                          </div>

                          {/* Column 4: Lock Toggle */}
                          <div
                            onClick={(evt) => {
                              evt.stopPropagation();
                              toggleElementLock(e.id, e.locked);
                            }}
                            style={{
                              width: '32px',
                              height: '100%',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              borderLeft: '1px solid #1e1e21',
                              cursor: 'pointer'
                            }}
                            title={e.locked ? "Unlock Layer" : "Lock Layer"}
                            onMouseOver={(evt) => { evt.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.05)'; }}
                            onMouseOut={(evt) => { evt.currentTarget.style.backgroundColor = 'transparent'; }}
                          >
                            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" 
                              stroke={e.locked ? "#dfdfe3" : "#4b4b52"} 
                              strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" 
                              style={{ display: 'block', opacity: e.locked ? 1 : 0.6 }}
                            >
                              <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                              <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                            </svg>
                          </div>

                          {/* Column 5: Visibility Eye */}
                          <div
                            onClick={(evt) => {
                              evt.stopPropagation();
                              setSelectedElementId(e.id);
                              toggleElementVisibility(e.id, e.visible === false);
                            }}
                            style={{
                              width: '32px',
                              height: '100%',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              borderLeft: '1px solid #1e1e21',
                              cursor: 'pointer'
                            }}
                            onMouseOver={(evt) => { evt.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.05)'; }}
                            onMouseOut={(evt) => { evt.currentTarget.style.backgroundColor = 'transparent'; }}
                          >
                            {e.visible === false ? (
                              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#4b4b52" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ opacity: 0.6 }}>
                                <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                                <line x1="1" y1="1" x2="23" y2="23" />
                              </svg>
                            ) : (
                              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#dfdfe3" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                                <circle cx="12" cy="12" r="3" />
                              </svg>
                            )}
                          </div>
                        </div>
                      );
                    })}

                    {sides[activeSide].elements.length === 0 && (
                      <div style={{ padding: '40px 10px', textAlign: 'center', color: '#5e5e64', fontSize: '11px' }}>
                        No layers on this side
                      </div>
                    )}
                  </div>

                  {/* Docked bottom action toolbar */}
                  <div style={{
                    height: '32px',
                    backgroundColor: '#232326',
                    borderTop: '1px solid #3c3c42',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '0 8px'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      {/* Folder Icon (Add Group/Folder) */}
                      <button
                        onClick={handleAddGroupFolder}
                        style={{
                          background: 'none',
                          border: 'none',
                          color: '#dfdfe3',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          padding: '4px'
                        }}
                        title="New Group Folder"
                      >
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
                        </svg>
                      </button>

                      {/* Duplicate Icon (Duplicate Selected Layer) */}
                      <button
                        onClick={handleDuplicateElement}
                        disabled={!selectedElementId}
                        style={{
                          background: 'none',
                          border: 'none',
                          color: selectedElementId ? '#dfdfe3' : '#55555c',
                          cursor: selectedElementId ? 'pointer' : 'default',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          padding: '4px'
                        }}
                        title="Duplicate Selected Layer"
                      >
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                          <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                        </svg>
                      </button>

                      {/* Separator Line */}
                      <div style={{ width: '1px', height: '12px', backgroundColor: '#3c3c42' }} />

                      {/* Duplicate to Opposite Layout Side */}
                      <button
                        onClick={handleDuplicateElementToOpposite}
                        disabled={!selectedElementId}
                        style={{
                          background: 'none',
                          border: 'none',
                          color: selectedElementId ? '#dfdfe3' : '#55555c',
                          cursor: selectedElementId ? 'pointer' : 'default',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          padding: '4px',
                          gap: '2px'
                        }}
                        title={activeSide === 'front' ? "Duplicate Layer to Back Side" : "Duplicate Layer to Front Side"}
                      >
                        {/* Duplicate Icon (same size as the duplicate button) */}
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                          <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                        </svg>

                        {/* Curved Arrow Icon */}
                        {activeSide === 'front' ? (
                          <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M21 7v6h-6"></path>
                            <path d="M3 17a9 9 0 0 1 9-9 9 9 0 0 1 6 2.3l3 2.7"></path>
                          </svg>
                        ) : (
                          <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M3 7v6h6"></path>
                            <path d="M21 17a9 9 0 0 0-9-9 9 9 0 0 0-6 2.3L3 13"></path>
                          </svg>
                        )}
                      </button>
                    </div>

                    {/* Trash Can delete button */}
                    <button
                      onClick={() => { if (selectedElementId) handleDeleteElement(selectedElementId); }}
                      disabled={!selectedElementId}
                      style={{
                        background: 'none',
                        border: 'none',
                        color: selectedElementId ? '#ff7b72' : '#55555c',
                        cursor: selectedElementId ? 'pointer' : 'default',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: '4px'
                      }}
                      title="Delete Selected Layer"
                    >
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="3 6 5 6 21 6" />
                        <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                        <line x1="10" y1="11" x2="10" y2="17" />
                        <line x1="14" y1="11" x2="14" y2="17" />
                      </svg>
                    </button>
                  </div>
                </div>
              )}

              {activeRightTab === 'transform' && (
                <div style={{ flex: 1, overflowY: 'auto', padding: '12px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  {activeElement ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                      {/* Group membership settings */}
                      {activeElement.parentId && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', borderBottom: '1px solid #3c3c42', paddingBottom: '10px' }}>
                          <button
                            type="button"
                            onClick={() => {
                              saveHistoryState();
                              updateActiveElement({ parentId: null });
                            }}
                            style={{
                              backgroundColor: '#232326',
                              color: '#dfdfe3',
                              border: '1px solid #3c3c42',
                              borderRadius: '3px',
                              padding: '4px 8px',
                              fontSize: '10px',
                              fontWeight: 'bold',
                              cursor: 'pointer',
                              width: '100%',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              gap: '6px'
                            }}
                          >
                            📁 Move Out of Group
                          </button>
                        </div>
                      )}
                      {activeElement.type === 'image' && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', borderTop: '1px solid #3c3c42', paddingTop: '10px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', userSelect: 'none' }}>
                            <span style={{ fontSize: '10px', fontWeight: 'bold', color: '#8e8e93', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                              {activeElement.imageType === 'frame' || !activeElement.src ? 'Placeholder Source' : 'Image Source'}
                            </span>
                          </div>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', paddingLeft: '14px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                              {/* Preview thumbnail */}
                              <div style={{
                                width: '50px',
                                height: '50px',
                                backgroundColor: '#141416',
                                border: '1px solid #3c3c42',
                                borderRadius: '4px',
                                overflow: 'hidden',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                              }}>
                                <img
                                  src={getResolvedImageSrc(activeElement)}
                                  alt="Source Preview"
                                  style={{ width: '100%', height: '100%', objectFit: 'contain', ...getImageFilterStyle(activeElement) }}
                                />
                              </div>
                              
                              {/* Edit Button */}
                              <button
                                onClick={() => {
                                  setIsChangingImageSource(true);
                                  setShowAssetSelectorModal(true);
                                }}
                                style={{
                                  backgroundColor: '#232326',
                                  color: '#dfdfe3',
                                  border: '1px solid #3c3c42',
                                  borderRadius: '3px',
                                  padding: '2px 8px',
                                  fontSize: '10px',
                                  fontWeight: 'bold',
                                  cursor: 'pointer',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '4px'
                                }}
                              >
                                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                  <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                                  <path d="M18.5 2.5a2.121 2.121 0 1 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                                </svg>
                               </button>

                                {getResolvedImageSrc(activeElement) && (
                                  <button
                                    type="button"
                                    onClick={() => openImageAdjustModal(activeElement)}
                                    style={{
                                      backgroundColor: '#1677ff',
                                      color: '#ffffff',
                                      border: 'none',
                                      borderRadius: '3px',
                                      padding: '2px 8px',
                                      fontSize: '10px',
                                      fontWeight: 'bold',
                                      cursor: 'pointer',
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: '4px'
                                    }}
                                    title="Pan & Zoom image within frame"
                                  >
                                    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                      <polyline points="15 3 21 3 21 9"></polyline>
                                      <polyline points="9 21 3 21 3 15"></polyline>
                                      <line x1="21" y1="3" x2="14" y2="10"></line>
                                      <line x1="3" y1="21" x2="10" y2="14"></line>
                                    </svg>
                                    Pan & Zoom
                                  </button>
                                )}
                              </div>

                              {/* Variable Name input (only for placeholder frames) */}
                              {(activeElement.imageType === 'frame' || !activeElement.src) && (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', marginTop: '4px' }}>
                                  <span style={{ fontSize: '10px', color: '#8e8e93' }}>Variable Name</span>
                                  <input
                                    type="text"
                                    value={activeElement.imageVariable || '$image_source'}
                                    onChange={(e) => updateActiveElement({ imageVariable: e.target.value })}
                                    style={{
                                      backgroundColor: '#1b1b1d',
                                      border: '1px solid #3c3c42',
                                      borderRadius: '4px',
                                      padding: '4px 6px',
                                      color: '#fff',
                                      fontSize: '11px',
                                      outline: 'none',
                                      boxSizing: 'border-box',
                                      width: '100%'
                                    }}
                                  />
                                </div>
                              )}

                              {/* Effect Selection (Only for Image Frames) */}
                              {activeElement.imageType === 'frame' && (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', marginTop: '6px' }}>
                                  <span style={{ fontSize: '10px', color: '#8e8e93' }}>Effect</span>
                                  <select
                                    value={activeElement.effect || 'none'}
                                    onChange={(e) => updateActiveElement({ effect: e.target.value })}
                                    style={{
                                      backgroundColor: '#1b1b1d',
                                      border: '1px solid #3c3c42',
                                      borderRadius: '4px',
                                      padding: '4px 6px',
                                      color: '#fff',
                                      fontSize: '11px',
                                      outline: 'none',
                                      boxSizing: 'border-box',
                                      width: '100%'
                                    }}
                                  >
                                    <option value="none">None</option>
                                    <option value="foil_shimmer">Holographic Shimmer Foil Sweep</option>
                                    <option value="iridescent_rainbow">Iridescent Rainbow Sheen</option>
                                    <option value="silver_glint">Horizontal Silver Glint</option>
                                    <option value="ambient_pulse">Ambient Warm Pulse</option>
                                    <option value="gold_flare">Golden Flare Sweep</option>
                                    <option value="glass_reflection">Slow Glass Reflection</option>
                                    <option value="cosmic_aurora">Cosmic Aurora Shifting</option>
                                    <option value="warm_leak">Subtle Warm Light Leak</option>
                                    <option value="cold_frost">Cold Frost Mist</option>
                                    <option value="matte_vignette">Matte Vignette Overlay</option>
                                    <option value="vintage_sepia">Vintage Sepia Tint</option>
                                    <option value="gold_dust">Gold Dust Speckles</option>
                                    <option value="silver_satin">Silver Satin Luster</option>
                                    <option value="warm_velvet">Warm Velvet Glow</option>
                                    <option value="emerald_glaze">Emerald Satin Glaze</option>
                                    <option value="royal_velvet">Royal Velvet Glow</option>
                                    <option value="mono_slate">Monochrome Slate</option>
                                    <option value="duotone_bronze">Classic Duotone Bronze</option>
                                  </select>
                                </div>
                              )}

                              {/* Color Adjustments: Hue & Saturation */}
                              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '6px', borderTop: '1px solid #3c3c42', paddingTop: '8px' }}>
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                  <span style={{ fontSize: '10px', fontWeight: 'bold', color: '#8e8e93', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                    Hue & Saturation
                                  </span>
                                  {((activeElement.hue !== undefined && Number(activeElement.hue) !== 0) ||
                                    (activeElement.saturation !== undefined && Number(activeElement.saturation) !== 100)) && (
                                    <button
                                      type="button"
                                      onClick={() => {
                                        saveHistoryState();
                                        updateActiveElement({ hue: 0, saturation: 100 });
                                      }}
                                      style={{
                                        backgroundColor: '#232326',
                                        color: '#faad14',
                                        border: '1px solid #3c3c42',
                                        borderRadius: '3px',
                                        padding: '1px 6px',
                                        fontSize: '9px',
                                        cursor: 'pointer'
                                      }}
                                      title="Reset Hue to 0° and Saturation to 100%"
                                    >
                                      Reset
                                    </button>
                                  )}
                                </div>

                                {/* Hue Slider */}
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
                                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '10px', color: '#8e8e93' }}>
                                    <span>Hue</span>
                                    <span style={{ color: '#dfdfe3', fontFamily: 'monospace' }}>
                                      {Math.round(activeElement.hue || 0) > 0 ? `+${Math.round(activeElement.hue || 0)}°` : `${Math.round(activeElement.hue || 0)}°`}
                                    </span>
                                  </div>
                                  <input
                                    type="range"
                                    data-testid="image-hue-slider"
                                    min="-180"
                                    max="180"
                                    step="1"
                                    value={activeElement.hue || 0}
                                    onPointerDown={() => saveHistoryState()}
                                    onDoubleClick={() => {
                                      saveHistoryState();
                                      updateActiveElement({ hue: 0 });
                                    }}
                                    onChange={(e) => updateActiveElement({ hue: parseFloat(e.target.value) })}
                                    style={{ width: '100%', accentColor: '#1677ff', cursor: 'pointer' }}
                                    title="Adjust image hue (-180° to +180°). Double-click to reset to 0°."
                                  />
                                </div>

                                {/* Saturation Slider */}
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
                                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '10px', color: '#8e8e93' }}>
                                    <span>Saturation</span>
                                    <span style={{ color: '#dfdfe3', fontFamily: 'monospace' }}>
                                      {Math.round(activeElement.saturation !== undefined ? activeElement.saturation : 100)}%
                                    </span>
                                  </div>
                                  <input
                                    type="range"
                                    data-testid="image-saturation-slider"
                                    min="0"
                                    max="200"
                                    step="1"
                                    value={activeElement.saturation !== undefined ? activeElement.saturation : 100}
                                    onPointerDown={() => saveHistoryState()}
                                    onDoubleClick={() => {
                                      saveHistoryState();
                                      updateActiveElement({ saturation: 100 });
                                    }}
                                    onChange={(e) => updateActiveElement({ saturation: parseFloat(e.target.value) })}
                                    style={{ width: '100%', accentColor: '#1677ff', cursor: 'pointer' }}
                                    title="Adjust image saturation (0% to 200%). Double-click to reset to 100%."
                                  />
                                </div>
                              </div>

                              {/* Fit & Position within Frame section */}
                              {getResolvedImageSrc(activeElement) && (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '6px', borderTop: '1px solid #3c3c42', paddingTop: '8px' }}>
                                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                    <span style={{ fontSize: '10px', fontWeight: 'bold', color: '#8e8e93', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                      Fit & Position in Frame
                                    </span>
                                    <span style={{ fontSize: '9px', color: activeElement.cropApplied ? '#1677ff' : '#52c41a', backgroundColor: '#1e1e21', padding: '1px 5px', borderRadius: '3px', border: '1px solid #3c3c42' }}>
                                      {activeElement.cropApplied ? '✂️ Cropped' : '🖼️ Full Photo'}
                                    </span>
                                  </div>

                                  {activeElement.cropApplied ? (
                                    <>
                                      <div style={{ display: 'flex', gap: '6px' }}>
                                        <button
                                          type="button"
                                          onClick={() => openImageAdjustModal(activeElement)}
                                          style={{
                                            flex: 1,
                                            backgroundColor: '#1677ff',
                                            color: '#ffffff',
                                            border: 'none',
                                            borderRadius: '3px',
                                            padding: '4px 0',
                                            fontSize: '10px',
                                            fontWeight: '600',
                                            cursor: 'pointer',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            gap: '4px'
                                          }}
                                          title="Open visual Pan & Zoom crop tool"
                                        >
                                          🔍 Adjust Pan & Zoom
                                        </button>
                                        <button
                                          type="button"
                                          data-testid="sidebar-fit-to-image-btn"
                                          onClick={() => {
                                            let nw = activeElement.naturalWidth;
                                            let nh = activeElement.naturalHeight;
                                            const src = getResolvedImageSrc(activeElement);
                                            if ((!nw || !nh) && src && globalImageAspectCache.has(src)) {
                                              const aspect = globalImageAspectCache.get(src);
                                              if (aspect > 0) {
                                                nw = 1000;
                                                nh = Math.round(1000 / aspect);
                                              }
                                            }
                                            if (nw && nh && nw > 0 && nh > 0) {
                                              const targetAspect = nw / nh;
                                              const currentMax = Math.max(activeElement.width || 160, activeElement.height || 120);
                                              let newW, newH;
                                              if (targetAspect >= 1) {
                                                newW = Math.round(currentMax);
                                                newH = Math.max(30, Math.round(currentMax / targetAspect));
                                              } else {
                                                newH = Math.round(currentMax);
                                                newW = Math.max(30, Math.round(currentMax * targetAspect));
                                              }
                                              const oldCenterX = (activeElement.x || 0) + (activeElement.width || 160) / 2;
                                              const oldCenterY = (activeElement.y || 0) + (activeElement.height || 120) / 2;
                                              saveHistoryState();
                                              updateActiveElement({
                                                x: Math.round(oldCenterX - newW / 2),
                                                y: Math.round(oldCenterY - newH / 2),
                                                width: newW,
                                                height: newH,
                                                cropApplied: true,
                                                imgPanX: 0,
                                                imgPanY: 0,
                                                imgZoom: 1,
                                                imgAspect: targetAspect
                                              });
                                            } else {
                                              openImageAdjustModal(activeElement);
                                            }
                                          }}
                                          style={{
                                            backgroundColor: '#1e293b',
                                            color: '#38bdf8',
                                            border: '1px solid #0284c7',
                                            borderRadius: '3px',
                                            padding: '4px 8px',
                                            fontSize: '9px',
                                            fontWeight: '600',
                                            cursor: 'pointer',
                                            whiteSpace: 'nowrap'
                                          }}
                                          title="Fit crop frame to match photo's natural aspect ratio"
                                        >
                                          🎯 Fit to Image
                                        </button>
                                        <button
                                          type="button"
                                          onClick={() => updateActiveElement({ cropApplied: false, imgPanX: 0, imgPanY: 0, imgZoom: 1 })}
                                          style={{
                                            backgroundColor: '#232326',
                                            color: '#faad14',
                                            border: '1px solid #3c3c42',
                                            borderRadius: '3px',
                                            padding: '4px 8px',
                                            fontSize: '9px',
                                            cursor: 'pointer'
                                          }}
                                          title="Remove crop and fit entire photo inside frame"
                                        >
                                          Remove Crop
                                        </button>
                                      </div>

                                      {/* Zoom Slider */}
                                      <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '10px', color: '#8e8e93' }}>
                                          <span>Zoom</span>
                                          <span style={{ color: '#dfdfe3', fontFamily: 'monospace' }}>{Math.round((activeElement.imgZoom || 1) * 100)}%</span>
                                        </div>
                                        <input
                                          type="range"
                                          min="0.4"
                                          max="4"
                                          step="0.05"
                                          value={activeElement.imgZoom || 1}
                                          onChange={(e) => updateActiveElement({ imgZoom: parseFloat(e.target.value) })}
                                          style={{ width: '100%', accentColor: '#1677ff', cursor: 'pointer' }}
                                        />
                                      </div>

                                      {/* Pan X Slider */}
                                      <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '10px', color: '#8e8e93' }}>
                                          <span>Pan X (Horizontal)</span>
                                          <span style={{ color: '#dfdfe3', fontFamily: 'monospace' }}>{Math.round(activeElement.imgPanX || 0)}%</span>
                                        </div>
                                        <input
                                          type="range"
                                          min="-100"
                                          max="100"
                                          step="1"
                                          value={activeElement.imgPanX || 0}
                                          onChange={(e) => updateActiveElement({ imgPanX: parseFloat(e.target.value) })}
                                          style={{ width: '100%', accentColor: '#1677ff', cursor: 'pointer' }}
                                        />
                                      </div>

                                      {/* Pan Y Slider */}
                                      <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '10px', color: '#8e8e93' }}>
                                          <span>Pan Y (Vertical)</span>
                                          <span style={{ color: '#dfdfe3', fontFamily: 'monospace' }}>{Math.round(activeElement.imgPanY || 0)}%</span>
                                        </div>
                                        <input
                                          type="range"
                                          min="-100"
                                          max="100"
                                          step="1"
                                          value={activeElement.imgPanY || 0}
                                          onChange={(e) => updateActiveElement({ imgPanY: parseFloat(e.target.value) })}
                                          style={{ width: '100%', accentColor: '#1677ff', cursor: 'pointer' }}
                                        />
                                      </div>

                                      {/* Quick action buttons */}
                                      <div style={{ display: 'flex', gap: '6px', marginTop: '2px' }}>
                                        <button
                                          type="button"
                                          onClick={() => updateActiveElement({ imgPanX: 0, imgPanY: 0 })}
                                          style={{
                                            flex: 1,
                                            backgroundColor: '#232326',
                                            color: '#dfdfe3',
                                            border: '1px solid #3c3c42',
                                            borderRadius: '3px',
                                            padding: '3px 0',
                                            fontSize: '9px',
                                            cursor: 'pointer'
                                          }}
                                        >
                                          Center Pan
                                        </button>
                                        <button
                                          type="button"
                                          onClick={() => updateActiveElement({ imgPanX: 0, imgPanY: 0, imgZoom: 1 })}
                                          style={{
                                            flex: 1,
                                            backgroundColor: '#232326',
                                            color: '#dfdfe3',
                                            border: '1px solid #3c3c42',
                                            borderRadius: '3px',
                                            padding: '3px 0',
                                            fontSize: '9px',
                                            cursor: 'pointer'
                                          }}
                                        >
                                          Reset Center
                                        </button>
                                      </div>
                                    </>
                                  ) : (
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                      <span style={{ fontSize: '9px', color: '#8e8e93' }}>
                                        Photo fits completely without cropping. Use Pan & Crop to fill frame and adjust composition.
                                      </span>
                                      <div style={{ display: 'flex', gap: '6px' }}>
                                        <button
                                          type="button"
                                          onClick={() => openImageAdjustModal(activeElement)}
                                          style={{
                                            flex: 1,
                                            backgroundColor: '#1677ff',
                                            color: '#ffffff',
                                            border: 'none',
                                            borderRadius: '3px',
                                            padding: '5px 0',
                                            fontSize: '10px',
                                            fontWeight: '600',
                                            cursor: 'pointer',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            gap: '4px'
                                          }}
                                        >
                                          ✂️ Pan & Crop in Frame
                                        </button>
                                        <button
                                          type="button"
                                          data-testid="sidebar-fit-to-image-uncropped-btn"
                                          onClick={() => {
                                            let nw = activeElement.naturalWidth;
                                            let nh = activeElement.naturalHeight;
                                            const src = getResolvedImageSrc(activeElement);
                                            if ((!nw || !nh) && src && globalImageAspectCache.has(src)) {
                                              const aspect = globalImageAspectCache.get(src);
                                              if (aspect > 0) {
                                                nw = 1000;
                                                nh = Math.round(1000 / aspect);
                                              }
                                            }
                                            if (nw && nh && nw > 0 && nh > 0) {
                                              const targetAspect = nw / nh;
                                              const currentMax = Math.max(activeElement.width || 160, activeElement.height || 120);
                                              let newW, newH;
                                              if (targetAspect >= 1) {
                                                newW = Math.round(currentMax);
                                                newH = Math.max(30, Math.round(currentMax / targetAspect));
                                              } else {
                                                newH = currentMax;
                                                newW = Math.max(30, Math.round(currentMax * targetAspect));
                                              }
                                              const oldCenterX = (activeElement.x || 0) + (activeElement.width || 160) / 2;
                                              const oldCenterY = (activeElement.y || 0) + (activeElement.height || 120) / 2;
                                              saveHistoryState();
                                              updateActiveElement({
                                                x: Math.round(oldCenterX - newW / 2),
                                                y: Math.round(oldCenterY - newH / 2),
                                                width: newW,
                                                height: newH,
                                                cropApplied: true,
                                                imgPanX: 0,
                                                imgPanY: 0,
                                                imgZoom: 1,
                                                imgAspect: targetAspect
                                              });
                                            } else {
                                              openImageAdjustModal(activeElement);
                                            }
                                          }}
                                          style={{
                                            backgroundColor: '#1e293b',
                                            color: '#38bdf8',
                                            border: '1px solid #0284c7',
                                            borderRadius: '3px',
                                            padding: '5px 8px',
                                            fontSize: '10px',
                                            fontWeight: '600',
                                            cursor: 'pointer',
                                            whiteSpace: 'nowrap'
                                          }}
                                          title="Match frame dimensions to photo aspect ratio"
                                        >
                                          🎯 Fit to Image
                                        </button>
                                      </div>
                                    </div>
                                  )}
                                </div>
                              )}

                              {/* Image Optimization & Quality section */}
                              {getResolvedImageSrc(activeElement) && (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '8px', borderTop: '1px solid #3c3c42', paddingTop: '10px' }}>
                                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                      <span style={{ fontSize: '10px', fontWeight: 'bold', color: '#8e8e93', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                        ⚡ Image Optimization
                                      </span>
                                    </div>
                                    <span style={{
                                      fontSize: '9px',
                                      fontWeight: '600',
                                      color: activeElement.optimizeImage !== false ? '#38bdf8' : '#e2e8f0',
                                      backgroundColor: activeElement.optimizeImage !== false ? 'rgba(56, 189, 248, 0.12)' : 'rgba(255, 255, 255, 0.08)',
                                      padding: '2px 6px',
                                      borderRadius: '4px',
                                      border: `1px solid ${activeElement.optimizeImage !== false ? 'rgba(56, 189, 248, 0.3)' : 'rgba(255, 255, 255, 0.15)'}`
                                    }}>
                                      {activeElement.optimizeImage !== false ? '✨ Optimized WebP' : '🖼️ Original Master'}
                                    </span>
                                  </div>

                                  {/* Toggle optimizeImage */}
                                  <label style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                    cursor: 'pointer',
                                    fontSize: '11px',
                                    color: '#ffffff',
                                    userSelect: 'none',
                                    backgroundColor: '#1b1b1e',
                                    padding: '6px 8px',
                                    borderRadius: '4px',
                                    border: '1px solid #3c3c42'
                                  }}>
                                    <input
                                      type="checkbox"
                                      data-testid="toggle-image-optimize-checkbox"
                                      checked={activeElement.optimizeImage !== false}
                                      onChange={(e) => {
                                        saveHistoryState();
                                        const willOptimize = e.target.checked;
                                        updateActiveElement({
                                          optimizeImage: willOptimize,
                                          originalSrc: activeElement.originalSrc || activeElement.src
                                        });
                                      }}
                                      style={{ cursor: 'pointer', accentColor: '#1677ff' }}
                                    />
                                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                                      <span style={{ fontWeight: '600' }}>Optimize for Fast Card Loading</span>
                                      <span style={{ fontSize: '9px', color: '#94a3b8' }}>
                                        Preserves original master as source while rendering fast WebP
                                      </span>
                                    </div>
                                  </label>

                                  {/* Original Source Reference info */}
                                  <div style={{
                                    fontSize: '9px',
                                    color: '#94a3b8',
                                    backgroundColor: '#141416',
                                    padding: '6px 8px',
                                    borderRadius: '4px',
                                    border: '1px solid #2a2a2e',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: '2px'
                                  }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                      <span style={{ color: '#64748b' }}>Master Source:</span>
                                      <span style={{
                                        color: '#cbd5e1',
                                        fontFamily: 'monospace',
                                        maxWidth: '150px',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap'
                                      }} title={activeElement.originalSrc || activeElement.src}>
                                        {(activeElement.originalSrc || activeElement.src || '').split('/').pop().split('?')[0] || 'Embedded'}
                                      </span>
                                    </div>
                                    {(activeElement.originalSize || activeElement.optimizedSize) && (
                                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '2px' }}>
                                        <span style={{ color: '#64748b' }}>Footprint:</span>
                                        <span style={{ color: '#38bdf8', fontWeight: '600' }}>
                                          {activeElement.originalSize ? `${Math.round(activeElement.originalSize / 1024)} KB` : ''}
                                          {activeElement.originalSize && activeElement.optimizedSize ? ' → ' : ''}
                                          {activeElement.optimizedSize ? `${Math.round(activeElement.optimizedSize / 1024)} KB` : ''}
                                          {activeElement.savingsPercent ? ` (-${activeElement.savingsPercent}%)` : ''}
                                        </span>
                                      </div>
                                    )}
                                  </div>

                                  {/* Resample Controls & Actions */}
                                  <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                    <div style={{ display: 'flex', gap: '6px' }}>
                                      <button
                                        type="button"
                                        data-testid="resample-from-original-btn"
                                        disabled={resamplingLoading}
                                        onClick={() => setResampleExpanded(prev => !prev)}
                                        style={{
                                          flex: 1,
                                          backgroundColor: '#232326',
                                          color: '#dfdfe3',
                                          border: '1px solid #3c3c42',
                                          borderRadius: '3px',
                                          padding: '5px 8px',
                                          fontSize: '10px',
                                          fontWeight: '600',
                                          cursor: resamplingLoading ? 'wait' : 'pointer',
                                          display: 'flex',
                                          alignItems: 'center',
                                          justifyContent: 'center',
                                          gap: '4px'
                                        }}
                                        title="Re-sample strictly from original master without generational quality loss"
                                      >
                                        🔄 {resampleExpanded ? 'Close Options' : 'Resample from Original'}
                                      </button>
                                      {activeElement.optimizeImage === false && (
                                        <button
                                          type="button"
                                          onClick={() => {
                                            saveHistoryState();
                                            updateActiveElement({ optimizeImage: true });
                                          }}
                                          style={{
                                            backgroundColor: '#1677ff',
                                            color: '#ffffff',
                                            border: 'none',
                                            borderRadius: '3px',
                                            padding: '5px 8px',
                                            fontSize: '10px',
                                            fontWeight: '600',
                                            cursor: 'pointer'
                                          }}
                                        >
                                          ⚡ Use Optimized
                                        </button>
                                      )}
                                      {activeElement.optimizeImage !== false && (
                                        <button
                                          type="button"
                                          data-testid="revert-to-original-btn"
                                          onClick={() => {
                                            saveHistoryState();
                                            updateActiveElement({
                                              optimizeImage: false,
                                              originalSrc: activeElement.originalSrc || activeElement.src
                                            });
                                          }}
                                          style={{
                                            backgroundColor: '#232326',
                                            color: '#faad14',
                                            border: '1px solid #3c3c42',
                                            borderRadius: '3px',
                                            padding: '5px 8px',
                                            fontSize: '10px',
                                            cursor: 'pointer'
                                          }}
                                          title="View card with uncompressed original master image"
                                        >
                                          Original Master
                                        </button>
                                      )}
                                    </div>

                                    {/* Expandable Resample Customizer */}
                                    {resampleExpanded && (
                                      <div style={{
                                        backgroundColor: '#18181b',
                                        border: '1px solid #3c3c42',
                                        borderRadius: '4px',
                                        padding: '8px',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: '6px'
                                      }}>
                                        <span style={{ fontSize: '9px', fontWeight: 'bold', color: '#38bdf8', textTransform: 'uppercase' }}>
                                          Lossless Source Resampling
                                        </span>
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                                          <label style={{ fontSize: '9px', color: '#94a3b8' }}>Target Resolution</label>
                                          <select
                                            value={resampleMaxDim}
                                            onChange={(e) => setResampleMaxDim(parseInt(e.target.value, 10))}
                                            style={{
                                              backgroundColor: '#121214',
                                              border: '1px solid #3c3c42',
                                              borderRadius: '3px',
                                              color: '#fff',
                                              fontSize: '10px',
                                              padding: '3px 6px',
                                              outline: 'none'
                                            }}
                                          >
                                            <option value={2048}>2048px (Recommended Retina Master)</option>
                                            <option value={1600}>1600px (Mobile Fast)</option>
                                            <option value={1200}>1200px (Standard Display)</option>
                                            <option value={0}>Original Dimensions (100% Full Unscaled)</option>
                                          </select>
                                        </div>

                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                                          <label style={{ fontSize: '9px', color: '#94a3b8' }}>Encoding Quality</label>
                                          <select
                                            value={resampleQuality}
                                            onChange={(e) => setResampleQuality(parseInt(e.target.value, 10))}
                                            style={{
                                              backgroundColor: '#121214',
                                              border: '1px solid #3c3c42',
                                              borderRadius: '3px',
                                              color: '#fff',
                                              fontSize: '10px',
                                              padding: '3px 6px',
                                              outline: 'none'
                                            }}
                                          >
                                            <option value={88}>88% (High Fidelity - Recommended)</option>
                                            <option value={85}>85% (Balanced)</option>
                                            <option value={95}>95% (Ultra Crisp)</option>
                                            <option value={100}>100% (Lossless Master)</option>
                                          </select>
                                        </div>

                                        <button
                                          type="button"
                                          disabled={resamplingLoading}
                                          onClick={() => handleResampleImage(activeElement)}
                                          style={{
                                            backgroundColor: '#1677ff',
                                            color: '#ffffff',
                                            border: 'none',
                                            borderRadius: '3px',
                                            padding: '5px',
                                            fontSize: '10px',
                                            fontWeight: 'bold',
                                            cursor: resamplingLoading ? 'wait' : 'pointer',
                                            marginTop: '4px'
                                          }}
                                        >
                                          {resamplingLoading ? '⏳ Re-sampling Master...' : '⚡ Apply Resample from Source'}
                                        </button>
                                      </div>
                                    )}

                                    {/* Success/Notice Message */}
                                    {resampleMsg && (
                                      <div style={{
                                        fontSize: '9px',
                                        color: '#38bdf8',
                                        backgroundColor: 'rgba(56, 189, 248, 0.1)',
                                        border: '1px solid rgba(56, 189, 248, 0.25)',
                                        borderRadius: '3px',
                                        padding: '4px 6px'
                                      }}>
                                        {resampleMsg}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              )}
                          </div>
                        </div>
                      )}

                      {activeElement.type === 'certify' && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', borderTop: '1px solid #3c3c42', paddingTop: '10px' }}>
                          <div 
                            onClick={() => setCertifySettingsExpanded(prev => !prev)}
                            style={{ 
                              display: 'flex', 
                              alignItems: 'center', 
                              gap: '6px', 
                              cursor: 'pointer',
                              userSelect: 'none'
                            }}
                          >
                            <span style={{ fontSize: '8px', color: '#8e8e93', width: '8px' }}>
                              {certifySettingsExpanded ? '▼' : '▶'}
                            </span>
                            <span style={{ fontSize: '10px', fontWeight: 'bold', color: '#8e8e93', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                              Certification Settings
                            </span>
                          </div>

                          {certifySettingsExpanded && (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', paddingLeft: '14px' }}>
                              {/* Overlay Element Type */}
                              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                <span style={{ fontSize: '10px', color: '#8e8e93' }}>Overlay Element Type</span>
                                <select
                                  value={activeElement.certifyType || 'stamp'}
                                  onChange={(e) => {
                                    const val = e.target.value;
                                    let sizeUpdates = {};
                                    if (val === 'stamp') {
                                      sizeUpdates = { width: 100, height: 100 };
                                    } else if (val === 'rectangle') {
                                      sizeUpdates = { width: 145, height: 85 };
                                    } else if (val === 'ribbon') {
                                      sizeUpdates = { width: 240, height: 40 };
                                    } else if (val === 'watermark') {
                                      sizeUpdates = { width: 220, height: 60 };
                                    }
                                    updateActiveElement({ certifyType: val, ...sizeUpdates });
                                  }}
                                  style={{ width: '100%', backgroundColor: '#1b1b1d', border: '1px solid #3c3c42', borderRadius: '4px', padding: '4px', color: '#fff', fontSize: '11px', outline: 'none' }}
                                >
                                  <option value="stamp">Official Stamp (Circular)</option>
                                  <option value="rectangle">Official Stamp (Rectangular)</option>
                                  <option value="ribbon">Corner Ribbon Banner</option>
                                  <option value="watermark">Text Watermark</option>
                                </select>
                              </div>

                              {/* Certification Overlay Text */}
                              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                <span style={{ fontSize: '10px', color: '#8e8e93' }}>Certification Overlay Text</span>
                                <textarea
                                  value={activeElement.certifyText || ''}
                                  onChange={(e) => updateActiveElement({ certifyText: e.target.value })}
                                  style={{ 
                                    width: '100%', 
                                    backgroundColor: '#1b1b1d', 
                                    border: '1px solid #3c3c42', 
                                    borderRadius: '4px', 
                                    padding: '6px', 
                                    color: '#fff', 
                                    fontSize: '11px', 
                                    outline: 'none',
                                    resize: 'vertical',
                                    fontFamily: 'sans-serif',
                                    height: '50px'
                                  }}
                                  placeholder="TROPHY&#10;CERTIFIED"
                                />
                              </div>

                              {/* Overlay Text Size */}
                              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '10px', color: '#8e8e93' }}>
                                  <span>Overlay Text Size</span>
                                  <span>{activeElement.certifyFontSize !== undefined ? activeElement.certifyFontSize : 100}%</span>
                                </div>
                                <input
                                  type="range"
                                  min="50"
                                  max="250"
                                  value={activeElement.certifyFontSize !== undefined ? activeElement.certifyFontSize : 100}
                                  onChange={(e) => updateActiveElement({ certifyFontSize: parseInt(e.target.value) || 100 })}
                                  style={{ width: '100%', accentColor: '#1677ff', height: '4px', cursor: 'pointer' }}
                                />
                              </div>

                              {/* Rotation Angle */}
                              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '10px', color: '#8e8e93' }}>
                                  <span>Rotation Angle</span>
                                  <span>{activeElement.rotation || 0}°</span>
                                </div>
                                <input
                                  type="range"
                                  min="-180"
                                  max="180"
                                  value={activeElement.rotation || 0}
                                  onChange={(e) => updateActiveElement({ rotation: parseInt(e.target.value) || 0 })}
                                  style={{ width: '100%', accentColor: '#1677ff', height: '4px', cursor: 'pointer' }}
                                />
                              </div>
                            </div>
                          )}
                        </div>
                      )}

                      {activeElement.type === 'qrcode' && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', borderTop: '1px solid #3c3c42', paddingTop: '10px' }}>
                          <div 
                            onClick={() => setQrSettingsExpanded(prev => !prev)}
                            style={{ 
                              display: 'flex', 
                              alignItems: 'center', 
                              gap: '6px', 
                              cursor: 'pointer',
                              userSelect: 'none'
                            }}
                          >
                            <span style={{ fontSize: '8px', color: '#8e8e93', width: '8px' }}>
                              {qrSettingsExpanded ? '▼' : '▶'}
                            </span>
                            <span style={{ fontSize: '10px', fontWeight: 'bold', color: '#8e8e93', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                              QR Code Configuration
                            </span>
                          </div>

                          {qrSettingsExpanded && (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', paddingLeft: '14px' }}>
                              {/* QR Code Action / Data Source */}
                              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                <span style={{ fontSize: '10px', color: '#8e8e93' }}>QR Action / Data Source</span>
                                <select
                                  value={activeElement.qrSource || 'card_link'}
                                  onChange={(e) => updateActiveElement({ qrSource: e.target.value })}
                                  style={{ width: '100%', backgroundColor: '#1b1b1d', border: '1px solid #3c3c42', borderRadius: '4px', padding: '5px', color: '#fff', fontSize: '11px', outline: 'none' }}
                                >
                                  <option value="card_link">🔗 Public Digital Brag Card Link (/c/:id)</option>
                                  <option value="verify">🛡️ Authenticity Verification Link (/verify)</option>
                                  <option value="booking">📅 Operator Booking Link / Demobook</option>
                                  <option value="website">🌐 Tenant / Operator Website</option>
                                  <option value="email">✉️ Enquiries Email (mailto:)</option>
                                  <option value="dynamic">⚡ Dynamic Field / Token Interpolation</option>
                                  <option value="custom">✏️ Custom URL / Text</option>
                                </select>
                              </div>

                              {/* Custom URL / Text or Dynamic Pattern input */}
                              {(activeElement.qrSource === 'custom' || activeElement.qrSource === 'dynamic' || !activeElement.qrSource) && (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <span style={{ fontSize: '10px', color: '#8e8e93' }}>
                                      {activeElement.qrSource === 'dynamic' ? 'Dynamic Template String' : 'Custom URL or Content'}
                                    </span>
                                    {activeElement.qrSource === 'dynamic' && (
                                      <span style={{ fontSize: '9px', color: '#1677ff', fontStyle: 'italic' }}>e.g. $serial_number</span>
                                    )}
                                  </div>
                                  <textarea
                                    value={activeElement.qrValue !== undefined ? activeElement.qrValue : (activeElement.customUrl || '')}
                                    onChange={(e) => updateActiveElement({ qrValue: e.target.value, customUrl: e.target.value })}
                                    style={{
                                      width: '100%',
                                      backgroundColor: '#1b1b1d',
                                      border: '1px solid #3c3c42',
                                      borderRadius: '4px',
                                      padding: '6px',
                                      color: '#fff',
                                      fontSize: '11px',
                                      outline: 'none',
                                      resize: 'vertical',
                                      minHeight: '44px',
                                      fontFamily: 'monospace'
                                    }}
                                    placeholder={
                                      activeElement.qrSource === 'dynamic'
                                        ? 'https://example.com/coin/$coin_id?serial=$serial_number'
                                        : 'https://trophycoin.app/c/$coin_id'
                                    }
                                  />
                                </div>
                              )}

                              {/* Live Evaluated Target URL / Text Preview */}
                              <div style={{
                                backgroundColor: '#141416',
                                border: '1px solid #2d2d30',
                                borderRadius: '4px',
                                padding: '6px 8px',
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '3px'
                              }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                  <span style={{ fontSize: '9px', fontWeight: 'bold', color: '#8e8e93', textTransform: 'uppercase', letterSpacing: '0.4px' }}>
                                    Resolved Target
                                  </span>
                                  <span style={{ fontSize: '9px', color: '#52c41a' }}>● Live</span>
                                </div>
                                <span style={{
                                  fontSize: '10px',
                                  color: '#52c41a',
                                  wordBreak: 'break-all',
                                  fontFamily: 'monospace',
                                  lineHeight: '1.3'
                                }}>
                                  {resolveQrCodeValue(activeElement, {}, datasets, user, orgData, tenantConfig) || '(empty)'}
                                </span>
                              </div>

                              {/* Colors: Foreground & Background */}
                              <div style={{ display: 'flex', gap: '8px' }}>
                                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                  <span style={{ fontSize: '10px', color: '#8e8e93' }}>QR Code Color</span>
                                  {renderColorInputRow(
                                    activeElement.fgColor || '#000000',
                                    (val) => updateActiveElement({ fgColor: val }),
                                    '#000000'
                                  )}
                                </div>
                                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <span style={{ fontSize: '10px', color: '#8e8e93' }}>Background</span>
                                    <button
                                      type="button"
                                      onClick={() => updateActiveElement({ bgColor: activeElement.bgColor === 'transparent' ? '#ffffff' : 'transparent' })}
                                      style={{
                                        border: 'none',
                                        background: 'none',
                                        color: activeElement.bgColor === 'transparent' ? '#1677ff' : '#8e8e93',
                                        fontSize: '9px',
                                        cursor: 'pointer',
                                        padding: 0
                                      }}
                                    >
                                      {activeElement.bgColor === 'transparent' ? 'Solid' : 'Transparent'}
                                    </button>
                                  </div>
                                  {activeElement.bgColor === 'transparent' ? (
                                    <div style={{
                                      height: '24px',
                                      backgroundColor: '#1b1b1d',
                                      border: '1px dashed #3c3c42',
                                      borderRadius: '4px',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      color: '#8e8e93',
                                      fontSize: '10px'
                                    }}>
                                      Transparent
                                    </div>
                                  ) : (
                                    renderColorInputRow(
                                      activeElement.bgColor || '#ffffff',
                                      (val) => updateActiveElement({ bgColor: val }),
                                      '#ffffff'
                                    )
                                  )}
                                </div>
                              </div>

                              {/* Quiet Zone (Margin) & Padding */}
                              <div style={{ display: 'flex', gap: '8px' }}>
                                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '9px', color: '#8e8e93' }}>
                                    <span>Quiet Zone</span>
                                    <span>{activeElement.margin !== undefined ? activeElement.margin : 2} mod</span>
                                  </div>
                                  <input
                                    type="range"
                                    min="0"
                                    max="6"
                                    value={activeElement.margin !== undefined ? activeElement.margin : 2}
                                    onChange={(e) => updateActiveElement({ margin: parseInt(e.target.value) || 0 })}
                                    style={{ width: '100%', accentColor: '#1677ff', height: '4px', cursor: 'pointer' }}
                                  />
                                </div>
                                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '9px', color: '#8e8e93' }}>
                                    <span>Padding</span>
                                    <span>{activeElement.padding || 0}px</span>
                                  </div>
                                  <input
                                    type="range"
                                    min="0"
                                    max="20"
                                    value={activeElement.padding || 0}
                                    onChange={(e) => updateActiveElement({ padding: parseInt(e.target.value) || 0 })}
                                    style={{ width: '100%', accentColor: '#1677ff', height: '4px', cursor: 'pointer' }}
                                  />
                                </div>
                              </div>

                              {/* Interactive Behavior */}
                              <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px', color: '#dfdfe3', cursor: 'pointer' }}>
                                <input
                                  type="checkbox"
                                  checked={activeElement.openOnClick !== false}
                                  onChange={(e) => updateActiveElement({ openOnClick: e.target.checked })}
                                  style={{ cursor: 'pointer' }}
                                />
                                <span>Open link on tap/click (Brag Card & Preview)</span>
                              </label>

                              {/* Rotation Angle Fieldset for QR Code */}
                              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', borderTop: '1px solid #2d2d30', paddingTop: '8px' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                  <span style={{ fontSize: '10px', color: '#8e8e93' }}>Rotation Angle</span>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
                                    <input
                                      type="number"
                                      min="-360"
                                      max="360"
                                      value={activeElement.rotation || 0}
                                      onChange={(e) => updateActiveElement({ rotation: parseInt(e.target.value) || 0 })}
                                      style={{
                                        width: '45px',
                                        backgroundColor: '#141416',
                                        border: '1px solid #3c3c42',
                                        borderRadius: '3px',
                                        padding: '2px 4px',
                                        color: '#fff',
                                        fontSize: '10px',
                                        textAlign: 'right',
                                        outline: 'none'
                                      }}
                                    />
                                    <span style={{ fontSize: '10px', color: '#8e8e93' }}>°</span>
                                  </div>
                                </div>
                                <input
                                  type="range"
                                  min="-180"
                                  max="180"
                                  value={activeElement.rotation || 0}
                                  onChange={(e) => updateActiveElement({ rotation: parseInt(e.target.value) || 0 })}
                                  style={{ width: '100%', accentColor: '#1677ff', height: '4px', cursor: 'pointer' }}
                                />
                                <div style={{ display: 'flex', gap: '4px' }}>
                                  {[
                                    { deg: 0, label: '0°' },
                                    { deg: 90, label: '90°' },
                                    { deg: 180, label: '180°' },
                                    { deg: 270, label: '270°' }
                                  ].map(p => (
                                    <button
                                      key={p.deg}
                                      type="button"
                                      onClick={() => updateActiveElement({ rotation: p.deg })}
                                      style={{
                                        flex: 1,
                                        padding: '3px 0',
                                        fontSize: '9px',
                                        borderRadius: '3px',
                                        border: '1px solid #3c3c42',
                                        backgroundColor: (activeElement.rotation || 0) === p.deg ? '#1677ff' : '#1b1b1d',
                                        color: (activeElement.rotation || 0) === p.deg ? '#ffffff' : '#dfdfe3',
                                        cursor: 'pointer'
                                      }}
                                    >
                                      {p.label}
                                    </button>
                                  ))}
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      )}

                      {activeElement.type === 'datagrid' && (
                        <>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', borderTop: '1px solid #3c3c42', paddingTop: '10px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <span style={{ fontSize: '10px', fontWeight: 'bold', color: '#8e8e93', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Grid Data</span>
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                e.preventDefault();
                                setShowGridDataModal(true);
                              }}
                              style={{
                                backgroundColor: '#232326',
                                color: '#dfdfe3',
                                border: '1px solid #3c3c42',
                                borderRadius: '3px',
                                padding: '2px 8px',
                                fontSize: '10px',
                                fontWeight: 'bold',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '4px'
                              }}
                            >
                              <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                                <path d="M18.5 2.5a2.121 2.121 0 1 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                              </svg>
                              Edit
                            </button>
                          </div>
                          
                          <div style={{
                            backgroundColor: '#141416',
                            border: '1px solid #2d2d30',
                            borderRadius: '4px',
                            overflow: 'hidden',
                            display: 'flex',
                            flexDirection: 'column',
                            maxHeight: '150px',
                            overflowY: 'auto'
                          }}>
                            {activeElement.rows && activeElement.rows.length > 0 ? (
                              activeElement.rows.map((row, idx) => (
                                <div 
                                  key={idx} 
                                  style={{
                                    display: 'flex',
                                    borderBottom: idx < activeElement.rows.length - 1 ? '1px solid #2d2d30' : 'none',
                                    backgroundColor: idx % 2 === 0 ? '#1b1b1d' : '#141416',
                                    padding: '5px 8px',
                                    fontSize: '10px',
                                    lineHeight: '1.4',
                                    alignItems: 'center'
                                  }}
                                >
                                  <div style={{ 
                                    flex: 1, 
                                    fontWeight: '500', 
                                    color: '#a2a2a6',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    paddingRight: '6px',
                                    borderRight: '1px solid #2d2d30'
                                  }}>
                                    {row[0] || <span style={{ color: '#5e5e64', fontStyle: 'italic' }}>no label</span>}
                                  </div>
                                  <div style={{ 
                                    flex: 1.5, 
                                    fontFamily: 'monospace', 
                                    color: '#dfdfe3',
                                    paddingLeft: '8px',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap'
                                  }}>
                                    {row[1] || <span style={{ color: '#5e5e64', fontStyle: 'italic' }}>empty</span>}
                                  </div>
                                </div>
                              ))
                            ) : (
                              <div style={{ padding: '8px', color: '#5e5e64', fontSize: '10px', fontStyle: 'italic', textAlign: 'center' }}>
                                No Rows Configured
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Label Column Styling collapsible panel */}
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', borderTop: '1px solid #3c3c42', paddingTop: '10px', marginTop: '4px' }}>
                          <div 
                            onClick={() => setLabelStylingExpanded(prev => !prev)}
                            style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', userSelect: 'none' }}
                          >
                            <span style={{ fontSize: '8px', color: '#8e8e93', width: '8px' }}>
                              {labelStylingExpanded ? '▼' : '▶'}
                            </span>
                            <span style={{ fontSize: '10px', fontWeight: 'bold', color: '#8e8e93', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                              Label Column Styling
                            </span>
                          </div>

                          {labelStylingExpanded && (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', paddingLeft: '14px' }}>
                              {/* Modify Text (Label Transform) */}
                              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', marginBottom: '4px' }}>
                                <span style={{ fontSize: '9px', color: '#8e8e93' }}>Modify Text</span>
                                <div style={{ display: 'flex', gap: '4px' }}>
                                  {[
                                    { value: undefined, label: 'None' },
                                    { value: 'uppercase', label: 'ABC' },
                                    { value: 'lowercase', label: 'abc' },
                                    { value: 'capitalize', label: 'Abc' }
                                  ].map(opt => {
                                    const isSelected = activeElement.labelTransformModifier === opt.value;
                                    return (
                                      <button
                                        key={opt.label}
                                        type="button"
                                        onClick={() => updateActiveElement({ labelTransformModifier: opt.value })}
                                        style={{
                                          flex: 1,
                                          padding: '4px 0',
                                          fontSize: '10px',
                                          fontWeight: 'bold',
                                          borderRadius: '4px',
                                          border: '1px solid #3c3c42',
                                          backgroundColor: isSelected ? '#1677ff' : '#1b1b1d',
                                          color: isSelected ? '#ffffff' : '#dfdfe3',
                                          cursor: 'pointer',
                                          transition: 'all 0.1s'
                                        }}
                                      >
                                        {opt.label}
                                      </button>
                                    );
                                  })}
                                </div>
                              </div>
                              {/* Label Font Family & Size */}
                              <div style={{ display: 'flex', gap: '8px' }}>
                                <div style={{ flex: 1.5, display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                  <span style={{ fontSize: '10px', color: '#8e8e93' }}>Font Family</span>
                                  <select
                                    value={activeElement.labelFontFamily || activeElement.fontFamily || 'Arial'}
                                    onChange={(e) => updateActiveElement({ labelFontFamily: e.target.value })}
                                    style={{ width: '100%', backgroundColor: '#1b1b1d', border: '1px solid #3c3c42', borderRadius: '4px', padding: '4px', color: '#fff', fontSize: '11px', outline: 'none' }}
                                  >
                                    {['Arial', 'Helvetica', 'Times New Roman', 'Georgia', 'Courier New', 'Verdana', 'Garamond', 'Bookman', 'Trebuchet MS', 'Impact'].map(font => (
                                      <option key={font} value={font}>{font}</option>
                                    ))}
                                  </select>
                                </div>
                                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                  <span style={{ fontSize: '10px', color: '#8e8e93' }}>Font Size</span>
                                  <input
                                    type="number"
                                    value={activeElement.labelFontSize !== undefined ? activeElement.labelFontSize : (activeElement.fontSize || 11)}
                                    onChange={(e) => updateActiveElement({ labelFontSize: parseInt(e.target.value) || 1 })}
                                    style={{ width: '100%', backgroundColor: '#1b1b1d', border: '1px solid #3c3c42', borderRadius: '4px', padding: '4px', color: '#fff', fontSize: '11px', outline: 'none' }}
                                  />
                                </div>
                              </div>

                              {/* Label Weight, Style & Letter Spacing */}
                              <div style={{ display: 'flex', gap: '8px' }}>
                                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                  <span style={{ fontSize: '10px', color: '#8e8e93' }}>Weight</span>
                                  <select
                                    value={activeElement.labelFontWeight || activeElement.fontWeight || '400'}
                                    onChange={(e) => updateActiveElement({ labelFontWeight: e.target.value })}
                                    style={{ width: '100%', backgroundColor: '#1b1b1d', border: '1px solid #3c3c42', borderRadius: '4px', padding: '4px', color: '#fff', fontSize: '11px', outline: 'none' }}
                                  >
                                    {['100', '200', '300', '400', '500', '600', '700', '800', '900'].map(w => (
                                      <option key={w} value={w}>{w}</option>
                                    ))}
                                  </select>
                                </div>
                                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                  <span style={{ fontSize: '10px', color: '#8e8e93' }}>Style</span>
                                  <select
                                    value={activeElement.labelFontStyle || activeElement.fontStyle || 'normal'}
                                    onChange={(e) => updateActiveElement({ labelFontStyle: e.target.value })}
                                    style={{ width: '100%', backgroundColor: '#1b1b1d', border: '1px solid #3c3c42', borderRadius: '4px', padding: '4px', color: '#fff', fontSize: '11px', outline: 'none' }}
                                  >
                                    <option value="normal">Normal</option>
                                    <option value="italic">Italic</option>
                                  </select>
                                </div>
                                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                  <span style={{ fontSize: '10px', color: '#8e8e93' }}>Spacing</span>
                                  <input
                                    type="number"
                                    value={activeElement.labelLetterSpacing !== undefined ? activeElement.labelLetterSpacing : (activeElement.letterSpacing || 0)}
                                    onChange={(e) => updateActiveElement({ labelLetterSpacing: parseInt(e.target.value) || 0 })}
                                    style={{ width: '100%', backgroundColor: '#1b1b1d', border: '1px solid #3c3c42', borderRadius: '4px', padding: '4px', color: '#fff', fontSize: '11px', outline: 'none' }}
                                  />
                                </div>
                              </div>

                              {/* Label Decoration */}
                              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                <span style={{ fontSize: '10px', color: '#8e8e93' }}>Text Decoration</span>
                                <select
                                  value={activeElement.labelDecoration || activeElement.textDecoration || 'none'}
                                  onChange={(e) => updateActiveElement({ labelDecoration: e.target.value })}
                                  style={{ width: '100%', backgroundColor: '#1b1b1d', border: '1px solid #3c3c42', borderRadius: '4px', padding: '4px', color: '#fff', fontSize: '11px', outline: 'none' }}
                                >
                                  <option value="none">None</option>
                                  <option value="underline">Underline</option>
                                  <option value="line-through">Line-Through</option>
                                </select>
                              </div>

                              {/* Label Text Fill Color controls */}
                              <div style={{ display: 'flex', gap: '8px' }}>
                                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                  <span style={{ fontSize: '10px', color: '#8e8e93' }}>Fill Type</span>
                                  <select
                                    value={activeElement.labelColorType || 'solid'}
                                    onChange={(e) => updateActiveElement({ labelColorType: e.target.value })}
                                    style={{ width: '100%', backgroundColor: '#1b1b1d', border: '1px solid #3c3c42', borderRadius: '4px', padding: '4px', color: '#fff', fontSize: '11px', outline: 'none' }}
                                  >
                                    <option value="solid">Solid Color</option>
                                    <option value="gradient">Gradient</option>
                                  </select>
                                </div>
                                <div style={{ flex: 1.5, display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                  <span style={{ fontSize: '10px', color: '#8e8e93' }}>
                                    {activeElement.labelColorType === 'gradient' ? 'Gradient (Click to Edit)' : 'Color'}
                                  </span>
                                  {activeElement.labelColorType === 'gradient' ? (
                                    <div 
                                      onClick={() => {
                                        const defaultGrad = {
                                          type: 'linear',
                                          angle: 90,
                                          stops: [
                                            { color: '#ffffff', position: 0, opacity: 1 },
                                            { color: '#a2a2a8', position: 100, opacity: 1 }
                                          ]
                                        };
                                        setGradientState(activeElement.labelGradient || defaultGrad);
                                        setEditingGradientTarget('datagridLabel');
                                        setSelectedStopIndex(0);
                                        setShowGradientModal(true);
                                      }}
                                      style={{
                                        width: '100%',
                                        height: '24px',
                                        borderRadius: '4px',
                                        border: '1px solid #3c3c42',
                                        background: activeElement.labelColor || 'linear-gradient(90deg, #ffffff 0%, #000000 100%)',
                                        cursor: 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontSize: '9px',
                                        color: '#ffffff',
                                        textShadow: '0 1px 2px rgba(0,0,0,0.8)',
                                        fontWeight: 'bold',
                                        userSelect: 'none'
                                      }}
                                    >
                                      Edit Gradient
                                    </div>
                                  ) : (
                                    <div style={{ display: 'flex', gap: '4px' }}>
                                      <input
                                        type="color"
                                        value={activeElement.labelColor || activeElement.color || '#ffffff'}
                                        onChange={(e) => updateActiveElement({ labelColor: e.target.value })}
                                        style={{ border: 'none', background: 'transparent', width: '28px', height: '24px', padding: 0, cursor: 'pointer' }}
                                      />
                                      <input
                                        type="text"
                                        value={activeElement.labelColor || activeElement.color || '#ffffff'}
                                        onChange={(e) => updateActiveElement({ labelColor: e.target.value })}
                                        style={{ flex: 1, backgroundColor: '#1b1b1d', border: '1px solid #3c3c42', borderRadius: '4px', padding: '4px', color: '#fff', fontSize: '10px', outline: 'none' }}
                                      />
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          )}
                        </div>

                        {/* Value Column Styling collapsible panel */}
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', borderTop: '1px solid #3c3c42', paddingTop: '10px', marginTop: '4px' }}>
                          <div 
                            onClick={() => setValueStylingExpanded(prev => !prev)}
                            style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', userSelect: 'none' }}
                          >
                            <span style={{ fontSize: '8px', color: '#8e8e93', width: '8px' }}>
                              {valueStylingExpanded ? '▼' : '▶'}
                            </span>
                            <span style={{ fontSize: '10px', fontWeight: 'bold', color: '#8e8e93', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                              Value Column Styling
                            </span>
                          </div>

                          {valueStylingExpanded && (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', paddingLeft: '14px' }}>
                              {/* Modify Text (Value Transform) */}
                              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', marginBottom: '4px' }}>
                                <span style={{ fontSize: '9px', color: '#8e8e93' }}>Modify Text</span>
                                <div style={{ display: 'flex', gap: '4px' }}>
                                  {[
                                    { value: undefined, label: 'None' },
                                    { value: 'uppercase', label: 'ABC' },
                                    { value: 'lowercase', label: 'abc' },
                                    { value: 'capitalize', label: 'Abc' }
                                  ].map(opt => {
                                    const isSelected = activeElement.valueTransformModifier === opt.value;
                                    return (
                                      <button
                                        key={opt.label}
                                        type="button"
                                        onClick={() => updateActiveElement({ valueTransformModifier: opt.value })}
                                        style={{
                                          flex: 1,
                                          padding: '4px 0',
                                          fontSize: '10px',
                                          fontWeight: 'bold',
                                          borderRadius: '4px',
                                          border: '1px solid #3c3c42',
                                          backgroundColor: isSelected ? '#1677ff' : '#1b1b1d',
                                          color: isSelected ? '#ffffff' : '#dfdfe3',
                                          cursor: 'pointer',
                                          transition: 'all 0.1s'
                                        }}
                                      >
                                        {opt.label}
                                      </button>
                                    );
                                  })}
                                </div>
                              </div>
                              {/* Value Font Family & Size */}
                              <div style={{ display: 'flex', gap: '8px' }}>
                                <div style={{ flex: 1.5, display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                  <span style={{ fontSize: '10px', color: '#8e8e93' }}>Font Family</span>
                                  <select
                                    value={activeElement.valueFontFamily || activeElement.fontFamily || 'Arial'}
                                    onChange={(e) => updateActiveElement({ valueFontFamily: e.target.value })}
                                    style={{ width: '100%', backgroundColor: '#1b1b1d', border: '1px solid #3c3c42', borderRadius: '4px', padding: '4px', color: '#fff', fontSize: '11px', outline: 'none' }}
                                  >
                                    {['Arial', 'Helvetica', 'Times New Roman', 'Georgia', 'Courier New', 'Verdana', 'Garamond', 'Bookman', 'Trebuchet MS', 'Impact'].map(font => (
                                      <option key={font} value={font}>{font}</option>
                                    ))}
                                  </select>
                                </div>
                                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                  <span style={{ fontSize: '10px', color: '#8e8e93' }}>Font Size</span>
                                  <input
                                    type="number"
                                    value={activeElement.valueFontSize !== undefined ? activeElement.valueFontSize : (activeElement.fontSize || 11)}
                                    onChange={(e) => updateActiveElement({ valueFontSize: parseInt(e.target.value) || 1 })}
                                    style={{ width: '100%', backgroundColor: '#1b1b1d', border: '1px solid #3c3c42', borderRadius: '4px', padding: '4px', color: '#fff', fontSize: '11px', outline: 'none' }}
                                  />
                                </div>
                              </div>

                              {/* Value Weight, Style & Letter Spacing */}
                              <div style={{ display: 'flex', gap: '8px' }}>
                                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                  <span style={{ fontSize: '10px', color: '#8e8e93' }}>Weight</span>
                                  <select
                                    value={activeElement.valueFontWeight || activeElement.fontWeight || '700'}
                                    onChange={(e) => updateActiveElement({ valueFontWeight: e.target.value })}
                                    style={{ width: '100%', backgroundColor: '#1b1b1d', border: '1px solid #3c3c42', borderRadius: '4px', padding: '4px', color: '#fff', fontSize: '11px', outline: 'none' }}
                                  >
                                    {['100', '200', '300', '400', '500', '600', '700', '800', '900'].map(w => (
                                      <option key={w} value={w}>{w}</option>
                                    ))}
                                  </select>
                                </div>
                                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                  <span style={{ fontSize: '10px', color: '#8e8e93' }}>Style</span>
                                  <select
                                    value={activeElement.valueFontStyle || activeElement.fontStyle || 'normal'}
                                    onChange={(e) => updateActiveElement({ valueFontStyle: e.target.value })}
                                    style={{ width: '100%', backgroundColor: '#1b1b1d', border: '1px solid #3c3c42', borderRadius: '4px', padding: '4px', color: '#fff', fontSize: '11px', outline: 'none' }}
                                  >
                                    <option value="normal">Normal</option>
                                    <option value="italic">Italic</option>
                                  </select>
                                </div>
                                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                  <span style={{ fontSize: '10px', color: '#8e8e93' }}>Spacing</span>
                                  <input
                                    type="number"
                                    value={activeElement.valueLetterSpacing !== undefined ? activeElement.valueLetterSpacing : (activeElement.letterSpacing || 0)}
                                    onChange={(e) => updateActiveElement({ valueLetterSpacing: parseInt(e.target.value) || 0 })}
                                    style={{ width: '100%', backgroundColor: '#1b1b1d', border: '1px solid #3c3c42', borderRadius: '4px', padding: '4px', color: '#fff', fontSize: '11px', outline: 'none' }}
                                  />
                                </div>
                              </div>

                              {/* Value Decoration */}
                              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                <span style={{ fontSize: '10px', color: '#8e8e93' }}>Text Decoration</span>
                                <select
                                  value={activeElement.valueDecoration || activeElement.textDecoration || 'none'}
                                  onChange={(e) => updateActiveElement({ valueDecoration: e.target.value })}
                                  style={{ width: '100%', backgroundColor: '#1b1b1d', border: '1px solid #3c3c42', borderRadius: '4px', padding: '4px', color: '#fff', fontSize: '11px', outline: 'none' }}
                                >
                                  <option value="none">None</option>
                                  <option value="underline">Underline</option>
                                  <option value="line-through">Line-Through</option>
                                </select>
                              </div>

                              {/* Value Text Fill Color controls */}
                              <div style={{ display: 'flex', gap: '8px' }}>
                                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                  <span style={{ fontSize: '10px', color: '#8e8e93' }}>Fill Type</span>
                                  <select
                                    value={activeElement.valueColorType || 'solid'}
                                    onChange={(e) => updateActiveElement({ valueColorType: e.target.value })}
                                    style={{ width: '100%', backgroundColor: '#1b1b1d', border: '1px solid #3c3c42', borderRadius: '4px', padding: '4px', color: '#fff', fontSize: '11px', outline: 'none' }}
                                  >
                                    <option value="solid">Solid Color</option>
                                    <option value="gradient">Gradient</option>
                                  </select>
                                </div>
                                <div style={{ flex: 1.5, display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                  <span style={{ fontSize: '10px', color: '#8e8e93' }}>
                                    {activeElement.valueColorType === 'gradient' ? 'Gradient (Click to Edit)' : 'Color'}
                                  </span>
                                  {activeElement.valueColorType === 'gradient' ? (
                                    <div 
                                      onClick={() => {
                                        const defaultGrad = {
                                          type: 'linear',
                                          angle: 90,
                                          stops: [
                                            { color: '#ffffff', position: 0, opacity: 1 },
                                            { color: '#a2a2a8', position: 100, opacity: 1 }
                                          ]
                                        };
                                        setGradientState(activeElement.valueGradient || defaultGrad);
                                        setEditingGradientTarget('datagridValue');
                                        setSelectedStopIndex(0);
                                        setShowGradientModal(true);
                                      }}
                                      style={{
                                        width: '100%',
                                        height: '24px',
                                        borderRadius: '4px',
                                        border: '1px solid #3c3c42',
                                        background: activeElement.valueColor || 'linear-gradient(90deg, #ffffff 0%, #000000 100%)',
                                        cursor: 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontSize: '9px',
                                        color: '#ffffff',
                                        textShadow: '0 1px 2px rgba(0,0,0,0.8)',
                                        fontWeight: 'bold',
                                        userSelect: 'none'
                                      }}
                                    >
                                      Edit Gradient
                                    </div>
                                  ) : (
                                    <div style={{ display: 'flex', gap: '4px' }}>
                                      <input
                                        type="color"
                                        value={activeElement.valueColor || activeElement.color || '#ffffff'}
                                        onChange={(e) => updateActiveElement({ valueColor: e.target.value })}
                                        style={{ border: 'none', background: 'transparent', width: '28px', height: '24px', padding: 0, cursor: 'pointer' }}
                                      />
                                      <input
                                        type="text"
                                        value={activeElement.valueColor || activeElement.color || '#ffffff'}
                                        onChange={(e) => updateActiveElement({ valueColor: e.target.value })}
                                        style={{ flex: 1, backgroundColor: '#1b1b1d', border: '1px solid #3c3c42', borderRadius: '4px', padding: '4px', color: '#fff', fontSize: '10px', outline: 'none' }}
                                      />
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      </>
                    )}

                      {/* DYNAMIC DATA PANEL */}
                      {activeElement.type === 'text' && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', borderTop: '1px solid #3c3c42', paddingTop: '10px', marginTop: '4px' }}>
                          <div 
                            onClick={() => setDynamicDataExpanded(prev => !prev)}
                            style={{ 
                              display: 'flex', 
                              alignItems: 'center', 
                              gap: '6px', 
                              cursor: 'pointer',
                              userSelect: 'none'
                            }}
                          >
                            <span style={{ fontSize: '8px', color: '#8e8e93', width: '8px' }}>
                              {dynamicDataExpanded ? '▼' : '▶'}
                            </span>
                            <span style={{ fontSize: '10px', fontWeight: 'bold', color: '#8e8e93', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                              Dynamic Data
                            </span>
                          </div>

                          {dynamicDataExpanded && (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', paddingLeft: '14px' }}>
                              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <span style={{ fontSize: '10px', color: '#8e8e93', fontWeight: 'bold' }}>Text Sequence</span>
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    e.preventDefault();
                                    setShowSequenceModal(true);
                                  }}
                                  style={{
                                    backgroundColor: '#232326',
                                    color: '#dfdfe3',
                                    border: '1px solid #3c3c42',
                                    borderRadius: '3px',
                                    padding: '2px 8px',
                                    fontSize: '10px',
                                    fontWeight: 'bold',
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '4px'
                                  }}
                                >
                                  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                                    <path d="M18.5 2.5a2.121 2.121 0 1 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                                  </svg>
                                  Edit
                                </button>
                              </div>
                              <div style={{
                                backgroundColor: '#141416',
                                border: '1px solid #3c3c42',
                                borderRadius: '4px',
                                padding: '8px',
                                color: '#e6a23c',
                                fontSize: '11px',
                                fontFamily: 'monospace',
                                minHeight: '32px',
                                wordBreak: 'break-all',
                                display: 'flex',
                                alignItems: 'center'
                              }}>
                                {activeElement.text || 'Empty Sequence'}
                              </div>
                            </div>
                          )}
                        </div>
                      )}

                      {/* CHARACTER PROPERTIES PANEL */}
                      {(activeElement.type === 'text') && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', borderTop: '1px solid #3c3c42', paddingTop: '10px', marginTop: '4px' }}>
                          <div 
                            onClick={() => setCharacterExpanded(prev => !prev)}
                            style={{ 
                              display: 'flex', 
                              alignItems: 'center', 
                              gap: '6px', 
                              cursor: 'pointer',
                              userSelect: 'none'
                            }}
                          >
                            <span style={{ fontSize: '8px', color: '#8e8e93', width: '8px' }}>
                              {characterExpanded ? '▼' : '▶'}
                            </span>
                            <span style={{ fontSize: '10px', fontWeight: 'bold', color: '#8e8e93', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                              Character
                            </span>
                          </div>

                          {characterExpanded && (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', paddingLeft: '14px' }}>
                              {/* Modify Text (Text Transform) */}
                              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', marginBottom: '4px' }}>
                                <span style={{ fontSize: '9px', color: '#8e8e93' }}>Modify Text</span>
                                <div style={{ display: 'flex', gap: '4px' }}>
                                  {[
                                    { value: undefined, label: 'None' },
                                    { value: 'uppercase', label: 'ABC' },
                                    { value: 'lowercase', label: 'abc' },
                                    { value: 'capitalize', label: 'Abc' }
                                  ].map(opt => {
                                    const isSelected = activeElement.textTransformModifier === opt.value;
                                    return (
                                      <button
                                        key={opt.label}
                                        type="button"
                                        onClick={() => updateActiveElement({ textTransformModifier: opt.value })}
                                        style={{
                                          flex: 1,
                                          padding: '4px 0',
                                          fontSize: '10px',
                                          fontWeight: 'bold',
                                          borderRadius: '4px',
                                          border: '1px solid #3c3c42',
                                          backgroundColor: isSelected ? '#1677ff' : '#1b1b1d',
                                          color: isSelected ? '#ffffff' : '#dfdfe3',
                                          cursor: 'pointer',
                                          transition: 'all 0.1s'
                                        }}
                                      >
                                        {opt.label}
                                      </button>
                                    );
                                  })}
                                </div>
                              </div>

                              {/* 1. Font Family & Style (Row 1) */}
                              <div style={{ display: 'flex', gap: '8px' }}>
                                <div style={{ flex: 1.5, display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                  <span style={{ fontSize: '10px', color: '#8e8e93' }}>Font Family</span>
                                  <select
                                    value={activeElement.fontFamily || 'Arial'}
                                    onChange={(e) => updateActiveElement({ fontFamily: e.target.value })}
                                    style={{ width: '100%', backgroundColor: '#1b1b1d', border: '1px solid #3c3c42', borderRadius: '4px', padding: '4px', color: '#fff', fontSize: '11px', outline: 'none' }}
                                  >
                                    {['Arial', 'Helvetica', 'Times New Roman', 'Georgia', 'Courier New', 'Verdana', 'Garamond', 'Bookman', 'Trebuchet MS', 'Impact'].map(font => (
                                      <option key={font} value={font}>{font}</option>
                                    ))}
                                  </select>
                                </div>
                                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                  <span style={{ fontSize: '10px', color: '#8e8e93' }}>Weight</span>
                                  <select
                                    value={activeElement.fontWeight || '400'}
                                    onChange={(e) => updateActiveElement({ fontWeight: e.target.value })}
                                    style={{ width: '100%', backgroundColor: '#1b1b1d', border: '1px solid #3c3c42', borderRadius: '4px', padding: '4px', color: '#fff', fontSize: '11px', outline: 'none' }}
                                  >
                                    <option value="400">Regular (400)</option>
                                    <option value="500">Medium (500)</option>
                                    <option value="600">Semi-Bold (600)</option>
                                    <option value="700">Bold (700)</option>
                                    <option value="800">Extra-Bold (800)</option>
                                  </select>
                                </div>
                              </div>

                              {/* 2. Font Size, Alignment, and Italic Toggle (Row 2) */}
                              <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-end' }}>
                                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '10px', color: '#8e8e93' }}>
                                    <span>Font Size</span>
                                    <span>{activeElement.fontSize || 12}px</span>
                                  </div>
                                  <input
                                    type="range"
                                    min="6"
                                    max="72"
                                    value={activeElement.fontSize || 12}
                                    onChange={(e) => updateActiveElement({ fontSize: parseInt(e.target.value) || 12 })}
                                    style={{ width: '100%', accentColor: '#1677ff', height: '4px', cursor: 'pointer' }}
                                  />
                                </div>
                                <div style={{ display: 'flex', gap: '4px', height: '24px' }}>
                                  {/* Italic toggle button */}
                                  <button
                                    onClick={() => updateActiveElement({ fontStyle: activeElement.fontStyle === 'italic' ? 'normal' : 'italic' })}
                                    style={{
                                      width: '24px',
                                      height: '24px',
                                      fontSize: '11px',
                                      fontWeight: 'bold',
                                      fontStyle: 'italic',
                                      border: '1px solid #3c3c42',
                                      borderRadius: '3px',
                                      backgroundColor: activeElement.fontStyle === 'italic' ? '#1677ff' : '#1b1b1d',
                                      color: '#ffffff',
                                      cursor: 'pointer'
                                    }}
                                    title="Italic"
                                  >
                                    I
                                  </button>
                                </div>
                                {activeElement.type === 'text' && (
                                  <div style={{ display: 'flex', gap: '2px' }}>
                                    {['left', 'center', 'right'].map(align => (
                                      <button
                                        key={align}
                                        onClick={() => updateActiveElement({ alignment: align })}
                                        style={{
                                          padding: '4px 6px',
                                          border: '1px solid #3c3c42',
                                          borderRadius: '3px',
                                          backgroundColor: (activeElement.alignment || 'left') === align ? '#1677ff' : '#1b1b1d',
                                          color: '#ffffff',
                                          cursor: 'pointer',
                                          display: 'flex',
                                          alignItems: 'center',
                                          justifyContent: 'center',
                                          width: '24px',
                                          height: '24px'
                                        }}
                                        title={`${align} Align`}
                                      >
                                        {align === 'left' ? (
                                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                                            <line x1="3" y1="6" x2="21" y2="6" />
                                            <line x1="3" y1="12" x2="15" y2="12" />
                                            <line x1="3" y1="18" x2="18" y2="18" />
                                          </svg>
                                        ) : align === 'center' ? (
                                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                                            <line x1="3" y1="6" x2="21" y2="6" />
                                            <line x1="6" y1="12" x2="18" y2="12" />
                                            <line x1="4" y1="18" x2="20" y2="18" />
                                          </svg>
                                        ) : (
                                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                                            <line x1="3" y1="6" x2="21" y2="6" />
                                            <line x1="9" y1="12" x2="21" y2="12" />
                                            <line x1="6" y1="18" x2="21" y2="18" />
                                          </svg>
                                        )}
                                      </button>
                                    ))}
                                  </div>
                                )}
                              </div>

                              {/* 3. Decoration Fieldset */}
                              <fieldset style={{
                                border: '1px solid #3c3c42',
                                borderRadius: '4px',
                                padding: '8px 10px',
                                margin: '6px 0 0 0',
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '8px'
                              }}>
                                <legend style={{ fontSize: '9px', fontWeight: 'bold', color: '#8e8e93', padding: '0 4px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                  Decoration
                                </legend>

                                {/* Style Decoration Buttons */}
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                  <span style={{ fontSize: '9px', color: '#8e8e93' }}>Text Decoration</span>
                                  <div style={{ display: 'flex', gap: '4px' }}>
                                    {['none', 'underline', 'line-through'].map(dec => (
                                      <button
                                        key={dec}
                                        onClick={() => updateActiveElement({ textDecoration: dec })}
                                        style={{
                                          flex: 1,
                                          padding: '4px 0',
                                          fontSize: '10px',
                                          border: '1px solid #3c3c42',
                                          borderRadius: '3px',
                                          backgroundColor: (activeElement.textDecoration || 'none') === dec ? '#1677ff' : '#1b1b1d',
                                          color: '#ffffff',
                                          cursor: 'pointer',
                                          textTransform: 'capitalize'
                                        }}
                                      >
                                        {dec === 'line-through' ? 'Strikethrough' : dec === 'none' ? 'Normal' : dec}
                                      </button>
                                    ))}
                                  </div>
                                </div>

                                {/* Text Stroke Settings */}
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', borderTop: '1px solid #232326', paddingTop: '6px', marginTop: '2px' }}>
                                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '9px', color: '#8e8e93' }}>
                                    <span>Stroke Thickness</span>
                                    <span>{activeElement.textStrokeWidth || 0}px</span>
                                  </div>
                                  <input
                                    type="range"
                                    min="0"
                                    max="10"
                                    value={activeElement.textStrokeWidth || 0}
                                    onChange={(e) => updateActiveElement({ textStrokeWidth: parseInt(e.target.value) || 0 })}
                                    style={{ width: '100%', accentColor: '#1677ff', height: '4px', cursor: 'pointer' }}
                                  />
                                </div>

                                {/* Text Stroke Fill (only if thickness > 0) */}
                                {(activeElement.textStrokeWidth || 0) > 0 && (
                                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                    {/* Stroke Fill Type */}
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                      <span style={{ fontSize: '9px', color: '#8e8e93' }}>Stroke Fill Type</span>
                                      <select
                                        value={activeElement.textStrokeColorType || 'solid'}
                                        onChange={(e) => {
                                          const val = e.target.value;
                                          const updates = { textStrokeColorType: val };
                                          if (val === 'gradient') {
                                            if (!activeElement.textStrokeGradient) {
                                              updates.textStrokeGradient = {
                                                type: 'linear',
                                                angle: 90,
                                                stops: [
                                                  { color: '#ffffff', position: 0, opacity: 1 },
                                                  { color: '#000000', position: 100, opacity: 1 }
                                                ]
                                              };
                                            }
                                            if (!activeElement.textStrokeColor || (!activeElement.textStrokeColor.startsWith('linear') && !activeElement.textStrokeColor.startsWith('radial'))) {
                                              updates.textStrokeColor = 'linear-gradient(90deg, rgba(255, 255, 255, 1) 0%, rgba(0, 0, 0, 1) 100%)';
                                            }
                                          } else {
                                            const firstStopColor = activeElement.textStrokeGradient?.stops?.[0]?.color || '#ffffff';
                                            updates.textStrokeColor = firstStopColor.startsWith('#') ? firstStopColor : '#ffffff';
                                          }
                                          updateActiveElement(updates);
                                        }}
                                        style={{ width: '100%', backgroundColor: '#1b1b1d', border: '1px solid #3c3c42', borderRadius: '4px', padding: '4px', color: '#fff', fontSize: '11px', outline: 'none' }}
                                      >
                                        <option value="solid">Solid Color</option>
                                        <option value="gradient">Gradient</option>
                                      </select>
                                    </div>

                                    {/* Stroke Color Color Picker / Gradient Click Strip */}
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                      <span style={{ fontSize: '9px', color: '#8e8e93' }}>
                                        {activeElement.textStrokeColorType === 'gradient' ? 'Gradient Fill (Click to Edit)' : 'Stroke Color'}
                                      </span>
                                      {activeElement.textStrokeColorType === 'gradient' ? (
                                        <div 
                                          onClick={() => {
                                            const defaultGrad = {
                                              type: 'linear',
                                              angle: 90,
                                              stops: [
                                                { color: '#ffffff', position: 0, opacity: 1 },
                                                { color: '#000000', position: 100, opacity: 1 }
                                              ]
                                            };
                                            setGradientState(activeElement.textStrokeGradient || defaultGrad);
                                            setSelectedStopIndex(0);
                                            setEditingGradientTarget('textStroke');
                                            setShowGradientModal(true);
                                          }}
                                          style={{
                                            width: '100%',
                                            height: '24px',
                                            borderRadius: '4px',
                                            border: '1px solid #3c3c42',
                                            background: activeElement.textStrokeColor || 'linear-gradient(90deg, #ffffff 0%, #000000 100%)',
                                            cursor: 'pointer',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            fontSize: '9px',
                                            color: '#ffffff',
                                            textShadow: '0 1px 2px rgba(0,0,0,0.8)',
                                            fontWeight: 'bold',
                                            userSelect: 'none'
                                          }}
                                        >
                                          Edit Stroke Gradient
                                        </div>
                                      ) : (
                                        renderColorInputRow(
                                          activeElement.textStrokeColor,
                                          (val) => updateActiveElement({ textStrokeColor: val })
                                        )
                                      )}
                                    </div>
                                  </div>
                                )}
                              </fieldset>

                              {/* 4. Always Visible Fieldset for Position & Transform */}
                              <fieldset style={{
                                border: '1px solid #3c3c42',
                                borderRadius: '4px',
                                padding: '8px 10px',
                                margin: '6px 0 0 0',
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '8px'
                              }}>
                                <legend style={{ fontSize: '9px', fontWeight: 'bold', color: '#8e8e93', padding: '0 4px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                  Position & Transform
                                </legend>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                  {/* Tracking (Letter Spacing) & Leading (Line Height) */}
                                  <div style={{ display: 'flex', gap: '8px' }}>
                                    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '9px', color: '#8e8e93' }}>
                                        <span>Tracking</span>
                                        <span>{activeElement.letterSpacing || 0}px</span>
                                      </div>
                                      <input
                                        type="range"
                                        min="-5"
                                        max="15"
                                        value={activeElement.letterSpacing || 0}
                                        onChange={(e) => updateActiveElement({ letterSpacing: parseInt(e.target.value) || 0 })}
                                        style={{ width: '100%', accentColor: '#1677ff', height: '4px', cursor: 'pointer' }}
                                      />
                                    </div>
                                    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '9px', color: '#8e8e93' }}>
                                        <span>Leading</span>
                                        <span>{activeElement.lineHeight || 1.1}</span>
                                      </div>
                                      <input
                                        type="range"
                                        min="0.5"
                                        max="3.0"
                                        step="0.1"
                                        value={activeElement.lineHeight || 1.1}
                                        onChange={(e) => updateActiveElement({ lineHeight: parseFloat(e.target.value) || 1.1 })}
                                        style={{ width: '100%', accentColor: '#1677ff', height: '4px', cursor: 'pointer' }}
                                      />
                                    </div>
                                  </div>

                                  {/* Baseline Shift & Shear (Slant) */}
                                  <div style={{ display: 'flex', gap: '8px' }}>
                                    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '9px', color: '#8e8e93' }}>
                                        <span>Baseline Shift</span>
                                        <span>{activeElement.baselineShift || 0}px</span>
                                      </div>
                                      <input
                                        type="range"
                                        min="-20"
                                        max="20"
                                        value={activeElement.baselineShift || 0}
                                        onChange={(e) => updateActiveElement({ baselineShift: parseInt(e.target.value) || 0 })}
                                        style={{ width: '100%', accentColor: '#1677ff', height: '4px', cursor: 'pointer' }}
                                      />
                                    </div>
                                    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '9px', color: '#8e8e93' }}>
                                        <span>Shear (Slant)</span>
                                        <span>{activeElement.shear || 0}°</span>
                                      </div>
                                      <input
                                        type="range"
                                        min="-45"
                                        max="45"
                                        value={activeElement.shear || 0}
                                        onChange={(e) => updateActiveElement({ shear: parseInt(e.target.value) || 0 })}
                                        style={{ width: '100%', accentColor: '#1677ff', height: '4px', cursor: 'pointer' }}
                                      />
                                    </div>
                                  </div>

                                  {/* Horizontal Scale & Vertical Scale */}
                                  <div style={{ display: 'flex', gap: '8px' }}>
                                    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '9px', color: '#8e8e93' }}>
                                        <span>H-Scale</span>
                                        <span>{activeElement.scaleX !== undefined ? activeElement.scaleX : 100}%</span>
                                      </div>
                                      <input
                                        type="range"
                                        min="50"
                                        max="200"
                                        value={activeElement.scaleX !== undefined ? activeElement.scaleX : 100}
                                        onChange={(e) => updateActiveElement({ scaleX: parseInt(e.target.value) || 100 })}
                                        style={{ width: '100%', accentColor: '#1677ff', height: '4px', cursor: 'pointer' }}
                                      />
                                    </div>
                                    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '9px', color: '#8e8e93' }}>
                                        <span>V-Scale</span>
                                        <span>{activeElement.scaleY !== undefined ? activeElement.scaleY : 100}%</span>
                                      </div>
                                      <input
                                        type="range"
                                        min="50"
                                        max="200"
                                        value={activeElement.scaleY !== undefined ? activeElement.scaleY : 100}
                                        onChange={(e) => updateActiveElement({ scaleY: parseInt(e.target.value) || 100 })}
                                        style={{ width: '100%', accentColor: '#1677ff', height: '4px', cursor: 'pointer' }}
                                      />
                                    </div>
                                  </div>

                                  {/* Text Rotation by Degrees (Default: 0° Horizontal) */}
                                  <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', borderTop: '1px solid #232326', paddingTop: '6px', marginTop: '2px' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                      <span style={{ fontSize: '9px', color: '#8e8e93' }}>Rotation Angle (Default: 0° Horizontal)</span>
                                      <div style={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
                                        <input
                                          type="number"
                                          min="-360"
                                          max="360"
                                          value={activeElement.rotation || 0}
                                          onChange={(e) => updateActiveElement({ rotation: parseInt(e.target.value) || 0 })}
                                          style={{
                                            width: '45px',
                                            backgroundColor: '#141416',
                                            border: '1px solid #3c3c42',
                                            borderRadius: '3px',
                                            padding: '2px 4px',
                                            color: '#fff',
                                            fontSize: '10px',
                                            textAlign: 'right',
                                            outline: 'none'
                                          }}
                                        />
                                        <span style={{ fontSize: '9px', color: '#8e8e93' }}>°</span>
                                      </div>
                                    </div>
                                    <input
                                      type="range"
                                      min="-180"
                                      max="180"
                                      value={activeElement.rotation || 0}
                                      onChange={(e) => updateActiveElement({ rotation: parseInt(e.target.value) || 0 })}
                                      style={{ width: '100%', accentColor: '#1677ff', height: '4px', cursor: 'pointer' }}
                                    />
                                    {/* Presets: 0° Horizontal, 90° ↧, 180° ↷, 270° ↥, Reset */}
                                    <div style={{ display: 'flex', gap: '3px' }}>
                                      {[
                                        { deg: 0, label: '0° Horiz' },
                                        { deg: 90, label: '90° ↧' },
                                        { deg: 180, label: '180° ↷' },
                                        { deg: 270, label: '270° ↥' }
                                      ].map(preset => {
                                        const isSelected = (activeElement.rotation || 0) === preset.deg;
                                        return (
                                          <button
                                            key={preset.deg}
                                            type="button"
                                            onClick={() => updateActiveElement({ rotation: preset.deg })}
                                            style={{
                                              flex: 1,
                                              padding: '3px 0',
                                              fontSize: '9px',
                                              fontWeight: isSelected ? 'bold' : 'normal',
                                              borderRadius: '3px',
                                              border: '1px solid #3c3c42',
                                              backgroundColor: isSelected ? '#1677ff' : '#1b1b1d',
                                              color: isSelected ? '#ffffff' : '#dfdfe3',
                                              cursor: 'pointer',
                                              transition: 'all 0.1s'
                                            }}
                                            title={`Rotate to ${preset.deg}°`}
                                          >
                                            {preset.label}
                                          </button>
                                        );
                                      })}
                                      {(activeElement.rotation || 0) !== 0 && (
                                        <button
                                          type="button"
                                          onClick={() => updateActiveElement({ rotation: 0 })}
                                          style={{
                                            padding: '3px 5px',
                                            fontSize: '9px',
                                            borderRadius: '3px',
                                            border: '1px solid #3c3c42',
                                            backgroundColor: '#262629',
                                            color: '#e5e7eb',
                                            cursor: 'pointer'
                                          }}
                                          title="Reset rotation to 0°"
                                        >
                                          Reset
                                        </button>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              </fieldset>
                            </div>
                          )}
                        </div>
                      )}

                      {/* DIVIDER PROPERTIES PANEL */}
                      {activeElement.type === 'accent' && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', borderTop: '1px solid #3c3c42', paddingTop: '10px', marginTop: '4px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', userSelect: 'none' }}>
                            <span style={{ fontSize: '10px', fontWeight: 'bold', color: '#8e8e93', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                              Divider Settings
                            </span>
                          </div>
                          
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', paddingLeft: '14px' }}>
                            {/* Divider Thickness Slider */}
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '9px', color: '#8e8e93' }}>
                                <span>Thickness</span>
                                <span style={{ color: '#fff' }}>{activeElement.strokeWidth || 2}px</span>
                              </div>
                              <input
                                type="range"
                                min="1"
                                max="20"
                                value={activeElement.strokeWidth || 2}
                                onChange={(e) => {
                                  const val = parseInt(e.target.value) || 2;
                                  const isVertical = activeElement.dividerType === 'vertical' || activeElement.orientation === 'vertical';
                                  if (isVertical) {
                                    updateActiveElement({ strokeWidth: val, width: val });
                                  } else {
                                    updateActiveElement({ strokeWidth: val, height: val });
                                  }
                                }}
                                style={{ width: '100%', accentColor: '#1677ff', height: '4px', cursor: 'pointer' }}
                              />
                            </div>
                          </div>
                        </div>
                      )}

                      {/* COLLAPSIBLE FILL PANEL */}
                      {(activeElement.type === 'shape' || activeElement.type === 'text' || activeElement.type === 'datagrid' || activeElement.type === 'certify' || activeElement.type === 'accent' || activeElement.type === 'image') && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', borderTop: '1px solid #3c3c42', paddingTop: '10px', marginTop: '4px' }}>
                          <div 
                            onClick={() => setFillExpanded(prev => !prev)}
                            style={{ 
                              display: 'flex', 
                              alignItems: 'center', 
                              gap: '6px', 
                              cursor: 'pointer', 
                              userSelect: 'none'
                            }}
                          >
                            <span style={{ fontSize: '8px', color: '#8e8e93', width: '8px' }}>
                              {fillExpanded ? '▼' : '▶'}
                            </span>
                            <span style={{ fontSize: '10px', fontWeight: 'bold', color: '#8e8e93', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                              Fill
                            </span>
                          </div>

                          {fillExpanded && (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', paddingLeft: '14px' }}>
                              {/* Fill Type */}
                              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                <span style={{ fontSize: '10px', color: '#8e8e93' }}>Fill Type</span>
                                <select
                                  value={
                                    (activeElement.type === 'shape' || activeElement.type === 'datagrid' || activeElement.type === 'certify' || activeElement.type === 'accent' || activeElement.type === 'image')
                                      ? (activeElement.fillType || 'solid') 
                                      : (activeElement.colorType || 'solid')
                                  }
                                  onChange={(e) => {
                                    const val = e.target.value;
                                    if (activeElement.type === 'shape' || activeElement.type === 'datagrid' || activeElement.type === 'certify' || activeElement.type === 'accent' || activeElement.type === 'image') {
                                      const updates = { fillType: val };
                                      if (val === 'gradient') {
                                        if (!activeElement.fillGradient) {
                                          updates.fillGradient = {
                                            type: 'linear',
                                            angle: 90,
                                            stops: [
                                              { color: '#ffffff', position: 0, opacity: 1 },
                                              { color: '#000000', position: 100, opacity: 1 }
                                            ]
                                          };
                                        }
                                        if (!activeElement.fill || (!activeElement.fill.startsWith('linear') && !activeElement.fill.startsWith('radial'))) {
                                          updates.fill = 'linear-gradient(90deg, rgba(255, 255, 255, 1) 0%, rgba(0, 0, 0, 1) 100%)';
                                        }
                                      } else if (val === 'pattern') {
                                        const basePat = activeElement.pattern || {
                                          id: DEFAULT_CARD_PATTERNS[0].id,
                                          name: DEFAULT_CARD_PATTERNS[0].name,
                                          image: DEFAULT_CARD_PATTERNS[0].image,
                                          tileWidth: DEFAULT_CARD_PATTERNS[0].tileWidth,
                                          tileHeight: DEFAULT_CARD_PATTERNS[0].tileHeight,
                                          scale: 100,
                                          opacity: 100,
                                          repeat: 'repeat',
                                          tintColor: DEFAULT_CARD_PATTERNS[0].defaultTintColor || '#18181c',
                                          blendMode: 'normal'
                                        };
                                        updates.pattern = basePat;
                                        updates.fill = generatePatternString(basePat);
                                      } else {
                                        const firstStopColor = activeElement.fillGradient?.stops?.[0]?.color || activeElement.pattern?.tintColor || '#ffffff';
                                        updates.fill = firstStopColor.startsWith('#') ? firstStopColor : '#ffffff';
                                      }
                                      updateActiveElement(updates);
                                    } else {
                                      const updates = { colorType: val };
                                      if (val === 'gradient') {
                                        if (!activeElement.colorGradient) {
                                          updates.colorGradient = {
                                            type: 'linear',
                                            angle: 90,
                                            stops: [
                                              { color: '#ffffff', position: 0, opacity: 1 },
                                              { color: '#000000', position: 100, opacity: 1 }
                                            ]
                                          };
                                        }
                                        if (!activeElement.color || (!activeElement.color.startsWith('linear') && !activeElement.color.startsWith('radial'))) {
                                          updates.color = 'linear-gradient(90deg, rgba(255, 255, 255, 1) 0%, rgba(0, 0, 0, 1) 100%)';
                                        }
                                      } else {
                                        const firstStopColor = activeElement.colorGradient?.stops?.[0]?.color || '#ffffff';
                                        updates.color = firstStopColor.startsWith('#') ? firstStopColor : '#ffffff';
                                      }
                                      updateActiveElement(updates);
                                    }
                                  }}
                                  style={{ width: '100%', backgroundColor: '#1b1b1d', border: '1px solid #3c3c42', borderRadius: '4px', padding: '4px', color: '#fff', fontSize: '11px', outline: 'none' }}
                                >
                                  <option value="solid">Solid Color</option>
                                  <option value="gradient">Gradient</option>
                                  {(activeElement.type === 'shape' || activeElement.type === 'datagrid' || activeElement.type === 'certify' || activeElement.type === 'accent') && (
                                    <option value="pattern">Pattern / Texture</option>
                                  )}
                                </select>
                              </div>

                              {/* Fill Color Controls / Gradient / Pattern preview */}
                              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                <span style={{ fontSize: '10px', color: '#8e8e93' }}>
                                  {(((activeElement.type === 'shape' || activeElement.type === 'datagrid' || activeElement.type === 'certify' || activeElement.type === 'accent') && activeElement.fillType === 'gradient') || (activeElement.type === 'text' && activeElement.colorType === 'gradient')) 
                                    ? 'Gradient (Click to Edit)' 
                                    : (activeElement.fillType === 'pattern' ? 'Pattern (Click to Choose / Edit)' : 'Color')}
                                </span>
                                {activeElement.fillType === 'pattern' ? (
                                  <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                    <div
                                      onClick={() => {
                                        setPatternState(activeElement.pattern || {
                                          id: DEFAULT_CARD_PATTERNS[0].id,
                                          name: DEFAULT_CARD_PATTERNS[0].name,
                                          image: DEFAULT_CARD_PATTERNS[0].image,
                                          tileWidth: DEFAULT_CARD_PATTERNS[0].tileWidth,
                                          tileHeight: DEFAULT_CARD_PATTERNS[0].tileHeight,
                                          scale: 100,
                                          opacity: 100,
                                          repeat: 'repeat',
                                          tintColor: DEFAULT_CARD_PATTERNS[0].defaultTintColor || '#18181c',
                                          blendMode: 'normal'
                                        });
                                        setEditingPatternTarget('shapeFill');
                                        setShowPatternModal(true);
                                      }}
                                      style={{
                                        width: '100%',
                                        height: '36px',
                                        borderRadius: '4px',
                                        border: '1px solid #3c3c42',
                                        cursor: 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        padding: '0 8px',
                                        gap: '8px',
                                        backgroundColor: '#232326',
                                        userSelect: 'none'
                                      }}
                                      title="Click to Choose Pattern / Texture"
                                    >
                                      <div style={{
                                        width: '24px',
                                        height: '24px',
                                        borderRadius: '3px',
                                        border: '1px solid #4a4a52',
                                        backgroundColor: activeElement.pattern?.tintColor || '#ffffff',
                                        backgroundImage: `url("${activeElement.pattern?.image || DEFAULT_CARD_PATTERNS[0].image}")`,
                                        backgroundRepeat: 'repeat',
                                        backgroundSize: `${Math.max(2, Math.round((activeElement.pattern?.tileWidth || 32) * ((activeElement.pattern?.scale || 100) / 100)))}px`,
                                        flexShrink: 0
                                      }} />
                                      <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minWidth: 0 }}>
                                        <span style={{ fontSize: '11px', color: '#ffffff', fontWeight: 'bold', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                          {activeElement.pattern?.name || 'Choose Pattern...'}
                                        </span>
                                        <span style={{ fontSize: '9px', color: '#8e8e93' }}>
                                          Scale: {activeElement.pattern?.scale || 100}% • Opacity: {activeElement.pattern?.opacity !== undefined ? activeElement.pattern.opacity : 100}%
                                        </span>
                                      </div>
                                      <span style={{ fontSize: '10px', color: '#3b82f6', fontWeight: 'bold' }}>
                                        Edit
                                      </span>
                                    </div>

                                    {/* Quick Scale Slider */}
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', marginTop: '2px' }}>
                                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '9px', color: '#8e8e93' }}>
                                        <span>Pattern Scale</span>
                                        <span style={{ color: '#fff' }}>{activeElement.pattern?.scale || 100}%</span>
                                      </div>
                                      <input
                                        type="range"
                                        min="10"
                                        max="400"
                                        value={activeElement.pattern?.scale || 100}
                                        onChange={(e) => {
                                          const scaleVal = parseInt(e.target.value) || 100;
                                          const newPattern = { ...(activeElement.pattern || DEFAULT_CARD_PATTERNS[0]), scale: scaleVal };
                                          updateActiveElement({
                                            pattern: newPattern,
                                            fill: generatePatternString(newPattern)
                                          });
                                        }}
                                        style={{ accentColor: '#1677ff', height: '4px', cursor: 'pointer' }}
                                      />
                                    </div>

                                    {/* Quick Opacity Slider */}
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '9px', color: '#8e8e93' }}>
                                        <span>Pattern Opacity</span>
                                        <span style={{ color: '#fff' }}>{activeElement.pattern?.opacity !== undefined ? activeElement.pattern.opacity : 100}%</span>
                                      </div>
                                      <input
                                        type="range"
                                        min="0"
                                        max="100"
                                        value={activeElement.pattern?.opacity !== undefined ? activeElement.pattern.opacity : 100}
                                        onChange={(e) => {
                                          const opVal = parseInt(e.target.value);
                                          const newPattern = { ...(activeElement.pattern || DEFAULT_CARD_PATTERNS[0]), opacity: opVal };
                                          updateActiveElement({
                                            pattern: newPattern,
                                            fill: generatePatternString(newPattern)
                                          });
                                        }}
                                        style={{ accentColor: '#1677ff', height: '4px', cursor: 'pointer' }}
                                      />
                                    </div>

                                    {/* Quick Tint Color */}
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                                      <span style={{ fontSize: '9px', color: '#8e8e93' }}>Tint / Base Color</span>
                                      {renderColorInputRow(
                                        activeElement.pattern?.tintColor || '',
                                        (val) => {
                                          const newPattern = { ...(activeElement.pattern || DEFAULT_CARD_PATTERNS[0]), tintColor: val };
                                          updateActiveElement({
                                            pattern: newPattern,
                                            fill: generatePatternString(newPattern)
                                          });
                                        }
                                      )}
                                    </div>
                                  </div>
                                ) : (((activeElement.type === 'shape' || activeElement.type === 'datagrid' || activeElement.type === 'certify' || activeElement.type === 'accent') && activeElement.fillType === 'gradient') || (activeElement.type === 'text' && activeElement.colorType === 'gradient')) ? (
                                  <div 
                                    onClick={() => {
                                      const defaultGrad = {
                                        type: 'linear',
                                        angle: 90,
                                        stops: [
                                          { color: '#ffffff', position: 0, opacity: 1 },
                                          { color: '#000000', position: 100, opacity: 1 }
                                        ]
                                      };
                                      if (activeElement.type === 'shape' || activeElement.type === 'datagrid' || activeElement.type === 'certify' || activeElement.type === 'accent') {
                                        setGradientState(activeElement.fillGradient || defaultGrad);
                                        setEditingGradientTarget('shapeFill');
                                      } else {
                                        setGradientState(activeElement.colorGradient || defaultGrad);
                                        setEditingGradientTarget('textFill');
                                      }
                                      setSelectedStopIndex(0);
                                      setShowGradientModal(true);
                                    }}
                                    style={{
                                      width: '100%',
                                      height: '24px',
                                      borderRadius: '4px',
                                      border: '1px solid #3c3c42',
                                      background: ((activeElement.type === 'shape' || activeElement.type === 'datagrid' || activeElement.type === 'certify' || activeElement.type === 'accent') ? activeElement.fill : activeElement.color) || 'linear-gradient(90deg, #ffffff 0%, #000000 100%)',
                                      cursor: 'pointer',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      fontSize: '9px',
                                      color: '#ffffff',
                                      textShadow: '0 1px 2px rgba(0,0,0,0.8)',
                                      fontWeight: 'bold',
                                      userSelect: 'none'
                                    }}
                                  >
                                    Edit Gradient
                                  </div>
                                ) : (
                                  <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                    {renderColorInputRow(
                                      (activeElement.type === 'shape' || activeElement.type === 'datagrid' || activeElement.type === 'certify' || activeElement.type === 'accent' || activeElement.type === 'image') 
                                        ? (activeElement.fill || '') 
                                        : (activeElement.color || ''),
                                      (val) => {
                                        if (activeElement.type === 'shape' || activeElement.type === 'datagrid' || activeElement.type === 'certify' || activeElement.type === 'accent' || activeElement.type === 'image') {
                                          updateActiveElement({ fill: val });
                                        } else {
                                          updateActiveElement({ color: val });
                                        }
                                      },
                                      activeElement.type === 'image' ? 'transparent' : '#ffffff'
                                    )}
                                    {activeElement.type === 'image' && activeElement.fill && activeElement.fill !== 'transparent' && (
                                      <button
                                        type="button"
                                        onClick={() => updateActiveElement({ fill: 'transparent' })}
                                        style={{
                                          backgroundColor: '#232326',
                                          color: '#8e8e93',
                                          border: '1px solid #3c3c42',
                                          borderRadius: '3px',
                                          padding: '2px 8px',
                                          fontSize: '10px',
                                          cursor: 'pointer',
                                          width: 'fit-content'
                                        }}
                                      >
                                        Clear to Transparent
                                      </button>
                                    )}
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      )}

                      {/* GRID PANEL (Table Frame exclusive) */}
                      {activeElement.type === 'datagrid' && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', borderTop: '1px solid #3c3c42', paddingTop: '10px', marginTop: '4px' }}>
                          <div 
                            onClick={() => setGridExpanded(prev => !prev)}
                            style={{ 
                              display: 'flex', 
                              alignItems: 'center', 
                              gap: '6px', 
                              cursor: 'pointer',
                              userSelect: 'none'
                            }}
                          >
                            <span style={{ fontSize: '8px', color: '#8e8e93', width: '8px' }}>
                              {gridExpanded ? '▼' : '▶'}
                            </span>
                            <span style={{ fontSize: '10px', fontWeight: 'bold', color: '#8e8e93', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                              Grid
                            </span>
                          </div>

                          {gridExpanded && (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', paddingLeft: '14px' }}>
                              
                              {/* 1. Header Row Configuration */}
                              <fieldset style={{
                                border: '1px solid #3c3c42',
                                borderRadius: '4px',
                                padding: '10px',
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '8px',
                                boxSizing: 'border-box'
                              }}>
                                <legend style={{ fontSize: '10px', color: '#8e8e93', fontWeight: 'bold', textTransform: 'uppercase', padding: '0 6px' }}>
                                  Header Row
                                </legend>

                                <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px', color: '#dfdfe3', cursor: 'pointer' }}>
                                  <input
                                    type="checkbox"
                                    checked={activeElement.headerEnabled || false}
                                    onChange={(e) => updateActiveElement({ headerEnabled: e.target.checked })}
                                    style={{ cursor: 'pointer' }}
                                  />
                                  <span>Enable Header Row</span>
                                </label>

                                {(activeElement.headerEnabled || false) && (
                                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '4px' }}>
                                    <div style={{ display: 'flex', gap: '6px' }}>
                                      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '3px' }}>
                                        <span style={{ fontSize: '9px', color: '#8e8e93' }}>Left Label</span>
                                        <input
                                          type="text"
                                          value={activeElement.headerLabelText || 'Attribute'}
                                          onChange={(e) => updateActiveElement({ headerLabelText: e.target.value })}
                                          style={{ width: '100%', backgroundColor: '#1b1b1d', border: '1px solid #3c3c42', borderRadius: '3px', padding: '4px', color: '#fff', fontSize: '11px', outline: 'none' }}
                                        />
                                      </div>
                                      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '3px' }}>
                                        <span style={{ fontSize: '9px', color: '#8e8e93' }}>Right Label</span>
                                        <input
                                          type="text"
                                          value={activeElement.headerValueText || 'Value'}
                                          onChange={(e) => updateActiveElement({ headerValueText: e.target.value })}
                                          style={{ width: '100%', backgroundColor: '#1b1b1d', border: '1px solid #3c3c42', borderRadius: '3px', padding: '4px', color: '#fff', fontSize: '11px', outline: 'none' }}
                                        />
                                      </div>
                                    </div>

                                    {/* Header Text Color */}
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                      <span style={{ fontSize: '10px', color: '#8e8e93' }}>Header Text Color</span>
                                      {renderColorInputRow(activeElement.headerColor || '#ffffff', (val) => updateActiveElement({ headerColor: val }))}
                                    </div>

                                    {/* Header Fill Option */}
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                      <span style={{ fontSize: '10px', color: '#8e8e93' }}>Header Fill Type</span>
                                      <select
                                        value={activeElement.headerFillType || 'solid'}
                                        onChange={(e) => {
                                          const val = e.target.value;
                                          const updates = { headerFillType: val };
                                          if (val === 'gradient') {
                                            if (!activeElement.headerFillGradient) {
                                              updates.headerFillGradient = {
                                                type: 'linear',
                                                angle: 90,
                                                stops: [
                                                  { color: 'rgba(255,255,255,0.15)', position: 0, opacity: 1 },
                                                  { color: 'rgba(0,0,0,0.2)', position: 100, opacity: 1 }
                                                ]
                                              };
                                            }
                                            if (!activeElement.headerFill || (!activeElement.headerFill.startsWith('linear') && !activeElement.headerFill.startsWith('radial'))) {
                                              updates.headerFill = 'linear-gradient(90deg, rgba(255, 255, 255, 0.15) 0%, rgba(0, 0, 0, 0.2) 100%)';
                                            }
                                          } else {
                                            const firstStopColor = activeElement.headerFillGradient?.stops?.[0]?.color || 'rgba(255,255,255,0.1)';
                                            updates.headerFill = firstStopColor.startsWith('#') || firstStopColor.startsWith('rgba') ? firstStopColor : 'rgba(255,255,255,0.1)';
                                          }
                                          updateActiveElement(updates);
                                        }}
                                        style={{ width: '100%', backgroundColor: '#1b1b1d', border: '1px solid #3c3c42', borderRadius: '4px', padding: '4px', color: '#fff', fontSize: '11px', outline: 'none' }}
                                      >
                                        <option value="solid">Solid Color</option>
                                        <option value="gradient">Gradient</option>
                                      </select>
                                    </div>

                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                      <span style={{ fontSize: '10px', color: '#8e8e93' }}>
                                        {activeElement.headerFillType === 'gradient' ? 'Header Gradient (Click to Edit)' : 'Header Fill Color'}
                                      </span>
                                      {activeElement.headerFillType === 'gradient' ? (
                                        <div 
                                          onClick={() => {
                                            const defaultGrad = {
                                              type: 'linear',
                                              angle: 90,
                                              stops: [
                                                { color: 'rgba(255,255,255,0.15)', position: 0, opacity: 1 },
                                                { color: 'rgba(0,0,0,0.2)', position: 100, opacity: 1 }
                                              ]
                                            };
                                            setGradientState(activeElement.headerFillGradient || defaultGrad);
                                            setSelectedStopIndex(0);
                                            setEditingGradientTarget('headerFill');
                                            setShowGradientModal(true);
                                          }}
                                          style={{
                                            width: '100%',
                                            height: '24px',
                                            borderRadius: '4px',
                                            border: '1px solid #3c3c42',
                                            background: activeElement.headerFill || 'linear-gradient(90deg, rgba(255,255,255,0.15) 0%, rgba(0,0,0,0.2) 100%)',
                                            cursor: 'pointer',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            fontSize: '9px',
                                            color: '#ffffff',
                                            textShadow: '0 1px 2px rgba(0,0,0,0.8)',
                                            fontWeight: 'bold',
                                            userSelect: 'none'
                                          }}
                                        >
                                          Edit Header Gradient
                                        </div>
                                      ) : (
                                        <div style={{ display: 'flex', gap: '6px' }}>
                                          {renderColorInputRow(activeElement.headerFill || '', (val) => updateActiveElement({ headerFill: val }))}
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                )}
                              </fieldset>

                              {/* 2. Padding and Spacing */}
                              <fieldset style={{
                                border: '1px solid #3c3c42',
                                borderRadius: '4px',
                                padding: '10px',
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '8px',
                                boxSizing: 'border-box'
                              }}>
                                <legend style={{ fontSize: '10px', color: '#8e8e93', fontWeight: 'bold', textTransform: 'uppercase', padding: '0 6px' }}>
                                  Padding & Spacing
                                </legend>

                                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '10px', color: '#8e8e93' }}>
                                    <span>Row Padding (Vertical)</span>
                                    <span>{activeElement.rowPadding !== undefined ? activeElement.rowPadding : 4}px</span>
                                  </div>
                                  <input
                                    type="range"
                                    min="0"
                                    max="20"
                                    value={activeElement.rowPadding !== undefined ? activeElement.rowPadding : 4}
                                    onChange={(e) => updateActiveElement({ rowPadding: parseInt(e.target.value) || 0 })}
                                    style={{ width: '100%', accentColor: '#1677ff', height: '4px', cursor: 'pointer' }}
                                  />
                                </div>

                                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '10px', color: '#8e8e93' }}>
                                    <span>Col Spacing (Horizontal)</span>
                                    <span>{activeElement.colPadding !== undefined ? activeElement.colPadding : 6}px</span>
                                  </div>
                                  <input
                                    type="range"
                                    min="0"
                                    max="20"
                                    value={activeElement.colPadding !== undefined ? activeElement.colPadding : 6}
                                    onChange={(e) => updateActiveElement({ colPadding: parseInt(e.target.value) || 0 })}
                                    style={{ width: '100%', accentColor: '#1677ff', height: '4px', cursor: 'pointer' }}
                                  />
                                </div>
                              </fieldset>

                              {/* 3. Width Split & Alignment */}
                              <fieldset style={{
                                border: '1px solid #3c3c42',
                                borderRadius: '4px',
                                padding: '10px',
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '8px',
                                boxSizing: 'border-box'
                              }}>
                                <legend style={{ fontSize: '10px', color: '#8e8e93', fontWeight: 'bold', textTransform: 'uppercase', padding: '0 6px' }}>
                                  Columns & Alignment
                                </legend>

                                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '10px', color: '#8e8e93' }}>
                                    <span>Col Ratio (Left / Right)</span>
                                    <span>{(activeElement.colWidthRatio || 50)}% / {100 - (activeElement.colWidthRatio || 50)}%</span>
                                  </div>
                                  <input
                                    type="range"
                                    min="10"
                                    max="90"
                                    value={activeElement.colWidthRatio || 50}
                                    onChange={(e) => updateActiveElement({ colWidthRatio: parseInt(e.target.value) || 50 })}
                                    style={{ width: '100%', accentColor: '#1677ff', height: '4px', cursor: 'pointer' }}
                                  />
                                </div>

                                {/* Label Alignment */}
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                  <span style={{ fontSize: '10px', color: '#8e8e93' }}>Left Label Alignment</span>
                                  <div style={{ display: 'flex', gap: '4px' }}>
                                    {['left', 'center', 'right'].map(align => (
                                      <button
                                        key={align}
                                        onClick={() => updateActiveElement({ labelAlign: align })}
                                        style={{
                                          flex: 1,
                                          padding: '4px 0',
                                          fontSize: '10px',
                                          border: '1px solid #3c3c42',
                                          borderRadius: '3px',
                                          backgroundColor: (activeElement.labelAlign || 'left') === align ? '#1677ff' : '#1b1b1d',
                                          color: '#ffffff',
                                          cursor: 'pointer',
                                          textTransform: 'capitalize'
                                        }}
                                      >
                                        {align}
                                      </button>
                                    ))}
                                  </div>
                                </div>

                                {/* Value Alignment */}
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                  <span style={{ fontSize: '10px', color: '#8e8e93' }}>Right Value Alignment</span>
                                  <div style={{ display: 'flex', gap: '4px' }}>
                                    {['left', 'center', 'right'].map(align => (
                                      <button
                                        key={align}
                                        onClick={() => updateActiveElement({ valueAlign: align })}
                                        style={{
                                          flex: 1,
                                          padding: '4px 0',
                                          fontSize: '10px',
                                          border: '1px solid #3c3c42',
                                          borderRadius: '3px',
                                          backgroundColor: (activeElement.valueAlign || 'right') === align ? '#1677ff' : '#1b1b1d',
                                          color: '#ffffff',
                                          cursor: 'pointer',
                                          textTransform: 'capitalize'
                                        }}
                                      >
                                        {align}
                                      </button>
                                    ))}
                                  </div>
                                </div>
                              </fieldset>

                              {/* 4. Zebra Striping */}
                              <fieldset style={{
                                border: '1px solid #3c3c42',
                                borderRadius: '4px',
                                padding: '10px',
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '8px',
                                boxSizing: 'border-box'
                              }}>
                                <legend style={{ fontSize: '10px', color: '#8e8e93', fontWeight: 'bold', textTransform: 'uppercase', padding: '0 6px' }}>
                                  Zebra Striping
                                </legend>

                                <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px', color: '#dfdfe3', cursor: 'pointer' }}>
                                  <input
                                    type="checkbox"
                                    checked={activeElement.zebraEnabled || false}
                                    onChange={(e) => updateActiveElement({ zebraEnabled: e.target.checked })}
                                    style={{ cursor: 'pointer' }}
                                  />
                                  <span>Enable Zebra Striping</span>
                                </label>

                                {(activeElement.zebraEnabled || false) && (
                                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', marginTop: '4px' }}>
                                    <span style={{ fontSize: '10px', color: '#8e8e93' }}>Stripe Background Color</span>
                                      {renderColorInputRow(
                                        activeElement.zebraColor || '',
                                        (val) => updateActiveElement({ zebraColor: val }),
                                        'rgba(255,255,255,0.03)'
                                      )}
                                  </div>
                                )}
                              </fieldset>

                              {/* 5. Grid Lines / Dividers */}
                              <fieldset style={{
                                border: '1px solid #3c3c42',
                                borderRadius: '4px',
                                padding: '10px',
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '8px',
                                boxSizing: 'border-box'
                              }}>
                                <legend style={{ fontSize: '10px', color: '#8e8e93', fontWeight: 'bold', textTransform: 'uppercase', padding: '0 6px' }}>
                                  Dividers / Lines
                                </legend>

                                <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px', color: '#dfdfe3', cursor: 'pointer' }}>
                                  <input
                                    type="checkbox"
                                    checked={activeElement.gridLinesEnabled !== false}
                                    onChange={(e) => updateActiveElement({ gridLinesEnabled: e.target.checked })}
                                    style={{ cursor: 'pointer' }}
                                  />
                                  <span>Enable Row Dividers</span>
                                </label>

                                {(activeElement.gridLinesEnabled !== false) && (
                                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '4px' }}>
                                    
                                    {/* Line Style */}
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                      <span style={{ fontSize: '10px', color: '#8e8e93' }}>Divider Style</span>
                                      <div style={{ display: 'flex', gap: '4px' }}>
                                        {['solid', 'dashed', 'dotted'].map(st => (
                                          <button
                                            key={st}
                                            onClick={() => updateActiveElement({ gridLineStyle: st })}
                                            style={{
                                              flex: 1,
                                              padding: '4px 0',
                                              fontSize: '10px',
                                              border: '1px solid #3c3c42',
                                              borderRadius: '3px',
                                              backgroundColor: (activeElement.gridLineStyle || 'solid') === st ? '#1677ff' : '#1b1b1d',
                                              color: '#ffffff',
                                              cursor: 'pointer',
                                              textTransform: 'capitalize'
                                            }}
                                          >
                                            {st === 'solid' ? 'Line' : st}
                                          </button>
                                        ))}
                                      </div>
                                    </div>

                                    {/* Line Color */}
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                      <span style={{ fontSize: '10px', color: '#8e8e93' }}>Divider Color</span>
                                        {renderColorInputRow(
                                          activeElement.gridLineColor || '',
                                          (val) => updateActiveElement({ gridLineColor: val }),
                                          'rgba(255,255,255,0.08)'
                                        )}
                                    </div>

                                    {/* Line Thickness */}
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '10px', color: '#8e8e93' }}>
                                        <span>Divider Thickness</span>
                                        <span>{activeElement.gridLineThickness !== undefined ? activeElement.gridLineThickness : 1}px</span>
                                      </div>
                                      <input
                                        type="range"
                                        min="0"
                                        max="5"
                                        value={activeElement.gridLineThickness !== undefined ? activeElement.gridLineThickness : 1}
                                        onChange={(e) => updateActiveElement({ gridLineThickness: parseInt(e.target.value) || 0 })}
                                        style={{ width: '100%', accentColor: '#1677ff', height: '4px', cursor: 'pointer' }}
                                      />
                                    </div>
                                  </div>
                                )}
                              </fieldset>
                            </div>
                          )}
                        </div>
                      )}

                      {/* BORDERS & EDGES PANEL */}
                      {((activeElement.type === 'image') || (activeElement.type === 'qrcode') || (activeElement.type === 'datagrid') || (activeElement.type === 'shape' && !['triangle', 'polygon', 'diamond', 'star'].includes(activeElement.shapeType))) && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', borderTop: '1px solid #3c3c42', paddingTop: '10px', marginTop: '4px' }}>
                          <div 
                            onClick={() => setBordersExpanded(prev => !prev)}
                            style={{ 
                              display: 'flex', 
                              alignItems: 'center', 
                              gap: '6px', 
                              cursor: 'pointer',
                              userSelect: 'none'
                            }}
                          >
                            <span style={{ fontSize: '8px', color: '#8e8e93', width: '8px' }}>
                              {bordersExpanded ? '▼' : '▶'}
                            </span>
                            <span style={{ fontSize: '10px', fontWeight: 'bold', color: '#8e8e93', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                              Borders & Edges
                            </span>
                          </div>

                          {bordersExpanded && (
                            <>
                              {/* Checkbox toggle: Enable borders */}
                              <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px', color: '#dfdfe3', cursor: 'pointer' }}>
                                <input
                                  type="checkbox"
                                  checked={activeElement.hasBorder !== false}
                                  onChange={(e) => updateActiveElement({ hasBorder: e.target.checked })}
                                  style={{ cursor: 'pointer' }}
                                />
                                <span>Enable Borders & Edges</span>
                              </label>

                              {activeElement.hasBorder !== false && (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', paddingLeft: '4px', marginTop: '4px' }}>
                                  {/* 1. Corner Rounding (Hide/disable for circle and pill) */}
                                  {!(activeElement.type === 'shape' && ['circle', 'pill'].includes(activeElement.shapeType)) && (
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '10px', color: '#8e8e93' }}>
                                        <span>Outer Corner Rounding</span>
                                        <span>{activeElement.borderRadius || 0}px</span>
                                      </div>
                                      <input
                                        type="range"
                                        min="0"
                                        max="50"
                                        value={activeElement.borderRadius || 0}
                                        onChange={(e) => updateActiveElement({ borderRadius: parseInt(e.target.value) || 0 })}
                                        style={{ width: '100%', accentColor: '#1677ff', height: '4px', cursor: 'pointer' }}
                                      />
                                    </div>
                                  )}

                                  {/* Primary Border Group */}
                                  <fieldset style={{
                                    border: '1px solid #3c3c42',
                                    borderRadius: '4px',
                                    padding: '10px',
                                    margin: '4px 0',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: '8px',
                                    boxSizing: 'border-box'
                                  }}>
                                    <legend style={{
                                      fontSize: '10px',
                                      color: '#8e8e93',
                                      fontWeight: 'bold',
                                      textTransform: 'uppercase',
                                      padding: '0 6px'
                                    }}>
                                      Primary Border
                                    </legend>

                                    {/* 2. Border Style */}
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                      <span style={{ fontSize: '10px', color: '#8e8e93' }}>Border Style</span>
                                      <div style={{ display: 'flex', gap: '4px' }}>
                                        {['solid', 'dashed', 'dotted'].map(st => (
                                          <button
                                            key={st}
                                            onClick={() => updateActiveElement({ strokeStyle: st })}
                                            style={{
                                              flex: 1,
                                              padding: '4px 0',
                                              fontSize: '10px',
                                              border: '1px solid #3c3c42',
                                              borderRadius: '3px',
                                              backgroundColor: (activeElement.strokeStyle || 'solid') === st ? '#1677ff' : '#1b1b1d',
                                              color: '#ffffff',
                                              cursor: 'pointer',
                                              textTransform: 'capitalize'
                                            }}
                                          >
                                            {st === 'solid' ? 'Line' : st}
                                          </button>
                                        ))}
                                      </div>
                                    </div>

                                    {/* 3. Border Thickness */}
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '10px', color: '#8e8e93' }}>
                                        <span>Border Thickness</span>
                                        <span>{activeElement.strokeWidth || 0}px</span>
                                      </div>
                                      <input
                                        type="range"
                                        min="0"
                                        max="20"
                                        value={activeElement.strokeWidth || 0}
                                        onChange={(e) => updateActiveElement({ strokeWidth: parseInt(e.target.value) || 0 })}
                                        style={{ width: '100%', accentColor: '#1677ff', height: '4px', cursor: 'pointer' }}
                                      />
                                    </div>

                                    {/* Border Fill Group */}
                                    <fieldset style={{
                                      border: '1px solid #3c3c42',
                                      borderRadius: '4px',
                                      padding: '10px',
                                      margin: '4px 0',
                                      display: 'flex',
                                      flexDirection: 'column',
                                      gap: '8px',
                                      boxSizing: 'border-box'
                                    }}>
                                      <legend style={{
                                        fontSize: '10px',
                                        color: '#8e8e93',
                                        fontWeight: 'bold',
                                        textTransform: 'uppercase',
                                        padding: '0 6px'
                                      }}>
                                        Fill
                                      </legend>

                                      {/* 4. Border Fill type */}
                                      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                        <span style={{ fontSize: '10px', color: '#8e8e93' }}>Border Fill Type</span>
                                        <select
                                          value={activeElement.borderFillType || 'solid'}
                                          onChange={(e) => {
                                            const val = e.target.value;
                                            const updates = { borderFillType: val };
                                            if (val === 'gradient') {
                                              if (!activeElement.borderGradient) {
                                                updates.borderGradient = {
                                                  type: 'linear',
                                                  angle: 90,
                                                  stops: [
                                                    { color: '#ffffff', position: 0, opacity: 1 },
                                                    { color: '#000000', position: 100, opacity: 1 }
                                                  ]
                                                };
                                              }
                                              if (!activeElement.strokeColor || (!activeElement.strokeColor.startsWith('linear') && !activeElement.strokeColor.startsWith('radial'))) {
                                                updates.strokeColor = 'linear-gradient(90deg, rgba(255, 255, 255, 1) 0%, rgba(0, 0, 0, 1) 100%)';
                                              }
                                            } else {
                                              const firstStopColor = activeElement.borderGradient?.stops?.[0]?.color || '#ffffff';
                                              updates.strokeColor = firstStopColor.startsWith('#') ? firstStopColor : '#ffffff';
                                            }
                                            updateActiveElement(updates);
                                          }}
                                          style={{ width: '100%', backgroundColor: '#1b1b1d', border: '1px solid #3c3c42', borderRadius: '4px', padding: '4px', color: '#fff', fontSize: '11px', outline: 'none' }}
                                        >
                                          <option value="solid">Solid Color</option>
                                          <option value="gradient">Gradient</option>
                                        </select>
                                      </div>

                                      {/* 5. Border Fill Color / Gradient Value */}
                                      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                        <span style={{ fontSize: '10px', color: '#8e8e93' }}>
                                          {activeElement.borderFillType === 'gradient' ? 'Gradient Fill (Click to Edit)' : 'Border Color'}
                                        </span>
                                        {activeElement.borderFillType === 'gradient' ? (
                                          <div 
                                            onClick={() => {
                                              const defaultGrad = {
                                                type: 'linear',
                                                angle: 90,
                                                stops: [
                                                  { color: '#ffffff', position: 0, opacity: 1 },
                                                  { color: '#000000', position: 100, opacity: 1 }
                                                ]
                                              };
                                              setGradientState(activeElement.borderGradient || defaultGrad);
                                              setSelectedStopIndex(0);
                                              setEditingGradientTarget('primary');
                                              setShowGradientModal(true);
                                            }}
                                            style={{
                                              width: '100%',
                                              height: '24px',
                                              borderRadius: '4px',
                                              border: '1px solid #3c3c42',
                                              background: activeElement.strokeColor || 'linear-gradient(90deg, #ffffff 0%, #000000 100%)',
                                              cursor: 'pointer',
                                              display: 'flex',
                                              alignItems: 'center',
                                              justifyContent: 'center',
                                              fontSize: '9px',
                                              color: '#ffffff',
                                              textShadow: '0 1px 2px rgba(0,0,0,0.8)',
                                              fontWeight: 'bold',
                                              userSelect: 'none'
                                            }}
                                          >
                                            Edit Gradient
                                          </div>
                                        ) : (
                                          <div style={{ display: 'flex', gap: '6px' }}>
                                            {renderColorInputRow(
                                              activeElement.strokeColor,
                                              (val) => updateActiveElement({ strokeColor: val })
                                            )}
                                          </div>
                                        )}
                                      </div>
                                    </fieldset>
                                  </fieldset>

                                  {/* 6. Secondary Border Checkbox */}
                                  <div style={{ borderTop: '1px solid #232326', paddingTop: '8px', marginTop: '4px' }}>
                                    <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px', color: '#dfdfe3', cursor: 'pointer' }}>
                                      <input
                                        type="checkbox"
                                        checked={activeElement.hasSecondBorder || false}
                                        onChange={(e) => updateActiveElement({ hasSecondBorder: e.target.checked })}
                                        style={{ cursor: 'pointer' }}
                                      />
                                      <span>Second Border</span>
                                    </label>

                                    {activeElement.hasSecondBorder && (
                                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '6px' }}>
                                        {/* Border Spacing / Gap (moved first) */}
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '10px', color: '#8e8e93' }}>
                                            <span>Border Spacing (Gap)</span>
                                            <span>{activeElement.borderGap || 0}px</span>
                                          </div>
                                          <input
                                            type="range"
                                            min="0"
                                            max="20"
                                            value={activeElement.borderGap || 0}
                                            onChange={(e) => updateActiveElement({ borderGap: parseInt(e.target.value) || 0 })}
                                            style={{ width: '100%', accentColor: '#1677ff', height: '4px', cursor: 'pointer' }}
                                          />
                                        </div>

                                        {/* Secondary Border Group */}
                                        <fieldset style={{
                                          border: '1px solid #3c3c42',
                                          borderRadius: '4px',
                                          padding: '10px',
                                          margin: '4px 0',
                                          display: 'flex',
                                          flexDirection: 'column',
                                          gap: '8px',
                                          boxSizing: 'border-box'
                                        }}>
                                          <legend style={{
                                            fontSize: '10px',
                                            color: '#8e8e93',
                                            fontWeight: 'bold',
                                            textTransform: 'uppercase',
                                            padding: '0 6px'
                                          }}>
                                            Secondary Border
                                          </legend>

                                          {/* Secondary Style */}
                                          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                            <span style={{ fontSize: '10px', color: '#8e8e93' }}>Secondary Border Style</span>
                                            <div style={{ display: 'flex', gap: '4px' }}>
                                              {['solid', 'dashed', 'dotted'].map(st => (
                                                <button
                                                  key={st}
                                                  onClick={() => updateActiveElement({ strokeStyle2: st })}
                                                  style={{
                                                    flex: 1,
                                                    padding: '4px 0',
                                                    fontSize: '10px',
                                                    border: '1px solid #3c3c42',
                                                    borderRadius: '3px',
                                                    backgroundColor: (activeElement.strokeStyle2 || 'solid') === st ? '#1677ff' : '#1b1b1d',
                                                    color: '#ffffff',
                                                    cursor: 'pointer',
                                                    textTransform: 'capitalize'
                                                  }}
                                                >
                                                  {st === 'solid' ? 'Line' : st}
                                                </button>
                                              ))}
                                            </div>
                                          </div>

                                          {/* Secondary Thickness (now a slider) */}
                                          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '10px', color: '#8e8e93' }}>
                                              <span>Secondary Thickness</span>
                                              <span>{activeElement.strokeWidth2 || 2}px</span>
                                            </div>
                                            <input
                                              type="range"
                                              min="1"
                                              max="20"
                                              value={activeElement.strokeWidth2 || 2}
                                              onChange={(e) => updateActiveElement({ strokeWidth2: parseInt(e.target.value) || 2 })}
                                              style={{ width: '100%', accentColor: '#1677ff', height: '4px', cursor: 'pointer' }}
                                            />
                                          </div>

                                          {/* Secondary Border Fill Group */}
                                          <fieldset style={{
                                            border: '1px solid #3c3c42',
                                            borderRadius: '4px',
                                            padding: '10px',
                                            margin: '4px 0',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            gap: '8px',
                                            boxSizing: 'border-box'
                                          }}>
                                            <legend style={{
                                              fontSize: '10px',
                                              color: '#8e8e93',
                                              fontWeight: 'bold',
                                              textTransform: 'uppercase',
                                              padding: '0 6px'
                                            }}>
                                              Fill
                                            </legend>

                                            {/* Secondary Border Fill Type */}
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                              <span style={{ fontSize: '10px', color: '#8e8e93' }}>Secondary Border Fill Type</span>
                                              <select
                                                value={activeElement.borderFillType2 || 'solid'}
                                                onChange={(e) => {
                                                  const val = e.target.value;
                                                  const updates = { borderFillType2: val };
                                                  if (val === 'gradient') {
                                                    if (!activeElement.borderGradient2) {
                                                      updates.borderGradient2 = {
                                                        type: 'linear',
                                                        angle: 90,
                                                        stops: [
                                                          { color: '#ffffff', position: 0, opacity: 1 },
                                                          { color: '#000000', position: 100, opacity: 1 }
                                                        ]
                                                      };
                                                    }
                                                    if (!activeElement.strokeColor2 || (!activeElement.strokeColor2.startsWith('linear') && !activeElement.strokeColor2.startsWith('radial'))) {
                                                      updates.strokeColor2 = 'linear-gradient(90deg, rgba(255, 255, 255, 1) 0%, rgba(0, 0, 0, 1) 100%)';
                                                    }
                                                  } else {
                                                    const firstStopColor = activeElement.borderGradient2?.stops?.[0]?.color || '#000000';
                                                    updates.strokeColor2 = firstStopColor.startsWith('#') ? firstStopColor : '#000000';
                                                  }
                                                  updateActiveElement(updates);
                                                }}
                                                style={{ width: '100%', backgroundColor: '#1b1b1d', border: '1px solid #3c3c42', borderRadius: '4px', padding: '4px', color: '#fff', fontSize: '11px', outline: 'none' }}
                                              >
                                                <option value="solid">Solid Color</option>
                                                <option value="gradient">Gradient</option>
                                              </select>
                                            </div>

                                            {/* Secondary Border Color / Gradient Value */}
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                              <span style={{ fontSize: '10px', color: '#8e8e93' }}>
                                                {activeElement.borderFillType2 === 'gradient' ? 'Gradient Fill (Click to Edit)' : 'Secondary Color'}
                                              </span>
                                              {activeElement.borderFillType2 === 'gradient' ? (
                                                <div 
                                                  onClick={() => {
                                                    const defaultGrad = {
                                                      type: 'linear',
                                                      angle: 90,
                                                      stops: [
                                                        { color: '#ffffff', position: 0, opacity: 1 },
                                                        { color: '#000000', position: 100, opacity: 1 }
                                                      ]
                                                    };
                                                    setGradientState(activeElement.borderGradient2 || defaultGrad);
                                                    setSelectedStopIndex(0);
                                                    setEditingGradientTarget('secondary');
                                                    setShowGradientModal(true);
                                                  }}
                                                  style={{
                                                    width: '100%',
                                                    height: '24px',
                                                    borderRadius: '4px',
                                                    border: '1px solid #3c3c42',
                                                    background: activeElement.strokeColor2 || 'linear-gradient(90deg, #ffffff 0%, #000000 100%)',
                                                    cursor: 'pointer',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    fontSize: '9px',
                                                    color: '#ffffff',
                                                    textShadow: '0 1px 2px rgba(0,0,0,0.8)',
                                                    fontWeight: 'bold',
                                                    userSelect: 'none'
                                                  }}
                                                >
                                                  Edit Gradient
                                                </div>
                                              ) : (
                                                <div style={{ display: 'flex', gap: '6px' }}>
                                                  {renderColorInputRow(
                                                    activeElement.strokeColor2,
                                                    (val) => updateActiveElement({ strokeColor2: val })
                                                  )}
                                                </div>
                                              )}
                                            </div>
                                          </fieldset>
                                        </fieldset>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              )}
                            </>
                          )}
                        </div>
                      )}

                      {/* SHADOW PANEL */}
                      {activeElement && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', borderTop: '1px solid #3c3c42', paddingTop: '10px', marginTop: '4px' }}>
                          <div 
                            onClick={() => setShadowExpanded(prev => !prev)}
                            style={{ 
                              display: 'flex', 
                              alignItems: 'center', 
                              gap: '6px', 
                              cursor: 'pointer',
                              userSelect: 'none'
                            }}
                          >
                            <span style={{ fontSize: '8px', color: '#8e8e93', width: '8px' }}>
                              {shadowExpanded ? '▼' : '▶'}
                            </span>
                            <span style={{ fontSize: '10px', fontWeight: 'bold', color: '#8e8e93', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                              Shadow
                            </span>
                          </div>

                          {shadowExpanded && (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', paddingLeft: '14px' }}>
                              {/* Enable Shadow Toggle */}
                              <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px', color: '#dfdfe3', cursor: 'pointer' }}>
                                <input
                                  type="checkbox"
                                  checked={activeElement.shadowEnabled || false}
                                  onChange={(e) => updateActiveElement({ shadowEnabled: e.target.checked })}
                                  style={{ cursor: 'pointer' }}
                                />
                                <span>Enable Shadow</span>
                              </label>

                              {activeElement.shadowEnabled && (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '4px' }}>
                                  {/* Offset X & Offset Y */}
                                  <div style={{ display: 'flex', gap: '8px' }}>
                                    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '9px', color: '#8e8e93' }}>
                                        <span>Offset X</span>
                                        <span>{activeElement.shadowOffsetX || 0}px</span>
                                      </div>
                                      <input
                                        type="range"
                                        min="-50"
                                        max="50"
                                        value={activeElement.shadowOffsetX || 0}
                                        onChange={(e) => updateActiveElement({ shadowOffsetX: parseInt(e.target.value) || 0 })}
                                        style={{ width: '100%', accentColor: '#1677ff', height: '4px', cursor: 'pointer' }}
                                      />
                                    </div>
                                    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '9px', color: '#8e8e93' }}>
                                        <span>Offset Y</span>
                                        <span>{activeElement.shadowOffsetY || 0}px</span>
                                      </div>
                                      <input
                                        type="range"
                                        min="-50"
                                        max="50"
                                        value={activeElement.shadowOffsetY || 0}
                                        onChange={(e) => updateActiveElement({ shadowOffsetY: parseInt(e.target.value) || 0 })}
                                        style={{ width: '100%', accentColor: '#1677ff', height: '4px', cursor: 'pointer' }}
                                      />
                                    </div>
                                  </div>

                                  {/* Blur Radius & Spread Radius */}
                                  <div style={{ display: 'flex', gap: '8px' }}>
                                    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '9px', color: '#8e8e93' }}>
                                        <span>Blur</span>
                                        <span>{activeElement.shadowBlur !== undefined ? activeElement.shadowBlur : 5}px</span>
                                      </div>
                                      <input
                                        type="range"
                                        min="0"
                                        max="50"
                                        value={activeElement.shadowBlur !== undefined ? activeElement.shadowBlur : 5}
                                        onChange={(e) => updateActiveElement({ shadowBlur: parseInt(e.target.value) || 0 })}
                                        style={{ width: '100%', accentColor: '#1677ff', height: '4px', cursor: 'pointer' }}
                                      />
                                    </div>
                                    
                                    {/* Spread is only for box-shadow (non-text layers) */}
                                    {activeElement.type !== 'text' && (
                                      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '9px', color: '#8e8e93' }}>
                                          <span>Spread</span>
                                          <span>{activeElement.shadowSpread || 0}px</span>
                                        </div>
                                        <input
                                          type="range"
                                          min="-20"
                                          max="20"
                                          value={activeElement.shadowSpread || 0}
                                          onChange={(e) => updateActiveElement({ shadowSpread: parseInt(e.target.value) || 0 })}
                                          style={{ width: '100%', accentColor: '#1677ff', height: '4px', cursor: 'pointer' }}
                                        />
                                      </div>
                                    )}
                                  </div>

                                  {/* Color & Opacity Row */}
                                  <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-end' }}>
                                    {/* Shadow Color */}
                                    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                      <span style={{ fontSize: '9px', color: '#8e8e93' }}>Color</span>
                                      {renderColorInputRow(
                                        activeElement.shadowColor || '#000000',
                                        (val) => updateActiveElement({ shadowColor: val })
                                      )}
                                    </div>

                                    {/* Shadow Opacity */}
                                    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '9px', color: '#8e8e93' }}>
                                        <span>Opacity</span>
                                        <span>{Math.round((activeElement.shadowOpacity !== undefined ? activeElement.shadowOpacity : 0.5) * 100)}%</span>
                                      </div>
                                      <input
                                        type="range"
                                        min="0"
                                        max="1"
                                        step="0.05"
                                        value={activeElement.shadowOpacity !== undefined ? activeElement.shadowOpacity : 0.5}
                                        onChange={(e) => updateActiveElement({ shadowOpacity: parseFloat(e.target.value) })}
                                        style={{ width: '100%', accentColor: '#1677ff', height: '4px', cursor: 'pointer' }}
                                      />
                                    </div>
                                  </div>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      )}

                      {/* GLOW PANEL */}
                      {activeElement && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', borderTop: '1px solid #3c3c42', paddingTop: '10px', marginTop: '4px' }}>
                          <div 
                            onClick={() => setGlowExpanded(prev => !prev)}
                            style={{ 
                              display: 'flex', 
                              alignItems: 'center', 
                              gap: '6px', 
                              cursor: 'pointer',
                              userSelect: 'none'
                            }}
                          >
                            <span style={{ fontSize: '8px', color: '#8e8e93', width: '8px' }}>
                              {glowExpanded ? '▼' : '▶'}
                            </span>
                            <span style={{ fontSize: '10px', fontWeight: 'bold', color: '#8e8e93', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                              Glow
                            </span>
                          </div>

                          {glowExpanded && (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', paddingLeft: '14px' }}>
                              {/* Enable Glow Toggle */}
                              <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px', color: '#dfdfe3', cursor: 'pointer' }}>
                                <input
                                  type="checkbox"
                                  checked={activeElement.glowEnabled || false}
                                  onChange={(e) => updateActiveElement({ glowEnabled: e.target.checked })}
                                  style={{ cursor: 'pointer' }}
                                />
                                <span>Enable Glow</span>
                              </label>

                              {activeElement.glowEnabled && (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '4px' }}>
                                  {/* Glow Type: Outer vs Inner (Not applicable to text) */}
                                  {activeElement.type !== 'text' && (
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                      <span style={{ fontSize: '9px', color: '#8e8e93' }}>Glow Type</span>
                                      <select
                                        value={activeElement.glowType || 'outer'}
                                        onChange={(e) => updateActiveElement({ glowType: e.target.value })}
                                        style={{ width: '100%', backgroundColor: '#1b1b1d', border: '1px solid #3c3c42', borderRadius: '4px', padding: '4px', color: '#fff', fontSize: '11px', outline: 'none' }}
                                      >
                                        <option value="outer">Outer Glow</option>
                                        <option value="inner">Inner Glow</option>
                                      </select>
                                    </div>
                                  )}

                                  {/* Blur Radius & Spread Radius */}
                                  <div style={{ display: 'flex', gap: '8px' }}>
                                    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '9px', color: '#8e8e93' }}>
                                        <span>Glow Size (Blur)</span>
                                        <span>{activeElement.glowBlur !== undefined ? activeElement.glowBlur : 10}px</span>
                                      </div>
                                      <input
                                        type="range"
                                        min="0"
                                        max="50"
                                        value={activeElement.glowBlur !== undefined ? activeElement.glowBlur : 10}
                                        onChange={(e) => updateActiveElement({ glowBlur: parseInt(e.target.value) || 0 })}
                                        style={{ width: '100%', accentColor: '#1677ff', height: '4px', cursor: 'pointer' }}
                                      />
                                    </div>
                                    
                                    {/* Spread is only for box-shadow based glows (non-text layers) */}
                                    {activeElement.type !== 'text' && (
                                      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '9px', color: '#8e8e93' }}>
                                          <span>Glow Spread</span>
                                          <span>{activeElement.glowSpread || 0}px</span>
                                        </div>
                                        <input
                                          type="range"
                                          min="0"
                                          max="20"
                                          value={activeElement.glowSpread || 0}
                                          onChange={(e) => updateActiveElement({ glowSpread: parseInt(e.target.value) || 0 })}
                                          style={{ width: '100%', accentColor: '#1677ff', height: '4px', cursor: 'pointer' }}
                                        />
                                      </div>
                                    )}
                                  </div>

                                  {/* Color & Opacity Row */}
                                  <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-end' }}>
                                    {/* Glow Color */}
                                    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                      <span style={{ fontSize: '9px', color: '#8e8e93' }}>Glow Color</span>
                                      {renderColorInputRow(
                                        activeElement.glowColor || '#00ff00',
                                        (val) => updateActiveElement({ glowColor: val })
                                      )}
                                    </div>

                                    {/* Glow Opacity */}
                                    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '9px', color: '#8e8e93' }}>
                                        <span>Glow Opacity</span>
                                        <span>{Math.round((activeElement.glowOpacity !== undefined ? activeElement.glowOpacity : 0.6) * 100)}%</span>
                                      </div>
                                      <input
                                        type="range"
                                        min="0"
                                        max="1"
                                        step="0.05"
                                        value={activeElement.glowOpacity !== undefined ? activeElement.glowOpacity : 0.6}
                                        onChange={(e) => updateActiveElement({ glowOpacity: parseFloat(e.target.value) })}
                                        style={{ width: '100%', accentColor: '#1677ff', height: '4px', cursor: 'pointer' }}
                                      />
                                    </div>
                                  </div>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ) : (
                    <div style={{ padding: '30px 0', textAlign: 'center', color: '#5e5e64', fontSize: '11px' }}>
                      No element selected. Select an element on the canvas to edit styles.
                    </div>
                  )}
                </div>
              )}

              {activeRightTab === 'settings' && (
                <div style={{ flex: 1, overflowY: 'auto', padding: '12px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  
                  {/* SYSTEM CARD TEMPLATE SECTION (Admin only) */}
                  {!isTrophyCardMode && isAdmin && (
                    <div style={{
                      backgroundColor: isSystemTemplate ? 'rgba(234, 179, 8, 0.08)' : '#1b1b1d',
                      border: isSystemTemplate ? '1px solid rgba(234, 179, 8, 0.4)' : '1px solid #3c3c42',
                      borderRadius: '6px',
                      padding: '10px 12px',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '6px',
                      transition: 'all 0.2s'
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <span style={{ fontSize: '13px' }}>⭐</span>
                          <span style={{ fontSize: '11px', fontWeight: '700', color: isSystemTemplate ? '#facc15' : '#ffffff' }}>
                            System Card Template
                          </span>
                        </div>
                        <input
                          type="checkbox"
                          checked={isSystemTemplate}
                          onChange={(e) => setIsSystemTemplate(e.target.checked)}
                          style={{ cursor: 'pointer', accentColor: '#eab308', width: '16px', height: '16px' }}
                        />
                      </div>
                      <div style={{ fontSize: '10px', color: '#8e8e93', lineHeight: '1.4' }}>
                        Designate this template as an official pre-designed system template for use in <strong>Purchased Package Automation</strong>.
                      </div>
                    </div>
                  )}

                  {/* CARD-LEVEL IMAGE OPTIMIZATION POLICY */}
                  <div style={{
                    backgroundColor: '#1b1b1d',
                    border: '1px solid #3c3c42',
                    borderRadius: '6px',
                    padding: '10px 12px',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '6px'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <span style={{ fontSize: '13px' }}>⚡</span>
                        <span style={{ fontSize: '11px', fontWeight: '700', color: '#38bdf8' }}>
                          Card Image Optimization
                        </span>
                      </div>
                      <input
                        type="checkbox"
                        data-testid="card-auto-optimize-images-toggle"
                        checked={autoOptimizeImages !== false}
                        onChange={(e) => {
                          saveHistoryState();
                          setAutoOptimizeImages(e.target.checked);
                        }}
                        style={{ cursor: 'pointer', accentColor: '#1677ff', width: '16px', height: '16px' }}
                      />
                    </div>
                    <div style={{ fontSize: '10px', color: '#8e8e93', lineHeight: '1.4' }}>
                      Auto-serves lightweight WebP variants to dramatically speed up card rendering on mobile devices, while permanently retaining original high-resolution master images for lossless resampling.
                    </div>
                  </div>

                  {/* COLLAPSIBLE FILL PANEL */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    <div 
                      onClick={() => setFillExpanded(prev => !prev)}
                      style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: '6px', 
                        cursor: 'pointer',
                        userSelect: 'none'
                      }}
                    >
                      <span style={{ fontSize: '8px', color: '#8e8e93', width: '8px' }}>
                        {fillExpanded ? '▼' : '▶'}
                      </span>
                      <span style={{ fontSize: '10px', fontWeight: 'bold', color: '#8e8e93', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        Fill
                      </span>
                    </div>

                    {fillExpanded && (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', paddingLeft: '14px' }}>
                        {/* Fill Type */}
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                          <span style={{ fontSize: '10px', color: '#8e8e93' }}>Background Fill Type</span>
                          <select
                            value={sides[activeSide].background.type || 'color'}
                            onChange={(e) => {
                              const val = e.target.value;
                              saveHistoryState();
                              if (val === 'gradient') {
                                const savedGrad = sides[activeSide].background.gradient || {
                                  type: 'linear',
                                  angle: 90,
                                  stops: [
                                    { color: '#1e293b', position: 0, opacity: 1 },
                                    { color: '#0f172a', position: 100, opacity: 1 }
                                  ]
                                };
                                setSides({
                                  ...sides,
                                  [activeSide]: {
                                    ...sides[activeSide],
                                    background: {
                                      type: 'gradient',
                                      value: generateGradientString(savedGrad),
                                      gradient: savedGrad
                                    }
                                  }
                                });
                              } else if (val === 'pattern') {
                                const basePat = sides[activeSide].background.pattern || {
                                  id: DEFAULT_CARD_PATTERNS[0].id,
                                  name: DEFAULT_CARD_PATTERNS[0].name,
                                  image: DEFAULT_CARD_PATTERNS[0].image,
                                  tileWidth: DEFAULT_CARD_PATTERNS[0].tileWidth,
                                  tileHeight: DEFAULT_CARD_PATTERNS[0].tileHeight,
                                  scale: 100,
                                  opacity: 100,
                                  repeat: 'repeat',
                                  tintColor: '#0f172a',
                                  blendMode: 'normal'
                                };
                                setSides({
                                  ...sides,
                                  [activeSide]: {
                                    ...sides[activeSide],
                                    background: {
                                      type: 'pattern',
                                      value: generatePatternString(basePat),
                                      pattern: basePat
                                    }
                                  }
                                });
                              } else {
                                const firstColor = sides[activeSide].background.gradient?.stops?.[0]?.color || sides[activeSide].background.pattern?.tintColor || '#0f172a';
                                setSides({
                                  ...sides,
                                  [activeSide]: {
                                    ...sides[activeSide],
                                    background: {
                                      type: 'color',
                                      value: firstColor.startsWith('#') ? firstColor : '#0f172a'
                                    }
                                  }
                                });
                              }
                            }}
                            style={{ width: '100%', backgroundColor: '#1b1b1d', border: '1px solid #3c3c42', borderRadius: '4px', padding: '4px', color: '#fff', fontSize: '11px', outline: 'none' }}
                          >
                            <option value="color">Solid Color</option>
                            <option value="gradient">Gradient</option>
                            <option value="pattern">Pattern / Texture</option>
                          </select>
                        </div>

                        {/* Fill Color / Gradient / Pattern Selector */}
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                          <span style={{ fontSize: '10px', color: '#8e8e93' }}>
                            {sides[activeSide].background.type === 'gradient' 
                              ? 'Gradient (Click to Edit)' 
                              : (sides[activeSide].background.type === 'pattern' ? 'Pattern (Click to Choose / Edit)' : 'Color')}
                          </span>
                          {sides[activeSide].background.type === 'pattern' ? (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                              <div
                                onClick={() => {
                                  setPatternState(sides[activeSide].background.pattern || {
                                    id: DEFAULT_CARD_PATTERNS[0].id,
                                    name: DEFAULT_CARD_PATTERNS[0].name,
                                    image: DEFAULT_CARD_PATTERNS[0].image,
                                    tileWidth: DEFAULT_CARD_PATTERNS[0].tileWidth,
                                    tileHeight: DEFAULT_CARD_PATTERNS[0].tileHeight,
                                    scale: 100,
                                    opacity: 100,
                                    repeat: 'repeat',
                                    tintColor: '#0f172a',
                                    blendMode: 'normal'
                                  });
                                  setEditingPatternTarget('cardBackground');
                                  setShowPatternModal(true);
                                }}
                                style={{
                                  width: '100%',
                                  height: '36px',
                                  borderRadius: '4px',
                                  border: '1px solid #3c3c42',
                                  cursor: 'pointer',
                                  display: 'flex',
                                  alignItems: 'center',
                                  padding: '0 8px',
                                  gap: '8px',
                                  backgroundColor: '#232326',
                                  userSelect: 'none'
                                }}
                                title="Click to Choose Pattern / Texture"
                              >
                                <div style={{
                                  width: '24px',
                                  height: '24px',
                                  borderRadius: '3px',
                                  border: '1px solid #4a4a52',
                                  backgroundColor: sides[activeSide].background.pattern?.tintColor || '#ffffff',
                                  backgroundImage: `url("${sides[activeSide].background.pattern?.image || DEFAULT_CARD_PATTERNS[0].image}")`,
                                  backgroundRepeat: 'repeat',
                                  backgroundSize: `${Math.max(2, Math.round((sides[activeSide].background.pattern?.tileWidth || 32) * ((sides[activeSide].background.pattern?.scale || 100) / 100)))}px`,
                                  flexShrink: 0
                                }} />
                                <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minWidth: 0 }}>
                                  <span style={{ fontSize: '11px', color: '#ffffff', fontWeight: 'bold', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                    {sides[activeSide].background.pattern?.name || 'Choose Pattern...'}
                                  </span>
                                  <span style={{ fontSize: '9px', color: '#8e8e93' }}>
                                    Scale: {sides[activeSide].background.pattern?.scale || 100}% • Opacity: {sides[activeSide].background.pattern?.opacity !== undefined ? sides[activeSide].background.pattern.opacity : 100}%
                                  </span>
                                </div>
                                <span style={{ fontSize: '10px', color: '#3b82f6', fontWeight: 'bold' }}>
                                  Edit
                                </span>
                              </div>

                              {/* Quick Scale Slider */}
                              <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', marginTop: '2px' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '9px', color: '#8e8e93' }}>
                                  <span>Pattern Scale</span>
                                  <span style={{ color: '#fff' }}>{sides[activeSide].background.pattern?.scale || 100}%</span>
                                </div>
                                <input
                                  type="range"
                                  min="10"
                                  max="400"
                                  value={sides[activeSide].background.pattern?.scale || 100}
                                  onChange={(e) => {
                                    const scaleVal = parseInt(e.target.value) || 100;
                                    const newPattern = { ...(sides[activeSide].background.pattern || DEFAULT_CARD_PATTERNS[0]), scale: scaleVal };
                                    saveHistoryState();
                                    setSides({
                                      ...sides,
                                      [activeSide]: {
                                        ...sides[activeSide],
                                        background: {
                                          ...sides[activeSide].background,
                                          value: generatePatternString(newPattern),
                                          pattern: newPattern
                                        }
                                      }
                                    });
                                  }}
                                  style={{ accentColor: '#1677ff', height: '4px', cursor: 'pointer' }}
                                />
                              </div>

                              {/* Quick Opacity Slider */}
                              <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '9px', color: '#8e8e93' }}>
                                  <span>Pattern Opacity</span>
                                  <span style={{ color: '#fff' }}>{sides[activeSide].background.pattern?.opacity !== undefined ? sides[activeSide].background.pattern.opacity : 100}%</span>
                                </div>
                                <input
                                  type="range"
                                  min="0"
                                  max="100"
                                  value={sides[activeSide].background.pattern?.opacity !== undefined ? sides[activeSide].background.pattern.opacity : 100}
                                  onChange={(e) => {
                                    const opVal = parseInt(e.target.value);
                                    const newPattern = { ...(sides[activeSide].background.pattern || DEFAULT_CARD_PATTERNS[0]), opacity: opVal };
                                    saveHistoryState();
                                    setSides({
                                      ...sides,
                                      [activeSide]: {
                                        ...sides[activeSide],
                                        background: {
                                          ...sides[activeSide].background,
                                          value: generatePatternString(newPattern),
                                          pattern: newPattern
                                        }
                                      }
                                    });
                                  }}
                                  style={{ accentColor: '#1677ff', height: '4px', cursor: 'pointer' }}
                                />
                              </div>

                              {/* Quick Tint Color */}
                              <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                                <span style={{ fontSize: '9px', color: '#8e8e93' }}>Tint / Base Color</span>
                                {renderColorInputRow(
                                  sides[activeSide].background.pattern?.tintColor || '',
                                  (val) => {
                                    const newPattern = { ...(sides[activeSide].background.pattern || DEFAULT_CARD_PATTERNS[0]), tintColor: val };
                                    saveHistoryState();
                                    setSides({
                                      ...sides,
                                      [activeSide]: {
                                        ...sides[activeSide],
                                        background: {
                                          ...sides[activeSide].background,
                                          value: generatePatternString(newPattern),
                                          pattern: newPattern
                                        }
                                      }
                                    });
                                  }
                                )}
                              </div>
                            </div>
                          ) : sides[activeSide].background.type === 'gradient' ? (
                            <div 
                              onClick={() => {
                                const defaultGrad = {
                                  type: 'linear',
                                  angle: 90,
                                  stops: [
                                    { color: '#1e293b', position: 0, opacity: 1 },
                                    { color: '#0f172a', position: 100, opacity: 1 }
                                  ]
                                };
                                setGradientState(sides[activeSide].background.gradient || defaultGrad);
                                setEditingGradientTarget('cardBackground');
                                setSelectedStopIndex(0);
                                setShowGradientModal(true);
                              }}
                              style={{
                                width: '100%',
                                height: '24px',
                                borderRadius: '4px',
                                border: '1px solid #3c3c42',
                                background: sides[activeSide].background.value || 'linear-gradient(90deg, #1e293b 0%, #0f172a 100%)',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: '9px',
                                color: '#ffffff',
                                textShadow: '0 1px 2px rgba(0,0,0,0.8)',
                                fontWeight: 'bold',
                                userSelect: 'none'
                              }}
                            >
                              Edit Gradient
                            </div>
                          ) : renderColorInputRow(
                            sides[activeSide].background.value || '',
                            (val) => {
                              saveHistoryState();
                              setSides({
                                ...sides,
                                [activeSide]: {
                                  ...sides[activeSide],
                                  background: {
                                    type: 'color',
                                    value: val
                                  }
                                }
                              });
                            }
                          )}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* COLLAPSIBLE BORDERS & EDGES PANEL */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', borderTop: '1px solid #3c3c42', paddingTop: '10px', marginTop: '4px' }}>
                    <div 
                      onClick={() => setBordersExpanded(prev => !prev)}
                      style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: '6px', 
                        cursor: 'pointer',
                        userSelect: 'none'
                      }}
                    >
                      <span style={{ fontSize: '8px', color: '#8e8e93', width: '8px' }}>
                        {bordersExpanded ? '▼' : '▶'}
                      </span>
                      <span style={{ fontSize: '10px', fontWeight: 'bold', color: '#8e8e93', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        Borders & Edges
                      </span>
                    </div>

                    {bordersExpanded && (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', paddingLeft: '14px' }}>
                        
                        {/* Corner Rounding */}
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '10px', color: '#8e8e93' }}>
                            <span>Card Corner Rounding</span>
                            <span>{parseInt(globalStyles.borderRadius) || 0}px</span>
                          </div>
                          <input
                            type="range"
                            min="0"
                            max="80"
                            value={parseInt(globalStyles.borderRadius) || 0}
                            onChange={(e) => {
                              saveHistoryState();
                              setGlobalStyles({ ...globalStyles, borderRadius: `${e.target.value}px` });
                            }}
                            style={{ width: '100%', accentColor: '#1677ff', height: '4px', cursor: 'pointer' }}
                          />
                        </div>

                        {/* Border Style Selector */}
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                          <span style={{ fontSize: '10px', color: '#8e8e93' }}>Border Style</span>
                          <div style={{ display: 'flex', gap: '4px' }}>
                            {['solid', 'dashed', 'dotted', 'none'].map(st => (
                              <button
                                key={st}
                                onClick={() => {
                                  saveHistoryState();
                                  setGlobalStyles({ ...globalStyles, borderStyle: st });
                                }}
                                style={{
                                  flex: 1,
                                  padding: '4px 0',
                                  fontSize: '10px',
                                  border: '1px solid #3c3c42',
                                  borderRadius: '3px',
                                  backgroundColor: globalStyles.borderStyle === st ? '#1677ff' : '#1b1b1d',
                                  color: '#fff',
                                  cursor: 'pointer',
                                  textTransform: 'capitalize'
                                }}
                              >
                                {st}
                              </button>
                            ))}
                          </div>
                        </div>

                        {globalStyles.borderStyle !== 'none' && (
                          <>
                            {/* Border Thickness */}
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '10px', color: '#8e8e93' }}>
                                <span>Border Thickness</span>
                                <span>{globalStyles.borderWidth || 0}px</span>
                              </div>
                              <input
                                type="range"
                                min="0"
                                max="20"
                                value={globalStyles.borderWidth || 0}
                                onChange={(e) => {
                                  saveHistoryState();
                                  setGlobalStyles({ ...globalStyles, borderWidth: parseInt(e.target.value) || 0 });
                                }}
                                style={{ width: '100%', accentColor: '#1677ff', height: '4px', cursor: 'pointer' }}
                              />
                            </div>

                            {/* Border Color */}
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                              <span style={{ fontSize: '10px', color: '#8e8e93' }}>Border Color</span>
                                {renderColorInputRow(
                                  globalStyles.borderColor || '',
                                  (val) => {
                                    saveHistoryState();
                                    setGlobalStyles({ ...globalStyles, borderColor: val });
                                  }
                                )}
                            </div>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {activeRightTab === 'data' && (
                <div style={{ flex: 1, overflowY: 'auto', padding: '12px', display: 'flex', flexDirection: 'column', gap: '14px' }}>
                  {/* Collapsible header for Link Datasets */}
                  <div 
                    style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'space-between',
                      borderBottom: '1px solid #3c3c42',
                      paddingBottom: '8px',
                      userSelect: 'none'
                    }}
                  >
                    <div 
                      onClick={() => setDatasetsPanelExpanded(!datasetsPanelExpanded)}
                      style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', flex: 1 }}
                    >
                      <span style={{ fontSize: '8px', color: '#8e8e93', width: '8px' }}>
                        {datasetsPanelExpanded ? '▼' : '▶'}
                      </span>
                      <span style={{ fontSize: '10px', fontWeight: 'bold', color: '#8e8e93', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        Link Datasets
                      </span>
                    </div>
                    <button
                      disabled={refreshingData}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRefreshData();
                      }}
                      style={{
                        backgroundColor: '#1b1b1d',
                        border: '1px solid #3c3c42',
                        borderRadius: '4px',
                        padding: '2px 8px',
                        fontSize: '9px',
                        color: refreshingData ? '#8e8e93' : '#dfdfe3',
                        cursor: refreshingData ? 'not-allowed' : 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                        opacity: refreshingData ? 0.75 : 1
                      }}
                      title="Refresh dynamic datasets & template sample data"
                    >
                      <style>{`
                        @keyframes spin-refresh {
                          0% { transform: rotate(0deg); }
                          100% { transform: rotate(360deg); }
                        }
                      `}</style>
                      <svg 
                        width="10" 
                        height="10" 
                        viewBox="0 0 24 24" 
                        fill="none" 
                        stroke="currentColor" 
                        strokeWidth="2.5" 
                        strokeLinecap="round" 
                        strokeLinejoin="round"
                        style={{
                          display: 'block',
                          animation: refreshingData ? 'spin-refresh 1s linear infinite' : 'none',
                          transformOrigin: 'center'
                        }}
                      >
                        <path d="M21.5 2v6h-6M21.34 15.57a10 10 0 1 1-.57-8.38l5.67-5.67" />
                      </svg>
                      {refreshingData ? 'Refreshing...' : 'Refresh Data'}
                    </button>
                  </div>

                  {datasetsPanelExpanded && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                      {/* Add Dataset Selector Row */}
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                        <span style={{ fontSize: '10px', color: '#8e8e93' }}>Available Datasets</span>
                        <div style={{ display: 'flex', gap: '6px' }}>
                          <select
                            id="add-dataset-select"
                            style={{ flex: 1, backgroundColor: '#1b1b1d', border: '1px solid #3c3c42', borderRadius: '4px', padding: '4px', color: '#fff', fontSize: '11px', outline: 'none' }}
                          >
                            {Object.keys(datasets)
                              .filter(key => !linkedDatasets.includes(key))
                              .map(key => (
                                <option key={key} value={key}>{datasets[key].name}</option>
                              ))
                            }
                            {Object.keys(datasets).filter(key => !linkedDatasets.includes(key)).length === 0 && (
                              <option value="">No more datasets available</option>
                            )}
                          </select>
                          <button
                            onClick={() => {
                              const sel = document.getElementById('add-dataset-select');
                              const key = sel ? sel.value : null;
                              if (key && key !== "") {
                                // Link new dataset
                                setLinkedDatasets(prev => [...prev, key]);
                                // check all variables by default
                                const variables = datasets[key]?.variables || {};
                                const checks = {};
                                Object.keys(variables).forEach(v => {
                                  checks[v] = true;
                                });
                                setEnabledFields(prev => ({
                                  ...prev,
                                  [key]: checks
                                }));
                                // expand by default
                                setExpandedDatasets(prev => ({
                                  ...prev,
                                  [key]: true
                                }));
                              }
                            }}
                            disabled={Object.keys(datasets).filter(key => !linkedDatasets.includes(key)).length === 0}
                            style={{
                              backgroundColor: '#1677ff',
                              color: '#fff',
                              border: 'none',
                              borderRadius: '4px',
                              padding: '4px 10px',
                              fontSize: '11px',
                              fontWeight: 'bold',
                              cursor: Object.keys(datasets).filter(key => !linkedDatasets.includes(key)).length === 0 ? 'not-allowed' : 'pointer',
                              opacity: Object.keys(datasets).filter(key => !linkedDatasets.includes(key)).length === 0 ? 0.5 : 1
                            }}
                          >
                            Link
                          </button>
                        </div>
                      </div>

                      {/* List of Linked Datasets */}
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', borderTop: '1px solid #3c3c42', paddingTop: '10px' }}>
                        <span style={{ fontSize: '10px', color: '#8e8e93', fontWeight: 'bold' }}>Linked Datasets</span>
                        {linkedDatasets.length === 0 ? (
                          <span style={{ fontSize: '10px', color: '#5e5e64', fontStyle: 'italic', textAlign: 'center', padding: '10px 0' }}>
                            No datasets linked. Link a dataset to enable dynamic text variables.
                          </span>
                        ) : (
                          linkedDatasets.map(key => {
                            const ds = datasets[key];
                            if (!ds) return null;
                            const isExpanded = !!expandedDatasets[key];
                            const enabledForDS = enabledFields[key] || {};
                            const fieldsList = ds.fields && ds.fields.length > 0
                              ? ds.fields.map(f => ({
                                  label: f.label,
                                  alias: f.alias || f.label,
                                  sample_data: f.sample_data || ds.variables?.[f.label] || ''
                                }))
                              : Object.keys(ds.variables || {}).map(k => ({
                                  label: k,
                                  alias: k.charAt(0).toUpperCase() + k.slice(1).replace(/_/g, ' '),
                                  sample_data: ds.variables[k] || ''
                                }));
                            
                            return (
                              <div key={key} style={{ border: '1px solid #3c3c42', borderRadius: '4px', backgroundColor: '#1b1b1d', display: 'flex', flexDirection: 'column' }}>
                                {/* Header row */}
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '6px 8px', cursor: 'pointer', borderBottom: isExpanded ? '1px solid #3c3c42' : 'none' }}>
                                  <div 
                                    onClick={() => setExpandedDatasets(prev => ({ ...prev, [key]: !isExpanded }))}
                                    style={{ display: 'flex', alignItems: 'center', gap: '6px', flex: 1 }}
                                  >
                                    <span style={{ fontSize: '8px', color: '#8e8e93', width: '8px', display: 'inline-block' }}>
                                      {isExpanded ? '▼' : '▶'}
                                    </span>
                                    <span style={{ fontSize: '11px', fontWeight: 'bold', color: '#dfdfe3' }}>
                                      {ds.name}
                                    </span>
                                  </div>
                                  <button
                                    onClick={() => {
                                      // Remove dataset
                                      setLinkedDatasets(prev => prev.filter(k => k !== key));
                                      // Clean up checks and expansion state
                                      setEnabledFields(prev => {
                                        const next = { ...prev };
                                        delete next[key];
                                        return next;
                                      });
                                      setHiddenFields(prev => {
                                        const next = { ...prev };
                                        delete next[key];
                                        return next;
                                      });
                                      setExpandedDatasets(prev => {
                                        const next = { ...prev };
                                        delete next[key];
                                        return next;
                                      });
                                    }}
                                    style={{
                                      backgroundColor: 'transparent',
                                      border: 'none',
                                      color: '#ff4d4f',
                                      cursor: 'pointer',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      padding: '2px'
                                    }}
                                    title="Remove Dataset"
                                  >
                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                      <polyline points="3 6 5 6 21 6" />
                                      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                                    </svg>
                                  </button>
                                </div>

                                {/* Expanded list of fields */}
                                {isExpanded && (
                                  <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', padding: '8px', backgroundColor: '#141416' }}>
                                    {fieldsList.map(fieldItem => {
                                      const isChecked = !!enabledForDS[fieldItem.label];
                                      return (
                                        <label key={fieldItem.label} style={{ display: 'flex', width: '100%', alignItems: 'center', gap: '8px', fontSize: '10px', color: '#dfdfe3', cursor: 'pointer', userSelect: 'none' }}>
                                          <input
                                            type="checkbox"
                                            checked={isChecked}
                                            onChange={(e) => {
                                              const checked = e.target.checked;
                                              setEnabledFields(prev => ({
                                                ...prev,
                                                [key]: {
                                                  ...(prev[key] || {}),
                                                  [fieldItem.label]: checked
                                                }
                                              }));
                                            }}
                                            style={{ cursor: 'pointer' }}
                                          />
                                          <div style={{ 
                                            display: 'flex', 
                                            flex: 1, 
                                            justifyContent: 'space-between', 
                                            alignItems: 'center',
                                            opacity: isChecked ? 1 : 0.45,
                                            transition: 'opacity 0.15s ease'
                                          }}>
                                            <div style={{ display: 'flex', flexDirection: 'column' }}>
                                              <span style={{ fontSize: '11px', fontWeight: '600', color: '#dfdfe3' }}>{fieldItem.alias || fieldItem.label}</span>
                                              <span style={{ fontSize: '9px', fontFamily: 'monospace', color: '#ff7875' }}>${fieldItem.label}</span>
                                            </div>
                                            <span style={{ color: '#8e8e93', fontSize: '10px', maxWidth: '140px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={String(fieldItem.sample_data || '')}>
                                              {String(fieldItem.sample_data || '')}
                                            </span>
                                          </div>
                                        </label>
                                      );
                                    })}
                                  </div>
                                )}
                              </div>
                            );
                          })
                        )}
                      </div>
                    </div>
                  )}

                  {/* Collapsible header for Dynamic Links */}
                  <div 
                    onClick={() => setDynamicLinksPanelExpanded(!dynamicLinksPanelExpanded)}
                    style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: '6px', 
                      cursor: 'pointer', 
                      borderBottom: '1px solid #3c3c42',
                      paddingTop: '10px',
                      paddingBottom: '8px',
                      userSelect: 'none'
                    }}
                  >
                    <span style={{ fontSize: '8px', color: '#8e8e93', width: '8px' }}>
                      {dynamicLinksPanelExpanded ? '▼' : '▶'}
                    </span>
                    <span style={{ fontSize: '10px', fontWeight: 'bold', color: '#8e8e93', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                      Dynamic Links
                    </span>
                  </div>

                  {dynamicLinksPanelExpanded && (() => {
                    const dynamicFields = getDynamicFieldsInTemplate();
                    const dynamicLinkMappingOptions = [
                      ...MAPPING_OPTIONS,
                      ...(dynamicFields.length > 0 ? [{
                        group: 'Template Dynamic Fields',
                        options: dynamicFields.map(f => ({
                          value: `dataset_field.${f.label}`,
                          label: `Field → ${f.alias || f.label.replace(/_/g, ' ')} ($${f.label})`
                        }))
                      }] : [])
                    ];

                    const allFrontElements = (sides.front?.elements || []).filter(e => e.visible !== false && e.type !== 'group');
                    const allBackElements = (sides.back?.elements || []).filter(e => e.visible !== false && e.type !== 'group');

                    const LINK_CONFIGS = [
                      {
                        key: 'booking',
                        label: 'Booking Link',
                        variable: '$booking_link',
                        altVars: [],
                        icon: '📅',
                        desc: 'Opens charter/operator booking page or custom URL'
                      },
                      {
                        key: 'website',
                        label: 'Website Link',
                        variable: '$website_link_element',
                        altVars: [],
                        icon: '🌐',
                        desc: 'Opens business website or custom URL in new tab'
                      },
                      {
                        key: 'email',
                        label: 'Email Link',
                        variable: '$emial_link',
                        altVars: ['$email_link'],
                        icon: '✉️',
                        desc: 'Opens email client via mailto: address'
                      },
                      {
                        key: 'verify',
                        label: 'Verify Link',
                        variable: '$verify_link',
                        altVars: [],
                        icon: '✓',
                        desc: 'Opens verification page / custom URL'
                      }
                    ];

                    return (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                        <p style={{ margin: 0, fontSize: '10px', color: '#8e8e93', lineHeight: '1.4' }}>
                          Assign interactive link elements and map them to dynamic system, organization, user, or dataset fields. Active links open directly when clicked in the flip preview.
                        </p>

                        {LINK_CONFIGS.map(item => {
                          const currentElem = (sides.front?.elements || []).concat(sides.back?.elements || []).find(e => 
                            e.linkVariable === item.variable || (item.altVars && item.altVars.includes(e.linkVariable))
                          );
                          const mappingKey = linkMappings[item.variable] || '';
                          const isMapped = !!mappingKey;

                          const linkInfo = resolveElementLink(
                            { linkVariable: item.variable, linkMapping: mappingKey },
                            linkMappings,
                            fieldMappings,
                            {},
                            datasets,
                            user,
                            orgData,
                            tenantConfig
                          );

                          const hasElement = !!currentElem;
                          const hasTarget = !!linkInfo && !!linkInfo.url;
                          const isActive = hasElement && hasTarget;

                          return (
                            <div
                              key={item.key}
                              style={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '6px',
                                backgroundColor: '#1b1b1d',
                                border: isActive ? '1px solid #1677ff' : (hasElement ? '1px solid #3c3c42' : '1px solid #2b2b2f'),
                                borderRadius: '6px',
                                padding: '8px 10px'
                              }}
                            >
                              {/* Header */}
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                                  <span style={{ fontSize: '11px' }}>{item.icon}</span>
                                  <span style={{ fontSize: '11px', fontWeight: '700', color: '#fff' }}>{item.label}</span>
                                  <span style={{ fontSize: '9px', fontFamily: 'monospace', color: '#38bdf8', backgroundColor: 'rgba(56, 189, 248, 0.12)', padding: '1px 4px', borderRadius: '3px' }}>
                                    {item.variable}
                                  </span>
                                </div>
                                <span style={{
                                  fontSize: '9px',
                                  fontWeight: '600',
                                  padding: '1px 5px',
                                  borderRadius: '3px',
                                  backgroundColor: isActive ? 'rgba(34, 197, 94, 0.15)' : 'rgba(255, 255, 255, 0.05)',
                                  color: isActive ? '#4ade80' : '#8e8e93',
                                  border: isActive ? '1px solid rgba(34, 197, 94, 0.3)' : '1px solid transparent'
                                }}>
                                  {isActive ? '● Active in Preview' : (hasElement ? '○ Blank Target' : '○ No Element')}
                                </span>
                              </div>

                              {/* Link Element Selector */}
                              <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
                                <span style={{ fontSize: '9px', color: '#8e8e93', fontWeight: '600' }}>Link Target Element:</span>
                                <select
                                  value={currentElem?.id || ""}
                                  onChange={(e) => handleSetLinkVariable(e.target.value, item.variable)}
                                  style={{
                                    backgroundColor: currentElem ? '#1e293b' : '#141416',
                                    border: currentElem ? '1px solid #3b82f6' : '1px solid #3c3c42',
                                    borderRadius: '4px',
                                    padding: '5px',
                                    color: currentElem ? '#60a5fa' : '#fff',
                                    fontSize: '10px',
                                    outline: 'none',
                                    width: '100%',
                                    cursor: 'pointer'
                                  }}
                                >
                                  <option value="">None (No Element Assigned)</option>
                                  {allFrontElements.length > 0 && (
                                    <optgroup label="Front Side Elements">
                                      {allFrontElements.map(e => (
                                        <option key={`front-${e.id}`} value={e.id}>
                                          [Front] {getLayerTypeIcon(e)} {e.name || e.text || `Unnamed ${e.type}`}
                                        </option>
                                      ))}
                                    </optgroup>
                                  )}
                                  {allBackElements.length > 0 && (
                                    <optgroup label="Back Side Elements">
                                      {allBackElements.map(e => (
                                        <option key={`back-${e.id}`} value={e.id}>
                                          [Back] {getLayerTypeIcon(e)} {e.name || e.text || `Unnamed ${e.type}`}
                                        </option>
                                      ))}
                                    </optgroup>
                                  )}
                                </select>
                              </div>

                              {/* "Map to:" Dropdown */}
                              <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                  <span style={{ fontSize: '9px', color: '#8e8e93', fontWeight: '600', whiteSpace: 'nowrap' }}>
                                    Map data to:
                                  </span>
                                  <select
                                    value={mappingKey}
                                    onChange={(e) => {
                                      const val = e.target.value;
                                      setLinkMappings(prev => ({
                                        ...prev,
                                        [item.variable]: val
                                      }));
                                    }}
                                    style={{
                                      flex: 1,
                                      backgroundColor: isMapped ? '#0c2838' : '#141416',
                                      border: isMapped ? '1px solid #0284c7' : '1px solid #3c3c42',
                                      borderRadius: '4px',
                                      padding: '4px 6px',
                                      color: isMapped ? '#38bdf8' : '#dfdfe3',
                                      fontSize: '10px',
                                      outline: 'none',
                                      cursor: 'pointer'
                                    }}
                                  >
                                    {dynamicLinkMappingOptions.map(opt => {
                                      if (opt.group) {
                                        return (
                                          <optgroup key={opt.group} label={opt.group}>
                                            {opt.options.map(subOpt => (
                                              <option key={subOpt.value} value={subOpt.value}>{subOpt.label}</option>
                                            ))}
                                          </optgroup>
                                        );
                                      }
                                      return (
                                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                                      );
                                    })}
                                  </select>
                                </div>
                              </div>

                              {/* Target URL Preview Banner */}
                              {linkInfo && linkInfo.url ? (
                                <div style={{
                                  fontSize: '9px',
                                  color: '#38bdf8',
                                  backgroundColor: 'rgba(56, 189, 248, 0.08)',
                                  border: '1px solid rgba(56, 189, 248, 0.25)',
                                  borderRadius: '4px',
                                  padding: '3px 6px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'space-between',
                                  gap: '4px',
                                  overflow: 'hidden'
                                }}>
                                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>
                                    {linkInfo.type === 'email' ? '✉️ ' : '🔗 '} {linkInfo.url}
                                  </span>
                                  <span style={{ fontSize: '8px', color: '#94a3b8', whiteSpace: 'nowrap' }}>
                                    {linkInfo.type === 'email' ? 'mailto:' : 'https://'}
                                  </span>
                                </div>
                              ) : (
                                <div style={{ fontSize: '9px', color: '#71717a', fontStyle: 'italic', padding: '1px 2px' }}>
                                  {hasElement ? 'Target value is currently blank' : 'No element assigned yet'}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    );
                  })()}
                </div>
              )}

              {activeRightTab === 'colour' && (
                <div style={{ flex: 1, overflowY: 'auto', padding: '12px', display: 'flex', flexDirection: 'column', gap: '14px' }}>
                  <span style={{ fontSize: '10px', fontWeight: 'bold', color: '#8e8e93', textTransform: 'uppercase' }}>Global Color Swatch</span>
                  <p style={{ margin: 0, fontSize: '10px', color: '#8e8e93', lineHeight: '1.4' }}>
                    Double-click solid colors to open the color picker, or double-click gradients to open the gradient editor.
                  </p>

                  {/* Hidden color picker for double-click solid colors */}
                  <StableColorInput
                    id="global-swatch-color-picker"
                    style={{ position: 'absolute', opacity: 0, width: 0, height: 0, pointerEvents: 'none' }}
                    value={localColorVal.startsWith('#') && localColorVal.length === 7 ? localColorVal : '#ffffff'}
                    onChange={(e) => {
                      const newVal = e.target.value;
                      setLocalColorVal(newVal);
                      updateGlobalColor(editingGlobalColor, newVal);
                    }}
                  />
                  
                  {/* Grid of colors */}
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(42px, 1fr))',
                    gap: '8px',
                    border: '1px solid #3c3c42',
                    borderRadius: '6px',
                    padding: '10px',
                    backgroundColor: '#1b1b1d'
                  }}>
                    {getGlobalColors().length === 0 ? (
                      <div style={{ gridColumn: '1 / -1', textAlign: 'center', fontSize: '11px', color: '#8e8e93', fontStyle: 'italic', padding: '10px 0' }}>
                        No colors found in design.
                      </div>
                    ) : (
                      getGlobalColors().map((col, idx) => {
                        const isSelected = editingGlobalColor === col;
                        const isGradient = col.includes('gradient');
                        return (
                          <button
                            key={`global-col-${idx}`}
                            onClick={() => setEditingGlobalColor(col)}
                            onDoubleClick={() => handleSwatchDoubleClick(col)}
                            style={{
                              aspectRatio: '1',
                              width: '100%',
                              borderRadius: '4px',
                              border: isSelected ? '2px solid #1677ff' : '1px solid #3c3c42',
                              background: col,
                              cursor: 'pointer',
                              padding: 0,
                              boxShadow: isSelected ? '0 0 8px rgba(22, 119, 255, 0.6)' : 'none',
                              position: 'relative'
                            }}
                            title={col}
                          >
                            {isGradient && (
                              <span style={{
                                position: 'absolute',
                                bottom: '2px',
                                right: '2px',
                                fontSize: '8px',
                                color: '#fff',
                                textShadow: '0 1px 2px rgba(0,0,0,0.8)',
                                pointerEvents: 'none'
                              }}>
                                ▧
                              </span>
                            )}
                          </button>
                        );
                      })
                    )}
                  </div>

                  {/* Editor for selected global color */}
                  {editingGlobalColor && (
                    <div style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '10px',
                      border: '1px solid #3c3c42',
                      borderRadius: '6px',
                      padding: '12px',
                      backgroundColor: '#141416'
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ fontSize: '10px', fontWeight: 'bold', color: '#dfdfe3' }}>Edit Selected Color</span>
                        <button
                          type="button"
                          onClick={() => setEditingGlobalColor(null)}
                          style={{
                            background: 'none',
                            border: 'none',
                            color: '#8e8e93',
                            fontSize: '11px',
                            cursor: 'pointer'
                          }}
                        >
                          Cancel
                        </button>
                      </div>

                      {/* Display active color string */}
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                        <span style={{ fontSize: '9px', color: '#8e8e93' }}>Original Color Value</span>
                        <div style={{
                          fontSize: '10px',
                          fontFamily: 'monospace',
                          color: '#dfdfe3',
                          backgroundColor: '#1b1b1d',
                          padding: '6px',
                          borderRadius: '4px',
                          border: '1px solid #2b2b2f',
                          wordBreak: 'break-all'
                        }}>
                          {editingGlobalColor}
                        </div>
                      </div>

                      {/* Color Adjustment controls */}
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '4px' }}>
                        <span style={{ fontSize: '9px', color: '#8e8e93' }}>Adjust Color / Gradient CSS</span>
                        
                        <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                          {/* Standard HTML Color Picker if it's not a gradient */}
                          {!localColorVal.includes('gradient') && (
                            <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                              <StableColorInput
                                value={localColorVal.startsWith('#') && localColorVal.length === 7 ? localColorVal : '#ffffff'}
                                onChange={(e) => {
                                  const newVal = e.target.value;
                                  setLocalColorVal(newVal);
                                  updateGlobalColor(editingGlobalColor, newVal);
                                }}
                                style={{
                                  width: '32px',
                                  height: '32px',
                                  border: 'none',
                                  borderRadius: '4px',
                                  padding: 0,
                                  cursor: 'pointer',
                                  backgroundColor: 'transparent'
                                }}
                              />
                              {typeof window !== 'undefined' && 'EyeDropper' in window && (
                                <button
                                  type="button"
                                  onClick={async () => {
                                    try {
                                      const eyeDropper = new window.EyeDropper();
                                      const result = await eyeDropper.open();
                                      setLocalColorVal(result.sRGBHex);
                                      updateGlobalColor(editingGlobalColor, result.sRGBHex);
                                    } catch (e) {
                                      console.log("EyeDropper closed or failed:", e);
                                    }
                                  }}
                                  style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    width: '32px',
                                    height: '32px',
                                    backgroundColor: '#1b1b1d',
                                    border: '1px solid #3c3c42',
                                    borderRadius: '4px',
                                    color: '#dfdfe3',
                                    cursor: 'pointer',
                                    padding: 0
                                  }}
                                  onMouseOver={(e) => { e.currentTarget.style.backgroundColor = '#2d2d31'; }}
                                  onMouseOut={(e) => { e.currentTarget.style.backgroundColor = '#1b1b1d'; }}
                                  title="Sample color from screen"
                                >
                                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M21 3L11 13" />
                                    <path d="M19 5l-2.5-2.5a1.5 1.5 0 0 0-2.12 0l-3 3a1.5 1.5 0 0 0 0 2.12L13.88 10" />
                                    <path d="M11 11H8.5a1.5 1.5 0 0 0-1.5 1.5v1.79c0 .4-.16.78-.44 1.06L3.5 18.5a1 1 0 0 0 0 1.41l.59.59a1 1 0 0 0 1.41 0l3.15-3.06c.28-.28.66-.44 1.06-.44H11.5A1.5 1.5 0 0 0 13 15.5V13" />
                                  </svg>
                                </button>
                              )}
                            </div>
                          )}
                          <input
                            type="text"
                            value={localColorVal}
                            onChange={(e) => setLocalColorVal(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                updateGlobalColor(editingGlobalColor, localColorVal);
                              }
                            }}
                            onBlur={() => {
                              updateGlobalColor(editingGlobalColor, localColorVal);
                            }}
                            style={{
                              flex: 1,
                              backgroundColor: '#1b1b1d',
                              border: '1px solid #3c3c42',
                              borderRadius: '4px',
                              padding: '6px 8px',
                              color: '#fff',
                              fontSize: '11px',
                              fontFamily: 'monospace'
                            }}
                            placeholder="Hex code or CSS gradient string"
                          />
                          <button
                            type="button"
                            onClick={() => updateGlobalColor(editingGlobalColor, localColorVal)}
                            style={{
                              backgroundColor: '#232326',
                              color: '#dfdfe3',
                              border: '1px solid #3c3c42',
                              borderRadius: '4px',
                              padding: '6px 10px',
                              fontSize: '11px',
                              cursor: 'pointer',
                              fontWeight: 'bold'
                            }}
                          >
                            Apply
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {activeRightTab === 'form' && (
                <div style={{
                  flex: 1,
                  padding: '16px',
                  overflowY: 'auto',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '14px',
                  backgroundColor: '#2b2b2f',
                  boxSizing: 'border-box'
                }}>
                  <div style={{ fontSize: '11px', color: '#8e8e93', marginBottom: '4px', lineHeight: '1.4' }}>
                    {isTrophyCardMode
                      ? 'Enter Trophy Card details below. Mapped operator and organization fields are auto-filled and locked.'
                      : 'Configure field data mappings, test values, and reorder dataset sections for logging.'}
                  </div>

                  {(() => {
                    const fields = getDynamicFieldsInTemplate();
                    const inputStyle = {
                      width: '100%',
                      backgroundColor: '#1b1b1d',
                      border: '1px solid #3c3c42',
                      borderRadius: '4px',
                      padding: '6px',
                      color: '#fff',
                      fontSize: '11px',
                      outline: 'none',
                      boxSizing: 'border-box',
                      marginTop: '2px'
                    };

                    const disabledInputStyle = {
                      ...inputStyle,
                      backgroundColor: 'var(--disabled-bg, #141416)',
                      borderColor: '#2d2d30',
                      color: '#94a3b8',
                      cursor: 'not-allowed'
                    };

                    // Group fields by dataset name
                    const groups = {};
                    fields.forEach(field => {
                      const dsId = field.datasetKey || '';
                      let dsName = 'General Variables';
                      if (dsId && datasets[dsId]) {
                        dsName = datasets[dsId].name || dsId;
                      }
                      if (!groups[dsName]) {
                        groups[dsName] = [];
                      }
                      groups[dsName].push(field);
                    });

                    const existingGroupNames = Object.keys(groups);
                    const orderedGroupNames = [
                      ...formDatasetOrder.filter(name => existingGroupNames.includes(name)),
                      ...existingGroupNames.filter(name => !formDatasetOrder.includes(name))
                    ];

                    const handleMoveDatasetOrder = (groupName, direction) => {
                      const currentIndex = orderedGroupNames.indexOf(groupName);
                      if (currentIndex < 0) return;
                      const targetIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
                      if (targetIndex < 0 || targetIndex >= orderedGroupNames.length) return;

                      const newOrder = [...orderedGroupNames];
                      const [moved] = newOrder.splice(currentIndex, 1);
                      newOrder.splice(targetIndex, 0, moved);
                      setFormDatasetOrder(newOrder);
                    };

                    const allCollapsed = orderedGroupNames.length > 0 && orderedGroupNames.every(name => collapsedFormDatasets[name]);

                    const handleToggleCollapseAll = () => {
                      if (allCollapsed) {
                        setCollapsedFormDatasets({});
                      } else {
                        const next = {};
                        orderedGroupNames.forEach(name => { next[name] = true; });
                        setCollapsedFormDatasets(next);
                      }
                    };

                    return (
                      <>
                        {/* Top Feature: Hide pre-populated datasets */}
                        <div style={{
                          padding: '8px 12px',
                          backgroundColor: 'rgba(0,0,0,0.25)',
                          borderRadius: '6px',
                          border: '1px solid #3c3c42',
                          marginBottom: '6px',
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '3px'
                        }}>
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <label style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '8px',
                              fontSize: '11px',
                              fontWeight: '600',
                              color: '#f4f4f5',
                              cursor: 'pointer',
                              userSelect: 'none'
                            }}>
                              <input
                                type="checkbox"
                                data-testid="hide-prepopulated-datasets-checkbox"
                                checked={hidePrepopulatedDatasets}
                                onChange={(e) => setHidePrepopulatedDatasets(e.target.checked)}
                                style={{ width: '15px', height: '15px', accentColor: '#3b82f6', cursor: 'pointer' }}
                              />
                              <span>Hide pre-populated datasets</span>
                            </label>
                            <span style={{
                              fontSize: '9px',
                              fontWeight: '600',
                              color: hidePrepopulatedDatasets ? '#60a5fa' : '#71717a',
                              backgroundColor: hidePrepopulatedDatasets ? 'rgba(59, 130, 246, 0.15)' : 'rgba(255, 255, 255, 0.05)',
                              border: `1px solid ${hidePrepopulatedDatasets ? 'rgba(59, 130, 246, 0.3)' : '#3c3c42'}`,
                              padding: '1px 6px',
                              borderRadius: '4px'
                            }}>
                              {hidePrepopulatedDatasets ? 'Auto-skip enabled' : 'Off'}
                            </span>
                          </div>
                          <div style={{ fontSize: '10px', color: '#94a3b8', paddingLeft: '23px', lineHeight: '1.3' }}>
                            In the Form Wizard, automatically skip dataset steps if all fields are pre-populated and valid. Navigating back still allows review.
                          </div>
                        </div>

                        {/* Top Toolbar */}
                        {orderedGroupNames.length > 0 && (
                          <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            padding: '6px 10px',
                            backgroundColor: 'rgba(0,0,0,0.25)',
                            borderRadius: '6px',
                            border: '1px solid #3c3c42',
                            marginBottom: '2px'
                          }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                              <span style={{ fontSize: '11px', fontWeight: '700', color: '#dfdfe3' }}>Data Groups</span>
                              <span style={{
                                fontSize: '10px',
                                color: '#a1a1aa',
                                backgroundColor: '#1b1b1d',
                                border: '1px solid #3c3c42',
                                borderRadius: '10px',
                                padding: '1px 7px'
                              }}>
                                {orderedGroupNames.length}
                              </span>
                            </div>

                            <button
                              type="button"
                              onClick={handleToggleCollapseAll}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '5px',
                                backgroundColor: '#232326',
                                color: '#dfdfe3',
                                border: '1px solid #3c3c42',
                                borderRadius: '4px',
                                padding: '4px 8px',
                                fontSize: '10px',
                                fontWeight: '600',
                                cursor: 'pointer',
                                transition: 'all 0.15s ease'
                              }}
                              onMouseOver={(e) => {
                                e.currentTarget.style.backgroundColor = '#2c2c30';
                                e.currentTarget.style.borderColor = '#55555c';
                              }}
                              onMouseOut={(e) => {
                                e.currentTarget.style.backgroundColor = '#232326';
                                e.currentTarget.style.borderColor = '#3c3c42';
                              }}
                            >
                              <span>{allCollapsed ? '▼' : '▶'}</span>
                              <span>{allCollapsed ? 'Expand All' : 'Collapse All'}</span>
                            </button>
                          </div>
                        )}

                        {orderedGroupNames.map((groupName, groupIndex) => {
                          const groupFields = groups[groupName] || [];
                          const isCollapsed = !!collapsedFormDatasets[groupName];

                          const toggleGroupCollapse = () => {
                            setCollapsedFormDatasets(prev => ({
                              ...prev,
                              [groupName]: !prev[groupName]
                            }));
                          };

                          return (
                            <fieldset
                              key={groupName}
                              style={{
                                border: '1px solid #3c3c42',
                                borderRadius: '8px',
                                padding: isCollapsed ? '8px 12px' : '12px 14px',
                                margin: '0 0 12px 0',
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '12px',
                                boxSizing: 'border-box',
                                backgroundColor: isCollapsed ? 'rgba(0,0,0,0.25)' : 'rgba(0,0,0,0.15)',
                                transition: 'all 0.15s ease'
                              }}
                            >
                              <legend style={{
                                fontSize: '10px',
                                fontWeight: '700',
                                color: '#bdb2ff',
                                padding: '0 6px',
                                textTransform: 'uppercase',
                                letterSpacing: '0.5px',
                                width: 'calc(100% - 12px)',
                                cursor: 'pointer',
                                userSelect: 'none'
                              }}
                              onClick={toggleGroupCollapse}
                              >
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', gap: '8px' }}>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                    <span style={{ fontSize: '9px', color: '#a1a1aa' }}>
                                      {isCollapsed ? '▶' : '▼'}
                                    </span>
                                    <span>{groupName}</span>
                                    <span style={{ fontSize: '9px', color: '#71717a', fontWeight: 'normal', textTransform: 'none' }}>
                                      {(() => {
                                        const counts = groupFields.reduce((acc, f) => {
                                          const k = f.datasetKey || '';
                                          const ed = enabledFields[k] || enabledFields[String(k)] || {};
                                          const hd = hiddenFields[k] || hiddenFields[String(k)] || {};
                                          const isOmitted = ed[f.label] === false || (ed[f.label] === undefined && enabledFields[f.label] === false);
                                          const isHidden = !isOmitted && (hd[f.label] === true || hiddenFields[f.label] === true || ed[f.label] === 'hidden' || enabledFields[f.label] === 'hidden');
                                          if (isOmitted) acc.omitted++;
                                          else if (isHidden) acc.hidden++;
                                          else acc.visible++;
                                          return acc;
                                        }, { visible: 0, hidden: 0, omitted: 0 });

                                        if (counts.hidden === 0 && counts.omitted === 0) {
                                          return `(${counts.visible} ${counts.visible === 1 ? 'field' : 'fields'})`;
                                        }
                                        const parts = [];
                                        if (counts.visible > 0) parts.push(`${counts.visible} visible`);
                                        if (counts.hidden > 0) parts.push(`${counts.hidden} hidden`);
                                        if (counts.omitted > 0) parts.push(`${counts.omitted} omitted`);
                                        return `(${parts.join(' · ')})`;
                                      })()}
                                    </span>
                                  </div>
                                  {!isTrophyCardMode && (
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                      <button
                                        type="button"
                                        disabled={groupIndex === 0}
                                        onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleMoveDatasetOrder(groupName, 'up'); }}
                                        title="Move dataset section up in form"
                                        style={{
                                          background: 'none',
                                          border: '1px solid #3c3c42',
                                          borderRadius: '3px',
                                          color: groupIndex === 0 ? '#55555c' : '#bdb2ff',
                                          cursor: groupIndex === 0 ? 'not-allowed' : 'pointer',
                                          padding: '1px 5px',
                                          fontSize: '10px',
                                          lineHeight: '1.2'
                                        }}
                                      >
                                        ▲
                                      </button>
                                      <button
                                        type="button"
                                        disabled={groupIndex === orderedGroupNames.length - 1}
                                        onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleMoveDatasetOrder(groupName, 'down'); }}
                                        title="Move dataset section down in form"
                                        style={{
                                          background: 'none',
                                          border: '1px solid #3c3c42',
                                          borderRadius: '3px',
                                          color: groupIndex === orderedGroupNames.length - 1 ? '#55555c' : '#bdb2ff',
                                          cursor: groupIndex === orderedGroupNames.length - 1 ? 'not-allowed' : 'pointer',
                                          padding: '1px 5px',
                                          fontSize: '10px',
                                          lineHeight: '1.2'
                                        }}
                                      >
                                        ▼
                                      </button>
                                    </div>
                                  )}
                                </div>
                              </legend>

                              {!isCollapsed && groupFields.map(field => {
                                const dsKey = field.datasetKey || '';
                                const enabledForDS = enabledFields[dsKey] || enabledFields[String(dsKey)] || {};
                                const hiddenForDS = hiddenFields[dsKey] || hiddenFields[String(dsKey)] || {};

                                const isExplicitlyOmitted = enabledForDS[field.label] === false || (enabledForDS[field.label] === undefined && enabledFields[field.label] === false);
                                const isExplicitlyHidden = !isExplicitlyOmitted && (
                                  hiddenForDS[field.label] === true ||
                                  hiddenFields[field.label] === true ||
                                  enabledForDS[field.label] === 'hidden' ||
                                  enabledFields[field.label] === 'hidden'
                                );
                                const formStatus = isExplicitlyOmitted ? 'omitted' : (isExplicitlyHidden ? 'hidden' : 'visible');

                                if (isTrophyCardMode && formStatus !== 'visible') {
                                  return null;
                                }

                                const setFieldStatus = (newStatus) => {
                                  const key = dsKey || 'default';
                                  setEnabledFields(prev => ({
                                    ...prev,
                                    [key]: {
                                      ...(prev[key] || {}),
                                      [field.label]: newStatus === 'omitted' ? false : true
                                    }
                                  }));
                                  setHiddenFields(prev => ({
                                    ...prev,
                                    [key]: {
                                      ...(prev[key] || {}),
                                      [field.label]: newStatus === 'hidden'
                                    }
                                  }));
                                };

                                const mappingKey = fieldMappings[field.label] || '';
                                const isMapped = !!mappingKey;
                                const isExifMapping = isMapped && mappingKey.startsWith('photo_exif.');
                                const isOptionLocked = lockedOptions[field.label] !== undefined && lockedOptions[field.label] !== null && lockedOptions[field.label] !== '';
                                const isRequired = requiredFields[field.label] !== undefined ? !!requiredFields[field.label] : !!field.fieldDef?.required;
                                const resolvedVal = isMapped ? resolveMappedValue(mappingKey, user, orgData, tenantConfig) : null;
                                
                                let currentValue = '';
                                const ds = datasets[field.datasetKey];
                                if (isOptionLocked) {
                                  currentValue = lockedOptions[field.label];
                                } else if (isMapped && !isExifMapping && resolvedVal !== null && resolvedVal !== undefined && resolvedVal !== '') {
                                  currentValue = resolvedVal;
                                } else if (ds && ds.variables && ds.variables[field.label] !== undefined && ds.variables[field.label] !== '') {
                                  currentValue = ds.variables[field.label];
                                } else if (isExifMapping && resolvedVal !== null && resolvedVal !== undefined && resolvedVal !== '') {
                                  currentValue = resolvedVal;
                                } else {
                                  currentValue = field.sampleValue || field.fieldDef?.sample_data || (field.options?.[0] || '');
                                }

                                const isLocked = isTrophyCardMode && ((isMapped && !isExifMapping) || isOptionLocked);

                                return (
                                  <div key={`${field.datasetKey}-${field.label}`} style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                      <label style={{
                                        fontSize: '10px',
                                        fontWeight: '600',
                                        color: formStatus === 'omitted' ? '#6b7280' : (formStatus === 'hidden' ? '#c084fc' : (isLocked ? '#94a3b8' : '#e2e8f0')),
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '4px'
                                      }}>
                                        <span style={{ textDecoration: formStatus === 'omitted' ? 'line-through' : 'none' }}>
                                          {field.alias || field.label.replace(/_/g, ' ')}
                                        </span>
                                        {formStatus === 'visible' && isRequired && (
                                          <span style={{ color: '#ef4444', fontWeight: '700' }} title="Required field">*</span>
                                        )}
                                      </label>
                                      <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                        {formStatus === 'omitted' ? (
                                          <span style={{ fontSize: '9px', backgroundColor: 'rgba(107, 114, 128, 0.2)', color: '#9ca3af', border: '1px solid rgba(107, 114, 128, 0.3)', padding: '1px 5px', borderRadius: '3px', fontWeight: '600' }}>
                                            OMITTED (NO DATA)
                                          </span>
                                        ) : formStatus === 'hidden' ? (
                                          <span style={{ fontSize: '9px', backgroundColor: 'rgba(168, 85, 247, 0.18)', color: '#c084fc', border: '1px solid rgba(168, 85, 247, 0.35)', padding: '1px 5px', borderRadius: '3px', fontWeight: '700' }}>
                                            HIDDEN (CAPTURED)
                                          </span>
                                        ) : (
                                          <>
                                            {isRequired && (
                                              <span style={{ fontSize: '9px', backgroundColor: 'rgba(239, 68, 68, 0.15)', color: '#ef4444', padding: '1px 5px', borderRadius: '3px', fontWeight: '700' }}>
                                                REQUIRED
                                              </span>
                                            )}
                                            {isLocked ? (
                                              <span style={{ fontSize: '9px', color: isOptionLocked ? 'var(--warning-text, #b45309)' : 'var(--text-mute, #94a3b8)', display: 'flex', alignItems: 'center', gap: '3px' }}>
                                                🔒 {isOptionLocked ? 'Locked Option' : 'Read-Only (Mapped)'}
                                              </span>
                                            ) : isExifMapping ? (
                                              <span style={{ fontSize: '9px', color: '#38bdf8', display: 'flex', alignItems: 'center', gap: '3px' }}>
                                                📍 Photo EXIF (Editable)
                                              </span>
                                            ) : null}
                                          </>
                                        )}
                                      </div>
                                    </div>

                                    {/* Template Authoring Mode: 3-Way Form Status Segmented Control */}
                                    {!isTrophyCardMode && (
                                      <div style={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: '4px',
                                        backgroundColor: '#1b1b1d',
                                        border: '1px solid #3c3c42',
                                        borderRadius: '6px',
                                        padding: '5px 8px',
                                        margin: '1px 0 3px 0'
                                      }}>
                                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                          <span style={{ fontSize: '9px', fontWeight: '700', color: '#8e8e93', textTransform: 'uppercase', letterSpacing: '0.3px' }}>
                                            Form Wizard Status
                                          </span>
                                          <span style={{ fontSize: '9px', color: formStatus === 'visible' ? '#38bdf8' : (formStatus === 'hidden' ? '#c084fc' : '#9ca3af'), fontWeight: '600' }}>
                                            {formStatus === 'visible' && 'Visible (Data Captured)'}
                                            {formStatus === 'hidden' && 'Hidden (Data Captured)'}
                                            {formStatus === 'omitted' && 'Omitted (No Data Captured)'}
                                          </span>
                                        </div>

                                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '4px' }}>
                                          {/* 1. VISIBLE BUTTON */}
                                          <button
                                            type="button"
                                            onClick={() => setFieldStatus('visible')}
                                            style={{
                                              display: 'flex',
                                              alignItems: 'center',
                                              justifyContent: 'center',
                                              gap: '4px',
                                              padding: '4px 6px',
                                              borderRadius: '4px',
                                              fontSize: '10px',
                                              fontWeight: '700',
                                              cursor: 'pointer',
                                              border: formStatus === 'visible' ? '1px solid #0284c7' : '1px solid #2d2d32',
                                              backgroundColor: formStatus === 'visible' ? 'rgba(2, 132, 199, 0.25)' : '#141416',
                                              color: formStatus === 'visible' ? '#38bdf8' : '#71717a',
                                              transition: 'all 0.15s ease'
                                            }}
                                            title="Field is displayed in the operator form wizard and its data is captured"
                                          >
                                            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                              <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                                              <circle cx="12" cy="12" r="3" />
                                            </svg>
                                            <span>Visible</span>
                                          </button>

                                          {/* 2. HIDDEN BUTTON */}
                                          <button
                                            type="button"
                                            onClick={() => setFieldStatus('hidden')}
                                            style={{
                                              display: 'flex',
                                              alignItems: 'center',
                                              justifyContent: 'center',
                                              gap: '4px',
                                              padding: '4px 6px',
                                              borderRadius: '4px',
                                              fontSize: '10px',
                                              fontWeight: '700',
                                              cursor: 'pointer',
                                              border: formStatus === 'hidden' ? '1px solid #a855f7' : '1px solid #2d2d32',
                                              backgroundColor: formStatus === 'hidden' ? 'rgba(168, 85, 247, 0.25)' : '#141416',
                                              color: formStatus === 'hidden' ? '#c084fc' : '#71717a',
                                              transition: 'all 0.15s ease'
                                            }}
                                            title="Field is hidden from the operator form wizard, but its data is captured from defaults or mapping"
                                          >
                                            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                              <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                                              <line x1="1" y1="1" x2="23" y2="23" />
                                            </svg>
                                            <span>Hidden</span>
                                          </button>

                                          {/* 3. OMITTED BUTTON */}
                                          <button
                                            type="button"
                                            onClick={() => setFieldStatus('omitted')}
                                            style={{
                                              display: 'flex',
                                              alignItems: 'center',
                                              justifyContent: 'center',
                                              gap: '4px',
                                              padding: '4px 6px',
                                              borderRadius: '4px',
                                              fontSize: '10px',
                                              fontWeight: '700',
                                              cursor: 'pointer',
                                              border: formStatus === 'omitted' ? '1px solid #6b7280' : '1px solid #2d2d32',
                                              backgroundColor: formStatus === 'omitted' ? 'rgba(107, 114, 128, 0.25)' : '#141416',
                                              color: formStatus === 'omitted' ? '#9ca3af' : '#71717a',
                                              transition: 'all 0.15s ease'
                                            }}
                                            title="Field is NOT included in the form wizard and NO data will be captured"
                                          >
                                            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                              <circle cx="12" cy="12" r="10" />
                                              <line x1="4.93" y1="4.93" x2="19.07" y2="19.07" />
                                            </svg>
                                            <span>Omitted</span>
                                          </button>
                                        </div>
                                      </div>
                                    )}

                                    {formStatus === 'omitted' ? (
                                      <div style={{ fontSize: '10px', color: '#6b7280', fontStyle: 'italic', padding: '2px 0 6px 4px' }}>
                                        Field is omitted from the form wizard — no data will be captured.
                                      </div>
                                    ) : (
                                      <>
                                        {formStatus === 'hidden' && (
                                          <div style={{
                                            fontSize: '10px',
                                            color: '#c084fc',
                                            backgroundColor: 'rgba(168, 85, 247, 0.08)',
                                            border: '1px solid rgba(168, 85, 247, 0.25)',
                                            borderRadius: '4px',
                                            padding: '4px 8px',
                                            marginBottom: '3px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '6px'
                                          }}>
                                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
                                              <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                                              <line x1="1" y1="1" x2="23" y2="23" />
                                            </svg>
                                            <span><strong>Hidden Field:</strong> Data will be captured in coin records, but this field will not be shown to the operator in the wizard.</span>
                                          </div>
                                        )}

                                        {/* Template Authoring Mode: Mapping Selector & Required Toggle */}
                                        {!isTrophyCardMode && (
                                          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', marginBottom: '2px' }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flex: 1 }}>
                                                <span style={{ fontSize: '10px', color: '#8e8e93', fontWeight: '600', whiteSpace: 'nowrap' }}>
                                                  Map to:
                                                </span>
                                                <select
                                                  value={mappingKey}
                                                  onChange={(e) => {
                                                    const newMapping = e.target.value;
                                                    setFieldMappings(prev => ({
                                                      ...prev,
                                                      [field.label]: newMapping
                                                    }));
                                                    if (newMapping) {
                                                      const resVal = resolveMappedValue(newMapping, user, orgData, tenantConfig);
                                                      if (resVal !== null && resVal !== undefined && resVal !== '') {
                                                        updateDatasetVariable(field.datasetKey, field.label, resVal);
                                                      } else {
                                                        updateDatasetVariable(field.datasetKey, field.label, field.sampleValue || field.fieldDef?.sample_data || (field.options?.[0] || ''));
                                                      }
                                                    } else {
                                                      updateDatasetVariable(field.datasetKey, field.label, field.sampleValue || field.fieldDef?.sample_data || (field.options?.[0] || ''));
                                                    }
                                                  }}
                                                  style={{
                                                    flex: 1,
                                                    backgroundColor: isMapped ? (isExifMapping ? '#0c2838' : '#1e293b') : '#1b1b1d',
                                                    border: isMapped ? (isExifMapping ? '1px solid #0284c7' : '1px solid #3b82f6') : '1px solid #3c3c42',
                                                    borderRadius: '4px',
                                                    padding: '3px 6px',
                                                    color: isMapped ? (isExifMapping ? '#38bdf8' : '#60a5fa') : '#dfdfe3',
                                                    fontSize: '10px',
                                                    outline: 'none',
                                                    cursor: 'pointer'
                                                  }}
                                                >
                                                  {MAPPING_OPTIONS.map(opt => {
                                                    if (opt.group) {
                                                      return (
                                                        <optgroup key={opt.group} label={opt.group}>
                                                          {opt.options.map(subOpt => (
                                                            <option key={subOpt.value} value={subOpt.value}>{subOpt.label}</option>
                                                          ))}
                                                        </optgroup>
                                                      );
                                                    }
                                                    return (
                                                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                                                    );
                                                  })}
                                                </select>
                                              </div>

                                          {/* Authoring Required Checkbox Toggle (only applicable for Visible fields) */}
                                          {formStatus === 'visible' && (
                                            <label style={{
                                              display: 'flex',
                                              alignItems: 'center',
                                              gap: '4px',
                                              cursor: 'pointer',
                                              fontSize: '10px',
                                              color: isRequired ? '#ef4444' : '#8e8e93',
                                              fontWeight: '600',
                                              userSelect: 'none',
                                              padding: '2px 6px',
                                              borderRadius: '4px',
                                              backgroundColor: isRequired ? 'rgba(239, 68, 68, 0.12)' : 'transparent',
                                              border: isRequired ? '1px solid rgba(239, 68, 68, 0.3)' : '1px solid transparent',
                                              transition: 'all 0.15s ease'
                                            }} title="Make this field required in operator forms">
                                              <input
                                                type="checkbox"
                                                checked={isRequired}
                                                onChange={(e) => {
                                                  setRequiredFields(prev => ({
                                                    ...prev,
                                                    [field.label]: e.target.checked
                                                  }));
                                                }}
                                                style={{ width: '12px', height: '12px', accentColor: '#ef4444', cursor: 'pointer' }}
                                              />
                                              <span>Required</span>
                                            </label>
                                          )}
                                        </div>

                                        {/* Date/Time Format Selector (if field is datetime/date/time or custom format) */}
                                        {(() => {
                                          const fType = String(field.type || field.fieldDef?.type || '').toLowerCase();
                                          const isDateTimeField = fType.includes('date') || fType.includes('time') || Boolean(dateFormats[field.label]) || Boolean(field.fieldDef?.date_format);
                                          if (!isDateTimeField) return null;

                                          const currentFmt = dateFormats[field.label] !== undefined ? dateFormats[field.label] : (field.fieldDef?.date_format || '');
                                          const isPreset = DATE_TIME_FORMAT_PRESETS.some(p => p.value === currentFmt && p.value !== 'custom');
                                          const selectValue = isPreset ? currentFmt : (currentFmt ? 'custom' : '');

                                          return (
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: '2px' }}>
                                              <span style={{ fontSize: '10px', color: '#eab308', fontWeight: '600', whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: '3px' }}>
                                                <span>📅</span> Format:
                                              </span>
                                              <select
                                                value={selectValue}
                                                onChange={(e) => {
                                                  const val = e.target.value;
                                                  if (val === 'custom') {
                                                    const baseFmt = prev => prev[field.label] || (field.alias && prev[field.alias]) || 'YYYY/MM/DD';
                                                    setDateFormats(prev => {
                                                      const f = baseFmt(prev);
                                                      const copy = { ...prev, [field.label]: f };
                                                      if (field.alias) copy[field.alias] = f;
                                                      return copy;
                                                    });
                                                  } else {
                                                    setDateFormats(prev => {
                                                      const copy = { ...prev };
                                                      if (!val) {
                                                        delete copy[field.label];
                                                        if (field.alias) delete copy[field.alias];
                                                      } else {
                                                        copy[field.label] = val;
                                                        if (field.alias) copy[field.alias] = val;
                                                      }
                                                      return copy;
                                                    });
                                                    if (val && currentValue) {
                                                      const formatted = formatDateTimeValue(currentValue, val);
                                                      updateDatasetVariable(field.datasetKey, field.label, formatted);
                                                    }
                                                  }
                                                }}
                                                style={{
                                                  flex: 1,
                                                  backgroundColor: currentFmt ? '#231d10' : '#1b1b1d',
                                                  border: currentFmt ? '1px solid #f59e0b' : '1px solid #3c3c42',
                                                  borderRadius: '4px',
                                                  padding: '3px 6px',
                                                  color: currentFmt ? '#fbbf24' : '#dfdfe3',
                                                  fontSize: '10px',
                                                  outline: 'none',
                                                  cursor: 'pointer'
                                                }}
                                              >
                                                {DATE_TIME_FORMAT_PRESETS.map(preset => (
                                                  <option key={preset.value} value={preset.value}>{preset.label}</option>
                                                ))}
                                              </select>
                                              {selectValue === 'custom' && (
                                                <input
                                                  type="text"
                                                  placeholder="e.g. YYYY/MM/DD or H:i"
                                                  value={currentFmt}
                                                  onChange={(e) => {
                                                    const val = e.target.value;
                                                    setDateFormats(prev => {
                                                      const copy = { ...prev, [field.label]: val };
                                                      if (field.alias) copy[field.alias] = val;
                                                      return copy;
                                                    });
                                                    if (val && currentValue) {
                                                      const formatted = formatDateTimeValue(currentValue, val);
                                                      updateDatasetVariable(field.datasetKey, field.label, formatted);
                                                    }
                                                  }}
                                                  style={{
                                                    width: '110px',
                                                    backgroundColor: '#1b1b1d',
                                                    border: '1px solid #f59e0b',
                                                    borderRadius: '4px',
                                                    padding: '3px 6px',
                                                    color: '#fbbf24',
                                                    fontSize: '10px',
                                                    outline: 'none'
                                                  }}
                                                  title="Tokens: YYYY, YY, MMMM, MMM, MM, DD, D, HH, H, hh, h, mm, i, ss, A, a"
                                                />
                                              )}
                                            </div>
                                          );
                                        })()}

                                        {isMapped && (
                                          <div style={{
                                            fontSize: '9px',
                                            color: isExifMapping ? '#38bdf8' : '#60a5fa',
                                            backgroundColor: isExifMapping ? 'rgba(56, 189, 248, 0.12)' : 'rgba(59, 130, 246, 0.12)',
                                            padding: '3px 6px',
                                            borderRadius: '4px',
                                            border: isExifMapping ? '1px solid rgba(56, 189, 248, 0.3)' : '1px solid rgba(59, 130, 246, 0.3)',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '4px'
                                          }}>
                                            <span>
                                              {isExifMapping
                                                ? '📍 Auto-suggested from photo EXIF GPS/metadata (Operator can accept or manually edit if photo lacks GPS)'
                                                : `🔗 Auto-mapped from ${MAPPING_OPTIONS.flatMap(g => g.options || [g]).find(o => o.value === mappingKey)?.label || mappingKey} (Locked at logging time)`}
                                            </span>
                                          </div>
                                        )}
                                      </div>
                                    )}

                                    {/* Input Controls */}
                                    {field.options && field.options.length > 0 ? (
                                      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                        {field.option_source === 'species_library' && (
                                          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                            <span style={{ fontSize: '9px', fontWeight: '600', color: '#38bdf8', backgroundColor: 'rgba(56, 189, 248, 0.12)', border: '1px solid rgba(56, 189, 248, 0.25)', padding: '1px 5px', borderRadius: '3px' }}>
                                              🌿 Species Library ({field.species_field || 'common_name'}{field.species_category && field.species_category !== 'All' ? ` | ${field.species_category}` : ''})
                                            </span>
                                          </div>
                                        )}
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                          <select
                                            disabled={isLocked}
                                            required={isRequired}
                                            value={currentValue}
                                            onChange={(e) => {
                                              const val = e.target.value;
                                              updateDatasetVariable(field.datasetKey, field.label, val);
                                              if (isOptionLocked) {
                                                setLockedOptions(prev => ({
                                                  ...prev,
                                                  [field.label]: val
                                                }));
                                              }
                                            }}
                                            style={{
                                              ...(isLocked ? disabledInputStyle : inputStyle),
                                              flex: 1,
                                              border: isOptionLocked ? '1px solid #f59e0b' : (isLocked ? disabledInputStyle.border : inputStyle.border),
                                              backgroundColor: isOptionLocked && !isLocked ? '#231d10' : (isLocked ? disabledInputStyle.backgroundColor : inputStyle.backgroundColor),
                                              color: isOptionLocked && !isLocked ? '#fbbf24' : (isLocked ? disabledInputStyle.color : inputStyle.color)
                                            }}
                                          >
                                            {field.options.map(opt => (
                                              <option key={opt} value={opt}>{opt}</option>
                                            ))}
                                          </select>

                                          {!isTrophyCardMode && (
                                            <button
                                              type="button"
                                              onClick={() => {
                                                if (isOptionLocked) {
                                                  setLockedOptions(prev => {
                                                    const copy = { ...prev };
                                                    delete copy[field.label];
                                                    return copy;
                                                  });
                                                } else {
                                                  const valToLock = currentValue || field.options[0] || '';
                                                  setLockedOptions(prev => ({
                                                    ...prev,
                                                    [field.label]: valToLock
                                                  }));
                                                  updateDatasetVariable(field.datasetKey, field.label, valToLock);
                                                }
                                              }}
                                              title={isOptionLocked ? 'Unlock option selection (allow operator to change)' : 'Lock this option (force this option for operators)'}
                                              style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '4px',
                                                padding: '4px 7px',
                                                borderRadius: '4px',
                                                border: isOptionLocked ? '1px solid var(--warning-border, #f59e0b)' : '1px solid var(--border-color, #3c3c42)',
                                                backgroundColor: isOptionLocked ? 'var(--warning-bg, rgba(245, 158, 11, 0.18))' : 'var(--button-secondary-bg, #232326)',
                                                color: isOptionLocked ? 'var(--warning-text, #b45309)' : 'var(--text-mute, #a1a1aa)',
                                                fontSize: '10px',
                                                fontWeight: '600',
                                                cursor: 'pointer',
                                                whiteSpace: 'nowrap',
                                                marginTop: '2px',
                                                transition: 'all 0.15s ease'
                                              }}
                                            >
                                              <span>{isOptionLocked ? '🔒 Locked' : '🔓 Lock Option'}</span>
                                            </button>
                                          )}
                                        </div>

                                        {isOptionLocked && (
                                          <div style={{
                                            fontSize: '9px',
                                            color: 'var(--warning-text, #b45309)',
                                            backgroundColor: 'var(--warning-bg, rgba(245, 158, 11, 0.12))',
                                            padding: '3px 6px',
                                            borderRadius: '4px',
                                            border: '1px solid var(--warning-border, rgba(245, 158, 11, 0.3))',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '4px'
                                          }}>
                                            <span>🔒 Option locked to "{currentValue}" (Operator cannot change this selection)</span>
                                          </div>
                                        )}
                                      </div>
                                    ) : field.type === 'image' ? (
                                      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                        <input
                                          type="text"
                                          disabled={isLocked}
                                          required={isRequired}
                                          value={currentValue}
                                          onChange={(e) => updateDatasetVariable(field.datasetKey, field.label, e.target.value)}
                                          placeholder="Enter image URL..."
                                          style={isLocked ? disabledInputStyle : inputStyle}
                                        />
                                        {!isLocked && (
                                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                            <input
                                              type="file"
                                              accept="image/*"
                                              onChange={async (e) => {
                                                const file = e.target.files[0];
                                                if (!file) return;

                                                // Extract EXIF data from photo (GPS coordinates, altitude, timestamp)
                                                try {
                                                  const exif = await extractExifFromBlob(file);
                                                  if (exif) {
                                                    const allFields = getDynamicFieldsInTemplate();
                                                    allFields.forEach(f => {
                                                      const fMap = fieldMappings[f.label];
                                                      if (fMap && fMap.startsWith('photo_exif.')) {
                                                        const subKey = fMap.replace('photo_exif.', '');
                                                        if (exif[subKey]) {
                                                          updateDatasetVariable(f.datasetKey, f.label, exif[subKey]);
                                                        }
                                                      }
                                                    });

                                                    if (exif.gps_latitude && exif.gps_longitude) {
                                                      reverseGeocodePlace(exif.gps_latitude, exif.gps_longitude).then(placeName => {
                                                        if (placeName) {
                                                          allFields.forEach(f => {
                                                            const fMap = fieldMappings[f.label];
                                                            if (fMap === 'photo_exif.place') {
                                                              updateDatasetVariable(f.datasetKey, f.label, placeName);
                                                            }
                                                          });
                                                        }
                                                      });
                                                    }
                                                  }
                                                } catch (exifErr) {
                                                  console.warn("Could not read EXIF data", exifErr);
                                                }

                                                const formData = new FormData();
                                                formData.append('file', file);
                                                try {
                                                  const res = await fetch(`${API_BASE}/api/upload`, {
                                                    method: 'POST',
                                                    headers: { 'Authorization': `Bearer ${token}` },
                                                    body: formData
                                                  });
                                                  const data = await res.json();
                                                  if (res.ok && data.file) {
                                                    const rawLoc = data.file.location || '';
                                                    const uploadedUrl = rawLoc.includes('/uploads/')
                                                      ? rawLoc.substring(rawLoc.indexOf('/uploads/')).split('?')[0]
                                                      : (rawLoc.startsWith('/') ? rawLoc : `/${rawLoc}`);
                                                    updateDatasetVariable(field.datasetKey, field.label, uploadedUrl);
                                                    const targetElem = sides[activeSide].elements.find(el => el.type === 'image' && (el.imageVariable === `$${field.label}` || el.imageVariable === field.label));
                                                    if (targetElem) {
                                                      openImageAdjustModal({ ...targetElem, src: uploadedUrl });
                                                    }
                                                  }
                                                } catch (err) {
                                                  console.error("Upload failed", err);
                                                }
                                              }}
                                              style={{ fontSize: '10px', color: '#8e8e93', flex: 1 }}
                                            />
                                          </div>
                                        )}
                                        {currentValue && (
                                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '4px' }}>
                                            <img
                                              src={currentValue.includes('/uploads/') ? `${API_BASE ? API_BASE.replace(/\/+$/, '') : ''}${currentValue.substring(currentValue.indexOf('/uploads/'))}` : currentValue}
                                              alt="Preview"
                                              style={{ width: '32px', height: '32px', objectFit: 'cover', borderRadius: '4px', border: '1px solid #3c3c42' }}
                                            />
                                            <button
                                              type="button"
                                              onClick={() => {
                                                const targetElem = sides[activeSide].elements.find(el => el.type === 'image' && (el.imageVariable === `$${field.label}` || el.imageVariable === field.label));
                                                if (targetElem) {
                                                  openImageAdjustModal({ ...targetElem, src: currentValue });
                                                }
                                              }}
                                              style={{
                                                backgroundColor: '#2b2b2f',
                                                color: '#dfdfe3',
                                                border: '1px solid #3c3c42',
                                                borderRadius: '3px',
                                                padding: '3px 8px',
                                                fontSize: '10px',
                                                cursor: 'pointer',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '4px'
                                              }}
                                            >
                                              🔍 Adjust in Frame
                                            </button>
                                          </div>
                                        )}
                                      </div>
                                    ) : (
                                      <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                                        <input
                                          type={field.type === 'number' ? 'number' : field.type === 'date' ? 'date' : 'text'}
                                          disabled={isLocked}
                                          required={isRequired}
                                          value={currentValue}
                                          onChange={(e) => updateDatasetVariable(field.datasetKey, field.label, e.target.value)}
                                          placeholder={isExifMapping ? "Enter coordinates or upload photo with GPS..." : ((dateFormats[field.label] || field.fieldDef?.date_format) ? `Format: ${dateFormats[field.label] || field.fieldDef?.date_format}` : "")}
                                          style={isLocked ? disabledInputStyle : inputStyle}
                                        />
                                        {(dateFormats[field.label] || field.fieldDef?.date_format) && (
                                          <span style={{ fontSize: '9px', color: '#eab308', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                            <span>📅</span> Format: <code>{dateFormats[field.label] || field.fieldDef?.date_format}</code>
                                          </span>
                                        )}
                                        {isTrophyCardMode && isExifMapping && (
                                          <span style={{ fontSize: '9px', color: '#71717a' }}>
                                            📍 Auto-suggested from photo GPS if available. Editable if manual entry needed.
                                          </span>
                                        )}
                                      </div>
                                    )}
                                  </>
                                )}
                              </div>
                            );
                              })}
                            </fieldset>
                          );
                        })}
                        {fields.length === 0 && (
                          <div style={{ fontStyle: 'italic', fontSize: '11px', color: '#8e8e93', textAlign: 'center', marginTop: '20px' }}>
                            No dynamic fields found in this card layout template.
                          </div>
                        )}
                      </>
                    );
                  })()}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Bottom Help / Status Bar */}
        <div style={{
          height: '24px',
          backgroundColor: '#2b2b2f',
          borderTop: '1px solid #3c3c42',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '0 12px',
          fontSize: '11px',
          color: '#8e8e93',
          userSelect: 'none'
        }}>
          <div style={{ display: 'flex', gap: '14px', alignItems: 'center' }}>
            <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              {activeTool === 'select' ? (
                <>
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ display: 'block' }}>
                    <polygon points="3 3 10 21 13 13 21 10 3 3" />
                  </svg>
                  <span>Drag elements to position, double-click text to modify value.</span>
                </>
              ) : (
                <>
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ display: 'block' }}>
                    <line x1="12" y1="5" x2="12" y2="19"></line>
                    <line x1="5" y1="12" x2="19" y2="12"></line>
                  </svg>
                  <span>Click on canvas to insert new {activeTool} element.</span>
                </>
              )}
            </span>
            <div style={{ display: 'flex', gap: '4px', alignItems: 'center', borderLeft: '1px solid #3c3c42', paddingLeft: '10px' }}>
              <button
                onClick={handleUndo}
                disabled={undoStack.length === 0}
                style={{
                  backgroundColor: 'transparent',
                  color: undoStack.length === 0 ? '#55555c' : '#dfdfe3',
                  border: 'none',
                  fontSize: '10px',
                  cursor: 'pointer'
                }}
              >
                Undo
              </button>
              <span>/</span>
              <button
                onClick={handleRedo}
                disabled={redoStack.length === 0}
                style={{
                  backgroundColor: 'transparent',
                  color: redoStack.length === 0 ? '#55555c' : '#dfdfe3',
                  border: 'none',
                  fontSize: '10px',
                  cursor: 'pointer'
                }}
              >
                Redo
              </button>
            </div>
          </div>

          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <span>{templateName} @ {Math.round(zoomLevel * 100)}%</span>
            <input
              type="range"
              min="0.5"
              max="1.5"
              step="0.05"
              value={zoomLevel}
              onChange={(e) => setZoomLevel(parseFloat(e.target.value))}
              style={{ width: '80px', accentColor: '#1677ff', height: '4px' }}
            />
          </div>
        </div>

        {/* 3D INTERACTIVE FLIP PREVIEW MODAL (Portaled inside the overlay body) */}
        {show3DPreview && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.85)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999999, // Above the portaled designer
            backdropFilter: 'blur(8px)'
          }}>
            <div style={{
              backgroundColor: '#202023',
              border: '1px solid #3c3c42',
              borderRadius: '16px',
              padding: '24px',
              width: '450px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              position: 'relative',
              boxShadow: '0 20px 40px rgba(0,0,0,0.6)'
            }}>
              <button
                onClick={() => setShow3DPreview(false)}
                style={{
                  position: 'absolute',
                  top: '15px',
                  right: '15px',
                  backgroundColor: 'rgba(255,255,255,0.06)',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '50%',
                  width: '32px',
                  height: '32px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: 'bold',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                ✕
              </button>

              <h4 style={{ margin: '0 0 4px 0', fontSize: '18px', fontWeight: '800', color: '#1677ff' }}>Mobile 3D Live Experience</h4>
              <p style={{ margin: '0 0 20px 0', fontSize: '12px', color: '#8e8e93' }}>Click card to flip between Front and Back design layouts</p>

              {/* 3D Flipping container */}
              <div
                onClick={() => setPreviewFlipped(!previewFlipped)}
                style={{
                  width: `${cardDimensions.width}px`,
                  height: `${cardDimensions.height}px`,
                  perspective: '1000px',
                  WebkitPerspective: '1000px',
                  cursor: 'pointer',
                  marginBottom: '20px'
                }}
              >
                <div style={{
                  width: '100%',
                  height: '100%',
                  transition: 'transform 0.6s cubic-bezier(0.4, 0, 0.2, 1)',
                  transformStyle: 'preserve-3d',
                  WebkitTransformStyle: 'preserve-3d',
                  position: 'relative',
                  transform: previewFlipped ? 'rotateY(180deg)' : 'rotateY(0deg)',
                  WebkitTransform: previewFlipped ? 'rotateY(180deg)' : 'rotateY(0deg)'
                }}>
                  {/* Front Side */}
                  <div style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    backfaceVisibility: 'hidden',
                    WebkitBackfaceVisibility: 'hidden',
                    transform: 'rotateY(0deg) translateZ(1px)',
                    WebkitTransform: 'rotateY(0deg) translateZ(1px)',
                    transformStyle: 'flat',
                    WebkitTransformStyle: 'flat',
                    zIndex: previewFlipped ? 1 : 2,
                    pointerEvents: previewFlipped ? 'none' : 'auto',
                    borderRadius: globalStyles.borderRadius,
                    border: `${globalStyles.borderWidth}px ${globalStyles.borderStyle} ${globalStyles.borderColor}`,
                    ...(sides.front.background?.type === 'gradient' || (sides.front.background?.value && String(sides.front.background.value).includes('gradient'))
                      ? { background: sides.front.background.value }
                      : sides.front.background?.type === 'pattern'
                        ? {
                            backgroundColor: sides.front.background?.pattern?.tintColor || '#ffffff',
                            background: sides.front.background?.value || '#ffffff'
                          }
                        : {
                            backgroundColor: sides.front.background?.value || '#ffffff'
                          }
                    ),
                    padding: 0,
                    boxSizing: 'border-box'
                  }}>
                    {sides.front.background.type === 'pattern' && sides.front.background.pattern && (
                      <div style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        borderRadius: 'inherit',
                        pointerEvents: 'none',
                        opacity: sides.front.background.pattern.opacity !== undefined ? (sides.front.background.pattern.opacity > 1 ? sides.front.background.pattern.opacity / 100 : sides.front.background.pattern.opacity) : 1,
                        backgroundImage: `url("${sides.front.background.pattern.image}")`,
                        backgroundRepeat: sides.front.background.pattern.repeat || 'repeat',
                        backgroundSize: `${Math.max(2, Math.round((sides.front.background.pattern.tileWidth || 32) * ((sides.front.background.pattern.scale !== undefined ? sides.front.background.pattern.scale : 100) / 100)))}px ${Math.max(2, Math.round((sides.front.background.pattern.tileHeight || 32) * ((sides.front.background.pattern.scale !== undefined ? sides.front.background.pattern.scale : 100) / 100)))}px`,
                        backgroundPosition: '0 0',
                        mixBlendMode: sides.front.background.pattern.blendMode || 'normal',
                        zIndex: 0
                      }} />
                    )}
                    {sides.front.elements
                      .filter(e => {
                        if (e.visible === false) return false;
                        if (e.parentId) {
                          const parent = sides.front.elements.find(el => el.id === e.parentId);
                          if (parent && parent.visible === false) return false;
                        }
                        return true;
                      })
                      .map(elem => {
                        if (elem.type === 'group') return null;
                        const linkInfo = resolveElementLink(elem, linkMappings, fieldMappings, {}, datasets, user, orgData, tenantConfig);
                        return (
                          <div
                            key={elem.id}
                            onClick={(e) => {
                              if (elem.type === 'qrcode' && elem.openOnClick !== false) {
                                const qrUrl = resolveQrCodeValue(elem, {}, datasets, user, orgData, tenantConfig);
                                if (qrUrl && (qrUrl.startsWith('http://') || qrUrl.startsWith('https://') || qrUrl.startsWith('mailto:'))) {
                                  e.stopPropagation();
                                  window.open(qrUrl, '_blank', 'noopener,noreferrer');
                                  return;
                                }
                              }
                              if (linkInfo && linkInfo.url) {
                                e.stopPropagation();
                                openLinkSafely(linkInfo);
                              }
                            }}
                            title={
                              elem.type === 'qrcode' && elem.openOnClick !== false
                                ? `📱 QR Code: ${resolveQrCodeValue(elem, {}, datasets, user, orgData, tenantConfig)}`
                                : (linkInfo ? `${linkInfo.type === 'email' ? '📧 Email: ' : '🔗 Open Link: '}${linkInfo.url}` : undefined)
                            }
                            style={{
                              position: 'absolute',
                              left: `${elem.x}px`,
                              top: `${elem.y}px`,
                              width: `${elem.width}px`,
                              height: `${elem.height}px`,
                              zIndex: sides.front.elements.indexOf(elem),
                              cursor: (linkInfo || (elem.type === 'qrcode' && elem.openOnClick !== false)) ? 'pointer' : 'default',
                              opacity: (() => {
                                let opacity = elem.opacity !== undefined ? elem.opacity / 100 : 1;
                                if (elem.parentId) {
                                  const parent = sides.front.elements.find(el => el.id === elem.parentId);
                                  if (parent && parent.opacity !== undefined) {
                                    opacity *= (parent.opacity / 100);
                                  }
                                }
                                return opacity;
                              })(),
                              mixBlendMode: elem.blendMode || 'normal',
                              transform: elem.rotation ? `rotate(${elem.rotation}deg)` : 'none',
                              transformOrigin: 'center center',
                              ...getElementEffectsStyle(elem)
                            }}
                          >
                        {elem.type === 'image' && (() => {
                          const { rOuter, rGap, rPrimaryOuter, rInner } = calculateFrameBorderRadii(elem);
                          return (
                            <div style={{
                              width: '100%',
                              height: '100%',
                              boxSizing: 'border-box',
                              borderRadius: `${rOuter}px`,
                              overflow: 'hidden',
                              
                              // 1. Secondary Border styling (solid, dashed, dotted)
                              border: (elem.hasBorder !== false && elem.hasSecondBorder && elem.borderFillType2 !== 'gradient')
                                ? `${elem.strokeWidth2 || 2}px ${elem.strokeStyle2 || 'solid'} ${elem.strokeColor2 || '#000000'}`
                                : 'none',
                              
                              // 2. Gap/Border padding
                              padding: (elem.hasBorder !== false && elem.hasSecondBorder)
                                ? (elem.borderFillType2 === 'gradient' ? `${elem.strokeWidth2 || 2}px` : `${elem.borderGap || 0}px`)
                                : 0,
                              background: (elem.hasBorder !== false && elem.hasSecondBorder && elem.borderFillType2 === 'gradient')
                                ? elem.strokeColor2 || 'linear-gradient(to right, red, blue)'
                                : 'transparent'
                            }}>
                              {/* Intermediate gap wrapper to support secondary gradient border gap */}
                              <div style={{
                                width: '100%',
                                height: '100%',
                                boxSizing: 'border-box',
                                borderRadius: `${rGap}px`,
                                padding: (elem.hasBorder !== false && elem.hasSecondBorder && elem.borderFillType2 === 'gradient')
                                  ? `${elem.borderGap || 0}px`
                                  : 0,
                                background: (elem.hasBorder !== false && elem.hasSecondBorder && elem.borderFillType2 === 'gradient')
                                  ? (sides.front.background.value || '#ffffff')
                                  : 'transparent',
                                display: (elem.hasBorder !== false && elem.hasSecondBorder && elem.borderFillType2 === 'gradient')
                                  ? 'flex'
                                  : 'block',
                                alignItems: 'center',
                                justifyContent: 'center'
                              }}>
                                {/* Inner element wrapper containing primary border */}
                                <div style={{
                                  width: '100%',
                                  height: '100%',
                                  boxSizing: 'border-box',
                                  borderRadius: `${rPrimaryOuter}px`,
                                  overflow: 'hidden',
                                  
                                  // 3. Primary Border styling
                                  border: (elem.hasBorder !== false && (elem.strokeWidth !== undefined ? elem.strokeWidth : 1) > 0 && elem.borderFillType !== 'gradient')
                                    ? `${elem.strokeWidth !== undefined ? elem.strokeWidth : 1}px ${elem.strokeStyle || 'solid'} ${elem.strokeColor || '#ffffff'}`
                                    : 'none',
                                  
                                  // 4. Primary Border Gradient padding
                                  padding: (elem.hasBorder !== false && (elem.strokeWidth !== undefined ? elem.strokeWidth : 1) > 0 && elem.borderFillType === 'gradient')
                                    ? `${elem.strokeWidth !== undefined ? elem.strokeWidth : 1}px`
                                    : 0,
                                  background: (elem.hasBorder !== false && (elem.strokeWidth !== undefined ? elem.strokeWidth : 1) > 0 && elem.borderFillType === 'gradient')
                                    ? elem.strokeColor || 'linear-gradient(to right, red, blue)'
                                    : (elem.fill || 'transparent'),
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center'
                                }}>
                                  {/* Inner content box for image clipping */}
                                  <div style={{ position: 'relative', width: '100%', height: '100%', borderRadius: `${rInner}px`, overflow: 'hidden' }}>
                                    {getResolvedImageSrc(elem) ? (
                                      elem.hasBorder !== false && elem.borderFillType === 'gradient' ? (
                                        <div style={{ width: '100%', height: '100%', borderRadius: `${rInner}px`, overflow: 'hidden', position: 'relative', zIndex: 1 }}>
                                          <PictureFrameImage
                                            elem={elem}
                                            resolvedSrc={getResolvedImageSrc(elem)}
                                            filterStyle={getImageFilterStyle(elem.effect)}
                                          />
                                        </div>
                                      ) : (
                                        <PictureFrameImage
                                          elem={elem}
                                          resolvedSrc={getResolvedImageSrc(elem)}
                                          filterStyle={getImageFilterStyle(elem.effect)}
                                        />
                                      )
                                    ) : (
                                      <div style={{
                                        width: '100%',
                                        height: '100%',
                                        backgroundColor: '#262629', display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        color: 'var(--text-mute)',
                                        gap: '6px'
                                      }}>
                                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ opacity: 0.7 }}>
                                          <circle cx="12" cy="12" r="10" />
                                          <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
                                          <line x1="12" y1="17" x2="12.01" y2="17" />
                                        </svg>
                                        <span style={{ fontSize: '9px', opacity: 0.6 }}>No Source</span>
                                      </div>
                                    )}
                                    {/* Overlay element for gradient/sheen overlays */}
                                    {getImageOverlayStyle(elem.effect) && (
                                      <div style={{
                                        position: 'absolute',
                                        top: 0, left: 0, right: 0, bottom: 0,
                                        pointerEvents: 'none',
                                        borderRadius: `${rInner}px`,
                                        ...getImageOverlayStyle(elem.effect), zIndex: 5
                                      }} />
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>
                          );
                        })()}
                        {elem.type === 'shape' && (
                          elem.shapeType === 'triangle' ? (
                            <svg width="100%" height="100%" viewBox="0 0 100 100" preserveAspectRatio="none">
                              {elem.fillType === 'pattern' && elem.pattern && (
                                <defs>
                                  <pattern
                                    id={`pat-front-${elem.id}`}
                                    patternUnits="userSpaceOnUse"
                                    width={Math.max(2, Math.round((elem.pattern.tileWidth || 32) * ((elem.pattern.scale !== undefined ? elem.pattern.scale : 100) / 100)))}
                                    height={Math.max(2, Math.round((elem.pattern.tileHeight || 32) * ((elem.pattern.scale !== undefined ? elem.pattern.scale : 100) / 100)))}
                                  >
                                    {elem.pattern.tintColor && elem.pattern.tintColor !== 'transparent' && (
                                      <rect width="100%" height="100%" fill={elem.pattern.tintColor} />
                                    )}
                                    <image
                                      href={elem.pattern.image}
                                      width={Math.max(2, Math.round((elem.pattern.tileWidth || 32) * ((elem.pattern.scale !== undefined ? elem.pattern.scale : 100) / 100)))}
                                      height={Math.max(2, Math.round((elem.pattern.tileHeight || 32) * ((elem.pattern.scale !== undefined ? elem.pattern.scale : 100) / 100)))}
                                      opacity={elem.pattern.opacity !== undefined ? (elem.pattern.opacity > 1 ? elem.pattern.opacity / 100 : elem.pattern.opacity) : 1}
                                    />
                                  </pattern>
                                </defs>
                              )}
                              <polygon points="50,0 0,100 100,100" fill={elem.fillType === 'pattern' && elem.pattern ? `url(#pat-front-${elem.id})` : (elem.fill || '#e6e6e6')} />
                            </svg>
                          ) : elem.shapeType === 'diamond' ? (
                            <svg width="100%" height="100%" viewBox="0 0 100 100" preserveAspectRatio="none">
                              {elem.fillType === 'pattern' && elem.pattern && (
                                <defs>
                                  <pattern
                                    id={`pat-front-${elem.id}`}
                                    patternUnits="userSpaceOnUse"
                                    width={Math.max(2, Math.round((elem.pattern.tileWidth || 32) * ((elem.pattern.scale !== undefined ? elem.pattern.scale : 100) / 100)))}
                                    height={Math.max(2, Math.round((elem.pattern.tileHeight || 32) * ((elem.pattern.scale !== undefined ? elem.pattern.scale : 100) / 100)))}
                                  >
                                    {elem.pattern.tintColor && elem.pattern.tintColor !== 'transparent' && (
                                      <rect width="100%" height="100%" fill={elem.pattern.tintColor} />
                                    )}
                                    <image
                                      href={elem.pattern.image}
                                      width={Math.max(2, Math.round((elem.pattern.tileWidth || 32) * ((elem.pattern.scale !== undefined ? elem.pattern.scale : 100) / 100)))}
                                      height={Math.max(2, Math.round((elem.pattern.tileHeight || 32) * ((elem.pattern.scale !== undefined ? elem.pattern.scale : 100) / 100)))}
                                      opacity={elem.pattern.opacity !== undefined ? (elem.pattern.opacity > 1 ? elem.pattern.opacity / 100 : elem.pattern.opacity) : 1}
                                    />
                                  </pattern>
                                </defs>
                              )}
                              <polygon points="50,0 100,50 50,100 0,50" fill={elem.fillType === 'pattern' && elem.pattern ? `url(#pat-front-${elem.id})` : (elem.fill || '#e6e6e6')} />
                            </svg>
                          ) : elem.shapeType === 'polygon' ? (
                            <svg width="100%" height="100%" viewBox="0 0 100 100" preserveAspectRatio="none">
                              {elem.fillType === 'pattern' && elem.pattern && (
                                <defs>
                                  <pattern
                                    id={`pat-front-${elem.id}`}
                                    patternUnits="userSpaceOnUse"
                                    width={Math.max(2, Math.round((elem.pattern.tileWidth || 32) * ((elem.pattern.scale !== undefined ? elem.pattern.scale : 100) / 100)))}
                                    height={Math.max(2, Math.round((elem.pattern.tileHeight || 32) * ((elem.pattern.scale !== undefined ? elem.pattern.scale : 100) / 100)))}
                                  >
                                    {elem.pattern.tintColor && elem.pattern.tintColor !== 'transparent' && (
                                      <rect width="100%" height="100%" fill={elem.pattern.tintColor} />
                                    )}
                                    <image
                                      href={elem.pattern.image}
                                      width={Math.max(2, Math.round((elem.pattern.tileWidth || 32) * ((elem.pattern.scale !== undefined ? elem.pattern.scale : 100) / 100)))}
                                      height={Math.max(2, Math.round((elem.pattern.tileHeight || 32) * ((elem.pattern.scale !== undefined ? elem.pattern.scale : 100) / 100)))}
                                      opacity={elem.pattern.opacity !== undefined ? (elem.pattern.opacity > 1 ? elem.pattern.opacity / 100 : elem.pattern.opacity) : 1}
                                    />
                                  </pattern>
                                </defs>
                              )}
                              <polygon points="25,0 75,0 100,50 75,100 25,100 0,50" fill={elem.fillType === 'pattern' && elem.pattern ? `url(#pat-front-${elem.id})` : (elem.fill || '#e6e6e6')} />
                            </svg>
                          ) : elem.shapeType === 'star' ? (
                            <svg width="100%" height="100%" viewBox="0 0 100 100" preserveAspectRatio="none">
                              {elem.fillType === 'pattern' && elem.pattern && (
                                <defs>
                                  <pattern
                                    id={`pat-front-${elem.id}`}
                                    patternUnits="userSpaceOnUse"
                                    width={Math.max(2, Math.round((elem.pattern.tileWidth || 32) * ((elem.pattern.scale !== undefined ? elem.pattern.scale : 100) / 100)))}
                                    height={Math.max(2, Math.round((elem.pattern.tileHeight || 32) * ((elem.pattern.scale !== undefined ? elem.pattern.scale : 100) / 100)))}
                                  >
                                    {elem.pattern.tintColor && elem.pattern.tintColor !== 'transparent' && (
                                      <rect width="100%" height="100%" fill={elem.pattern.tintColor} />
                                    )}
                                    <image
                                      href={elem.pattern.image}
                                      width={Math.max(2, Math.round((elem.pattern.tileWidth || 32) * ((elem.pattern.scale !== undefined ? elem.pattern.scale : 100) / 100)))}
                                      height={Math.max(2, Math.round((elem.pattern.tileHeight || 32) * ((elem.pattern.scale !== undefined ? elem.pattern.scale : 100) / 100)))}
                                      opacity={elem.pattern.opacity !== undefined ? (elem.pattern.opacity > 1 ? elem.pattern.opacity / 100 : elem.pattern.opacity) : 1}
                                    />
                                  </pattern>
                                </defs>
                              )}
                              <polygon points="50,0 61,35 98,35 68,57 79,91 50,70 21,91 32,57 2,35 39,35" fill={elem.fillType === 'pattern' && elem.pattern ? `url(#pat-front-${elem.id})` : (elem.fill || '#e6e6e6')} />
                            </svg>
                          ) : (
                            // Rectangle, Circle, Pill with border styling
                            <div style={{
                              width: '100%',
                              height: '100%',
                              boxSizing: 'border-box',
                              borderRadius: elem.shapeType === 'circle' ? '50%' : elem.shapeType === 'pill' ? '999px' : `${elem.hasBorder !== false ? (elem.borderRadius !== undefined ? elem.borderRadius : 0) : 0}px`,
                              overflow: 'hidden',
                              
                              // 1. Secondary Border styling (Dotted, dashed, solid/line)
                              border: (elem.hasBorder !== false && elem.hasSecondBorder && elem.borderFillType2 !== 'gradient')
                                ? `${elem.strokeWidth2 || 2}px ${elem.strokeStyle2 || 'solid'} ${elem.strokeColor2 || '#000000'}`
                                : 'none',
                              
                              // 2. Gap/Border padding
                              padding: (elem.hasBorder !== false && elem.hasSecondBorder)
                                ? (elem.borderFillType2 === 'gradient' ? `${elem.strokeWidth2 || 2}px` : `${elem.borderGap || 0}px`)
                                : 0,
                              background: (elem.hasBorder !== false && elem.hasSecondBorder && elem.borderFillType2 === 'gradient')
                                ? elem.strokeColor2 || 'linear-gradient(to right, red, blue)'
                                : 'transparent'
                            }}>
                              {/* Intermediate gap wrapper to support secondary gradient border gap */}
                              <div style={{
                                width: '100%',
                                height: '100%',
                                boxSizing: 'border-box',
                                borderRadius: 'inherit',
                                padding: (elem.hasBorder !== false && elem.hasSecondBorder && elem.borderFillType2 === 'gradient')
                                  ? `${elem.borderGap || 0}px`
                                  : 0,
                                background: (elem.hasBorder !== false && elem.hasSecondBorder && elem.borderFillType2 === 'gradient')
                                  ? (sides.front.background.value || '#ffffff')
                                  : 'transparent',
                                display: (elem.hasBorder !== false && elem.hasSecondBorder && elem.borderFillType2 === 'gradient')
                                  ? 'flex'
                                  : 'block',
                                alignItems: 'center',
                                justifyItems: 'center'
                              }}>
                                {/* Inner element wrapper that contains the primary border and fill background */}
                                <div style={{
                                  width: '100%',
                                  height: '100%',
                                  boxSizing: 'border-box',
                                  borderRadius: 'inherit', // inherits parent border radius
                                  position: 'relative',
                                  overflow: 'hidden',
                                  
                                  // 3. Primary Border styling (only if not gradient border fill)
                                  border: (elem.hasBorder !== false && elem.borderFillType !== 'gradient') 
                                    ? `${elem.strokeWidth || 0}px ${elem.strokeStyle || 'solid'} ${elem.strokeColor || '#cbd5e1'}` 
                                    : 'none',
                                  
                                  // 4. Primary Border Gradient padding
                                  padding: (elem.hasBorder !== false && elem.borderFillType === 'gradient')
                                    ? `${elem.strokeWidth || 0}px`
                                    : 0,
                                  background: (elem.hasBorder !== false && elem.borderFillType === 'gradient')
                                    ? elem.strokeColor || 'linear-gradient(to right, red, blue)'
                                    : (elem.fillType === 'pattern' ? (elem.pattern?.tintColor || '#e6e6e6') : (elem.fill || '#e6e6e6'))
                                }}>
                                  {/* Content box inside primary gradient border */}
                                  {elem.hasBorder !== false && elem.borderFillType === 'gradient' && (
                                    <div style={{
                                      width: '100%',
                                      height: '100%',
                                      position: 'relative',
                                      overflow: 'hidden',
                                      background: elem.fillType === 'pattern' ? (elem.pattern?.tintColor || '#e6e6e6') : (elem.fill || '#e6e6e6'),
                                      borderRadius: 'inherit'
                                    }}>
                                      {elem.fillType === 'pattern' && elem.pattern && (
                                        <div style={{
                                          position: 'absolute',
                                          top: 0,
                                          left: 0,
                                          width: '100%',
                                          height: '100%',
                                          borderRadius: 'inherit',
                                          pointerEvents: 'none',
                                          opacity: elem.pattern.opacity !== undefined ? (elem.pattern.opacity > 1 ? elem.pattern.opacity / 100 : elem.pattern.opacity) : 1,
                                          backgroundImage: `url("${elem.pattern.image}")`,
                                          backgroundRepeat: elem.pattern.repeat || 'repeat',
                                          backgroundSize: `${Math.max(2, Math.round((elem.pattern.tileWidth || 32) * ((elem.pattern.scale !== undefined ? elem.pattern.scale : 100) / 100)))}px ${Math.max(2, Math.round((elem.pattern.tileHeight || 32) * ((elem.pattern.scale !== undefined ? elem.pattern.scale : 100) / 100)))}px`,
                                          backgroundPosition: '0 0',
                                          mixBlendMode: elem.pattern.blendMode || 'normal'
                                        }} />
                                      )}
                                    </div>
                                  )}
                                  {!(elem.hasBorder !== false && elem.borderFillType === 'gradient') && elem.fillType === 'pattern' && elem.pattern && (
                                    <div style={{
                                      position: 'absolute',
                                      top: 0,
                                      left: 0,
                                      width: '100%',
                                      height: '100%',
                                      borderRadius: 'inherit',
                                      pointerEvents: 'none',
                                      opacity: elem.pattern.opacity !== undefined ? (elem.pattern.opacity > 1 ? elem.pattern.opacity / 100 : elem.pattern.opacity) : 1,
                                      backgroundImage: `url("${elem.pattern.image}")`,
                                      backgroundRepeat: elem.pattern.repeat || 'repeat',
                                      backgroundSize: `${Math.max(2, Math.round((elem.pattern.tileWidth || 32) * ((elem.pattern.scale !== undefined ? elem.pattern.scale : 100) / 100)))}px ${Math.max(2, Math.round((elem.pattern.tileHeight || 32) * ((elem.pattern.scale !== undefined ? elem.pattern.scale : 100) / 100)))}px`,
                                      backgroundPosition: '0 0',
                                      mixBlendMode: elem.pattern.blendMode || 'normal'
                                    }} />
                                  )}
                                </div>
                              </div>
                            </div>
                          )
                        )}
                        {elem.type === 'text' && (
                          <div style={{
                            width: '100%',
                            height: '100%',
                            fontSize: `${elem.fontSize}px`,
                            fontFamily: elem.fontFamily,
                            fontWeight: elem.fontWeight,
                            fontStyle: elem.fontStyle || 'normal',
                            textDecoration: elem.textDecoration || 'none',
                            letterSpacing: `${elem.letterSpacing || 0}px`,
                            textTransform: elem.textTransform || 'none',
                            textAlign: elem.alignment || 'left',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: elem.alignment === 'center' ? 'center' : elem.alignment === 'right' ? 'flex-end' : 'flex-start',
                            lineHeight: elem.lineHeight || 1.1,
                            background: elem.colorType === 'gradient' ? (elem.color || 'linear-gradient(90deg, #ffffff 0%, #000000 100%)') : 'transparent',
                            WebkitBackgroundClip: elem.colorType === 'gradient' ? 'text' : 'unset',
                            WebkitTextFillColor: elem.colorType === 'gradient' ? 'transparent' : 'unset',
                            color: elem.colorType === 'gradient' ? 'transparent' : (elem.color || '#ffffff'),
                            transform: `translateY(${elem.baselineShift || 0}px) scaleX(${elem.scaleX !== undefined ? elem.scaleX / 100 : 1}) scaleY(${elem.scaleY !== undefined ? elem.scaleY / 100 : 1}) skewX(${elem.shear || 0}deg)`,
                            transformOrigin: elem.alignment === 'center' ? 'center center' : elem.alignment === 'right' ? 'right center' : 'left center',
                            WebkitTextStrokeWidth: `${elem.textStrokeWidth || 0}px`,
                            WebkitTextStrokeColor: elem.textStrokeColorType === 'gradient' ? (elem.textStrokeGradient?.stops?.[0]?.color || '#ffffff') : (elem.textStrokeColor || '#ffffff')
                          }}>{resolveElementText(elem)}</div>
                        )}

                        {elem.type === 'datagrid' && (
                          <div style={{
                            width: '100%',
                            height: '100%',
                            boxSizing: 'border-box',
                            borderRadius: elem.hasBorder !== false ? `${elem.borderRadius || 0}px` : 0,
                            overflow: 'hidden',
                            
                            // 1. Secondary Border styling
                            border: (elem.hasBorder !== false && elem.hasSecondBorder && elem.borderFillType2 !== 'gradient')
                              ? `${elem.strokeWidth2 || 2}px ${elem.strokeStyle2 || 'solid'} ${elem.strokeColor2 || '#000000'}`
                              : 'none',
                            
                            // 2. Gap/Border padding
                            padding: (elem.hasBorder !== false && elem.hasSecondBorder)
                              ? (elem.borderFillType2 === 'gradient' ? `${elem.strokeWidth2 || 2}px` : `${elem.borderGap || 0}px`)
                              : 0,
                            background: (elem.hasBorder !== false && elem.hasSecondBorder && elem.borderFillType2 === 'gradient')
                              ? elem.strokeColor2 || 'linear-gradient(to right, red, blue)'
                              : 'transparent'
                          }}>
                            {/* Intermediate gap wrapper */}
                            <div style={{
                              width: '100%',
                              height: '100%',
                              boxSizing: 'border-box',
                              borderRadius: 'inherit',
                              padding: (elem.hasBorder !== false && elem.hasSecondBorder && elem.borderFillType2 === 'gradient')
                                ? `${elem.borderGap || 0}px`
                                : 0,
                              background: (elem.hasBorder !== false && elem.hasSecondBorder && elem.borderFillType2 === 'gradient')
                                ? (sides.front.background.value || '#ffffff')
                                : 'transparent',
                              display: (elem.hasBorder !== false && elem.hasSecondBorder && elem.borderFillType2 === 'gradient')
                                ? 'flex'
                                : 'block',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}>
                              {/* Inner element wrapper containing primary border */}
                              <div style={{
                                width: '100%',
                                height: '100%',
                                boxSizing: 'border-box',
                                borderRadius: 'inherit',
                                overflow: 'hidden',
                                flexShrink: 0,
                                
                                // 3. Primary Border styling
                                border: (elem.hasBorder !== false && elem.borderFillType !== 'gradient')
                                  ? `${elem.strokeWidth !== undefined ? elem.strokeWidth : 1}px ${elem.strokeStyle || 'solid'} ${elem.strokeColor || '#ffffff'}`
                                  : 'none',
                                
                                // 4. Primary Border Gradient padding
                                padding: (elem.hasBorder !== false && elem.borderFillType === 'gradient')
                                  ? `${elem.strokeWidth !== undefined ? elem.strokeWidth : 1}px`
                                  : 0,
                                background: (elem.hasBorder !== false && elem.borderFillType === 'gradient')
                                  ? elem.strokeColor || 'linear-gradient(to right, red, blue)'
                                  : 'transparent',
                                display: 'flex',
                                flexDirection: 'column'
                              }}>
                                {/* Actual Table / Datagrid content */}
                                <div style={{
                                  width: '100%',
                                  height: '100%',
                                  display: 'flex',
                                  flexDirection: 'column',
                                  boxSizing: 'border-box',
                                  fontFamily: elem.fontFamily || 'Arial',
                                  fontSize: `${elem.fontSize || 11}px`,
                                  letterSpacing: `${elem.letterSpacing || 0}px`,
                                  lineHeight: elem.lineHeight || 1.1,
                                  background: elem.fillType === 'gradient'
                                    ? (elem.fill || 'linear-gradient(90deg, rgba(255,255,255,0.15) 0%, rgba(0,0,0,0.2) 100%)')
                                    : (elem.fillType === 'pattern' ? (elem.pattern?.tintColor || 'rgba(0,0,0,0.06)') : (elem.fill || 'rgba(0,0,0,0.06)')),
                                  overflow: 'hidden',
                                  borderRadius: 'inherit',
                                  position: 'relative'
                                }}>
                                  {elem.fillType === 'pattern' && elem.pattern && (
                                    <div style={{
                                      position: 'absolute',
                                      top: 0,
                                      left: 0,
                                      width: '100%',
                                      height: '100%',
                                      borderRadius: 'inherit',
                                      pointerEvents: 'none',
                                      opacity: elem.pattern.opacity !== undefined ? (elem.pattern.opacity > 1 ? elem.pattern.opacity / 100 : elem.pattern.opacity) : 1,
                                      backgroundImage: `url("${elem.pattern.image}")`,
                                      backgroundRepeat: elem.pattern.repeat || 'repeat',
                                      backgroundSize: `${Math.max(2, Math.round((elem.pattern.tileWidth || 32) * ((elem.pattern.scale !== undefined ? elem.pattern.scale : 100) / 100)))}px ${Math.max(2, Math.round((elem.pattern.tileHeight || 32) * ((elem.pattern.scale !== undefined ? elem.pattern.scale : 100) / 100)))}px`,
                                      backgroundPosition: '0 0',
                                      mixBlendMode: elem.pattern.blendMode || 'normal',
                                      zIndex: 0
                                    }} />
                                  )}
                                  {/* Header Row */}
                                  {elem.headerEnabled && (
                                    <div style={{
                                      display: 'flex',
                                      justifyContent: 'space-between',
                                      alignItems: 'center',
                                      background: elem.headerFillType === 'gradient' ? (elem.headerFill || 'linear-gradient(90deg, rgba(255,255,255,0.15) 0%, rgba(0,0,0,0.2) 100%)') : (elem.headerFill || 'rgba(255,255,255,0.1)'),
                                      color: elem.headerColor || '#ffffff',
                                      fontWeight: 'bold',
                                      paddingTop: `${elem.rowPadding !== undefined ? elem.rowPadding : 4}px`,
                                      paddingBottom: `${elem.rowPadding !== undefined ? elem.rowPadding : 4}px`,
                                      paddingLeft: `${elem.colPadding !== undefined ? elem.colPadding : 6}px`,
                                      paddingRight: `${elem.colPadding !== undefined ? elem.colPadding : 6}px`,
                                      borderBottom: (elem.gridLinesEnabled !== false)
                                        ? `${elem.gridLineThickness !== undefined ? elem.gridLineThickness : 1}px ${elem.gridLineStyle || 'solid'} ${elem.gridLineColor || 'rgba(255,255,255,0.08)'}`
                                        : 'none'
                                    }}>
                                      <span style={{
                                        width: `${elem.colWidthRatio || 50}%`,
                                        textAlign: elem.labelAlign || 'left',
                                        boxSizing: 'border-box'
                                      }}>
                                        {elem.headerLabelText || 'Attribute'}
                                      </span>
                                      <span style={{
                                        width: `${100 - (elem.colWidthRatio || 50)}%`,
                                        textAlign: elem.valueAlign || 'right',
                                        boxSizing: 'border-box'
                                      }}>
                                        {elem.headerValueText || 'Value'}
                                      </span>
                                    </div>
                                  )}

                                  {/* Data Rows */}
                                  {elem.rows.map((row, idx) => (
                                    <div key={idx} style={{
                                      display: 'flex',
                                      justifyContent: 'space-between',
                                      alignItems: 'center',
                                      backgroundColor: (elem.zebraEnabled && idx % 2 === 1) ? (elem.zebraColor || 'rgba(255,255,255,0.03)') : 'transparent',
                                      paddingTop: `${elem.rowPadding !== undefined ? elem.rowPadding : 4}px`,
                                      paddingBottom: `${elem.rowPadding !== undefined ? elem.rowPadding : 4}px`,
                                      paddingLeft: `${elem.colPadding !== undefined ? elem.colPadding : 6}px`,
                                      paddingRight: `${elem.colPadding !== undefined ? elem.colPadding : 6}px`,
                                      borderBottom: (elem.gridLinesEnabled !== false && idx < elem.rows.length - 1)
                                        ? `${elem.gridLineThickness !== undefined ? elem.gridLineThickness : 1}px ${elem.gridLineStyle || 'solid'} ${elem.gridLineColor || 'rgba(255,255,255,0.08)'}`
                                        : 'none',
                                      boxSizing: 'border-box'
                                    }}>
                                      <span style={{ 
                                        width: `${elem.colWidthRatio || 50}%`,
                                        textAlign: elem.labelAlign || 'left',
                                        fontSize: `${elem.labelFontSize || elem.fontSize || 11}px`,
                                        fontFamily: elem.labelFontFamily || elem.fontFamily || 'Arial',
                                        fontWeight: elem.labelFontWeight || elem.fontWeight || '400',
                                        fontStyle: elem.labelFontStyle || elem.fontStyle || 'normal',
                                        textDecoration: elem.labelDecoration || elem.textDecoration || 'none',
                                        letterSpacing: `${elem.labelLetterSpacing !== undefined ? elem.labelLetterSpacing : (elem.letterSpacing || 0)}px`,
                                        background: elem.labelColorType === 'gradient' ? (elem.labelColor || 'linear-gradient(90deg, #ffffff 0%, #000000 100%)') : 'transparent',
                                        WebkitBackgroundClip: elem.labelColorType === 'gradient' ? 'text' : 'unset',
                                        WebkitTextFillColor: elem.labelColorType === 'gradient' ? 'transparent' : 'unset',
                                        color: elem.labelColorType === 'gradient' ? 'transparent' : (elem.labelColor || elem.color || '#ffffff'),
                                        display: elem.labelColorType === 'gradient' ? 'inline-block' : 'block',
                                        boxSizing: 'border-box',
                                        paddingRight: '4px',
                                        whiteSpace: 'nowrap',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis'
                                      }}>{applyTextTransform(row[0], elem.labelTransformModifier)}</span>
                                      <span style={{ 
                                        width: `${100 - (elem.colWidthRatio || 50)}%`,
                                        textAlign: elem.valueAlign || 'right',
                                        fontSize: `${elem.valueFontSize || elem.fontSize || 11}px`,
                                        fontFamily: elem.valueFontFamily || elem.fontFamily || 'Arial',
                                        fontWeight: elem.valueFontWeight || (elem.fontWeight ? (parseInt(elem.fontWeight) >= 700 ? elem.fontWeight : '700') : '700'),
                                        fontStyle: elem.valueFontStyle || elem.fontStyle || 'normal',
                                        textDecoration: elem.valueDecoration || elem.textDecoration || 'none',
                                        letterSpacing: `${elem.valueLetterSpacing !== undefined ? elem.valueLetterSpacing : (elem.letterSpacing || 0)}px`,
                                        background: elem.valueColorType === 'gradient' ? (elem.valueColor || 'linear-gradient(90deg, #ffffff 0%, #000000 100%)') : 'transparent',
                                        WebkitBackgroundClip: elem.valueColorType === 'gradient' ? 'text' : 'unset',
                                        WebkitTextFillColor: elem.valueColorType === 'gradient' ? 'transparent' : 'unset',
                                        color: elem.valueColorType === 'gradient' ? 'transparent' : (elem.valueColor || elem.color || '#ffffff'),
                                        display: elem.valueColorType === 'gradient' ? 'inline-block' : 'block',
                                        boxSizing: 'border-box',
                                        paddingLeft: '4px',
                                        whiteSpace: 'nowrap',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis'
                                      }}>{applyTextTransform(resolveTemplateVariables(row[1]), elem.valueTransformModifier)}</span>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            </div>
                          </div>
                        )}
                        {elem.type === 'certify' && renderCertifyElement(elem)}

                        {elem.type === 'accent' && (
                          <div style={{
                            width: (elem.dividerType === 'vertical' || elem.orientation === 'vertical') ? `${elem.strokeWidth || 2}px` : '100%',
                            height: (elem.dividerType === 'vertical' || elem.orientation === 'vertical') ? '100%' : `${elem.strokeWidth || 2}px`,
                            background: elem.fill || elem.strokeColor || '#cbd5e1',
                            margin: (elem.dividerType === 'vertical' || elem.orientation === 'vertical') ? '0 auto' : '0'
                          }} />
                        )}

                        {elem.type === 'qrcode' && (
                          <div style={{
                            width: '100%',
                            height: '100%',
                            boxSizing: 'border-box',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            borderRadius: `${elem.borderRadius !== undefined ? elem.borderRadius : 6}px`,
                            overflow: 'hidden',
                            backgroundColor: elem.bgColor === 'transparent' ? 'transparent' : (elem.bgColor || '#ffffff'),
                            border: (elem.hasBorder && elem.strokeWidth) ? `${elem.strokeWidth}px ${elem.strokeStyle || 'solid'} ${elem.strokeColor || '#cbd5e1'}` : 'none',
                            padding: `${elem.padding || 0}px`
                          }}>
                            <QRCodeSVG
                              value={resolveQrCodeValue(elem, {}, datasets, user, orgData, tenantConfig)}
                              size={Math.min(elem.width, elem.height)}
                              fgColor={elem.fgColor || '#000000'}
                              bgColor={elem.bgColor === 'transparent' ? 'transparent' : (elem.bgColor || '#ffffff')}
                              margin={elem.margin !== undefined ? elem.margin : 2}
                              style={{ width: '100%', height: '100%', borderRadius: 'inherit' }}
                            />
                          </div>
                        )}
                      </div>
                    );
                  })}
                  </div>

                  {/* Back Side */}
                  <div style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    backfaceVisibility: 'hidden',
                    WebkitBackfaceVisibility: 'hidden',
                    transform: 'rotateY(180deg) translateZ(1px)',
                    WebkitTransform: 'rotateY(180deg) translateZ(1px)',
                    transformStyle: 'flat',
                    WebkitTransformStyle: 'flat',
                    zIndex: previewFlipped ? 2 : 1,
                    pointerEvents: previewFlipped ? 'auto' : 'none',
                    borderRadius: globalStyles.borderRadius,
                    border: `${globalStyles.borderWidth}px ${globalStyles.borderStyle} ${globalStyles.borderColor}`,
                    ...(sides.back.background?.type === 'gradient' || (sides.back.background?.value && String(sides.back.background.value).includes('gradient'))
                      ? { background: sides.back.background.value }
                      : sides.back.background?.type === 'pattern'
                        ? {
                            backgroundColor: sides.back.background?.pattern?.tintColor || '#ffffff',
                            background: sides.back.background?.value || '#ffffff'
                          }
                        : {
                            backgroundColor: sides.back.background?.value || '#ffffff'
                          }
                    ),
                    padding: 0,
                    boxSizing: 'border-box'
                  }}>
                    {sides.back.background.type === 'pattern' && sides.back.background.pattern && (
                      <div style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        borderRadius: 'inherit',
                        pointerEvents: 'none',
                        opacity: sides.back.background.pattern.opacity !== undefined ? (sides.back.background.pattern.opacity > 1 ? sides.back.background.pattern.opacity / 100 : sides.back.background.pattern.opacity) : 1,
                        backgroundImage: `url("${sides.back.background.pattern.image}")`,
                        backgroundRepeat: sides.back.background.pattern.repeat || 'repeat',
                        backgroundSize: `${Math.max(2, Math.round((sides.back.background.pattern.tileWidth || 32) * ((sides.back.background.pattern.scale !== undefined ? sides.back.background.pattern.scale : 100) / 100)))}px ${Math.max(2, Math.round((sides.back.background.pattern.tileHeight || 32) * ((sides.back.background.pattern.scale !== undefined ? sides.back.background.pattern.scale : 100) / 100)))}px`,
                        backgroundPosition: '0 0',
                        mixBlendMode: sides.back.background.pattern.blendMode || 'normal',
                        zIndex: 0
                      }} />
                    )}
                    {sides.back.elements
                      .filter(e => {
                        if (e.visible === false) return false;
                        if (e.parentId) {
                          const parent = sides.back.elements.find(el => el.id === e.parentId);
                          if (parent && parent.visible === false) return false;
                        }
                        return true;
                      })
                      .map(elem => {
                        if (elem.type === 'group') return null;
                        const linkInfo = resolveElementLink(elem, linkMappings, fieldMappings, {}, datasets, user, orgData, tenantConfig);
                        return (
                          <div
                            key={elem.id}
                            onClick={(e) => {
                              if (elem.type === 'qrcode' && elem.openOnClick !== false) {
                                const qrUrl = resolveQrCodeValue(elem, {}, datasets, user, orgData, tenantConfig);
                                if (qrUrl && (qrUrl.startsWith('http://') || qrUrl.startsWith('https://') || qrUrl.startsWith('mailto:'))) {
                                  e.stopPropagation();
                                  window.open(qrUrl, '_blank', 'noopener,noreferrer');
                                  return;
                                }
                              }
                              if (linkInfo && linkInfo.url) {
                                e.stopPropagation();
                                openLinkSafely(linkInfo);
                              }
                            }}
                            title={
                              elem.type === 'qrcode' && elem.openOnClick !== false
                                ? `📱 QR Code: ${resolveQrCodeValue(elem, {}, datasets, user, orgData, tenantConfig)}`
                                : (linkInfo ? `${linkInfo.type === 'email' ? '📧 Email: ' : '🔗 Open Link: '}${linkInfo.url}` : undefined)
                            }
                            style={{
                              position: 'absolute',
                              left: `${elem.x}px`,
                              top: `${elem.y}px`,
                              width: `${elem.width}px`,
                              height: `${elem.height}px`,
                              zIndex: sides.back.elements.indexOf(elem),
                              cursor: (linkInfo || (elem.type === 'qrcode' && elem.openOnClick !== false)) ? 'pointer' : 'default',
                              opacity: (() => {
                                let opacity = elem.opacity !== undefined ? elem.opacity / 100 : 1;
                                if (elem.parentId) {
                                  const parent = sides.back.elements.find(el => el.id === elem.parentId);
                                  if (parent && parent.opacity !== undefined) {
                                    opacity *= (parent.opacity / 100);
                                  }
                                }
                                return opacity;
                              })(),
                              mixBlendMode: elem.blendMode || 'normal',
                              transform: elem.rotation ? `rotate(${elem.rotation}deg)` : 'none',
                              transformOrigin: 'center center',
                              ...getElementEffectsStyle(elem)
                            }}
                          >
                        {elem.type === 'image' && (() => {
                          const { rOuter, rGap, rPrimaryOuter, rInner } = calculateFrameBorderRadii(elem);
                          return (
                            <div style={{
                              width: '100%',
                              height: '100%',
                              boxSizing: 'border-box',
                              borderRadius: `${rOuter}px`,
                              overflow: 'hidden',
                              
                              // 1. Secondary Border styling (solid, dashed, dotted)
                              border: (elem.hasBorder !== false && elem.hasSecondBorder && elem.borderFillType2 !== 'gradient')
                                ? `${elem.strokeWidth2 || 2}px ${elem.strokeStyle2 || 'solid'} ${elem.strokeColor2 || '#000000'}`
                                : 'none',
                              
                              // 2. Gap/Border padding
                              padding: (elem.hasBorder !== false && elem.hasSecondBorder)
                                ? (elem.borderFillType2 === 'gradient' ? `${elem.strokeWidth2 || 2}px` : `${elem.borderGap || 0}px`)
                                : 0,
                              background: (elem.hasBorder !== false && elem.hasSecondBorder && elem.borderFillType2 === 'gradient')
                                ? elem.strokeColor2 || 'linear-gradient(to right, red, blue)'
                                : 'transparent'
                            }}>
                              {/* Intermediate gap wrapper to support secondary gradient border gap */}
                              <div style={{
                                width: '100%',
                                height: '100%',
                                boxSizing: 'border-box',
                                borderRadius: `${rGap}px`,
                                padding: (elem.hasBorder !== false && elem.hasSecondBorder && elem.borderFillType2 === 'gradient')
                                  ? `${elem.borderGap || 0}px`
                                  : 0,
                                background: (elem.hasBorder !== false && elem.hasSecondBorder && elem.borderFillType2 === 'gradient')
                                  ? (sides.back.background.value || '#ffffff')
                                  : 'transparent',
                                display: (elem.hasBorder !== false && elem.hasSecondBorder && elem.borderFillType2 === 'gradient')
                                  ? 'flex'
                                  : 'block',
                                alignItems: 'center',
                                justifyContent: 'center'
                              }}>
                                {/* Inner element wrapper containing primary border */}
                                <div style={{
                                  width: '100%',
                                  height: '100%',
                                  boxSizing: 'border-box',
                                  borderRadius: `${rPrimaryOuter}px`,
                                  overflow: 'hidden',
                                  flexShrink: 0,
                                  
                                  // 3. Primary Border styling
                                  border: (elem.hasBorder !== false && (elem.strokeWidth !== undefined ? elem.strokeWidth : 1) > 0 && elem.borderFillType !== 'gradient')
                                    ? `${elem.strokeWidth !== undefined ? elem.strokeWidth : 1}px ${elem.strokeStyle || 'solid'} ${elem.strokeColor || '#ffffff'}`
                                    : 'none',
                                  
                                  // 4. Primary Border Gradient padding
                                  padding: (elem.hasBorder !== false && (elem.strokeWidth !== undefined ? elem.strokeWidth : 1) > 0 && elem.borderFillType === 'gradient')
                                    ? `${elem.strokeWidth !== undefined ? elem.strokeWidth : 1}px`
                                    : 0,
                                  background: (elem.hasBorder !== false && (elem.strokeWidth !== undefined ? elem.strokeWidth : 1) > 0 && elem.borderFillType === 'gradient')
                                    ? elem.strokeColor || 'linear-gradient(to right, red, blue)'
                                    : (elem.fill || 'transparent'),
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center'
                                }}>
                                  {/* Inner content box for image clipping */}
                                  <div style={{ position: 'relative', width: '100%', height: '100%', borderRadius: `${rInner}px`, overflow: 'hidden' }}>
                                    {getResolvedImageSrc(elem) ? (
                                      elem.hasBorder !== false && elem.borderFillType === 'gradient' ? (
                                        <div style={{ width: '100%', height: '100%', borderRadius: `${rInner}px`, overflow: 'hidden', position: 'relative', zIndex: 1 }}>
                                          <PictureFrameImage
                                            elem={elem}
                                            resolvedSrc={getResolvedImageSrc(elem)}
                                            filterStyle={getImageFilterStyle(elem.effect)}
                                          />
                                        </div>
                                      ) : (
                                        <PictureFrameImage
                                          elem={elem}
                                          resolvedSrc={getResolvedImageSrc(elem)}
                                          filterStyle={getImageFilterStyle(elem.effect)}
                                        />
                                      )
                                    ) : (
                                      <div style={{
                                        width: '100%',
                                        height: '100%',
                                        backgroundColor: '#262629', display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        color: 'var(--text-mute)',
                                        gap: '6px'
                                      }}>
                                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ opacity: 0.7 }}>
                                          <circle cx="12" cy="12" r="10" />
                                          <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
                                          <line x1="12" y1="17" x2="12.01" y2="17" />
                                        </svg>
                                        <span style={{ fontSize: '9px', opacity: 0.6 }}>No Source</span>
                                      </div>
                                    )}
                                    {/* Overlay element for gradient/sheen overlays */}
                                    {getImageOverlayStyle(elem.effect) && (
                                      <div style={{
                                        position: 'absolute',
                                        top: 0, left: 0, right: 0, bottom: 0,
                                        pointerEvents: 'none',
                                        borderRadius: `${rInner}px`,
                                        ...getImageOverlayStyle(elem.effect), zIndex: 5
                                      }} />
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>
                          );
                        })()}

                        {elem.type === 'shape' && (
                          elem.shapeType === 'triangle' ? (
                            <svg width="100%" height="100%" viewBox="0 0 100 100" preserveAspectRatio="none">
                              {elem.fillType === 'pattern' && elem.pattern && (
                                <defs>
                                  <pattern
                                    id={`pat-back-${elem.id}`}
                                    patternUnits="userSpaceOnUse"
                                    width={Math.max(2, Math.round((elem.pattern.tileWidth || 32) * ((elem.pattern.scale !== undefined ? elem.pattern.scale : 100) / 100)))}
                                    height={Math.max(2, Math.round((elem.pattern.tileHeight || 32) * ((elem.pattern.scale !== undefined ? elem.pattern.scale : 100) / 100)))}
                                  >
                                    {elem.pattern.tintColor && elem.pattern.tintColor !== 'transparent' && (
                                      <rect width="100%" height="100%" fill={elem.pattern.tintColor} />
                                    )}
                                    <image
                                      href={elem.pattern.image}
                                      width={Math.max(2, Math.round((elem.pattern.tileWidth || 32) * ((elem.pattern.scale !== undefined ? elem.pattern.scale : 100) / 100)))}
                                      height={Math.max(2, Math.round((elem.pattern.tileHeight || 32) * ((elem.pattern.scale !== undefined ? elem.pattern.scale : 100) / 100)))}
                                      opacity={elem.pattern.opacity !== undefined ? (elem.pattern.opacity > 1 ? elem.pattern.opacity / 100 : elem.pattern.opacity) : 1}
                                    />
                                  </pattern>
                                </defs>
                              )}
                              <polygon points="50,0 0,100 100,100" fill={elem.fillType === 'pattern' && elem.pattern ? `url(#pat-back-${elem.id})` : (elem.fill || '#e6e6e6')} />
                            </svg>
                          ) : elem.shapeType === 'diamond' ? (
                            <svg width="100%" height="100%" viewBox="0 0 100 100" preserveAspectRatio="none">
                              {elem.fillType === 'pattern' && elem.pattern && (
                                <defs>
                                  <pattern
                                    id={`pat-back-${elem.id}`}
                                    patternUnits="userSpaceOnUse"
                                    width={Math.max(2, Math.round((elem.pattern.tileWidth || 32) * ((elem.pattern.scale !== undefined ? elem.pattern.scale : 100) / 100)))}
                                    height={Math.max(2, Math.round((elem.pattern.tileHeight || 32) * ((elem.pattern.scale !== undefined ? elem.pattern.scale : 100) / 100)))}
                                  >
                                    {elem.pattern.tintColor && elem.pattern.tintColor !== 'transparent' && (
                                      <rect width="100%" height="100%" fill={elem.pattern.tintColor} />
                                    )}
                                    <image
                                      href={elem.pattern.image}
                                      width={Math.max(2, Math.round((elem.pattern.tileWidth || 32) * ((elem.pattern.scale !== undefined ? elem.pattern.scale : 100) / 100)))}
                                      height={Math.max(2, Math.round((elem.pattern.tileHeight || 32) * ((elem.pattern.scale !== undefined ? elem.pattern.scale : 100) / 100)))}
                                      opacity={elem.pattern.opacity !== undefined ? (elem.pattern.opacity > 1 ? elem.pattern.opacity / 100 : elem.pattern.opacity) : 1}
                                    />
                                  </pattern>
                                </defs>
                              )}
                              <polygon points="50,0 100,50 50,100 0,50" fill={elem.fillType === 'pattern' && elem.pattern ? `url(#pat-back-${elem.id})` : (elem.fill || '#e6e6e6')} />
                            </svg>
                          ) : elem.shapeType === 'polygon' ? (
                            <svg width="100%" height="100%" viewBox="0 0 100 100" preserveAspectRatio="none">
                              {elem.fillType === 'pattern' && elem.pattern && (
                                <defs>
                                  <pattern
                                    id={`pat-back-${elem.id}`}
                                    patternUnits="userSpaceOnUse"
                                    width={Math.max(2, Math.round((elem.pattern.tileWidth || 32) * ((elem.pattern.scale !== undefined ? elem.pattern.scale : 100) / 100)))}
                                    height={Math.max(2, Math.round((elem.pattern.tileHeight || 32) * ((elem.pattern.scale !== undefined ? elem.pattern.scale : 100) / 100)))}
                                  >
                                    {elem.pattern.tintColor && elem.pattern.tintColor !== 'transparent' && (
                                      <rect width="100%" height="100%" fill={elem.pattern.tintColor} />
                                    )}
                                    <image
                                      href={elem.pattern.image}
                                      width={Math.max(2, Math.round((elem.pattern.tileWidth || 32) * ((elem.pattern.scale !== undefined ? elem.pattern.scale : 100) / 100)))}
                                      height={Math.max(2, Math.round((elem.pattern.tileHeight || 32) * ((elem.pattern.scale !== undefined ? elem.pattern.scale : 100) / 100)))}
                                      opacity={elem.pattern.opacity !== undefined ? (elem.pattern.opacity > 1 ? elem.pattern.opacity / 100 : elem.pattern.opacity) : 1}
                                    />
                                  </pattern>
                                </defs>
                              )}
                              <polygon points="25,0 75,0 100,50 75,100 25,100 0,50" fill={elem.fillType === 'pattern' && elem.pattern ? `url(#pat-back-${elem.id})` : (elem.fill || '#e6e6e6')} />
                            </svg>
                          ) : elem.shapeType === 'star' ? (
                            <svg width="100%" height="100%" viewBox="0 0 100 100" preserveAspectRatio="none">
                              {elem.fillType === 'pattern' && elem.pattern && (
                                <defs>
                                  <pattern
                                    id={`pat-back-${elem.id}`}
                                    patternUnits="userSpaceOnUse"
                                    width={Math.max(2, Math.round((elem.pattern.tileWidth || 32) * ((elem.pattern.scale !== undefined ? elem.pattern.scale : 100) / 100)))}
                                    height={Math.max(2, Math.round((elem.pattern.tileHeight || 32) * ((elem.pattern.scale !== undefined ? elem.pattern.scale : 100) / 100)))}
                                  >
                                    {elem.pattern.tintColor && elem.pattern.tintColor !== 'transparent' && (
                                      <rect width="100%" height="100%" fill={elem.pattern.tintColor} />
                                    )}
                                    <image
                                      href={elem.pattern.image}
                                      width={Math.max(2, Math.round((elem.pattern.tileWidth || 32) * ((elem.pattern.scale !== undefined ? elem.pattern.scale : 100) / 100)))}
                                      height={Math.max(2, Math.round((elem.pattern.tileHeight || 32) * ((elem.pattern.scale !== undefined ? elem.pattern.scale : 100) / 100)))}
                                      opacity={elem.pattern.opacity !== undefined ? (elem.pattern.opacity > 1 ? elem.pattern.opacity / 100 : elem.pattern.opacity) : 1}
                                    />
                                  </pattern>
                                </defs>
                              )}
                              <polygon points="50,0 61,35 98,35 68,57 79,91 50,70 21,91 32,57 2,35 39,35" fill={elem.fillType === 'pattern' && elem.pattern ? `url(#pat-back-${elem.id})` : (elem.fill || '#e6e6e6')} />
                            </svg>
                          ) : (
                            <div style={{
                              width: '100%',
                              height: '100%',
                              boxSizing: 'border-box',
                              borderRadius: elem.shapeType === 'circle' ? '50%' : elem.shapeType === 'pill' ? '999px' : `${elem.hasBorder !== false ? (elem.borderRadius !== undefined ? elem.borderRadius : 0) : 0}px`,
                              overflow: 'hidden',
                              border: (elem.hasBorder !== false && elem.hasSecondBorder && elem.borderFillType2 !== 'gradient')
                                ? `${elem.strokeWidth2 || 2}px ${elem.strokeStyle2 || 'solid'} ${elem.strokeColor2 || '#000000'}`
                                : 'none',
                              padding: (elem.hasBorder !== false && elem.hasSecondBorder)
                                ? (elem.borderFillType2 === 'gradient' ? `${elem.strokeWidth2 || 2}px` : `${elem.borderGap || 0}px`)
                                : 0,
                              background: (elem.hasBorder !== false && elem.hasSecondBorder && elem.borderFillType2 === 'gradient')
                                ? elem.strokeColor2 || 'linear-gradient(to right, red, blue)'
                                : 'transparent'
                            }}>
                              <div style={{
                                width: '100%',
                                height: '100%',
                                boxSizing: 'border-box',
                                borderRadius: 'inherit',
                                padding: (elem.hasBorder !== false && elem.hasSecondBorder && elem.borderFillType2 === 'gradient')
                                  ? `${elem.borderGap || 0}px`
                                  : 0,
                                background: (elem.hasBorder !== false && elem.hasSecondBorder && elem.borderFillType2 === 'gradient')
                                  ? (sides.back.background.value || '#ffffff')
                                  : 'transparent',
                                display: (elem.hasBorder !== false && elem.hasSecondBorder && elem.borderFillType2 === 'gradient')
                                  ? 'flex'
                                  : 'block',
                                alignItems: 'center',
                                justifyContent: 'center'
                              }}>
                                <div style={{
                                  width: '100%',
                                  height: '100%',
                                  boxSizing: 'border-box',
                                  borderRadius: 'inherit',
                                  position: 'relative',
                                  overflow: 'hidden',
                                  border: (elem.hasBorder !== false && elem.borderFillType !== 'gradient') 
                                    ? `${elem.strokeWidth || 0}px ${elem.strokeStyle || 'solid'} ${elem.strokeColor || '#cbd5e1'}` 
                                    : 'none',
                                  padding: (elem.hasBorder !== false && elem.borderFillType === 'gradient')
                                    ? `${elem.strokeWidth || 0}px`
                                    : 0,
                                  background: (elem.hasBorder !== false && elem.borderFillType === 'gradient')
                                    ? elem.strokeColor || 'linear-gradient(to right, red, blue)'
                                    : (elem.fillType === 'pattern' ? (elem.pattern?.tintColor || '#e6e6e6') : (elem.fill || '#e6e6e6'))
                                }}>
                                  {elem.hasBorder !== false && elem.borderFillType === 'gradient' && (
                                    <div style={{
                                      width: '100%',
                                      height: '100%',
                                      position: 'relative',
                                      overflow: 'hidden',
                                      background: elem.fillType === 'pattern' ? (elem.pattern?.tintColor || '#e6e6e6') : (elem.fill || '#e6e6e6'),
                                      borderRadius: 'inherit'
                                    }}>
                                      {elem.fillType === 'pattern' && elem.pattern && (
                                        <div style={{
                                          position: 'absolute',
                                          top: 0,
                                          left: 0,
                                          width: '100%',
                                          height: '100%',
                                          borderRadius: 'inherit',
                                          pointerEvents: 'none',
                                          opacity: elem.pattern.opacity !== undefined ? (elem.pattern.opacity > 1 ? elem.pattern.opacity / 100 : elem.pattern.opacity) : 1,
                                          backgroundImage: `url("${elem.pattern.image}")`,
                                          backgroundRepeat: elem.pattern.repeat || 'repeat',
                                          backgroundSize: `${Math.max(2, Math.round((elem.pattern.tileWidth || 32) * ((elem.pattern.scale !== undefined ? elem.pattern.scale : 100) / 100)))}px ${Math.max(2, Math.round((elem.pattern.tileHeight || 32) * ((elem.pattern.scale !== undefined ? elem.pattern.scale : 100) / 100)))}px`,
                                          backgroundPosition: '0 0',
                                          mixBlendMode: elem.pattern.blendMode || 'normal'
                                        }} />
                                      )}
                                    </div>
                                  )}
                                  {!(elem.hasBorder !== false && elem.borderFillType === 'gradient') && elem.fillType === 'pattern' && elem.pattern && (
                                    <div style={{
                                      position: 'absolute',
                                      top: 0,
                                      left: 0,
                                      width: '100%',
                                      height: '100%',
                                      borderRadius: 'inherit',
                                      pointerEvents: 'none',
                                      opacity: elem.pattern.opacity !== undefined ? (elem.pattern.opacity > 1 ? elem.pattern.opacity / 100 : elem.pattern.opacity) : 1,
                                      backgroundImage: `url("${elem.pattern.image}")`,
                                      backgroundRepeat: elem.pattern.repeat || 'repeat',
                                      backgroundSize: `${Math.max(2, Math.round((elem.pattern.tileWidth || 32) * ((elem.pattern.scale !== undefined ? elem.pattern.scale : 100) / 100)))}px ${Math.max(2, Math.round((elem.pattern.tileHeight || 32) * ((elem.pattern.scale !== undefined ? elem.pattern.scale : 100) / 100)))}px`,
                                      backgroundPosition: '0 0',
                                      mixBlendMode: elem.pattern.blendMode || 'normal'
                                    }} />
                                  )}
                                </div>
                              </div>
                            </div>
                          )
                        )}

                        {elem.type === 'text' && (
                          <div style={{
                            width: '100%',
                            height: '100%',
                            fontSize: `${elem.fontSize}px`,
                            fontFamily: elem.fontFamily,
                            fontWeight: elem.fontWeight,
                            fontStyle: elem.fontStyle || 'normal',
                            textDecoration: elem.textDecoration || 'none',
                            letterSpacing: `${elem.letterSpacing || 0}px`,
                            textTransform: elem.textTransform || 'none',
                            textAlign: elem.alignment || 'left',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: elem.alignment === 'center' ? 'center' : elem.alignment === 'right' ? 'flex-end' : 'flex-start',
                            lineHeight: elem.lineHeight || 1.1,
                            background: elem.colorType === 'gradient' ? (elem.color || 'linear-gradient(90deg, #ffffff 0%, #000000 100%)') : 'transparent',
                            WebkitBackgroundClip: elem.colorType === 'gradient' ? 'text' : 'unset',
                            WebkitTextFillColor: elem.colorType === 'gradient' ? 'transparent' : 'unset',
                            color: elem.colorType === 'gradient' ? 'transparent' : (elem.color || '#ffffff'),
                            transform: `translateY(${elem.baselineShift || 0}px) scaleX(${elem.scaleX !== undefined ? elem.scaleX / 100 : 1}) scaleY(${elem.scaleY !== undefined ? elem.scaleY / 100 : 1}) skewX(${elem.shear || 0}deg)`,
                            transformOrigin: elem.alignment === 'center' ? 'center center' : elem.alignment === 'right' ? 'right center' : 'left center',
                            WebkitTextStrokeWidth: `${elem.textStrokeWidth || 0}px`,
                            WebkitTextStrokeColor: elem.textStrokeColorType === 'gradient' ? (elem.textStrokeGradient?.stops?.[0]?.color || '#ffffff') : (elem.textStrokeColor || '#ffffff')
                          }}>{resolveElementText(elem)}</div>
                        )}

                        {elem.type === 'datagrid' && (
                          <div style={{
                            width: '100%',
                            height: '100%',
                            boxSizing: 'border-box',
                            borderRadius: elem.hasBorder !== false ? `${elem.borderRadius || 0}px` : 0,
                            overflow: 'hidden',
                            
                            // 1. Secondary Border styling
                            border: (elem.hasBorder !== false && elem.hasSecondBorder && elem.borderFillType2 !== 'gradient')
                              ? `${elem.strokeWidth2 || 2}px ${elem.strokeStyle2 || 'solid'} ${elem.strokeColor2 || '#000000'}`
                              : 'none',
                            
                            // 2. Gap/Border padding
                            padding: (elem.hasBorder !== false && elem.hasSecondBorder)
                              ? (elem.borderFillType2 === 'gradient' ? `${elem.strokeWidth2 || 2}px` : `${elem.borderGap || 0}px`)
                              : 0,
                            background: (elem.hasBorder !== false && elem.hasSecondBorder && elem.borderFillType2 === 'gradient')
                              ? elem.strokeColor2 || 'linear-gradient(to right, red, blue)'
                              : 'transparent'
                          }}>
                            {/* Intermediate gap wrapper */}
                            <div style={{
                              width: '100%',
                              height: '100%',
                              boxSizing: 'border-box',
                              borderRadius: 'inherit',
                              padding: (elem.hasBorder !== false && elem.hasSecondBorder && elem.borderFillType2 === 'gradient')
                                ? `${elem.borderGap || 0}px`
                                : 0,
                              background: (elem.hasBorder !== false && elem.hasSecondBorder && elem.borderFillType2 === 'gradient')
                                ? (sides.back.background.value || '#ffffff')
                                : 'transparent',
                              display: (elem.hasBorder !== false && elem.hasSecondBorder && elem.borderFillType2 === 'gradient')
                                ? 'flex'
                                : 'block',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}>
                              {/* Inner element wrapper containing primary border */}
                              <div style={{
                                width: '100%',
                                height: '100%',
                                boxSizing: 'border-box',
                                borderRadius: 'inherit',
                                overflow: 'hidden',
                                flexShrink: 0,
                                
                                // 3. Primary Border styling
                                border: (elem.hasBorder !== false && elem.borderFillType !== 'gradient')
                                  ? `${elem.strokeWidth !== undefined ? elem.strokeWidth : 1}px ${elem.strokeStyle || 'solid'} ${elem.strokeColor || '#ffffff'}`
                                  : 'none',
                                
                                // 4. Primary Border Gradient padding
                                padding: (elem.hasBorder !== false && elem.borderFillType === 'gradient')
                                  ? `${elem.strokeWidth !== undefined ? elem.strokeWidth : 1}px`
                                  : 0,
                                background: (elem.hasBorder !== false && elem.borderFillType === 'gradient')
                                  ? elem.strokeColor || 'linear-gradient(to right, red, blue)'
                                  : 'transparent',
                                display: 'flex',
                                flexDirection: 'column'
                              }}>
                                {/* Actual Table / Datagrid content */}
                                <div style={{
                                  width: '100%',
                                  height: '100%',
                                  display: 'flex',
                                  flexDirection: 'column',
                                  boxSizing: 'border-box',
                                  fontFamily: elem.fontFamily || 'Arial',
                                  fontSize: `${elem.fontSize || 11}px`,
                                  letterSpacing: `${elem.letterSpacing || 0}px`,
                                  lineHeight: elem.lineHeight || 1.1,
                                  background: elem.fillType === 'gradient'
                                    ? (elem.fill || 'linear-gradient(90deg, rgba(255,255,255,0.15) 0%, rgba(0,0,0,0.2) 100%)')
                                    : (elem.fillType === 'pattern' ? (elem.pattern?.tintColor || 'rgba(0,0,0,0.06)') : (elem.fill || 'rgba(0,0,0,0.06)')),
                                  overflow: 'hidden',
                                  borderRadius: 'inherit',
                                  position: 'relative'
                                }}>
                                  {elem.fillType === 'pattern' && elem.pattern && (
                                    <div style={{
                                      position: 'absolute',
                                      top: 0,
                                      left: 0,
                                      width: '100%',
                                      height: '100%',
                                      borderRadius: 'inherit',
                                      pointerEvents: 'none',
                                      opacity: elem.pattern.opacity !== undefined ? (elem.pattern.opacity > 1 ? elem.pattern.opacity / 100 : elem.pattern.opacity) : 1,
                                      backgroundImage: `url("${elem.pattern.image}")`,
                                      backgroundRepeat: elem.pattern.repeat || 'repeat',
                                      backgroundSize: `${Math.max(2, Math.round((elem.pattern.tileWidth || 32) * ((elem.pattern.scale !== undefined ? elem.pattern.scale : 100) / 100)))}px ${Math.max(2, Math.round((elem.pattern.tileHeight || 32) * ((elem.pattern.scale !== undefined ? elem.pattern.scale : 100) / 100)))}px`,
                                      backgroundPosition: '0 0',
                                      mixBlendMode: elem.pattern.blendMode || 'normal',
                                      zIndex: 0
                                    }} />
                                  )}
                                  {/* Header Row */}
                                  {elem.headerEnabled && (
                                    <div style={{
                                      display: 'flex',
                                      justifyContent: 'space-between',
                                      alignItems: 'center',
                                      background: elem.headerFillType === 'gradient' ? (elem.headerFill || 'linear-gradient(90deg, rgba(255,255,255,0.15) 0%, rgba(0,0,0,0.2) 100%)') : (elem.headerFill || 'rgba(255,255,255,0.1)'),
                                      color: elem.headerColor || '#ffffff',
                                      fontWeight: 'bold',
                                      paddingTop: `${elem.rowPadding !== undefined ? elem.rowPadding : 4}px`,
                                      paddingBottom: `${elem.rowPadding !== undefined ? elem.rowPadding : 4}px`,
                                      paddingLeft: `${elem.colPadding !== undefined ? elem.colPadding : 6}px`,
                                      paddingRight: `${elem.colPadding !== undefined ? elem.colPadding : 6}px`,
                                      borderBottom: (elem.gridLinesEnabled !== false)
                                        ? `${elem.gridLineThickness !== undefined ? elem.gridLineThickness : 1}px ${elem.gridLineStyle || 'solid'} ${elem.gridLineColor || 'rgba(255,255,255,0.08)'}`
                                        : 'none'
                                    }}>
                                      <span style={{
                                        width: `${elem.colWidthRatio || 50}%`,
                                        textAlign: elem.labelAlign || 'left',
                                        boxSizing: 'border-box'
                                      }}>
                                        {elem.headerLabelText || 'Attribute'}
                                      </span>
                                      <span style={{
                                        width: `${100 - (elem.colWidthRatio || 50)}%`,
                                        textAlign: elem.valueAlign || 'right',
                                        boxSizing: 'border-box'
                                      }}>
                                        {elem.headerValueText || 'Value'}
                                      </span>
                                    </div>
                                  )}

                                  {/* Data Rows */}
                                  {elem.rows.map((row, idx) => (
                                    <div key={idx} style={{
                                      display: 'flex',
                                      justifyContent: 'space-between',
                                      alignItems: 'center',
                                      backgroundColor: (elem.zebraEnabled && idx % 2 === 1) ? (elem.zebraColor || 'rgba(255,255,255,0.03)') : 'transparent',
                                      paddingTop: `${elem.rowPadding !== undefined ? elem.rowPadding : 4}px`,
                                      paddingBottom: `${elem.rowPadding !== undefined ? elem.rowPadding : 4}px`,
                                      paddingLeft: `${elem.colPadding !== undefined ? elem.colPadding : 6}px`,
                                      paddingRight: `${elem.colPadding !== undefined ? elem.colPadding : 6}px`,
                                      borderBottom: (elem.gridLinesEnabled !== false && idx < elem.rows.length - 1)
                                        ? `${elem.gridLineThickness !== undefined ? elem.gridLineThickness : 1}px ${elem.gridLineStyle || 'solid'} ${elem.gridLineColor || 'rgba(255,255,255,0.08)'}`
                                        : 'none',
                                      boxSizing: 'border-box'
                                    }}>
                                      <span style={{ 
                                        width: `${elem.colWidthRatio || 50}%`,
                                        textAlign: elem.labelAlign || 'left',
                                        fontSize: `${elem.labelFontSize || elem.fontSize || 11}px`,
                                        fontFamily: elem.labelFontFamily || elem.fontFamily || 'Arial',
                                        fontWeight: elem.labelFontWeight || elem.fontWeight || '400',
                                        fontStyle: elem.labelFontStyle || elem.fontStyle || 'normal',
                                        textDecoration: elem.labelDecoration || elem.textDecoration || 'none',
                                        letterSpacing: `${elem.labelLetterSpacing !== undefined ? elem.labelLetterSpacing : (elem.letterSpacing || 0)}px`,
                                        background: elem.labelColorType === 'gradient' ? (elem.labelColor || 'linear-gradient(90deg, #ffffff 0%, #000000 100%)') : 'transparent',
                                        WebkitBackgroundClip: elem.labelColorType === 'gradient' ? 'text' : 'unset',
                                        WebkitTextFillColor: elem.labelColorType === 'gradient' ? 'transparent' : 'unset',
                                        color: elem.labelColorType === 'gradient' ? 'transparent' : (elem.labelColor || elem.color || '#ffffff'),
                                        display: elem.labelColorType === 'gradient' ? 'inline-block' : 'block',
                                        boxSizing: 'border-box',
                                        paddingRight: '4px',
                                        whiteSpace: 'nowrap',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis'
                                      }}>{applyTextTransform(row[0], elem.labelTransformModifier)}</span>
                                      <span style={{ 
                                        width: `${100 - (elem.colWidthRatio || 50)}%`,
                                        textAlign: elem.valueAlign || 'right',
                                        fontSize: `${elem.valueFontSize || elem.fontSize || 11}px`,
                                        fontFamily: elem.valueFontFamily || elem.fontFamily || 'Arial',
                                        fontWeight: elem.valueFontWeight || (elem.fontWeight ? (parseInt(elem.fontWeight) >= 700 ? elem.fontWeight : '700') : '700'),
                                        fontStyle: elem.valueFontStyle || elem.fontStyle || 'normal',
                                        textDecoration: elem.valueDecoration || elem.textDecoration || 'none',
                                        letterSpacing: `${elem.valueLetterSpacing !== undefined ? elem.valueLetterSpacing : (elem.letterSpacing || 0)}px`,
                                        background: elem.valueColorType === 'gradient' ? (elem.valueColor || 'linear-gradient(90deg, #ffffff 0%, #000000 100%)') : 'transparent',
                                        WebkitBackgroundClip: elem.valueColorType === 'gradient' ? 'text' : 'unset',
                                        WebkitTextFillColor: elem.valueColorType === 'gradient' ? 'transparent' : 'unset',
                                        color: elem.valueColorType === 'gradient' ? 'transparent' : (elem.valueColor || elem.color || '#ffffff'),
                                        display: elem.valueColorType === 'gradient' ? 'inline-block' : 'block',
                                        boxSizing: 'border-box',
                                        paddingLeft: '4px',
                                        whiteSpace: 'nowrap',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis'
                                      }}>{applyTextTransform(resolveTemplateVariables(row[1]), elem.valueTransformModifier)}</span>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            </div>
                          </div>
                        )}
                        {elem.type === 'certify' && renderCertifyElement(elem)}

                        {elem.type === 'accent' && (
                          <div style={{
                            width: (elem.dividerType === 'vertical' || elem.orientation === 'vertical') ? `${elem.strokeWidth || 2}px` : '100%',
                            height: (elem.dividerType === 'vertical' || elem.orientation === 'vertical') ? '100%' : `${elem.strokeWidth || 2}px`,
                            background: elem.fill || elem.strokeColor || '#cbd5e1',
                            margin: (elem.dividerType === 'vertical' || elem.orientation === 'vertical') ? '0 auto' : '0'
                          }} />
                        )}

                        {elem.type === 'qrcode' && (
                          <div style={{
                            width: '100%',
                            height: '100%',
                            boxSizing: 'border-box',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            borderRadius: `${elem.borderRadius !== undefined ? elem.borderRadius : 6}px`,
                            overflow: 'hidden',
                            backgroundColor: elem.bgColor === 'transparent' ? 'transparent' : (elem.bgColor || '#ffffff'),
                            border: (elem.hasBorder && elem.strokeWidth) ? `${elem.strokeWidth}px ${elem.strokeStyle || 'solid'} ${elem.strokeColor || '#cbd5e1'}` : 'none',
                            padding: `${elem.padding || 0}px`
                          }}>
                            <QRCodeSVG
                              value={resolveQrCodeValue(elem, {}, datasets, user, orgData, tenantConfig)}
                              size={Math.min(elem.width, elem.height)}
                              fgColor={elem.fgColor || '#000000'}
                              bgColor={elem.bgColor === 'transparent' ? 'transparent' : (elem.bgColor || '#ffffff')}
                              margin={elem.margin !== undefined ? elem.margin : 2}
                              style={{ width: '100%', height: '100%', borderRadius: 'inherit' }}
                            />
                          </div>
                        )}
                      </div>
                    );
                  })}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {showSequenceModal && activeElement && activeElement.type === 'text' && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.75)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 99999,
            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
          }}>
            <div style={{
              backgroundColor: '#1b1b1d',
              border: '1px solid #3c3c42',
              borderRadius: '8px',
              width: '480px',
              padding: '16px',
              boxShadow: '0 8px 24px rgba(0,0,0,0.5)',
              display: 'flex',
              flexDirection: 'column',
              gap: '12px'
            }}>
              {/* Header */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #2d2d30', paddingBottom: '8px' }}>
                <span style={{ fontSize: '11px', fontWeight: 'bold', color: '#dfdfe3', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  Edit Text Sequence
                </span>
                <button 
                  onClick={() => setShowSequenceModal(false)}
                  style={{ backgroundColor: 'transparent', border: 'none', color: '#8e8e93', cursor: 'pointer', fontSize: '14px' }}
                >
                  ✕
                </button>
              </div>

              {/* Modal Body */}
              {(() => {
                try {
                  const activeFields = getActiveFields();
                  const dynamicFieldsList = activeElement.dynamicFields || (activeElement.text ? [{
                    id: 'df-init',
                    type: 'static',
                    value: activeElement.text
                  }] : []);

                  return (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                      {/* Result Preview Box */}
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', backgroundColor: '#141416', padding: '10px', borderRadius: '4px', border: '1px solid #2d2d30' }}>
                        <span style={{ fontSize: '10px', color: '#8e8e93', fontWeight: 'bold' }}>Sequence Preview:</span>
                        <span style={{ fontSize: '12px', color: '#dfdfe3', wordBreak: 'break-all' }}>
                          {resolveElementText(activeElement)}
                        </span>
                      </div>

                      {/* 1. Add Dynamic Field Row */}
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                        <span style={{ fontSize: '10px', color: '#8e8e93' }}>Insert Active Data Field</span>
                        <div style={{ display: 'flex', gap: '6px' }}>
                          <select
                            id="designer-dynamic-field-select"
                            style={{ flex: 1, backgroundColor: '#1b1b1d', border: '1px solid #3c3c42', borderRadius: '4px', padding: '6px', color: '#fff', fontSize: '11px', outline: 'none' }}
                          >
                            {activeFields.map(f => (
                              <option key={`${f.datasetKey}-${f.label}`} value={`${f.datasetKey}::${f.label}`}>
                                {f.alias} (${f.label})
                              </option>
                            ))}
                            {activeFields.length === 0 && (
                              <option value="">No active fields. Link datasets first.</option>
                            )}
                          </select>
                          <button
                            type="button"
                            disabled={activeFields.length === 0}
                            onClick={() => {
                              const sel = document.getElementById('designer-dynamic-field-select');
                              if (sel && sel.value) {
                                const [dsKey, fieldLabel] = sel.value.split('::');
                                const fieldObj = activeFields.find(f => f.datasetKey === dsKey && f.label === fieldLabel);
                                if (fieldObj) {
                                  const newItem = {
                                    id: 'df-' + Date.now() + '-' + Math.random().toString(36).substr(2, 4),
                                    type: 'dynamic',
                                    datasetKey: fieldObj.datasetKey,
                                    label: fieldObj.label,
                                    alias: fieldObj.alias,
                                    sample_data: fieldObj.sample_data,
                                    render: fieldObj.render || 'none'
                                  };
                                  const nextList = [...dynamicFieldsList, newItem];
                                  const newText = nextList.map(item => item.type === 'static' ? item.value : `$${item.label}`).join('');
                                  updateActiveElement({
                                    dynamicFields: nextList,
                                    text: newText
                                  });
                                }
                              }
                            }}
                            style={{
                              backgroundColor: '#1677ff',
                              color: '#fff',
                              border: 'none',
                              borderRadius: '4px',
                              padding: '6px 12px',
                              fontSize: '11px',
                              fontWeight: 'bold',
                              cursor: activeFields.length === 0 ? 'not-allowed' : 'pointer',
                              opacity: activeFields.length === 0 ? 0.5 : 1
                            }}
                          >
                            Add Field
                          </button>
                        </div>
                      </div>

                      {/* 2. Add Static Text Row */}
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                        <span style={{ fontSize: '10px', color: '#8e8e93' }}>Insert Static Text</span>
                        <div style={{ display: 'flex', gap: '6px' }}>
                          <input
                            type="text"
                            placeholder="Type static text here..."
                            value={newStaticText}
                            onChange={(e) => setNewStaticText(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                e.preventDefault();
                                if (newStaticText.trim() !== '') {
                                  const newItem = {
                                    id: 'df-' + Date.now() + '-' + Math.random().toString(36).substr(2, 4),
                                    type: 'static',
                                    value: newStaticText
                                  };
                                  const nextList = [...dynamicFieldsList, newItem];
                                  const newText = nextList.map(item => item.type === 'static' ? item.value : `$${item.label}`).join('');
                                  updateActiveElement({
                                    dynamicFields: nextList,
                                    text: newText
                                  });
                                  setNewStaticText('');
                                }
                              }
                            }}
                            style={{ flex: 1, backgroundColor: '#1b1b1d', border: '1px solid #3c3c42', borderRadius: '4px', padding: '6px', color: '#fff', fontSize: '11px', outline: 'none' }}
                          />
                          <button
                            type="button"
                            onClick={() => {
                              if (newStaticText.trim() !== '') {
                                const newItem = {
                                  id: 'df-' + Date.now() + '-' + Math.random().toString(36).substr(2, 4),
                                  type: 'static',
                                  value: newStaticText
                                };
                                const nextList = [...dynamicFieldsList, newItem];
                                const newText = nextList.map(item => item.type === 'static' ? item.value : `$${item.label}`).join('');
                                updateActiveElement({
                                  dynamicFields: nextList,
                                  text: newText
                                });
                                setNewStaticText('');
                              }
                            }}
                            style={{
                              backgroundColor: '#3c3c42',
                              color: '#fff',
                              border: 'none',
                              borderRadius: '4px',
                              padding: '6px 12px',
                              fontSize: '11px',
                              fontWeight: 'bold',
                              cursor: 'pointer'
                            }}
                          >
                            Add Static
                          </button>
                        </div>
                      </div>

                      {/* 3. Sequence Items list */}
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', borderTop: '1px solid #2d2d30', paddingTop: '10px' }}>
                        <span style={{ fontSize: '10px', color: '#8e8e93', fontWeight: 'bold' }}>Text Sequence Components</span>
                        {dynamicFieldsList.length === 0 ? (
                          <span style={{ fontSize: '11px', color: '#5e5e64', fontStyle: 'italic', textAlign: 'center', padding: '12px 0' }}>
                            No components in text sequence.
                          </span>
                        ) : (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', maxHeight: '180px', overflowY: 'auto', paddingRight: '4px' }}>
                            {dynamicFieldsList.map((item, idx) => (
                              <div key={item.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '6px', border: '1px solid #3c3c42', borderRadius: '4px', backgroundColor: '#141416' }}>
                                {item.type === 'static' ? (
                                  <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '2px', marginRight: '8px' }}>
                                    <span style={{ fontSize: '9px', color: '#a2a2a6', fontWeight: 'bold' }}>Static Text</span>
                                    <input
                                      type="text"
                                      value={item.value}
                                      onChange={(e) => {
                                        const val = e.target.value;
                                        const nextList = dynamicFieldsList.map(x => x.id === item.id ? { ...x, value: val } : x);
                                        const newText = nextList.map(x => x.type === 'static' ? x.value : `$${x.label}`).join('');
                                        updateActiveElement({
                                          dynamicFields: nextList,
                                          text: newText
                                        });
                                      }}
                                      style={{ width: '100%', backgroundColor: '#1b1b1d', border: '1px solid #3c3c42', borderRadius: '3px', padding: '4px', color: '#fff', fontSize: '11px', outline: 'none' }}
                                    />
                                  </div>
                                ) : (
                                  <div style={{ flex: 1, display: 'flex', flexDirection: 'column', marginRight: '8px' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                                      <span style={{ fontSize: '10px', color: '#dfdfe3', fontWeight: 'bold' }}>{item.alias}</span>
                                      <span style={{ fontSize: '9px', fontFamily: 'monospace', color: '#ff7875' }}>${item.label}</span>
                                    </div>
                                    <span style={{ fontSize: '9px', color: '#8e8e93', textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap', maxWidth: '240px' }}>
                                      Sample: "{item.sample_data}"{(() => { const ds = datasets[item.datasetKey]; let rId = item.render || 'none'; let fObj = null; if (ds && ds.fields) { const matched = ds.fields.find(f => f.label === item.label); if (matched) { fObj = matched; if (matched.render) rId = matched.render; } } if (!fObj && item.custom_renderer) { fObj = { custom_renderer: item.custom_renderer }; } if (rId && rId !== 'none') { return ` → "${applyRenderer(item.sample_data || '', rId, fObj)}"`; } return ''; })()}
                                    </span>
                                  </div>
                                )}

                                {/* Reorder and Delete Actions */}
                                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                  <button
                                    type="button"
                                    disabled={idx === 0}
                                    onClick={() => {
                                      if (idx === 0) return;
                                      const nextList = [...dynamicFieldsList];
                                      const temp = nextList[idx];
                                      nextList[idx] = nextList[idx - 1];
                                      nextList[idx - 1] = temp;
                                      const newText = nextList.map(x => x.type === 'static' ? x.value : `$${x.label}`).join('');
                                      updateActiveElement({
                                        dynamicFields: nextList,
                                        text: newText
                                      });
                                    }}
                                    style={{ background: 'none', border: 'none', color: idx === 0 ? '#3c3c42' : '#8e8e93', cursor: idx === 0 ? 'default' : 'pointer', fontSize: '9px', padding: '2px' }}
                                  >
                                    ▲
                                  </button>
                                  <button
                                    type="button"
                                    disabled={idx === dynamicFieldsList.length - 1}
                                    onClick={() => {
                                      if (idx === dynamicFieldsList.length - 1) return;
                                      const nextList = [...dynamicFieldsList];
                                      const temp = nextList[idx];
                                      nextList[idx] = nextList[idx + 1];
                                      nextList[idx + 1] = temp;
                                      const newText = nextList.map(x => x.type === 'static' ? x.value : `$${x.label}`).join('');
                                      updateActiveElement({
                                        dynamicFields: nextList,
                                        text: newText
                                      });
                                    }}
                                    style={{ background: 'none', border: 'none', color: idx === dynamicFieldsList.length - 1 ? '#3c3c42' : '#8e8e93', cursor: idx === dynamicFieldsList.length - 1 ? 'default' : 'pointer', fontSize: '9px', padding: '2px' }}
                                  >
                                    ▼
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => {
                                      const nextList = dynamicFieldsList.filter(x => x.id !== item.id);
                                      const newText = nextList.map(x => x.type === 'static' ? x.value : `$${x.label}`).join('');
                                      updateActiveElement({
                                        dynamicFields: nextList,
                                        text: newText
                                      });
                                    }}
                                    style={{ background: 'none', border: 'none', color: '#ff4d4f', cursor: 'pointer', fontSize: '10px', padding: '2px', marginLeft: '4px' }}
                                  >
                                    🗑️
                                  </button>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                } catch (e) {
                  console.error("Modal Render Error:", e);
                  return (
                    <div style={{ color: '#ff4d4f', padding: '16px', fontSize: '12px' }}>
                      Render Error: {e.message}
                    </div>
                  );
                }
              })()}

              {/* Footer */}
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', borderTop: '1px solid #2d2d30', paddingTop: '10px', marginTop: '4px' }}>
                <button
                  type="button"
                  onClick={() => setShowSequenceModal(false)}
                  style={{
                    padding: '6px 12px',
                    fontSize: '11px',
                    backgroundColor: '#1677ff',
                    color: '#ffffff',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontWeight: 'bold'
                  }}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
        {renderGridDataModal()}
        {renderAssetSelectorModal()}
        {renderImageAdjustModal()}
        {renderExitConfirmModal()}
        {renderShareModal()}
        {renderImportModal()}
        {renderPushModal()}
        {renderUnissuedSyncModal()}
        {renderPrintExportModal()}
      </div>,
      document.body
    )}
    {showGradientModal && createPortal(
          <div style={{
            position: 'fixed',
            top: `${gradientWindowPos.y}px`,
            left: `${gradientWindowPos.x}px`,
            width: '420px',
            backgroundColor: '#1b1b1d',
            border: '1px solid #3c3c42',
            borderRadius: '8px',
            padding: '16px',
            boxShadow: '0 8px 24px rgba(0,0,0,0.5)',
            display: 'flex',
            flexDirection: 'column',
            gap: '12px',
            zIndex: 1000001,
            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
          }}>
            {/* Header (Draggable) */}
            <div 
              onMouseDown={handleGradientWindowDragStart}
              style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center', 
                borderBottom: '1px solid #2d2d30', 
                paddingBottom: '8px',
                cursor: 'grab',
                userSelect: 'none'
              }}
            >
              <span style={{ fontSize: '11px', fontWeight: 'bold', color: '#dfdfe3', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Gradient Editor</span>
              <button 
                onClick={() => setShowGradientModal(false)}
                onMouseDown={(e) => e.stopPropagation()}
                style={{ backgroundColor: 'transparent', border: 'none', color: '#8e8e93', cursor: 'pointer', fontSize: '14px' }}
              >
                ✕
              </button>
            </div>

            {/* Gradient Preview Line container */}
            <div style={{ padding: '0 8px' }}>
              {/* Horizontal preview bar */}
              <div style={{ position: 'relative', margin: '20px 0 30px 0' }}>
                {/* The gradient strip itself */}
                <div 
                  ref={barRef}
                  onClick={(e) => {
                    if (e.target !== e.currentTarget) return;
                    const rect = e.currentTarget.getBoundingClientRect();
                    const x = e.clientX - rect.left;
                    const percent = Math.max(0, Math.min(100, Math.round((x / rect.width) * 100)));
                    
                    const defaultColor = activeStop ? activeStop.color : '#ffffff';
                    const defaultOpacity = activeStop && activeStop.opacity !== undefined ? activeStop.opacity : 1;
                    const newStop = { color: defaultColor, position: percent, opacity: defaultOpacity };
                    
                    setGradientState(prev => {
                      const newStops = [...prev.stops, newStop];
                      newStops.sort((a, b) => a.position - b.position);
                      const idx = newStops.indexOf(newStop);
                      setSelectedStopIndex(idx >= 0 ? idx : newStops.length - 1);
                      return { ...prev, stops: newStops };
                    });
                  }}
                  style={{
                    width: '100%',
                    height: '34px',
                    borderRadius: '6px',
                    border: '1px solid #3c3c42',
                    background: generateGradientString(gradientState),
                    cursor: 'crosshair',
                    position: 'relative'
                  }}
                />
                
                {/* The center line showing handles */}
                <div style={{
                  position: 'absolute',
                  left: 0,
                  right: 0,
                  top: '17px',
                  height: '1px',
                  backgroundColor: 'rgba(255, 255, 255, 0.4)',
                  pointerEvents: 'none'
                }} />
                
                {/* Render the handle circles */}
                {gradientState.stops.map((stop, index) => {
                  const isSelected = index === selectedStopIndex;
                  return (
                    <div
                      key={index}
                      onMouseDown={(e) => {
                        e.stopPropagation();
                        setSelectedStopIndex(index);
                        const startX = e.clientX;
                        const startPos = stop.position;
                        
                        const handleMouseMove = (moveEvent) => {
                          if (!barRef.current) return;
                          const rect = barRef.current.getBoundingClientRect();
                          const deltaX = moveEvent.clientX - startX;
                          const deltaPercent = Math.round((deltaX / rect.width) * 100);
                          const newPercent = Math.max(0, Math.min(100, startPos + deltaPercent));
                          
                          setGradientState(prev => {
                            const newStops = [...prev.stops];
                            newStops[index] = { ...newStops[index], position: newPercent };
                            return { ...prev, stops: newStops };
                          });
                        };
                        
                        const handleMouseUp = () => {
                          window.removeEventListener('mousemove', handleMouseMove);
                          window.removeEventListener('mouseup', handleMouseUp);
                        };
                        
                        window.addEventListener('mousemove', handleMouseMove);
                        window.addEventListener('mouseup', handleMouseUp);
                      }}
                      style={{
                        position: 'absolute',
                        left: `calc(${stop.position}% - 8px)`,
                        top: '9px',
                        width: '16px',
                        height: '16px',
                        borderRadius: '50%',
                        backgroundColor: stop.color,
                        border: isSelected ? '2px solid #1677ff' : '2px solid #ffffff',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.5)',
                        cursor: 'ew-resize',
                        zIndex: isSelected ? 10 : 2
                      }}
                    />
                  );
                })}
              </div>
            </div>

            {/* Parameters Grid */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', fontSize: '11px', color: '#8e8e93' }}>
              {/* Type Selector */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                <span>Type</span>
                <select
                  value={gradientState.type}
                  onChange={(e) => setGradientState({ ...gradientState, type: e.target.value })}
                  style={{ backgroundColor: '#232326', border: '1px solid #3c3c42', borderRadius: '4px', padding: '5px', color: '#fff', fontSize: '11px', outline: 'none' }}
                >
                  <option value="linear">Linear</option>
                  <option value="radial">Radial</option>
                </select>
              </div>

              {/* Angle Input (only if Linear) */}
              {gradientState.type === 'linear' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span>Angle</span>
                    <span style={{ color: '#fff' }}>{gradientState.angle || 90}°</span>
                  </div>
                  <input
                    type="range"
                    min="0"
                    max="360"
                    value={gradientState.angle || 90}
                    onChange={(e) => setGradientState({ ...gradientState, angle: parseInt(e.target.value) || 0 })}
                    style={{ accentColor: '#1677ff', height: '4px', cursor: 'pointer', marginTop: '6px' }}
                  />
                </div>
              )}
            </div>

            {/* Selected Stop Parameters block */}
            {activeStop && (
              <div style={{ borderTop: '1px solid #2d2d30', paddingTop: '10px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <span style={{ fontSize: '10px', fontWeight: 'bold', color: '#8e8e93', textTransform: 'uppercase' }}>Selected Stop #{selectedStopIndex + 1}</span>
                
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', fontSize: '11px', color: '#8e8e93' }}>
                  {/* Position */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span>Position</span>
                      <span style={{ color: '#fff' }}>{activeStop.position}%</span>
                    </div>
                    <input
                      type="range"
                      min="0"
                      max="100"
                      value={activeStop.position}
                      onChange={(e) => {
                        const val = parseInt(e.target.value) || 0;
                        setGradientState(prev => {
                          const newStops = [...prev.stops];
                          newStops[selectedStopIndex] = { ...newStops[selectedStopIndex], position: val };
                          return { ...prev, stops: newStops };
                        });
                      }}
                      style={{ accentColor: '#1677ff', height: '4px', cursor: 'pointer', marginTop: '6px' }}
                    />
                  </div>

                  {/* Opacity */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span>Opacity</span>
                      <span style={{ color: '#fff' }}>{Math.round((activeStop.opacity !== undefined ? activeStop.opacity : 1) * 100)}%</span>
                    </div>
                    <input
                      type="range"
                      min="0"
                      max="100"
                      value={Math.round((activeStop.opacity !== undefined ? activeStop.opacity : 1) * 100)}
                      onChange={(e) => {
                        const val = parseFloat(e.target.value) / 100;
                        setGradientState(prev => {
                          const newStops = [...prev.stops];
                          newStops[selectedStopIndex] = { ...newStops[selectedStopIndex], opacity: val };
                          return { ...prev, stops: newStops };
                        });
                      }}
                      style={{ accentColor: '#1677ff', height: '4px', cursor: 'pointer', marginTop: '6px' }}
                    />
                  </div>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '11px', color: '#8e8e93', marginTop: '4px' }}>
                  {/* Color Swatch / Input */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', flex: 1 }}>
                    <span>Colour</span>
                    <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                      <input
                        type="text"
                        value={activeStop.color}
                        onChange={(e) => {
                          const val = e.target.value;
                          setGradientState(prev => {
                            const newStops = [...prev.stops];
                            newStops[selectedStopIndex] = { ...newStops[selectedStopIndex], color: val };
                            return { ...prev, stops: newStops };
                          });
                        }}
                        style={{ flex: 1, backgroundColor: '#232326', border: '1px solid #3c3c42', borderRadius: '4px', padding: '4px 6px', color: '#fff', fontSize: '11px', outline: 'none' }}
                      />
                      <StableColorInput
                        value={activeStop.color?.startsWith('#') ? activeStop.color : '#ffffff'}
                        onChange={(e) => {
                          const val = e.target.value;
                          setGradientState(prev => {
                            const newStops = [...prev.stops];
                            newStops[selectedStopIndex] = { ...newStops[selectedStopIndex], color: val };
                            return { ...prev, stops: newStops };
                          });
                        }}
                        style={{ width: '28px', height: '24px', border: 'none', padding: 0, cursor: 'pointer', backgroundColor: 'transparent' }}
                      />
                      {typeof window !== 'undefined' && 'EyeDropper' in window ? (
                        <button
                          type="button"
                          onClick={async () => {
                            try {
                              const eyeDropper = new window.EyeDropper();
                              const result = await eyeDropper.open();
                              const val = result.sRGBHex;
                              setGradientState(prev => {
                                const newStops = [...prev.stops];
                                newStops[selectedStopIndex] = { ...newStops[selectedStopIndex], color: val };
                                return { ...prev, stops: newStops };
                              });
                            } catch (e) {
                              console.log("EyeDropper closed or failed:", e);
                            }
                          }}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: '24px',
                            height: '24px',
                            backgroundColor: '#232326',
                            border: '1px solid #3c3c42',
                            borderRadius: '4px',
                            color: '#dfdfe3',
                            cursor: 'pointer',
                            padding: 0
                          }}
                          onMouseOver={(e) => { e.currentTarget.style.backgroundColor = '#2d2d31'; }}
                          onMouseOut={(e) => { e.currentTarget.style.backgroundColor = '#232326'; }}
                          title="Sample color from screen"
                        >
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M21 3L11 13" />
                            <path d="M19 5l-2.5-2.5a1.5 1.5 0 0 0-2.12 0l-3 3a1.5 1.5 0 0 0 0 2.12L13.88 10" />
                            <path d="M11 11H8.5a1.5 1.5 0 0 0-1.5 1.5v1.79c0 .4-.16.78-.44 1.06L3.5 18.5a1 1 0 0 0 0 1.41l.59.59a1 1 0 0 0 1.41 0l3.15-3.06c.28-.28.66-.44 1.06-.44H11.5A1.5 1.5 0 0 0 13 15.5V13" />
                          </svg>
                        </button>
                      ) : (
                        <button
                          type="button"
                          disabled
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: '24px',
                            height: '24px',
                            backgroundColor: '#232326',
                            border: '1px solid #1b1b1d',
                            borderRadius: '4px',
                            color: '#555',
                            cursor: 'not-allowed',
                            padding: 0
                          }}
                          title="EyeDropper requires a secure context (HTTPS or localhost)"
                        >
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" opacity="0.3">
                            <path d="M21 3L11 13" />
                            <path d="M19 5l-2.5-2.5a1.5 1.5 0 0 0-2.12 0l-3 3a1.5 1.5 0 0 0 0 2.12L13.88 10" />
                            <path d="M11 11H8.5a1.5 1.5 0 0 0-1.5 1.5v1.79c0 .4-.16.78-.44 1.06L3.5 18.5a1 1 0 0 0 0 1.41l.59.59a1 1 0 0 0 1.41 0l3.15-3.06c.28-.28.66-.44 1.06-.44H11.5A1.5 1.5 0 0 0 13 15.5V13" />
                          </svg>
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Actions Button Row */}
            <div style={{ borderTop: '1px solid #2d2d30', paddingTop: '10px', display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
              <button
                onClick={handleInsertStop}
                style={{
                  flex: 1,
                  padding: '6px 0',
                  fontSize: '10px',
                  fontWeight: 'bold',
                  border: '1px solid #3c3c42',
                  borderRadius: '4px',
                  backgroundColor: '#232326',
                  color: '#ffffff',
                  cursor: 'pointer'
                }}
              >
                Insert Stop
              </button>
              <button
                onClick={handleCopyStop}
                style={{
                  flex: 1,
                  padding: '6px 0',
                  fontSize: '10px',
                  fontWeight: 'bold',
                  border: '1px solid #3c3c42',
                  borderRadius: '4px',
                  backgroundColor: '#232326',
                  color: '#ffffff',
                  cursor: 'pointer'
                }}
              >
                Copy
              </button>
              <button
                onClick={handleDeleteStop}
                disabled={gradientState.stops.length <= 2}
                style={{
                  flex: 1,
                  padding: '6px 0',
                  fontSize: '10px',
                  fontWeight: 'bold',
                  border: '1px solid #3c3c42',
                  borderRadius: '4px',
                  backgroundColor: gradientState.stops.length <= 2 ? '#1b1b1d' : '#232326',
                  color: gradientState.stops.length <= 2 ? '#55555c' : '#ffffff',
                  cursor: gradientState.stops.length <= 2 ? 'default' : 'pointer'
                }}
              >
                Delete
              </button>
              <button
                onClick={handleReverseGradient}
                style={{
                  flex: 1,
                  padding: '6px 0',
                  fontSize: '10px',
                  fontWeight: 'bold',
                  border: '1px solid #3c3c42',
                  borderRadius: '4px',
                  backgroundColor: '#232326',
                  color: '#ffffff',
                  cursor: 'pointer'
                }}
              >
                Reverse
              </button>
            </div>

            {/* Presets Grid */}
            <div style={{ borderTop: '1px solid #2d2d30', paddingTop: '10px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: '10px', fontWeight: 'bold', color: '#8e8e93', textTransform: 'uppercase' }}>Presets</span>
                <button
                  type="button"
                  onClick={() => {
                    const currentStr = JSON.stringify(gradientState);
                    if (!gradientPresets.some(p => JSON.stringify(p) === currentStr)) {
                      setGradientPresets([...gradientPresets, { ...gradientState }]);
                    }
                  }}
                  style={{
                    backgroundColor: 'transparent',
                    border: 'none',
                    color: '#1677ff',
                    cursor: 'pointer',
                    fontSize: '10px',
                    fontWeight: 'bold',
                    padding: '2px 4px',
                    borderRadius: '3px'
                  }}
                  onMouseOver={(e) => { e.currentTarget.style.backgroundColor = 'rgba(22, 119, 255, 0.1)'; }}
                  onMouseOut={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                >
                  + Save Current
                </button>
              </div>
              
              <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', maxHeight: '70px', overflowY: 'auto', padding: '2px' }}>
                {gradientPresets.map((preset, idx) => {
                  const gradStr = generateGradientString(preset);
                  return (
                    <div style={{ position: 'relative' }} key={idx}>
                      <div
                        onClick={() => {
                          setGradientState({ ...preset });
                          setSelectedStopIndex(0);
                        }}
                        style={{
                          width: '28px',
                          height: '28px',
                          borderRadius: '4px',
                          border: '1px solid #3c3c42',
                          background: gradStr,
                          cursor: 'pointer',
                          boxShadow: '0 1px 3px rgba(0,0,0,0.3)'
                        }}
                        title="Apply preset to editor"
                      />
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setGradientPresets(gradientPresets.filter((_, i) => i !== idx));
                        }}
                        style={{
                          position: 'absolute',
                          top: '-4px',
                          right: '-4px',
                          width: '12px',
                          height: '12px',
                          borderRadius: '50%',
                          backgroundColor: '#3c3c42',
                          border: 'none',
                          color: '#dfdfe3',
                          fontSize: '8px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          cursor: 'pointer',
                          padding: 0,
                          boxShadow: '0 1px 2px rgba(0,0,0,0.5)'
                        }}
                        title="Delete Preset"
                      >
                        ✕
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Footer controls */}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', borderTop: '1px solid #2d2d30', paddingTop: '10px', marginTop: '4px' }}>
              <button
                onClick={() => setShowGradientModal(false)}
                style={{
                  padding: '6px 12px',
                  fontSize: '11px',
                  backgroundColor: '#232326',
                  color: '#dfdfe3',
                  border: '1px solid #3c3c42',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleApplyGradient}
                style={{
                  padding: '6px 12px',
                  fontSize: '11px',
                  backgroundColor: '#1677ff',
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                OK
              </button>
            </div>
          </div>,
          document.body
        )}
        {showPatternModal && createPortal(
          <div style={{
            position: 'fixed',
            top: `${patternWindowPos.y}px`,
            left: `${patternWindowPos.x}px`,
            width: '460px',
            maxHeight: '88vh',
            overflowY: 'auto',
            background: 'var(--card-bg, #1b1b1d)',
            border: '1px solid var(--border-color, #3c3c42)',
            borderRadius: '8px',
            padding: '16px',
            boxShadow: '0 12px 36px rgba(0,0,0,0.65)',
            display: 'flex',
            flexDirection: 'column',
            gap: '12px',
            zIndex: 1000002,
            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
          }}>
            {/* Header (Draggable) */}
            <div
              onMouseDown={handlePatternWindowDragStart}
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                borderBottom: '1px solid var(--border-color, #2d2d30)',
                paddingBottom: '8px',
                cursor: 'grab',
                userSelect: 'none'
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ fontSize: '13px' }}>▦</span>
                <span style={{ fontSize: '11px', fontWeight: 'bold', color: 'var(--text-main, #dfdfe3)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  Pattern & Texture Chooser
                </span>
                <span style={{ fontSize: '10px', color: '#8e8e93', backgroundColor: '#232326', padding: '2px 6px', borderRadius: '4px' }}>
                  {editingPatternTarget === 'cardBackground' ? 'Card Background' : 'Shape / Panel Fill'}
                </span>
              </div>
              <button
                type="button"
                onClick={() => setShowPatternModal(false)}
                onMouseDown={(e) => e.stopPropagation()}
                style={{ backgroundColor: 'transparent', border: 'none', color: '#8e8e93', cursor: 'pointer', fontSize: '14px', padding: '2px 6px' }}
              >
                ✕
              </button>
            </div>

            {/* Live Preview Box */}
            <div style={{
              position: 'relative',
              height: '76px',
              borderRadius: '6px',
              border: '1px solid #3c3c42',
              overflow: 'hidden',
              background: 'conic-gradient(#252528 25%, #18181b 0 50%, #252528 0 75%, #18181b 0) 0 0 / 14px 14px'
            }}>
              <div style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                backgroundColor: patternState.tintColor || 'transparent'
              }}>
                <div style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  backgroundImage: `url("${patternState.image}")`,
                  backgroundRepeat: patternState.repeat || 'repeat',
                  backgroundSize: `${Math.max(2, Math.round((patternState.tileWidth || 32) * ((patternState.scale !== undefined ? patternState.scale : 100) / 100)))}px ${Math.max(2, Math.round((patternState.tileHeight || 32) * ((patternState.scale !== undefined ? patternState.scale : 100) / 100)))}px`,
                  backgroundPosition: '0 0',
                  opacity: patternState.opacity !== undefined ? (patternState.opacity > 1 ? patternState.opacity / 100 : patternState.opacity) : 1,
                  mixBlendMode: patternState.blendMode || 'normal'
                }} />
              </div>
              <div style={{
                position: 'absolute',
                bottom: '6px',
                right: '8px',
                backgroundColor: 'rgba(0, 0, 0, 0.75)',
                backdropFilter: 'blur(4px)',
                color: '#dfdfe3',
                fontSize: '10px',
                padding: '3px 8px',
                borderRadius: '4px',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                display: 'flex',
                gap: '8px',
                alignItems: 'center'
              }}>
                <span style={{ fontWeight: 'bold' }}>{patternState.name || 'Pattern'}</span>
                <span style={{ color: '#8e8e93' }}>
                  {Math.round((patternState.tileWidth || 32) * ((patternState.scale !== undefined ? patternState.scale : 100) / 100))}×{Math.round((patternState.tileHeight || 32) * ((patternState.scale !== undefined ? patternState.scale : 100) / 100))}px ({patternState.scale !== undefined ? patternState.scale : 100}%)
                </span>
              </div>
            </div>

            {/* Category Filter Pills & Upload Button */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
              <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap', flex: 1 }}>
                {['All', 'Carbon & Mesh', 'Geometric', 'Tech & Metal', 'Organic & Surface', 'Custom'].map(cat => {
                  const isActive = patternCategoryFilter === cat;
                  return (
                    <button
                      key={cat}
                      type="button"
                      onClick={() => setPatternCategoryFilter(cat)}
                      style={{
                        padding: '3px 8px',
                        fontSize: '10px',
                        fontWeight: isActive ? 'bold' : 'normal',
                        backgroundColor: isActive ? '#1677ff' : '#232326',
                        color: isActive ? '#ffffff' : '#a2a2a6',
                        border: `1px solid ${isActive ? '#1677ff' : '#3c3c42'}`,
                        borderRadius: '12px',
                        cursor: 'pointer',
                        transition: 'all 0.15s ease'
                      }}
                    >
                      {cat}
                    </button>
                  );
                })}
              </div>
              <input
                type="file"
                ref={patternFileInputRef}
                accept="image/png,image/jpeg,image/webp,image/svg+xml"
                onChange={handlePatternFileUpload}
                style={{ display: 'none' }}
              />
              <button
                type="button"
                onClick={() => patternFileInputRef.current?.click()}
                style={{
                  padding: '3px 9px',
                  fontSize: '10px',
                  fontWeight: 'bold',
                  backgroundColor: '#2b2b30',
                  color: '#40a9ff',
                  border: '1px dashed #40a9ff',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  whiteSpace: 'nowrap'
                }}
              >
                <span>+</span> Upload Tile
              </button>
            </div>

            {/* Swatch Grid */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(4, 1fr)',
              gap: '8px',
              maxHeight: '190px',
              overflowY: 'auto',
              padding: '2px',
              backgroundColor: '#141416',
              borderRadius: '6px',
              border: '1px solid #2d2d30'
            }}>
              {(() => {
                const allPatterns = [...DEFAULT_CARD_PATTERNS, ...customPatterns];
                const displayed = patternCategoryFilter === 'All'
                  ? allPatterns
                  : patternCategoryFilter === 'Custom'
                    ? customPatterns
                    : allPatterns.filter(p => p.category === patternCategoryFilter);

                if (displayed.length === 0) {
                  return (
                    <div style={{ gridColumn: 'span 4', textAlign: 'center', padding: '24px 8px', color: '#8e8e93', fontSize: '11px' }}>
                      {patternCategoryFilter === 'Custom'
                        ? 'No custom pattern tiles uploaded yet. Click "+ Upload Tile" to add seamless images.'
                        : 'No patterns found in this category.'}
                    </div>
                  );
                }

                return displayed.map(pat => {
                  const isSelected = patternState.id === pat.id;
                  return (
                    <div
                      key={pat.id}
                      onClick={() => {
                        setPatternState(prev => ({
                          ...prev,
                          id: pat.id,
                          name: pat.name,
                          image: pat.image,
                          tileWidth: pat.tileWidth || 32,
                          tileHeight: pat.tileHeight || 32,
                          ...(pat.defaultScale && prev.scale === 100 ? { scale: pat.defaultScale } : {}),
                          ...(pat.defaultTintColor && (prev.tintColor === 'transparent' || prev.tintColor === '#18181c') ? { tintColor: pat.defaultTintColor } : {})
                        }));
                      }}
                      style={{
                        position: 'relative',
                        display: 'flex',
                        flexDirection: 'column',
                        borderRadius: '6px',
                        overflow: 'hidden',
                        border: isSelected ? '2px solid #1677ff' : '1px solid #3c3c42',
                        backgroundColor: '#1b1b1d',
                        cursor: 'pointer',
                        transition: 'transform 0.1s ease, border-color 0.15s ease'
                      }}
                    >
                      {/* Swatch preview */}
                      <div style={{
                        position: 'relative',
                        height: '52px',
                        background: 'conic-gradient(#252528 25%, #18181b 0 50%, #252528 0 75%, #18181b 0) 0 0 / 10px 10px',
                        overflow: 'hidden'
                      }}>
                        <div style={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          width: '100%',
                          height: '100%',
                          backgroundImage: `url("${pat.image}")`,
                          backgroundRepeat: 'repeat',
                          backgroundSize: `${pat.tileWidth || 32}px ${pat.tileHeight || 32}px`,
                          backgroundPosition: '0 0'
                        }} />
                        {pat.isCustom && (
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              deleteCustomPattern(pat.id);
                              setCustomPatterns(getStoredCustomPatterns());
                              if (patternState.id === pat.id) {
                                const fallback = DEFAULT_CARD_PATTERNS[0];
                                setPatternState(prev => ({
                                  ...prev,
                                  id: fallback.id,
                                  name: fallback.name,
                                  image: fallback.image,
                                  tileWidth: fallback.tileWidth,
                                  tileHeight: fallback.tileHeight
                                }));
                              }
                            }}
                            title="Delete custom pattern"
                            style={{
                              position: 'absolute',
                              top: '2px',
                              right: '2px',
                              width: '16px',
                              height: '16px',
                              borderRadius: '50%',
                              backgroundColor: 'rgba(255, 77, 79, 0.85)',
                              color: '#ffffff',
                              border: 'none',
                              fontSize: '9px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              cursor: 'pointer',
                              padding: 0
                            }}
                          >
                            ✕
                          </button>
                        )}
                      </div>
                      {/* Swatch label */}
                      <div style={{
                        padding: '4px',
                        fontSize: '9.5px',
                        color: isSelected ? '#40a9ff' : '#dfdfe3',
                        fontWeight: isSelected ? 'bold' : 'normal',
                        textAlign: 'center',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis'
                      }}>
                        {pat.name}
                      </div>
                    </div>
                  );
                });
              })()}
            </div>

            {/* Adjustments: Scale, Opacity, Tint, Repeat, Blend */}
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '10px',
              borderTop: '1px solid #2d2d30',
              paddingTop: '10px'
            }}>
              {/* Scale Control */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontSize: '10px', fontWeight: 'bold', color: '#8e8e93', textTransform: 'uppercase' }}>
                    Tile Scale: {patternState.scale !== undefined ? patternState.scale : 100}%
                  </span>
                  <div style={{ display: 'flex', gap: '4px' }}>
                    {[50, 100, 150, 200].map(sVal => (
                      <button
                        key={sVal}
                        type="button"
                        onClick={() => setPatternState(prev => ({ ...prev, scale: sVal }))}
                        style={{
                          backgroundColor: patternState.scale === sVal ? '#1677ff' : '#232326',
                          color: patternState.scale === sVal ? '#ffffff' : '#8e8e93',
                          border: '1px solid #3c3c42',
                          borderRadius: '3px',
                          padding: '1px 5px',
                          fontSize: '9px',
                          cursor: 'pointer'
                        }}
                      >
                        {sVal}%
                      </button>
                    ))}
                    <button
                      type="button"
                      onClick={() => setPatternState(prev => ({ ...prev, scale: 100 }))}
                      style={{
                        backgroundColor: '#232326',
                        color: '#8e8e93',
                        border: '1px solid #3c3c42',
                        borderRadius: '3px',
                        padding: '1px 5px',
                        fontSize: '9px',
                        cursor: 'pointer'
                      }}
                    >
                      Reset
                    </button>
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <input
                    type="range"
                    min="10"
                    max="400"
                    step="5"
                    value={patternState.scale !== undefined ? patternState.scale : 100}
                    onChange={(e) => setPatternState(prev => ({ ...prev, scale: parseInt(e.target.value) || 100 }))}
                    style={{ flex: 1, accentColor: '#1677ff' }}
                  />
                  <input
                    type="number"
                    min="10"
                    max="400"
                    value={patternState.scale !== undefined ? patternState.scale : 100}
                    onChange={(e) => setPatternState(prev => ({ ...prev, scale: parseInt(e.target.value) || 100 }))}
                    style={{
                      width: '48px',
                      backgroundColor: '#141416',
                      border: '1px solid #3c3c42',
                      borderRadius: '4px',
                      color: '#ffffff',
                      fontSize: '11px',
                      padding: '2px 4px',
                      textAlign: 'center'
                    }}
                  />
                </div>
              </div>

              {/* Opacity Control */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontSize: '10px', fontWeight: 'bold', color: '#8e8e93', textTransform: 'uppercase' }}>
                    Pattern Opacity: {patternState.opacity !== undefined ? patternState.opacity : 100}%
                  </span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <input
                    type="range"
                    min="0"
                    max="100"
                    step="1"
                    value={patternState.opacity !== undefined ? patternState.opacity : 100}
                    onChange={(e) => setPatternState(prev => ({ ...prev, opacity: parseInt(e.target.value) || 0 }))}
                    style={{ flex: 1, accentColor: '#1677ff' }}
                  />
                  <input
                    type="number"
                    min="0"
                    max="100"
                    value={patternState.opacity !== undefined ? patternState.opacity : 100}
                    onChange={(e) => setPatternState(prev => ({ ...prev, opacity: parseInt(e.target.value) || 0 }))}
                    style={{
                      width: '48px',
                      backgroundColor: '#141416',
                      border: '1px solid #3c3c42',
                      borderRadius: '4px',
                      color: '#ffffff',
                      fontSize: '11px',
                      padding: '2px 4px',
                      textAlign: 'center'
                    }}
                  />
                </div>
              </div>

              {/* Tint Color Control */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                <span style={{ fontSize: '10px', fontWeight: 'bold', color: '#8e8e93', textTransform: 'uppercase' }}>
                  Base / Tint Color
                </span>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <StableColorInput
                    value={patternState.tintColor && patternState.tintColor !== 'transparent' ? patternState.tintColor : '#ffffff'}
                    onChange={(e) => setPatternState(prev => ({ ...prev, tintColor: e.target.value }))}
                    style={{
                      width: '32px',
                      height: '28px',
                      padding: '0',
                      border: '1px solid #3c3c42',
                      borderRadius: '4px',
                      backgroundColor: 'transparent',
                      cursor: 'pointer'
                    }}
                  />
                  <input
                    type="text"
                    value={patternState.tintColor || 'transparent'}
                    onChange={(e) => setPatternState(prev => ({ ...prev, tintColor: e.target.value }))}
                    placeholder="transparent / #hex"
                    style={{
                      flex: 1,
                      backgroundColor: '#141416',
                      border: '1px solid #3c3c42',
                      borderRadius: '4px',
                      color: '#ffffff',
                      fontSize: '11px',
                      padding: '5px 8px'
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => setPatternState(prev => ({ ...prev, tintColor: 'transparent' }))}
                    style={{
                      padding: '5px 10px',
                      fontSize: '10px',
                      backgroundColor: patternState.tintColor === 'transparent' ? '#1677ff' : '#232326',
                      color: patternState.tintColor === 'transparent' ? '#ffffff' : '#8e8e93',
                      border: '1px solid #3c3c42',
                      borderRadius: '4px',
                      cursor: 'pointer'
                    }}
                  >
                    Transparent
                  </button>
                  {typeof window !== 'undefined' && 'EyeDropper' in window && (
                    <button
                      type="button"
                      onClick={async () => {
                        try {
                          const eyeDropper = new window.EyeDropper();
                          const result = await eyeDropper.open();
                          if (result && result.sRGBHex) {
                            setPatternState(prev => ({ ...prev, tintColor: result.sRGBHex }));
                          }
                        } catch (err) {
                          // EyeDropper cancelled or dismissed
                        }
                      }}
                      title="Sample color from screen"
                      style={{
                        padding: '5px 8px',
                        fontSize: '11px',
                        backgroundColor: '#232326',
                        color: '#dfdfe3',
                        border: '1px solid #3c3c42',
                        borderRadius: '4px',
                        cursor: 'pointer'
                      }}
                    >
                      👁️
                    </button>
                  )}
                </div>
              </div>

              {/* Repeat & Blend Mode Rows */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  <span style={{ fontSize: '10px', fontWeight: 'bold', color: '#8e8e93', textTransform: 'uppercase' }}>
                    Repeat
                  </span>
                  <select
                    value={patternState.repeat || 'repeat'}
                    onChange={(e) => setPatternState(prev => ({ ...prev, repeat: e.target.value }))}
                    style={{
                      backgroundColor: '#141416',
                      border: '1px solid #3c3c42',
                      borderRadius: '4px',
                      color: '#ffffff',
                      fontSize: '11px',
                      padding: '5px 8px',
                      outline: 'none'
                    }}
                  >
                    <option value="repeat">Tile Both (X & Y)</option>
                    <option value="repeat-x">Tile Horizontally (X)</option>
                    <option value="repeat-y">Tile Vertically (Y)</option>
                    <option value="no-repeat">Single Tile (No Repeat)</option>
                  </select>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  <span style={{ fontSize: '10px', fontWeight: 'bold', color: '#8e8e93', textTransform: 'uppercase' }}>
                    Blend Mode
                  </span>
                  <select
                    value={patternState.blendMode || 'normal'}
                    onChange={(e) => setPatternState(prev => ({ ...prev, blendMode: e.target.value }))}
                    style={{
                      backgroundColor: '#141416',
                      border: '1px solid #3c3c42',
                      borderRadius: '4px',
                      color: '#ffffff',
                      fontSize: '11px',
                      padding: '5px 8px',
                      outline: 'none'
                    }}
                  >
                    <option value="normal">Normal</option>
                    <option value="multiply">Multiply</option>
                    <option value="screen">Screen</option>
                    <option value="overlay">Overlay</option>
                    <option value="darken">Darken</option>
                    <option value="lighten">Lighten</option>
                    <option value="color-dodge">Color Dodge</option>
                    <option value="color-burn">Color Burn</option>
                    <option value="hard-light">Hard Light</option>
                    <option value="soft-light">Soft Light</option>
                    <option value="difference">Difference</option>
                    <option value="exclusion">Exclusion</option>
                    <option value="hue">Hue</option>
                    <option value="saturation">Saturation</option>
                    <option value="color">Color</option>
                    <option value="luminosity">Luminosity</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Footer buttons */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              borderTop: '1px solid var(--border-color, #2d2d30)',
              paddingTop: '12px',
              marginTop: '4px'
            }}>
              <button
                type="button"
                onClick={handleClearPattern}
                style={{
                  padding: '6px 12px',
                  fontSize: '11px',
                  background: 'var(--panel-bg, #232326)',
                  color: '#ff4d4f',
                  border: '1px solid rgba(255, 77, 79, 0.4)',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                Clear Pattern
              </button>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button
                  type="button"
                  onClick={() => setShowPatternModal(false)}
                  style={{
                    padding: '6px 14px',
                    fontSize: '11px',
                    background: 'var(--panel-bg, #232326)',
                    color: 'var(--text-main, #dfdfe3)',
                    border: '1px solid var(--border-color, #3c3c42)',
                    borderRadius: '4px',
                    cursor: 'pointer'
                  }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleApplyPattern}
                  style={{
                    padding: '6px 16px',
                    fontSize: '11px',
                    fontWeight: 'bold',
                    background: 'var(--primary-gradient, var(--primary-color, #1677ff))',
                    color: '#ffffff',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer'
                  }}
                >
                  Apply Pattern
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}
      </>
    );
  }

  // ----------------------------------------------------
  // NORMAL RENDER: INLINE MANAGER VIEW (Integrated into Tabs)
  // ----------------------------------------------------
  return (
    <div style={{ color: 'var(--text-main)' }}>
      <style>{`
        @keyframes shimmerFoilLoop {
          0% { background-position: 0% 0%; }
          50% { background-position: 100% 100%; }
          100% { background-position: 0% 0%; }
        }
        @keyframes iridescentRainbowLoop {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
        @keyframes silverGlintLoop {
          0% { background-position: -200% 0%; }
          30% { background-position: 200% 0%; }
          100% { background-position: 200% 0%; }
        }
        @keyframes ambientWarmPulseLoop {
          0% { opacity: 0.3; }
          50% { opacity: 0.75; }
          100% { opacity: 0.3; }
        }
        @keyframes goldFlareLoop {
          0% { background-position: -200% 0%; }
          40% { background-position: 200% 0%; }
          100% { background-position: 200% 0%; }
        }
        @keyframes glassReflectionLoop {
          0% { background-position: 0% 0%; }
          100% { background-position: 200% 200%; }
        }
        @keyframes cosmicAuroraLoop {
          0% { transform: scale(1) translate(0, 0); opacity: 0.4; }
          50% { transform: scale(1.15) translate(-4%, -4%); opacity: 0.8; }
          100% { transform: scale(1) translate(0, 0); opacity: 0.4; }
        }
      `}</style>
      {/* Toolbar Panel */}
      <div style={{
        display: 'flex',
        gap: '8px',
        alignItems: 'center',
        backgroundColor: 'transparent',
        borderBottom: '1px solid var(--border-color)',
        padding: '12px 16px'
      }}>
          <button
            onClick={handleCreateNewTemplate}
            data-testid="create-new-template-btn"
            style={{
              backgroundColor: '#ffffff',
              color: '#18181b',
              border: '1px solid #cbd5e1',
              borderRadius: '6px',
              padding: '8px 16px',
              fontSize: '12px',
              fontWeight: '600',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ display: 'block' }}>
              <line x1="12" y1="5" x2="12" y2="19"></line>
              <line x1="5" y1="12" x2="19" y2="12"></line>
            </svg>
            <span>New</span>
          </button>

          <div style={{ width: '1px', height: '24px', backgroundColor: 'var(--border-color)', margin: '0 8px' }} />

          <button
            disabled={!selectedGridTemplateId}
            onClick={() => {
              const selectedT = templates.find(t => t.id === selectedGridTemplateId);
              if (selectedT) handleEditTemplate(selectedT);
            }}
            title="Edit Template"
            style={{
              background: selectedGridTemplateId ? '#e4e4e7' : 'var(--disabled-bg, #000000)',
              color: selectedGridTemplateId ? '#18181b' : 'var(--disabled-text, #5f5f65)',
              border: '1px solid ' + (selectedGridTemplateId ? '#cbd5e1' : 'var(--border-color)'),
              borderRadius: '6px',
              padding: '8px 10px',
              fontSize: '12px',
              fontWeight: '600',
              cursor: selectedGridTemplateId ? 'pointer' : 'not-allowed',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              transition: 'all 0.2s',
              opacity: selectedGridTemplateId ? 1 : 0.85
            }}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ display: 'block' }}>
              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
              <path d="M18.5 2.5a2.121 2.121 0 1 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
            </svg>
          </button>

          <button
            disabled={!selectedGridTemplateId}
            onClick={(e) => {
              const selectedT = templates.find(t => t.id === selectedGridTemplateId);
              if (selectedT) handleDuplicateTemplate(selectedT, e);
            }}
            title="Duplicate Template"
            style={{
              background: selectedGridTemplateId ? '#e4e4e7' : 'var(--disabled-bg, #000000)',
              color: selectedGridTemplateId ? '#18181b' : 'var(--disabled-text, #5f5f65)',
              border: '1px solid ' + (selectedGridTemplateId ? '#cbd5e1' : 'var(--border-color)'),
              borderRadius: '6px',
              padding: '8px 10px',
              fontSize: '12px',
              fontWeight: '600',
              cursor: selectedGridTemplateId ? 'pointer' : 'not-allowed',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              transition: 'all 0.2s',
              opacity: selectedGridTemplateId ? 1 : 0.85
            }}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ display: 'block' }}>
              <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
              <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
            </svg>
          </button>

          <button
            disabled={!selectedGridTemplateId}
            onClick={(e) => {
              if (selectedGridTemplateId) {
                handleDeleteTemplate(selectedGridTemplateId, e);
                setSelectedGridTemplateId(null);
              }
            }}
            title="Delete Template"
            style={{
              background: selectedGridTemplateId ? '#e4e4e7' : 'var(--disabled-bg, #000000)',
              color: selectedGridTemplateId ? '#18181b' : 'var(--disabled-text, #5f5f65)',
              border: '1px solid ' + (selectedGridTemplateId ? '#cbd5e1' : 'var(--border-color)'),
              borderRadius: '6px',
              padding: '8px 10px',
              fontSize: '12px',
              fontWeight: '600',
              cursor: selectedGridTemplateId ? 'pointer' : 'not-allowed',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              transition: 'all 0.2s',
              opacity: selectedGridTemplateId ? 1 : 0.85
            }}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ display: 'block' }}>
              <polyline points="3 6 5 6 21 6"></polyline>
              <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
            </svg>
          </button>

          <button
            disabled={!selectedGridTemplateId}
            onClick={() => {
              if (selectedGridTemplateId) {
                toggleFlipTemplate(selectedGridTemplateId);
              }
            }}
            title="Flip Preview"
            style={{
              background: selectedGridTemplateId ? '#e4e4e7' : 'var(--disabled-bg, #000000)',
              color: selectedGridTemplateId ? '#18181b' : 'var(--disabled-text, #5f5f65)',
              border: '1px solid ' + (selectedGridTemplateId ? '#cbd5e1' : 'var(--border-color)'),
              borderRadius: '6px',
              padding: '8px 10px',
              fontSize: '12px',
              fontWeight: '600',
              cursor: selectedGridTemplateId ? 'pointer' : 'not-allowed',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              transition: 'all 0.2s',
              opacity: selectedGridTemplateId ? 1 : 0.85
            }}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ display: 'block' }}>
              <polyline points="23 4 23 10 17 10"></polyline>
              <polyline points="1 20 1 14 7 14"></polyline>
              <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"></path>
            </svg>
          </button>

          <button
            disabled={!selectedGridTemplateId}
            onClick={() => {
              if (selectedGridTemplateId) {
                const selectedT = templates.find(t => t.id === selectedGridTemplateId);
                if (selectedT) handleOpenShareModal(selectedT);
              }
            }}
            title="Share Template Access"
            style={{
              background: selectedGridTemplateId ? '#e4e4e7' : 'var(--disabled-bg, #000000)',
              color: selectedGridTemplateId ? '#18181b' : 'var(--disabled-text, #5f5f65)',
              border: '1px solid ' + (selectedGridTemplateId ? '#cbd5e1' : 'var(--border-color)'),
              borderRadius: '6px',
              padding: '8px 10px',
              fontSize: '12px',
              fontWeight: '600',
              cursor: selectedGridTemplateId ? 'pointer' : 'not-allowed',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              transition: 'all 0.2s',
              opacity: selectedGridTemplateId ? 1 : 0.85
            }}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{ display: 'block' }}>
              <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
              <circle cx="9" cy="7" r="4" />
              <line x1="19" y1="8" x2="19" y2="14" />
              <line x1="16" y1="11" x2="22" y2="11" />
            </svg>
          </button>

          {isAdmin && (
            <button
              disabled={!selectedGridTemplateId}
              onClick={handleToggleSystemTemplate}
              title={
                selectedGridTemplateId && templates.find(t => t.id === selectedGridTemplateId)?.config?.is_system_template
                  ? "Remove System Template Designation"
                  : "Designate as System Card Template (for Purchased Package Automation)"
              }
              style={{
                background: selectedGridTemplateId
                  ? (templates.find(t => t.id === selectedGridTemplateId)?.config?.is_system_template ? '#eab308' : '#e4e4e7')
                  : 'var(--disabled-bg, #000000)',
                color: selectedGridTemplateId
                  ? (templates.find(t => t.id === selectedGridTemplateId)?.config?.is_system_template ? '#000000' : '#18181b')
                  : 'var(--disabled-text, #5f5f65)',
                border: '1px solid ' + (selectedGridTemplateId ? '#cbd5e1' : 'var(--border-color)'),
                borderRadius: '6px',
                padding: '8px 10px',
                fontSize: '12px',
                fontWeight: '700',
                cursor: selectedGridTemplateId ? 'pointer' : 'not-allowed',
                display: 'flex',
                alignItems: 'center',
                gap: '5px',
                transition: 'all 0.2s',
                opacity: selectedGridTemplateId ? 1 : 0.85
              }}
            >
              <span>⭐</span>
              <span>
                {selectedGridTemplateId && templates.find(t => t.id === selectedGridTemplateId)?.config?.is_system_template
                  ? 'System'
                  : 'Tag System'}
              </span>
            </button>
          )}

          {canExportImport && (
            <div style={{ position: 'relative' }}>
              <button
                type="button"
                onClick={() => setShowCardActionsDropdown(!showCardActionsDropdown)}
                data-testid="card-actions-dropdown-btn"
                title="Card Template Actions (Export, Import, Push to Prod)"
                style={{
                  backgroundColor: '#ffffff',
                  color: '#18181b',
                  border: '1px solid #cbd5e1',
                  borderRadius: '6px',
                  padding: '8px 12px',
                  fontSize: '12px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  transition: 'all 0.2s'
                }}
              >
                <span style={{ fontSize: '13px' }}>⚡</span>
                <span>Actions</span>
                <span style={{ fontSize: '9px', color: '#6b7280' }}>▾</span>
              </button>

              <div
                data-testid="card-actions-dropdown-menu"
                style={{
                  display: showCardActionsDropdown ? 'flex' : 'none',
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  marginTop: '4px',
                  backgroundColor: '#1b1b1d',
                  border: '1px solid #3c3c42',
                  borderRadius: '6px',
                  padding: '4px',
                  zIndex: 100,
                  flexDirection: 'column',
                  minWidth: '160px',
                  boxShadow: '0 8px 24px rgba(0,0,0,0.5)',
                  gap: '2px'
                }}
              >
                <button
                  disabled={!selectedGridTemplateId || isPrintExporting}
                  onClick={(e) => {
                    if (e && e.stopPropagation) e.stopPropagation();
                    setShowCardActionsDropdown(false);
                    handleOpenPrintModalForGrid(selectedGridTemplateId);
                  }}
                  title="Export High-Res Print PDF for Epson EcoTank L8160 (Book Mode & Normal Mode)"
                  data-testid="grid-export-print-btn"
                  style={{
                    width: '100%',
                    backgroundColor: 'transparent',
                    color: selectedGridTemplateId ? '#38bdf8' : '#6b7280',
                    border: 'none',
                    borderRadius: '4px',
                    padding: '8px 10px',
                    fontSize: '12px',
                    fontWeight: '600',
                    cursor: selectedGridTemplateId ? 'pointer' : 'not-allowed',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    textAlign: 'left',
                    transition: 'background-color 0.15s'
                  }}
                  onMouseEnter={(e) => {
                    if (selectedGridTemplateId) e.currentTarget.style.backgroundColor = '#2d2d30';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }}
                >
                  <span style={{ fontSize: '14px' }}>🖨️</span>
                  <span>Export Print (PDF)</span>
                </button>

                <button
                  disabled={!selectedGridTemplateId || isExporting}
                  onClick={() => {
                    setShowCardActionsDropdown(false);
                    handleExportCard(selectedGridTemplateId);
                  }}
                  title="Export Trophy Card with Geometry & Assets"
                  data-testid="export-card-btn"
                  style={{
                    width: '100%',
                    backgroundColor: 'transparent',
                    color: selectedGridTemplateId && !isExporting ? '#dfdfe3' : '#6b7280',
                    border: 'none',
                    borderRadius: '4px',
                    padding: '8px 10px',
                    fontSize: '12px',
                    fontWeight: '500',
                    cursor: selectedGridTemplateId && !isExporting ? 'pointer' : 'not-allowed',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    textAlign: 'left',
                    transition: 'background-color 0.15s'
                  }}
                  onMouseEnter={(e) => {
                    if (selectedGridTemplateId && !isExporting) e.currentTarget.style.backgroundColor = '#2d2d30';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }}
                >
                  <span style={{ fontSize: '14px' }}>📤</span>
                  <span>{isExporting ? 'Exporting...' : 'Export'}</span>
                </button>

                <button
                  onClick={() => {
                    setShowCardActionsDropdown(false);
                    handleOpenImportModal();
                  }}
                  title="Import Trophy Card JSON & Assets into a Tenant Card Library"
                  data-testid="import-card-btn"
                  style={{
                    width: '100%',
                    backgroundColor: 'transparent',
                    color: '#dfdfe3',
                    border: 'none',
                    borderRadius: '4px',
                    padding: '8px 10px',
                    fontSize: '12px',
                    fontWeight: '500',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    textAlign: 'left',
                    transition: 'background-color 0.15s'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#2d2d30';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }}
                >
                  <span style={{ fontSize: '14px' }}>📥</span>
                  <span>Import Card</span>
                </button>

                <div style={{ height: '1px', backgroundColor: '#2d2d30', margin: '3px 0' }} />

                <button
                  disabled={!selectedGridTemplateId}
                  onClick={() => {
                    setShowCardActionsDropdown(false);
                    handleOpenPushModal(selectedGridTemplateId);
                  }}
                  title="Push selected card directly to Production / Remote server"
                  data-testid="push-card-btn"
                  style={{
                    width: '100%',
                    backgroundColor: 'transparent',
                    color: selectedGridTemplateId ? '#60a5fa' : '#6b7280',
                    border: 'none',
                    borderRadius: '4px',
                    padding: '8px 10px',
                    fontSize: '12px',
                    fontWeight: '500',
                    cursor: selectedGridTemplateId ? 'pointer' : 'not-allowed',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    textAlign: 'left',
                    transition: 'background-color 0.15s'
                  }}
                  onMouseEnter={(e) => {
                    if (selectedGridTemplateId) e.currentTarget.style.backgroundColor = '#2d2d30';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }}
                >
                  <span style={{ fontSize: '14px' }}>🚀</span>
                  <span>Push to Prod</span>
                </button>
              </div>

              {showCardActionsDropdown && (
                <div
                  onClick={() => setShowCardActionsDropdown(false)}
                  style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 99 }}
                />
              )}
            </div>
          )}

          <button
            type="button"
            onClick={handleToggleTemplateUsageInfo}
            data-testid="template-info-toggle-btn"
            title={showTemplateUsageInfo ? "Hide Template Usage Details" : "Show Template Usage Details (Used & Unallocated Counts)"}
            style={{
              backgroundColor: showTemplateUsageInfo ? '#2563eb' : '#ffffff',
              color: showTemplateUsageInfo ? '#ffffff' : '#18181b',
              border: '1px solid ' + (showTemplateUsageInfo ? '#1d4ed8' : '#cbd5e1'),
              borderRadius: '6px',
              padding: '8px 10px',
              fontSize: '12px',
              fontWeight: '700',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '4px',
              transition: 'all 0.2s',
              boxShadow: showTemplateUsageInfo ? '0 0 0 2px rgba(37,99,235,0.25)' : 'none'
            }}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{ display: 'block' }}>
              <circle cx="12" cy="12" r="10"></circle>
              <line x1="12" y1="16" x2="12" y2="12"></line>
              <line x1="12" y1="8" x2="12.01" y2="8"></line>
            </svg>
          </button>

          {/* Zoom Slider Control (working like opacity slider in layers tab) */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px', position: 'relative', marginLeft: 'auto', marginRight: '16px' }}>
            <span style={{ fontSize: '11px', color: 'var(--text-mute)' }}>Zoom:</span>
            <button
              type="button"
              onClick={() => setShowGridZoomSlider(!showGridZoomSlider)}
              data-testid="grid-zoom-toggle-btn"
              title="Adjust Grid Zoom"
              style={{
                backgroundColor: '#ffffff',
                border: '1px solid #cbd5e1',
                borderRadius: '6px',
                color: '#18181b',
                fontSize: '11px',
                fontWeight: '600',
                padding: '4px 8px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '3px',
                transition: 'all 0.15s'
              }}
            >
              <span>{gridZoom}%</span>
              <span style={{ fontSize: '9px', color: '#6b7280' }}>▾</span>
            </button>

            {showGridZoomSlider && (
              <>
                <div
                  onClick={() => setShowGridZoomSlider(false)}
                  style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 99 }}
                />
                <div style={{
                  position: 'absolute',
                  top: '32px',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  backgroundColor: '#1b1b1d',
                  border: '1px solid #3c3c42',
                  borderRadius: '6px',
                  padding: '8px 8px',
                  zIndex: 100,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  boxShadow: '0 4px 16px rgba(0,0,0,0.5)',
                  gap: '6px'
                }}>
                  <input
                    type="range"
                    min="60"
                    max="140"
                    value={gridZoom}
                    onChange={(e) => setGridZoom(Number(e.target.value))}
                    data-testid="grid-zoom-slider"
                    style={{
                      height: '80px',
                      width: '20px',
                      WebkitAppearance: 'slider-vertical',
                      cursor: 'pointer',
                      outline: 'none',
                      accentColor: '#3b82f6'
                    }}
                  />
                  <input
                    type="number"
                    min="60"
                    max="140"
                    value={gridZoom}
                    onChange={(e) => {
                      let val = parseInt(e.target.value);
                      if (isNaN(val)) val = 60;
                      if (val < 60) val = 60;
                      if (val > 140) val = 140;
                      setGridZoom(val);
                    }}
                    data-testid="grid-zoom-number-input"
                    style={{
                      width: '38px',
                      backgroundColor: '#222225',
                      border: '1px solid #2d2d30',
                      borderRadius: '3px',
                      color: '#fff',
                      fontSize: '10px',
                      padding: '2px 3px',
                      textAlign: 'center',
                      outline: 'none'
                    }}
                  />
                </div>
              </>
            )}
          </div>

          {/* Template Category Filter */}
          <select
            value={templateFilter}
            onChange={(e) => setTemplateFilter(e.target.value)}
            style={{
              backgroundColor: '#ffffff',
              color: '#18181b',
              border: '1px solid #cbd5e1',
              borderRadius: '6px',
              padding: '7px 10px',
              fontSize: '12px',
              fontWeight: '500',
              outline: 'none',
              cursor: 'pointer',
              marginRight: '8px'
            }}
          >
            <option value="all">All Templates</option>
            <option value="system">⭐ System Templates</option>
          </select>

          {/* Search Input Filter */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', position: 'relative' }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ color: '#4b5563', position: 'absolute', left: '10px' }}>
              <circle cx="11" cy="11" r="8"></circle>
              <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
            </svg>
            <input
              type="text"
              placeholder="Search templates..."
              value={templateSearchQuery}
              onChange={(e) => setTemplateSearchQuery(e.target.value)}
              style={{
                backgroundColor: '#ffffff',
                color: '#18181b',
                border: '1px solid #cbd5e1',
                borderRadius: '6px',
                padding: '8px 12px 8px 30px',
                fontSize: '12px',
                outline: 'none',
                width: '200px',
                transition: 'border-color 0.15s'
              }}
            />
          </div>
        </div>

        <div style={{ padding: '12px' }}>
          {loading ? (
          <div style={{ padding: '40px', textAlign: 'center', color: 'var(--text-mute)' }}>Loading templates...</div>
        ) : (
          <div style={{
            display: 'grid',
            gridTemplateColumns: `repeat(auto-fill, minmax(${Math.round(220 * (gridZoom / 100))}px, 1fr))`,
            gap: '10px',
            marginTop: '4px'
          }}>
            {/* Templates Listing */}
            {templates
              .filter(t => {
                if (templateFilter === 'system') {
                  return Boolean(t.config?.is_system_template);
                }
                return true;
              })
              .filter(t => t.name.toLowerCase().includes(templateSearchQuery.toLowerCase()))
              .map(t => {
                const isSelected = selectedGridTemplateId === t.id;
                const isFlipped = flippedTemplateIds.includes(t.id);
                const cfg = t.config || {};
                const isSharedAll = cfg.shared_all !== false;
                const sharedEmails = Array.isArray(cfg.shared_user_emails) ? cfg.shared_user_emails : [];
                const sharedCompanies = Array.isArray(cfg.shared_companies) ? cfg.shared_companies : (Array.isArray(cfg.shared_company_ids) ? cfg.shared_company_ids : []);
                const showSharedBadge = isSharedAll || sharedEmails.length > 0 || sharedCompanies.length > 0;
                return (
                  <div
                    key={t.id}
                    data-testid={`card-template-grid-item-${t.id}`}
                    onClick={() => setSelectedGridTemplateId(isSelected ? null : t.id)}
                    onDoubleClick={() => handleEditTemplate(t)}
                    style={{
                      height: `${Math.round(280 * (gridZoom / 100))}px`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                      position: 'relative'
                    }}
                  >
                    {/* 3D Perspective Card Wrapper */}
                    <div style={{
                      position: 'relative',
                      height: '90%',
                      aspectRatio: '5 / 7',
                      perspective: '1000px',
                      WebkitPerspective: '1000px',
                      zIndex: isSelected ? 10 : 1
                    }}>
                      {/* Inner Rotating/Scaling Card Sheet */}
                      <div style={{
                        position: 'relative',
                        width: '100%',
                        height: '100%',
                        transformStyle: 'preserve-3d',
                        WebkitTransformStyle: 'preserve-3d',
                        transition: 'transform 0.6s cubic-bezier(0.4, 0, 0.2, 1), box-shadow 0.2s',
                        transform: `scale(${isSelected ? 1.1 : 1}) rotateY(${isFlipped ? 180 : 0}deg)`,
                        WebkitTransform: `scale(${isSelected ? 1.1 : 1}) rotateY(${isFlipped ? 180 : 0}deg)`,
                        boxShadow: isSelected 
                          ? '0 0 0 2px #1677ff' 
                          : 'none',
                        borderRadius: `${Math.round(12 * (gridZoom / 100))}px`
                      }}>
                        {/* FRONT FACE */}
                        <div style={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          width: '100%',
                          height: '100%',
                          backfaceVisibility: 'hidden',
                          WebkitBackfaceVisibility: 'hidden',
                          transform: 'rotateY(0deg) translateZ(1px)',
                          WebkitTransform: 'rotateY(0deg) translateZ(1px)',
                          transformStyle: 'flat',
                          WebkitTransformStyle: 'flat',
                          zIndex: isFlipped ? 1 : 2,
                          pointerEvents: isFlipped ? 'none' : 'auto',
                          borderRadius: 'inherit',
                          overflow: 'hidden',
                          display: 'flex',
                          flexDirection: 'column',
                          backgroundColor: 'transparent'
                        }}>
                          {/* Top Badges Container */}
                          <div style={{
                            position: 'absolute',
                            top: `${Math.round(8 * (gridZoom / 100))}px`,
                            right: `${Math.round(8 * (gridZoom / 100))}px`,
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px',
                            zIndex: 5
                          }}>
                            {Boolean(t.config?.is_system_template) && (
                              <div style={{
                                backgroundColor: '#eab308',
                                color: '#000000',
                                fontSize: `${Math.max(8, Math.round(10 * (gridZoom / 100)))}px`,
                                fontWeight: '800',
                                padding: '2px 6px',
                                borderRadius: '4px',
                                boxShadow: '0 2px 5px rgba(0,0,0,0.3)',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '3px',
                                letterSpacing: '0.5px'
                              }}>
                                <span>⭐ SYSTEM</span>
                              </div>
                            )}

                            {showSharedBadge && (
                              <div
                                data-testid={`card-template-shared-badge-${t.id}`}
                                title={
                                  isSharedAll
                                    ? 'Shared with all tenant users'
                                    : sharedCompanies.length > 0
                                      ? `Shared with ${sharedCompanies.length} company/companies${sharedCompanies.map(c => c.name).filter(Boolean).length > 0 ? ` (${sharedCompanies.map(c => c.name || c).join(', ')})` : ''}${sharedEmails.length > 0 ? ` & ${sharedEmails.length} user(s)` : ''}`
                                      : `Shared with ${sharedEmails.length} user(s)`
                                }
                                style={{
                                  backgroundColor: 'rgba(24, 24, 27, 0.88)',
                                  color: isSharedAll ? '#38bdf8' : sharedCompanies.length > 0 ? '#60a5fa' : '#38bdf8',
                                  border: '1px solid rgba(255, 255, 255, 0.16)',
                                  width: `${Math.max(18, Math.round(20 * (gridZoom / 100)))}px`,
                                  height: `${Math.max(18, Math.round(20 * (gridZoom / 100)))}px`,
                                  borderRadius: '4px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  boxShadow: '0 2px 5px rgba(0,0,0,0.3)',
                                  backdropFilter: 'blur(4px)'
                                }}
                              >
                                {isSharedAll ? (
                                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.3" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
                                    <circle cx="9" cy="7" r="4" />
                                    <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
                                    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                                  </svg>
                                ) : sharedCompanies.length > 0 ? (
                                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.3" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M3 21h18" />
                                    <path d="M5 21V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2v16" />
                                    <line x1="9" y1="8" x2="10" y2="8" />
                                    <line x1="9" y1="12" x2="10" y2="12" />
                                    <line x1="14" y1="8" x2="15" y2="8" />
                                    <line x1="14" y1="12" x2="15" y2="12" />
                                  </svg>
                                ) : (
                                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.3" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                                    <circle cx="12" cy="7" r="4" />
                                  </svg>
                                )}
                              </div>
                            )}
                          </div>
                          {t.config?.preview_image ? (
                            <img
                              src={t.config.preview_image}
                              alt={t.name}
                              style={{
                                width: '85%',
                                height: '85%',
                                objectFit: 'contain',
                                margin: 'auto',
                                pointerEvents: 'none',
                                borderRadius: '6px',
                                boxShadow: isSelected
                                  ? '0 12px 28px rgba(0, 0, 0, 0.45)'
                                  : '0 4px 12px rgba(0, 0, 0, 0.25)'
                              }}
                            />
                          ) : (
                            <div style={{
                              width: '100%',
                              height: '100%',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              color: 'var(--text-mute)',
                              fontSize: `${Math.max(9, Math.round(11 * (gridZoom / 100)))}px`,
                              fontStyle: 'italic',
                              border: '1px dashed var(--border-color)',
                              borderRadius: `${Math.round(10 * (gridZoom / 100))}px`,
                              backgroundColor: 'rgba(255,255,255,0.02)'
                            }}>
                              No Preview
                            </div>
                          )}

                          {/* Bottom Translucent Title Overlay */}
                          <div style={{
                            position: 'absolute',
                            bottom: 0,
                            left: 0,
                            right: 0,
                            backgroundColor: 'rgba(255, 255, 255, 0.3)',
                            backdropFilter: 'blur(6px)',
                            WebkitBackdropFilter: 'blur(6px)',
                            borderTop: '1px solid rgba(0, 0, 0, 0.1)',
                            padding: `${Math.round(8 * (gridZoom / 100))}px ${Math.round(12 * (gridZoom / 100))}px`,
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center',
                            zIndex: 2
                          }}>
                            <h3 style={{ margin: 0, fontSize: `${Math.max(10, Math.round(12 * (gridZoom / 100)))}px`, fontWeight: '700', color: '#121212', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', textAlign: 'center' }}>
                              {t.name}
                            </h3>
                            {showTemplateUsageInfo && (
                              <div
                                data-testid={`template-usage-info-${t.id}`}
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  gap: `${Math.max(4, Math.round(6 * (gridZoom / 100)))}px`,
                                  marginTop: `${Math.max(3, Math.round(4 * (gridZoom / 100)))}px`,
                                  flexWrap: 'wrap'
                                }}
                              >
                                <span
                                  title="Times used (issued coins)"
                                  data-testid={`template-used-count-${t.id}`}
                                  style={{
                                    backgroundColor: '#059669',
                                    color: '#ffffff',
                                    fontSize: `${Math.max(8, Math.round(10 * (gridZoom / 100)))}px`,
                                    fontWeight: '700',
                                    padding: `${Math.max(1, Math.round(2 * (gridZoom / 100)))}px ${Math.max(4, Math.round(6 * (gridZoom / 100)))}px`,
                                    borderRadius: '4px',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '3px',
                                    boxShadow: '0 1px 3px rgba(0,0,0,0.25)',
                                    lineHeight: 1.2
                                  }}
                                >
                                  <span>Used:</span>
                                  <span>{loadingUsageStats && !templateUsageStats[t.id] ? '...' : (templateUsageStats[t.id]?.used_count ?? 0)}</span>
                                </span>
                                <span
                                  title="Unallocated instances remaining"
                                  data-testid={`template-unallocated-count-${t.id}`}
                                  style={{
                                    backgroundColor: '#2563eb',
                                    color: '#ffffff',
                                    fontSize: `${Math.max(8, Math.round(10 * (gridZoom / 100)))}px`,
                                    fontWeight: '700',
                                    padding: `${Math.max(1, Math.round(2 * (gridZoom / 100)))}px ${Math.max(4, Math.round(6 * (gridZoom / 100)))}px`,
                                    borderRadius: '4px',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '3px',
                                    boxShadow: '0 1px 3px rgba(0,0,0,0.25)',
                                    lineHeight: 1.2
                                  }}
                                >
                                  <span>Remaining:</span>
                                  <span>{loadingUsageStats && !templateUsageStats[t.id] ? '...' : (templateUsageStats[t.id]?.unallocated_count ?? 0)}</span>
                                </span>
                              </div>
                            )}
                          </div>
                        </div>

                        {/* BACK FACE */}
                        <div style={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          width: '100%',
                          height: '100%',
                          backfaceVisibility: 'hidden',
                          WebkitBackfaceVisibility: 'hidden',
                          transform: 'rotateY(180deg) translateZ(1px)',
                          WebkitTransform: 'rotateY(180deg) translateZ(1px)',
                          transformStyle: 'flat',
                          WebkitTransformStyle: 'flat',
                          zIndex: isFlipped ? 2 : 1,
                          pointerEvents: isFlipped ? 'auto' : 'none',
                          borderRadius: 'inherit',
                          overflow: 'hidden',
                          display: 'flex',
                          flexDirection: 'column',
                          backgroundColor: 'transparent'
                        }}>
                          {/* Top Badges Container */}
                          <div style={{
                            position: 'absolute',
                            top: `${Math.round(8 * (gridZoom / 100))}px`,
                            right: `${Math.round(8 * (gridZoom / 100))}px`,
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px',
                            zIndex: 5
                          }}>
                            {Boolean(t.config?.is_system_template) && (
                              <div style={{
                                backgroundColor: '#eab308',
                                color: '#000000',
                                fontSize: `${Math.max(8, Math.round(10 * (gridZoom / 100)))}px`,
                                fontWeight: '800',
                                padding: '2px 6px',
                                borderRadius: '4px',
                                boxShadow: '0 2px 5px rgba(0,0,0,0.3)',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '3px',
                                letterSpacing: '0.5px'
                              }}>
                                <span>⭐ SYSTEM</span>
                              </div>
                            )}

                            {showSharedBadge && (
                              <div
                                data-testid={`card-template-shared-badge-back-${t.id}`}
                                title={
                                  isSharedAll
                                    ? 'Shared with all tenant users'
                                    : sharedCompanies.length > 0
                                      ? `Shared with ${sharedCompanies.length} company/companies${sharedCompanies.map(c => c.name).filter(Boolean).length > 0 ? ` (${sharedCompanies.map(c => c.name || c).join(', ')})` : ''}${sharedEmails.length > 0 ? ` & ${sharedEmails.length} user(s)` : ''}`
                                      : `Shared with ${sharedEmails.length} user(s)`
                                }
                                style={{
                                  backgroundColor: 'rgba(24, 24, 27, 0.88)',
                                  color: isSharedAll ? '#38bdf8' : sharedCompanies.length > 0 ? '#60a5fa' : '#38bdf8',
                                  border: '1px solid rgba(255, 255, 255, 0.16)',
                                  width: `${Math.max(18, Math.round(20 * (gridZoom / 100)))}px`,
                                  height: `${Math.max(18, Math.round(20 * (gridZoom / 100)))}px`,
                                  borderRadius: '4px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  boxShadow: '0 2px 5px rgba(0,0,0,0.3)',
                                  backdropFilter: 'blur(4px)'
                                }}
                              >
                                {isSharedAll ? (
                                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.3" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
                                    <circle cx="9" cy="7" r="4" />
                                    <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
                                    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                                  </svg>
                                ) : sharedCompanies.length > 0 ? (
                                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.3" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M3 21h18" />
                                    <path d="M5 21V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2v16" />
                                    <line x1="9" y1="8" x2="10" y2="8" />
                                    <line x1="9" y1="12" x2="10" y2="12" />
                                    <line x1="14" y1="8" x2="15" y2="8" />
                                    <line x1="14" y1="12" x2="15" y2="12" />
                                  </svg>
                                ) : (
                                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.3" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                                    <circle cx="12" cy="7" r="4" />
                                  </svg>
                                )}
                              </div>
                            )}
                          </div>
                          {t.config?.preview_image_back ? (
                            <img
                              src={t.config.preview_image_back}
                              alt={`${t.name} Back`}
                              style={{
                                width: '85%',
                                height: '85%',
                                objectFit: 'contain',
                                margin: 'auto',
                                pointerEvents: 'none',
                                borderRadius: '6px',
                                boxShadow: isSelected
                                  ? '0 12px 28px rgba(0, 0, 0, 0.45)'
                                  : '0 4px 12px rgba(0, 0, 0, 0.25)'
                              }}
                            />
                          ) : (
                            <div style={{
                              width: '100%',
                              height: '100%',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              color: 'var(--text-mute)',
                              fontSize: `${Math.max(9, Math.round(11 * (gridZoom / 100)))}px`,
                              fontStyle: 'italic',
                              border: '1px dashed var(--border-color)',
                              borderRadius: `${Math.round(10 * (gridZoom / 100))}px`,
                              backgroundColor: 'rgba(255,255,255,0.02)'
                            }}>
                              No Back Preview
                            </div>
                          )}

                          {/* Bottom Translucent Title Overlay */}
                          <div style={{
                            position: 'absolute',
                            bottom: 0,
                            left: 0,
                            right: 0,
                            backgroundColor: 'rgba(255, 255, 255, 0.3)',
                            backdropFilter: 'blur(6px)',
                            WebkitBackdropFilter: 'blur(6px)',
                            borderTop: '1px solid rgba(0, 0, 0, 0.1)',
                            padding: `${Math.round(8 * (gridZoom / 100))}px ${Math.round(12 * (gridZoom / 100))}px`,
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center',
                            zIndex: 2
                          }}>
                            <h3 style={{ margin: 0, fontSize: `${Math.max(10, Math.round(12 * (gridZoom / 100)))}px`, fontWeight: '700', color: '#121212', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', textAlign: 'center' }}>
                              {t.name} (Back)
                            </h3>
                            {showTemplateUsageInfo && (
                              <div
                                data-testid={`template-usage-info-back-${t.id}`}
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  gap: `${Math.max(4, Math.round(6 * (gridZoom / 100)))}px`,
                                  marginTop: `${Math.max(3, Math.round(4 * (gridZoom / 100)))}px`,
                                  flexWrap: 'wrap'
                                }}
                              >
                                <span
                                  title="Times used (issued coins)"
                                  data-testid={`template-used-count-back-${t.id}`}
                                  style={{
                                    backgroundColor: '#059669',
                                    color: '#ffffff',
                                    fontSize: `${Math.max(8, Math.round(10 * (gridZoom / 100)))}px`,
                                    fontWeight: '700',
                                    padding: `${Math.max(1, Math.round(2 * (gridZoom / 100)))}px ${Math.max(4, Math.round(6 * (gridZoom / 100)))}px`,
                                    borderRadius: '4px',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '3px',
                                    boxShadow: '0 1px 3px rgba(0,0,0,0.25)',
                                    lineHeight: 1.2
                                  }}
                                >
                                  <span>Used:</span>
                                  <span>{loadingUsageStats && !templateUsageStats[t.id] ? '...' : (templateUsageStats[t.id]?.used_count ?? 0)}</span>
                                </span>
                                <span
                                  title="Unallocated instances remaining"
                                  data-testid={`template-unallocated-count-back-${t.id}`}
                                  style={{
                                    backgroundColor: '#2563eb',
                                    color: '#ffffff',
                                    fontSize: `${Math.max(8, Math.round(10 * (gridZoom / 100)))}px`,
                                    fontWeight: '700',
                                    padding: `${Math.max(1, Math.round(2 * (gridZoom / 100)))}px ${Math.max(4, Math.round(6 * (gridZoom / 100)))}px`,
                                    borderRadius: '4px',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '3px',
                                    boxShadow: '0 1px 3px rgba(0,0,0,0.25)',
                                    lineHeight: 1.2
                                  }}
                                >
                                  <span>Remaining:</span>
                                  <span>{loadingUsageStats && !templateUsageStats[t.id] ? '...' : (templateUsageStats[t.id]?.unallocated_count ?? 0)}</span>
                                </span>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
          </div>
        )}
      </div>
      

      {showSequenceModal && createPortal(
        activeElement && activeElement.type === 'text' ? (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.75)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 99999,
            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
          }}>
            <div style={{
              background: 'var(--card-bg, #1b1b1d)',
              border: '1px solid var(--border-color, #3c3c42)',
              borderRadius: '8px',
              width: '480px',
              padding: '16px',
              boxShadow: '0 8px 24px rgba(0,0,0,0.5)',
              display: 'flex',
              flexDirection: 'column',
              gap: '12px'
            }}>
              {/* Header */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid var(--border-color, #2d2d30)', paddingBottom: '8px' }}>
                <span style={{ fontSize: '11px', fontWeight: 'bold', color: 'var(--text-main, #dfdfe3)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  Edit Text Sequence
                </span>
                <button 
                  onClick={() => setShowSequenceModal(false)}
                  style={{ backgroundColor: 'transparent', border: 'none', color: 'var(--text-mute, #8e8e93)', cursor: 'pointer', fontSize: '14px' }}
                >
                  ✕
                </button>
              </div>

              {/* Modal Body */}
              {(() => {
                try {
                  const activeFields = getActiveFields();
                  const dynamicFieldsList = activeElement.dynamicFields || (activeElement.text ? [{
                    id: 'df-init',
                    type: 'static',
                    value: activeElement.text
                  }] : []);

                  return (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                      {/* Result Preview Box */}
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', backgroundColor: '#141416', padding: '10px', borderRadius: '4px', border: '1px solid #2d2d30' }}>
                        <span style={{ fontSize: '10px', color: '#8e8e93', fontWeight: 'bold' }}>Sequence Preview:</span>
                        <span style={{ fontSize: '12px', color: '#dfdfe3', wordBreak: 'break-all' }}>
                          {resolveElementText(activeElement)}
                        </span>
                      </div>

                      {/* 1. Add Dynamic Field Row */}
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                        <span style={{ fontSize: '10px', color: '#8e8e93' }}>Insert Active Data Field</span>
                        <div style={{ display: 'flex', gap: '6px' }}>
                          <select
                            id="manager-dynamic-field-select"
                            style={{ flex: 1, backgroundColor: '#1b1b1d', border: '1px solid #3c3c42', borderRadius: '4px', padding: '6px', color: '#fff', fontSize: '11px', outline: 'none' }}
                          >
                            {activeFields.map(f => (
                              <option key={`${f.datasetKey}-${f.label}`} value={`${f.datasetKey}::${f.label}`}>
                                {f.alias} (${f.label})
                              </option>
                            ))}
                            {activeFields.length === 0 && (
                              <option value="">No active fields. Link datasets first.</option>
                            )}
                          </select>
                          <button
                            type="button"
                            disabled={activeFields.length === 0}
                            onClick={() => {
                              const sel = document.getElementById('manager-dynamic-field-select');
                              if (sel && sel.value) {
                                const [dsKey, fieldLabel] = sel.value.split('::');
                                const fieldObj = activeFields.find(f => f.datasetKey === dsKey && f.label === fieldLabel);
                                if (fieldObj) {
                                  const newItem = {
                                    id: 'df-' + Date.now() + '-' + Math.random().toString(36).substr(2, 4),
                                    type: 'dynamic',
                                    datasetKey: fieldObj.datasetKey,
                                    label: fieldObj.label,
                                    alias: fieldObj.alias,
                                    sample_data: fieldObj.sample_data,
                                    render: fieldObj.render || 'none'
                                  };
                                  const nextList = [...dynamicFieldsList, newItem];
                                  const newText = nextList.map(item => item.type === 'static' ? item.value : `$${item.label}`).join('');
                                  updateActiveElement({
                                    dynamicFields: nextList,
                                    text: newText
                                  });
                                }
                              }
                            }}
                            style={{
                              backgroundColor: '#1677ff',
                              color: '#fff',
                              border: 'none',
                              borderRadius: '4px',
                              padding: '6px 12px',
                              fontSize: '11px',
                              fontWeight: 'bold',
                              cursor: activeFields.length === 0 ? 'not-allowed' : 'pointer',
                              opacity: activeFields.length === 0 ? 0.5 : 1
                            }}
                          >
                            Add Field
                          </button>
                        </div>
                      </div>

                      {/* 2. Add Static Text Row */}
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                        <span style={{ fontSize: '10px', color: '#8e8e93' }}>Insert Static Text</span>
                        <div style={{ display: 'flex', gap: '6px' }}>
                          <input
                            type="text"
                            placeholder="Type static text here..."
                            value={newStaticText}
                            onChange={(e) => setNewStaticText(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                e.preventDefault();
                                if (newStaticText.trim() !== '') {
                                  const newItem = {
                                    id: 'df-' + Date.now() + '-' + Math.random().toString(36).substr(2, 4),
                                    type: 'static',
                                    value: newStaticText
                                  };
                                  const nextList = [...dynamicFieldsList, newItem];
                                  const newText = nextList.map(item => item.type === 'static' ? item.value : `$${item.label}`).join('');
                                  updateActiveElement({
                                    dynamicFields: nextList,
                                    text: newText
                                  });
                                  setNewStaticText('');
                                }
                              }
                            }}
                            style={{ flex: 1, backgroundColor: '#1b1b1d', border: '1px solid #3c3c42', borderRadius: '4px', padding: '6px', color: '#fff', fontSize: '11px', outline: 'none' }}
                          />
                          <button
                            type="button"
                            onClick={() => {
                              if (newStaticText.trim() !== '') {
                                const newItem = {
                                  id: 'df-' + Date.now() + '-' + Math.random().toString(36).substr(2, 4),
                                  type: 'static',
                                  value: newStaticText
                                };
                                const nextList = [...dynamicFieldsList, newItem];
                                const newText = nextList.map(item => item.type === 'static' ? item.value : `$${item.label}`).join('');
                                updateActiveElement({
                                  dynamicFields: nextList,
                                  text: newText
                                });
                                setNewStaticText('');
                              }
                            }}
                            style={{
                              backgroundColor: '#3c3c42',
                              color: '#fff',
                              border: 'none',
                              borderRadius: '4px',
                              padding: '6px 12px',
                              fontSize: '11px',
                              fontWeight: 'bold',
                              cursor: 'pointer'
                            }}
                          >
                            Add Static
                          </button>
                        </div>
                      </div>

                      {/* 3. Sequence Items list */}
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', borderTop: '1px solid #2d2d30', paddingTop: '10px' }}>
                        <span style={{ fontSize: '10px', color: '#8e8e93', fontWeight: 'bold' }}>Text Sequence Components</span>
                        {dynamicFieldsList.length === 0 ? (
                          <span style={{ fontSize: '11px', color: '#5e5e64', fontStyle: 'italic', textAlign: 'center', padding: '12px 0' }}>
                            No components in text sequence.
                          </span>
                        ) : (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', maxHeight: '180px', overflowY: 'auto', paddingRight: '4px' }}>
                            {dynamicFieldsList.map((item, idx) => (
                              <div key={item.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '6px', border: '1px solid #3c3c42', borderRadius: '4px', backgroundColor: '#141416' }}>
                                {item.type === 'static' ? (
                                  <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '2px', marginRight: '8px' }}>
                                    <span style={{ fontSize: '9px', color: '#a2a2a6', fontWeight: 'bold' }}>Static Text</span>
                                    <input
                                      type="text"
                                      value={item.value}
                                      onChange={(e) => {
                                        const val = e.target.value;
                                        const nextList = dynamicFieldsList.map(x => x.id === item.id ? { ...x, value: val } : x);
                                        const newText = nextList.map(x => x.type === 'static' ? x.value : `$${x.label}`).join('');
                                        updateActiveElement({
                                          dynamicFields: nextList,
                                          text: newText
                                        });
                                      }}
                                      style={{ width: '100%', backgroundColor: '#1b1b1d', border: '1px solid #3c3c42', borderRadius: '3px', padding: '4px', color: '#fff', fontSize: '11px', outline: 'none' }}
                                    />
                                  </div>
                                ) : (
                                  <div style={{ flex: 1, display: 'flex', flexDirection: 'column', marginRight: '8px' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                                      <span style={{ fontSize: '10px', color: '#dfdfe3', fontWeight: 'bold' }}>{item.alias}</span>
                                      <span style={{ fontSize: '9px', fontFamily: 'monospace', color: '#ff7875' }}>${item.label}</span>
                                    </div>
                                    <span style={{ fontSize: '9px', color: '#8e8e93', textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap', maxWidth: '240px' }}>
                                      Sample: "{item.sample_data}"{(() => { const ds = datasets[item.datasetKey]; let rId = item.render || 'none'; let fObj = null; if (ds && ds.fields) { const matched = ds.fields.find(f => f.label === item.label); if (matched) { fObj = matched; if (matched.render) rId = matched.render; } } if (!fObj && item.custom_renderer) { fObj = { custom_renderer: item.custom_renderer }; } if (rId && rId !== 'none') { return ` → "${applyRenderer(item.sample_data || '', rId, fObj)}"`; } return ''; })()}
                                    </span>
                                  </div>
                                )}

                                {/* Reorder and Delete Actions */}
                                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                  <button
                                    type="button"
                                    disabled={idx === 0}
                                    onClick={() => {
                                      if (idx === 0) return;
                                      const nextList = [...dynamicFieldsList];
                                      const temp = nextList[idx];
                                      nextList[idx] = nextList[idx - 1];
                                      nextList[idx - 1] = temp;
                                      const newText = nextList.map(x => x.type === 'static' ? x.value : `$${x.label}`).join('');
                                      updateActiveElement({
                                        dynamicFields: nextList,
                                        text: newText
                                      });
                                    }}
                                    style={{ background: 'none', border: 'none', color: idx === 0 ? '#3c3c42' : '#8e8e93', cursor: idx === 0 ? 'default' : 'pointer', fontSize: '9px', padding: '2px' }}
                                  >
                                    ▲
                                  </button>
                                  <button
                                    type="button"
                                    disabled={idx === dynamicFieldsList.length - 1}
                                    onClick={() => {
                                      if (idx === dynamicFieldsList.length - 1) return;
                                      const nextList = [...dynamicFieldsList];
                                      const temp = nextList[idx];
                                      nextList[idx] = nextList[idx + 1];
                                      nextList[idx + 1] = temp;
                                      const newText = nextList.map(x => x.type === 'static' ? x.value : `$${x.label}`).join('');
                                      updateActiveElement({
                                        dynamicFields: nextList,
                                        text: newText
                                      });
                                    }}
                                    style={{ background: 'none', border: 'none', color: idx === dynamicFieldsList.length - 1 ? '#3c3c42' : '#8e8e93', cursor: idx === dynamicFieldsList.length - 1 ? 'default' : 'pointer', fontSize: '9px', padding: '2px' }}
                                  >
                                    ▼
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => {
                                      const nextList = dynamicFieldsList.filter(x => x.id !== item.id);
                                      const newText = nextList.map(x => x.type === 'static' ? x.value : `$${x.label}`).join('');
                                      updateActiveElement({
                                        dynamicFields: nextList,
                                        text: newText
                                      });
                                    }}
                                    style={{ background: 'none', border: 'none', color: '#ff4d4f', cursor: 'pointer', fontSize: '10px', padding: '2px', marginLeft: '4px' }}
                                  >
                                    🗑️
                                  </button>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                } catch (e) {
                  console.error("Modal Render Error:", e);
                  return (
                    <div style={{ color: '#ff4d4f', padding: '16px', fontSize: '12px' }}>
                      Render Error: {e.message}
                    </div>
                  );
                }
              })()}

              {/* Footer */}
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', borderTop: '1px solid var(--border-color, #2d2d30)', paddingTop: '10px', marginTop: '4px' }}>
                <button
                  type="button"
                  onClick={() => setShowSequenceModal(false)}
                  style={{
                    padding: '6px 12px',
                    fontSize: '11px',
                    background: 'var(--primary-gradient, var(--primary-color, #1677ff))',
                    color: '#ffffff',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontWeight: 'bold'
                  }}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        ) : null,
        document.body
      )}
      {renderGridDataModal()}
      {renderAssetSelectorModal()}
      {renderImageAdjustModal()}
      {renderExitConfirmModal()}
      {renderSavingModal()}
      {renderShareModal()}
      {renderImportModal()}
      {renderPushModal()}
      {renderUnissuedSyncModal()}
      {renderPrintExportModal()}
    </div>
  );
}
